/*
 * ╔══════════════════════════════════════════════════════════════╗
 * ║                        CartIQ™                              ║
 * ║           Smart Grocery Savings · Instacart Affiliate        ║
 * ╠══════════════════════════════════════════════════════════════╣
 * ║  Copyright © 2026 CartIQ. All Rights Reserved.              ║
 * ║  Proprietary & Confidential.                                ║
 * ║  DMCA Agent: legal@cartiq.app                               ║
 * ╚══════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useRef, useCallback, useMemo, Component, memo, lazy, Suspense, Fragment } from "react";

/* ═══════════════════════════════════════════════════════════
   FONTS — Farm-fresh typography
   ═══════════════════════════════════════════════════════════ */
const FONT_URL = "https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;1,6..72,400&family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap";
const FONT_DISPLAY = '"Newsreader", "Instrument Serif", Georgia, serif';
const FONT_SANS    = '"Geist", "Inter Tight", -apple-system, system-ui, sans-serif';
const FONT_MONO    = '"Geist Mono", ui-monospace, monospace';

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — Farm-fresh light theme
   ═══════════════════════════════════════════════════════════ */
const T = {
  // Backgrounds & surfaces
  cream:      '#FAF6EC',
  paper:      '#FFFDF6',
  parchment:  '#F1EBDB',
  // Greens
  forest:     '#1E3528',
  basil:      '#3F5D34',
  sage:       '#B8C9A6',
  sageSoft:   '#DCE5CD',
  leaf:       '#6E8C56',
  // Warm accents
  peach:      '#F0B886',
  peachSoft:  '#F8DCC0',
  apricot:    '#E89A65',
  butter:     '#EFD479',
  butterSoft: '#F8E9B5',
  berry:      '#A04562',
  berrySoft:  '#E8C9D2',
  // Extended palette (added v1)
  clay:       '#C2654A',
  claySoft:   '#ECC8B8',
  oat:        '#E8DAB8',
  sky:        '#A4B5B8',
  skySoft:    '#D4DEDF',
  skyBright:  '#60a5fa',  // Phase B unit-price highlight — brighter than T.sky for visibility on cards
  plum:       '#5E4459',
  plumSoft:   '#C9B5C2',
  cocoa:      '#4E3525',
  cocoaSoft:  '#C9B5A4',
  // Neutrals
  ink:        '#1E3528',
  ink70:      'rgba(30,53,40,0.72)',
  ink50:      'rgba(30,53,40,0.50)',
  ink30:      'rgba(30,53,40,0.30)',
  ink15:      'rgba(30,53,40,0.15)',
  ink08:      'rgba(30,53,40,0.08)',
  ink04:      'rgba(30,53,40,0.04)',
  hairline:   'rgba(30,53,40,0.10)',
  // Status
  success:    '#3F5D34',
  warn:       '#C77A2C',
  // Radius
  r4:4, r8:8, r12:12, r16:16, r20:20, r24:24, r28:28, r32:32,
  // Shadows
  shadowCard:  '0 1px 0 rgba(30,53,40,0.04), 0 8px 24px -12px rgba(30,53,40,0.10)',
  shadowFloat: '0 12px 40px -16px rgba(30,53,40,0.18)',
  shadowSoft:  '0 1px 2px rgba(30,53,40,0.04)',
};

/* Typography scale */
const TY = {
  display: { fontFamily: FONT_DISPLAY, fontWeight: 400, letterSpacing: '-0.02em', fontOpticalSizing: 'auto' },
  sans:    { fontFamily: FONT_SANS,    fontWeight: 400, letterSpacing: '-0.005em' },
  mono:    { fontFamily: FONT_MONO,    letterSpacing: '0.02em' },
  h1:      { fontFamily: FONT_DISPLAY, fontSize: 36, lineHeight: '1.0',  fontWeight: 400, letterSpacing: '-0.025em' },
  h2:      { fontFamily: FONT_DISPLAY, fontSize: 26, lineHeight: '1.05', fontWeight: 400, letterSpacing: '-0.02em'  },
  h3:      { fontFamily: FONT_SANS,    fontSize: 18, lineHeight: '1.2',  fontWeight: 600, letterSpacing: '-0.01em'  },
  body:    { fontFamily: FONT_SANS,    fontSize: 14, lineHeight: '1.45', fontWeight: 400 },
  small:   { fontFamily: FONT_SANS,    fontSize: 12, lineHeight: '1.4',  fontWeight: 500 },
  eyebrow: { fontFamily: FONT_SANS,    fontSize: 10.5, lineHeight: '1',  fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' },
  price:   { fontFamily: FONT_DISPLAY, fontWeight: 400, letterSpacing: '-0.02em' },
};

/* ═══════════════════════════════════════════════════════════
   AFFILIATE CONFIG
   ═══════════════════════════════════════════════════════════ */
const AFFILIATE_ID    = "YOUR_AFFILIATE_ID";
const INSTACART_BASE  = "https://www.instacart.com";
const AFFILIATE_PARAM = "affiliate_id";
const COMMISSION_NEW  = 10.00;
const COMMISSION_REPEAT = 0.03;

// Flip to `true` once the Impact.com/Instacart affiliate partnership is
// approved and live. Until then, user-facing copy says "supports CartIQ
// at no cost to you" rather than claiming commissions are being earned.
// This keeps the app legally accurate pre-launch while making the
// "go live" flip a single-line change.
const AFFILIATE_LIVE  = false;

// Centralised user-facing affiliate copy, gated on AFFILIATE_LIVE.
// Edit here, not at call sites.
const AFFILIATE_COPY = {
  // Short footer line shown below the Instacart checkout button
  checkoutFooter: AFFILIATE_LIVE
    ? "CartIQ earns a commission from Instacart at no cost to you"
    : "Using our link supports CartIQ at no cost to you",
  // Terms modal disclosure section
  termsDisclosure: AFFILIATE_LIVE
    ? "CartIQ earns affiliate commissions from Instacart via Impact.com. This does not affect the price you pay."
    : "CartIQ is building an affiliate partnership with Instacart via Impact.com. Once live, CartIQ may earn a commission from orders placed through its links. You will never pay more as a result.",
  // Earnings tab header label
  earningsHeader: AFFILIATE_LIVE
    ? "CartIQ Platform"
    : "CartIQ Platform · Preview",
  earningsSubheader: AFFILIATE_LIVE
    ? "IMPACT.COM"
    : "IMPACT.COM · PENDING",
  // Status pill
  liveStatusLabel: AFFILIATE_LIVE ? "LIVE" : "PREVIEW",
  liveStatusColor: AFFILIATE_LIVE ? T.leaf : T.warn,
  // All-time earnings label
  earningsLabel: AFFILIATE_LIVE
    ? "ALL-TIME EARNINGS"
    : "PROJECTED EARNINGS (DEMO)",
};

// ── Instacart integration mode ────────────────────────────────────────────
// Controls all "Send to Cart" UX. While false, the Send to Cart button still
// works (it opens the correct Instacart storefront in a new tab) but the UI
// honestly discloses that cart contents do not auto-transfer to Instacart yet.
//
// Flip to true ONLY after wiring the Instacart Developer Platform (IDP)
// products_link endpoint via a backend proxy. See Build Guide Addendum v2 for
// the integration steps.
const INSTACART_INTEGRATION_LIVE = false;

const INTEGRATION_COPY = {
  // Tiny pill on the "Open Instacart Now" button
  pillLabel: INSTACART_INTEGRATION_LIVE ? null : "PREVIEW",
  // Banner shown above each Send-to-Cart button on Smart Cart
  bannerText: INSTACART_INTEGRATION_LIVE
    ? null
    : "Preview mode — opens the store on Instacart so you can shop manually. Auto-loaded carts are coming soon.",
  // First-time disclosure modal headline
  modalTitle: INSTACART_INTEGRATION_LIVE ? null : "Heads up — preview mode",
  // First-time disclosure modal body
  modalBody: INSTACART_INTEGRATION_LIVE ? null :
    "Tapping this button opens the right Instacart store in a new tab so you can shop your list manually. Auto-loading your CartIQ cart into Instacart's checkout is coming soon — once Instacart approves our partnership, you'll be able to send your full cart with one tap.",
};

// ── Phase 5a: Substitution payload mapping (dormant) ────────────────────────
// Master switch for the IDP line-item payload that carries each user's saved
// substitution preference into the Instacart cart on handoff. Stays false
// until INSTACART_INTEGRATION_LIVE flips AND the IDP products_link endpoint
// is wired through a backend proxy. When false, buildLineItems is callable
// but its payload is never assembled into a live request — the helper exists
// today only so the shape can be unit-tested ahead of integration approval.
const SUBSTITUTION_PAYLOAD_ENABLED = false;

// ── Rewards tracking ────────────────────────────────────────────────────────
// Conservative estimate of what CartIQ MIGHT share back with users once the
// Instacart/Impact.com affiliate partnership is approved and a payout mechanism
// is in place. 2% is a deliberate floor: actual commission rate from Impact
// is expected to be 3-5%, but we'd rather under-promise and upgrade than
// over-promise and claw back.
//
// IMPORTANT: No payout mechanism exists today. This is ESTIMATION only —
// every surface that shows a rewards number must include clear disclosure
// that payouts are pending and balances are tracked, not owed.
const REWARDS_RATE = 0.02;

// Max price-memory entries kept per item. Older entries roll off so the
// localStorage footprint stays bounded even for power users.
const PRICE_MEMORY_CAP = 12;

// Health goals for Nutrition IQ cart rollup. Labels intentionally avoid
// FDA-regulated nutrient-content claims ("Low Sodium", "High Protein") —
// they describe what the USER is watching, not a claim about the food.
// Thresholds are per cart total, not per item, to stay informational.
const HEALTH_GOALS = [
  { id: "sodium",  label: "Watching sodium",   short: "Sodium",  field: "sodium",  unit: "mg", direction: "under", target: 2300 },
  { id: "protein", label: "Protein-focused",   short: "Protein", field: "protein", unit: "g",  direction: "over",  target: 50   },
  { id: "sugar",   label: "Mindful of sugar",  short: "Sugar",   field: "sugar",   unit: "g",  direction: "under", target: 50   },
  { id: "fiber",   label: "Fiber-focused",     short: "Fiber",   field: "fiber",   unit: "g",  direction: "over",  target: 25   },
];

// Expected Impact.com commission rate. Used to compute the confirmation
// threshold — an entry confirms in FULL only if Impact's reported commission
// on the order is at least `cartTotal × EXPECTED_COMMISSION_RATE`, which is
// the commission we'd expect if the user purchased AT LEAST the amount we
// sent. Default 0.05 (5%) per Impact's current published tier. Set this to
// your actual negotiated rate once Impact approves the partnership.
const EXPECTED_COMMISSION_RATE = 0.05;

// Proportional-confirmation floor. When Impact's reported commission is
// BELOW the full threshold but ABOVE this floor (expressed as a fraction of
// expected commission), the entry confirms PARTIALLY — the user earns a
// reward proportional to what they actually purchased.
//
// Math:
//   estimatedActualPurchase = actualImpactCommission / expectedCommissionRate
//   proportionalReward       = estimatedActualPurchase × REWARDS_RATE
//   finalReward              = min(proportionalReward, originalReward)
//
// Below this floor, the entry stays pending — these cases are almost always
// failed cookie fires, $5 add-on purchases, or other near-zero conversions
// that don't represent real shopping intent. 0.10 (10% of expected) is a
// conservative starting value; tune up if you see a lot of low-dollar
// confirmations in the tester round, tune down if testers complain that
// small-but-real partial purchases are being denied.
const REWARDS_FLOOR_RATIO = 0.10;
const SHARE_MIN_SAVINGS = 100; // gate "share my savings" until this much all-time has been saved

// Number of days a pending entry is eligible for Impact.com matching before
// it expires. Impact's affiliate cookie is 7 days, payouts are net-60, and
// Instacart's refund window is 14 days — so 90 days is a safe outer bound.
// After this window, a pending entry is almost certainly lost to cookie
// failures or abandoned carts and moves to "expired."
const REWARDS_EXPIRY_DAYS = 90;

// ── Build identification ────────────────────────────────────────────────────
// Shown in the About page footer so testers can reference a specific version
// in feedback reports, and so you can correlate feedback to specific deploys.
// Update at each meaningful push: bump the patch segment for fixes, the minor
// for feature additions, and the major for breaking changes.
const APP_VERSION = "1.0.0";
const BUILD_DATE  = "April 2026";

// Centralised destination for user feedback. Keep in one place so the in-app
// Feedback button, the Error Boundary's error report action, and any future
// prompts all route to the same inbox.
const FEEDBACK_EMAIL = "cartiq.feedback@gmail.com";

// Admin mode URL parameter. Intentionally obscure so curious testers don't
// stumble into the admin tools by guessing. Change this string if you ever
// suspect testers have discovered it — a new string effectively rotates the
// "key" without changing functionality.
const ADMIN_PARAM = "_x";
const ADMIN_TOKEN = "4k2j8";

// ── Sage AI assistant ──────────────────────────────────────────────────────
// When true, Sage's "Ask" modal calls the real Anthropic API via the
// /api/sage.js serverless proxy. When false, Sage uses the offline heuristic
// answers (no API calls, no cost). Flip to false instantly if the proxy
// misbehaves, costs spike, or you want to pause Sage — the rest of the app
// is unaffected.
const SAGE_LIVE = true;

// Per-session cap on how many questions a user can ask Sage. Resets when the
// app reloads. A second brake behind the Anthropic console spend cap — this
// one stops a single curious tester from looping the API in one sitting.
const SAGE_SESSION_LIMIT = 20;

// ── Phase A: Staples picker ────────────────────────────────────────────────
// Controls the new onboarding step that asks users to pick their usual staples.
// When false, the staples step is skipped entirely and the app behaves as
// before (Pantry seeded with PANTRY_DEFAULT demo data). Flip to false if the
// feature breaks for testers — the rest of the app continues to work.
const STAPLES_PICKER_ENABLED = true;

// Caps how many staples start checked in the picker. We take the first N
// items in catalog order whose `defaultOn` flag is true. So `defaultOn` is the
// curation signal (these are the most-universal picks), and this constant is
// the gate (we may not want to overwhelm with all of them at once).
// Lower number = less cognitive load but more taps; higher = opposite.
const STAPLES_DEFAULT_CHECKED_COUNT = 8;

// ── Phase C: Rebuild my usual cart ─────────────────────────────────────────
// Master switch for the dashboard rebuild card and Smart Cart empty-state CTA.
// When false, both surfaces fall back to v1.0.0 behavior. Independent of
// STAPLES_PICKER_ENABLED so staples can exist without the rebuild surface.
const REBUILD_CART_ENABLED = true;

// ── Phase B: Unit-price truth-telling ──────────────────────────────────────
// Master switch for prominent unit-price display. When false, falls back to
// the v1.0.0 behavior (unit price shown only on scanner/search results).
const UNIT_PRICE_DISPLAY_ENABLED = true;

// Master switch for the shrinkflation alert chip. When false, no chip is
// shown even if size data is present. Kept independent of unit-price display
// so the alert can be flipped off without losing the per-unit comparisons.
const SHRINKFLATION_ALERTS_ENABLED = true;

// ── Phase 4b: Pre-checkout fee estimate ─────────────────────────────────
// Master switch for the estimated-total panel in the checkout review step.
const FEE_ESTIMATE_ENABLED = true;

// Instacart fee model — figures match the Pro Tips "delivery-windows" entry.
// These are ESTIMATES. Instacart sets the real fees at its own checkout.
// Keep these in sync with the Pro Tips body if Instacart's policy changes.
const FEE_MODEL = {
  serviceFeeRate: 0.05,     // ~5% of subtotal
  busySurgeRate:  0.05,     // rough peak-time surcharge on top of subtotal
  // deliveryFee comes from the store object (store.deliveryFee).
};

// ── Phase 4a: Trip Receipt ──────────────────────────────────────────────
// Master switch for the post-checkout TripReceiptModal AND the dashboard
// "Recent trips" re-open list. Both honor this flag — flip to false to
// disable the entire feature surface.
const TRIP_RECEIPT_ENABLED = true;

const REWARDS_COPY = {
  // Short label used on stat tiles / small surfaces
  shortLabel: AFFILIATE_LIVE ? "Rewards earned" : "Rewards tracked",
  // Longer label for cards / dashboards
  longLabel: AFFILIATE_LIVE ? "Rewards earned" : "Estimated rewards tracked",
  // Hero headline above the confirmed number
  heroConfirmedLabel: AFFILIATE_LIVE ? "CONFIRMED BALANCE" : "CONFIRMED · DEMO",
  // Hero secondary label above pending
  heroPendingLabel: "PENDING · AWAITING INSTACART CONFIRMATION",
  // Status pill color
  statusColor: AFFILIATE_LIVE ? T.leaf : T.warn,
  // Per-entry state pills
  pillPending: { label: "PENDING", bg: "rgba(251,191,36,.14)", border: T.butter, fg: T.warn },
  pillConfirmed: { label: "CONFIRMED", bg: T.sageSoft, border: T.sage, fg: T.leaf },
  pillExpired: { label: "EXPIRED", bg: T.parchment, border: `${T.hairline}`, fg: T.ink50 },
  // Dashboard card disclosure (short)
  dashboardDisclosure: AFFILIATE_LIVE
    ? `You earn ${(REWARDS_RATE * 100).toFixed(0)}% back on every confirmed Instacart order.`
    : `Estimated at ${(REWARDS_RATE * 100).toFixed(0)}% of each order. Payouts begin once CartIQ's Instacart partnership is approved.`,
  // Full disclosure for the Rewards detail view
  detailDisclosure: AFFILIATE_LIVE
    ? `Your CartIQ rewards are ${(REWARDS_RATE * 100).toFixed(0)}% of each Instacart order. Orders move from Pending to Confirmed once Instacart confirms the purchase (typically within 30-60 days). If Impact's commission is lower than our ${(REWARDS_RATE * 100).toFixed(0)}% estimate, the confirmed amount adjusts to what was actually paid. Cash out once your confirmed balance reaches $10.`
    : `This is a DEMO balance, tracked at ${(REWARDS_RATE * 100).toFixed(0)}% of every order you've sent to Instacart through CartIQ. Pending entries track what WOULD be earned — confirmed entries will appear once our Instacart affiliate partnership is live and payouts have been verified. Entries that don't confirm within ${REWARDS_EXPIRY_DAYS} days automatically expire.`,
  // Celebratory line at the end of the checkout flow
  checkoutCelebration: AFFILIATE_LIVE
    ? "rewards pending"
    : "rewards tracked (demo)",
};

/* ═══════════════════════════════════════════════════════════
   RATINGS API CONFIG
   ═══════════════════════════════════════════════════════════
   Two separate data sources for ratings:

   1) GOOGLE PLACES API — for physical store ratings (live, ready).
      Get a key at console.cloud.google.com → enable "Places API".
      Free tier covers ~11,000 detail calls/month.

   2) INSTACART CONNECT API — for product ratings (stub, waiting on approval).
      Apply at instacart.com/company/partner-with-us. Once approved,
      you'll receive a client ID + secret for OAuth2 token exchange.
      Until then, the product ratings hook returns null gracefully.
   ═══════════════════════════════════════════════════════════ */
// Google Places key now lives ONLY on the server (Vercel env GOOGLE_PLACES_API_KEY,
// no VITE_ prefix). Client calls /api/places, /api/geocode, /api/nearby instead
// of hitting maps.googleapis.com directly — fixes CORS and prevents the key
// from being extracted from the JS bundle.
// VITE_PLACES_LIVE is a build-time boolean ("true"/"false") that gates the
// "live mode" vs "demo mode" copy in the ZIP setup. Set it alongside the
// server-side key in Vercel.
const PLACES_LIVE           = import.meta.env.VITE_PLACES_LIVE === "true";
const INSTACART_API_KEY     = "YOUR_INSTACART_CONNECT_API_KEY";
const INSTACART_API_BASE    = "https://connect.instacart.com/v1";  // placeholder
const RATINGS_CACHE_HOURS   = 24;  // cache ratings for 24h to stay under free tier

const hasGoogleKey   = () => PLACES_LIVE;
const hasInstacartKey = () => INSTACART_API_KEY !== "YOUR_INSTACART_CONNECT_API_KEY";

/* ═══════════════════════════════════════════════════════════
   LEGAL
   ═══════════════════════════════════════════════════════════ */
const COPYRIGHT_YEAR   = new Date().getFullYear();
const LEGAL_OWNER      = "CartIQ";
const DMCA_EMAIL       = "legal@cartiq.app";
const PRIVACY_URL      = "https://cartiq.app/privacy";
const COPYRIGHT_NOTICE = `© ${COPYRIGHT_YEAR} ${LEGAL_OWNER}. All Rights Reserved.`;

function buildAffiliateUrl(store, cartDeals, trackingId) {
  // Verified Instacart store slugs (April 2026, see /store/<slug>/storefront).
  // Update this map if Instacart renames a storefront.
  const storeSlug = { aldi:"aldi", publix:"publix", sprouts:"sprouts", freshMarket:"the-fresh-market" }[store.id] || store.id;
  // Base storefront URL — the cleanest, most legitimate handoff we can build
  // without the IDP /products_link API. The user lands on a real Instacart
  // page for the right retailer.
  const base = `${INSTACART_BASE}/store/${storeSlug}/storefront`;
  // Build query params conditionally. Three principles:
  //   1. NEVER ship "YOUR_AFFILIATE_ID" in a URL a real user (or reviewer)
  //      would screenshot. If AFFILIATE_LIVE is false, omit affiliate_id entirely.
  //   2. utm_* params are safe to include either way — they help us measure
  //      handoff volume in our own analytics without affecting Instacart.
  //   3. Per-order tracking ID is embedded as `SubId` (Impact.com's custom
  //      tracking field) AND duplicated as `utm_content` for our own analytics.
  //      When Impact reports commissions, we look them up by SubId to match
  //      each commission back to the specific ledger entry. This is what
  //      enables the three-state ledger to automatically transition entries
  //      from pending → confirmed.
  // The fake `items=<comma list>` query param has been REMOVED. Instacart does
  // not consume it; including it gave testers (and reviewers) the false
  // impression that we were transferring cart contents.
  const params = new URLSearchParams({
    utm_source: "cartiq",
    utm_medium: "app",
    utm_campaign: "cart_handoff",
  });
  if (trackingId) {
    params.set("utm_content", trackingId);
    // Impact.com's SubId / sub1 / sharedId field — per Impact's spec this is
    // the parameter used for per-partner custom tracking IDs passed through
    // to commission reports.
    params.set("subId1", trackingId);
  }
  if (AFFILIATE_LIVE && AFFILIATE_ID && AFFILIATE_ID !== "YOUR_AFFILIATE_ID") {
    params.set(AFFILIATE_PARAM, AFFILIATE_ID);
  }
  return `${base}?${params.toString()}`;
}

// TODO(IDP): verify field names against developer docs once the development
// key arrives. The mapping LOGIC below is correct; only the JSON keys
// ("substitution", "preferred_substitute", "upcs") are best-guess. Keep them
// isolated in this one helper so a rename is a one-function change.
//
// SUB_OPTIONS → IDP intent. Three keys exist today:
//   brand_only  → "do_not_substitute"  (user explicitly wants this brand)
//   similar_ok  → "allow_substitution" (default — closest match)
//   any         → "allow_substitution" + preferred_substitute hint
// preferred_substitute only attaches when the user named a specific
// alternative (subPrefs[id].sub is a non-empty trimmed string). When the
// user picked "any" but didn't name an alternative, the intent stays
// "allow_substitution" with no hint — Instacart's shopper picks.
//
// Unsupported by the live SUB_OPTIONS today: a "contact me first" intent
// (IDP has no equivalent — would map lossy to "allow_substitution"), and a
// "cheapest from suggested" auto-pick (getSuggestedSubs returns names only,
// no price data). If SUB_OPTIONS grows new keys, extend this switch.
function buildLineItems(cartDeals, subPrefs) {
  return cartDeals.map(deal => {
    const pref = subPrefs && subPrefs[deal.id];
    const choice = pref && pref.pref;
    const named = pref && typeof pref.sub === "string" && pref.sub.trim();
    const substitution = choice === "brand_only" ? "do_not_substitute" : "allow_substitution";
    const item = {
      name: deal.name,
      quantity: deal.qty || 1,
      unit: deal.unit || "each",
      substitution,
    };
    if (deal.upc) item.upcs = [deal.upc];
    if (named) item.preferred_substitute = { name: named };
    return item;
  });
}

// ── Phase 5a: Dormant IDP cart handoff ──────────────────────────────────────
// Staged for the Instacart Developer Platform /products_link endpoint, which
// must be reached through a backend proxy because the API key cannot ship in
// the client bundle. Per Build Guide Addendum v2.
//
// INERT until INSTACART_INTEGRATION_LIVE flips to true. Production today uses
// buildAffiliateUrl — a static storefront deep link with no cart payload.
// Once IDP is wired, callers will switch to this function and its returned
// URL becomes the actual checkout target. Until then this exists only so the
// payload shape can be reviewed and unit-tested ahead of integration approval.
//
// Why the guard matters: calling /products_link directly from the client
// would either leak the API key or fail CORS. Either way the cart breaks.
// The throw at the top is the kill-switch so an accidental call surfaces
// immediately in dev instead of silently hitting a non-existent proxy.
async function buildInstacartCartUrl(store, cartDeals, subPrefs, trackingId) {
  if (!INSTACART_INTEGRATION_LIVE) {
    throw new Error("buildInstacartCartUrl: INSTACART_INTEGRATION_LIVE is false");
  }
  // TODO(IDP): verify retailer_key — IDP uses store-specific slugs that may
  // not match CartIQ's internal store.id (e.g. "publix" vs "publix-supermarkets").
  // Map via a STORE_RETAILER_KEYS lookup once the development key arrives.
  const payload = {
    retailer_key: store.id,
    line_items: buildLineItems(cartDeals, subPrefs),
    landing_page_configuration: {
      partner_linkback_url: window.location.origin,
    },
    expires_in: 60 * 60 * 24, // 24h, IDP default
  };
  if (trackingId) payload.reference_id = trackingId;
  // Backend proxy path — must exist server-side before this is reachable.
  // The proxy holds the IDP API key, calls /products_link, and returns the
  // products_link_url field from the IDP response body.
  const res = await fetch("/api/instacart/products-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`IDP proxy failed: ${res.status}`);
  const data = await res.json();
  return data.products_link_url;
}

/* ═══════════════════════════════════════════════════════════
   ICONS & HELPERS
   ═══════════════════════════════════════════════════════════ */
const CAT_ICON = { Dairy:"🥛", Seafood:"🐟", Meat:"🥩", Produce:"🥦", Grains:"🌾", Pantry:"🫙", Beverages:"💧", Bakery:"🍞", Frozen:"❄️", Snacks:"🍿", Household:"🧹", Eggs:"🥚" };

/* ── Per-item emoji map ────────────────────────────────────
   More specific than category fallback — used wherever we need
   product imagery (Price IQ cards, cart line items, watchlist).
   Renders via Twemoji SVG illustration set (see ItemIcon).
   When a key isn't matched we fall back to category emoji. */
const ITEM_EMOJI = {
  // Dairy / Eggs
  "organic-whole-milk":"🥛", "free-range-eggs":"🥚", "greek-yogurt":"🥣",
  "cheddar":"🧀", "butter":"🧈", "almond-milk":"🥛",
  // Meat / Seafood
  "chicken-breast":"🍗", "ground-turkey":"🦃", "atlantic-salmon":"🐟",
  "shrimp":"🦐", "bacon":"🥓", "ribeye":"🥩",
  // Produce
  "baby-spinach":"🥬", "avocados":"🥑", "bananas":"🍌",
  "cherry-tomatoes":"🍅", "lemons":"🍋", "blueberries":"🫐",
  "strawberries":"🍓", "broccoli":"🥦", "carrots":"🥕",
  "garlic":"🧄", "onions":"🧅",
  // Grains / Bakery
  "bread":"🍞", "organic-quinoa":"🌾", "brown-rice":"🍚",
  "pasta":"🍝", "oats":"🥣", "tortillas":"🌯",
  // Pantry
  "almond-butter":"🥜", "olive-oil":"🫒", "honey":"🍯",
  "peanut-butter":"🥜", "maple-syrup":"🍯",
  // Beverages
  "sparkling-water":"💧", "kombucha":"🍵", "cold-brew":"☕",
  "orange-juice":"🍊",
  // Frozen
  "organic-frozen-berries":"🫐", "organic-frozen-acai":"🍇",
  "ice-cream":"🍦",
  // Snacks
  "tortilla-chips":"🌽", "popcorn":"🍿", "dark-chocolate":"🍫",
};
const getItemEmoji = (id, name, category) => {
  if (id && ITEM_EMOJI[id]) return ITEM_EMOJI[id];
  if (/\begg(s)?\b/i.test(name)) return "🥚";
  return CAT_ICON[category] || "🛒";
};
// Backward compat — existing call sites pass (name, category)
const getItemIcon = (name, category) => getItemEmoji(null, name, category);

/* ── ItemIcon — renders a Twemoji SVG for an emoji.
   We use the JSDelivr CDN'd Twemoji asset set — open-licensed, consistent
   illustration style, served as crisp SVG. Falls back to native emoji on load
   error so the screen never goes blank.
   Props:
     emoji  : single emoji glyph (e.g. "🥛")
     size   : pixel size of the rendered icon (default 22)
     style  : optional override styles
   ─────────────────────────────────────────────────────── */
function ItemIcon({ emoji, size = 22, style = {} }) {
  // Convert emoji glyph to its Unicode codepoint(s) for Twemoji's filename scheme
  const codepoints = [...emoji]
    .map(c => c.codePointAt(0).toString(16))
    .filter(cp => cp !== "fe0f")  // strip variation selector
    .join("-");
  const url = `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codepoints}.svg`;
  const [errored, setErrored] = useState(false);
  if (errored) {
    return <span style={{fontSize:size, lineHeight:1, ...style}}>{emoji}</span>;
  }
  return (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      onError={()=>setErrored(true)}
      style={{display:"block", width:size, height:size, ...style}}
    />
  );
}

const isOrganic = n => /organic|wild.caught|free.range|artisan|açaí/i.test(n);

const CAT_TINT = { Dairy:"rgba(96,165,250,.06)", Seafood:"rgba(56,189,248,.06)", Meat:"rgba(248,113,113,.06)", Produce:"rgba(74,222,128,.06)", Grains:T.butterSoft, Pantry:T.berrySoft, Beverages:"rgba(96,165,250,.06)", Bakery:"rgba(251,146,60,.06)", Frozen:"rgba(147,197,253,.06)", Snacks:T.butterSoft };

/* ═══════════════════════════════════════════════════════════
   DIETARY RESTRICTIONS — personalization filter
   ═══════════════════════════════════════════════════════════
   User toggles these in onboarding or settings. Each restriction
   has a predicate that returns true when an item SHOULD BE HIDDEN.

   Matching is pattern-based (name + category) since CartIQ doesn't
   have per-item ingredient data. When Instacart Connect is approved,
   this can be upgraded to use the real product tags they provide.
   ═══════════════════════════════════════════════════════════ */
const DIETARY_RESTRICTIONS = [
  {
    id: "meat",
    label: "No meat",
    icon: "🥩",
    desc: "Hides beef, pork, poultry, and processed meat products",
    blocks: (item) => {
      if (item.category === "Meat") return true;
      return /\b(chicken|beef|pork|turkey|bacon|sausage|ham|steak|ground|ribs|lamb|veal)\b/i.test(item.name);
    },
  },
  {
    id: "seafood",
    label: "No seafood",
    icon: "🐟",
    desc: "Hides fish, shellfish, and seafood products",
    blocks: (item) => {
      if (item.category === "Seafood") return true;
      return /\b(salmon|tuna|shrimp|crab|lobster|cod|tilapia|halibut|mackerel|anchov|sardine|oyster|clam|mussel|squid|calamari)\b/i.test(item.name);
    },
  },
  {
    id: "dairy",
    label: "No dairy",
    icon: "🥛",
    desc: "Hides milk, cheese, yogurt, butter, and cream",
    blocks: (item) => {
      if (item.category === "Dairy") return true;
      return /\b(milk|cheese|yogurt|butter|cream|whey|kefir|cottage cheese|sour cream)\b/i.test(item.name)
        && !/\b(almond|soy|oat|coconut|cashew|rice|hemp) milk\b/i.test(item.name);  // allow plant milks
    },
  },
  {
    id: "gluten",
    label: "No gluten",
    icon: "🌾",
    desc: "Hides bread, pasta, wheat, barley, and rye products",
    blocks: (item) => {
      if (/\bgluten.?free\b/i.test(item.name)) return false;  // explicit GF exception
      return /\b(bread|pasta|wheat|flour|barley|rye|tortilla|bagel|muffin|cracker|pretzel|couscous|bulgur|semolina|croissant)\b/i.test(item.name);
    },
  },
  {
    id: "pork",
    label: "No pork",
    icon: "🐖",
    desc: "Hides pork, bacon, ham, and pork-derived products",
    blocks: (item) => /\b(pork|bacon|ham|pepperoni|prosciutto|chorizo|salami|sausage)\b/i.test(item.name),
  },
  {
    id: "shellfish",
    label: "No shellfish",
    icon: "🦐",
    desc: "Hides shrimp, crab, lobster, oysters, and shellfish",
    blocks: (item) => /\b(shrimp|crab|lobster|oyster|clam|mussel|scallop|crawfish|prawn)\b/i.test(item.name),
  },
  {
    id: "eggs",
    label: "No eggs",
    icon: "🥚",
    desc: "Hides eggs and egg-heavy products",
    blocks: (item) => /\begg(s)?\b/i.test(item.name),
  },
];

// Returns true if the item passes ALL active restrictions (i.e., should be shown)
function matchesDietary(item, activeRestrictions) {
  if (!item || !activeRestrictions || activeRestrictions.length === 0) return true;
  for (const restrictionId of activeRestrictions) {
    const r = DIETARY_RESTRICTIONS.find(x => x.id === restrictionId);
    if (r && r.blocks(item)) return false;
  }
  return true;
}

/* ═══════════════════════════════════════════════════════════
   SOUND ENGINE
   ═══════════════════════════════════════════════════════════ */
let audioCtx = null;
const getCtx = () => { if (!audioCtx) audioCtx = new (window.AudioContext||window.webkitAudioContext)(); return audioCtx; };

// Sound on/off — single source of truth for the audio layer.
// Mutated by the CartIQ component via setSoundEnabled() (below) when the user
// toggles the preference. Defaults to ON; persisted state hydrates async, so
// during the first ~50ms a returning user with sounds-off may hear one tone.
// That's an acceptable cost for keeping playTone synchronous and React-free.
let soundEnabled = true;
function setSoundEnabled(on) { soundEnabled = !!on; }

// Haptics on/off — same architecture as soundEnabled. Routes through a single
// hapticTap() helper so every haptic in the app respects the user's preference
// without each call site having to know about the toggle. Default ON.
// iOS Safari ignores navigator.vibrate entirely (Apple disabled it), so on
// iPhone this toggle is a no-op either way — kept for Android/Chrome users.
let hapticsEnabled = true;
function setHapticsEnabled(on) { hapticsEnabled = !!on; }
function hapticTap(ms = 30) {
  if (!hapticsEnabled) return;
  if (typeof navigator === "undefined" || !("vibrate" in navigator)) return;
  try { navigator.vibrate(ms); } catch(e) {}
}

function playTone(freq,type,vol,dur,delay=0) {
  if (!soundEnabled) return;
  try {
    const ctx=getCtx(), osc=ctx.createOscillator(), gain=ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type=type; osc.frequency.setValueAtTime(freq,ctx.currentTime+delay);
    gain.gain.setValueAtTime(0,ctx.currentTime+delay);
    gain.gain.linearRampToValueAtTime(vol,ctx.currentTime+delay+0.01);
    gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+delay+dur);
    osc.start(ctx.currentTime+delay); osc.stop(ctx.currentTime+delay+dur+0.05);
  } catch(e){}
}
const SFX = {
  addCart:   ()=>{ playTone(523,"sine",.18,.12); playTone(659,"sine",.14,.10,.10); playTone(784,"sine",.10,.18,.18); },
  removeCart:()=>{ playTone(400,"sine",.12,.18); playTone(320,"sine",.08,.16,.14); },
  tabSwitch: ()=>{ playTone(880,"sine",.06,.08); },
  success:   ()=>{ playTone(523,"triangle",.15,.10); playTone(659,"triangle",.12,.10,.10); playTone(1047,"triangle",.10,.22,.20); },
  payment:   ()=>{ [392,494,587,740,880].forEach((f,i)=>playTone(f,"sine",.12,.18,i*.09)); },
  notify:    ()=>{ playTone(698,"sine",.12,.10); playTone(880,"sine",.10,.14,.12); },
  redeem:    ()=>{ [523,659,784,1047].forEach((f,i)=>playTone(f,"triangle",.12,.15,i*.08)); },
  restock:   ()=>{ playTone(440,"sine",.10,.12); playTone(554,"sine",.08,.12,.12); },
  aiSend:    ()=>{ playTone(880,"sine",.07,.08); playTone(1047,"sine",.05,.06,.08); },
  aiReply:   ()=>{ playTone(659,"triangle",.08,.10); playTone(784,"triangle",.06,.08,.10); },
  scan:      ()=>{ playTone(1200,"sawtooth",.06,.06); playTone(900,"sawtooth",.04,.06,.08); },
  cash:      ()=>{ [880,1047,1319].forEach((f,i)=>playTone(f,"triangle",.14,.14,i*.07)); },
};

/* ═══════════════════════════════════════════════════════════
   STORE DATA
   ═══════════════════════════════════════════════════════════ */
const STORES = [
  { id:"aldi", name:"ALDI", tagline:"ALDI Finds · Weekly Specials", color:T.apricot, glow:T.butterSoft,
    bg:`linear-gradient(135deg,${T.forest},${T.apricot})`, logo:"🛒", deliveryFee:3.99, deliveryMin:35, memberFee:0,
    deliveryNote:"Via Instacart · no membership needed",
    placeId:"ChIJ_____ALDI_PLACE_ID_HERE",  // ← replace with real Google Place ID for your ALDI location
    deals:[
      {id:"chicken-breast",   name:"Chicken Breast",   dealLabel:"ALDI LOW",  savings:4.50,origPrice:8.99, salePrice:4.49,expiresIn:3,perfectTime:"Mon 8–10AM", confidence:95,category:"Meat",    stockLevel:"high",  ibotta:.50,stackable:true},
      {id:"free-range-eggs",  name:"Free Range Eggs",  dealLabel:"ALDI FIND", savings:2.50,origPrice:5.49, salePrice:2.99,expiresIn:2,perfectTime:"Sun 9–11AM", confidence:98,category:"Dairy",   stockLevel:"medium",ibotta:.25,stackable:true},
      {id:"baby-spinach",     name:"Baby Spinach",     dealLabel:"25% OFF",   savings:1.50,origPrice:2.99, salePrice:1.49,expiresIn:5,perfectTime:"Wed 9–11AM", confidence:90,category:"Produce", stockLevel:"high",  ibotta:0,  stackable:false},
      {id:"greek-yogurt",     name:"Greek Yogurt",     dealLabel:"BOGO",      savings:2.49,origPrice:2.49, salePrice:0,   expiresIn:4,perfectTime:"Tue 10–12PM",confidence:93,category:"Dairy",   stockLevel:"high",  ibotta:.50,stackable:true},
      {id:"bread",            name:"Multigrain Bread", dealLabel:"ALDI FIND", savings:1.79,origPrice:3.49, salePrice:1.70,expiresIn:1,perfectTime:"TODAY 2–4PM",confidence:99,category:"Bakery",  stockLevel:"low",   ibotta:0,  stackable:false},
    ]},
  { id:"publix", name:"Publix", tagline:"BOGO Weekly Deals", color:T.basil, glow:T.sage,
    bg:`${T.forest}`, logo:"🌿", deliveryFee:9.99, deliveryMin:10, memberFee:0,
    deliveryNote:"No Express minimum",
    placeId:"ChIJ_____PUBLIX_PLACE_ID_HERE",
    deals:[
      {id:"organic-whole-milk",     name:"Organic Whole Milk",dealLabel:"BOGO",      savings:6.99,origPrice:6.99, salePrice:0,   expiresIn:4,perfectTime:"Thu 9–11AM", confidence:97,category:"Dairy",   stockLevel:"high",  ibotta:1.00,stackable:true},
      {id:"ground-turkey",          name:"Ground Turkey",     dealLabel:"BOGO",      savings:5.99,origPrice:5.99, salePrice:0,   expiresIn:4,perfectTime:"Thu 9–11AM", confidence:97,category:"Meat",    stockLevel:"medium",ibotta:.75,stackable:true},
      {id:"atlantic-salmon",        name:"Atlantic Salmon",   dealLabel:"BOGO",      savings:9.99,origPrice:9.99, salePrice:0,   expiresIn:4,perfectTime:"Wed 11–1PM", confidence:96,category:"Seafood", stockLevel:"high",  ibotta:1.50,stackable:true},
      {id:"avocados",               name:"Avocados",          dealLabel:"4 for $5",  savings:2.96,origPrice:1.99, salePrice:1.25,expiresIn:3,perfectTime:"Mon 10–12PM",confidence:88,category:"Produce", stockLevel:"high",  ibotta:.25,stackable:true},
      {id:"sparkling-water",        name:"Sparkling Water",   dealLabel:"BOGO 50%",  savings:4.99,origPrice:9.99, salePrice:4.99,expiresIn:6,perfectTime:"Sat 10–12PM",confidence:85,category:"Beverages",stockLevel:"high", ibotta:.50,stackable:true},
      {id:"organic-quinoa",         name:"Organic Quinoa",    dealLabel:"B2G1 FREE", savings:13.99,origPrice:13.99,salePrice:0, expiresIn:5,perfectTime:"Thu 10–12PM",confidence:91,category:"Grains", stockLevel:"high",  ibotta:.50,stackable:true},
    ]},
  { id:"sprouts", name:"Sprouts", tagline:"Double Ad Wednesday", color:T.leaf, glow:T.sageSoft,
    bg:`linear-gradient(135deg,${T.basil},${T.leaf})`, logo:"🌱", deliveryFee:6.99, deliveryMin:35, memberFee:0,
    deliveryNote:"Wed: two ads overlap — max savings",
    placeId:"ChIJ_____SPROUTS_PLACE_ID_HERE",
    deals:[
      {id:"organic-quinoa",         name:"Organic Quinoa",       dealLabel:"20% OFF",  savings:2.80,origPrice:13.99,salePrice:11.19,expiresIn:3,perfectTime:"Wed 8–10AM", confidence:92,category:"Grains", stockLevel:"high",  ibotta:0,   stackable:false},
      {id:"almond-butter",          name:"Almond Butter",        dealLabel:"BOGO 50%", savings:4.99,origPrice:9.99, salePrice:4.99,expiresIn:3,perfectTime:"Wed 9–11AM",  confidence:94,category:"Pantry", stockLevel:"medium",ibotta:1.00,stackable:true},
      {id:"cherry-tomatoes",        name:"Cherry Tomatoes",      dealLabel:"2 for $5", savings:2.98,origPrice:3.99, salePrice:2.50,expiresIn:6,perfectTime:"Sat 9–11AM",  confidence:87,category:"Produce",stockLevel:"high",  ibotta:.25,stackable:true},
      {id:"organic-frozen-acai",    name:"Organic Frozen Açaí",  dealLabel:"25% OFF",  savings:2.50,origPrice:9.99, salePrice:7.49,expiresIn:5,perfectTime:"Thu 10–12PM", confidence:89,category:"Frozen", stockLevel:"high",  ibotta:.50,stackable:true},
      {id:"olive-oil",              name:"Extra Virgin Olive Oil",dealLabel:"30% OFF", savings:5.40,origPrice:17.99,salePrice:12.59,expiresIn:2,perfectTime:"Sun 10–12PM",confidence:91,category:"Pantry", stockLevel:"low",   ibotta:.75,stackable:true},
      {id:"organic-frozen-berries", name:"Organic Frozen Berries",dealLabel:"B2G1 FREE",savings:6.99,origPrice:6.99, salePrice:0, expiresIn:7,perfectTime:"Wed 9–11AM", confidence:90,category:"Frozen", stockLevel:"high",  ibotta:.50,stackable:true},
    ]},
  { id:"freshMarket", name:"The Fresh Market", tagline:"The Big Deal & Weekly Specials", color:T.berry, glow:T.berrySoft,
    bg:`linear-gradient(135deg,${T.forest},${T.berry})`, logo:"🌹", deliveryFee:7.99, deliveryMin:30, memberFee:0,
    deliveryNote:"'The Big Deal' = premium rotating hero item",
    placeId:"ChIJ_____FRESHMARKET_PLACE_ID_HERE",
    deals:[
      {id:"atlantic-salmon", name:"Wild-Caught Salmon",  dealLabel:"BIG DEAL",  savings:8.00,origPrice:19.99,salePrice:11.99,expiresIn:2,perfectTime:"Sun 11–1PM",  confidence:96,category:"Seafood",stockLevel:"low",   ibotta:1.50,stackable:true},
      {id:"greek-yogurt",    name:"Artisan Greek Yogurt",dealLabel:"3 for $10", savings:5.97,origPrice:5.99, salePrice:3.33,expiresIn:5,perfectTime:"Wed 10–12PM", confidence:88,category:"Dairy",  stockLevel:"high",  ibotta:.50,stackable:true},
      {id:"bread",           name:"Sourdough Boule",     dealLabel:"$2 OFF",    savings:2.00,origPrice:7.99, salePrice:5.99,expiresIn:3,perfectTime:"Fri 4–6PM",   confidence:90,category:"Bakery", stockLevel:"medium",ibotta:0,  stackable:false},
      {id:"avocados",        name:"Organic Avocados",    dealLabel:"4 for $6",  savings:3.96,origPrice:2.49, salePrice:1.50,expiresIn:4,perfectTime:"Thu 9–11AM",  confidence:85,category:"Produce",stockLevel:"high",  ibotta:.25,stackable:true},
      {id:"baby-spinach",    name:"Baby Spinach",        dealLabel:"B2G1 FREE", savings:2.99,origPrice:2.99, salePrice:0, expiresIn:4,perfectTime:"Wed 8–10AM",  confidence:88,category:"Produce",stockLevel:"high",  ibotta:.25,stackable:true},
    ]},
];

const MASTER_LIST = [
  {id:"organic-whole-milk",     name:"Organic Whole Milk",     category:"Dairy",     qty:2,unit:"gal",   priority:"high",   nutrition:{protein:8, fiber:0,calories:150,sodium:120,sugar:12},weeklyUse:7},
  {id:"free-range-eggs",        name:"Free Range Eggs",        category:"Dairy",     qty:2,unit:"doz",   priority:"high",   nutrition:{protein:12,fiber:0,calories:140,sodium:140,sugar:1},weeklyUse:12},
  {id:"atlantic-salmon",        name:"Atlantic Salmon",        category:"Seafood",   qty:2,unit:"lb",    priority:"medium", nutrition:{protein:25,fiber:0,calories:208,sodium:60,sugar:0},weeklyUse:2},
  {id:"chicken-breast",         name:"Chicken Breast",         category:"Meat",      qty:3,unit:"lb",    priority:"high",   nutrition:{protein:31,fiber:0,calories:165,sodium:74,sugar:0},weeklyUse:3},
  {id:"baby-spinach",           name:"Baby Spinach",           category:"Produce",   qty:2,unit:"bag",   priority:"high",   nutrition:{protein:3, fiber:2,calories:23,sodium:24,sugar:0}, weeklyUse:14},
  {id:"avocados",               name:"Avocados",               category:"Produce",   qty:4,unit:"ea",    priority:"medium", nutrition:{protein:2, fiber:7,calories:160,sodium:7,sugar:1},weeklyUse:4},
  {id:"greek-yogurt",           name:"Greek Yogurt",           category:"Dairy",     qty:4,unit:"ea",    priority:"medium", nutrition:{protein:17,fiber:0,calories:100,sodium:65,sugar:6},weeklyUse:4},
  {id:"organic-quinoa",         name:"Organic Quinoa",         category:"Grains",    qty:1,unit:"bag",   priority:"low",    nutrition:{protein:8, fiber:5,calories:222,sodium:13,sugar:2},weeklyUse:3},
  {id:"almond-butter",          name:"Almond Butter",          category:"Pantry",    qty:1,unit:"jar",   priority:"medium", nutrition:{protein:7, fiber:2,calories:190,sodium:75,sugar:3},weeklyUse:7},
  {id:"sparkling-water",        name:"Sparkling Water",        category:"Beverages", qty:2,unit:"case",  priority:"low",    nutrition:{protein:0, fiber:0,calories:0,sodium:0,sugar:0},  weeklyUse:14},
  {id:"bread",                  name:"Bread",                  category:"Bakery",    qty:1,unit:"loaf",  priority:"medium", nutrition:{protein:3, fiber:2,calories:80,sodium:170,sugar:2},  weeklyUse:7},
  {id:"olive-oil",              name:"Olive Oil",              category:"Pantry",    qty:1,unit:"bottle",priority:"medium", nutrition:{protein:0, fiber:0,calories:119,sodium:0,sugar:0},weeklyUse:2},
  {id:"organic-frozen-berries", name:"Organic Frozen Berries", category:"Frozen",    qty:2,unit:"bag",   priority:"low",    nutrition:{protein:1, fiber:4,calories:70,sodium:0,sugar:8},  weeklyUse:7},
  {id:"ground-turkey",          name:"Ground Turkey",          category:"Meat",      qty:2,unit:"lb",    priority:"high",   nutrition:{protein:27,fiber:0,calories:170,sodium:71,sugar:0},weeklyUse:2},
  {id:"cherry-tomatoes",        name:"Cherry Tomatoes",        category:"Produce",   qty:2,unit:"pint",  priority:"medium", nutrition:{protein:1, fiber:1,calories:27,sodium:5,sugar:2},  weeklyUse:7},
];

// ── Expanded catalog for universal search (Phase 1) ────────────────────
// ~263 canonical items with per-store availability.
// Canonical IDs preserve existing MASTER_LIST IDs so Smart Cart routing
// is uninterrupted. When real Instacart API is wired, swap EXPANDED_CATALOG
// + STORE_AVAILABILITY for API-fetched data — UI and Smart Cart don't change.
const EXPANDED_CATALOG = [
  {
    "id": "organic-whole-milk",
    "name": "Organic Whole Milk",
    "category": "Dairy",
    "size": "1 gal",
    "basePrice": 6.49,
    "organic": true
  },
  {
    "id": "organic-whole-milk-half-gal",
    "name": "Organic Whole Milk",
    "category": "Dairy",
    "size": "1/2 gal",
    "basePrice": 3.99,
    "organic": true
  },
  {
    "id": "whole-milk",
    "name": "Whole Milk",
    "category": "Dairy",
    "size": "1 gal",
    "basePrice": 3.99
  },
  {
    "id": "skim-milk",
    "name": "Skim Milk",
    "category": "Dairy",
    "size": "1 gal",
    "basePrice": 3.99
  },
  {
    "id": "two-percent-milk",
    "name": "2% Milk",
    "category": "Dairy",
    "size": "1 gal",
    "basePrice": 3.99
  },
  {
    "id": "oat-milk",
    "name": "Oat Milk",
    "category": "Dairy",
    "size": "64 oz",
    "basePrice": 5.49
  },
  {
    "id": "almond-milk",
    "name": "Almond Milk, Unsweetened",
    "category": "Dairy",
    "size": "64 oz",
    "basePrice": 4.49
  },
  {
    "id": "coconut-milk",
    "name": "Coconut Milk, Refrigerated",
    "category": "Dairy",
    "size": "64 oz",
    "basePrice": 4.99
  },
  {
    "id": "heavy-cream",
    "name": "Heavy Cream",
    "category": "Dairy",
    "size": "1 pint",
    "basePrice": 4.49
  },
  {
    "id": "half-and-half",
    "name": "Half & Half",
    "category": "Dairy",
    "size": "1 qt",
    "basePrice": 3.99
  },
  {
    "id": "unsalted-butter",
    "name": "Unsalted Butter",
    "category": "Dairy",
    "size": "1 lb",
    "basePrice": 5.49
  },
  {
    "id": "salted-butter",
    "name": "Salted Butter",
    "category": "Dairy",
    "size": "1 lb",
    "basePrice": 5.49
  },
  {
    "id": "organic-butter",
    "name": "Organic Butter",
    "category": "Dairy",
    "size": "1 lb",
    "basePrice": 7.99,
    "organic": true
  },
  {
    "id": "greek-yogurt",
    "name": "Greek Yogurt, Plain",
    "category": "Dairy",
    "size": "32 oz",
    "basePrice": 5.99
  },
  {
    "id": "greek-yogurt-vanilla",
    "name": "Greek Yogurt, Vanilla",
    "category": "Dairy",
    "size": "32 oz",
    "basePrice": 5.99
  },
  {
    "id": "greek-yogurt-single",
    "name": "Greek Yogurt, Single Serve",
    "category": "Dairy",
    "size": "5.3 oz",
    "basePrice": 1.49
  },
  {
    "id": "regular-yogurt",
    "name": "Regular Yogurt",
    "category": "Dairy",
    "size": "32 oz",
    "basePrice": 4.49
  },
  {
    "id": "cottage-cheese",
    "name": "Cottage Cheese, 2%",
    "category": "Dairy",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "cream-cheese",
    "name": "Cream Cheese",
    "category": "Dairy",
    "size": "8 oz",
    "basePrice": 3.49
  },
  {
    "id": "cheddar-shredded",
    "name": "Cheddar Cheese, Shredded",
    "category": "Dairy",
    "size": "8 oz",
    "basePrice": 3.99
  },
  {
    "id": "mozzarella-shredded",
    "name": "Mozzarella Cheese, Shredded",
    "category": "Dairy",
    "size": "8 oz",
    "basePrice": 3.99
  },
  {
    "id": "parmesan-grated",
    "name": "Parmesan, Grated",
    "category": "Dairy",
    "size": "6 oz",
    "basePrice": 5.99
  },
  {
    "id": "feta-crumbled",
    "name": "Feta, Crumbled",
    "category": "Dairy",
    "size": "4 oz",
    "basePrice": 3.99
  },
  {
    "id": "string-cheese",
    "name": "String Cheese",
    "category": "Dairy",
    "size": "12 ct",
    "basePrice": 4.99
  },
  {
    "id": "sour-cream",
    "name": "Sour Cream",
    "category": "Dairy",
    "size": "16 oz",
    "basePrice": 2.99
  },
  {
    "id": "free-range-eggs",
    "name": "Free Range Eggs",
    "category": "Eggs",
    "size": "12 ct",
    "basePrice": 5.49,
    "organic": true
  },
  {
    "id": "free-range-eggs-18",
    "name": "Free Range Eggs",
    "category": "Eggs",
    "size": "18 ct",
    "basePrice": 7.99,
    "organic": true
  },
  {
    "id": "large-eggs-12",
    "name": "Large Eggs",
    "category": "Eggs",
    "size": "12 ct",
    "basePrice": 3.49
  },
  {
    "id": "large-eggs-18",
    "name": "Large Eggs",
    "category": "Eggs",
    "size": "18 ct",
    "basePrice": 4.99
  },
  {
    "id": "organic-eggs",
    "name": "Organic Eggs",
    "category": "Eggs",
    "size": "12 ct",
    "basePrice": 6.99,
    "organic": true
  },
  {
    "id": "egg-whites",
    "name": "Egg Whites, Liquid",
    "category": "Eggs",
    "size": "16 oz",
    "basePrice": 4.49
  },
  {
    "id": "baby-spinach",
    "name": "Baby Spinach",
    "category": "Produce",
    "size": "5 oz bag",
    "basePrice": 2.99
  },
  {
    "id": "organic-baby-spinach",
    "name": "Organic Baby Spinach",
    "category": "Produce",
    "size": "5 oz bag",
    "basePrice": 4.49,
    "organic": true
  },
  {
    "id": "romaine-hearts",
    "name": "Romaine Hearts",
    "category": "Produce",
    "size": "3 ct",
    "basePrice": 3.49
  },
  {
    "id": "kale",
    "name": "Kale Bunch",
    "category": "Produce",
    "size": "each",
    "basePrice": 2.49
  },
  {
    "id": "arugula",
    "name": "Baby Arugula",
    "category": "Produce",
    "size": "5 oz",
    "basePrice": 3.99
  },
  {
    "id": "mixed-greens",
    "name": "Spring Mix",
    "category": "Produce",
    "size": "5 oz",
    "basePrice": 3.99
  },
  {
    "id": "avocados",
    "name": "Avocados",
    "category": "Produce",
    "size": "each",
    "basePrice": 1.99
  },
  {
    "id": "organic-avocados",
    "name": "Organic Avocados",
    "category": "Produce",
    "size": "each",
    "basePrice": 2.49,
    "organic": true
  },
  {
    "id": "bananas",
    "name": "Bananas",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 0.59
  },
  {
    "id": "organic-bananas",
    "name": "Organic Bananas",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 0.79,
    "organic": true
  },
  {
    "id": "apples-honeycrisp",
    "name": "Honeycrisp Apples",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 2.99
  },
  {
    "id": "apples-gala",
    "name": "Gala Apples",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "apples-granny",
    "name": "Granny Smith Apples",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "strawberries",
    "name": "Strawberries",
    "category": "Produce",
    "size": "1 lb",
    "basePrice": 4.99
  },
  {
    "id": "blueberries",
    "name": "Blueberries",
    "category": "Produce",
    "size": "6 oz",
    "basePrice": 3.99
  },
  {
    "id": "raspberries",
    "name": "Raspberries",
    "category": "Produce",
    "size": "6 oz",
    "basePrice": 4.99
  },
  {
    "id": "blackberries",
    "name": "Blackberries",
    "category": "Produce",
    "size": "6 oz",
    "basePrice": 4.49
  },
  {
    "id": "grapes-red",
    "name": "Red Grapes",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 2.99
  },
  {
    "id": "grapes-green",
    "name": "Green Grapes",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 2.99
  },
  {
    "id": "oranges",
    "name": "Navel Oranges",
    "category": "Produce",
    "size": "4 lb bag",
    "basePrice": 5.99
  },
  {
    "id": "lemons",
    "name": "Lemons",
    "category": "Produce",
    "size": "each",
    "basePrice": 0.79
  },
  {
    "id": "limes",
    "name": "Limes",
    "category": "Produce",
    "size": "each",
    "basePrice": 0.49
  },
  {
    "id": "cherry-tomatoes",
    "name": "Cherry Tomatoes",
    "category": "Produce",
    "size": "1 pint",
    "basePrice": 3.49
  },
  {
    "id": "roma-tomatoes",
    "name": "Roma Tomatoes",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "tomatoes-vine",
    "name": "Tomatoes on the Vine",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 2.99
  },
  {
    "id": "cucumber",
    "name": "Cucumber",
    "category": "Produce",
    "size": "each",
    "basePrice": 1.29
  },
  {
    "id": "english-cucumber",
    "name": "English Cucumber",
    "category": "Produce",
    "size": "each",
    "basePrice": 2.49
  },
  {
    "id": "bell-peppers-red",
    "name": "Red Bell Peppers",
    "category": "Produce",
    "size": "each",
    "basePrice": 1.99
  },
  {
    "id": "bell-peppers-green",
    "name": "Green Bell Peppers",
    "category": "Produce",
    "size": "each",
    "basePrice": 1.29
  },
  {
    "id": "bell-peppers-yellow",
    "name": "Yellow Bell Peppers",
    "category": "Produce",
    "size": "each",
    "basePrice": 1.99
  },
  {
    "id": "onions-yellow",
    "name": "Yellow Onions",
    "category": "Produce",
    "size": "3 lb bag",
    "basePrice": 3.49
  },
  {
    "id": "onions-red",
    "name": "Red Onions",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.49
  },
  {
    "id": "onions-sweet",
    "name": "Sweet Onions",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "garlic",
    "name": "Garlic",
    "category": "Produce",
    "size": "each bulb",
    "basePrice": 0.99
  },
  {
    "id": "ginger",
    "name": "Fresh Ginger",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 4.99
  },
  {
    "id": "carrots",
    "name": "Carrots",
    "category": "Produce",
    "size": "2 lb bag",
    "basePrice": 2.49
  },
  {
    "id": "baby-carrots",
    "name": "Baby Carrots",
    "category": "Produce",
    "size": "1 lb bag",
    "basePrice": 1.99
  },
  {
    "id": "celery",
    "name": "Celery",
    "category": "Produce",
    "size": "each bunch",
    "basePrice": 2.99
  },
  {
    "id": "broccoli",
    "name": "Broccoli Crowns",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 2.49
  },
  {
    "id": "cauliflower",
    "name": "Cauliflower Head",
    "category": "Produce",
    "size": "each",
    "basePrice": 3.99
  },
  {
    "id": "brussels-sprouts",
    "name": "Brussels Sprouts",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 3.99
  },
  {
    "id": "asparagus",
    "name": "Asparagus",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 4.99
  },
  {
    "id": "sweet-potato",
    "name": "Sweet Potatoes",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.49
  },
  {
    "id": "russet-potatoes",
    "name": "Russet Potatoes",
    "category": "Produce",
    "size": "5 lb bag",
    "basePrice": 3.99
  },
  {
    "id": "yukon-potatoes",
    "name": "Yukon Gold Potatoes",
    "category": "Produce",
    "size": "3 lb bag",
    "basePrice": 3.99
  },
  {
    "id": "zucchini",
    "name": "Zucchini",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "yellow-squash",
    "name": "Yellow Squash",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "mushrooms-white",
    "name": "White Mushrooms",
    "category": "Produce",
    "size": "8 oz",
    "basePrice": 2.49
  },
  {
    "id": "mushrooms-portobello",
    "name": "Portobello Mushrooms",
    "category": "Produce",
    "size": "6 oz",
    "basePrice": 4.49
  },
  {
    "id": "corn-ears",
    "name": "Corn on the Cob",
    "category": "Produce",
    "size": "4 ct",
    "basePrice": 3.99
  },
  {
    "id": "jalapenos",
    "name": "Jalapeño Peppers",
    "category": "Produce",
    "size": "per lb",
    "basePrice": 1.99
  },
  {
    "id": "cilantro",
    "name": "Cilantro",
    "category": "Produce",
    "size": "each bunch",
    "basePrice": 0.99
  },
  {
    "id": "parsley",
    "name": "Italian Parsley",
    "category": "Produce",
    "size": "each bunch",
    "basePrice": 0.99
  },
  {
    "id": "basil",
    "name": "Fresh Basil",
    "category": "Produce",
    "size": "0.75 oz",
    "basePrice": 2.99
  },
  {
    "id": "mint",
    "name": "Fresh Mint",
    "category": "Produce",
    "size": "0.75 oz",
    "basePrice": 2.99
  },
  {
    "id": "chicken-breast",
    "name": "Boneless Skinless Chicken Breast",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 5.99
  },
  {
    "id": "organic-chicken-breast",
    "name": "Organic Chicken Breast",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 9.99,
    "organic": true
  },
  {
    "id": "chicken-thighs",
    "name": "Boneless Chicken Thighs",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 4.49
  },
  {
    "id": "chicken-thighs-bone-in",
    "name": "Bone-In Chicken Thighs",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 3.49
  },
  {
    "id": "whole-chicken",
    "name": "Whole Chicken",
    "category": "Meat",
    "size": "each (~4 lb)",
    "basePrice": 9.99
  },
  {
    "id": "chicken-wings",
    "name": "Chicken Wings",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 4.99
  },
  {
    "id": "ground-beef",
    "name": "Ground Beef, 85/15",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 6.99
  },
  {
    "id": "ground-beef-93",
    "name": "Ground Beef, 93/7 Lean",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 7.99
  },
  {
    "id": "ground-beef-80",
    "name": "Ground Beef, 80/20",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 5.99
  },
  {
    "id": "organic-ground-beef",
    "name": "Organic Grass-Fed Ground Beef",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 10.99,
    "organic": true
  },
  {
    "id": "ground-turkey",
    "name": "Ground Turkey",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 5.49
  },
  {
    "id": "ground-turkey-lean",
    "name": "Ground Turkey, Lean",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 6.49
  },
  {
    "id": "sirloin-steak",
    "name": "Top Sirloin Steak",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 11.99
  },
  {
    "id": "ribeye-steak",
    "name": "Ribeye Steak",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 17.99,
    "premium": true
  },
  {
    "id": "ny-strip",
    "name": "NY Strip Steak",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 15.99,
    "premium": true
  },
  {
    "id": "pork-chops",
    "name": "Boneless Pork Chops",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 4.99
  },
  {
    "id": "pork-tenderloin",
    "name": "Pork Tenderloin",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 6.99
  },
  {
    "id": "pork-ribs",
    "name": "Pork Ribs",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 5.99
  },
  {
    "id": "bacon",
    "name": "Bacon",
    "category": "Meat",
    "size": "12 oz",
    "basePrice": 6.99
  },
  {
    "id": "turkey-bacon",
    "name": "Turkey Bacon",
    "category": "Meat",
    "size": "12 oz",
    "basePrice": 4.99
  },
  {
    "id": "deli-turkey",
    "name": "Oven Roasted Turkey, Deli",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 9.99
  },
  {
    "id": "deli-ham",
    "name": "Black Forest Ham, Deli",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 8.99
  },
  {
    "id": "sausage-italian",
    "name": "Italian Sausage",
    "category": "Meat",
    "size": "per lb",
    "basePrice": 5.99
  },
  {
    "id": "sausage-breakfast",
    "name": "Breakfast Sausage",
    "category": "Meat",
    "size": "12 oz",
    "basePrice": 4.99
  },
  {
    "id": "atlantic-salmon",
    "name": "Atlantic Salmon Fillet",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 13.99
  },
  {
    "id": "wild-salmon",
    "name": "Wild-Caught Salmon",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 19.99,
    "premium": true
  },
  {
    "id": "tilapia",
    "name": "Tilapia Fillet",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 7.99
  },
  {
    "id": "cod",
    "name": "Cod Fillet",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 11.99
  },
  {
    "id": "shrimp-large",
    "name": "Large Shrimp, Raw Peeled",
    "category": "Seafood",
    "size": "1 lb",
    "basePrice": 11.99
  },
  {
    "id": "shrimp-jumbo",
    "name": "Jumbo Shrimp",
    "category": "Seafood",
    "size": "1 lb",
    "basePrice": 16.99,
    "premium": true
  },
  {
    "id": "tuna-ahi",
    "name": "Ahi Tuna Steak",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 21.99,
    "premium": true
  },
  {
    "id": "scallops",
    "name": "Sea Scallops",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 22.99,
    "premium": true
  },
  {
    "id": "halibut",
    "name": "Halibut Fillet",
    "category": "Seafood",
    "size": "per lb",
    "basePrice": 24.99,
    "premium": true
  },
  {
    "id": "bread",
    "name": "Wheat Bread",
    "category": "Bakery",
    "size": "1 loaf",
    "basePrice": 3.99
  },
  {
    "id": "sourdough-bread",
    "name": "Sourdough Bread",
    "category": "Bakery",
    "size": "1 loaf",
    "basePrice": 4.99
  },
  {
    "id": "sourdough-boule",
    "name": "Sourdough Boule",
    "category": "Bakery",
    "size": "1 loaf",
    "basePrice": 6.99
  },
  {
    "id": "white-bread",
    "name": "White Bread",
    "category": "Bakery",
    "size": "1 loaf",
    "basePrice": 3.49
  },
  {
    "id": "multigrain-bread",
    "name": "Multigrain Bread",
    "category": "Bakery",
    "size": "1 loaf",
    "basePrice": 4.49
  },
  {
    "id": "bagels-plain",
    "name": "Plain Bagels",
    "category": "Bakery",
    "size": "6 ct",
    "basePrice": 4.49
  },
  {
    "id": "bagels-everything",
    "name": "Everything Bagels",
    "category": "Bakery",
    "size": "6 ct",
    "basePrice": 4.49
  },
  {
    "id": "english-muffins",
    "name": "English Muffins",
    "category": "Bakery",
    "size": "6 ct",
    "basePrice": 3.99
  },
  {
    "id": "tortillas-flour",
    "name": "Flour Tortillas",
    "category": "Bakery",
    "size": "10 ct",
    "basePrice": 3.49
  },
  {
    "id": "tortillas-corn",
    "name": "Corn Tortillas",
    "category": "Bakery",
    "size": "30 ct",
    "basePrice": 2.99
  },
  {
    "id": "pita-bread",
    "name": "Pita Bread",
    "category": "Bakery",
    "size": "6 ct",
    "basePrice": 3.49
  },
  {
    "id": "croissants",
    "name": "Butter Croissants",
    "category": "Bakery",
    "size": "4 ct",
    "basePrice": 5.99
  },
  {
    "id": "dinner-rolls",
    "name": "Dinner Rolls",
    "category": "Bakery",
    "size": "12 ct",
    "basePrice": 3.99
  },
  {
    "id": "olive-oil",
    "name": "Extra Virgin Olive Oil",
    "category": "Pantry",
    "size": "16.9 oz",
    "basePrice": 12.99
  },
  {
    "id": "olive-oil-large",
    "name": "Extra Virgin Olive Oil",
    "category": "Pantry",
    "size": "33.8 oz",
    "basePrice": 19.99
  },
  {
    "id": "avocado-oil",
    "name": "Avocado Oil",
    "category": "Pantry",
    "size": "17 oz",
    "basePrice": 9.99
  },
  {
    "id": "canola-oil",
    "name": "Canola Oil",
    "category": "Pantry",
    "size": "48 oz",
    "basePrice": 5.49
  },
  {
    "id": "vegetable-oil",
    "name": "Vegetable Oil",
    "category": "Pantry",
    "size": "48 oz",
    "basePrice": 4.99
  },
  {
    "id": "coconut-oil",
    "name": "Coconut Oil",
    "category": "Pantry",
    "size": "14 oz",
    "basePrice": 7.99
  },
  {
    "id": "balsamic-vinegar",
    "name": "Balsamic Vinegar",
    "category": "Pantry",
    "size": "16.9 oz",
    "basePrice": 5.99
  },
  {
    "id": "apple-cider-vinegar",
    "name": "Apple Cider Vinegar",
    "category": "Pantry",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "white-vinegar",
    "name": "White Vinegar",
    "category": "Pantry",
    "size": "32 oz",
    "basePrice": 2.49
  },
  {
    "id": "salt-sea",
    "name": "Sea Salt",
    "category": "Pantry",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "salt-kosher",
    "name": "Kosher Salt",
    "category": "Pantry",
    "size": "3 lb",
    "basePrice": 3.99
  },
  {
    "id": "black-pepper",
    "name": "Ground Black Pepper",
    "category": "Pantry",
    "size": "3 oz",
    "basePrice": 3.99
  },
  {
    "id": "almond-butter",
    "name": "Almond Butter",
    "category": "Pantry",
    "size": "16 oz jar",
    "basePrice": 9.99
  },
  {
    "id": "peanut-butter",
    "name": "Peanut Butter, Creamy",
    "category": "Pantry",
    "size": "16 oz",
    "basePrice": 4.49
  },
  {
    "id": "peanut-butter-crunchy",
    "name": "Peanut Butter, Crunchy",
    "category": "Pantry",
    "size": "16 oz",
    "basePrice": 4.49
  },
  {
    "id": "nutella",
    "name": "Hazelnut Spread",
    "category": "Pantry",
    "size": "13 oz",
    "basePrice": 4.49
  },
  {
    "id": "honey",
    "name": "Pure Honey",
    "category": "Pantry",
    "size": "12 oz",
    "basePrice": 6.99
  },
  {
    "id": "maple-syrup",
    "name": "Pure Maple Syrup",
    "category": "Pantry",
    "size": "12 oz",
    "basePrice": 9.99,
    "premium": true
  },
  {
    "id": "agave",
    "name": "Agave Nectar",
    "category": "Pantry",
    "size": "11.75 oz",
    "basePrice": 4.49
  },
  {
    "id": "soy-sauce",
    "name": "Soy Sauce",
    "category": "Pantry",
    "size": "10 oz",
    "basePrice": 3.49
  },
  {
    "id": "tamari",
    "name": "Tamari, Gluten Free",
    "category": "Pantry",
    "size": "10 oz",
    "basePrice": 4.99
  },
  {
    "id": "sriracha",
    "name": "Sriracha Hot Sauce",
    "category": "Pantry",
    "size": "17 oz",
    "basePrice": 4.49
  },
  {
    "id": "hot-sauce",
    "name": "Louisiana Hot Sauce",
    "category": "Pantry",
    "size": "12 oz",
    "basePrice": 2.99
  },
  {
    "id": "mustard-dijon",
    "name": "Dijon Mustard",
    "category": "Condiments",
    "size": "8 oz",
    "basePrice": 3.49
  },
  {
    "id": "mustard-yellow",
    "name": "Yellow Mustard",
    "category": "Condiments",
    "size": "20 oz",
    "basePrice": 2.49
  },
  {
    "id": "ketchup",
    "name": "Ketchup",
    "category": "Condiments",
    "size": "20 oz",
    "basePrice": 3.99
  },
  {
    "id": "mayo",
    "name": "Mayonnaise",
    "category": "Condiments",
    "size": "30 oz",
    "basePrice": 5.49
  },
  {
    "id": "olive-mayo",
    "name": "Olive Oil Mayo",
    "category": "Condiments",
    "size": "30 oz",
    "basePrice": 6.49
  },
  {
    "id": "ranch-dressing",
    "name": "Ranch Dressing",
    "category": "Condiments",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "italian-dressing",
    "name": "Italian Dressing",
    "category": "Condiments",
    "size": "16 oz",
    "basePrice": 3.49
  },
  {
    "id": "balsamic-vinaigrette",
    "name": "Balsamic Vinaigrette",
    "category": "Condiments",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "salsa-mild",
    "name": "Salsa, Medium",
    "category": "Condiments",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "marinara",
    "name": "Marinara Sauce",
    "category": "Pantry",
    "size": "24 oz",
    "basePrice": 3.99
  },
  {
    "id": "pasta-sauce-vodka",
    "name": "Vodka Sauce",
    "category": "Pantry",
    "size": "24 oz",
    "basePrice": 5.99
  },
  {
    "id": "pesto",
    "name": "Basil Pesto",
    "category": "Pantry",
    "size": "7 oz",
    "basePrice": 5.99
  },
  {
    "id": "canned-tomatoes",
    "name": "Diced Tomatoes, Canned",
    "category": "Pantry",
    "size": "14.5 oz",
    "basePrice": 1.49
  },
  {
    "id": "tomato-sauce",
    "name": "Tomato Sauce, Canned",
    "category": "Pantry",
    "size": "15 oz",
    "basePrice": 1.29
  },
  {
    "id": "tomato-paste",
    "name": "Tomato Paste",
    "category": "Pantry",
    "size": "6 oz",
    "basePrice": 1.29
  },
  {
    "id": "canned-beans-black",
    "name": "Black Beans, Canned",
    "category": "Pantry",
    "size": "15 oz",
    "basePrice": 1.49
  },
  {
    "id": "canned-beans-kidney",
    "name": "Kidney Beans, Canned",
    "category": "Pantry",
    "size": "15 oz",
    "basePrice": 1.49
  },
  {
    "id": "canned-beans-garbanzo",
    "name": "Garbanzo Beans, Canned",
    "category": "Pantry",
    "size": "15 oz",
    "basePrice": 1.49
  },
  {
    "id": "canned-beans-pinto",
    "name": "Pinto Beans, Canned",
    "category": "Pantry",
    "size": "15 oz",
    "basePrice": 1.49
  },
  {
    "id": "canned-tuna",
    "name": "Tuna, Canned in Water",
    "category": "Pantry",
    "size": "5 oz",
    "basePrice": 1.99
  },
  {
    "id": "canned-chicken",
    "name": "Chicken, Canned",
    "category": "Pantry",
    "size": "12.5 oz",
    "basePrice": 3.99
  },
  {
    "id": "chicken-broth",
    "name": "Chicken Broth",
    "category": "Pantry",
    "size": "32 oz",
    "basePrice": 3.49
  },
  {
    "id": "vegetable-broth",
    "name": "Vegetable Broth",
    "category": "Pantry",
    "size": "32 oz",
    "basePrice": 3.49
  },
  {
    "id": "beef-broth",
    "name": "Beef Broth",
    "category": "Pantry",
    "size": "32 oz",
    "basePrice": 3.49
  },
  {
    "id": "organic-quinoa",
    "name": "Organic Quinoa",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 6.99,
    "organic": true
  },
  {
    "id": "white-rice",
    "name": "Long Grain White Rice",
    "category": "Grains",
    "size": "2 lb",
    "basePrice": 3.49
  },
  {
    "id": "brown-rice",
    "name": "Brown Rice",
    "category": "Grains",
    "size": "2 lb",
    "basePrice": 3.99
  },
  {
    "id": "jasmine-rice",
    "name": "Jasmine Rice",
    "category": "Grains",
    "size": "2 lb",
    "basePrice": 4.49
  },
  {
    "id": "basmati-rice",
    "name": "Basmati Rice",
    "category": "Grains",
    "size": "2 lb",
    "basePrice": 4.99
  },
  {
    "id": "arborio-rice",
    "name": "Arborio Rice",
    "category": "Grains",
    "size": "1 lb",
    "basePrice": 3.99
  },
  {
    "id": "farro",
    "name": "Farro",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 5.99
  },
  {
    "id": "rolled-oats",
    "name": "Rolled Oats",
    "category": "Grains",
    "size": "18 oz",
    "basePrice": 3.99
  },
  {
    "id": "steel-cut-oats",
    "name": "Steel-Cut Oats",
    "category": "Grains",
    "size": "24 oz",
    "basePrice": 4.99
  },
  {
    "id": "instant-oatmeal",
    "name": "Instant Oatmeal",
    "category": "Grains",
    "size": "10 ct",
    "basePrice": 3.99
  },
  {
    "id": "pasta-spaghetti",
    "name": "Spaghetti",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 1.99
  },
  {
    "id": "pasta-penne",
    "name": "Penne Pasta",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 1.99
  },
  {
    "id": "pasta-rotini",
    "name": "Rotini Pasta",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 1.99
  },
  {
    "id": "pasta-angel-hair",
    "name": "Angel Hair Pasta",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 1.99
  },
  {
    "id": "gluten-free-pasta",
    "name": "Gluten-Free Pasta",
    "category": "Grains",
    "size": "12 oz",
    "basePrice": 3.99
  },
  {
    "id": "whole-wheat-pasta",
    "name": "Whole Wheat Pasta",
    "category": "Grains",
    "size": "16 oz",
    "basePrice": 2.49
  },
  {
    "id": "couscous",
    "name": "Couscous",
    "category": "Grains",
    "size": "10 oz",
    "basePrice": 2.99
  },
  {
    "id": "sparkling-water",
    "name": "Sparkling Water",
    "category": "Beverages",
    "size": "12 ct 12 oz",
    "basePrice": 5.99
  },
  {
    "id": "sparkling-water-lime",
    "name": "Sparkling Water, Lime",
    "category": "Beverages",
    "size": "12 ct 12 oz",
    "basePrice": 5.99
  },
  {
    "id": "bottled-water",
    "name": "Spring Water",
    "category": "Beverages",
    "size": "24 ct 16.9 oz",
    "basePrice": 4.99
  },
  {
    "id": "coffee-ground",
    "name": "Ground Coffee",
    "category": "Beverages",
    "size": "12 oz",
    "basePrice": 8.99
  },
  {
    "id": "coffee-whole-bean",
    "name": "Whole Bean Coffee",
    "category": "Beverages",
    "size": "12 oz",
    "basePrice": 9.99
  },
  {
    "id": "coffee-k-cups",
    "name": "K-Cup Coffee Pods",
    "category": "Beverages",
    "size": "18 ct",
    "basePrice": 11.99
  },
  {
    "id": "coffee-cold-brew",
    "name": "Cold Brew Coffee",
    "category": "Beverages",
    "size": "32 oz",
    "basePrice": 6.99
  },
  {
    "id": "tea-green",
    "name": "Green Tea Bags",
    "category": "Beverages",
    "size": "40 ct",
    "basePrice": 4.99
  },
  {
    "id": "tea-black",
    "name": "Black Tea Bags",
    "category": "Beverages",
    "size": "40 ct",
    "basePrice": 4.49
  },
  {
    "id": "tea-herbal",
    "name": "Herbal Tea",
    "category": "Beverages",
    "size": "20 ct",
    "basePrice": 3.99
  },
  {
    "id": "orange-juice",
    "name": "Orange Juice",
    "category": "Beverages",
    "size": "52 oz",
    "basePrice": 5.99
  },
  {
    "id": "apple-juice",
    "name": "Apple Juice",
    "category": "Beverages",
    "size": "64 oz",
    "basePrice": 3.99
  },
  {
    "id": "cranberry-juice",
    "name": "Cranberry Juice",
    "category": "Beverages",
    "size": "64 oz",
    "basePrice": 4.99
  },
  {
    "id": "lemonade",
    "name": "Lemonade",
    "category": "Beverages",
    "size": "52 oz",
    "basePrice": 3.49
  },
  {
    "id": "cola",
    "name": "Cola",
    "category": "Beverages",
    "size": "12 ct 12 oz",
    "basePrice": 7.99
  },
  {
    "id": "diet-cola",
    "name": "Diet Cola",
    "category": "Beverages",
    "size": "12 ct 12 oz",
    "basePrice": 7.99
  },
  {
    "id": "gatorade",
    "name": "Sports Drink, Variety",
    "category": "Beverages",
    "size": "12 ct 20 oz",
    "basePrice": 11.99
  },
  {
    "id": "kombucha",
    "name": "Kombucha",
    "category": "Beverages",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "organic-frozen-berries",
    "name": "Organic Frozen Berries",
    "category": "Frozen",
    "size": "12 oz bag",
    "basePrice": 6.99,
    "organic": true
  },
  {
    "id": "frozen-blueberries",
    "name": "Frozen Blueberries",
    "category": "Frozen",
    "size": "16 oz",
    "basePrice": 4.99
  },
  {
    "id": "frozen-strawberries",
    "name": "Frozen Strawberries",
    "category": "Frozen",
    "size": "16 oz",
    "basePrice": 4.49
  },
  {
    "id": "frozen-mango",
    "name": "Frozen Mango Chunks",
    "category": "Frozen",
    "size": "16 oz",
    "basePrice": 4.99
  },
  {
    "id": "organic-frozen-acai",
    "name": "Organic Frozen Açaí",
    "category": "Frozen",
    "size": "4 ct",
    "basePrice": 9.99,
    "organic": true
  },
  {
    "id": "frozen-pizza",
    "name": "Frozen Pizza",
    "category": "Frozen",
    "size": "each",
    "basePrice": 6.99
  },
  {
    "id": "frozen-pizza-cauliflower",
    "name": "Cauliflower Crust Pizza",
    "category": "Frozen",
    "size": "each",
    "basePrice": 8.99
  },
  {
    "id": "frozen-veggies-mixed",
    "name": "Mixed Vegetables, Frozen",
    "category": "Frozen",
    "size": "12 oz",
    "basePrice": 2.49
  },
  {
    "id": "frozen-broccoli",
    "name": "Frozen Broccoli",
    "category": "Frozen",
    "size": "12 oz",
    "basePrice": 2.49
  },
  {
    "id": "frozen-peas",
    "name": "Frozen Peas",
    "category": "Frozen",
    "size": "12 oz",
    "basePrice": 2.49
  },
  {
    "id": "frozen-corn",
    "name": "Frozen Corn",
    "category": "Frozen",
    "size": "12 oz",
    "basePrice": 2.49
  },
  {
    "id": "frozen-cauliflower-rice",
    "name": "Cauliflower Rice, Frozen",
    "category": "Frozen",
    "size": "12 oz",
    "basePrice": 3.49
  },
  {
    "id": "frozen-edamame",
    "name": "Edamame, Frozen",
    "category": "Frozen",
    "size": "10 oz",
    "basePrice": 3.49
  },
  {
    "id": "ice-cream-vanilla",
    "name": "Vanilla Ice Cream",
    "category": "Frozen",
    "size": "48 oz",
    "basePrice": 6.99
  },
  {
    "id": "ice-cream-chocolate",
    "name": "Chocolate Ice Cream",
    "category": "Frozen",
    "size": "48 oz",
    "basePrice": 6.99
  },
  {
    "id": "frozen-waffles",
    "name": "Frozen Waffles",
    "category": "Frozen",
    "size": "12 ct",
    "basePrice": 3.99
  },
  {
    "id": "frozen-burritos",
    "name": "Frozen Burritos",
    "category": "Frozen",
    "size": "8 ct",
    "basePrice": 6.99
  },
  {
    "id": "tortilla-chips",
    "name": "Tortilla Chips",
    "category": "Snacks",
    "size": "13 oz",
    "basePrice": 3.99
  },
  {
    "id": "potato-chips",
    "name": "Potato Chips",
    "category": "Snacks",
    "size": "8 oz",
    "basePrice": 3.99
  },
  {
    "id": "popcorn",
    "name": "Microwave Popcorn",
    "category": "Snacks",
    "size": "6 ct",
    "basePrice": 4.99
  },
  {
    "id": "pretzels",
    "name": "Pretzels",
    "category": "Snacks",
    "size": "16 oz",
    "basePrice": 3.49
  },
  {
    "id": "granola-bars",
    "name": "Granola Bars",
    "category": "Snacks",
    "size": "12 ct",
    "basePrice": 5.99
  },
  {
    "id": "protein-bars",
    "name": "Protein Bars",
    "category": "Snacks",
    "size": "12 ct",
    "basePrice": 12.99
  },
  {
    "id": "mixed-nuts",
    "name": "Mixed Nuts",
    "category": "Snacks",
    "size": "16 oz",
    "basePrice": 10.99
  },
  {
    "id": "almonds",
    "name": "Raw Almonds",
    "category": "Snacks",
    "size": "16 oz",
    "basePrice": 8.99
  },
  {
    "id": "cashews",
    "name": "Cashews",
    "category": "Snacks",
    "size": "16 oz",
    "basePrice": 11.99
  },
  {
    "id": "trail-mix",
    "name": "Trail Mix",
    "category": "Snacks",
    "size": "16 oz",
    "basePrice": 7.99
  },
  {
    "id": "hummus",
    "name": "Classic Hummus",
    "category": "Snacks",
    "size": "10 oz",
    "basePrice": 4.49
  },
  {
    "id": "guacamole",
    "name": "Guacamole",
    "category": "Snacks",
    "size": "8 oz",
    "basePrice": 4.99
  },
  {
    "id": "cookies-oreo",
    "name": "Sandwich Cookies",
    "category": "Snacks",
    "size": "14.3 oz",
    "basePrice": 4.49
  },
  {
    "id": "crackers-ritz",
    "name": "Butter Crackers",
    "category": "Snacks",
    "size": "13.7 oz",
    "basePrice": 4.49
  },
  {
    "id": "crackers-saltine",
    "name": "Saltine Crackers",
    "category": "Snacks",
    "size": "16 oz",
    "basePrice": 3.99
  },
  {
    "id": "cereal-cheerios",
    "name": "Oat Cereal",
    "category": "Breakfast",
    "size": "18 oz",
    "basePrice": 5.99
  },
  {
    "id": "cereal-granola",
    "name": "Granola",
    "category": "Breakfast",
    "size": "12 oz",
    "basePrice": 5.99
  },
  {
    "id": "cereal-bran-flakes",
    "name": "Bran Flakes",
    "category": "Breakfast",
    "size": "17 oz",
    "basePrice": 4.49
  },
  {
    "id": "pancake-mix",
    "name": "Pancake Mix",
    "category": "Breakfast",
    "size": "32 oz",
    "basePrice": 4.99
  },
  {
    "id": "syrup-pancake",
    "name": "Pancake Syrup",
    "category": "Breakfast",
    "size": "24 oz",
    "basePrice": 3.99
  },
  {
    "id": "jam-strawberry",
    "name": "Strawberry Jam",
    "category": "Breakfast",
    "size": "18 oz",
    "basePrice": 3.99
  },
  {
    "id": "jam-grape",
    "name": "Grape Jelly",
    "category": "Breakfast",
    "size": "18 oz",
    "basePrice": 3.49
  },
  {
    "id": "paper-towels",
    "name": "Paper Towels",
    "category": "Household",
    "size": "6 rolls",
    "basePrice": 12.99
  },
  {
    "id": "toilet-paper",
    "name": "Toilet Paper",
    "category": "Household",
    "size": "12 rolls",
    "basePrice": 14.99
  },
  {
    "id": "dish-soap",
    "name": "Dish Soap",
    "category": "Household",
    "size": "22 oz",
    "basePrice": 3.99
  },
  {
    "id": "laundry-detergent",
    "name": "Laundry Detergent",
    "category": "Household",
    "size": "92 oz",
    "basePrice": 12.99
  },
  {
    "id": "trash-bags",
    "name": "Tall Kitchen Trash Bags",
    "category": "Household",
    "size": "50 ct",
    "basePrice": 9.99
  },
  {
    "id": "foil",
    "name": "Aluminum Foil",
    "category": "Household",
    "size": "75 sq ft",
    "basePrice": 4.99
  },
  {
    "id": "plastic-wrap",
    "name": "Plastic Wrap",
    "category": "Household",
    "size": "200 sq ft",
    "basePrice": 3.99
  },
  {
    "id": "parchment-paper",
    "name": "Parchment Paper",
    "category": "Household",
    "size": "50 sq ft",
    "basePrice": 4.49
  },
  {
    "id": "ziploc-gallon",
    "name": "Storage Bags, Gallon",
    "category": "Household",
    "size": "40 ct",
    "basePrice": 5.99
  },
  {
    "id": "ziploc-sandwich",
    "name": "Sandwich Bags",
    "category": "Household",
    "size": "150 ct",
    "basePrice": 4.99
  }
];

// Per-store availability + pricing for each canonical item.
// Shape: { [canonicalId]: [{storeId, price}, ...] }
const STORE_AVAILABILITY = {
  "organic-whole-milk": [
    {
      "storeId": "publix",
      "price": 6.43
    },
    {
      "storeId": "aldi",
      "price": 5.52
    },
    {
      "storeId": "sprouts",
      "price": 7.2
    },
    {
      "storeId": "freshMarket",
      "price": 8.1
    }
  ],
  "organic-whole-milk-half-gal": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.36
    },
    {
      "storeId": "sprouts",
      "price": 4.47
    },
    {
      "storeId": "freshMarket",
      "price": 5.03
    }
  ],
  "whole-milk": [
    {
      "storeId": "publix",
      "price": 4.02
    },
    {
      "storeId": "aldi",
      "price": 3.1
    },
    {
      "storeId": "sprouts",
      "price": 4.22
    },
    {
      "storeId": "freshMarket",
      "price": 4.76
    }
  ],
  "skim-milk": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.08
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.83
    }
  ],
  "two-percent-milk": [
    {
      "storeId": "publix",
      "price": 4.02
    },
    {
      "storeId": "aldi",
      "price": 3.11
    },
    {
      "storeId": "sprouts",
      "price": 4.22
    },
    {
      "storeId": "freshMarket",
      "price": 4.77
    }
  ],
  "oat-milk": [
    {
      "storeId": "publix",
      "price": 5.53
    },
    {
      "storeId": "aldi",
      "price": 4.27
    },
    {
      "storeId": "sprouts",
      "price": 5.81
    },
    {
      "storeId": "freshMarket",
      "price": 6.56
    }
  ],
  "almond-milk": [
    {
      "storeId": "publix",
      "price": 4.49
    },
    {
      "storeId": "aldi",
      "price": 3.53
    },
    {
      "storeId": "sprouts",
      "price": 4.71
    },
    {
      "storeId": "freshMarket",
      "price": 5.43
    }
  ],
  "coconut-milk": [
    {
      "storeId": "publix",
      "price": 5.02
    },
    {
      "storeId": "aldi",
      "price": 3.88
    },
    {
      "storeId": "sprouts",
      "price": 5.28
    },
    {
      "storeId": "freshMarket",
      "price": 5.96
    }
  ],
  "heavy-cream": [
    {
      "storeId": "publix",
      "price": 4.47
    },
    {
      "storeId": "aldi",
      "price": 3.52
    },
    {
      "storeId": "sprouts",
      "price": 4.7
    },
    {
      "storeId": "freshMarket",
      "price": 5.41
    }
  ],
  "half-and-half": [
    {
      "storeId": "publix",
      "price": 4.03
    },
    {
      "storeId": "aldi",
      "price": 3.11
    },
    {
      "storeId": "sprouts",
      "price": 4.23
    },
    {
      "storeId": "freshMarket",
      "price": 4.77
    }
  ],
  "unsalted-butter": [
    {
      "storeId": "publix",
      "price": 5.5
    },
    {
      "storeId": "aldi",
      "price": 4.24
    },
    {
      "storeId": "sprouts",
      "price": 5.77
    },
    {
      "storeId": "freshMarket",
      "price": 6.65
    }
  ],
  "salted-butter": [
    {
      "storeId": "publix",
      "price": 5.46
    },
    {
      "storeId": "aldi",
      "price": 4.3
    },
    {
      "storeId": "sprouts",
      "price": 5.73
    },
    {
      "storeId": "freshMarket",
      "price": 6.6
    }
  ],
  "organic-butter": [
    {
      "storeId": "publix",
      "price": 7.96
    },
    {
      "storeId": "aldi",
      "price": 6.83
    },
    {
      "storeId": "sprouts",
      "price": 8.91
    },
    {
      "storeId": "freshMarket",
      "price": 10.03
    }
  ],
  "greek-yogurt": [
    {
      "storeId": "publix",
      "price": 6.01
    },
    {
      "storeId": "aldi",
      "price": 4.64
    },
    {
      "storeId": "sprouts",
      "price": 6.31
    },
    {
      "storeId": "freshMarket",
      "price": 7.12
    }
  ],
  "greek-yogurt-vanilla": [
    {
      "storeId": "publix",
      "price": 5.94
    },
    {
      "storeId": "aldi",
      "price": 4.68
    },
    {
      "storeId": "sprouts",
      "price": 6.23
    },
    {
      "storeId": "freshMarket",
      "price": 7.18
    }
  ],
  "greek-yogurt-single": [
    {
      "storeId": "publix",
      "price": 1.48
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.55
    },
    {
      "storeId": "freshMarket",
      "price": 1.78
    }
  ],
  "regular-yogurt": [
    {
      "storeId": "publix",
      "price": 4.45
    },
    {
      "storeId": "aldi",
      "price": 3.51
    },
    {
      "storeId": "sprouts",
      "price": 4.67
    },
    {
      "storeId": "freshMarket",
      "price": 5.38
    }
  ],
  "cottage-cheese": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.14
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.82
    }
  ],
  "cream-cheese": [
    {
      "storeId": "publix",
      "price": 3.48
    },
    {
      "storeId": "aldi",
      "price": 2.74
    },
    {
      "storeId": "sprouts",
      "price": 3.65
    },
    {
      "storeId": "freshMarket",
      "price": 4.2
    }
  ],
  "cheddar-shredded": [
    {
      "storeId": "publix",
      "price": 4.01
    },
    {
      "storeId": "aldi",
      "price": 3.1
    },
    {
      "storeId": "sprouts",
      "price": 4.21
    },
    {
      "storeId": "freshMarket",
      "price": 4.75
    }
  ],
  "mozzarella-shredded": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.14
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.82
    }
  ],
  "parmesan-grated": [
    {
      "storeId": "publix",
      "price": 6
    },
    {
      "storeId": "aldi",
      "price": 4.63
    },
    {
      "storeId": "sprouts",
      "price": 6.3
    },
    {
      "storeId": "freshMarket",
      "price": 7.25
    }
  ],
  "feta-crumbled": [
    {
      "storeId": "publix",
      "price": 3.98
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.18
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "string-cheese": [
    {
      "storeId": "publix",
      "price": 4.99
    },
    {
      "storeId": "aldi",
      "price": 3.93
    },
    {
      "storeId": "sprouts",
      "price": 5.23
    },
    {
      "storeId": "freshMarket",
      "price": 6.03
    }
  ],
  "sour-cream": [
    {
      "storeId": "publix",
      "price": 2.97
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.11
    },
    {
      "storeId": "freshMarket",
      "price": 3.59
    }
  ],
  "free-range-eggs": [
    {
      "storeId": "publix",
      "price": 5.52
    },
    {
      "storeId": "aldi",
      "price": 4.64
    },
    {
      "storeId": "sprouts",
      "price": 6.18
    },
    {
      "storeId": "freshMarket",
      "price": 6.81
    }
  ],
  "free-range-eggs-18": [
    {
      "storeId": "publix",
      "price": 7.95
    },
    {
      "storeId": "aldi",
      "price": 6.83
    },
    {
      "storeId": "sprouts",
      "price": 8.9
    },
    {
      "storeId": "freshMarket",
      "price": 10.02
    }
  ],
  "large-eggs-12": [
    {
      "storeId": "publix",
      "price": 3.5
    },
    {
      "storeId": "aldi",
      "price": 2.71
    },
    {
      "storeId": "sprouts",
      "price": 3.68
    },
    {
      "storeId": "freshMarket",
      "price": 4.15
    }
  ],
  "large-eggs-18": [
    {
      "storeId": "publix",
      "price": 4.94
    },
    {
      "storeId": "aldi",
      "price": 3.89
    },
    {
      "storeId": "sprouts",
      "price": 5.19
    },
    {
      "storeId": "freshMarket",
      "price": 5.98
    }
  ],
  "organic-eggs": [
    {
      "storeId": "publix",
      "price": 6.96
    },
    {
      "storeId": "aldi",
      "price": 5.98
    },
    {
      "storeId": "sprouts",
      "price": 7.8
    },
    {
      "storeId": "freshMarket",
      "price": 8.77
    }
  ],
  "egg-whites": [
    {
      "storeId": "publix",
      "price": 4.5
    },
    {
      "storeId": "aldi",
      "price": 3.47
    },
    {
      "storeId": "sprouts",
      "price": 4.72
    },
    {
      "storeId": "freshMarket",
      "price": 5.33
    }
  ],
  "baby-spinach": [
    {
      "storeId": "publix",
      "price": 2.96
    },
    {
      "storeId": "aldi",
      "price": 2.33
    },
    {
      "storeId": "sprouts",
      "price": 3.11
    },
    {
      "storeId": "freshMarket",
      "price": 3.58
    }
  ],
  "organic-baby-spinach": [
    {
      "storeId": "publix",
      "price": 4.47
    },
    {
      "storeId": "aldi",
      "price": 3.84
    },
    {
      "storeId": "sprouts",
      "price": 5
    },
    {
      "storeId": "freshMarket",
      "price": 5.63
    }
  ],
  "romaine-hearts": [
    {
      "storeId": "publix",
      "price": 3.52
    },
    {
      "storeId": "aldi",
      "price": 2.72
    },
    {
      "storeId": "sprouts",
      "price": 3.7
    },
    {
      "storeId": "freshMarket",
      "price": 4.18
    }
  ],
  "kale": [
    {
      "storeId": "publix",
      "price": 2.5
    },
    {
      "storeId": "aldi",
      "price": 1.93
    },
    {
      "storeId": "sprouts",
      "price": 2.62
    },
    {
      "storeId": "freshMarket",
      "price": 2.96
    }
  ],
  "arugula": [
    {
      "storeId": "publix",
      "price": 4
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.2
    },
    {
      "storeId": "freshMarket",
      "price": 4.74
    }
  ],
  "mixed-greens": [
    {
      "storeId": "publix",
      "price": 3.97
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.8
    }
  ],
  "avocados": [
    {
      "storeId": "publix",
      "price": 1.99
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.09
    },
    {
      "storeId": "freshMarket",
      "price": 2.4
    }
  ],
  "organic-avocados": [
    {
      "storeId": "publix",
      "price": 2.49
    },
    {
      "storeId": "aldi",
      "price": 2.1
    },
    {
      "storeId": "sprouts",
      "price": 2.79
    },
    {
      "storeId": "freshMarket",
      "price": 3.08
    }
  ],
  "bananas": [
    {
      "storeId": "publix",
      "price": 0.59
    },
    {
      "storeId": "aldi",
      "price": 0.46
    },
    {
      "storeId": "sprouts",
      "price": 0.62
    },
    {
      "storeId": "freshMarket",
      "price": 0.71
    }
  ],
  "organic-bananas": [
    {
      "storeId": "publix",
      "price": 0.79
    },
    {
      "storeId": "aldi",
      "price": 0.68
    },
    {
      "storeId": "sprouts",
      "price": 0.88
    },
    {
      "storeId": "freshMarket",
      "price": 0.99
    }
  ],
  "apples-honeycrisp": [
    {
      "storeId": "publix",
      "price": 2.97
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.11
    },
    {
      "storeId": "freshMarket",
      "price": 3.59
    }
  ],
  "apples-gala": [
    {
      "storeId": "publix",
      "price": 2
    },
    {
      "storeId": "aldi",
      "price": 1.54
    },
    {
      "storeId": "sprouts",
      "price": 2.1
    },
    {
      "storeId": "freshMarket",
      "price": 2.37
    }
  ],
  "apples-granny": [
    {
      "storeId": "publix",
      "price": 1.98
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.08
    },
    {
      "storeId": "freshMarket",
      "price": 2.4
    }
  ],
  "strawberries": [
    {
      "storeId": "publix",
      "price": 4.99
    },
    {
      "storeId": "aldi",
      "price": 3.93
    },
    {
      "storeId": "sprouts",
      "price": 5.23
    },
    {
      "storeId": "freshMarket",
      "price": 6.03
    }
  ],
  "blueberries": [
    {
      "storeId": "publix",
      "price": 4
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.2
    },
    {
      "storeId": "freshMarket",
      "price": 4.74
    }
  ],
  "raspberries": [
    {
      "storeId": "publix",
      "price": 4.97
    },
    {
      "storeId": "aldi",
      "price": 3.92
    },
    {
      "storeId": "sprouts",
      "price": 5.22
    },
    {
      "storeId": "freshMarket",
      "price": 6.01
    }
  ],
  "blackberries": [
    {
      "storeId": "publix",
      "price": 4.52
    },
    {
      "storeId": "aldi",
      "price": 3.49
    },
    {
      "storeId": "sprouts",
      "price": 4.75
    },
    {
      "storeId": "freshMarket",
      "price": 5.36
    }
  ],
  "grapes-red": [
    {
      "storeId": "publix",
      "price": 2.97
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.11
    },
    {
      "storeId": "freshMarket",
      "price": 3.59
    }
  ],
  "grapes-green": [
    {
      "storeId": "publix",
      "price": 3.01
    },
    {
      "storeId": "aldi",
      "price": 2.32
    },
    {
      "storeId": "sprouts",
      "price": 3.16
    },
    {
      "storeId": "freshMarket",
      "price": 3.57
    }
  ],
  "oranges": [
    {
      "storeId": "publix",
      "price": 6
    },
    {
      "storeId": "aldi",
      "price": 4.63
    },
    {
      "storeId": "sprouts",
      "price": 6.3
    },
    {
      "storeId": "freshMarket",
      "price": 7.25
    }
  ],
  "lemons": [
    {
      "storeId": "publix",
      "price": 0.79
    },
    {
      "storeId": "aldi",
      "price": 0.61
    },
    {
      "storeId": "sprouts",
      "price": 0.83
    },
    {
      "storeId": "freshMarket",
      "price": 0.94
    }
  ],
  "limes": [
    {
      "storeId": "publix",
      "price": 0.49
    },
    {
      "storeId": "aldi",
      "price": 0.38
    },
    {
      "storeId": "sprouts",
      "price": 0.52
    },
    {
      "storeId": "freshMarket",
      "price": 0.59
    }
  ],
  "cherry-tomatoes": [
    {
      "storeId": "publix",
      "price": 3.5
    },
    {
      "storeId": "aldi",
      "price": 2.71
    },
    {
      "storeId": "sprouts",
      "price": 3.68
    },
    {
      "storeId": "freshMarket",
      "price": 4.15
    }
  ],
  "roma-tomatoes": [
    {
      "storeId": "publix",
      "price": 1.99
    },
    {
      "storeId": "aldi",
      "price": 1.54
    },
    {
      "storeId": "sprouts",
      "price": 2.09
    },
    {
      "storeId": "freshMarket",
      "price": 2.36
    }
  ],
  "tomatoes-vine": [
    {
      "storeId": "publix",
      "price": 3
    },
    {
      "storeId": "aldi",
      "price": 2.32
    },
    {
      "storeId": "sprouts",
      "price": 3.16
    },
    {
      "storeId": "freshMarket",
      "price": 3.56
    }
  ],
  "cucumber": [
    {
      "storeId": "publix",
      "price": 1.3
    },
    {
      "storeId": "aldi",
      "price": 1
    },
    {
      "storeId": "sprouts",
      "price": 1.36
    },
    {
      "storeId": "freshMarket",
      "price": 1.54
    }
  ],
  "english-cucumber": [
    {
      "storeId": "publix",
      "price": 2.48
    },
    {
      "storeId": "aldi",
      "price": 1.95
    },
    {
      "storeId": "sprouts",
      "price": 2.6
    },
    {
      "storeId": "freshMarket",
      "price": 3
    }
  ],
  "bell-peppers-red": [
    {
      "storeId": "publix",
      "price": 1.98
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.08
    },
    {
      "storeId": "freshMarket",
      "price": 2.4
    }
  ],
  "bell-peppers-green": [
    {
      "storeId": "publix",
      "price": 1.28
    },
    {
      "storeId": "aldi",
      "price": 1.01
    },
    {
      "storeId": "sprouts",
      "price": 1.34
    },
    {
      "storeId": "freshMarket",
      "price": 1.55
    }
  ],
  "bell-peppers-yellow": [
    {
      "storeId": "publix",
      "price": 1.97
    },
    {
      "storeId": "aldi",
      "price": 1.55
    },
    {
      "storeId": "sprouts",
      "price": 2.07
    },
    {
      "storeId": "freshMarket",
      "price": 2.38
    }
  ],
  "onions-yellow": [
    {
      "storeId": "publix",
      "price": 3.51
    },
    {
      "storeId": "aldi",
      "price": 2.71
    },
    {
      "storeId": "sprouts",
      "price": 3.68
    },
    {
      "storeId": "freshMarket",
      "price": 4.16
    }
  ],
  "onions-red": [
    {
      "storeId": "publix",
      "price": 1.48
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.55
    },
    {
      "storeId": "freshMarket",
      "price": 1.79
    }
  ],
  "onions-sweet": [
    {
      "storeId": "publix",
      "price": 2.01
    },
    {
      "storeId": "aldi",
      "price": 1.55
    },
    {
      "storeId": "sprouts",
      "price": 2.11
    },
    {
      "storeId": "freshMarket",
      "price": 2.38
    }
  ],
  "garlic": [
    {
      "storeId": "publix",
      "price": 0.99
    },
    {
      "storeId": "aldi",
      "price": 0.78
    },
    {
      "storeId": "sprouts",
      "price": 1.04
    },
    {
      "storeId": "freshMarket",
      "price": 1.19
    }
  ],
  "ginger": [
    {
      "storeId": "publix",
      "price": 5.02
    },
    {
      "storeId": "aldi",
      "price": 3.88
    },
    {
      "storeId": "sprouts",
      "price": 5.27
    },
    {
      "storeId": "freshMarket",
      "price": 5.95
    }
  ],
  "carrots": [
    {
      "storeId": "publix",
      "price": 2.48
    },
    {
      "storeId": "aldi",
      "price": 1.95
    },
    {
      "storeId": "sprouts",
      "price": 2.6
    },
    {
      "storeId": "freshMarket",
      "price": 3
    }
  ],
  "baby-carrots": [
    {
      "storeId": "publix",
      "price": 1.98
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.08
    },
    {
      "storeId": "freshMarket",
      "price": 2.4
    }
  ],
  "celery": [
    {
      "storeId": "publix",
      "price": 2.97
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.12
    },
    {
      "storeId": "freshMarket",
      "price": 3.6
    }
  ],
  "broccoli": [
    {
      "storeId": "publix",
      "price": 2.48
    },
    {
      "storeId": "aldi",
      "price": 1.95
    },
    {
      "storeId": "sprouts",
      "price": 2.6
    },
    {
      "storeId": "freshMarket",
      "price": 3
    }
  ],
  "cauliflower": [
    {
      "storeId": "publix",
      "price": 3.95
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.15
    },
    {
      "storeId": "freshMarket",
      "price": 4.78
    }
  ],
  "brussels-sprouts": [
    {
      "storeId": "publix",
      "price": 3.98
    },
    {
      "storeId": "aldi",
      "price": 3.14
    },
    {
      "storeId": "sprouts",
      "price": 4.18
    },
    {
      "storeId": "freshMarket",
      "price": 4.82
    }
  ],
  "asparagus": [
    {
      "storeId": "publix",
      "price": 4.98
    },
    {
      "storeId": "aldi",
      "price": 3.92
    },
    {
      "storeId": "sprouts",
      "price": 5.22
    },
    {
      "storeId": "freshMarket",
      "price": 6.02
    }
  ],
  "sweet-potato": [
    {
      "storeId": "publix",
      "price": 1.48
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.55
    },
    {
      "storeId": "freshMarket",
      "price": 1.78
    }
  ],
  "russet-potatoes": [
    {
      "storeId": "publix",
      "price": 3.96
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.79
    }
  ],
  "yukon-potatoes": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.08
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.83
    }
  ],
  "zucchini": [
    {
      "storeId": "publix",
      "price": 1.97
    },
    {
      "storeId": "aldi",
      "price": 1.55
    },
    {
      "storeId": "sprouts",
      "price": 2.07
    },
    {
      "storeId": "freshMarket",
      "price": 2.39
    }
  ],
  "yellow-squash": [
    {
      "storeId": "publix",
      "price": 2
    },
    {
      "storeId": "aldi",
      "price": 1.54
    },
    {
      "storeId": "sprouts",
      "price": 2.1
    },
    {
      "storeId": "freshMarket",
      "price": 2.37
    }
  ],
  "mushrooms-white": [
    {
      "storeId": "publix",
      "price": 2.5
    },
    {
      "storeId": "aldi",
      "price": 1.93
    },
    {
      "storeId": "sprouts",
      "price": 2.63
    },
    {
      "storeId": "freshMarket",
      "price": 2.97
    }
  ],
  "mushrooms-portobello": [
    {
      "storeId": "publix",
      "price": 4.45
    },
    {
      "storeId": "aldi",
      "price": 3.5
    },
    {
      "storeId": "sprouts",
      "price": 4.67
    },
    {
      "storeId": "freshMarket",
      "price": 5.38
    }
  ],
  "corn-ears": [
    {
      "storeId": "publix",
      "price": 3.97
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.17
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "jalapenos": [
    {
      "storeId": "publix",
      "price": 2
    },
    {
      "storeId": "aldi",
      "price": 1.55
    },
    {
      "storeId": "sprouts",
      "price": 2.1
    },
    {
      "storeId": "freshMarket",
      "price": 2.38
    }
  ],
  "cilantro": [
    {
      "storeId": "publix",
      "price": 0.98
    },
    {
      "storeId": "aldi",
      "price": 0.77
    },
    {
      "storeId": "sprouts",
      "price": 1.03
    },
    {
      "storeId": "freshMarket",
      "price": 1.19
    }
  ],
  "parsley": [
    {
      "storeId": "publix",
      "price": 0.99
    },
    {
      "storeId": "aldi",
      "price": 0.78
    },
    {
      "storeId": "sprouts",
      "price": 1.04
    },
    {
      "storeId": "freshMarket",
      "price": 1.2
    }
  ],
  "basil": [
    {
      "storeId": "publix",
      "price": 2.97
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.12
    },
    {
      "storeId": "freshMarket",
      "price": 3.59
    }
  ],
  "mint": [
    {
      "storeId": "publix",
      "price": 2.96
    },
    {
      "storeId": "aldi",
      "price": 2.33
    },
    {
      "storeId": "sprouts",
      "price": 3.11
    },
    {
      "storeId": "freshMarket",
      "price": 3.58
    }
  ],
  "chicken-breast": [
    {
      "storeId": "publix",
      "price": 6
    },
    {
      "storeId": "aldi",
      "price": 4.63
    },
    {
      "storeId": "sprouts",
      "price": 6.3
    },
    {
      "storeId": "freshMarket",
      "price": 7.25
    }
  ],
  "organic-chicken-breast": [
    {
      "storeId": "publix",
      "price": 10.04
    },
    {
      "storeId": "aldi",
      "price": 8.45
    },
    {
      "storeId": "sprouts",
      "price": 11.24
    },
    {
      "storeId": "freshMarket",
      "price": 12.4
    }
  ],
  "chicken-thighs": [
    {
      "storeId": "publix",
      "price": 4.52
    },
    {
      "storeId": "aldi",
      "price": 3.49
    },
    {
      "storeId": "sprouts",
      "price": 4.75
    },
    {
      "storeId": "freshMarket",
      "price": 5.36
    }
  ],
  "chicken-thighs-bone-in": [
    {
      "storeId": "publix",
      "price": 3.46
    },
    {
      "storeId": "aldi",
      "price": 2.73
    },
    {
      "storeId": "sprouts",
      "price": 3.64
    },
    {
      "storeId": "freshMarket",
      "price": 4.19
    }
  ],
  "whole-chicken": [
    {
      "storeId": "publix",
      "price": 10.02
    },
    {
      "storeId": "aldi",
      "price": 7.74
    },
    {
      "storeId": "sprouts",
      "price": 10.52
    },
    {
      "storeId": "freshMarket",
      "price": 11.88
    }
  ],
  "chicken-wings": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.2
    },
    {
      "storeId": "freshMarket",
      "price": 5.99
    }
  ],
  "ground-beef": [
    {
      "storeId": "publix",
      "price": 6.93
    },
    {
      "storeId": "aldi",
      "price": 5.46
    },
    {
      "storeId": "sprouts",
      "price": 7.28
    },
    {
      "storeId": "freshMarket",
      "price": 8.39
    }
  ],
  "ground-beef-93": [
    {
      "storeId": "publix",
      "price": 8.03
    },
    {
      "storeId": "aldi",
      "price": 6.2
    },
    {
      "storeId": "sprouts",
      "price": 8.43
    },
    {
      "storeId": "freshMarket",
      "price": 9.52
    }
  ],
  "ground-beef-80": [
    {
      "storeId": "publix",
      "price": 6
    },
    {
      "storeId": "aldi",
      "price": 4.63
    },
    {
      "storeId": "sprouts",
      "price": 6.3
    },
    {
      "storeId": "freshMarket",
      "price": 7.25
    }
  ],
  "organic-ground-beef": [
    {
      "storeId": "publix",
      "price": 10.95
    },
    {
      "storeId": "aldi",
      "price": 9.4
    },
    {
      "storeId": "sprouts",
      "price": 12.26
    },
    {
      "storeId": "freshMarket",
      "price": 13.79
    }
  ],
  "ground-turkey": [
    {
      "storeId": "publix",
      "price": 5.52
    },
    {
      "storeId": "aldi",
      "price": 4.27
    },
    {
      "storeId": "sprouts",
      "price": 5.8
    },
    {
      "storeId": "freshMarket",
      "price": 6.55
    }
  ],
  "ground-turkey-lean": [
    {
      "storeId": "publix",
      "price": 6.54
    },
    {
      "storeId": "aldi",
      "price": 5.05
    },
    {
      "storeId": "sprouts",
      "price": 6.86
    },
    {
      "storeId": "freshMarket",
      "price": 7.75
    }
  ],
  "sirloin-steak": [
    {
      "storeId": "publix",
      "price": 11.98
    },
    {
      "storeId": "aldi",
      "price": 9.44
    },
    {
      "storeId": "sprouts",
      "price": 12.58
    },
    {
      "storeId": "freshMarket",
      "price": 14.49
    }
  ],
  "ribeye-steak": [
    {
      "storeId": "publix",
      "price": 17.83
    },
    {
      "storeId": "aldi",
      "price": 14.77
    },
    {
      "storeId": "sprouts",
      "price": 19.25
    },
    {
      "storeId": "freshMarket",
      "price": 24.26
    }
  ],
  "ny-strip": [
    {
      "storeId": "publix",
      "price": 16.12
    },
    {
      "storeId": "aldi",
      "price": 13.09
    },
    {
      "storeId": "sprouts",
      "price": 17.41
    },
    {
      "storeId": "freshMarket",
      "price": 21.5
    }
  ],
  "pork-chops": [
    {
      "storeId": "publix",
      "price": 4.99
    },
    {
      "storeId": "aldi",
      "price": 3.85
    },
    {
      "storeId": "sprouts",
      "price": 5.24
    },
    {
      "storeId": "freshMarket",
      "price": 6.04
    }
  ],
  "pork-tenderloin": [
    {
      "storeId": "publix",
      "price": 6.96
    },
    {
      "storeId": "aldi",
      "price": 5.48
    },
    {
      "storeId": "sprouts",
      "price": 7.3
    },
    {
      "storeId": "freshMarket",
      "price": 8.41
    }
  ],
  "pork-ribs": [
    {
      "storeId": "publix",
      "price": 5.94
    },
    {
      "storeId": "aldi",
      "price": 4.68
    },
    {
      "storeId": "sprouts",
      "price": 6.23
    },
    {
      "storeId": "freshMarket",
      "price": 7.18
    }
  ],
  "bacon": [
    {
      "storeId": "publix",
      "price": 7.02
    },
    {
      "storeId": "aldi",
      "price": 5.42
    },
    {
      "storeId": "sprouts",
      "price": 7.38
    },
    {
      "storeId": "freshMarket",
      "price": 8.33
    }
  ],
  "turkey-bacon": [
    {
      "storeId": "publix",
      "price": 5.02
    },
    {
      "storeId": "aldi",
      "price": 3.88
    },
    {
      "storeId": "sprouts",
      "price": 5.27
    },
    {
      "storeId": "freshMarket",
      "price": 5.95
    }
  ],
  "deli-turkey": [
    {
      "storeId": "publix",
      "price": 10.04
    },
    {
      "storeId": "aldi",
      "price": 7.75
    },
    {
      "storeId": "sprouts",
      "price": 10.54
    },
    {
      "storeId": "freshMarket",
      "price": 11.9
    }
  ],
  "deli-ham": [
    {
      "storeId": "publix",
      "price": 8.98
    },
    {
      "storeId": "aldi",
      "price": 7.08
    },
    {
      "storeId": "sprouts",
      "price": 9.43
    },
    {
      "storeId": "freshMarket",
      "price": 10.86
    }
  ],
  "sausage-italian": [
    {
      "storeId": "publix",
      "price": 5.98
    },
    {
      "storeId": "aldi",
      "price": 4.71
    },
    {
      "storeId": "sprouts",
      "price": 6.28
    },
    {
      "storeId": "freshMarket",
      "price": 7.23
    }
  ],
  "sausage-breakfast": [
    {
      "storeId": "publix",
      "price": 5.02
    },
    {
      "storeId": "aldi",
      "price": 3.88
    },
    {
      "storeId": "sprouts",
      "price": 5.28
    },
    {
      "storeId": "freshMarket",
      "price": 5.96
    }
  ],
  "atlantic-salmon": [
    {
      "storeId": "publix",
      "price": 13.89
    },
    {
      "storeId": "sprouts",
      "price": 14.59
    },
    {
      "storeId": "freshMarket",
      "price": 16.8
    }
  ],
  "wild-salmon": [
    {
      "storeId": "publix",
      "price": 19.93
    },
    {
      "storeId": "sprouts",
      "price": 21.52
    },
    {
      "storeId": "freshMarket",
      "price": 27.12
    }
  ],
  "tilapia": [
    {
      "storeId": "publix",
      "price": 7.91
    },
    {
      "storeId": "sprouts",
      "price": 8.31
    },
    {
      "storeId": "freshMarket",
      "price": 9.57
    }
  ],
  "cod": [
    {
      "storeId": "publix",
      "price": 11.99
    },
    {
      "storeId": "sprouts",
      "price": 12.59
    },
    {
      "storeId": "freshMarket",
      "price": 14.5
    }
  ],
  "shrimp-large": [
    {
      "storeId": "publix",
      "price": 11.95
    },
    {
      "storeId": "sprouts",
      "price": 12.55
    },
    {
      "storeId": "freshMarket",
      "price": 14.46
    }
  ],
  "shrimp-jumbo": [
    {
      "storeId": "publix",
      "price": 16.91
    },
    {
      "storeId": "sprouts",
      "price": 18.26
    },
    {
      "storeId": "freshMarket",
      "price": 23.01
    }
  ],
  "tuna-ahi": [
    {
      "storeId": "publix",
      "price": 22.01
    },
    {
      "storeId": "sprouts",
      "price": 23.77
    },
    {
      "storeId": "freshMarket",
      "price": 29.95
    }
  ],
  "scallops": [
    {
      "storeId": "publix",
      "price": 22.88
    },
    {
      "storeId": "sprouts",
      "price": 24.71
    },
    {
      "storeId": "freshMarket",
      "price": 31.13
    }
  ],
  "halibut": [
    {
      "storeId": "publix",
      "price": 24.87
    },
    {
      "storeId": "sprouts",
      "price": 26.85
    },
    {
      "storeId": "freshMarket",
      "price": 33.84
    }
  ],
  "bread": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.08
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.83
    }
  ],
  "sourdough-bread": [
    {
      "storeId": "publix",
      "price": 4.98
    },
    {
      "storeId": "aldi",
      "price": 3.92
    },
    {
      "storeId": "sprouts",
      "price": 5.22
    },
    {
      "storeId": "freshMarket",
      "price": 6.02
    }
  ],
  "sourdough-boule": [
    {
      "storeId": "publix",
      "price": 7
    },
    {
      "storeId": "aldi",
      "price": 5.41
    },
    {
      "storeId": "sprouts",
      "price": 7.35
    },
    {
      "storeId": "freshMarket",
      "price": 8.3
    }
  ],
  "white-bread": [
    {
      "storeId": "publix",
      "price": 3.46
    },
    {
      "storeId": "aldi",
      "price": 2.72
    },
    {
      "storeId": "sprouts",
      "price": 3.63
    },
    {
      "storeId": "freshMarket",
      "price": 4.18
    }
  ],
  "multigrain-bread": [
    {
      "storeId": "publix",
      "price": 4.53
    },
    {
      "storeId": "aldi",
      "price": 3.5
    },
    {
      "storeId": "sprouts",
      "price": 4.76
    },
    {
      "storeId": "freshMarket",
      "price": 5.37
    }
  ],
  "bagels-plain": [
    {
      "storeId": "publix",
      "price": 4.53
    },
    {
      "storeId": "aldi",
      "price": 3.5
    },
    {
      "storeId": "sprouts",
      "price": 4.76
    },
    {
      "storeId": "freshMarket",
      "price": 5.37
    }
  ],
  "bagels-everything": [
    {
      "storeId": "publix",
      "price": 4.45
    },
    {
      "storeId": "aldi",
      "price": 3.5
    },
    {
      "storeId": "sprouts",
      "price": 4.67
    },
    {
      "storeId": "freshMarket",
      "price": 5.38
    }
  ],
  "english-muffins": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.08
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.83
    }
  ],
  "tortillas-flour": [
    {
      "storeId": "publix",
      "price": 3.48
    },
    {
      "storeId": "aldi",
      "price": 2.74
    },
    {
      "storeId": "sprouts",
      "price": 3.65
    },
    {
      "storeId": "freshMarket",
      "price": 4.21
    }
  ],
  "tortillas-corn": [
    {
      "storeId": "publix",
      "price": 2.99
    },
    {
      "storeId": "aldi",
      "price": 2.35
    },
    {
      "storeId": "sprouts",
      "price": 3.14
    },
    {
      "storeId": "freshMarket",
      "price": 3.61
    }
  ],
  "pita-bread": [
    {
      "storeId": "publix",
      "price": 3.47
    },
    {
      "storeId": "aldi",
      "price": 2.74
    },
    {
      "storeId": "sprouts",
      "price": 3.65
    },
    {
      "storeId": "freshMarket",
      "price": 4.2
    }
  ],
  "croissants": [
    {
      "storeId": "publix",
      "price": 6.03
    },
    {
      "storeId": "aldi",
      "price": 4.66
    },
    {
      "storeId": "sprouts",
      "price": 6.33
    },
    {
      "storeId": "freshMarket",
      "price": 7.15
    }
  ],
  "dinner-rolls": [
    {
      "storeId": "publix",
      "price": 3.95
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.15
    },
    {
      "storeId": "freshMarket",
      "price": 4.78
    }
  ],
  "olive-oil": [
    {
      "storeId": "publix",
      "price": 13.02
    },
    {
      "storeId": "aldi",
      "price": 10.05
    },
    {
      "storeId": "sprouts",
      "price": 13.67
    },
    {
      "storeId": "freshMarket",
      "price": 15.43
    }
  ],
  "olive-oil-large": [
    {
      "storeId": "publix",
      "price": 19.79
    },
    {
      "storeId": "aldi",
      "price": 15.59
    },
    {
      "storeId": "sprouts",
      "price": 20.78
    },
    {
      "storeId": "freshMarket",
      "price": 23.94
    }
  ],
  "avocado-oil": [
    {
      "storeId": "publix",
      "price": 9.91
    },
    {
      "storeId": "aldi",
      "price": 7.81
    },
    {
      "storeId": "sprouts",
      "price": 10.41
    },
    {
      "storeId": "freshMarket",
      "price": 11.99
    }
  ],
  "canola-oil": [
    {
      "storeId": "publix",
      "price": 5.5
    },
    {
      "storeId": "aldi",
      "price": 4.24
    },
    {
      "storeId": "sprouts",
      "price": 5.77
    },
    {
      "storeId": "freshMarket",
      "price": 6.65
    }
  ],
  "vegetable-oil": [
    {
      "storeId": "publix",
      "price": 5
    },
    {
      "storeId": "aldi",
      "price": 3.86
    },
    {
      "storeId": "sprouts",
      "price": 5.25
    },
    {
      "storeId": "freshMarket",
      "price": 5.93
    }
  ],
  "coconut-oil": [
    {
      "storeId": "publix",
      "price": 8.01
    },
    {
      "storeId": "aldi",
      "price": 6.18
    },
    {
      "storeId": "sprouts",
      "price": 8.41
    },
    {
      "storeId": "freshMarket",
      "price": 9.49
    }
  ],
  "balsamic-vinegar": [
    {
      "storeId": "publix",
      "price": 5.94
    },
    {
      "storeId": "aldi",
      "price": 4.68
    },
    {
      "storeId": "sprouts",
      "price": 6.23
    },
    {
      "storeId": "freshMarket",
      "price": 7.18
    }
  ],
  "apple-cider-vinegar": [
    {
      "storeId": "publix",
      "price": 3.98
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.18
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "white-vinegar": [
    {
      "storeId": "publix",
      "price": 2.51
    },
    {
      "storeId": "aldi",
      "price": 1.94
    },
    {
      "storeId": "sprouts",
      "price": 2.64
    },
    {
      "storeId": "freshMarket",
      "price": 2.98
    }
  ],
  "salt-sea": [
    {
      "storeId": "publix",
      "price": 4.01
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.21
    },
    {
      "storeId": "freshMarket",
      "price": 4.75
    }
  ],
  "salt-kosher": [
    {
      "storeId": "publix",
      "price": 4
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.2
    },
    {
      "storeId": "freshMarket",
      "price": 4.74
    }
  ],
  "black-pepper": [
    {
      "storeId": "publix",
      "price": 3.97
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.17
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "almond-butter": [
    {
      "storeId": "publix",
      "price": 9.91
    },
    {
      "storeId": "aldi",
      "price": 7.81
    },
    {
      "storeId": "sprouts",
      "price": 10.41
    },
    {
      "storeId": "freshMarket",
      "price": 11.99
    }
  ],
  "peanut-butter": [
    {
      "storeId": "publix",
      "price": 4.45
    },
    {
      "storeId": "aldi",
      "price": 3.5
    },
    {
      "storeId": "sprouts",
      "price": 4.67
    },
    {
      "storeId": "freshMarket",
      "price": 5.38
    }
  ],
  "peanut-butter-crunchy": [
    {
      "storeId": "publix",
      "price": 4.49
    },
    {
      "storeId": "aldi",
      "price": 3.53
    },
    {
      "storeId": "sprouts",
      "price": 4.71
    },
    {
      "storeId": "freshMarket",
      "price": 5.43
    }
  ],
  "nutella": [
    {
      "storeId": "publix",
      "price": 4.52
    },
    {
      "storeId": "aldi",
      "price": 3.49
    },
    {
      "storeId": "sprouts",
      "price": 4.75
    },
    {
      "storeId": "freshMarket",
      "price": 5.36
    }
  ],
  "honey": [
    {
      "storeId": "publix",
      "price": 6.97
    },
    {
      "storeId": "aldi",
      "price": 5.49
    },
    {
      "storeId": "sprouts",
      "price": 7.32
    },
    {
      "storeId": "freshMarket",
      "price": 8.43
    }
  ],
  "maple-syrup": [
    {
      "storeId": "publix",
      "price": 10
    },
    {
      "storeId": "aldi",
      "price": 8.12
    },
    {
      "storeId": "sprouts",
      "price": 10.8
    },
    {
      "storeId": "freshMarket",
      "price": 13.61
    }
  ],
  "agave": [
    {
      "storeId": "publix",
      "price": 4.52
    },
    {
      "storeId": "aldi",
      "price": 3.49
    },
    {
      "storeId": "sprouts",
      "price": 4.74
    },
    {
      "storeId": "freshMarket",
      "price": 5.36
    }
  ],
  "soy-sauce": [
    {
      "storeId": "publix",
      "price": 3.46
    },
    {
      "storeId": "aldi",
      "price": 2.72
    },
    {
      "storeId": "sprouts",
      "price": 3.63
    },
    {
      "storeId": "freshMarket",
      "price": 4.18
    }
  ],
  "tamari": [
    {
      "storeId": "publix",
      "price": 5.03
    },
    {
      "storeId": "aldi",
      "price": 3.88
    },
    {
      "storeId": "sprouts",
      "price": 5.28
    },
    {
      "storeId": "freshMarket",
      "price": 5.96
    }
  ],
  "sriracha": [
    {
      "storeId": "publix",
      "price": 4.47
    },
    {
      "storeId": "aldi",
      "price": 3.52
    },
    {
      "storeId": "sprouts",
      "price": 4.69
    },
    {
      "storeId": "freshMarket",
      "price": 5.4
    }
  ],
  "hot-sauce": [
    {
      "storeId": "publix",
      "price": 2.98
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.12
    },
    {
      "storeId": "freshMarket",
      "price": 3.6
    }
  ],
  "mustard-dijon": [
    {
      "storeId": "publix",
      "price": 3.47
    },
    {
      "storeId": "aldi",
      "price": 2.74
    },
    {
      "storeId": "sprouts",
      "price": 3.65
    },
    {
      "storeId": "freshMarket",
      "price": 4.2
    }
  ],
  "mustard-yellow": [
    {
      "storeId": "publix",
      "price": 2.47
    },
    {
      "storeId": "aldi",
      "price": 1.94
    },
    {
      "storeId": "sprouts",
      "price": 2.59
    },
    {
      "storeId": "freshMarket",
      "price": 2.99
    }
  ],
  "ketchup": [
    {
      "storeId": "publix",
      "price": 4.01
    },
    {
      "storeId": "aldi",
      "price": 3.1
    },
    {
      "storeId": "sprouts",
      "price": 4.21
    },
    {
      "storeId": "freshMarket",
      "price": 4.76
    }
  ],
  "mayo": [
    {
      "storeId": "publix",
      "price": 5.53
    },
    {
      "storeId": "aldi",
      "price": 4.27
    },
    {
      "storeId": "sprouts",
      "price": 5.81
    },
    {
      "storeId": "freshMarket",
      "price": 6.56
    }
  ],
  "olive-mayo": [
    {
      "storeId": "publix",
      "price": 6.46
    },
    {
      "storeId": "aldi",
      "price": 5.09
    },
    {
      "storeId": "sprouts",
      "price": 6.79
    },
    {
      "storeId": "freshMarket",
      "price": 7.82
    }
  ],
  "ranch-dressing": [
    {
      "storeId": "publix",
      "price": 4
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.2
    },
    {
      "storeId": "freshMarket",
      "price": 4.74
    }
  ],
  "italian-dressing": [
    {
      "storeId": "publix",
      "price": 3.48
    },
    {
      "storeId": "aldi",
      "price": 2.74
    },
    {
      "storeId": "sprouts",
      "price": 3.65
    },
    {
      "storeId": "freshMarket",
      "price": 4.2
    }
  ],
  "balsamic-vinaigrette": [
    {
      "storeId": "publix",
      "price": 4.03
    },
    {
      "storeId": "aldi",
      "price": 3.11
    },
    {
      "storeId": "sprouts",
      "price": 4.23
    },
    {
      "storeId": "freshMarket",
      "price": 4.77
    }
  ],
  "salsa-mild": [
    {
      "storeId": "publix",
      "price": 4.03
    },
    {
      "storeId": "aldi",
      "price": 3.11
    },
    {
      "storeId": "sprouts",
      "price": 4.23
    },
    {
      "storeId": "freshMarket",
      "price": 4.77
    }
  ],
  "marinara": [
    {
      "storeId": "publix",
      "price": 3.96
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.79
    }
  ],
  "pasta-sauce-vodka": [
    {
      "storeId": "publix",
      "price": 5.98
    },
    {
      "storeId": "aldi",
      "price": 4.71
    },
    {
      "storeId": "sprouts",
      "price": 6.28
    },
    {
      "storeId": "freshMarket",
      "price": 7.24
    }
  ],
  "pesto": [
    {
      "storeId": "publix",
      "price": 6.02
    },
    {
      "storeId": "aldi",
      "price": 4.65
    },
    {
      "storeId": "sprouts",
      "price": 6.32
    },
    {
      "storeId": "freshMarket",
      "price": 7.14
    }
  ],
  "canned-tomatoes": [
    {
      "storeId": "publix",
      "price": 1.5
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.58
    },
    {
      "storeId": "freshMarket",
      "price": 1.78
    }
  ],
  "tomato-sauce": [
    {
      "storeId": "publix",
      "price": 1.3
    },
    {
      "storeId": "aldi",
      "price": 1
    },
    {
      "storeId": "sprouts",
      "price": 1.36
    },
    {
      "storeId": "freshMarket",
      "price": 1.54
    }
  ],
  "tomato-paste": [
    {
      "storeId": "publix",
      "price": 1.28
    },
    {
      "storeId": "aldi",
      "price": 1.01
    },
    {
      "storeId": "sprouts",
      "price": 1.35
    },
    {
      "storeId": "freshMarket",
      "price": 1.55
    }
  ],
  "canned-beans-black": [
    {
      "storeId": "publix",
      "price": 1.5
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.58
    },
    {
      "storeId": "freshMarket",
      "price": 1.78
    }
  ],
  "canned-beans-kidney": [
    {
      "storeId": "publix",
      "price": 1.49
    },
    {
      "storeId": "aldi",
      "price": 1.15
    },
    {
      "storeId": "sprouts",
      "price": 1.57
    },
    {
      "storeId": "freshMarket",
      "price": 1.77
    }
  ],
  "canned-beans-garbanzo": [
    {
      "storeId": "publix",
      "price": 1.48
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.55
    },
    {
      "storeId": "freshMarket",
      "price": 1.78
    }
  ],
  "canned-beans-pinto": [
    {
      "storeId": "publix",
      "price": 1.48
    },
    {
      "storeId": "aldi",
      "price": 1.16
    },
    {
      "storeId": "sprouts",
      "price": 1.55
    },
    {
      "storeId": "freshMarket",
      "price": 1.79
    }
  ],
  "canned-tuna": [
    {
      "storeId": "publix",
      "price": 1.97
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.07
    },
    {
      "storeId": "freshMarket",
      "price": 2.39
    }
  ],
  "canned-chicken": [
    {
      "storeId": "publix",
      "price": 3.98
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.18
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "chicken-broth": [
    {
      "storeId": "publix",
      "price": 3.5
    },
    {
      "storeId": "aldi",
      "price": 2.7
    },
    {
      "storeId": "sprouts",
      "price": 3.68
    },
    {
      "storeId": "freshMarket",
      "price": 4.15
    }
  ],
  "vegetable-broth": [
    {
      "storeId": "publix",
      "price": 3.49
    },
    {
      "storeId": "aldi",
      "price": 2.7
    },
    {
      "storeId": "sprouts",
      "price": 3.67
    },
    {
      "storeId": "freshMarket",
      "price": 4.23
    }
  ],
  "beef-broth": [
    {
      "storeId": "publix",
      "price": 3.49
    },
    {
      "storeId": "aldi",
      "price": 2.69
    },
    {
      "storeId": "sprouts",
      "price": 3.66
    },
    {
      "storeId": "freshMarket",
      "price": 4.22
    }
  ],
  "organic-quinoa": [
    {
      "storeId": "publix",
      "price": 7.04
    },
    {
      "storeId": "aldi",
      "price": 5.92
    },
    {
      "storeId": "sprouts",
      "price": 7.88
    },
    {
      "storeId": "freshMarket",
      "price": 8.69
    }
  ],
  "white-rice": [
    {
      "storeId": "publix",
      "price": 3.49
    },
    {
      "storeId": "aldi",
      "price": 2.75
    },
    {
      "storeId": "sprouts",
      "price": 3.66
    },
    {
      "storeId": "freshMarket",
      "price": 4.22
    }
  ],
  "brown-rice": [
    {
      "storeId": "publix",
      "price": 4.01
    },
    {
      "storeId": "aldi",
      "price": 3.1
    },
    {
      "storeId": "sprouts",
      "price": 4.21
    },
    {
      "storeId": "freshMarket",
      "price": 4.76
    }
  ],
  "jasmine-rice": [
    {
      "storeId": "publix",
      "price": 4.48
    },
    {
      "storeId": "aldi",
      "price": 3.53
    },
    {
      "storeId": "sprouts",
      "price": 4.7
    },
    {
      "storeId": "freshMarket",
      "price": 5.41
    }
  ],
  "basmati-rice": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.19
    },
    {
      "storeId": "freshMarket",
      "price": 5.98
    }
  ],
  "arborio-rice": [
    {
      "storeId": "publix",
      "price": 4.01
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.21
    },
    {
      "storeId": "freshMarket",
      "price": 4.75
    }
  ],
  "farro": [
    {
      "storeId": "publix",
      "price": 6.04
    },
    {
      "storeId": "aldi",
      "price": 4.66
    },
    {
      "storeId": "sprouts",
      "price": 6.34
    },
    {
      "storeId": "freshMarket",
      "price": 7.16
    }
  ],
  "rolled-oats": [
    {
      "storeId": "publix",
      "price": 3.97
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.17
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "steel-cut-oats": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.2
    },
    {
      "storeId": "freshMarket",
      "price": 5.99
    }
  ],
  "instant-oatmeal": [
    {
      "storeId": "publix",
      "price": 4
    },
    {
      "storeId": "aldi",
      "price": 3.09
    },
    {
      "storeId": "sprouts",
      "price": 4.2
    },
    {
      "storeId": "freshMarket",
      "price": 4.74
    }
  ],
  "pasta-spaghetti": [
    {
      "storeId": "publix",
      "price": 1.99
    },
    {
      "storeId": "aldi",
      "price": 1.54
    },
    {
      "storeId": "sprouts",
      "price": 2.09
    },
    {
      "storeId": "freshMarket",
      "price": 2.41
    }
  ],
  "pasta-penne": [
    {
      "storeId": "publix",
      "price": 2
    },
    {
      "storeId": "aldi",
      "price": 1.55
    },
    {
      "storeId": "sprouts",
      "price": 2.1
    },
    {
      "storeId": "freshMarket",
      "price": 2.37
    }
  ],
  "pasta-rotini": [
    {
      "storeId": "publix",
      "price": 1.98
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.07
    },
    {
      "storeId": "freshMarket",
      "price": 2.39
    }
  ],
  "pasta-angel-hair": [
    {
      "storeId": "publix",
      "price": 1.98
    },
    {
      "storeId": "aldi",
      "price": 1.56
    },
    {
      "storeId": "sprouts",
      "price": 2.08
    },
    {
      "storeId": "freshMarket",
      "price": 2.4
    }
  ],
  "gluten-free-pasta": [
    {
      "storeId": "publix",
      "price": 3.95
    },
    {
      "storeId": "aldi",
      "price": 3.11
    },
    {
      "storeId": "sprouts",
      "price": 4.15
    },
    {
      "storeId": "freshMarket",
      "price": 4.78
    }
  ],
  "whole-wheat-pasta": [
    {
      "storeId": "publix",
      "price": 2.48
    },
    {
      "storeId": "aldi",
      "price": 1.96
    },
    {
      "storeId": "sprouts",
      "price": 2.61
    },
    {
      "storeId": "freshMarket",
      "price": 3
    }
  ],
  "couscous": [
    {
      "storeId": "publix",
      "price": 2.97
    },
    {
      "storeId": "aldi",
      "price": 2.34
    },
    {
      "storeId": "sprouts",
      "price": 3.12
    },
    {
      "storeId": "freshMarket",
      "price": 3.6
    }
  ],
  "sparkling-water": [
    {
      "storeId": "publix",
      "price": 5.95
    },
    {
      "storeId": "aldi",
      "price": 4.69
    },
    {
      "storeId": "sprouts",
      "price": 6.25
    },
    {
      "storeId": "freshMarket",
      "price": 7.2
    }
  ],
  "sparkling-water-lime": [
    {
      "storeId": "publix",
      "price": 6
    },
    {
      "storeId": "aldi",
      "price": 4.63
    },
    {
      "storeId": "sprouts",
      "price": 6.3
    },
    {
      "storeId": "freshMarket",
      "price": 7.25
    }
  ],
  "bottled-water": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.2
    },
    {
      "storeId": "freshMarket",
      "price": 5.99
    }
  ],
  "coffee-ground": [
    {
      "storeId": "publix",
      "price": 9.04
    },
    {
      "storeId": "aldi",
      "price": 6.98
    },
    {
      "storeId": "sprouts",
      "price": 9.5
    },
    {
      "storeId": "freshMarket",
      "price": 10.72
    }
  ],
  "coffee-whole-bean": [
    {
      "storeId": "publix",
      "price": 10.04
    },
    {
      "storeId": "aldi",
      "price": 7.75
    },
    {
      "storeId": "sprouts",
      "price": 10.54
    },
    {
      "storeId": "freshMarket",
      "price": 11.9
    }
  ],
  "coffee-k-cups": [
    {
      "storeId": "publix",
      "price": 12.06
    },
    {
      "storeId": "aldi",
      "price": 9.31
    },
    {
      "storeId": "sprouts",
      "price": 12.67
    },
    {
      "storeId": "freshMarket",
      "price": 14.3
    }
  ],
  "coffee-cold-brew": [
    {
      "storeId": "publix",
      "price": 7.03
    },
    {
      "storeId": "aldi",
      "price": 5.43
    },
    {
      "storeId": "sprouts",
      "price": 7.38
    },
    {
      "storeId": "freshMarket",
      "price": 8.34
    }
  ],
  "tea-green": [
    {
      "storeId": "publix",
      "price": 4.98
    },
    {
      "storeId": "aldi",
      "price": 3.92
    },
    {
      "storeId": "sprouts",
      "price": 5.23
    },
    {
      "storeId": "freshMarket",
      "price": 6.02
    }
  ],
  "tea-black": [
    {
      "storeId": "publix",
      "price": 4.48
    },
    {
      "storeId": "aldi",
      "price": 3.53
    },
    {
      "storeId": "sprouts",
      "price": 4.71
    },
    {
      "storeId": "freshMarket",
      "price": 5.42
    }
  ],
  "tea-herbal": [
    {
      "storeId": "publix",
      "price": 3.95
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.15
    },
    {
      "storeId": "freshMarket",
      "price": 4.78
    }
  ],
  "orange-juice": [
    {
      "storeId": "publix",
      "price": 5.98
    },
    {
      "storeId": "aldi",
      "price": 4.71
    },
    {
      "storeId": "sprouts",
      "price": 6.28
    },
    {
      "storeId": "freshMarket",
      "price": 7.24
    }
  ],
  "apple-juice": [
    {
      "storeId": "publix",
      "price": 3.96
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.79
    }
  ],
  "cranberry-juice": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.19
    },
    {
      "storeId": "freshMarket",
      "price": 5.98
    }
  ],
  "lemonade": [
    {
      "storeId": "publix",
      "price": 3.51
    },
    {
      "storeId": "aldi",
      "price": 2.71
    },
    {
      "storeId": "sprouts",
      "price": 3.69
    },
    {
      "storeId": "freshMarket",
      "price": 4.17
    }
  ],
  "cola": [
    {
      "storeId": "publix",
      "price": 8.03
    },
    {
      "storeId": "aldi",
      "price": 6.2
    },
    {
      "storeId": "sprouts",
      "price": 8.43
    },
    {
      "storeId": "freshMarket",
      "price": 9.52
    }
  ],
  "diet-cola": [
    {
      "storeId": "publix",
      "price": 7.93
    },
    {
      "storeId": "aldi",
      "price": 6.24
    },
    {
      "storeId": "sprouts",
      "price": 8.32
    },
    {
      "storeId": "freshMarket",
      "price": 9.59
    }
  ],
  "gatorade": [
    {
      "storeId": "publix",
      "price": 12.1
    },
    {
      "storeId": "aldi",
      "price": 9.34
    },
    {
      "storeId": "sprouts",
      "price": 12.7
    },
    {
      "storeId": "freshMarket",
      "price": 14.34
    }
  ],
  "kombucha": [
    {
      "storeId": "publix",
      "price": 3.96
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.79
    }
  ],
  "organic-frozen-berries": [
    {
      "storeId": "publix",
      "price": 7.04
    },
    {
      "storeId": "aldi",
      "price": 5.92
    },
    {
      "storeId": "sprouts",
      "price": 7.88
    },
    {
      "storeId": "freshMarket",
      "price": 8.69
    }
  ],
  "frozen-blueberries": [
    {
      "storeId": "publix",
      "price": 5.02
    },
    {
      "storeId": "aldi",
      "price": 3.88
    },
    {
      "storeId": "sprouts",
      "price": 5.28
    },
    {
      "storeId": "freshMarket",
      "price": 5.96
    }
  ],
  "frozen-strawberries": [
    {
      "storeId": "publix",
      "price": 4.51
    },
    {
      "storeId": "aldi",
      "price": 3.48
    },
    {
      "storeId": "sprouts",
      "price": 4.73
    },
    {
      "storeId": "freshMarket",
      "price": 5.34
    }
  ],
  "frozen-mango": [
    {
      "storeId": "publix",
      "price": 5.01
    },
    {
      "storeId": "aldi",
      "price": 3.87
    },
    {
      "storeId": "sprouts",
      "price": 5.27
    },
    {
      "storeId": "freshMarket",
      "price": 5.95
    }
  ],
  "organic-frozen-acai": [
    {
      "storeId": "publix",
      "price": 9.96
    },
    {
      "storeId": "aldi",
      "price": 8.55
    },
    {
      "storeId": "sprouts",
      "price": 11.16
    },
    {
      "storeId": "freshMarket",
      "price": 12.55
    }
  ],
  "frozen-pizza": [
    {
      "storeId": "publix",
      "price": 6.94
    },
    {
      "storeId": "aldi",
      "price": 5.47
    },
    {
      "storeId": "sprouts",
      "price": 7.29
    },
    {
      "storeId": "freshMarket",
      "price": 8.4
    }
  ],
  "frozen-pizza-cauliflower": [
    {
      "storeId": "publix",
      "price": 8.98
    },
    {
      "storeId": "aldi",
      "price": 7.08
    },
    {
      "storeId": "sprouts",
      "price": 9.43
    },
    {
      "storeId": "freshMarket",
      "price": 10.86
    }
  ],
  "frozen-veggies-mixed": [
    {
      "storeId": "publix",
      "price": 2.49
    },
    {
      "storeId": "aldi",
      "price": 1.92
    },
    {
      "storeId": "sprouts",
      "price": 2.62
    },
    {
      "storeId": "freshMarket",
      "price": 3.01
    }
  ],
  "frozen-broccoli": [
    {
      "storeId": "publix",
      "price": 2.49
    },
    {
      "storeId": "aldi",
      "price": 1.92
    },
    {
      "storeId": "sprouts",
      "price": 2.61
    },
    {
      "storeId": "freshMarket",
      "price": 3.01
    }
  ],
  "frozen-peas": [
    {
      "storeId": "publix",
      "price": 2.49
    },
    {
      "storeId": "aldi",
      "price": 1.92
    },
    {
      "storeId": "sprouts",
      "price": 2.61
    },
    {
      "storeId": "freshMarket",
      "price": 3.01
    }
  ],
  "frozen-corn": [
    {
      "storeId": "publix",
      "price": 2.51
    },
    {
      "storeId": "aldi",
      "price": 1.94
    },
    {
      "storeId": "sprouts",
      "price": 2.64
    },
    {
      "storeId": "freshMarket",
      "price": 2.98
    }
  ],
  "frozen-cauliflower-rice": [
    {
      "storeId": "publix",
      "price": 3.49
    },
    {
      "storeId": "aldi",
      "price": 2.69
    },
    {
      "storeId": "sprouts",
      "price": 3.66
    },
    {
      "storeId": "freshMarket",
      "price": 4.22
    }
  ],
  "frozen-edamame": [
    {
      "storeId": "publix",
      "price": 3.52
    },
    {
      "storeId": "aldi",
      "price": 2.72
    },
    {
      "storeId": "sprouts",
      "price": 3.7
    },
    {
      "storeId": "freshMarket",
      "price": 4.18
    }
  ],
  "ice-cream-vanilla": [
    {
      "storeId": "publix",
      "price": 7.05
    },
    {
      "storeId": "aldi",
      "price": 5.44
    },
    {
      "storeId": "sprouts",
      "price": 7.4
    },
    {
      "storeId": "freshMarket",
      "price": 8.35
    }
  ],
  "ice-cream-chocolate": [
    {
      "storeId": "publix",
      "price": 6.93
    },
    {
      "storeId": "aldi",
      "price": 5.46
    },
    {
      "storeId": "sprouts",
      "price": 7.27
    },
    {
      "storeId": "freshMarket",
      "price": 8.38
    }
  ],
  "frozen-waffles": [
    {
      "storeId": "publix",
      "price": 3.99
    },
    {
      "storeId": "aldi",
      "price": 3.14
    },
    {
      "storeId": "sprouts",
      "price": 4.19
    },
    {
      "storeId": "freshMarket",
      "price": 4.82
    }
  ],
  "frozen-burritos": [
    {
      "storeId": "publix",
      "price": 7.02
    },
    {
      "storeId": "aldi",
      "price": 5.42
    },
    {
      "storeId": "sprouts",
      "price": 7.38
    },
    {
      "storeId": "freshMarket",
      "price": 8.33
    }
  ],
  "tortilla-chips": [
    {
      "storeId": "publix",
      "price": 4.01
    },
    {
      "storeId": "aldi",
      "price": 3.1
    },
    {
      "storeId": "sprouts",
      "price": 4.21
    },
    {
      "storeId": "freshMarket",
      "price": 4.75
    }
  ],
  "potato-chips": [
    {
      "storeId": "publix",
      "price": 3.96
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.79
    }
  ],
  "popcorn": [
    {
      "storeId": "publix",
      "price": 4.99
    },
    {
      "storeId": "aldi",
      "price": 3.93
    },
    {
      "storeId": "sprouts",
      "price": 5.23
    },
    {
      "storeId": "freshMarket",
      "price": 6.03
    }
  ],
  "pretzels": [
    {
      "storeId": "publix",
      "price": 3.49
    },
    {
      "storeId": "aldi",
      "price": 2.75
    },
    {
      "storeId": "sprouts",
      "price": 3.66
    },
    {
      "storeId": "freshMarket",
      "price": 4.22
    }
  ],
  "granola-bars": [
    {
      "storeId": "publix",
      "price": 5.98
    },
    {
      "storeId": "aldi",
      "price": 4.71
    },
    {
      "storeId": "sprouts",
      "price": 6.28
    },
    {
      "storeId": "freshMarket",
      "price": 7.24
    }
  ],
  "protein-bars": [
    {
      "storeId": "publix",
      "price": 13.09
    },
    {
      "storeId": "aldi",
      "price": 10.11
    },
    {
      "storeId": "sprouts",
      "price": 13.75
    },
    {
      "storeId": "freshMarket",
      "price": 15.53
    }
  ],
  "mixed-nuts": [
    {
      "storeId": "publix",
      "price": 11.08
    },
    {
      "storeId": "aldi",
      "price": 8.56
    },
    {
      "storeId": "sprouts",
      "price": 11.63
    },
    {
      "storeId": "freshMarket",
      "price": 13.14
    }
  ],
  "almonds": [
    {
      "storeId": "publix",
      "price": 8.99
    },
    {
      "storeId": "aldi",
      "price": 6.94
    },
    {
      "storeId": "sprouts",
      "price": 9.44
    },
    {
      "storeId": "freshMarket",
      "price": 10.87
    }
  ],
  "cashews": [
    {
      "storeId": "publix",
      "price": 11.99
    },
    {
      "storeId": "aldi",
      "price": 9.26
    },
    {
      "storeId": "sprouts",
      "price": 12.59
    },
    {
      "storeId": "freshMarket",
      "price": 14.5
    }
  ],
  "trail-mix": [
    {
      "storeId": "publix",
      "price": 8.06
    },
    {
      "storeId": "aldi",
      "price": 6.23
    },
    {
      "storeId": "sprouts",
      "price": 8.47
    },
    {
      "storeId": "freshMarket",
      "price": 9.56
    }
  ],
  "hummus": [
    {
      "storeId": "publix",
      "price": 4.49
    },
    {
      "storeId": "aldi",
      "price": 3.47
    },
    {
      "storeId": "sprouts",
      "price": 4.72
    },
    {
      "storeId": "freshMarket",
      "price": 5.44
    }
  ],
  "guacamole": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.2
    },
    {
      "storeId": "freshMarket",
      "price": 5.99
    }
  ],
  "cookies-oreo": [
    {
      "storeId": "publix",
      "price": 4.49
    },
    {
      "storeId": "aldi",
      "price": 3.47
    },
    {
      "storeId": "sprouts",
      "price": 4.72
    },
    {
      "storeId": "freshMarket",
      "price": 5.44
    }
  ],
  "crackers-ritz": [
    {
      "storeId": "publix",
      "price": 4.48
    },
    {
      "storeId": "aldi",
      "price": 3.53
    },
    {
      "storeId": "sprouts",
      "price": 4.71
    },
    {
      "storeId": "freshMarket",
      "price": 5.42
    }
  ],
  "crackers-saltine": [
    {
      "storeId": "publix",
      "price": 3.96
    },
    {
      "storeId": "aldi",
      "price": 3.12
    },
    {
      "storeId": "sprouts",
      "price": 4.16
    },
    {
      "storeId": "freshMarket",
      "price": 4.79
    }
  ],
  "cereal-cheerios": [
    {
      "storeId": "publix",
      "price": 6.02
    },
    {
      "storeId": "aldi",
      "price": 4.65
    },
    {
      "storeId": "sprouts",
      "price": 6.32
    },
    {
      "storeId": "freshMarket",
      "price": 7.14
    }
  ],
  "cereal-granola": [
    {
      "storeId": "publix",
      "price": 5.96
    },
    {
      "storeId": "aldi",
      "price": 4.7
    },
    {
      "storeId": "sprouts",
      "price": 6.26
    },
    {
      "storeId": "freshMarket",
      "price": 7.21
    }
  ],
  "cereal-bran-flakes": [
    {
      "storeId": "publix",
      "price": 4.53
    },
    {
      "storeId": "aldi",
      "price": 3.5
    },
    {
      "storeId": "sprouts",
      "price": 4.76
    },
    {
      "storeId": "freshMarket",
      "price": 5.37
    }
  ],
  "pancake-mix": [
    {
      "storeId": "publix",
      "price": 4.95
    },
    {
      "storeId": "aldi",
      "price": 3.9
    },
    {
      "storeId": "sprouts",
      "price": 5.2
    },
    {
      "storeId": "freshMarket",
      "price": 5.99
    }
  ],
  "syrup-pancake": [
    {
      "storeId": "publix",
      "price": 3.98
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.18
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "jam-strawberry": [
    {
      "storeId": "publix",
      "price": 3.97
    },
    {
      "storeId": "aldi",
      "price": 3.13
    },
    {
      "storeId": "sprouts",
      "price": 4.17
    },
    {
      "storeId": "freshMarket",
      "price": 4.81
    }
  ],
  "jam-grape": [
    {
      "storeId": "publix",
      "price": 3.47
    },
    {
      "storeId": "aldi",
      "price": 2.73
    },
    {
      "storeId": "sprouts",
      "price": 3.64
    },
    {
      "storeId": "freshMarket",
      "price": 4.2
    }
  ],
  "paper-towels": [
    {
      "storeId": "publix",
      "price": 13
    },
    {
      "storeId": "aldi",
      "price": 10.04
    }
  ],
  "toilet-paper": [
    {
      "storeId": "publix",
      "price": 15.11
    },
    {
      "storeId": "aldi",
      "price": 11.67
    }
  ],
  "dish-soap": [
    {
      "storeId": "publix",
      "price": 3.97
    },
    {
      "storeId": "aldi",
      "price": 3.12
    }
  ],
  "laundry-detergent": [
    {
      "storeId": "publix",
      "price": 13.04
    },
    {
      "storeId": "aldi",
      "price": 10.07
    }
  ],
  "trash-bags": [
    {
      "storeId": "publix",
      "price": 9.93
    },
    {
      "storeId": "aldi",
      "price": 7.82
    }
  ],
  "foil": [
    {
      "storeId": "publix",
      "price": 4.97
    },
    {
      "storeId": "aldi",
      "price": 3.92
    }
  ],
  "plastic-wrap": [
    {
      "storeId": "publix",
      "price": 4.03
    },
    {
      "storeId": "aldi",
      "price": 3.11
    }
  ],
  "parchment-paper": [
    {
      "storeId": "publix",
      "price": 4.46
    },
    {
      "storeId": "aldi",
      "price": 3.51
    }
  ],
  "ziploc-gallon": [
    {
      "storeId": "publix",
      "price": 6.04
    },
    {
      "storeId": "aldi",
      "price": 4.67
    }
  ],
  "ziploc-sandwich": [
    {
      "storeId": "publix",
      "price": 4.99
    },
    {
      "storeId": "aldi",
      "price": 3.86
    }
  ]
};

/* ═══════════════════════════════════════════════════════════
   UNIVERSAL SEARCH (Phase 1)
   ═══════════════════════════════════════════════════════════
   searchItems(query, options) — searches EXPANDED_CATALOG by name,
   returning canonical items annotated with per-store availability
   and pricing. UI surfaces in Phase 2+ will consume this function.

   Options:
     - storeId: string — scope search to a specific store
     - scope: "store" | "all" — overrides storeId behavior
     - limit: number (default 40)

   Cache: session-only, keyed by query + scope. Cleared on reload.

   When real Instacart API is wired (post-IDP approval), replace the
   implementation of this function to call the API. UI signature stays
   the same, so nothing downstream changes.
   ═══════════════════════════════════════════════════════════ */

const SEARCH_CACHE = new Map();
const SEARCH_CACHE_MAX = 100;  // hard cap — evict oldest when exceeded

function _searchScore(query, name) {
  const q = query.toLowerCase();
  const n = name.toLowerCase();
  // Exact name equality — the ideal match
  if (n === q) return 100;
  // Phrase-level prefix: name STARTS WITH the query phrase. Two sub-cases:
  //  - Name ENDS at the phrase (e.g., query "olive oil" matching name "Olive Oil") → 95
  //  - Name continues past the phrase (e.g., "Olive Oil Mayo") → 90
  // This ordering ensures simpler matches rank above category-extension matches.
  if (n.startsWith(q + " ")) return 90;
  if (n.startsWith(q)) return 95;
  // Tokens in order, appearing as a phrase anywhere in the name
  // (e.g. "olive oil" inside "Extra Virgin Olive Oil")
  if (q.includes(" ") && n.includes(q)) return 85;
  // Word-start match on any single word of the name
  const words = n.split(/\s+/);
  for (const w of words) {
    if (w.startsWith(q)) return 75;
  }
  // All query tokens present anywhere (multi-token AND)
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1 && tokens.every(t => n.includes(t))) return 60;
  // Simple substring match
  if (n.includes(q)) return 45;
  return 0;
}

function _storeName(storeId) {
  // Safe lookup — STORES might not be imported in isolation
  const store = STORES.find(s => s.id === storeId);
  return store ? store.name : storeId;
}

// Normalises a dietaryRestrictions array into a stable cache-key fragment.
// Sorted so ["vegan","gluten-free"] and ["gluten-free","vegan"] hash the same.
function _dietaryKey(restrictions) {
  if (!restrictions || restrictions.length === 0) return "";
  return [...restrictions].sort().join(",");
}

function searchItems(query, options = {}) {
  const q = (query || "").trim();
  if (q.length < 2) return [];

  const {
    storeId,
    scope,
    limit = 40,
    dietaryRestrictions,   // array of active restriction IDs — filters results
  } = options;
  // Resolve effective scope: explicit "all" always wins; otherwise default to
  // storeId-scoped when storeId given, else "all"
  const effectiveScope = scope === "all" ? "all" : (storeId ? "store" : "all");
  // Cache key includes dietary restrictions so toggling diet prefs mid-session
  // always returns freshly filtered results.
  const cacheKey = q.toLowerCase()
    + "|" + (storeId || "")
    + "|" + effectiveScope
    + "|" + limit
    + "|" + _dietaryKey(dietaryRestrictions);

  const cached = SEARCH_CACHE.get(cacheKey);
  if (cached) return cached;

  // Pass 1: score every canonical item against the query
  const scored = [];
  for (const item of EXPANDED_CATALOG) {
    const score = _searchScore(q, item.name);
    if (score === 0) continue;

    // Dietary filter — respects the user's active restrictions.
    // matchesDietary() reads item.name via regex so catalog items work fine.
    if (dietaryRestrictions && dietaryRestrictions.length > 0) {
      if (!matchesDietary(item, dietaryRestrictions)) continue;
    }

    // Resolve availability for this scope
    const availability = STORE_AVAILABILITY[item.id] || [];
    const storesInScope = effectiveScope === "store"
      ? availability.filter(a => a.storeId === storeId)
      : availability;
    if (storesInScope.length === 0) continue;

    const priced = storesInScope.map(a => ({
      storeId: a.storeId,
      storeName: _storeName(a.storeId),
      price: a.price,
    }));
    // Sort stores cheapest first — detail view will show them in this order
    priced.sort((a, b) => a.price - b.price);
    const bestPriceEntry = priced[0];

    // Savings estimate: highest price in scope minus lowest price. This is the
    // real value Smart Cart routing delivers for this item (vs a random store).
    // For single-store results (store-scoped search), savings is 0.
    const maxPrice = priced.reduce((m, p) => Math.max(m, p.price), 0);
    const maxSavings = Math.max(0, maxPrice - bestPriceEntry.price);

    scored.push({
      canonicalId: item.id,
      name: item.name,
      category: item.category,
      size: item.size,
      organic: !!item.organic,
      premium: !!item.premium,
      stores: priced,
      bestPrice: bestPriceEntry.price,
      bestStore: bestPriceEntry.storeId,
      maxSavings,
      inCatalog: true,
      _score: score,
    });
  }

  // Sort by score DESC, then shorter name (more specific) first, then alphabetic
  scored.sort((a, b) => {
    if (b._score !== a._score) return b._score - a._score;
    if (a.name.length !== b.name.length) return a.name.length - b.name.length;
    return a.name.localeCompare(b.name);
  });
  const results = scored.slice(0, limit).map(r => { delete r._score; return r; });

  // Cache
  if (SEARCH_CACHE.size >= SEARCH_CACHE_MAX) {
    // Evict oldest (first inserted) — Map iteration order is insertion order
    const firstKey = SEARCH_CACHE.keys().next().value;
    if (firstKey !== undefined) SEARCH_CACHE.delete(firstKey);
  }
  SEARCH_CACHE.set(cacheKey, results);

  return results;
}

// Canonical item lookup — given any ID (from a deal, a cart key, etc.),
// return the canonical catalog entry. Used by Smart Cart routing and
// by UI surfaces that need item metadata beyond what's in a specific deal.
function getCanonicalItem(id) {
  if (!id) return null;
  return EXPANDED_CATALOG.find(item => item.id === id) || null;
}

/* ═══════════════════════════════════════════════════════════
   REBUILD CART FROM STAPLES — Phase C helper
   ═══════════════════════════════════════════════════════════
   Walks the user's staples list, finds the best-priced offer in
   their active stores for each, and returns:
     - cartUpdates: a partial cartMap to merge in
     - itemCount: how many staples were routed
     - estimatedSavings: gross dollar savings vs the worst-priced
       offer for each item across all active stores
     - storeBreakdown: { [storeId]: count } so the UI can show
       "3 stores: Publix (4), ALDI (6), Sprouts (2)"
     - missingItems: staples with no offer in any active store

   Note: this helper does not mutate state. The caller is
   responsible for setCartMap(...) and setTab("split").
   ═══════════════════════════════════════════════════════════ */
function rebuildCartFromStaples(stapleIds, allDeals, activeStores) {
  if (!Array.isArray(stapleIds) || stapleIds.length === 0) {
    return { cartUpdates: {}, itemCount: 0, estimatedSavings: 0, storeBreakdown: {}, missingItems: [] };
  }
  const cartUpdates = {};
  const storeBreakdown = {};
  const missingItems = [];
  let estimatedSavings = 0;

  for (const id of stapleIds) {
    const offers = allDeals.filter(d => d.id === id);
    if (offers.length === 0) {
      missingItems.push(id);
      continue;
    }
    // Best offer = lowest effective per-unit price (falls back to salePrice/origPrice)
    const ranked = [...offers].sort((a, b) => {
      const aP = (a.salePrice > 0 ? a.salePrice : a.origPrice) || Infinity;
      const bP = (b.salePrice > 0 ? b.salePrice : b.origPrice) || Infinity;
      return aP - bP;
    });
    const best = ranked[0];
    const worst = ranked[ranked.length - 1];

    cartUpdates[`${best.store.id}_${best.id}`] = true;
    storeBreakdown[best.store.id] = (storeBreakdown[best.store.id] || 0) + 1;

    const bestPrice = (best.salePrice > 0 ? best.salePrice : best.origPrice) || 0;
    const worstPrice = (worst.salePrice > 0 ? worst.salePrice : worst.origPrice) || bestPrice;
    estimatedSavings += Math.max(0, worstPrice - bestPrice);
  }

  return {
    cartUpdates,
    itemCount: Object.keys(cartUpdates).length,
    estimatedSavings,
    storeBreakdown,
    missingItems,
  };
}

// Per-store price lookup for a canonical item. Returns null if store
// doesn't carry the item (or unknown canonical ID).
function getCatalogPriceAtStore(canonicalId, storeId) {
  const arr = STORE_AVAILABILITY[canonicalId];
  if (!arr) return null;
  const entry = arr.find(a => a.storeId === storeId);
  return entry ? entry.price : null;
}

/* ═══════════════════════════════════════════════════════════
   LEGACY ID MIGRATION
   ═══════════════════════════════════════════════════════════
   In v1, items were keyed by numeric id (1-15). v2 switched to
   string slugs for safety (typos visible, no id collisions).
   This table maps old → new so we can migrate persisted user
   state on first load. Once all users have hydrated through this
   migration at least once, this table can be deleted.
   ═══════════════════════════════════════════════════════════ */
const LEGACY_ID_TO_SLUG = {
  1:  "organic-whole-milk",
  2:  "free-range-eggs",
  3:  "atlantic-salmon",
  4:  "chicken-breast",
  5:  "baby-spinach",
  6:  "avocados",
  7:  "greek-yogurt",
  8:  "organic-quinoa",
  9:  "almond-butter",
  10: "sparkling-water",
  11: "bread",
  12: "olive-oil",
  13: "organic-frozen-berries",  // note: also covered the açaí entry in legacy data
  14: "ground-turkey",
  15: "cherry-tomatoes",
};

// Convert any value that might be a legacy numeric id to a slug.
// Returns the input unchanged if it's already a slug or unrecognizable.
function migrateId(id) {
  if (typeof id === "number" && LEGACY_ID_TO_SLUG[id]) return LEGACY_ID_TO_SLUG[id];
  return id;
}

// Migrate a cartMap (keys are `${storeId}_${itemId}`) — split, migrate the
// item portion, rejoin. Store IDs are already strings and untouched.
function migrateCartMap(cartMap) {
  if (!cartMap || typeof cartMap !== "object") return cartMap;
  const migrated = {};
  let changed = false;
  for (const [key, value] of Object.entries(cartMap)) {
    const idx = key.lastIndexOf("_");
    if (idx === -1) { migrated[key] = value; continue; }
    const storeId = key.slice(0, idx);
    const itemPart = key.slice(idx + 1);
    const asNum = Number(itemPart);
    if (Number.isInteger(asNum) && LEGACY_ID_TO_SLUG[asNum]) {
      migrated[`${storeId}_${LEGACY_ID_TO_SLUG[asNum]}`] = value;
      changed = true;
    } else {
      migrated[key] = value;
    }
  }
  return changed ? migrated : cartMap;
}

// Migrate an array of items each having `.id` (pantry, watchlist, etc).
function migrateItemArray(items) {
  if (!Array.isArray(items)) return items;
  let changed = false;
  const migrated = items.map(item => {
    if (item && typeof item.id === "number" && LEGACY_ID_TO_SLUG[item.id]) {
      changed = true;
      return { ...item, id: LEGACY_ID_TO_SLUG[item.id] };
    }
    // Watchlist items use itemId, not id
    if (item && typeof item.itemId === "number" && LEGACY_ID_TO_SLUG[item.itemId]) {
      changed = true;
      return { ...item, itemId: LEGACY_ID_TO_SLUG[item.itemId] };
    }
    return item;
  });
  return changed ? migrated : items;
}

/* ═══════════════════════════════════════════════════════════
   UNIT PRICING — per-ounce / per-pound / per-count comparisons
   ═══════════════════════════════════════════════════════════
   For each item id, declare the package size + the unit shoppers
   actually compare in. CartIQ then computes $/unit on every deal.
   ═══════════════════════════════════════════════════════════ */
const ITEM_UNIT_DATA = {
  "organic-whole-milk":     { size: 128, unit: "fl oz", display: "gal" },     // milk, 1 gal = 128 fl oz
  "free-range-eggs":        { size: 12,  unit: "ct",    display: "doz" },     // eggs, dozen
  "atlantic-salmon":        { size: 16,  unit: "oz",    display: "lb" },      // salmon
  "chicken-breast":         { size: 16,  unit: "oz",    display: "lb" },      // chicken
  "baby-spinach":           { size: 5,   unit: "oz",    display: "bag" },     // baby spinach, 5 oz bag
  "avocados":               { size: 1,   unit: "ea",    display: "ea" },      // avocados
  "greek-yogurt":           { size: 32,  unit: "oz",    display: "tub" },     // greek yogurt
  "organic-quinoa":         { size: 16,  unit: "oz",    display: "bag" },     // quinoa
  "almond-butter":          { size: 16,  unit: "oz",    display: "jar" },     // almond butter
  "sparkling-water":        { size: 12,  unit: "ct",    display: "case" },    // sparkling water case
  "bread":                  { size: 20,  unit: "oz",    display: "loaf" },    // bread
  "olive-oil":              { size: 17,  unit: "fl oz", display: "bottle" },  // olive oil
  "organic-frozen-berries": { size: 12,  unit: "oz",    display: "bag" },     // frozen berries
  "ground-turkey":          { size: 16,  unit: "oz",    display: "lb" },      // ground turkey
  "cherry-tomatoes":        { size: 10,  unit: "oz",    display: "pint" },    // cherry tomatoes
};

// Compute unit price for a deal — returns { value, label } or null
function computeUnitPrice(deal) {
  const data = ITEM_UNIT_DATA[deal.id];
  if (!data) return null;
  const price = deal.salePrice > 0 ? deal.salePrice : deal.origPrice;
  if (!price) return null;
  const perUnit = price / data.size;
  // Format nicely — small values get 4 decimals, larger get 2
  const formatted = perUnit < 0.10 ? perUnit.toFixed(4) : perUnit.toFixed(2);
  return {
    value: perUnit,
    label: `$${formatted}/${data.unit}`,
    fullLabel: `$${formatted} per ${data.unit}`,
    packageSize: `${data.size} ${data.unit}`,
  };
}

/* ═══════════════════════════════════════════════════════════
   ITEM SIZE HISTORY — Phase B shrinkflation tracking
   ═══════════════════════════════════════════════════════════
   For each item ID, an array of historical package sizes (units
   match ITEM_UNIT_DATA[id].unit). Ordered oldest first, current
   last. A drop in current vs the max historical baseline → the
   item has shrunk, and the deal card flags it.

   For the round, this is curated data based on real-world
   shrinkflation patterns. Once the Instacart Connect catalog is
   wired, replace this with a live computation: compare current
   product size against the rolling 12-month max for that UPC.

   Example: cereal originally 12 oz, now 10.5 oz → entry is
   [12, 12, 11.5, 10.5]. The card flags "Shrunk 12 → 10.5 oz (12.5% less)."
   ═══════════════════════════════════════════════════════════ */
const ITEM_SIZE_HISTORY = {
  // Real shrinkflation cases observed in 2023–2025
  "organic-frozen-berries": [16, 16, 14, 12],     // bag shrunk from 16 oz to 12 oz
  "greek-yogurt":            [32, 32, 32, 32],     // unchanged (control case)
  "chips":                   [10, 10, 9.25, 8.5],  // classic chip shrinkflation
  "crackers":                [16, 14, 13, 12],     // box shrunk gradually
  "cereal-generic":          [18, 16, 14, 12],
  "pasta":                   [16, 16, 16, 14],     // recent dip
  "granola-bars":            [12, 10, 10, 8],      // count, not weight
  "orange-juice":            [64, 59, 52, 52],     // fl oz; "half gallon" is no longer 64
  // Items not tracked yet just don't get a chip — silent fallback
};

// Returns shrinkflation info for a deal, or null if no shrink detected.
// { previousSize, currentSize, percentLess, unit } — caller renders.
function computeShrinkflation(dealId) {
  if (!SHRINKFLATION_ALERTS_ENABLED) return null;
  const history = ITEM_SIZE_HISTORY[dealId];
  if (!Array.isArray(history) || history.length < 2) return null;
  const current = history[history.length - 1];
  const max = Math.max(...history.slice(0, -1));
  if (current >= max) return null;  // no shrink
  const percentLess = Math.round(((max - current) / max) * 100);
  if (percentLess < 5) return null;  // ignore noise / minor variance
  const unitData = ITEM_UNIT_DATA[dealId];
  return {
    previousSize: max,
    currentSize: current,
    percentLess,
    unit: unitData ? unitData.unit : "",
  };
}

// Phase B — Standardized unit-price line. Used on deal cards, Smart Cart
// rows, search results, and item detail sheets. Returns null when there's
// no unit data for the deal — never a placeholder.
//
// Compact mode shows just the per-unit price (e.g., "$0.0312/fl oz").
// Full mode includes the package size annotation ("5 oz · $0.30/oz").
function UnitPriceLine({ deal, compact = false, color = T.skyBright }) {
  if (!UNIT_PRICE_DISPLAY_ENABLED) return null;
  const up = computeUnitPrice(deal);
  if (!up) return null;
  return (
    <div style={{fontSize:10,color,fontWeight:700,marginTop:2,display:"flex",alignItems:"center",gap:5,lineHeight:1.3}}>
      <span style={{fontSize:10,opacity:.85}}>📏</span>
      <span>{up.label}</span>
      {!compact && (
        <span style={{color:T.ink50,fontWeight:500}}>· {up.packageSize}</span>
      )}
    </div>
  );
}

// Phase B — Shrinkflation warning chip. Renders only when computeShrinkflation
// returns a non-null result. Mirrors the insight chip's warn palette
// (T.butterSoft / T.butter / T.warn) so it reads as "heads up" not "danger."
function ShrinkflationChip({ dealId }) {
  const shrink = computeShrinkflation(dealId);
  if (!shrink) return null;
  return (
    <div style={{
      display:"inline-flex",
      alignItems:"center",
      gap:5,
      background:T.butterSoft,
      border:`1px solid ${T.butter}`,
      borderRadius:8,
      padding:"3px 8px",
      fontFamily:FONT_SANS,
      fontSize:10,
      color:T.warn,
      fontWeight:600,
      letterSpacing:.2,
      marginBottom:4,
    }} title={`Package size has decreased over time. Was ${shrink.previousSize} ${shrink.unit}, now ${shrink.currentSize} ${shrink.unit}.`}>
      <span style={{fontSize:10}}>⚠</span>
      <span>Shrunk · {shrink.previousSize}→{shrink.currentSize} {shrink.unit} ({shrink.percentLess}% less)</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   UPC LOOKUP — for the barcode scanner
   ═══════════════════════════════════════════════════════════
   Maps real UPC barcodes to MASTER_LIST item IDs. As you onboard
   real users, this should be replaced with an Instacart Connect
   API call (`GET /products?upc=<barcode>`) that returns canonical
   product data. For now it's a small static demo lookup.

   To add real items: scan an item with Google Lens to find its UPC,
   then add `"<upc>": <itemId>` here.
   ═══════════════════════════════════════════════════════════ */
const UPC_TO_ITEM = {
  // Real UPCs for common Publix/ALDI items (sample — extend as needed)
  "041220576456": "organic-whole-milk",
  "041220117154": "free-range-eggs",
  "041303001257": "chicken-breast",         // Boneless skinless chicken breast
  "041220500505": "baby-spinach",
  "041220116881": "greek-yogurt",           // Greek yogurt plain
  "894156001124": "organic-quinoa",
  "898391001408": "almond-butter",
  "012000001031": "sparkling-water",
  "041220116522": "bread",                  // Multigrain bread
  "041220117079": "olive-oil",              // Extra virgin olive oil
  "041220016709": "ground-turkey",          // 93/7
  "041220012503": "cherry-tomatoes",
};

/* ═══════════════════════════════════════════════════════════════════════════
   DEAL PRICING — SINGLE SOURCE OF TRUTH
   ═══════════════════════════════════════════════════════════════════════════
   Every component that needs to know what a deal costs, what the user pays,
   how many units are required, or what the math caption should say MUST go
   through the helpers in this section. Do NOT inline label-string parsing
   anywhere else — that's how the "BOGO shows $0.00" bug happened.

   Deal data convention reminder:
     - BOGO Free:  origPrice = $X, salePrice = 0, savings = $X
       (the "second one is free" → salePrice in data is the discounted unit's price)
     - BOGO 50%:   origPrice = $X, salePrice = $X/2 OR savings encodes it
     - B2G1:       origPrice = $X, salePrice = 0, savings = $X
     - Plain sale: origPrice = orig, salePrice = sale price, savings = diff
     - Regular:    origPrice = price, salePrice = price (or 0), savings = 0
   ═══════════════════════════════════════════════════════════════════════════ */

const DEAL_TYPES = Object.freeze({
  BOGO_FREE: "bogo_free",   // Buy 1, get 1 free
  BOGO_50:   "bogo_50",     // Buy 1, get 1 at 50% off
  B2G1:      "b2g1",        // Buy 2, get 1 free
  SALE:      "sale",        // Standard discount (salePrice < origPrice)
  REGULAR:   "regular",     // No discount
});

/**
 * Classify a deal into one of DEAL_TYPES.
 * Reads only the deal label — pricing fields are interpreted *by* the type.
 */
function dealType(deal) {
  if (!deal) return DEAL_TYPES.REGULAR;
  const label = (deal.dealLabel || "").toUpperCase();
  if (label.includes("B2G1") || label.includes("BUY 2 GET 1")) return DEAL_TYPES.B2G1;
  if (label.includes("BOGO")) {
    return label.includes("50") ? DEAL_TYPES.BOGO_50 : DEAL_TYPES.BOGO_FREE;
  }
  if (deal.salePrice > 0 && deal.salePrice < deal.origPrice) return DEAL_TYPES.SALE;
  return DEAL_TYPES.REGULAR;
}

/**
 * Multi-unit bundle deals require buying N items to trigger the savings.
 * Used to gate stepper minimums and warning hints.
 */
function isBundleDeal(deal) {
  const t = dealType(deal);
  return t === DEAL_TYPES.BOGO_FREE || t === DEAL_TYPES.BOGO_50 || t === DEAL_TYPES.B2G1;
}

/** Minimum qty required to trigger the deal at all. */
function bundleMinQty(deal) {
  const t = dealType(deal);
  if (t === DEAL_TYPES.B2G1) return 3;
  if (t === DEAL_TYPES.BOGO_FREE || t === DEAL_TYPES.BOGO_50) return 2;
  return 1;
}

/** Default qty for split-cart lines — same as min so the deal triggers. */
function defaultBundleQty(deal) {
  return bundleMinQty(deal);
}

/**
 * Cash you actually pay for `qty` units of a deal.
 * BOGO Free: pay full price for ceil(qty/2), get floor(qty/2) free.
 * BOGO 50%:  pay full for ceil(qty/2), half-price for floor(qty/2).
 * B2G1:      pay full for (qty - floor(qty/3)) units; floor(qty/3) are free.
 * SALE:      salePrice × qty
 * REGULAR:   origPrice × qty
 */
function effectiveLinePrice(deal, qty) {
  if (!deal || qty <= 0) return 0;
  const orig = deal.origPrice || 0;
  switch (dealType(deal)) {
    case DEAL_TYPES.BOGO_FREE:
      return orig * Math.ceil(qty / 2);
    case DEAL_TYPES.BOGO_50:
      return (orig * Math.ceil(qty / 2)) + (orig * 0.5 * Math.floor(qty / 2));
    case DEAL_TYPES.B2G1:
      return orig * (qty - Math.floor(qty / 3));
    case DEAL_TYPES.SALE:
      return deal.salePrice * qty;
    case DEAL_TYPES.REGULAR:
    default:
      return (deal.salePrice > 0 ? deal.salePrice : orig) * qty;
  }
}

/**
 * Effective per-unit price after the deal triggers — used for sorting and
 * cross-store comparison. For bundles, this is the average price per unit
 * when buying exactly the minimum bundle qty.
 */
function effectivePerUnit(deal) {
  if (!deal) return Infinity;
  const orig = deal.origPrice || 0;
  switch (dealType(deal)) {
    case DEAL_TYPES.BOGO_FREE: return orig / 2;
    case DEAL_TYPES.BOGO_50:   return orig * 0.75;
    case DEAL_TYPES.B2G1:      return orig * (2 / 3);
    case DEAL_TYPES.SALE:      return deal.salePrice;
    case DEAL_TYPES.REGULAR:
    default:                   return deal.salePrice > 0 ? deal.salePrice : orig;
  }
}

/**
 * How many "free units" worth of savings the user actually triggers at this qty.
 * Multiply by deal.savings to get the dollar amount saved on this line.
 */
function triggeredSavingsMultiplier(deal, qty) {
  if (!deal || qty <= 0) return 0;
  switch (dealType(deal)) {
    case DEAL_TYPES.BOGO_FREE:
    case DEAL_TYPES.BOGO_50:
      return Math.floor(qty / 2);
    case DEAL_TYPES.B2G1:
      return Math.floor(qty / 3);
    case DEAL_TYPES.SALE:
    case DEAL_TYPES.REGULAR:
    default:
      return qty;
  }
}

/**
 * Headline price for a single-row card (deal browser tile, scanner result row).
 * For bundles, shows the per-unit shelf price (origPrice). For sales, shows salePrice.
 */
function displayPrice(deal) {
  if (!deal) return 0;
  const t = dealType(deal);
  if (t === DEAL_TYPES.SALE) return deal.salePrice;
  return deal.origPrice || 0;
}

/**
 * Human-readable explanation for bundle deals: "buy 2 for $13.98 · $6.99 ea".
 * Returns null for non-bundle deals (caller can decide to render unit-price label instead).
 */
function dealMathCaption(deal) {
  if (!deal) return null;
  const orig = deal.origPrice || 0;
  switch (dealType(deal)) {
    case DEAL_TYPES.BOGO_FREE:
      return `buy 2 for $${orig.toFixed(2)} · $${(orig / 2).toFixed(2)} ea`;
    case DEAL_TYPES.BOGO_50:
      return `2 for $${(orig * 1.5).toFixed(2)} · $${(orig * 0.75).toFixed(2)} ea`;
    case DEAL_TYPES.B2G1:
      return `buy 3 for $${(orig * 2).toFixed(2)} · $${(orig * 2 / 3).toFixed(2)} ea`;
    default:
      return null;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   END DEAL PRICING
   ═══════════════════════════════════════════════════════════════════════════ */

// Look up an item by UPC, return enriched record with all available deals

function lookupByBarcode(upc) {
  const itemId = UPC_TO_ITEM[upc];
  if (!itemId) return null;

  // Phase 3 — prefer EXPANDED_CATALOG as the authoritative item source, fall
  // back to MASTER_LIST when the canonical entry isn't in the expanded catalog.
  const canonical = getCanonicalItem(itemId);
  const masterItem = MASTER_LIST.find(m => m.id === itemId);
  const item = canonical || masterItem;
  if (!item) return null;

  // Build per-store deal rows for every store carrying this canonical item.
  // If a store has an active promotion, we prefer that (it has real salePrice,
  // savings, dealLabel). Otherwise we synthesize a regular-price row from
  // STORE_AVAILABILITY data.
  const availability = STORE_AVAILABILITY[itemId] || [];
  const deals = [];

  for (const avail of availability) {
    const store = STORES.find(s => s.id === avail.storeId);
    if (!store) continue;
    const promo = store.deals.find(d => d.id === itemId);
    if (promo) {
      deals.push({ ...promo, store });
    } else {
      deals.push({
        id: itemId,
        name: item.name,
        category: item.category,
        dealLabel: "REG PRICE",
        savings: 0,
        origPrice: avail.price,
        salePrice: avail.price,
        expiresIn: 999,
        ibotta: 0,
        stackable: false,
        stockLevel: "unknown",
        store,
        _catalogSourced: true,
      });
    }
  }

  // Fallback: neither catalog nor availability had the item — use legacy logic
  if (deals.length === 0) {
    const legacyDeals = STORES.flatMap(s =>
      s.deals.filter(d => d.id === itemId).map(d => ({ ...d, store: s }))
    );
    deals.push(...legacyDeals);
  }

  // Sort cheapest first by effective per-unit price
  deals.sort((a, b) => {
    const pa = effectivePerUnit(a);
    const pb = effectivePerUnit(b);
    if (pa !== pb) return pa - pb;
    return (b.savings || 0) - (a.savings || 0);
  });

  return {
    item,
    deals,
    bestDeal: deals[0] || null,
    upc,
    canonicalId: itemId,
  };
}

/* ═══════════════════════════════════════════════════════════
   COUPON CLIPPING — some stores require in-app clipping
   ═══════════════════════════════════════════════════════════
   Publix, Kroger, and Target require users to manually "clip"
   digital coupons inside their own apps BEFORE checkout for
   the discount to apply. CartIQ detects deals that need this
   and reminds users before the Instacart handoff.
   ═══════════════════════════════════════════════════════════ */
const CLIPPING_REQUIRED_STORES = {
  publix: {
    appName: "Publix",
    iosUrl: "https://apps.apple.com/us/app/publix/id1133002911",
    androidUrl: "https://play.google.com/store/apps/details?id=com.publix.services",
    webUrl: "https://www.publix.com/savings/digital-coupons",
  },
  kroger: {
    appName: "Kroger",
    iosUrl: "https://apps.apple.com/us/app/kroger/id403901186",
    androidUrl: "https://play.google.com/store/apps/details?id=com.kroger.mobile",
    webUrl: "https://www.kroger.com/cl/coupons",
  },
  target: {
    appName: "Target Circle",
    iosUrl: "https://apps.apple.com/us/app/target/id297430070",
    androidUrl: "https://play.google.com/store/apps/details?id=com.target.ui",
    webUrl: "https://www.target.com/circle",
  },
};

// A deal requires clipping if it's a BOGO or coupon-style deal at a store
// that uses the digital-coupon model. We detect by store ID and deal label.
function requiresClipping(deal) {
  const storeId = deal.store?.id || deal.storeId;
  if (!storeId || !CLIPPING_REQUIRED_STORES[storeId]) return false;
  const label = (deal.dealLabel || "").toUpperCase();
  return label.includes("BOGO") || label.includes("COUPON") || label.includes("DIGITAL") || label.includes("CLIP");
}

// Find related deals when a scanned UPC isn't in the catalog.
// Uses simple word-overlap fuzzy matching against MASTER_LIST names
// plus category hints. Returns up to `limit` deals sorted by relevance.
function findSimilarDeals(hintText, limit = 5) {
  if (!hintText) return [];
  const words = hintText.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  if (words.length === 0) return [];
  const allDeals = STORES.flatMap(s => s.deals.map(d => ({ ...d, store: s })));
  // Score each deal by word overlap with the hint
  const scored = allDeals.map(deal => {
    const name = deal.name.toLowerCase();
    let score = 0;
    for (const w of words) {
      if (name.includes(w)) score += 2;
    }
    // Category hint: if hint contains a category word
    if (deal.category && words.some(w => deal.category.toLowerCase().includes(w))) score += 1;
    return { deal, score };
  }).filter(s => s.score > 0);
  scored.sort((a, b) => b.score - a.score || b.deal.savings - a.deal.savings);
  // Dedupe by item id — keep first (best-scored) match per item
  const seen = new Set();
  const out = [];
  for (const { deal } of scored) {
    if (seen.has(deal.id)) continue;
    seen.add(deal.id);
    out.push(deal);
    if (out.length >= limit) break;
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════
   STAPLES PICKER — Phase A onboarding catalog
   ═══════════════════════════════════════════════════════════
   25 items shown during the post-store-selection onboarding step.
   Top 8 (in this order) start checked by default — they're the
   most universally bought items in US grocery shopping.

   Order of items inside each category is intentional (most-bought
   first), but display order is grouped by category so the picker
   reads naturally.

   Schema:
     id          — canonical item ID (matches MASTER_LIST when possible)
     name        — display name
     category    — grouping label (Dairy, Produce, Meat, Pantry,
                   Bakery, Frozen, Beverages)
     defaultQty  — seeded quantity when added to pantry
     defaultUnit — display unit on Pantry tile
     icon        — single emoji shown on the picker chip
     defaultOn   — true if this item starts checked
   ═══════════════════════════════════════════════════════════ */
const STAPLES_PICKER_ITEMS = [
  // Dairy (3) — high-frequency, universal
  { id: "organic-whole-milk", name: "Milk",          category: "Dairy",     defaultQty: 1, defaultUnit: "gal",   icon: "🥛", defaultOn: true  },
  { id: "free-range-eggs",    name: "Eggs",          category: "Dairy",     defaultQty: 1, defaultUnit: "doz",   icon: "🥚", defaultOn: true  },
  { id: "greek-yogurt",       name: "Yogurt",        category: "Dairy",     defaultQty: 2, defaultUnit: "tub",   icon: "🥛", defaultOn: false },
  { id: "cheese-block",       name: "Cheese",        category: "Dairy",     defaultQty: 1, defaultUnit: "block", icon: "🧀", defaultOn: false },

  // Produce (5) — heavy weekly turnover
  { id: "bananas",            name: "Bananas",       category: "Produce",   defaultQty: 1, defaultUnit: "bunch", icon: "🍌", defaultOn: true  },
  { id: "baby-spinach",       name: "Salad greens",  category: "Produce",   defaultQty: 1, defaultUnit: "bag",   icon: "🥬", defaultOn: true  },
  { id: "avocados",           name: "Avocados",      category: "Produce",   defaultQty: 3, defaultUnit: "ea",    icon: "🥑", defaultOn: false },
  { id: "tomatoes",           name: "Tomatoes",      category: "Produce",   defaultQty: 1, defaultUnit: "lb",    icon: "🍅", defaultOn: false },
  { id: "apples",             name: "Apples",        category: "Produce",   defaultQty: 1, defaultUnit: "bag",   icon: "🍎", defaultOn: false },

  // Meat & seafood (4)
  { id: "chicken-breast",     name: "Chicken",       category: "Meat",      defaultQty: 2, defaultUnit: "lb",    icon: "🍗", defaultOn: true  },
  { id: "ground-beef",        name: "Ground beef",   category: "Meat",      defaultQty: 1, defaultUnit: "lb",    icon: "🥩", defaultOn: false },
  { id: "ground-turkey",      name: "Ground turkey", category: "Meat",      defaultQty: 1, defaultUnit: "lb",    icon: "🦃", defaultOn: false },
  { id: "atlantic-salmon",    name: "Salmon",        category: "Seafood",   defaultQty: 1, defaultUnit: "lb",    icon: "🐟", defaultOn: false },

  // Pantry (5)
  { id: "bread",              name: "Bread",         category: "Bakery",    defaultQty: 1, defaultUnit: "loaf",  icon: "🍞", defaultOn: true  },
  { id: "pasta",              name: "Pasta",         category: "Pantry",    defaultQty: 2, defaultUnit: "box",   icon: "🍝", defaultOn: false },
  { id: "rice",               name: "Rice",          category: "Pantry",    defaultQty: 1, defaultUnit: "bag",   icon: "🍚", defaultOn: false },
  { id: "olive-oil",          name: "Olive oil",     category: "Pantry",    defaultQty: 1, defaultUnit: "bottle",icon: "🫒", defaultOn: false },
  { id: "almond-butter",      name: "Nut butter",    category: "Pantry",    defaultQty: 1, defaultUnit: "jar",   icon: "🥜", defaultOn: false },

  // Frozen (2)
  { id: "organic-frozen-berries", name: "Frozen berries", category: "Frozen", defaultQty: 1, defaultUnit: "bag", icon: "🫐", defaultOn: false },
  { id: "frozen-vegetables",  name: "Frozen vegetables", category: "Frozen", defaultQty: 2, defaultUnit: "bag",  icon: "🥦", defaultOn: false },

  // Beverages (3)
  { id: "coffee",             name: "Coffee",        category: "Beverages", defaultQty: 1, defaultUnit: "bag",   icon: "☕", defaultOn: true  },
  { id: "sparkling-water",    name: "Sparkling water",category: "Beverages",defaultQty: 1, defaultUnit: "case",  icon: "💧", defaultOn: true  },
  { id: "orange-juice",       name: "Juice",         category: "Beverages", defaultQty: 1, defaultUnit: "bottle",icon: "🧃", defaultOn: false },

  // Snacks (3)
  { id: "chips",              name: "Chips",         category: "Pantry",    defaultQty: 1, defaultUnit: "bag",   icon: "🥨", defaultOn: false },
  { id: "granola-bars",       name: "Granola bars",  category: "Pantry",    defaultQty: 1, defaultUnit: "box",   icon: "🍫", defaultOn: false },
  { id: "crackers",           name: "Crackers",      category: "Pantry",    defaultQty: 1, defaultUnit: "box",   icon: "🍘", defaultOn: false },
];

// Display order of categories in the picker. Items not matching a category
// here go to the bottom in catalog order.
const STAPLES_CATEGORY_ORDER = ["Dairy", "Produce", "Meat", "Seafood", "Bakery", "Pantry", "Frozen", "Beverages"];

const PANTRY_DEFAULT = [
  {id:"organic-whole-milk",name:"Organic Whole Milk",qty:.5,unit:"gal",   daysLeft:3, lastBought:"Mar 22", expiryDate:"2026-04-02"},
  {id:"baby-spinach",      name:"Baby Spinach",      qty:1, unit:"bag",   daysLeft:2, lastBought:"Mar 24", expiryDate:"2026-04-01"},
  {id:"almond-butter",     name:"Almond Butter",     qty:.3,unit:"jar",   daysLeft:4, lastBought:"Mar 14", expiryDate:"2026-09-15"},
  {id:"olive-oil",         name:"Olive Oil",         qty:.5,unit:"bottle",daysLeft:10,lastBought:"Mar 1",  expiryDate:"2027-01-01"},
  {id:"organic-quinoa",    name:"Organic Quinoa",    qty:.2,unit:"bag",   daysLeft:2, lastBought:"Mar 20", expiryDate:"2026-08-30"},
  {id:"free-range-eggs",   name:"Free Range Eggs",   qty:4, unit:"eggs",  daysLeft:5, lastBought:"Mar 20", expiryDate:"2026-04-08"},
];

const HOUSEHOLD = [
  {id:1,name:"Sarah",  avatar:"👩",role:"admin", lastActive:"now"},
  {id:2,name:"Marcus", avatar:"👨",role:"member",lastActive:"2h ago"},
  {id:3,name:"Emma",   avatar:"👧",role:"member",lastActive:"yesterday"},
];

const PRICE_HISTORY = {
  "chicken-breast":[8.99,8.49,8.99,7.99,8.99,4.49],
  "free-range-eggs":[5.49,5.99,5.49,5.49,5.49,2.99],
  "baby-spinach":[2.99,2.99,2.49,2.99,2.99,1.49],
  "greek-yogurt":[2.49,2.49,2.99,2.49,2.49,0],
  "bread":[3.49,3.99,3.49,3.49,3.49,1.70],
  "organic-whole-milk":[6.99,6.49,6.99,6.99,6.99,0],
  "ground-turkey":[5.99,6.49,5.99,5.99,5.99,0],
  "atlantic-salmon":[9.99,10.99,9.99,9.99,10.49,0],
  "avocados":[1.99,1.79,1.99,2.29,1.99,1.25],
  "sparkling-water":[9.99,9.99,9.49,9.99,9.99,4.99],
  "organic-quinoa":[13.99,13.99,12.99,13.99,13.99,11.19],
  "almond-butter":[9.99,10.49,9.99,9.99,9.99,4.99],
  "cherry-tomatoes":[3.99,3.49,3.99,3.99,4.29,2.50],
  "organic-frozen-berries":[9.99,9.49,9.99,9.99,9.99,7.49],
  "organic-frozen-acai":[9.99,9.49,9.99,9.99,9.99,7.49],
  "olive-oil":[17.99,17.99,15.99,17.99,17.99,12.59],
};

const MOCK_TXN_HISTORY = [
  {id:"txn_001",date:"Mar 24",store:"Publix",   grossSavings:28.92,deliveryFee:9.99,netGain:18.93,estCommission:.57,userCard:"via Instacart",status:"redirected"},
  {id:"txn_002",date:"Mar 22",store:"ALDI",     grossSavings:12.78,deliveryFee:3.99,netGain:8.79, estCommission:.26,userCard:"via Instacart",status:"redirected"},
  {id:"txn_003",date:"Mar 20",store:"Sprouts",  grossSavings:19.67,deliveryFee:6.99,netGain:12.68,estCommission:.38,userCard:"via Instacart",status:"redirected"},
  {id:"txn_004",date:"Mar 18",store:"Fresh Mkt",grossSavings:21.93,deliveryFee:7.99,netGain:13.94,estCommission:.42,userCard:"via Instacart",status:"redirected"},
  {id:"txn_005",date:"Mar 15",store:"Publix",   grossSavings:31.94,deliveryFee:9.99,netGain:21.95,estCommission:.66,userCard:"via Instacart",status:"redirected"},
];

const MOCK_PLATFORM = {
  totalEarned:3847.28, thisMonth:412.55, lastMonth:389.20, thisWeek:97.84,
  activeUsers:1842, avgNetGain:18.42, avgFee:0.46,
  payoutSchedule:"Every Monday", nextPayout:"Mar 31 — est. $97.84",
  stripeAccountId:"impact_cartiq_•••",
  projections:[{users:1000,weekly:876,annual:45552},{users:5000,weekly:4380,annual:227760},{users:10000,weekly:8760,annual:455520},{users:50000,weekly:43800,annual:2277600}],
  weeklyData:[22.10,31.47,28.88,44.22,38.91,52.14,61.33,55.80,72.44,67.91,81.20,78.55,89.33,97.84],
};

/* ═══════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════ */
const netBenefit = s => s.deals.reduce((t,d)=>t+d.savings,0) - s.deliveryFee - s.memberFee;
const totalIbotta = deals => deals.filter(d=>d.stackable).reduce((s,d)=>s+d.ibotta,0);

// Returns an itemized fee estimate for a cart. Display-only — never used
// for rewards math. `applySurge` reflects whether the user expects a peak
// delivery window (default true = conservative / worst-case estimate).
function estimateOrderTotal(cartTotal, deliveryFee, applySurge = true) {
  const serviceFee = cartTotal * FEE_MODEL.serviceFeeRate;
  const surge = applySurge ? cartTotal * FEE_MODEL.busySurgeRate : 0;
  const fees = serviceFee + (deliveryFee || 0) + surge;
  return {
    subtotal: cartTotal,
    serviceFee,
    deliveryFee: deliveryFee || 0,
    surge,
    feesTotal: fees,
    estimatedTotal: cartTotal + fees,
  };
}

/* ═══════════════════════════════════════════════════════════
   RATINGS SERVICE — Google Places + Instacart Connect
   ═══════════════════════════════════════════════════════════
   In-memory cache keyed by placeId / productId. Entries expire
   after RATINGS_CACHE_HOURS. Persistent cache via window.storage
   survives page reloads to stay under free tier quotas.
   ═══════════════════════════════════════════════════════════ */
const ratingsCache = { store: new Map(), product: new Map() };

async function getCachedOrFetch(kind, key, fetcher) {
  // 1) In-memory cache
  const mem = ratingsCache[kind].get(key);
  if (mem && Date.now() - mem.ts < RATINGS_CACHE_HOURS * 3600 * 1000) return mem.data;

  // 2) Persistent cache (survives reload)
  try {
    const stored = await window.storage.get(`rating_${kind}_${key}`);
    if (stored) {
      const parsed = JSON.parse(stored.value);
      if (Date.now() - parsed.ts < RATINGS_CACHE_HOURS * 3600 * 1000) {
        ratingsCache[kind].set(key, parsed);
        return parsed.data;
      }
    }
  } catch(e) {}

  // 3) Live fetch
  try {
    const data = await fetcher();
    if (data) {
      const entry = { ts: Date.now(), data };
      ratingsCache[kind].set(key, entry);
      try { await window.storage.set(`rating_${kind}_${key}`, JSON.stringify(entry)); } catch(e) {}
      return data;
    }
  } catch(e) {
    console.warn(`[ratings] ${kind} fetch failed for ${key}:`, e.message);
  }
  return null;
}

/* ─── Google Places — store ratings ──────────────────────── */
async function fetchGooglePlaceDetails(placeId) {
  if (!hasGoogleKey() || !placeId || placeId.includes("_____")) return null;
  // Same-origin proxy at api/places.js holds the server-side key.
  const url = `/api/places?placeId=${encodeURIComponent(placeId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.status !== "OK") throw new Error(json.status);
  const r = json.result;
  return {
    rating: r.rating ?? null,
    reviewCount: r.user_ratings_total ?? 0,
    address: r.formatted_address ?? "",
    isOpen: r.opening_hours?.open_now ?? null,
    reviews: (r.reviews ?? []).slice(0, 5).map(rv => ({
      author: rv.author_name,
      rating: rv.rating,
      text: rv.text,
      relativeTime: rv.relative_time_description,
      profilePhoto: rv.profile_photo_url,
    })),
  };
}

/* ─── Google Geocoding — ZIP → lat/lng ───────────────────── */
async function zipToLatLng(zip) {
  if (!hasGoogleKey() || !zip || !/^\d{5}$/.test(zip)) return null;
  // Same-origin proxy at api/geocode.js holds the server-side key.
  const url = `/api/geocode?zip=${encodeURIComponent(zip)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.status !== "OK" || !json.results?.[0]) return null;
  const loc = json.results[0].geometry.location;
  const addr = json.results[0].formatted_address;
  return { lat: loc.lat, lng: loc.lng, address: addr };
}

/* ─── Google Places Nearby — find groceries near a ZIP ───── */
// Maps Google's "types" and name patterns to CartIQ chain IDs
const CHAIN_PATTERNS = [
  { id:"aldi",        name:"ALDI",             match:/\baldi\b/i },
  { id:"publix",      name:"Publix",           match:/\bpublix\b/i },
  { id:"sprouts",     name:"Sprouts",          match:/\bsprouts\b/i },
  { id:"freshMarket", name:"The Fresh Market", match:/\bfresh market\b/i },
  { id:"wholeFoods",  name:"Whole Foods",      match:/\bwhole foods\b/i },
  { id:"trader",      name:"Trader Joe's",     match:/\btrader joe/i },
  { id:"target",      name:"Target",           match:/\btarget\b/i },
  { id:"walmart",     name:"Walmart",          match:/\bwalmart\b/i },
  { id:"kroger",      name:"Kroger",           match:/\bkroger\b/i },
  { id:"harris",      name:"Harris Teeter",    match:/\bharris teeter\b/i },
  { id:"wegmans",     name:"Wegmans",          match:/\bwegmans\b/i },
  { id:"costco",      name:"Costco",           match:/\bcostco\b/i },
  { id:"samsclub",    name:"Sam's Club",       match:/\bsam'?s club\b/i },
  { id:"winnDixie",   name:"Winn-Dixie",       match:/\bwinn.dixie\b/i },
  { id:"heb",         name:"H-E-B",            match:/\bh.?e.?b\b/i },
  { id:"safeway",     name:"Safeway",          match:/\bsafeway\b/i },
];

// Haversine distance (miles)
function milesBetween(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function matchChain(name) {
  return CHAIN_PATTERNS.find(c => c.match.test(name));
}

async function findStoresNearZip(zip, radiusMiles=15) {
  if (!hasGoogleKey()) return [];
  const geo = await zipToLatLng(zip);
  if (!geo) return [];

  // Nearby Search for type=supermarket (server-side proxy at api/nearby.js).
  const radiusMeters = Math.round(radiusMiles * 1609.34);
  const url = `/api/nearby?lat=${geo.lat}&lng=${geo.lng}&radius=${radiusMeters}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.status !== "OK" && json.status !== "ZERO_RESULTS") throw new Error(json.status);

  const results = (json.results || [])
    .map(p => {
      const chain = matchChain(p.name);
      if (!chain) return null;  // filter out unknown bodegas / gas stations
      return {
        placeId: p.place_id,
        name: p.name,
        chainId: chain.id,
        chainName: chain.name,
        address: p.vicinity,
        lat: p.geometry?.location?.lat,
        lng: p.geometry?.location?.lng,
        rating: p.rating ?? null,
        reviewCount: p.user_ratings_total ?? 0,
        distance: p.geometry?.location ? milesBetween(geo.lat, geo.lng, p.geometry.location.lat, p.geometry.location.lng) : null,
        openNow: p.opening_hours?.open_now ?? null,
      };
    })
    .filter(Boolean)
    .sort((a,b) => (a.distance ?? 999) - (b.distance ?? 999));

  return { geo, stores: results };
}

/* ─── Instacart Connect — retailer search by ZIP (stub) ──── */
// When Instacart Connect approval comes through, this replaces
// findStoresNearZip as the primary data source. Returns the same
// shape, so the rest of the app doesn't need to change.
async function findInstacartRetailersByZip(zip) {
  if (!hasInstacartKey() || !zip) return null;
  // Typical Connect flow (confirm exact endpoint with Instacart docs):
  //   GET /v1/retailers?postal_code={zip}
  //   Response: { retailers: [{ id, name, logo_url, min_order, delivery_fee, ... }] }
  try {
    const res = await fetch(`${INSTACART_API_BASE}/retailers?postal_code=${encodeURIComponent(zip)}`, {
      headers: { "Authorization": `Bearer ${INSTACART_API_KEY}`, "Accept": "application/json" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json.retailers || []).map(r => ({
      placeId: r.google_place_id || null,  // Instacart may pass Google IDs through
      name: r.name,
      chainId: r.slug || r.id,
      chainName: r.name,
      address: r.address || "",
      rating: null,  // Instacart may not expose store ratings — fall back to Google
      reviewCount: 0,
      distance: r.distance_miles ?? null,
      deliveryFee: r.delivery_fee,
      minOrder: r.min_order,
      // Flag the source so the UI can indicate data authority
      source: "instacart",
    }));
  } catch(e) {
    console.warn("[instacart] retailer search failed:", e.message);
    return null;
  }
}

// Unified lookup — prefers Instacart when available, falls back to Google
async function findStoresForZip(zip) {
  const instacart = await findInstacartRetailersByZip(zip);
  if (instacart && instacart.length > 0) {
    return { source: "instacart", stores: instacart, geo: null };
  }
  const google = await findStoresNearZip(zip);
  if (!google) return { source: "none", stores: [], geo: null };
  return { source: "google", stores: google.stores, geo: google.geo };
}

/* ─── Instacart Connect — product ratings (stub) ─────────── */
async function fetchInstacartProductRating(productId) {
  if (!hasInstacartKey() || !productId) return null;
  // This is a stub. Instacart Connect's exact endpoint & auth flow
  // depend on your partnership tier. Typical flow:
  //   1) OAuth2 client-credentials → bearer token
  //   2) GET /v1/products/{id} with Authorization: Bearer <token>
  //   3) Response includes avg_rating, review_count, recent_reviews
  const res = await fetch(`${INSTACART_API_BASE}/products/${encodeURIComponent(productId)}`, {
    headers: {
      "Authorization": `Bearer ${INSTACART_API_KEY}`,
      "Accept": "application/json",
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return {
    rating: json.avg_rating ?? null,
    reviewCount: json.review_count ?? 0,
    topReview: json.recent_reviews?.[0] ?? null,
  };
}

/* ─── React hooks for ratings ────────────────────────────── */
function useStoreRating(placeId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!placeId || placeId.includes("_____")) return;
    let cancelled = false;
    setLoading(true);
    getCachedOrFetch("store", placeId, () => fetchGooglePlaceDetails(placeId))
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [placeId]);
  return { data, loading };
}

function useProductRating(productId) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!productId || !hasInstacartKey()) return;
    let cancelled = false;
    getCachedOrFetch("product", productId, () => fetchInstacartProductRating(productId))
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productId]);
  return data;
}

/* ─── Product Photo Cascade ──────────────────────────────────
   Tries multiple sources in order of authority:
     1. Instacart Connect (if key configured) — canonical retailer photo
     2. Open Food Facts (if enabled) — community-sourced packaged goods
     3. Curated CDN photos (CURATED_PHOTOS map below)
     4. Fallback emoji from getItemIcon()

   ALL LIVE CALLS DISABLED BY DEFAULT. To enable:
     - Set ENABLE_OPEN_FOOD_FACTS = true to turn on the community DB
     - Add Instacart Connect key to enable the canonical source
   ═══════════════════════════════════════════════════════════ */
const ENABLE_OPEN_FOOD_FACTS = false;  // Set to true once you've vetted the source

// Curated stock photos — drop in real CDN URLs when ready (Cloudinary, Vercel, etc.)
// Keys are MASTER_LIST item IDs. Empty string = no curated photo (will fall back to emoji)
const CURATED_PHOTOS = {
  // 1: "https://your-cdn.com/cartiq/milk.jpg",
  // 2: "https://your-cdn.com/cartiq/eggs.jpg",
  // ... add as you curate them
};

async function fetchInstacartProductPhoto(productId) {
  if (!hasInstacartKey() || !productId) return null;
  try {
    const res = await fetch(`${INSTACART_API_BASE}/products/${encodeURIComponent(productId)}`, {
      headers: { "Authorization": `Bearer ${INSTACART_API_KEY}`, "Accept": "application/json" },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.image_url || json.images?.[0]?.url || null;
  } catch(e) { return null; }
}

async function fetchOpenFoodFactsPhoto(productName) {
  if (!ENABLE_OPEN_FOOD_FACTS || !productName) return null;
  try {
    // Open Food Facts is free, no API key, no rate limits
    // Search by product name, return first hit's image_url
    const url = `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(productName)}&search_simple=1&action=process&json=1&page_size=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const product = json.products?.[0];
    return product?.image_url || product?.image_front_url || null;
  } catch(e) { return null; }
}

function useProductPhoto(item) {
  // item: { id, name, instacartProductId? }
  const [photo, setPhoto] = useState(() => CURATED_PHOTOS[item?.id] || null);

  useEffect(() => {
    if (!item) return;
    // 0. Curated photo wins immediately if present
    if (CURATED_PHOTOS[item.id]) { setPhoto(CURATED_PHOTOS[item.id]); return; }

    let cancelled = false;
    (async () => {
      // 1. Instacart Connect (most authoritative)
      if (hasInstacartKey() && item.instacartProductId) {
        const url = await getCachedOrFetch("product", `photo_${item.instacartProductId}`,
          () => fetchInstacartProductPhoto(item.instacartProductId).then(u => u ? { photo: u } : null));
        if (!cancelled && url?.photo) { setPhoto(url.photo); return; }
      }
      // 2. Open Food Facts (community)
      if (ENABLE_OPEN_FOOD_FACTS) {
        const url = await getCachedOrFetch("product", `off_${item.name}`,
          () => fetchOpenFoodFactsPhoto(item.name).then(u => u ? { photo: u } : null));
        if (!cancelled && url?.photo) { setPhoto(url.photo); return; }
      }
      // 3. Fall through to emoji (no photo set)
    })();
    return () => { cancelled = true; };
  }, [item?.id, item?.name, item?.instacartProductId]);

  return photo;
}

/* ─── ProductImage component — photo if available, emoji otherwise */
function ProductImage({ item, size=40, rounded=true }) {
  const photo = useProductPhoto(item);
  const [errored, setErrored] = useState(false);
  if (photo && !errored) {
    return (
      <img
        src={photo}
        alt={item?.name || ""}
        onError={() => setErrored(true)}
        style={{
          width: size, height: size, objectFit: "cover",
          borderRadius: rounded ? Math.round(size * 0.22) : 0,
          background: T.parchment,
          border: `1px solid ${T.hairline}`,
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center",
      background: T.parchment,
      borderRadius: rounded ? Math.round(size * 0.22) : 0,
      border: `1px solid ${T.hairline}`,
      flexShrink: 0,
    }}>
      <ItemIcon emoji={getItemEmoji(item?.id, item?.name || "", item?.category)} size={Math.round(size * 0.55)}/>
    </div>
  );
}

/* ─── Star rating display component ──────────────────────── */
function StarRating({ rating, size=12, showNumber=true }) {
  if (rating == null) return null;
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:size}}>
      <span style={{color:T.warn,letterSpacing:-1}}>
        {"★".repeat(full)}{half ? "⯨" : ""}{"☆".repeat(5 - full - (half?1:0))}
      </span>
      {showNumber && <span style={{color:T.forest,fontWeight:700,fontSize:size-1}}>{rating.toFixed(1)}</span>}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   WATCHLIST — Save items, monitor prices, get sale alerts
   ═══════════════════════════════════════════════════════════
   Data shape per watched item:
   {
     id: "wl_<timestamp>",                    // unique watchlist ID
     itemId: "chicken-breast",                 // links to MASTER_LIST slug (or custom)
     name: "Chicken Breast",
     category: "Meat",
     storeId: "publix",                        // current store source
     storeName: "Publix",
     instacartProductId: "items_xxx" | null,
     currentPrice: 8.99,
     targetPrice: 7.19,                        // user-set, default = currentPrice * 0.8
     allTimeLow: 7.99,
     priceHistory: [{date, price}],            // append-only log
     addedDate: "2026-04-07",
     lastChecked: "2026-04-07",
     alertOnTarget: true,                      // notify when price hits target
     alertOnAllTimeLow: true,                  // notify when new low
     alertOnAnySale: false,                    // notify on any sale
     status: "watching" | "alerted" | "purchased",
     triggeredAlerts: [{type, date, price}],   // history of fired alerts
   }
   ═══════════════════════════════════════════════════════════ */

function makeWatchlistItem({ name, category, storeId, storeName, currentPrice, instacartProductId, itemId }) {
  return {
    id: `wl_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
    itemId: itemId || null,
    name,
    category: category || null,
    storeId: storeId || null,
    storeName: storeName || null,
    instacartProductId: instacartProductId || null,
    currentPrice,
    targetPrice: parseFloat((currentPrice * 0.80).toFixed(2)),
    allTimeLow: currentPrice,
    priceHistory: [{ date: new Date().toISOString().slice(0,10), price: currentPrice }],
    addedDate: new Date().toISOString().slice(0,10),
    lastChecked: new Date().toISOString().slice(0,10),
    alertOnTarget: true,
    alertOnAllTimeLow: true,
    alertOnAnySale: false,
    status: "watching",
    triggeredAlerts: [],
  };
}

// Update a watchlist item with a new observed price + check alerts
// Returns { item, newAlerts: [...] }
function checkWatchlistPrice(item, observedPrice) {
  const today = new Date().toISOString().slice(0,10);
  const newAlerts = [];
  const updated = { ...item, currentPrice: observedPrice, lastChecked: today };

  // Append to price history if price changed or it's a new day
  const lastEntry = item.priceHistory[item.priceHistory.length - 1];
  if (!lastEntry || lastEntry.price !== observedPrice || lastEntry.date !== today) {
    updated.priceHistory = [...item.priceHistory, { date: today, price: observedPrice }];
  }

  // Track all-time low
  if (observedPrice < item.allTimeLow) {
    updated.allTimeLow = observedPrice;
    if (item.alertOnAllTimeLow) {
      newAlerts.push({ type: "allTimeLow", date: today, price: observedPrice, itemId: item.id, itemName: item.name });
    }
  }

  // Target price hit
  if (observedPrice <= item.targetPrice && item.currentPrice > item.targetPrice && item.alertOnTarget) {
    newAlerts.push({ type: "targetHit", date: today, price: observedPrice, itemId: item.id, itemName: item.name });
  }

  // Any sale (price dropped from previous check)
  if (observedPrice < item.currentPrice && item.alertOnAnySale) {
    newAlerts.push({ type: "anySale", date: today, price: observedPrice, itemId: item.id, itemName: item.name });
  }

  if (newAlerts.length > 0) {
    updated.status = "alerted";
    updated.triggeredAlerts = [...item.triggeredAlerts, ...newAlerts];
  }

  return { item: updated, newAlerts };
}

/* ─── Bookmark button — small heart/star toggle for save-for-later ── */
function BookmarkButton({ saved, onClick, size=14 }) {
  return (
    <button onClick={(e)=>{e.stopPropagation(); onClick();}}
      aria-label={saved ? "Remove from watchlist" : "Add to watchlist"}
      aria-pressed={saved}
      style={{
        background: saved ? T.sageSoft : T.parchment,
        border: `1px solid ${saved ? T.sage : T.ink15}`,
        borderRadius: 8,
        width: size + 16, height: size + 16,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        fontSize: size,
        flexShrink: 0,
        padding: 0,
        transition: "all .15s",
      }}
      title={saved ? "Saved to Watchlist" : "Save to Watchlist"}
    >
      <span aria-hidden="true">{saved ? "🔖" : "🤍"}</span>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════
   LISTS — RECURRING SHOPPING MISSIONS
   ═══════════════════════════════════════════════════════════
   Lists is a thin metadata layer over a unified "My Items"
   catalog. One source of truth, viewed three ways:

     • Pantry view  — items where inPantry === true
     • Lists view   — items where lists.length > 0, by name
     • All view     — every item the user has ever interacted with

   ─── My Items record shape ──────────────────────────────────
   {
     id: "free-range-eggs",         // slug; matches MASTER_LIST when known
     name: "Free Range Eggs",
     category: "Dairy",
     // Pantry flags (mirror legacy pantry record fields)
     inPantry: true,
     qty: 4, unit: "eggs",
     daysLeft: 5, lastBought: "Mar 20", expiryDate: "2026-04-08",
     // List membership — names of lists this item belongs to
     lists: ["Weekly", "Kids' Snacks"],
     // Per-list state — { [listName]: { active: bool, qty?: number } }
     listState: { "Weekly": { active: true, qty: 1 } },
     addedDate: "2026-04-07",
     addedFrom: "pantry"|"cart"|"search"|"deal"|"starter"|"unknown",
   }

   ─── List record shape ──────────────────────────────────────
   {
     id: "list_<timestamp>",
     name: "Weekly",                  // user-facing label, also the join key
     occasion: "groceries"|"snacks"|"meal"|"event"|null,
     preferredStores: ["publix"],
     budget: 80 | null,
     dietaryOverride: [] | null,
     createdDate: "2026-05-08",
     lastFilledDate: null,
     fillCount: 0,
   }

   ─── Schedule record shape ──────────────────────────────────
   listSchedules[listName] = {
     cadence: "weekly"|"biweekly"|"monthly"|"custom",
     dayOfWeek: 1, timeOfDay: "morning"|"afternoon"|"evening",
     nextDue: "2026-05-15", remindersEnabled: true,
   }
   ═══════════════════════════════════════════════════════════ */

const MY_ITEM_DEFAULT_FLAGS = {
  inPantry: false,
  lists: [],
  listState: {},
  addedFrom: "unknown",
};

// One-shot migration: adopt every item in the legacy `pantry` array as a
// My Items record with `inPantry: true`. Returns the new array, or null if
// no migration is needed (my_items already populated, or pantry empty).
function migratePantryToMyItems(pantry, currentMyItems) {
  if (Array.isArray(currentMyItems) && currentMyItems.length > 0) return null;
  if (!Array.isArray(pantry) || pantry.length === 0) return null;
  const today = new Date().toISOString().slice(0,10);
  return pantry.map(p => ({
    ...MY_ITEM_DEFAULT_FLAGS,
    ...p,
    inPantry: true,
    lists: [],
    listState: {},
    addedFrom: "pantry",
    addedDate: p.addedDate || today,
  }));
}

// Toggle membership of an item in a named list. Idempotent.
function setItemListMembership(items, itemId, listName, opts = {}) {
  const { active = true, qty } = opts;
  return items.map(item => {
    if (item.id !== itemId) return item;
    const lists = item.lists || [];
    const listState = item.listState || {};
    if (lists.includes(listName)) {
      const next = { ...(listState[listName] || {}), active };
      if (qty != null) next.qty = qty;
      return { ...item, listState: { ...listState, [listName]: next } };
    }
    return {
      ...item,
      lists: [...lists, listName],
      listState: { ...listState, [listName]: { active, ...(qty != null ? { qty } : {}) } },
    };
  });
}

// Remove an item from a named list. Caller decides whether to drop the
// record entirely if both lists and inPantry become empty.
function removeItemFromList(items, itemId, listName) {
  return items.map(item => {
    if (item.id !== itemId) return item;
    const lists = (item.lists || []).filter(l => l !== listName);
    const { [listName]: _removed, ...listState } = item.listState || {};
    return { ...item, lists, listState };
  });
}

// Find a record by stable slug id, or fall back to a case-insensitive name.
function findMyItem(items, key) {
  if (!key) return null;
  const byId = items.find(i => i.id === key);
  if (byId) return byId;
  const lower = String(key).toLowerCase();
  return items.find(i => i.name && i.name.toLowerCase() === lower) || null;
}

// Create a new List record. Name dedupe is the caller's job.
function makeList({ name, occasion, preferredStores, budget, dietaryOverride } = {}) {
  const today = new Date().toISOString().slice(0,10);
  return {
    id: `list_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
    name: (name || "").trim(),
    occasion: occasion || null,
    preferredStores: preferredStores || [],
    budget: budget != null ? budget : null,
    dietaryOverride: dietaryOverride || null,
    createdDate: today,
    lastFilledDate: null,
    fillCount: 0,
  };
}

/* ═══════════════════════════════════════════════════════════
   ANIMATED COUNTER
   ═══════════════════════════════════════════════════════════ */
function useCounter(target, dur=1200) {
  const [v,setV]=useState(0); const r=useRef();
  useEffect(()=>{
    const t0=Date.now();
    const tick=()=>{ const p=Math.min((Date.now()-t0)/dur,1); setV(target*(1-Math.pow(1-p,3))); if(p<1) r.current=requestAnimationFrame(tick); };
    r.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(r.current);
  },[target,dur]);
  return v;
}
function AnimNum({v,pre="$",suf="",dec=2,color,size=48,bold=true}) {
  const n=useCounter(v);
  return <span style={{fontFamily:FONT_DISPLAY,fontSize:size,fontWeight:700,color,letterSpacing:-1,lineHeight:1}}>{pre}{n.toFixed(dec)}{suf}</span>;
}

/* ═══════════════════════════════════════════════════════════
   GLASS CARD — #21 Glassmorphism
   ═══════════════════════════════════════════════════════════ */
function Glass({ children, style, className, onClick, onMouseEnter, onMouseLeave, innerRef }) {
  return (
    <div ref={innerRef} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={className}
      style={{ background:T.paper, border:`1px solid ${T.hairline}`, borderRadius:18, boxShadow:T.shadowCard, ...style }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ANIMATED MESH GRADIENT — #22
   ═══════════════════════════════════════════════════════════ */
function MeshGradient({ colors=[T.forest, T.basil, T.leaf, T.sage], style }) {
  return (
    <div className="mesh-gradient" style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none", ...style }}>
      {colors.map((c,i) => (
        <div key={i} className={`mesh-blob mesh-blob-${i}`} style={{
          position:"absolute", width:"40%", height:"40%", borderRadius:"50%",
          background:`radial-gradient(circle, ${c}40, transparent 70%)`,
          filter:"blur(40px)", opacity:.7,
        }}/>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CARTIQ MARK — primary brand mark
   ─────────────────────────────────────────────────────────────
   Replaces the previous SageMark herb-sprig and earlier mascot
   experiments. The current mark is "Verdant
   Cart B": a warm bronze cart silhouette with a leaf overflowing
   the basket area. Conveys "fresh produce + smart shopping" while
   being legally distinct from the Sprouts Farmers Market plant-
   only logo.

   Design tokens (consistent across all sizes and treatments):
     CART STROKE  : bronze gradient #7a4f1f → #a67134
     LEAF FILL    : heirloom green gradient #a4c93f → #5e9c2a → #2d5a1a
     WHEELS       : dark bronze #7a4f1f outer, warm gold #d4a574 inner
     GLOW         : warm amber drop-shadow rgba(166,113,52,.45)

   The `mood` prop is preserved for backward compatibility with
   existing call sites (happy / excited / thinking / sleeping /
   celebrating). All moods render the same mark — motion changes:
     - celebrating: small pulse on the leaf to mark a positive moment
     - all others : static (carts shouldn't sway, wheels don't bob)

   The `animate` prop intensifies the warm drop-shadow (used by
   the onboarding tour spotlight bullet).

   For marketing/hero contexts (app store screens, splash, marketing
   site), use CartIQMarkMarketing below — it includes a golden wheat
   sprig accent that adds richness at large sizes but doesn't survive
   at favicon scales.
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   Icon — single source of truth for all in-app iconography.

   Replaces emoji glyphs across the app per the farm-fresh
   brief ("subtle line illustrations").

   Design language:
   - 24×24 viewBox, scaled via `size` prop
   - 1.5px stroke, round joins/caps, no fills (currentColor)
   - Drawn with slight imperfection / organic curves where it
     reads naturally (the leaf, the wheat sprig, the basket weave)
   - `accent` prop adds a single soft fill in select icons
     (leaf on wallet, etc.) for visual interest

   Usage:
     <Icon name="wallet" size={20} color={T.basil}/>
     <Icon name="leaf"   size={14} color={T.leaf}   accent={T.sageSoft}/>
   ═══════════════════════════════════════════════════════════ */
function Icon({ name, size=20, color="currentColor", accent, strokeWidth, stroke, style }) {
  // Accept both `strokeWidth` (canonical) and `stroke` (alias) — many call sites
  // use `stroke` as shorthand. Default 2 for confident, readable strokes.
  const sw = strokeWidth ?? stroke ?? 2;
  const common = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: color, strokeWidth: sw, strokeLinecap: "round", strokeLinejoin: "round",
    style: { display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style },
    "aria-hidden": true, focusable: "false",
  };
  switch (name) {
    case "wallet": return (
      <svg {...common}>
        <path d="M3 8.5C3 7.12 4.12 6 5.5 6h13A2.5 2.5 0 0 1 21 8.5V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5Z"/>
        <path d="M3 9h13.5A1.5 1.5 0 0 1 18 10.5v3a1.5 1.5 0 0 1-1.5 1.5H3"/>
        {accent && <circle cx="14.5" cy="12" r="1.1" fill={accent} stroke="none"/>}
        <path d="M14.5 12h.01" strokeWidth={sw*1.2}/>
      </svg>
    );
    case "cart": return (
      <svg {...common}>
        <path d="M3 4h2.2l2 11.2a2 2 0 0 0 2 1.6h8.4a2 2 0 0 0 2-1.5L21 8H6.5"/>
        <circle cx="10" cy="20" r="1.4"/>
        <circle cx="17.5" cy="20" r="1.4"/>
      </svg>
    );
    case "cart-plus": return (
      <svg {...common}>
        <path d="M3 4h2.2l2 11.2a2 2 0 0 0 2 1.6h8.4a2 2 0 0 0 2-1.5L21 8H6.5"/>
        <circle cx="10" cy="20" r="1.4"/>
        <circle cx="17.5" cy="20" r="1.4"/>
        <path d="M13.25 11.5h3.5M15 9.75v3.5"/>
      </svg>
    );
    case "leaf": return (
      <svg {...common}>
        {accent && <path d="M5 19c0-7 5.5-13 16-13 0 9-6 14-13 14-2 0-3-.5-3-1Z" fill={accent} stroke="none" opacity=".55"/>}
        <path d="M5 19c0-7 5.5-13 16-13 0 9-6 14-13 14-2 0-3-.5-3-1Z"/>
        <path d="M5 19c4-6 8-9 14-11"/>
      </svg>
    );
    case "sprout": return (
      <svg {...common}>
        <path d="M12 20v-7"/>
        <path d="M12 13c0-3.5 2.5-6 6-6-.2 4-2.5 6-6 6Z"/>
        <path d="M12 13c0-3-2-5.5-5-5.5.2 3.5 2.2 5.5 5 5.5Z"/>
      </svg>
    );
    case "salad": return (
      <svg {...common}>
        <path d="M4 11h16l-1.4 7.2A2 2 0 0 1 16.6 20H7.4a2 2 0 0 1-2-1.8L4 11Z"/>
        <path d="M8 11c-.5-2 1-4 3-4M12 11c0-2 1.5-3.5 3.5-3.5M16 11c0-1.5 1-2.5 2.5-2.5"/>
      </svg>
    );
    case "box": return (
      <svg {...common}>
        <path d="M3.5 7.5 12 4l8.5 3.5v9L12 20l-8.5-3.5v-9Z"/>
        <path d="M3.5 7.5 12 11l8.5-3.5M12 11v9"/>
      </svg>
    );
    case "bolt": return (
      <svg {...common}>
        <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z"/>
      </svg>
    );
    case "flame": return (
      <svg {...common}>
        <path d="M12 3c1 3 4 4.5 4 8.5a4 4 0 0 1-8 0c0-1.5.5-2.5 1.5-3.5C10.5 9 11 8 12 3Z"/>
        <path d="M10.5 16.5c.5 1 .5 2 1.5 2s1-1 1.5-2"/>
      </svg>
    );
    case "sparkle": return (
      <svg {...common}>
        <path d="M12 4v6M12 14v6M4 12h6M14 12h6"/>
        <path d="m6.5 6.5 2 2M15.5 15.5l2 2M17.5 6.5l-2 2M8.5 15.5l-2 2"/>
      </svg>
    );
    case "bookmark": return (
      <svg {...common}>
        <path d="M6 4h12v17l-6-4-6 4V4Z"/>
      </svg>
    );
    case "home": return (
      <svg {...common}>
        <path d="M4 11 12 4l8 7v8a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1v-8Z"/>
      </svg>
    );
    case "pin": return (
      <svg {...common}>
        <path d="M12 21s7-6 7-11.5A7 7 0 0 0 5 9.5C5 15 12 21 12 21Z"/>
        <circle cx="12" cy="9.5" r="2.5"/>
      </svg>
    );
    case "bell": return (
      <svg {...common}>
        <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2.5h-15L6 16Z"/>
        <path d="M10 19a2 2 0 0 0 4 0"/>
      </svg>
    );
    case "gift": return (
      <svg {...common}>
        <path d="M3.5 9.5h17v3h-17z"/>
        <path d="M5 12.5v8h14v-8M12 9.5v11"/>
        <path d="M12 9.5C9.5 9.5 7.5 8 7.5 6.2 7.5 5 8.5 4 9.7 4c1.5 0 2.3 1.5 2.3 5.5Z"/>
        <path d="M12 9.5c2.5 0 4.5-1.5 4.5-3.3 0-1.2-1-2.2-2.2-2.2-1.5 0-2.3 1.5-2.3 5.5Z"/>
      </svg>
    );
    case "camera": return (
      <svg {...common}>
        <path d="M3 8.5C3 7.7 3.7 7 4.5 7h3l1.5-2h6l1.5 2h3a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9Z"/>
        <circle cx="12" cy="13" r="3.5"/>
      </svg>
    );
    case "clip": return (
      <svg {...common}>
        <path d="M9 13.5 14.5 8a3 3 0 0 1 4.2 4.2l-7 7a4.5 4.5 0 0 1-6.4-6.4l7-7"/>
      </svg>
    );
    case "calendar": return (
      <svg {...common}>
        <rect x="3.5" y="5.5" width="17" height="15" rx="2"/>
        <path d="M8 3v4M16 3v4M3.5 10h17"/>
      </svg>
    );
    case "mail": return (
      <svg {...common}>
        <rect x="3" y="6" width="18" height="13" rx="2"/>
        <path d="m3.5 7 8.5 6 8.5-6"/>
      </svg>
    );
    case "store": return (
      <svg {...common}>
        <path d="M4 9.5 5.5 5h13L20 9.5"/>
        <path d="M4 9.5v9.5h16V9.5"/>
        <path d="M4 9.5a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 1 0"/>
        <path d="M9 19v-5h6v5"/>
      </svg>
    );
    case "rose": return (
      <svg {...common}>
        <circle cx="12" cy="11" r="3"/>
        <path d="M12 8c0-1.5 1-2.5 2.5-2.5M12 14c1.5 0 2.5 1 2.5 2.5M9 11c-1.5 0-2.5-1-2.5-2.5M15 11c1.5 0 2.5 1 2.5 2.5"/>
        <path d="M12 14v6M9 18l3-1.5 3 1.5"/>
      </svg>
    );
    case "search": return (
      <svg {...common}>
        <circle cx="11" cy="11" r="6.5"/>
        <path d="m20 20-4.5-4.5"/>
      </svg>
    );
    case "settings": return (
      <svg {...common}>
        <circle cx="12" cy="12" r="2.5"/>
        <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8"/>
      </svg>
    );
    case "check": return (
      <svg {...common}>
        <path d="m5 12.5 4.5 4.5L19 7"/>
      </svg>
    );
    case "shield-check": return (
      <svg {...common}>
        <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3z"/>
        <path d="m9 12 2 2 4-4"/>
      </svg>
    );
    case "x": return (
      <svg {...common}>
        <path d="M6 6l12 12M18 6 6 18"/>
      </svg>
    );
    case "download": return (
      <svg {...common}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <path d="M7 10l5 5 5-5"/>
        <path d="M12 15V3"/>
      </svg>
    );
    case "share": return (
      <svg {...common}>
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
        <path d="M16 6l-4-4-4 4"/>
        <path d="M12 2v13"/>
      </svg>
    );
    case "plus": return (
      <svg {...common}>
        <path d="M12 5v14M5 12h14"/>
      </svg>
    );
    case "minus": return (
      <svg {...common}>
        <path d="M5 12h14"/>
      </svg>
    );
    case "chevron-right": return (
      <svg {...common}>
        <path d="m9 5 7 7-7 7"/>
      </svg>
    );
    case "chevron-down": return (
      <svg {...common}>
        <path d="m5 9 7 7 7-7"/>
      </svg>
    );
    case "chevron-left": return (
      <svg {...common}>
        <path d="m15 5-7 7 7 7"/>
      </svg>
    );
    case "chevron-up": return (
      <svg {...common}>
        <path d="m5 15 7-7 7 7"/>
      </svg>
    );
    case "trophy": return (
      <svg {...common}>
        <path d="M8 4h8v4a4 4 0 0 1-8 0z"/>
        <path d="M8 6H5v2a3 3 0 0 0 3 3M16 6h3v2a3 3 0 0 1-3 3"/>
        <path d="M10 13h4l-.5 3.5h-3z"/>
        <path d="M8 20h8"/>
      </svg>
    );
    case "star": return (
      <svg {...common}>
        <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6L12 17l-5.4 2.8 1-6L3.2 9.5l6.1-.9z"/>
      </svg>
    );
    case "target": return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9"/>
        <circle cx="12" cy="12" r="5"/>
        <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
      </svg>
    );
    case "package": return (
      <svg {...common}>
        <path d="m12 3 8.5 4.5v9L12 21l-8.5-4.5v-9z"/>
        <path d="M12 12 3.5 7.5M12 12l8.5-4.5M12 12v9"/>
        <path d="m7.5 5 8.5 4.5"/>
      </svg>
    );
    case "check-circle": return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9"/>
        <path d="m8 12.5 3 3 5-6"/>
      </svg>
    );
    case "arrow-right": return (
      <svg {...common}>
        <path d="M4 12h16M14 6l6 6-6 6"/>
      </svg>
    );
    case "wheat": return (
      <svg {...common}>
        <path d="M12 21V8"/>
        <path d="M12 13c-2 0-3-1-3-3 1.5 0 3 1 3 3ZM12 13c2 0 3-1 3-3-1.5 0-3 1-3 3Z"/>
        <path d="M12 17c-2 0-3-1-3-3 1.5 0 3 1 3 3ZM12 17c2 0 3-1 3-3-1.5 0-3 1-3 3Z"/>
        <path d="M12 9c-2 0-3-1-3-3 1.5 0 3 1 3 3ZM12 9c2 0 3-1 3-3-1.5 0-3 1-3 3Z"/>
      </svg>
    );
    case "tag": return (
      <svg {...common}>
        <path d="M3 12.5V4h8.5L21 13.5l-7.5 7.5L3 12.5Z"/>
        <circle cx="7.5" cy="8" r="1.2"/>
      </svg>
    );
    case "list": return (
      <svg {...common}>
        <path d="M8 6h12M8 12h12M8 18h12"/>
        <circle cx="4" cy="6" r=".7" fill={color}/>
        <circle cx="4" cy="12" r=".7" fill={color}/>
        <circle cx="4" cy="18" r=".7" fill={color}/>
      </svg>
    );
    case "user": return (
      <svg {...common}>
        <circle cx="12" cy="8" r="3.5"/>
        <path d="M5 20c1-4 4-5.5 7-5.5s6 1.5 7 5.5"/>
      </svg>
    );
    case "info": return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8.5"/>
        <path d="M12 11v5M12 7.5v.5"/>
      </svg>
    );
    case "trending-up": return (
      <svg {...common}>
        <path d="m3 17 6-6 4 4 8-8"/>
        <path d="M14 7h7v7"/>
      </svg>
    );
    case "trending-down": return (
      <svg {...common}>
        <path d="m3 7 6 6 4-4 8 8"/>
        <path d="M14 17h7v-7"/>
      </svg>
    );
    case "chart-line": return (
      <svg {...common}>
        <path d="M3 4v16h18"/>
        <path d="m6 14 4-4 3 3 5-6"/>
      </svg>
    );
    case "edit": return (
      <svg {...common}>
        <path d="M16 4l4 4-11 11H5v-4L16 4z"/>
      </svg>
    );
    case "clock": return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9"/>
        <path d="M12 7v5l3 2"/>
      </svg>
    );
    case "filter": return (
      <svg {...common}>
        <path d="M4 5h16l-6 8v6l-4-2v-4z"/>
      </svg>
    );
    case "refresh": return (
      <svg {...common}>
        <path d="M3 12a9 9 0 0 1 15.5-6.3M21 12a9 9 0 0 1-15.5 6.3"/>
        <path d="M18 3v4h-4M6 21v-4h4"/>
      </svg>
    );
    default: return null;
  }
}

function CartIQMark({ size=36, mood="happy", animate=false }) {
  const shadowStrength = animate ? "0 0 12px rgba(166,113,52,.65)" : "0 2px 6px rgba(166,158,52,.4)";
  // Unique gradient IDs per render so multiple marks on one page don't collide.
  const gid = `ciq-${size}-${mood}`;
  const isCelebrating = mood === "celebrating";
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display:"block", flexShrink:0, filter: `drop-shadow(${shadowStrength})` }}>
      <defs>
        <linearGradient id={`${gid}-cart`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7a4f1f"/>
          <stop offset="100%" stopColor="#a67134"/>
        </linearGradient>
        <linearGradient id={`${gid}-leaf`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a4c93f"/>
          <stop offset="50%" stopColor="#5e9c2a"/>
          <stop offset="100%" stopColor="#2d5a1a"/>
        </linearGradient>
      </defs>
      {/* Cart silhouette: handle hook → underside curve → basket front-rim → top-back corner */}
      <path d="M22 32 L28 32 L36 70 L78 70 L84 42 L40 42" fill="none" stroke={`url(#${gid}-cart)`} strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Leaf overflowing the basket (the Verdant element) */}
      <g style={isCelebrating ? { transformOrigin:"60px 38px", animation:"cartiqLeafPulse 1.6s ease-in-out infinite" } : undefined}>
        <path d="M40 42 Q60 22 84 26 Q82 50 60 56 Q44 56 40 42 Z" fill={`url(#${gid}-leaf)`}/>
      </g>
      {/* Dual-tone wheels */}
      <circle cx="44" cy="80" r="5" fill="#7a4f1f"/>
      <circle cx="44" cy="80" r="2.2" fill="#d4a574"/>
      <circle cx="72" cy="80" r="5" fill="#7a4f1f"/>
      <circle cx="72" cy="80" r="2.2" fill="#d4a574"/>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   CARTIQ MARK — MARKETING VARIANT (with wheat sprig accent)
   ─────────────────────────────────────────────────────────────
   For high-real-estate surfaces only: app store hero, splash
   screen, marketing site, printed materials. NOT for favicon
   or in-app header — at small sizes the wheat sprig becomes
   visual noise. Use the plain CartIQMark above for those.

   Adds a golden wheat sprig peeking out from behind the cart
   on the left side. The wheat reads as "everyday staples" and
   broadens the brand's color palette from green-only to green
   + bronze + gold.
   ═══════════════════════════════════════════════════════════ */
function CartIQMarkMarketing({ size=160 }) {
  const gid = `ciqm-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display:"block", flexShrink:0, filter: "drop-shadow(0 6px 18px rgba(166,113,52,.45))" }}>
      <defs>
        <linearGradient id={`${gid}-cart`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7a4f1f"/>
          <stop offset="100%" stopColor="#a67134"/>
        </linearGradient>
        <linearGradient id={`${gid}-leaf`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a4c93f"/>
          <stop offset="50%" stopColor="#5e9c2a"/>
          <stop offset="100%" stopColor="#2d5a1a"/>
        </linearGradient>
        <linearGradient id={`${gid}-wheat`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#a67134"/>
          <stop offset="100%" stopColor="#e6b86a"/>
        </linearGradient>
      </defs>
      {/* Wheat sprig — placed behind the cart, slightly to the left */}
      <path d="M16 84 Q14 62 18 42 Q20 28 22 18" fill="none" stroke={`url(#${gid}-wheat)`} strokeWidth="1.6" strokeLinecap="round"/>
      <ellipse cx="20" cy="22" rx="2" ry="3.5" fill={`url(#${gid}-wheat)`} transform="rotate(-15 20 22)"/>
      <ellipse cx="16" cy="32" rx="2" ry="3.2" fill={`url(#${gid}-wheat)`} transform="rotate(-30 16 32)"/>
      <ellipse cx="24" cy="32" rx="2" ry="3.2" fill={`url(#${gid}-wheat)`} transform="rotate(15 24 32)"/>
      <ellipse cx="14" cy="42" rx="1.8" ry="3" fill={`url(#${gid}-wheat)`} transform="rotate(-35 14 42)" opacity=".9"/>
      <ellipse cx="22" cy="42" rx="1.8" ry="3" fill={`url(#${gid}-wheat)`} transform="rotate(10 22 42)" opacity=".9"/>
      <ellipse cx="13" cy="52" rx="1.6" ry="2.6" fill={`url(#${gid}-wheat)`} transform="rotate(-40 13 52)" opacity=".75"/>
      <ellipse cx="22" cy="52" rx="1.6" ry="2.6" fill={`url(#${gid}-wheat)`} transform="rotate(8 22 52)" opacity=".75"/>
      <line x1="20" y1="22" x2="22" y2="6" stroke={`url(#${gid}-wheat)`} strokeWidth="1" strokeLinecap="round"/>
      {/* Cart + leaf + wheels — same geometry as primary mark, drawn on top */}
      <path d="M22 32 L28 32 L36 70 L78 70 L84 42 L40 42" fill="none" stroke={`url(#${gid}-cart)`} strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M40 42 Q60 22 84 26 Q82 50 60 56 Q44 56 40 42 Z" fill={`url(#${gid}-leaf)`}/>
      <circle cx="44" cy="80" r="5" fill="#7a4f1f"/>
      <circle cx="44" cy="80" r="2.2" fill="#d4a574"/>
      <circle cx="72" cy="80" r="5" fill="#7a4f1f"/>
      <circle cx="72" cy="80" r="2.2" fill="#d4a574"/>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   SMALL COMPONENTS
   ═══════════════════════════════════════════════════════════ */
function Pill({label,color}) {
  // Bundle deals (BOGO/B2G1/BIG DEAL) use berry; others use basil or caller-supplied color
  const isBundle = label.includes("BOGO") || label.includes("BIG DEAL") || label.includes("B2G1") || label.toUpperCase().includes("BUY 2 GET 1");
  const bg   = isBundle ? T.berrySoft : (color ? `${color}20` : T.sageSoft);
  const fg   = isBundle ? T.berry     : (color || T.basil);
  const bdr  = isBundle ? T.berrySoft : (color ? `${color}40` : T.sage);
  return <span style={{background:bg,color:fg,border:`1px solid ${bdr}`,fontFamily:FONT_SANS,fontSize:10,fontWeight:700,letterSpacing:.6,padding:"3px 9px",borderRadius:20,whiteSpace:"nowrap"}}>{label}</span>;
}
function OrgBadge({small}) {
  return <span style={{background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,fontSize:small?8:9,fontWeight:700,padding:small?"1px 5px":"2px 7px",borderRadius:20,whiteSpace:"nowrap",letterSpacing:.5}}>🌿 {small?"ORG":"ORGANIC"}</span>;
}
function UrgBadge({days}) {
  if(days<=1) return <span style={{color:T.warn,fontSize:10,fontWeight:700,background:T.butterSoft,padding:"2px 7px",borderRadius:20}}>⚡ ENDS TODAY</span>;
  if(days<=3) return <span style={{color:T.apricot,fontSize:10,fontWeight:600}}>{days}d left</span>;
  return <span style={{color:T.ink50,fontSize:10}}>{days}d left</span>;
}
function StockDot({level}) {
  const c = level==="high" ? T.basil : level==="medium" ? T.warn : T.berry;
  return <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,color:c}}><span style={{width:6,height:6,borderRadius:"50%",background:c,display:"inline-block"}}/>{level==="high"?"In Stock":level==="medium"?"Limited":"Low Stock"}</span>;
}
function Sparkline({data}) {
  const w=80,h=28,pad=2,mn=Math.min(...data),mx=Math.max(...data),rng=mx-mn||1;
  const coords=data.map((v,i)=>({x:pad+(w-pad*2)*(i/(data.length-1)),y:pad+(h-pad*2)*(1-(v-mn)/rng)}));
  const pts=coords.map(c=>`${c.x},${c.y}`).join(" ");
  const down=data[data.length-1]<data[data.length-2];
  const {x:ex,y:ey}=coords[coords.length-1];
  return <svg width={w} height={h} style={{overflow:"visible"}}><polyline points={pts} fill="none" stroke={down?T.basil:T.berry} strokeWidth="1.5" strokeLinejoin="round"/><circle cx={ex} cy={ey} r="2.5" fill={down?T.basil:T.berry}/></svg>;
}
/* PriceTrendChart — Price IQ card chart.
   Full-width line chart with a 6-week-average reference line, all-time-low marker,
   and an end dot colored by trend. Used on the Price Intelligence cards.
   data: number[]   atl: number   avg: number */
function PriceTrendChart({data, atl, avg, height=64}) {
  const w=320, h=height, padX=6, padY=12;
  const allVals = [...data, atl, avg];
  const mn = Math.min(...allVals), mx = Math.max(...allVals), rng = mx - mn || 1;
  const xAt = i => padX + (w - padX*2) * (i / (data.length - 1));
  const yAt = v => padY + (h - padY*2) * (1 - (v - mn) / rng);
  const coords = data.map((v,i)=>({x:xAt(i), y:yAt(v)}));
  const pts = coords.map(c=>`${c.x},${c.y}`).join(" ");
  // Trend: compare last point to start
  const cur = data[data.length-1];
  const start = data[0];
  const isFalling = cur < start;
  const lineColor = isFalling ? T.basil : T.berry;
  const lineColorSoft = isFalling ? "rgba(22,163,106,.16)" : "rgba(220,38,38,.14)";
  // Area fill path — gives the chart visual weight
  const areaPath = `M ${coords[0].x},${h-padY} L ` +
    coords.map(c=>`${c.x},${c.y}`).join(" L ") +
    ` L ${coords[coords.length-1].x},${h-padY} Z`;
  const last = coords[coords.length-1];
  const atlY = yAt(atl);
  const avgY = yAt(avg);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{overflow:"visible",display:"block"}}>
      <defs>
        <linearGradient id="ptarea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.22"/>
          <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {/* 6-week average reference */}
      <line x1={padX} y1={avgY} x2={w-padX} y2={avgY} stroke={T.ink50} strokeWidth="0.8" strokeDasharray="3,3" opacity="0.55"/>
      {/* All-time-low reference */}
      <line x1={padX} y1={atlY} x2={w-padX} y2={atlY} stroke={T.sage} strokeWidth="0.8" strokeDasharray="2,3" opacity="0.7"/>
      {/* Area fill */}
      <path d={areaPath} fill="url(#ptarea)"/>
      {/* Trend line */}
      <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"/>
      {/* Current price dot */}
      <circle cx={last.x} cy={last.y} r="4" fill={T.paper} stroke={lineColor} strokeWidth="2"/>
      {/* ATL marker — small triangle on the left at the all-time-low value */}
      {(() => {
        const atlIdx = data.indexOf(atl);
        if (atlIdx < 0) return null;
        const x = xAt(atlIdx);
        return <g><circle cx={x} cy={atlY} r="2.5" fill={T.basil}/></g>;
      })()}
    </svg>
  );
}
/* PriceTrack — full-width sparkline with target & all-time-low reference lines.
   Used on watchlist cards so the user can see at a glance how the current price
   sits between the price they're hoping for (target) and the best price ever (ATL).
   data: [number]   target: number   atl: number   onSale: boolean */
function PriceTrack({data, target, atl, onSale, height=58}) {
  const w=320, h=height, padX=4, padY=10;
  // Domain spans price extremes plus target/atl so reference lines are always visible
  const allVals = [...data, target, atl];
  const mn = Math.min(...allVals), mx = Math.max(...allVals), rng = mx - mn || 1;
  const yOf = v => padY + (h - padY*2) * (1 - (v - mn) / rng);
  const coords = data.map((v,i) => ({x: padX + (w - padX*2) * (i/(data.length-1||1)), y: yOf(v)}));
  const pts = coords.map(c=>`${c.x},${c.y}`).join(" ");
  const areaPts = `${padX},${h-padY} ${pts} ${w-padX},${h-padY}`;
  const last = coords[coords.length-1];
  const lineColor = onSale ? T.leaf : (data[data.length-1] < data[0] ? T.basil : T.apricot);
  const targetY = yOf(target);
  const atlY = yOf(atl);
  const id = `pt-${Math.random().toString(36).slice(2,8)}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h} style={{overflow:"visible",display:"block"}}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity=".22"/>
          <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {/* Target reference line */}
      <line x1={padX} y1={targetY} x2={w-padX} y2={targetY} stroke={T.warn} strokeWidth="1" strokeDasharray="3 3" opacity=".55" vectorEffect="non-scaling-stroke"/>
      {/* All-time-low reference line */}
      <line x1={padX} y1={atlY} x2={w-padX} y2={atlY} stroke={T.leaf} strokeWidth="1" strokeDasharray="2 4" opacity=".4" vectorEffect="non-scaling-stroke"/>
      {/* Filled area + line */}
      <polygon points={areaPts} fill={`url(#${id})`}/>
      <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
      {/* Current dot */}
      <circle cx={last.x} cy={last.y} r="4.5" fill={lineColor} stroke={T.parchment} strokeWidth="2" vectorEffect="non-scaling-stroke"/>
    </svg>
  );
}
function MiniBar({data,color}) {
  const mx=Math.max(...data);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:2,height:32}}>
      {data.map((v,i)=>(
        <div key={i} style={{flex:1,background:i===data.length-1?color:`${color}55`,borderRadius:"2px 2px 0 0",height:`${(v/mx)*100}%`,transition:"height .6s ease",minHeight:2}}/>
      ))}
    </div>
  );
}

/* ─── Ripple Button ──────────────────────────────────────── */
function RippleBtn({onClick,style,children,sound="addCart",disabled=false}) {
  const [ripples,setRipples]=useState([]);
  const ref=useRef();
  const handleClick=(e)=>{
    if(disabled) return;
    const rect=ref.current.getBoundingClientRect();
    const id=Date.now();
    setRipples(r=>[...r,{id,x:e.clientX-rect.left,y:e.clientY-rect.top}]);
    setTimeout(()=>setRipples(r=>r.filter(rr=>rr.id!==id)),600);
    if(SFX[sound]) SFX[sound]();
    if(onClick) onClick(e);
  };
  return (
    <button ref={ref} onClick={handleClick} disabled={disabled}
      style={{...style,position:"relative",overflow:"hidden",cursor:disabled?"not-allowed":"pointer",fontFamily:FONT_SANS}}>
      {ripples.map(r=><span key={r.id} style={{position:"absolute",left:r.x-20,top:r.y-20,width:40,height:40,borderRadius:"50%",background:"rgba(30,53,40,0.12)",animation:"ripple .55s ease-out both",pointerEvents:"none"}}/>)}
      {children}
    </button>
  );
}

/* ─── Countdown Timer — #17 ─────────────────────────────── */
function CountdownTimer({ hours=0 }) {
  const [secs, setSecs] = useState(hours * 3600);
  useEffect(() => {
    if (secs <= 0) return;
    const t = setInterval(() => setSecs(s => Math.max(0, s-1)), 1000);
    return () => clearInterval(t);
  }, [secs]);
  const h = Math.floor(secs/3600), m = Math.floor((secs%3600)/60), s = secs%60;
  return (
    <span style={{fontFamily:FONT_MONO,fontSize:11,fontWeight:600,color:T.warn,letterSpacing:1}}>
      {String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}
    </span>
  );
}

/* ─── Empty State — #11 ─────────────────────────────────── */
function EmptyState({ icon, title, sub, action, onAction }) {
  return (
    <div style={{textAlign:"center",padding:"48px 24px"}}>
      <div style={{fontSize:56,marginBottom:12,opacity:.7}}>{icon}</div>
      <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:400,color:T.forest,marginBottom:6}}>{title}</div>
      <div style={{fontFamily:FONT_SANS,fontSize:13,color:T.ink70,lineHeight:1.6,marginBottom:20,maxWidth:300,margin:"0 auto 20px"}}>{sub}</div>
      {action && <RippleBtn sound="notify" onClick={onAction} style={{background:T.forest,border:"none",color:T.paper,borderRadius:999,padding:"11px 24px",fontSize:13,fontWeight:600}}>{action}</RippleBtn>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUBSTITUTIONS
   ═══════════════════════════════════════════════════════════ */
const SUB_OPTIONS = {
  brand_only:{label:"Brand only",icon:"🏷",desc:"Only this exact brand"},
  similar_ok:{label:"Similar OK",icon:"🔄",desc:"Same category, comparable quality"},
  any:{label:"Best value",icon:"💰",desc:"Whatever is cheapest or best deal"},
};

// ── Phase 4c: Substitution cost cap ─────────────────────────────────────
// Master switch for the per-item substitution price ceiling. Until
// Instacart Developer Platform is wired, CartIQ can only RECORD the
// user's intent and surface it on the list — it cannot block a swap
// inside Instacart's checkout. Copy in the modal must say so honestly.
const SUB_COST_CAP_ENABLED = true;

// Preset ceilings offered in the substitution modal. `value` is a
// fraction of the original price (e.g. 0.25 = up to 25% more). A `value`
// of null means "no cap" — when picked, omit `maxIncrease` from the
// saved entry so the persisted shape stays clean. 0.0 = same-price-only.
const SUB_CAP_PRESETS = [
  { id: "none",  label: "No limit",        value: null },
  { id: "p10",   label: "Up to +10%",      value: 0.10 },
  { id: "p25",   label: "Up to +25%",      value: 0.25 },
  { id: "match", label: "Same price only", value: 0.0  },
];
const AUTO_SUBS = {
  "salmon":["Tilapia","Cod","Halibut","Trout"],
  "chicken":["Turkey breast","Pork tenderloin","Chicken thighs"],
  "spinach":["Kale","Baby arugula","Mixed greens"],
  "milk":["Oat milk","Almond milk","2% milk"],
  "eggs":["Egg whites","Liquid eggs"],
  "quinoa":["Brown rice","Farro","Bulgur wheat"],
  "almond butter":["Peanut butter","Sunflower butter","Cashew butter"],
  "olive oil":["Avocado oil","Canola oil","Coconut oil"],
  "greek yogurt":["Plain yogurt","Skyr","Cottage cheese"],
  "avocado":["Guacamole (pre-made)","Hummus"],
  "turkey":["Chicken breast","Lean ground beef"],
  "bread":["Whole wheat bread","Sourdough","English muffins"],
  "berries":["Frozen mango","Frozen peaches","Banana"],
};
function getSuggestedSubs(name) {
  const lower = name.toLowerCase();
  for(const [key,subs] of Object.entries(AUTO_SUBS)) { if(lower.includes(key)) return subs; }
  return [];
}

/* ═══════════════════════════════════════════════════════════
   ITEM DETAIL SHEET (Phase 2)
   ═══════════════════════════════════════════════════════════
   Bottom sheet that shows a canonical item's details and all stores
   that carry it, sorted cheapest first. Three destination actions:
   Smart Cart, Pantry, Watchlist.

   Designed for reuse: this same component will power the scanner
   enhancement later. Any entry point (search, scanner, detail page)
   passes in a SearchResult-shaped object and the destination handlers.

   Props:
     - item: SearchResult shape from searchItems()
     - onClose: dismiss
     - onAddToSmartCart(item, chosenStore): add synthetic deal to cart
     - onAddToPantry(item): add canonical item to pantry
     - onAddToWatchlist(item, chosenStore): add store+product watch
     - alreadyInCart(item, chosenStore): boolean check
     - alreadyInPantry(item): boolean check
     - alreadyInWatchlist(item, chosenStore): boolean check
   ═══════════════════════════════════════════════════════════ */
function ItemDetailSheet({
  item, onClose,
  onAddToSmartCart, onAddToPantry, onAddToWatchlist,
  alreadyInCart, alreadyInPantry, alreadyInWatchlist,
}) {
  // Chosen store — defaults to the cheapest (first in pre-sorted stores array).
  // Null only if the item has no store availability (shouldn't happen, but defensive).
  const [chosenStoreId, setChosenStoreId] = useState(
    item && item.stores && item.stores.length > 0 ? item.stores[0].storeId : null
  );

  if (!item) return null;

  const chosenStore = item.stores.find(s => s.storeId === chosenStoreId) || item.stores[0];
  const inCart = alreadyInCart ? alreadyInCart(item, chosenStore) : false;
  const inPantry = alreadyInPantry ? alreadyInPantry(item) : false;
  const inWatch = alreadyInWatchlist ? alreadyInWatchlist(item, chosenStore) : false;

  // Best-price savings display — same Option B math: max - min across stores.
  const hasMultipleStores = item.stores.length > 1;
  const highestPrice = item.stores[item.stores.length - 1]?.price || chosenStore.price;
  const savingsVsWorst = Math.max(0, highestPrice - chosenStore.price);

  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.50)",zIndex:1150,display:"flex",alignItems:"flex-end",justifyContent:"center",animation:"fadeIn .2s ease"}}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <Glass style={{
        width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",
        borderRadius:"22px 22px 0 0",border:`1px solid ${T.hairline}`,
        animation:"fadeIn .25s ease",
      }}>
        {/* Drag handle (visual only) */}
        <div style={{padding:"10px 0 4px",display:"flex",justifyContent:"center"}}>
          <div style={{width:44,height:4,borderRadius:2,background:T.parchment}} />
        </div>

        {/* Header — name, size, badges, close */}
        <div style={{padding:"8px 22px 14px",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"flex-start",gap:12}}>
          <div style={{flexShrink:0,marginTop:2,display:"flex",alignItems:"center"}}><ItemIcon emoji={getItemEmoji(item.id, item.name, item.category)} size={32}/></div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,lineHeight:1.25,marginBottom:3}}>{item.name}</div>
            <div style={{fontSize:11,color:T.ink50,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span>{item.size}</span>
              <span style={{color:T.ink50}}>·</span>
              <span>{item.category}</span>
              {item.organic && <OrgBadge small />}
              {item.premium && <span style={{fontSize:9,background:T.butterSoft,color:T.warn,padding:"2px 6px",borderRadius:10,fontWeight:800}}>PREMIUM</span>}
            </div>
          </div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
        </div>

        {/* Price hero — chosen store price + savings */}
        <div style={{padding:"16px 22px 10px",display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:12}}>
          <div>
            <div style={{fontSize:10,color:T.ink50,letterSpacing:.6,textTransform:"uppercase",marginBottom:2}}>Best price</div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:34,fontWeight:700,color:T.basil,lineHeight:1}}>${chosenStore.price.toFixed(2)}</div>
            <div style={{fontSize:11,color:T.ink50,marginTop:3}}>at {chosenStore.storeName}</div>
          </div>
          {hasMultipleStores && savingsVsWorst > 0.01 && (
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:10,color:T.ink50,letterSpacing:.6,textTransform:"uppercase",marginBottom:2}}>CartIQ saves you</div>
              <div style={{fontSize:18,fontWeight:700,color:T.warn}}>${savingsVsWorst.toFixed(2)}</div>
              <div style={{fontSize:10,color:T.ink50}}>vs highest store</div>
            </div>
          )}
        </div>

        {/* Store list — sorted cheapest-first, tap to select */}
        <div style={{padding:"10px 22px 4px"}}>
          <div style={{fontSize:10,color:T.ink50,letterSpacing:.6,textTransform:"uppercase",marginBottom:8}}>
            Available at {item.stores.length} {item.stores.length === 1 ? "store" : "stores"}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {item.stores.map((s, i) => {
              const isSelected = s.storeId === chosenStoreId;
              const isBest = i === 0;
              return (
                <button
                  key={s.storeId}
                  onClick={() => setChosenStoreId(s.storeId)}
                  style={{
                    display:"flex",alignItems:"center",gap:10,
                    padding:"10px 12px",
                    background: isSelected ? T.sageSoft : T.parchment,
                    border: isSelected ? "1px solid rgba(74,222,128,.45)" : `1px solid ${T.hairline}`,
                    borderRadius:10,
                    cursor:"pointer",
                    fontFamily:"inherit",
                    textAlign:"left",
                    transition:"all .15s ease",
                  }}
                >
                  <div style={{width:18,height:18,borderRadius:9,border:isSelected?"none":`2px solid ${T.hairline}`,background:isSelected?T.leaf:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {isSelected && <span style={{color:T.forest,fontSize:12,fontWeight:700,lineHeight:1}}>✓</span>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.forest}}>{s.storeName}</div>
                    {isBest && item.stores.length > 1 && (
                      <div style={{fontSize:9,color:T.leaf,fontWeight:800,letterSpacing:.4,marginTop:1}}>LOWEST PRICE</div>
                    )}
                  </div>
                  <div style={{fontSize:15,fontWeight:700,color:isSelected?T.leaf:T.forest,flexShrink:0}}>${s.price.toFixed(2)}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons — Smart Cart, Pantry, Watchlist */}
        <div style={{padding:"18px 22px 22px",borderTop:`1px solid ${T.hairline}`,marginTop:12,display:"flex",gap:8}}>
          <button
            onClick={() => { if (!inCart) { onAddToSmartCart(item, chosenStore); onClose(); } }}
            disabled={inCart}
            style={{
              flex:1,padding:"12px 8px",
              background: inCart ? T.sageSoft : T.forest,
              border: inCart ? `1px solid ${T.sage}` : "none",
              color: inCart ? T.basil : "#fff",
              borderRadius:12,
              fontSize:11,fontWeight:800,cursor:inCart?"default":"pointer",
              fontFamily:"inherit",letterSpacing:.3,
              display:"flex",flexDirection:"column",alignItems:"center",gap:2,
            }}
          >
            <span style={{fontSize:18}}>🛒</span>
            <span>{inCart ? "✓ In cart" : "Smart Cart"}</span>
          </button>
          <button
            onClick={() => { onAddToPantry(item); if (!inPantry) onClose(); }}
            style={{
              flex:1,padding:"12px 8px",
              background: inPantry ? T.berrySoft : T.berrySoft,
              border: inPantry ? "1px solid rgba(160,69,98,.25)" : "1px solid rgba(160,69,98,.30)",
              color: inPantry ? T.basil : T.basil,
              borderRadius:12,
              fontSize:11,fontWeight:800,cursor:"pointer",
              fontFamily:"inherit",letterSpacing:.3,
              display:"flex",flexDirection:"column",alignItems:"center",gap:2,
            }}
          >
            <span style={{fontSize:18}}>🫙</span>
            <span>{inPantry ? "✓ In pantry" : "Pantry"}</span>
          </button>
          <button
            onClick={() => { onAddToWatchlist(item, chosenStore); if (!inWatch) onClose(); }}
            style={{
              flex:1,padding:"12px 8px",
              background: inWatch ? T.butterSoft : "rgba(251,191,36,.18)",
              border: inWatch ? "1px solid rgba(251,191,36,.3)" : "1px solid rgba(251,191,36,.35)",
              color: inWatch ? T.butter : T.warn,
              borderRadius:12,
              fontSize:11,fontWeight:800,cursor:"pointer",
              fontFamily:"inherit",letterSpacing:.3,
              display:"flex",flexDirection:"column",alignItems:"center",gap:2,
            }}
          >
            <span style={{fontSize:18}}>👁️</span>
            <span>{inWatch ? "✓ Watching" : "Watchlist"}</span>
          </button>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   GLOBAL SEARCH OVERLAY (Phase 2)
   ═══════════════════════════════════════════════════════════
   Full-screen search UI. Three entry points:
     1. Top-bar 🔍 icon (unscoped, dietary filters applied)
     2. Smart Cart embedded search bar (unscoped)
     3. Store Detail page 🔍 icon (scoped to that store, with pill
        toggle to expand to all stores)

   Props:
     - initialScopeStoreId: if given, search opens scoped to that store
     - dietaryRestrictions: array of active restriction IDs
     - activeStores: user's selected stores (for filtering)
     - onClose
     - onOpenItem(item): opens the ItemDetailSheet for the chosen result

   Internal state:
     - query
     - scope: "store" | "all" (only meaningful when initialScopeStoreId set)
   ═══════════════════════════════════════════════════════════ */
function GlobalSearchOverlay({
  initialScopeStoreId, dietaryRestrictions, activeStores,
  recentSearches, onRecordSearch, onClearRecentSearches,
  onClose, onOpenItem,
}) {
  const [q, setQ] = useState("");
  // Scope pill state — only relevant when initialScopeStoreId is set. When
  // true, search is narrowed to initialScopeStoreId; when false, it's
  // expanded to all stores for the current session.
  const [narrowed, setNarrowed] = useState(!!initialScopeStoreId);
  const inputRef = useRef(null);

  // Autofocus the input on mount so user can start typing immediately.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const effectiveStoreId = narrowed ? initialScopeStoreId : undefined;
  const scopeStoreName = initialScopeStoreId
    ? (activeStores.find(s => s.id === initialScopeStoreId)?.name || initialScopeStoreId)
    : null;

  const results = useMemo(() => {
    if (q.trim().length < 2) return [];
    const activeStoreIds = new Set(activeStores.map(s => s.id));
    const raw = searchItems(q, {
      storeId: effectiveStoreId,
      scope: effectiveStoreId ? "store" : "all",
      limit: 40,
      dietaryRestrictions,
    });
    // When all-stores scope, filter each result's per-store list to only the
    // stores the user has selected (activeStores). Drop results with nothing left.
    if (!effectiveStoreId) {
      return raw
        .map(r => {
          const filtered = r.stores.filter(s => activeStoreIds.has(s.storeId));
          if (filtered.length === 0) return null;
          const best = filtered[0];  // already sorted cheapest-first by searchItems
          const maxP = filtered[filtered.length - 1].price;
          return { ...r, stores: filtered, bestPrice: best.price, bestStore: best.storeId, maxSavings: Math.max(0, maxP - best.price) };
        })
        .filter(Boolean);
    }
    return raw;
  }, [q, effectiveStoreId, dietaryRestrictions, activeStores]);

  const dietaryActive = dietaryRestrictions && dietaryRestrictions.length > 0;

  return (
    <div style={{position:"fixed",inset:0,background:T.cream,zIndex:1100,display:"flex",flexDirection:"column",animation:"fadeIn .2s ease"}}>
      {/* Header — search input with leading icon + close button.
          paddingTop respects iPhone safe-area-inset-top so the input clears
          the Dynamic Island / notch on Face ID devices. */}
      <div style={{padding:"14px 16px 12px",paddingTop:"max(14px, env(safe-area-inset-top))",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",gap:10,flexShrink:0,background:T.cream}}>
        <div style={{flex:1,position:"relative",display:"flex",alignItems:"center"}}>
          <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",display:"flex",alignItems:"center",opacity:.55}} aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.forest} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
          </span>
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={initialScopeStoreId ? `Search ${scopeStoreName}…` : "Search all stores…"}
            style={{width:"100%",padding:"11px 14px 11px 36px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:11,color:T.forest,fontSize:14,fontFamily:"inherit",outline:"none",letterSpacing:-.1}}
          />
        </div>
        <button onClick={onClose}
          aria-label="Close search"
          style={{background:"transparent",border:"none",color:T.ink50,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:"6px 4px",letterSpacing:-.1}}>
          Cancel
        </button>
      </div>

      {/* Scope pill toggle — only shown when initialScopeStoreId is set.
          Uses the same pill style as the deal-browser chips for consistency. */}
      {initialScopeStoreId && (
        <div style={{padding:"12px 16px 0",display:"flex",gap:6,flexShrink:0}}>
          {[
            { id:"narrow", label:`${scopeStoreName} only`, active:narrowed,  onClick:()=>setNarrowed(true) },
            { id:"all",    label:"All stores",             active:!narrowed, onClick:()=>setNarrowed(false) },
          ].map(p => (
            <button key={p.id} onClick={p.onClick} aria-pressed={p.active}
              style={{
                padding:"7px 14px",fontSize:11.5,fontWeight:600,letterSpacing:-.1,
                background: p.active ? T.forest : T.parchment,
                color:      p.active ? "#fff" : T.forest,
                border: `1px solid ${p.active ? T.forest : T.ink15}`,
                borderRadius:999,cursor:"pointer",fontFamily:"inherit",
                boxShadow: p.active ? "0 1px 8px rgba(30,53,40,.18)" : "none",
                transition:"all .14s ease",
              }}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Dietary restrictions indicator */}
      {dietaryActive && (
        <div style={{padding:"12px 16px 0",display:"flex",alignItems:"center",gap:7,fontSize:11,color:T.basil,letterSpacing:-.1}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:T.basil,flexShrink:0}}></span>
          <span>Filtering by your dietary preferences <span style={{opacity:.55}}>·</span> {dietaryRestrictions.length} active</span>
        </div>
      )}

      {/* Results area */}
      <div style={{flex:1,overflowY:"auto",overscrollBehavior:"contain",padding:"12px 16px 120px"}}>
        {q.trim().length < 2 ? (
          <div>
            {/* Recent searches chips — shown when query is empty and the user
                has search history. Tapping a chip repopulates the query to
                re-run that search. Dedicated "Clear" affordance at the end. */}
            {recentSearches && recentSearches.length > 0 && (
              <div style={{padding:"4px 2px 24px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,flex:1}}>
                    <span style={{fontSize:10,letterSpacing:1.6,fontWeight:700,color:T.ink50}}>RECENT</span>
                    <span style={{flex:1,height:1,background:T.hairline}}></span>
                  </div>
                  {onClearRecentSearches && (
                    <button onClick={onClearRecentSearches}
                      style={{background:"none",border:"none",color:T.ink50,fontSize:10.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:0,letterSpacing:-.1}}>
                      Clear
                    </button>
                  )}
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {recentSearches.map((r,i) => (
                    <button key={r+"_"+i}
                      onClick={() => { setQ(r); if (inputRef.current) inputRef.current.focus(); }}
                      style={{
                        padding:"7px 13px",fontSize:12,fontWeight:600,letterSpacing:-.1,
                        background:T.parchment,
                        border:`1px solid ${T.ink15}`,
                        color:T.forest,
                        borderRadius:999,cursor:"pointer",fontFamily:"inherit",
                      }}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Default empty hint — shown whether or not history exists */}
            <div style={{padding:"32px 16px 60px",textAlign:"center"}}>
              <div style={{display:"flex",justifyContent:"center",marginBottom:14,opacity:.6}}>
                <ItemIcon emoji="🔎" size={42}/>
              </div>
              <div style={{fontSize:15,fontWeight:600,color:T.forest,marginBottom:6,letterSpacing:-.2}}>Search the catalog</div>
              <div style={{fontSize:12,color:T.ink50,lineHeight:1.55,maxWidth:300,margin:"0 auto"}}>
                Try <span style={{color:T.forest,fontWeight:600}}>eggs</span>, <span style={{color:T.forest,fontWeight:600}}>milk</span>, or <span style={{color:T.forest,fontWeight:600}}>kombucha</span>. We'll show every store that carries it, sorted cheapest first.
              </div>
            </div>
          </div>
        ) : results.length === 0 ? (
          <div style={{padding:"56px 16px",textAlign:"center"}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:14,opacity:.5}}>
              <ItemIcon emoji="🤷" size={42}/>
            </div>
            <div style={{fontSize:15,fontWeight:600,color:T.forest,marginBottom:6,letterSpacing:-.2}}>No matches found</div>
            <div style={{fontSize:12,color:T.ink50,lineHeight:1.55,maxWidth:300,margin:"0 auto"}}>
              Try a shorter or different term. Catalog has <span style={{fontWeight:600,color:T.forest,fontVariantNumeric:"tabular-nums"}}>{EXPANDED_CATALOG.length}</span> items in this preview.
            </div>
            {dietaryActive && (
              <div style={{fontSize:10.5,color:T.basil,marginTop:14,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:T.basil}}></span>
                Dietary filters may be hiding results
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{display:"flex",alignItems:"center",gap:7,padding:"4px 2px 12px"}}>
              <span style={{fontSize:10,letterSpacing:1.6,fontWeight:700,color:T.ink50}}>
                {results.length} {results.length === 1 ? "RESULT" : "RESULTS"}
              </span>
              <span style={{flex:1,height:1,background:T.hairline}}></span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {results.map(r => (
                <button
                  key={r.canonicalId}
                  onClick={() => {
                    if (onRecordSearch) onRecordSearch(q);
                    onOpenItem(r);
                  }}
                  style={{
                    display:"flex",alignItems:"center",gap:12,
                    padding:"12px 14px",
                    background:T.paper,
                    border:`1px solid ${T.ink15}`,
                    borderRadius:12,
                    cursor:"pointer",fontFamily:"inherit",textAlign:"left",
                    transition:"all .15s ease",
                  }}
                >
                  <div style={{flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",width:36,height:36,background:T.parchment,borderRadius:10}}>
                    <ItemIcon emoji={getItemEmoji(r.id, r.name, r.category)} size={24}/>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6,letterSpacing:-.1}}>
                      <span>{r.name}</span>
                      {r.organic && <span style={{fontSize:8.5,color:T.leaf,fontWeight:700,letterSpacing:.5,padding:"1px 5px",border:`1px solid ${T.leaf}`,borderRadius:3,lineHeight:1.3}}>ORG</span>}
                      {r.premium && <span style={{fontSize:8.5,color:T.warn,fontWeight:700,letterSpacing:.5,padding:"1px 5px",border:`1px solid ${T.warn}`,borderRadius:3,lineHeight:1.3}}>PREM</span>}
                    </div>
                    <div style={{fontSize:10.5,color:T.ink50,marginTop:3,display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                      <span>{r.size}</span>
                      <span style={{opacity:.4}}>·</span>
                      <span>{r.stores.length} {r.stores.length === 1 ? "store" : "stores"}</span>
                      {r.maxSavings > 0.01 && <>
                        <span style={{opacity:.4}}>·</span>
                        <span style={{color:T.basil,fontWeight:600,fontVariantNumeric:"tabular-nums"}}>save ${r.maxSavings.toFixed(2)}</span>
                      </>}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.basil,fontVariantNumeric:"tabular-nums",letterSpacing:-.3,lineHeight:1.1}}>${r.bestPrice.toFixed(2)}</div>
                    <div style={{fontSize:9.5,color:T.ink50,marginTop:2,letterSpacing:-.05}}>at {r.stores.find(s => s.storeId === r.bestStore)?.storeName}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ADMIN CATALOG SEARCH PANEL (Phase 1 verification view)
   ═══════════════════════════════════════════════════════════
   Internal tool for verifying the search data layer works before
   the user-facing search UI is built in Phase 2. Only visible when
   isAdminMode is true (URL param ?_x=4k2j8). Testers never see this.

   Three verification scopes:
     - All stores (default)
     - Each individual store (publix, aldi, sprouts, freshMarket)
   Results show canonical ID, name, size, organic/premium flags,
   store availability count, best price, and the store offering it.
   ═══════════════════════════════════════════════════════════ */
function AdminCatalogSearchPanel() {
  const [q, setQ] = useState("");
  const [scopeStoreId, setScopeStoreId] = useState("");  // "" = all stores
  const results = useMemo(() => {
    if (q.trim().length < 2) return [];
    return searchItems(q, {
      storeId: scopeStoreId || undefined,
      scope: scopeStoreId ? "store" : "all",
      limit: 20,
    });
  }, [q, scopeStoreId]);

  const storeOptions = [
    { id: "", name: "All stores" },
    ...STORES.map(s => ({ id: s.id, name: s.name })),
  ];

  return (
    <Glass style={{padding:"14px 16px",marginBottom:14,borderRadius:12,border:"1px solid rgba(160,69,98,.25)",background:T.berrySoft}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <span style={{fontSize:16}}>🔍</span>
        <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.basil}}>Catalog search (verification)</div>
      </div>
      <div style={{fontSize:11,color:T.ink50,lineHeight:1.5,marginBottom:10}}>
        Phase 1 data layer check — verifies the catalog is accessible and the search function works. Results show canonical items with per-store availability. When Phase 2 builds the user-facing search UI, it will call this same searchItems() function.
      </div>

      {/* Query input + scope selector */}
      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <input
          type="text"
          value={q}
          onChange={e=>setQ(e.target.value)}
          placeholder="Search catalog (e.g., eggs, milk, kombucha)…"
          style={{flex:"1 1 200px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:8,padding:"7px 11px",color:T.forest,fontSize:12,fontFamily:"inherit",outline:"none"}}
        />
        <select
          value={scopeStoreId}
          onChange={e=>setScopeStoreId(e.target.value)}
          style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:8,padding:"7px 11px",color:T.forest,fontSize:12,fontFamily:"inherit",outline:"none"}}
        >
          {storeOptions.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>

      {/* Summary */}
      <div style={{fontSize:10,color:T.ink50,marginBottom:8,letterSpacing:.5}}>
        {q.trim().length < 2
          ? "Type at least 2 characters to search."
          : `${results.length} result${results.length===1?"":"s"} · catalog has ${EXPANDED_CATALOG.length} canonical items`}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div style={{maxHeight:280,overflowY:"auto",borderRadius:8,border:`1px solid ${T.hairline}`}}>
          {results.map(r => (
            <div key={r.canonicalId} style={{padding:"8px 10px",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {r.name}
                  {r.organic && <span style={{marginLeft:6,fontSize:9,color:T.leaf,fontWeight:800}}>ORG</span>}
                  {r.premium && <span style={{marginLeft:6,fontSize:9,color:T.warn,fontWeight:800}}>PREM</span>}
                </div>
                <div style={{fontSize:10,color:T.ink50}}>
                  {r.size} · {r.category} · {r.stores.length} {r.stores.length===1?"store":"stores"}
                </div>
                <div style={{fontSize:9,color:T.ink50,fontFamily:"monospace",marginTop:2}}>
                  {r.canonicalId}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.basil}}>
                  ${r.bestPrice.toFixed(2)}
                </div>
                <div style={{fontSize:9,color:T.ink50}}>
                  best @ {r.stores.find(s=>s.storeId===r.bestStore)?.storeName}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cache info */}
      <div style={{fontSize:9,color:T.ink50,marginTop:8,fontFamily:"monospace"}}>
        cache: {SEARCH_CACHE.size} / {SEARCH_CACHE_MAX} entries
      </div>
    </Glass>
  );
}

/* ═══════════════════════════════════════════════════════════
   TRIP RECEIPT MODAL (Phase 4a)
   ═══════════════════════════════════════════════════════════
   Screenshot-able artifact handed to the user immediately after a
   successful Send-to-Instacart, and re-openable from the dashboard
   "Recent trips" list.

   The receipt prop mirrors the txnHistory row shape. Old MOCK rows
   seeded at first run lack lineItems / cartTotal / reward — every
   block that depends on those fields renders conditionally so an
   ancient row still opens and degrades gracefully.

   Breakdown row order (matches the brief):
     1. Instacart sale price         (cartTotal)         optional
     2. Ibotta rebates               (Σ stackable ibotta) optional
     3. CartIQ reward                (reward, DEMO pill)  optional
     4. Delivery fee                 (deliveryFee, −$X)  always
     5. Net you keep                 (netGain, bold)     always
   ═══════════════════════════════════════════════════════════ */
function TripReceiptModal({ receipt, lifetimeSavings, orderCount, isDemo, onClose, onShare }) {
  if (!receipt) return null;
  const hasItems     = Array.isArray(receipt.lineItems) && receipt.lineItems.length > 0;
  const hasCartTotal = typeof receipt.cartTotal === "number";
  const hasReward    = typeof receipt.reward === "number" && receipt.reward > 0;
  const ibottaTotal  = hasItems
    ? receipt.lineItems.filter(li => li.stackable).reduce((s, li) => s + (li.ibotta || 0) * (li.qty || 1), 0)
    : 0;
  const Row = ({ label, value, valueColor, bold, sign }) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",fontFamily:FONT_SANS,fontSize:13,color:T.ink}}>
      <span style={{color:T.ink70,fontWeight:bold?700:500,display:"flex",alignItems:"center",gap:8}}>{label}</span>
      <span style={{color:valueColor||T.ink,fontWeight:bold?800:600,fontVariantNumeric:"tabular-nums"}}>
        {sign === "neg" ? "−" : ""}${Math.abs(value).toFixed(2)}
      </span>
    </div>
  );
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:500,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.hairline}`}}>
        {/* Header */}
        <div style={{padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:22,lineHeight:1}}>{receipt.storeLogo || "🧾"}</span>
            <div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest}}>Trip receipt</div>
              <div style={{fontSize:11,color:T.ink50,marginTop:2}}>{receipt.store} · {receipt.date}</div>
            </div>
          </div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
        </div>

        <div style={{padding:"18px 22px"}}>
          {/* Hero — net savings */}
          <div style={{background:`linear-gradient(110deg,${T.sageSoft},${T.sage}4D)`,border:`1px solid ${T.sage}`,borderRadius:14,padding:"16px 18px",textAlign:"center",marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.8,textTransform:"uppercase",marginBottom:4}}>You kept</div>
            <AnimNum v={receipt.netGain || 0} size={36} color={T.forest}/>
            <div style={{fontSize:11,color:T.ink50,marginTop:4}}>on this trip</div>
          </div>

          {/* Breakdown */}
          <div style={{marginBottom:hasItems?16:8}}>
            {hasCartTotal && <Row label="Instacart sale price" value={receipt.cartTotal}/>}
            {ibottaTotal > 0 && <Row label="Ibotta rebates" value={ibottaTotal} valueColor={T.leaf}/>}
            {hasReward && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",fontFamily:FONT_SANS,fontSize:13}}>
                <span style={{color:T.ink70,fontWeight:500,display:"flex",alignItems:"center",gap:8}}>
                  CartIQ reward {isDemo && <Pill label="DEMO" color={T.warn}/>}
                </span>
                <span style={{color:T.leaf,fontWeight:600,fontVariantNumeric:"tabular-nums"}}>${receipt.reward.toFixed(2)}</span>
              </div>
            )}
            <Row label="Delivery fee" value={receipt.deliveryFee || 0} sign="neg" valueColor={T.ink70}/>
            <div style={{height:1,background:T.hairline,margin:"6px 0"}}/>
            <Row label="Net you keep" value={receipt.netGain || 0} valueColor={T.forest} bold/>
          </div>

          {/* Item list */}
          {hasItems && (
            <div style={{borderTop:`1px solid ${T.hairline}`,paddingTop:14,marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:10}}>Items ({receipt.lineItems.length})</div>
              {receipt.lineItems.map((it, i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",fontFamily:FONT_SANS,fontSize:12}}>
                  <ItemIcon emoji={getItemEmoji("", it.name, "")} size={20}/>
                  <div style={{flex:1,minWidth:0,color:T.ink,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{it.name}</div>
                  <div style={{color:T.ink50,fontSize:11,minWidth:24,textAlign:"right"}}>×{it.qty || 1}</div>
                  <div style={{color:T.ink,fontVariantNumeric:"tabular-nums",minWidth:56,textAlign:"right"}}>${((it.salePrice || 0) * (it.qty || 1)).toFixed(2)}</div>
                  {it.savings > 0 && <div style={{color:T.leaf,fontWeight:700,fontVariantNumeric:"tabular-nums",minWidth:48,textAlign:"right"}}>−${it.savings.toFixed(2)}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Cumulative line */}
          <div style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,padding:"12px 14px",display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:14}}>
            <div style={{fontSize:11,color:T.ink50}}>Saved across {orderCount} {orderCount === 1 ? "trip" : "trips"}</div>
            <AnimNum v={lifetimeSavings || 0} size={20} color={T.forest}/>
          </div>

          {/* Footer buttons */}
          <div style={{display:"flex",gap:10}}>
            <RippleBtn
              onClick={onShare}
              style={{flex:1,background:T.forest,color:T.paper,border:"none",borderRadius:12,padding:"12px 16px",fontSize:13,fontWeight:700}}
            >
              Share this trip
            </RippleBtn>
            <button
              onClick={onClose}
              style={{flex:"0 0 auto",background:T.parchment,color:T.ink70,border:`1px solid ${T.hairline}`,borderRadius:12,padding:"12px 18px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:FONT_SANS}}
            >
              Close
            </button>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CHECKOUT MODAL
   ═══════════════════════════════════════════════════════════ */
function CheckoutModal({store, cartDeals, trackingId, onClose, onConfirm, onRequestRedirect, subPrefs, onOpenSubModal}) {
  const [step,setStep] = useState("review");
  const [isNew,setIsNew] = useState(false);
  // Phase 4b: defaults to worst-case (peak) so the first number the user
  // sees is the conservative one. Toggle in the fee-estimate panel.
  const [applySurge,setApplySurge] = useState(true);
  const gross = cartDeals.reduce((s,d)=>s+d.savings,0);
  const netGain = Math.max(0, gross - store.deliveryFee);
  // Cart total and estimated reward — same math as handleConfirmOrder so the
  // checkout confirmation preview matches the ledger entry that will be written.
  const cartTotal = cartDeals.reduce((s,d) => {
    const qty = d.qty || 1;
    const price = d.salePrice || 0;
    return s + (price * qty);
  }, 0);
  const estReward = parseFloat((cartTotal * REWARDS_RATE).toFixed(2));
  // Affiliate URL carries the ledger entry's tracking ID so Impact can later
  // report a commission against this specific order. Without the ID, Impact
  // attribution still works (via the utm_* params) but we can't match the
  // commission back to an individual ledger entry for per-order confirmation.
  const affUrl = buildAffiliateUrl(store, cartDeals, trackingId);

  // Detect items that require digital coupon clipping in the store's app
  const clippingItems = cartDeals.filter(d => requiresClipping({ ...d, store }));
  const storeClipping = CLIPPING_REQUIRED_STORES[store.id];
  const needsClipping = clippingItems.length > 0 && storeClipping;

  function handleOpenInstacart() {
    SFX.payment();
    if (needsClipping) setStep("clipping");
    else setStep("redirecting");
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"92vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.hairline}`,boxShadow:"0 24px 80px rgba(0,0,0,.9)",animation:"slideUp .3s ease both"}}>
        <div style={{background:store.bg,padding:"18px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"relative",overflow:"hidden",borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[store.color,store.glow,T.basil]}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:28}}>{store.logo}</span>
            <div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.paper}}>Checkout via Instacart</div>
              <div style={{fontSize:11,color:T.paper,opacity:.6}}>{store.name} · {cartDeals.length} items</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close checkout" style={{background:T.parchment,border:"none",color:T.forest,borderRadius:10,width:32,height:32,cursor:"pointer",fontSize:16,position:"relative",zIndex:1}}>✕</button>
        </div>

        <div style={{padding:"20px 22px"}}>
          {step==="review" && <>
            {cartDeals.map((d,i)=>{
              // Phase 5a: surface the saved substitution preference per line so
              // users can verify/change it at the last moment before handoff.
              // Tap opens the existing SubstitutionModal (parent owns state).
              // Fallback label "Closest match" maps to the similar_ok default
              // that subPrefs defaults to when nothing has been saved yet.
              const sp = subPrefs && subPrefs[d.id];
              const subOpt = sp && SUB_OPTIONS[sp.pref];
              const subLabel = subOpt ? subOpt.label : "Closest match";
              const subIcon  = subOpt ? subOpt.icon  : "🔄";
              return (
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.hairline}`}}>
                <ItemIcon emoji={getItemEmoji(d.id, d.name, d.category)} size={18}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:T.forest}}>
                    {d.name}
                    {requiresClipping({...d,store}) && <span style={{marginLeft:6,fontSize:10,color:T.warn,fontWeight:800}} title="Requires digital coupon clipping">📎 clip</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,flexWrap:"wrap"}}>
                    <Pill label={d.dealLabel} color={store.color}/>
                    {SUBSTITUTION_PAYLOAD_ENABLED && onOpenSubModal && (
                      <button onClick={()=>onOpenSubModal(d)}
                        aria-label={`Substitution: ${subLabel}. Tap to change.`}
                        style={{display:"inline-flex",alignItems:"center",gap:4,background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:999,padding:"3px 9px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                        <span aria-hidden="true">{subIcon}</span>{subLabel}
                      </button>
                    )}
                  </div>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.basil}}>−${d.savings.toFixed(2)}</div>
              </div>
              );
            })}
            {needsClipping && (
              <div style={{padding:"10px 12px",marginTop:12,background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:11,fontSize:11,color:T.warn,display:"flex",alignItems:"center",gap:8}}>
                <span aria-hidden="true">📎</span>
                <div>
                  <strong>{clippingItems.length} item{clippingItems.length>1?"s":""} need{clippingItems.length===1?"s":""} coupon clipping.</strong><br/>
                  <span style={{color:T.butter,opacity:.9}}>We'll remind you before the handoff.</span>
                </div>
              </div>
            )}
            <Glass style={{padding:"14px 16px",marginTop:14,borderRadius:14}}>
              {[{l:"Deal savings",v:`+$${gross.toFixed(2)}`,c:T.leaf},{l:"Delivery",v:`−$${store.deliveryFee.toFixed(2)}`,c:T.berry},{l:"Your net gain",v:`$${netGain.toFixed(2)}`,c:T.forest,bold:true}].map((r,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:i<2?`1px solid ${T.hairline}`:"none"}}>
                  <span style={{fontSize:r.bold?12:11,color:r.bold?T.ink70:T.ink50,fontWeight:r.bold?700:400}}>{r.l}</span>
                  <span style={{fontSize:r.bold?15:11,fontWeight:r.bold?800:600,color:r.c}}>{r.v}</span>
                </div>
              ))}
            </Glass>
            {FEE_ESTIMATE_ENABLED && (() => {
              const est = estimateOrderTotal(cartTotal, store.deliveryFee, applySurge);
              const rows = [
                {l:"Item subtotal",            v:`$${est.subtotal.toFixed(2)}`,    c:T.ink70},
                {l:"Instacart service fee (est.)", v:`+$${est.serviceFee.toFixed(2)}`, c:T.ink70},
                {l:"Delivery fee",             v:`+$${est.deliveryFee.toFixed(2)}`, c:T.ink70},
                ...(applySurge ? [{l:"Busy-time surge (est.)", v:`+$${est.surge.toFixed(2)}`, c:T.warn}] : []),
                {l:"Estimated total",          v:`$${est.estimatedTotal.toFixed(2)}`, c:T.forest, bold:true},
              ];
              return (
                <Glass style={{padding:"14px 16px",marginTop:14,borderRadius:14}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:13,fontWeight:700,color:T.forest,letterSpacing:-.2}}>What this will cost</div>
                    <Pill label="ESTIMATE" color={T.warn}/>
                  </div>
                  {rows.map((r,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:i<rows.length-1?`1px solid ${T.hairline}`:"none"}}>
                      <span style={{fontSize:r.bold?12:11,color:r.bold?T.ink70:T.ink50,fontWeight:r.bold?700:400}}>{r.l}</span>
                      <span style={{fontSize:r.bold?15:11,fontWeight:r.bold?800:600,color:r.c}}>{r.v}</span>
                    </div>
                  ))}
                  <button onClick={()=>setApplySurge(s=>!s)}
                    style={{marginTop:10,width:"100%",background:applySurge?T.parchment:T.sageSoft,border:`1px solid ${applySurge?T.hairline:T.sage}`,color:applySurge?T.ink70:T.basil,borderRadius:9,padding:"7px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    {applySurge ? "I'll pick an off-peak slot →" : "✓ Off-peak — surge removed"}
                  </button>
                  <div style={{fontSize:10,color:T.ink50,marginTop:8,lineHeight:1.4}}>
                    Estimated — Instacart sets final fees at its own checkout. Tip is added there too.
                  </div>
                </Glass>
              );
            })()}
            <RippleBtn sound="success" onClick={handleOpenInstacart}
              style={{background:T.forest,border:"none",color:T.paper,borderRadius:13,width:"100%",padding:"15px",fontSize:14,fontWeight:700,marginTop:16,boxShadow:T.shadowFloat,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
              <span style={{fontSize:22}}>🛒</span> Open in Instacart →
            </RippleBtn>
            <div style={{textAlign:"center",fontSize:10,color:T.ink50,marginTop:8}}>{AFFILIATE_COPY.checkoutFooter}</div>
          </>}

          {step==="clipping" && (
            <div style={{padding:"4px 0"}}>
              <div style={{textAlign:"center",marginBottom:18}}>
                <div style={{fontSize:44,marginBottom:10}} aria-hidden="true">📎</div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.paper,marginBottom:6}}>
                  Clip coupons first!
                </div>
                <div style={{fontSize:12,color:T.ink50,lineHeight:1.6,maxWidth:340,margin:"0 auto"}}>
                  {store.name} requires you to "clip" these digital coupons inside the {storeClipping.appName} app <strong>before</strong> you check out. Otherwise the discounts won't apply.
                </div>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,marginBottom:8,textTransform:"uppercase"}}>
                  Items needing clipping ({clippingItems.length})
                </div>
                {clippingItems.map((d,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",marginBottom:6,background:T.butterSoft,border:"1px solid rgba(251,191,36,.18)",borderRadius:11}}>
                    <span style={{fontSize:16}} aria-hidden="true">📎</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.butter,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</div>
                      <div style={{fontSize:10,color:T.ink50}}>{d.dealLabel} · −${d.savings.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <a href={storeClipping.webUrl} target="_blank" rel="noopener noreferrer"
                onClick={()=>SFX.tabSwitch()}
                style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:T.butter,color:T.forest,borderRadius:12,padding:"13px 20px",fontSize:13,fontWeight:700,textDecoration:"none",marginBottom:10,boxShadow:"0 0 20px rgba(251,191,36,.25)"}}>
                <span style={{fontSize:18}}>📱</span> Open {storeClipping.appName} app
              </a>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setStep("review")}
                  style={{flex:1,background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:11,padding:"11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  ← Back
                </button>
                <RippleBtn sound="tabSwitch" onClick={()=>setStep("redirecting")}
                  style={{flex:1.5,background:`${T.forest}`,border:"none",color:T.paper,borderRadius:11,padding:"11px",fontSize:12,fontWeight:800}}>
                  I've clipped — continue →
                </RippleBtn>
              </div>
            </div>
          )}

          {step==="redirecting" && (
            <div style={{textAlign:"center",padding:"28px 20px"}}>
              <div style={{fontSize:58,marginBottom:14,animation:"bounce .9s ease infinite"}}>🛒</div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.paper,marginBottom:6}}>Your cart is ready!</div>
              {/* Banner shown when integration is in preview mode. Sits ABOVE
                  the CTA so users read it before tapping. Hidden entirely once
                  INSTACART_INTEGRATION_LIVE flips to true. */}
              {INTEGRATION_COPY.bannerText && (
                <div style={{margin:"4px auto 14px auto",maxWidth:380,padding:"10px 14px",borderRadius:11,background:T.butterSoft,border:"1px solid rgba(251,191,36,.22)",fontSize:11.5,color:T.forest,lineHeight:1.45,textAlign:"left",display:"flex",alignItems:"flex-start",gap:9}}>
                  <span style={{fontSize:15,flexShrink:0,lineHeight:1.2}} aria-hidden="true">ℹ️</span>
                  <span><span style={{color:T.warn,fontWeight:800}}>Preview:</span> {INTEGRATION_COPY.bannerText.replace("Preview mode — ", "")}</span>
                </div>
              )}
              {/* Delegating button — parent (CartIQ) owns the actual navigation
                  so it can intercept with the disclosure modal on first use. */}
              <button onClick={()=>{ SFX.cash(); setStep("done"); if (onRequestRedirect) onRequestRedirect(affUrl); }}
                style={{position:"relative",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:10,background:T.forest,color:T.paper,border:"none",borderRadius:14,padding:"16px 28px",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 0 30px #f9731650",marginBottom:14}}>
                🛒 Open Instacart Now →
                {/* Tiny PREVIEW pill, top-right of the button. Same amber
                    styling used elsewhere for beta/preview affordances. */}
                {INTEGRATION_COPY.pillLabel && (
                  <span style={{position:"absolute",top:-7,right:-7,fontSize:8.5,fontWeight:700,letterSpacing:.7,color:T.warn,background:T.forest,border:`1.5px solid ${T.butter}`,borderRadius:5,padding:"2px 6px",lineHeight:1.2,boxShadow:"0 2px 8px rgba(0,0,0,.4)"}}>
                    {INTEGRATION_COPY.pillLabel}
                  </span>
                )}
              </button>
            </div>
          )}

          {step==="done" && (
            <div style={{textAlign:"center",padding:"32px 20px",animation:"fadeIn .4s ease both"}}>
              <div className="confetti-burst" style={{fontSize:58,marginBottom:14}}>🎉</div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.paper,marginBottom:6}}>Instacart is open!</div>
              <AnimNum v={netGain} size={42} color={T.leaf} pre="You saved $"/>
              {/* Secondary line — estimated rewards tracked for this specific order.
                  Only shown when the reward is non-zero (cart had real dollar total)
                  so zero-value demo orders don't show a $0.00 reward line. */}
              {estReward > 0 && (
                <div style={{marginTop:10,padding:"8px 14px",display:"inline-flex",alignItems:"center",gap:7,background:T.sageSoft,border:"1px solid rgba(46,158,79,.22)",borderRadius:10}}>
                  <span style={{fontSize:14}}>🎁</span>
                  <span style={{fontSize:12,color:T.forest}}>
                    <span style={{color:T.basil,fontWeight:800}}>+${estReward.toFixed(2)}</span> {REWARDS_COPY.checkoutCelebration}
                  </span>
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:20}}>
                <RippleBtn sound="success" onClick={()=>{onConfirm();onClose();}}
                  style={{background:`linear-gradient(135deg,${T.basil},${T.leaf})`,border:"none",color:T.paper,borderRadius:11,padding:"11px 24px",fontSize:13,fontWeight:800}}>
                  ✓ Done Shopping
                </RippleBtn>
                <button onClick={()=>{ if (onRequestRedirect) onRequestRedirect(affUrl); }}
                  style={{background:T.apricot,color:T.paper,border:"none",borderRadius:11,padding:"11px 18px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center"}}>
                  🔁 Reopen
                </button>
              </div>
            </div>
          )}
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUBSTITUTION MODAL
   ═══════════════════════════════════════════════════════════ */
function SubstitutionModal({ deal, prefs, onSave, onClose }) {
  const existing = prefs[deal.id] || { pref:"similar_ok", sub:"", votes:{} };
  const [pref, setPref] = useState(existing.pref);
  const [sub, setSub] = useState(existing.sub);
  const [custom, setCustom] = useState("");
  const [votes, setVotes] = useState(existing.votes || {});
  // Phase 4c: map persisted maxIncrease back to a preset id for the
  // chip selector. Absent / null / unrecognized value all resolve to "none".
  const initialCapId = (() => {
    if (existing.maxIncrease == null) return "none";
    const match = SUB_CAP_PRESETS.find(p => p.value === existing.maxIncrease);
    return match ? match.id : "none";
  })();
  const [capId, setCapId] = useState(initialCapId);
  const suggested = getSuggestedSubs(deal.name);

  const upCount = Object.values(votes).filter(v => v === "up").length;
  const downCount = Object.values(votes).filter(v => v === "down").length;
  const hasDispute = upCount > 0 && downCount > 0;

  const castVote = (memberId, value) => {
    SFX.tabSwitch();
    setVotes(prev => {
      const next = { ...prev };
      if (next[memberId] === value) delete next[memberId]; // toggle off
      else next[memberId] = value;
      return next;
    });
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.55)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,boxShadow:"0 24px 80px rgba(0,0,0,.9)",animation:"slideUp .3s ease both",border:`1px solid ${T.hairline}`}}>
        <div style={{padding:"18px 20px",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",gap:12}}>
          <ItemIcon emoji={getItemEmoji(deal.id, deal.name, deal.category)} size={26}/>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:800,color:T.forest}}>{deal.name}</div>
            <div style={{fontSize:11,color:T.ink50,marginTop:2}}>Set substitution preference</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:28,height:28,cursor:"pointer",fontSize:14}}>✕</button>
        </div>
        <div style={{padding:"18px 20px"}}>
          <div style={{display:"grid",gap:8,marginBottom:20}}>
            {Object.entries(SUB_OPTIONS).map(([key,opt])=>(
              <button key={key} onClick={()=>setPref(key)}
                style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:pref===key?T.sageSoft:T.parchment,border:`2px solid ${pref===key?T.basil:T.parchment}`,borderRadius:12,cursor:"pointer",textAlign:"left",fontFamily:"inherit"}}>
                <span style={{fontSize:20}}>{opt.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:pref===key?T.leaf:T.forest}}>{opt.label}</div>
                  <div style={{fontSize:11,color:T.ink50}}>{opt.desc}</div>
                </div>
                {pref===key && <div style={{width:18,height:18,borderRadius:"50%",background:T.basil,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:T.paper}}>✓</div>}
              </button>
            ))}
          </div>
          {/* ── Phase 4c: cost-cap selector ── */}
          {SUB_COST_CAP_ENABLED && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.8,textTransform:"uppercase",marginBottom:8}}>If the swap costs more…</div>
              <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:8}}>
                {SUB_CAP_PRESETS.map(p => (
                  <button key={p.id} onClick={()=>setCapId(p.id)} aria-pressed={capId===p.id}
                    style={{background:capId===p.id?T.sageSoft:T.parchment,border:`1px solid ${capId===p.id?T.basil:T.parchment}`,color:capId===p.id?T.leaf:T.ink50,borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div style={{fontSize:10.5,color:T.ink50,lineHeight:1.5}}>
                CartIQ records this. When Instacart integration is live, swaps above your limit get flagged before they're charged.
              </div>
            </div>
          )}
          {pref!=="brand_only" && suggested.length>0 && (
            <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:16}}>
              {suggested.map(s=>(
                <button key={s} onClick={()=>{setSub(s);setCustom("");}}
                  style={{background:sub===s?T.sageSoft:T.parchment,border:`1px solid ${sub===s?T.basil:T.parchment}`,color:sub===s?T.leaf:T.ink50,borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                  {s}
                </button>
              ))}
            </div>
          )}
          {pref!=="brand_only" && (
            <input value={custom} onChange={e=>{setCustom(e.target.value);setSub("");}} placeholder="Or type custom alternative..."
              style={{width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,color:T.forest,padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none",marginBottom:16,boxSizing:"border-box"}}/>
          )}

          {/* ── HOUSEHOLD VOTING ── */}
          <div style={{background:T.berrySoft,border:"1px solid rgba(160,69,98,.15)",borderRadius:12,padding:"13px 14px",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{fontSize:10,letterSpacing:1.5,color:T.basil,fontWeight:800}}>👨‍👩‍👧 HOUSEHOLD VOTES</div>
              {(upCount > 0 || downCount > 0) && (
                <div style={{fontSize:10,color:T.ink50,display:"flex",gap:8}}>
                  <span style={{color:T.basil,fontWeight:800}}>👍 {upCount}</span>
                  <span style={{color:T.berry,fontWeight:800}}>👎 {downCount}</span>
                </div>
              )}
            </div>
            {hasDispute && (
              <div style={{fontSize:10,color:T.warn,background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:8,padding:"6px 9px",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                <span>⚠️</span><span><strong>Split vote</strong> — talk it out in Household chat.</span>
              </div>
            )}
            <div style={{display:"grid",gap:6}}>
              {HOUSEHOLD.map(m => {
                const v = votes[m.id];
                return (
                  <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 8px",background:T.parchment,borderRadius:9}}>
                    <span style={{fontSize:18}}>{m.avatar}</span>
                    <span style={{flex:1,fontSize:12,fontWeight:700,color:T.forest}}>{m.name}</span>
                    <button onClick={()=>castVote(m.id,"up")} aria-label={`${m.name} vote yes`} aria-pressed={v==="up"}
                      style={{background:v==="up"?T.sageSoft:T.parchment,border:`1px solid ${v==="up"?T.sage:T.ink15}`,borderRadius:8,width:30,height:26,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>
                      👍
                    </button>
                    <button onClick={()=>castVote(m.id,"down")} aria-label={`${m.name} vote no`} aria-pressed={v==="down"}
                      style={{background:v==="down"?T.berrySoft:T.parchment,border:`1px solid ${v==="down"?T.berry:T.ink15}`,borderRadius:8,width:30,height:26,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>
                      👎
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <button onClick={()=>{
            const saved = {pref,sub:pref==="brand_only"?"":(custom||sub),votes};
            // Phase 4c: only persist maxIncrease when a real cap is set.
            // "No limit" stays absent from the entry so the shape stays clean
            // and pre-Phase-4c entries remain indistinguishable from uncapped.
            if (SUB_COST_CAP_ENABLED) {
              const capPreset = SUB_CAP_PRESETS.find(p => p.id === capId);
              if (capPreset && capPreset.value !== null) saved.maxIncrease = capPreset.value;
            }
            onSave(deal.id, saved); onClose(); SFX.success();
          }}
            style={{width:"100%",background:`${T.forest}`,border:"none",color:T.paper,borderRadius:12,padding:"13px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:T.shadowFloat}}>
            Save Preference
          </button>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ASK SAGE MODAL — per-deal Q&A
   ═══════════════════════════════════════════════════════════
   Focused, deal-specific AI assistant. Replaces the old global
   chat bubble. Context is tight: just this deal + its price
   history, so the answer is either genuinely useful or it fails
   fast. Three canned prompts cover the most common questions;
   free-form input handles the rest.
   ═══════════════════════════════════════════════════════════ */
function AskSageModal({ deal, store, userName, onClose }) {
  const [messages, setMessages] = useState([]);  // {role, text}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const hist = PRICE_HISTORY[deal.id];
  const histAvg = hist && hist.length ? (hist.reduce((a, b) => a + b, 0) / hist.length) : null;
  const currentPrice = effectivePerUnit(deal);

  const systemPrompt = `You are Sage, a grocery-savings assistant in the CartIQ app. The user is asking about ONE specific deal:

DEAL: ${deal.name} at ${store.name}
Label: ${deal.dealLabel}
Original price: $${deal.origPrice?.toFixed(2)}
Sale price: $${deal.salePrice?.toFixed(2)}
Effective per-unit: $${currentPrice.toFixed(2)}
Savings: $${deal.savings?.toFixed(2)}${deal.ibotta ? ` + $${deal.ibotta.toFixed(2)} Ibotta cashback` : ""}
Expires in: ${deal.expiresIn} days
${histAvg ? `6-week average: $${histAvg.toFixed(2)}` : ""}
${hist ? `Price history: ${hist.map(p => `$${p.toFixed(2)}`).join(" → ")}` : ""}

Keep answers short (2-3 sentences). Be clear and concise. Only state facts you can see in the deal context above — never invent prices, future sale dates, competitor prices, or store inventory. If something isn't knowable from the context, say so plainly; that is accuracy, not hedging. If the user asks about a different item or a general question, gently redirect to the specific deal.`;

  const suggestedPrompts = [
    { icon: "target",      text: "Is this a good price?" },
    { icon: "clock",       text: "When will it go on sale again?" },
    { icon: "arrow-right", text: "What's a good substitute?" },
    { icon: "wallet",      text: "How much would I save buying 2?" },
  ];

  // % below 6-week average for the deal context card
  const pctBelowAvg = histAvg ? Math.round((1 - currentPrice/histAvg) * 100) : null;
  const verdict = pctBelowAvg == null ? null
                : pctBelowAvg >= 20 ? { label: "Great price",  c: T.leaf,   bg: T.sageSoft }
                : pctBelowAvg >= 8  ? { label: "Good price",   c: T.basil,  bg: T.sageSoft }
                : pctBelowAvg >= 0  ? { label: "Fair price",   c: T.ink50,  bg: T.parchment }
                : { label: "Above average", c: T.warn, bg: T.butterSoft };

  // Per-session question counter. useRef so it survives re-renders but
  // resets on a fresh app load.
  const sentCountRef = useRef(0);

  async function send(text) {
    if (!text.trim() || loading) return;

    const userMsg = { role: "user", text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");

    // Brake 1: per-session limit.
    if (SAGE_LIVE && sentCountRef.current >= SAGE_SESSION_LIMIT) {
      setMessages(prev => [...prev, { role: "assistant",
        text: "We've covered a lot this session — reopen Sage in a bit to keep going." }]);
      return;
    }

    setLoading(true);
    try {
      if (SAGE_LIVE) {
        sentCountRef.current += 1;
        // Build the API messages array from the running conversation.
        const apiMessages = [...messages, userMsg].map(m => ({
          role: m.role,
          content: m.text,
        }));
        const res = await fetch("/api/sage", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ systemPrompt, messages: apiMessages }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setMessages(prev => [...prev, { role: "assistant", text: (data.reply || "…").trim() }]);
      } else {
        // SAGE_LIVE off — offline heuristic, no API call, no cost.
        const answer = offlineHeuristicAnswer(text, deal, store, hist, currentPrice);
        setMessages(prev => [...prev, { role: "assistant", text: answer }]);
      }
    } catch (e) {
      // Graceful fallback: if the proxy fails, drop to the heuristic so
      // the user still gets an answer instead of an error.
      const answer = offlineHeuristicAnswer(text, deal, store, hist, currentPrice);
      setMessages(prev => [...prev, { role: "assistant", text: answer }]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (endRef.current && endRef.current.parentElement) {
      const container = endRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, loading]);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:900,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:0,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:480,maxHeight:"86vh",display:"flex",flexDirection:"column",borderRadius:"22px 22px 0 0",border:`1px solid ${T.sage}`,boxShadow:"0 -8px 40px rgba(0,0,0,.5)",animation:"slideUp .3s ease both",overscrollBehavior:"contain"}}>

        {/* Drag handle */}
        <div style={{display:"flex",justifyContent:"center",paddingTop:8,flexShrink:0}}>
          <div style={{width:36,height:4,borderRadius:2,background:T.hairline}}/>
        </div>

        {/* Header */}
        <div style={{padding:"10px 18px 14px",display:"flex",alignItems:"center",gap:11,flexShrink:0}}>
          <CartIQMark size={36} mood="happy"/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest,letterSpacing:-.2}}>Sage</div>
              <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:9.5,fontWeight:800,letterSpacing:1,color:T.leaf,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:20,padding:"2px 7px"}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:T.leaf,boxShadow:`0 0 6px ${T.leaf}`}}/>
                ONLINE
              </span>
            </div>
            <div style={{fontSize:11,color:T.ink50,fontWeight:600}}>Your grocery savings assistant</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>
            <Icon name="x" size={20} color={T.ink50} stroke={2.2}/>
          </button>
        </div>

        {/* Deal context card — sticky reference for the conversation */}
        <div style={{margin:"0 14px 12px",padding:"11px 13px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:14,display:"flex",alignItems:"center",gap:11,flexShrink:0}}>
          <div style={{width:34,height:34,borderRadius:9,background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon name="tag" size={21} color={T.basil} stroke={2.2}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:9.5,letterSpacing:1.4,color:T.ink50,fontWeight:800,marginBottom:2}}>DISCUSSING</div>
            <div style={{fontSize:13,color:T.forest,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{deal.name}</div>
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3,flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:T.ink50,fontWeight:600}}>{store.name}</span>
              <span style={{width:3,height:3,borderRadius:"50%",background:T.ink50,opacity:.5}}/>
              <span style={{fontFamily:FONT_DISPLAY,fontSize:12,color:T.forest,fontWeight:700}}>${currentPrice.toFixed(2)}</span>
              {deal.origPrice && deal.origPrice > deal.salePrice && (
                <span style={{fontSize:10.5,color:T.ink50,textDecoration:"line-through",fontWeight:600}}>${deal.origPrice.toFixed(2)}</span>
              )}
              {verdict && (
                <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:9.5,fontWeight:800,letterSpacing:.6,color:verdict.c,background:verdict.bg,border:`1px solid ${verdict.c}40`,borderRadius:20,padding:"1px 7px"}}>
                  {verdict.label}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{flex:1,overflowY:"auto",overscrollBehavior:"contain",padding:"4px 18px 14px",display:"flex",flexDirection:"column",gap:10}}>
          {messages.length === 0 && (
            <div style={{padding:"4px 0 8px"}}>
              <div style={{fontSize:12,color:T.forest,marginBottom:14,lineHeight:1.5,fontWeight:600}}>
                Hi{userName ? ` ${userName.split(/\s+/)[0]}` : ""} — ask me anything about this deal.
              </div>
              <div style={{fontSize:9.5,letterSpacing:1.4,color:T.ink50,fontWeight:800,marginBottom:8}}>SUGGESTED</div>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {suggestedPrompts.map((p, i) => (
                  <button key={i} onClick={() => send(p.text)}
                    style={{textAlign:"left",display:"flex",alignItems:"center",gap:10,background:T.parchment,border:`1px solid ${T.hairline}`,color:T.forest,borderRadius:11,padding:"10px 12px",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.background=T.sageSoft;e.currentTarget.style.borderColor=T.sage;}}
                    onMouseLeave={e=>{e.currentTarget.style.background=T.parchment;e.currentTarget.style.borderColor=T.hairline;}}>
                    <span style={{width:26,height:26,borderRadius:7,background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <Icon name={p.icon} size={14} color={T.basil} stroke={2.2}/>
                    </span>
                    <span style={{flex:1}}>{p.text}</span>
                    <Icon name="chevron-right" size={17} color={T.ink50} stroke={2.2}/>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",flexDirection:m.role==="user"?"row-reverse":"row"}}>
              {m.role==="assistant" && (
                <div style={{width:26,height:26,borderRadius:8,background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                  <CartIQMark size={16}/>
                </div>
              )}
              <div style={{
                background: m.role==="user" ? `linear-gradient(135deg, ${T.forest}, ${T.basil})` : T.parchment,
                border: m.role==="user" ? "none" : `1px solid ${T.hairline}`,
                borderRadius: m.role==="user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                padding:"10px 14px",
                maxWidth:"82%",
                fontSize:12.5,
                color: m.role==="user" ? T.paper : T.forest,
                lineHeight:1.5,
                whiteSpace:"pre-wrap",
                fontWeight:500,
              }}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
              <div style={{width:26,height:26,borderRadius:8,background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                <CartIQMark size={16}/>
              </div>
              <div style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:"16px 16px 16px 4px",padding:"12px 14px",display:"flex",alignItems:"center",gap:5}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:T.sage,animation:"bounce 1.2s infinite ease-in-out",animationDelay:"0s"}}/>
                <span style={{width:6,height:6,borderRadius:"50%",background:T.sage,animation:"bounce 1.2s infinite ease-in-out",animationDelay:".15s"}}/>
                <span style={{width:6,height:6,borderRadius:"50%",background:T.sage,animation:"bounce 1.2s infinite ease-in-out",animationDelay:".3s"}}/>
              </div>
            </div>
          )}
          <div ref={endRef}/>
        </div>

        {/* Input */}
        <div style={{padding:"10px 14px 14px",borderTop:`1px solid ${T.hairline}`,display:"flex",gap:8,flexShrink:0,paddingBottom:"max(14px, env(safe-area-inset-bottom))",alignItems:"center",background:T.paper}}>
          <div style={{flex:1,position:"relative",display:"flex",alignItems:"center"}}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send(input))}
              placeholder="Ask Sage anything…"
              disabled={loading}
              style={{flex:1,width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,color:T.forest,padding:"11px 14px",fontSize:13,fontFamily:"inherit",outline:"none",fontWeight:500}}
              onFocus={e=>e.currentTarget.style.borderColor=T.sage}
              onBlur={e=>e.currentTarget.style.borderColor=T.hairline}
            />
          </div>
          <RippleBtn sound="notify" onClick={() => send(input)} disabled={!input.trim() || loading} aria-label="Send"
            style={{
              background: input.trim()&&!loading ? `linear-gradient(135deg, ${T.forest}, ${T.basil})` : T.parchment,
              border: input.trim()&&!loading ? "none" : `1px solid ${T.hairline}`,
              color: input.trim()&&!loading ? T.paper : T.ink50,
              borderRadius:12,
              width:42,height:42,
              display:"flex",alignItems:"center",justifyContent:"center",
              flexShrink:0,
              transition:"all .15s",
            }}>
            <Icon name="arrow-right" size={21} color={input.trim()&&!loading?T.paper:T.ink50} stroke={2.4}/>
          </RippleBtn>
        </div>
      </Glass>
    </div>
  );
}

// Offline fallback when no API is wired. Uses the deal's own data to produce
// a useful answer for the three most common question patterns.
function offlineHeuristicAnswer(question, deal, store, hist, currentPrice) {
  const q = question.toLowerCase();
  const avg = hist && hist.length ? (hist.reduce((a,b)=>a+b,0) / hist.length) : null;
  if (q.includes("good") || q.includes("worth") || q.includes("should")) {
    if (avg && currentPrice <= avg * 0.85) {
      return `Yes — at $${currentPrice.toFixed(2)} effective, this is about ${Math.round((1 - currentPrice/avg) * 100)}% below the 6-week average of $${avg.toFixed(2)}. Good time to buy.`;
    }
    if (avg) {
      return `It's close to the 6-week average of $${avg.toFixed(2)}. Decent, but not exceptional. If you need it, fine — otherwise watchlist it.`;
    }
    return `Hard to say without more history. The savings tag of $${deal.savings?.toFixed(2)} is real though.`;
  }
  if (q.includes("when") || q.includes("again") || q.includes("next")) {
    return `Based on the 6-week pattern, ${deal.name} at ${store.name} tends to cycle every 2-3 weeks. Add it to your watchlist and I'll alert you when it drops.`;
  }
  if (q.includes("substitute") || q.includes("alternative") || q.includes("instead")) {
    return `For ${deal.name}, look for the same category at the other stores you've selected. Cheaper store brands often match quality. Check the Substitutions tab to set your default rule.`;
  }
  return `I can't reach the full assistant right now, but the deal saves $${deal.savings?.toFixed(2)} — the math is real. Try again in a bit for a detailed answer.`;
}

/* ═══════════════════════════════════════════════════════════
   INTEGRATION DISCLOSURE MODAL — preview-mode handoff explainer
   ═══════════════════════════════════════════════════════════
   Surfaces once per session the first time the user reaches the
   redirect step in the Checkout flow. Sets honest expectations:
   the button works, but cart contents do NOT auto-transfer to
   Instacart yet — the user shops manually for now.

   Hidden entirely when INSTACART_INTEGRATION_LIVE flips to true.
   ═══════════════════════════════════════════════════════════ */
function IntegrationDisclosureModal({ onContinue, onClose }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.55)",zIndex:710,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:420,borderRadius:22,border:"1px solid rgba(251,191,36,.3)",boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(251,191,36,.12)",animation:"slideUp .3s ease both",overflow:"hidden"}}>
        <div style={{padding:"22px 22px 14px 22px",borderBottom:`1px solid ${T.hairline}`}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
            <div style={{width:44,height:44,borderRadius:12,background:"linear-gradient(135deg,rgba(251,191,36,.22),rgba(180,83,9,.12))",border:`1px solid ${T.butter}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>
              🔗
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,lineHeight:1.2}}>{INTEGRATION_COPY.modalTitle}</div>
              <div style={{fontSize:11,color:T.warn,marginTop:3,fontWeight:700,letterSpacing:.4}}>INSTACART INTEGRATION · PENDING</div>
            </div>
          </div>
        </div>
        <div style={{padding:"18px 22px 22px 22px",display:"grid",gap:14}}>
          <div style={{fontSize:13,color:T.ink50,lineHeight:1.6}}>{INTEGRATION_COPY.modalBody}</div>

          {/* Two-column "now vs. soon" comparison — quick scan, sets expectations */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
            <div style={{padding:"11px 12px",borderRadius:10,background:T.butterSoft,border:"1px solid rgba(251,191,36,.22)"}}>
              <div style={{fontSize:9.5,fontWeight:700,color:T.warn,letterSpacing:.6,marginBottom:5}}>TODAY</div>
              <div style={{fontSize:11.5,color:T.forest,lineHeight:1.4}}>Opens the right Instacart store · you add items manually</div>
            </div>
            <div style={{padding:"11px 12px",borderRadius:10,background:T.sageSoft,border:"1px solid rgba(74,222,128,.22)"}}>
              <div style={{fontSize:9.5,fontWeight:700,color:T.basil,letterSpacing:.6,marginBottom:5}}>SOON</div>
              <div style={{fontSize:11.5,color:T.forest,lineHeight:1.4}}>Your full CartIQ cart auto-loads into Instacart's checkout</div>
            </div>
          </div>

          <button onClick={onContinue}
            style={{background:T.forest,border:"none",color:T.paper,borderRadius:12,padding:"14px",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:800,letterSpacing:.3,boxShadow:"0 6px 20px rgba(249,115,22,.35)",marginTop:4}}>
            Got it — continue to Instacart
          </button>
          <button onClick={onClose}
            style={{background:"transparent",border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:12,padding:"10px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600}}>
            Cancel
          </button>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TERMS MODAL
   ═══════════════════════════════════════════════════════════ */
function TermsModal({ onClose }) {
  const sections = [
    {title:"Copyright",icon:"©",color:T.leaf,text:`CartIQ™ and all content, features, source code, design, algorithms, and branding are the exclusive property of CartIQ. © ${COPYRIGHT_YEAR} CartIQ. All Rights Reserved.`},
    {title:"Proprietary Software",icon:"🔒",color:T.basil,text:"The CartIQ application is proprietary and confidential. Unauthorized copying, reproduction, distribution, modification, or reverse engineering is strictly prohibited."},
    {title:"DMCA Policy",icon:"⚖️",color:T.basil,text:`CartIQ complies with the DMCA. Contact our DMCA Agent at ${DMCA_EMAIL}.`},
    {title:"Affiliate Disclosure",icon:"💰",color:T.butter,text:AFFILIATE_COPY.termsDisclosure},
    {title:"Privacy",icon:"🛡️",color:T.leaf,text:"CartIQ collects only information necessary to provide savings features. We do not sell your data."},
  ];
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:500,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.hairline}`}}>
        <div style={{padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest}}>Legal & Terms</div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"20px 22px"}}>
          {sections.map((s,i)=>(
            <div key={i} style={{marginBottom:20,paddingBottom:16,borderBottom:i<sections.length-1?`1px solid ${T.hairline}`:"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:16}}>{s.icon}</span>
                <span style={{fontSize:13,fontWeight:800,color:s.color}}>{s.title}</span>
              </div>
              <p style={{fontSize:12,color:T.ink50,lineHeight:1.7,margin:0}}>{s.text}</p>
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   WHAT'S REAL VS DEMO MODAL
   In-app explainer shown from More → Help → What's real vs demo.
   Purpose: testers who didn't carefully read the User Quick Start
   can get the same context in-app when questions arise mid-session.
   Mirrors the Build Guide v5 Part 1 taxonomy: ✅ working / 🧪 demo / ⏳ not built.
   ═══════════════════════════════════════════════════════════ */
function WhatsRealModal({ onClose }) {
  const sections = [
    {
      title: "✅ Fully working",
      color: T.leaf,
      sub: "These features use real logic and persist your data.",
      items: [
        "Smart Cart routing — including BOGO, BOGO-50%, Buy-2-Get-1-Free, and bundle triggers",
        "Cart quantity controls, swipe-to-delete, preview quantities",
        "All navigation, tabs, onboarding, and settings",
        "The rewards ledger (three-state pending/confirmed/expired)",
        "Per-order tracking IDs on every Send to Cart",
        "Error recovery — if something breaks, you see a friendly screen",
        "Your name, cart, and preferences persist across visits",
      ],
    },
    {
      title: "🧪 Working, but demo data",
      color: T.warn,
      sub: "The engine is real. The numbers you see are realistic but not live.",
      items: [
        "Deals and BOGOs — a curated set for Publix, ALDI, Sprouts, and The Fresh Market",
        "Price history (Price IQ) — synthetic historical data",
        "Loyalty points — fake point totals for demonstration",
        "Pantry default items — seed list to demonstrate the feature",
        "Ask Sage — replies are currently templated, not yet AI-generated",
      ],
    },
    {
      title: "⏳ Not built yet — coming soon",
      color: T.ink50,
      sub: "Planned, but not in this build.",
      items: [
        "Auto-loading your CartIQ list into Instacart's cart (pending Instacart approval)",
        "Actual rewards payouts (pending affiliate partnership approval)",
        "Live deal sync from real store circulars",
        "Real Google Places store discovery outside Tampa Bay",
        "Real receipt OCR (the Receipts tab is BETA demo only)",
        "Cross-device sync (your data lives only in this browser right now)",
      ],
    },
  ];
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:500,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.hairline}`}}>
        <div style={{padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest}}>🧪 What's real vs demo</div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"18px 22px 10px"}}>
          <p style={{fontSize:12,color:T.ink50,lineHeight:1.6,margin:"0 0 14px"}}>
            CartIQ is in preview mode during this test round. Here's exactly what's behind each part of the app so nothing feels mysterious.
          </p>
          {sections.map((s,i)=>(
            <div key={i} style={{marginBottom:18,paddingBottom:14,borderBottom:i<sections.length-1?`1px solid ${T.hairline}`:"none"}}>
              <div style={{fontSize:13,fontWeight:800,color:s.color,marginBottom:4}}>{s.title}</div>
              <div style={{fontSize:11,color:T.ink50,marginBottom:10,lineHeight:1.5}}>{s.sub}</div>
              <ul style={{margin:0,paddingLeft:18,color:T.ink50,fontSize:12,lineHeight:1.65}}>
                {s.items.map((item,j)=>(
                  <li key={j} style={{marginBottom:4}}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
          <div style={{background:T.sageSoft,border:"1px solid rgba(30,53,40,.12)",borderRadius:10,padding:"12px 14px",marginTop:8}}>
            <div style={{fontSize:11,color:T.leaf,lineHeight:1.6}}>
              <strong>Found something weird?</strong> Tap More → 📣 Send feedback. Even short notes help a lot.
            </div>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PANTRY ADD OWN ITEM MODAL (Phase 2b)
   ═══════════════════════════════════════════════════════════
   Lets users add arbitrary items to their pantry that aren't in the
   canonical catalog (grandma's spice blend, a specific store-brand
   product, etc.). Fork 4 Option C from the Phase 2 design: text entry
   with category selection for correct Pantry icon rendering.

   Stored pantry shape stays consistent with catalog-sourced entries:
     { id, name, category, qty, unit, low, _userAdded }
   The _userAdded marker lets the UI distinguish user-entered items
   from catalog items (no deal lookup, no price comparison).

   Props:
     - onSave(item): called with the new pantry entry
     - onClose: dismiss
   ═══════════════════════════════════════════════════════════ */
function PantryAddOwnItemModal({ onSave, onClose }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Pantry");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("");

  // Categories mirror the CAT_ICON map so icon rendering works automatically.
  // Order is what a user would likely pick for a misc item.
  const CATEGORIES = [
    { id: "Pantry",     label: "Pantry / Dry goods", icon: "🫙" },
    { id: "Produce",    label: "Produce",            icon: "🥦" },
    { id: "Dairy",      label: "Dairy",              icon: "🥛" },
    { id: "Meat",       label: "Meat",               icon: "🥩" },
    { id: "Seafood",    label: "Seafood",            icon: "🐟" },
    { id: "Eggs",       label: "Eggs",               icon: "🥚" },
    { id: "Bakery",     label: "Bakery",             icon: "🍞" },
    { id: "Frozen",     label: "Frozen",             icon: "❄️" },
    { id: "Beverages",  label: "Beverages",          icon: "💧" },
    { id: "Grains",     label: "Grains",             icon: "🌾" },
    { id: "Snacks",     label: "Snacks",             icon: "🍿" },
    { id: "Household",  label: "Household",          icon: "🧹" },
  ];

  const canSave = name.trim().length > 0;

  const handleSave = () => {
    if (!canSave) return;
    // Generate a stable ID. Prefix with "user_" so it can't collide with
    // catalog canonical IDs. Use a timestamp for uniqueness.
    const id = `user_${Date.now()}_${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`;
    onSave({
      id,
      name: name.trim(),
      category,
      qty: parseInt(qty, 10) || 1,
      unit: unit.trim() || "each",
      low: false,
      _userAdded: true,
    });
    onClose();
  };

  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"88vh",overflowY:"auto",borderRadius:22,border:`1px solid ${T.hairline}`}}>
        {/* Header */}
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest}}>🫙 Add your own item</div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
        </div>

        <div style={{padding:"18px 22px"}}>
          <div style={{fontSize:11,color:T.ink50,lineHeight:1.6,marginBottom:18}}>
            For items not in our catalog — a specific store brand, a homemade blend, or anything else you want to track in your pantry.
          </div>

          {/* Name input */}
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:6}}>Item name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Grandma's spice blend"
              autoFocus
              style={{width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,padding:"10px 12px",color:T.forest,fontSize:13,fontFamily:"inherit",outline:"none"}}
            />
          </div>

          {/* Quantity + unit row */}
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <div style={{flex:"0 0 80px"}}>
              <label style={{display:"block",fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:6}}>Qty</label>
              <input
                type="number"
                inputMode="numeric"
                value={qty}
                onChange={e => setQty(e.target.value)}
                min="1"
                style={{width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,padding:"10px 12px",color:T.forest,fontSize:13,fontFamily:"inherit",outline:"none",textAlign:"center"}}
              />
            </div>
            <div style={{flex:1}}>
              <label style={{display:"block",fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:6}}>Unit (optional)</label>
              <input
                type="text"
                value={unit}
                onChange={e => setUnit(e.target.value)}
                placeholder="e.g., jar, lb, pack"
                style={{width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,padding:"10px 12px",color:T.forest,fontSize:13,fontFamily:"inherit",outline:"none"}}
              />
            </div>
          </div>

          {/* Category selection */}
          <div style={{marginBottom:18}}>
            <label style={{display:"block",fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:8}}>Category</label>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:6}}>
              {CATEGORIES.map(c => {
                const isSelected = category === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    style={{
                      padding:"10px 6px",
                      background: isSelected ? T.sageSoft : T.parchment,
                      border: isSelected ? `1px solid ${T.sage}` : `1px solid ${T.hairline}`,
                      borderRadius:10,cursor:"pointer",fontFamily:"inherit",
                      display:"flex",flexDirection:"column",alignItems:"center",gap:3,
                      color: isSelected ? T.basil : T.ink50,
                      transition:"all .15s ease",
                    }}
                  >
                    <span style={{fontSize:18}}>{c.icon}</span>
                    <span style={{fontSize:9,fontWeight:700,letterSpacing:.2,textAlign:"center"}}>{c.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div style={{display:"flex",gap:8}}>
            <button
              onClick={onClose}
              style={{flex:1,padding:"11px",background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:12,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                flex:2,padding:"11px",
                background: canSave ? T.basil : T.berrySoft,
                border:"none",
                color: canSave ? "#fff" : T.ink50,
                borderRadius:12,fontSize:12,fontWeight:800,cursor:canSave?"pointer":"default",fontFamily:"inherit",
              }}
            >
              Add to Pantry
            </button>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CREATE LIST MODAL — name + occasion for new list records
   ═══════════════════════════════════════════════════════════ */
function CreateListModal({ existingNames = [], onSave, onClose }) {
  const [name, setName] = useState("");
  const [occasion, setOccasion] = useState("groceries");

  const OCCASIONS = [
    { id: "groceries", label: "Groceries", icon: "🧺" },
    { id: "snacks",    label: "Snacks",    icon: "🍿" },
    { id: "meal",      label: "Meal",      icon: "🍝" },
    { id: "event",     label: "Event",     icon: "🎉" },
  ];

  const trimmed = name.trim();
  const isDuplicate = trimmed.length > 0 && existingNames.some(n => n.toLowerCase() === trimmed.toLowerCase());
  const canSave = trimmed.length > 0 && !isDuplicate;

  const handleSave = () => {
    if (!canSave) return;
    onSave({ name: trimmed, occasion });
    onClose();
  };

  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"88vh",overflowY:"auto",borderRadius:22,border:`1px solid ${T.hairline}`}}>
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest}}>📋 New list</div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
        </div>

        <div style={{padding:"18px 22px"}}>
          <div style={{fontSize:11,color:T.ink50,lineHeight:1.6,marginBottom:18}}>
            Lists turn one-off shops into recurring missions — Taco Tuesday, Sunday meal prep, Halloween basket.
          </div>

          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:6}}>List name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Weekly groceries"
              autoFocus
              style={{width:"100%",background:T.parchment,border:`1px solid ${isDuplicate ? T.berry : T.hairline}`,borderRadius:10,padding:"10px 12px",color:T.forest,fontSize:13,fontFamily:"inherit",outline:"none"}}
            />
            {isDuplicate && (
              <div style={{fontSize:10,color:T.berry,marginTop:4,fontWeight:700}}>A list with this name already exists.</div>
            )}
          </div>

          <div style={{marginBottom:18}}>
            <label style={{display:"block",fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.6,textTransform:"uppercase",marginBottom:8}}>Occasion</label>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:6}}>
              {OCCASIONS.map(o => {
                const isSelected = occasion === o.id;
                return (
                  <button
                    key={o.id}
                    onClick={() => setOccasion(o.id)}
                    style={{
                      padding:"10px 6px",
                      background: isSelected ? T.sageSoft : T.parchment,
                      border: isSelected ? `1px solid ${T.sage}` : `1px solid ${T.hairline}`,
                      borderRadius:10,cursor:"pointer",fontFamily:"inherit",
                      display:"flex",flexDirection:"column",alignItems:"center",gap:3,
                      color: isSelected ? T.basil : T.ink50,
                      transition:"all .15s ease",
                    }}
                  >
                    <span style={{fontSize:18}}>{o.icon}</span>
                    <span style={{fontSize:9,fontWeight:700,letterSpacing:.2,textAlign:"center"}}>{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{display:"flex",gap:8}}>
            <button
              onClick={onClose}
              style={{flex:1,padding:"11px",background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:12,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                flex:2,padding:"11px",
                background: canSave ? T.basil : T.berrySoft,
                border:"none",
                color: canSave ? "#fff" : T.ink50,
                borderRadius:12,fontSize:12,fontWeight:800,cursor:canSave?"pointer":"default",fontFamily:"inherit",
              }}
            >
              Create list
            </button>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ADD-ITEMS-TO-LIST MODAL — multi-select from My Items catalog
   ═══════════════════════════════════════════════════════════ */
function AddItemsToListModal({ list, candidates, onConfirm, onClose }) {
  // Local selection — committed only on Save.
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(c => `${c.name} ${c.category || ""}`.toLowerCase().includes(q));
  }, [candidates, query]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const canSave = selected.size > 0;

  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"88vh",display:"flex",flexDirection:"column",borderRadius:22,border:`1px solid ${T.hairline}`}}>
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{minWidth:0}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>📋 Add to {list.name}</div>
            <div style={{fontSize:11,color:T.ink50,marginTop:2}}>Pick items from your catalog</div>
          </div>
          <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
        </div>

        <div style={{padding:"14px 22px 0"}}>
          <div style={{display:"flex",alignItems:"center",gap:9,padding:"9px 12px",background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:12,boxShadow:T.shadowCard,marginBottom:10}}>
            <Icon name="search" size={16} color={T.ink50} stroke={2.2}/>
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search items…"
              autoFocus
              style={{flex:1,minWidth:0,background:"transparent",border:"none",outline:"none",fontFamily:"inherit",fontSize:13,color:T.forest}}
            />
          </div>
        </div>

        <div style={{padding:"4px 22px 14px",overflowY:"auto",flex:1}}>
          {filtered.length === 0 ? (
            <div style={{padding:"28px 12px",textAlign:"center",color:T.ink50,fontSize:12.5}}>
              {candidates.length === 0
                ? "No items in your catalog yet — add to your pantry first."
                : "Nothing matches that search."}
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {filtered.map(item => {
                const isSel = selected.has(item.id);
                return (
                  <button key={item.id}
                    onClick={() => toggle(item.id)}
                    style={{
                      display:"flex",alignItems:"center",gap:10,
                      padding:"10px 12px",
                      background: isSel ? T.sageSoft : T.parchment,
                      border:`1px solid ${isSel ? T.sage : T.hairline}`,
                      borderRadius:10,
                      cursor:"pointer", fontFamily:"inherit",
                      textAlign:"left", width:"100%",
                      transition:"all .15s",
                    }}>
                    <div style={{width:20,height:20,borderRadius:6,border:`1.5px solid ${isSel ? T.basil : T.ink30}`,background: isSel ? T.basil : "transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {isSel && <Icon name="check" size={12} color={T.paper} stroke={3}/>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.forest,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                      {item.category && <div style={{fontSize:10.5,color:T.ink50,fontWeight:600,marginTop:2}}>{item.category}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{padding:"14px 22px",borderTop:`1px solid ${T.hairline}`,display:"flex",gap:8,alignItems:"center"}}>
          <div style={{flex:1,fontSize:11,color:T.ink50,fontWeight:700}}>
            {selected.size} selected
          </div>
          <button
            onClick={onClose}
            style={{padding:"11px 16px",background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:12,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}
          >
            Cancel
          </button>
          <button
            onClick={() => { if (canSave) { onConfirm([...selected]); onClose(); } }}
            disabled={!canSave}
            style={{
              padding:"11px 18px",
              background: canSave ? T.basil : T.berrySoft,
              border:"none",
              color: canSave ? "#fff" : T.ink50,
              borderRadius:12,fontSize:12,fontWeight:800,cursor:canSave?"pointer":"default",fontFamily:"inherit",
            }}
          >
            Add{selected.size > 0 ? ` ${selected.size}` : ""}
          </button>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STAPLES PICKER MODAL (Phase A)
   ═══════════════════════════════════════════════════════════
   Onboarding step that runs after store selection. Asks the user
   which 25 staple items they typically buy. Top 8 start checked.

   On save, the parent:
     1) Persists the chosen ID list to cartiq_staples
     2) Seeds Pantry with chosen items at their default qty/unit
     3) Marks each chosen item in cartMap for the user's first
        active store (Smart Cart routing handles store choice)
     4) Sets cartiq_staples_seen so the modal never reopens

   Skipping the modal is a first-class action — `onSave([])` is
   how skip is signaled. We still set the seen flag so we don't
   keep nagging.

   Props:
     - userName  : string, used in greeting
     - onSave    : function(ids[]) — array of selected item IDs
     - onSkip    : function() — called when user taps Skip
     - onClose   : function() — same as Skip but from the X
   ═══════════════════════════════════════════════════════════ */
function StaplesPickerModal({ userName, onSave, onSkip, onClose }) {
  const [selected, setSelected] = useState(() => {
    const init = new Set();
    STAPLES_PICKER_ITEMS
      .filter(item => item.defaultOn)
      .slice(0, STAPLES_DEFAULT_CHECKED_COUNT)
      .forEach(item => init.add(item.id));
    return init;
  });

  const groupedItems = useMemo(() => {
    const byCat = {};
    STAPLES_PICKER_ITEMS.forEach(item => {
      if (!byCat[item.category]) byCat[item.category] = [];
      byCat[item.category].push(item);
    });
    const ordered = [];
    STAPLES_CATEGORY_ORDER.forEach(cat => {
      if (byCat[cat]) ordered.push({ category: cat, items: byCat[cat] });
    });
    Object.keys(byCat).forEach(cat => {
      if (!STAPLES_CATEGORY_ORDER.includes(cat)) ordered.push({ category: cat, items: byCat[cat] });
    });
    return ordered;
  }, []);

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    SFX.tabSwitch();
  }

  function handleSave() {
    SFX.success();
    onSave(Array.from(selected));
  }

  function handleSkip() {
    SFX.tabSwitch();
    onSkip();
  }

  const firstName = userName ? userName.split(/\s+/)[0] : null;

  return (
    <div style={{position:"fixed",inset:0,background:`${T.forest}99`,zIndex:850,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}}
         onClick={e => e.target === e.currentTarget && onClose && onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:T.shadowFloat,animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <CartIQMark size={36} mood="excited"/>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.paper}}>
                {firstName ? `Last step, ${firstName}` : "Last step"}
              </div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>
                What do you usually buy? Tap to add.
              </div>
            </div>
            {onClose && (
              <button onClick={onClose}
                aria-label="Close staples picker"
                style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        </div>

        {/* Body — category-grouped chips */}
        <div style={{padding:"16px 18px"}}>
          <div style={{padding:"12px 14px",marginBottom:14,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,fontSize:11,color:T.ink50,lineHeight:1.6}}>
            💡 We've checked the most common items to get you started. Tap to add or remove — this seeds your Pantry and Smart Cart with real items, not demo data.
          </div>

          {groupedItems.map(({category, items}) => (
            <div key={category} style={{marginBottom:14}}>
              <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:700,marginBottom:6,textTransform:"uppercase"}}>
                {category}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {items.map(item => {
                  const isOn = selected.has(item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggle(item.id)}
                      aria-pressed={isOn}
                      style={{
                        background: isOn ? T.sageSoft : T.parchment,
                        border: `1px solid ${isOn ? T.sage : T.hairline}`,
                        color: isOn ? T.leaf : T.ink50,
                        borderRadius: 999,
                        padding: "7px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: "inherit",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        boxShadow: isOn ? T.shadowSoft : "none",
                        transition: "all .15s ease"
                      }}
                    >
                      <span style={{fontSize:13}}>{item.icon}</span>
                      <span>{item.name}</span>
                      {isOn && <span style={{fontSize:10,marginLeft:2,color:T.basil}}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Action row */}
          <div style={{display:"flex",gap:8,marginTop:16}}>
            <RippleBtn sound="tabSwitch" onClick={handleSkip}
              style={{flex:1,background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:12,padding:"13px",fontSize:13,fontWeight:700}}>
              Skip — I'll add later
            </RippleBtn>
            <RippleBtn sound="success" onClick={handleSave}
              style={{flex:2,background:T.forest,border:"none",color:T.paper,borderRadius:12,padding:"13px",fontSize:13,fontWeight:800,boxShadow:T.shadowFloat}}>
              {selected.size === 0 ? "Save (none selected)" : `Add ${selected.size} item${selected.size === 1 ? "" : "s"} →`}
            </RippleBtn>
          </div>

          <div style={{marginTop:12,fontSize:10,color:T.ink50,textAlign:"center",lineHeight:1.5}}>
            You can edit your staples anytime in More → Settings.
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LOCATION SETUP MODAL — ZIP picker + nearby store selector
   ═══════════════════════════════════════════════════════════ */
function LocationSetupModal({ initialZip, initialName, onSave, onSaveName, onClose }) {
  // If we don't have a name yet, start on the "name" step; otherwise on "zip" (or "results" if ZIP too)
  const [step, setStep] = useState(
    !initialName ? "name" :
    initialZip ? "results" : "zip"
  );
  const [name, setName] = useState(initialName || "");
  const [zip, setZip] = useState(initialZip || "");
  const [stores, setStores] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);
  const [geo, setGeo] = useState(null);

  function submitName() {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (onSaveName) onSaveName(trimmed);
    setStep("zip");
    SFX.success();
  }

  async function searchZip() {
    if (!/^\d{5}$/.test(zip)) {
      setError("Please enter a valid 5-digit ZIP code");
      return;
    }
    setError(null);
    setStep("loading");
    SFX.tabSwitch();

    if (!hasGoogleKey()) {
      // Demo mode — return the existing static stores so the flow still works
      setTimeout(() => {
        const demoStores = STORES.map(s => ({
          placeId: s.placeId, name: s.name, chainId: s.id, chainName: s.name,
          address: "Demo location · add Google Places key for real data",
          rating: null, reviewCount: 0, distance: Math.random() * 10 + 1, openNow: null,
        }));
        setStores(demoStores);
        setSource("demo");
        setStep("results");
      }, 600);
      return;
    }

    try {
      const result = await findStoresForZip(zip);
      if (!result.stores.length) {
        setError("No stores found near this ZIP. Try a nearby ZIP code.");
        setStep("error");
        return;
      }
      setStores(result.stores);
      setSource(result.source);
      setGeo(result.geo);
      // Pre-select stores that match the existing CartIQ catalog
      const preselect = new Set(result.stores.filter(s => STORES.find(cs => cs.id === s.chainId)).map(s => s.placeId || s.chainId));
      setSelected(preselect);
      setStep("results");
      SFX.success();
    } catch(e) {
      setError(`Couldn't search this ZIP: ${e.message}`);
      setStep("error");
    }
  }

  function toggleStore(s) {
    const key = s.placeId || s.chainId;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    SFX.tabSwitch();
  }

  function handleSave() {
    const picked = stores.filter(s => selected.has(s.placeId || s.chainId));
    onSave({ zip, stores: picked, source, geo });
    SFX.success();
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:850,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&onClose&&onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <CartIQMark size={36} mood="excited"/>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.paper}}>
                {step==="name" ? "Welcome to CartIQ" :
                 step==="zip" ? (name ? `Hey ${name.split(/\s+/)[0]} — where do you shop?` : "Where do you shop?") :
                 step==="loading" ? "Finding your stores..." :
                 step==="results" ? "Pick your stores" :
                 "Hmm, no luck"}
              </div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>
                {step==="name" ? "First — what should we call you?" :
                 step==="zip" ? "Enter your ZIP and we'll find groceries near you" :
                 step==="loading" ? "Scanning Google Places..." :
                 step==="results" ? `${stores.length} stores within 15 miles` :
                 "Let's try again"}
              </div>
            </div>
            {onClose && <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>✕</button>}
          </div>
        </div>

        <div style={{padding:"20px 22px"}}>

          {/* ─── Name entry (new) ─── */}
          {step === "name" && (
            <>
              <input
                type="text"
                value={name}
                onChange={e=>setName(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&name.trim()&&submitName()}
                placeholder="What's your name?"
                autoFocus
                maxLength={40}
                style={{width:"100%",background:T.parchment,border:`1px solid ${T.sage}`,borderRadius:12,color:T.forest,padding:"14px 18px",fontSize:18,fontFamily:FONT_SANS,outline:"none",textAlign:"center",fontWeight:600,boxSizing:"border-box"}}
              />
              <RippleBtn sound="success" onClick={submitName} disabled={!name.trim()}
                style={{width:"100%",marginTop:12,background:name.trim()?`${T.forest}`:T.parchment,border:"none",color:name.trim()?T.paper:T.ink50,borderRadius:12,padding:"13px",fontSize:13,fontWeight:800,boxShadow:name.trim()?"0 0 20px rgba(30,53,40,.20)":"none"}}>
                Continue →
              </RippleBtn>
              <div style={{marginTop:18,padding:"12px 14px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,fontSize:11,color:T.ink50,lineHeight:1.6}}>
                👋 We'll use your name to personalize your dashboard. Stored only on this device.
              </div>
            </>
          )}

          {/* ─── ZIP entry ─── */}
          {step === "zip" && (
            <>
              <div style={{display:"flex",gap:8}}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  value={zip}
                  onChange={e=>setZip(e.target.value.replace(/\D/g,""))}
                  onKeyDown={e=>e.key==="Enter"&&searchZip()}
                  placeholder="33602"
                  autoFocus
                  style={{flex:1,background:T.parchment,border:`1px solid ${T.sage}`,borderRadius:12,color:T.forest,padding:"14px 18px",fontSize:18,fontFamily:FONT_SANS,outline:"none",letterSpacing:4,textAlign:"center",fontWeight:700}}
                />
                <RippleBtn sound="success" onClick={searchZip} disabled={zip.length!==5}
                  style={{background:zip.length===5?`${T.forest}`:T.parchment,border:"none",color:zip.length===5?T.paper:T.ink50,borderRadius:12,padding:"0 22px",fontSize:13,fontWeight:800,boxShadow:zip.length===5?"0 0 20px rgba(30,53,40,.20)":"none"}}>
                  Search →
                </RippleBtn>
              </div>
              {error && <div style={{marginTop:10,fontSize:11,color:T.berry,textAlign:"center"}}>{error}</div>}
              <div style={{marginTop:18,padding:"12px 14px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,fontSize:11,color:T.ink50,lineHeight:1.6}}>
                💡 We'll show you every grocery chain that delivers to your area — Publix, ALDI, Sprouts, Whole Foods, Trader Joe's, Target, and more. You pick which ones to monitor for deals.
              </div>
              <div style={{marginTop:10,fontSize:10,color:T.ink50,textAlign:"center"}}>
                Your ZIP is stored only on this device. CartIQ doesn't share your location.
              </div>
            </>
          )}

          {/* ─── Loading ─── */}
          {step === "loading" && (
            <div style={{textAlign:"center",padding:"40px 20px"}}>
              <div style={{display:"inline-block",animation:"spin 1.2s linear infinite",fontSize:42,marginBottom:16}}>🛒</div>
              <div style={{fontSize:13,color:T.ink50}}>Looking up groceries near {zip}...</div>
            </div>
          )}

          {/* ─── Results ─── */}
          {step === "results" && (
            <>
              {source === "demo" && (
                <div style={{padding:"8px 12px",marginBottom:12,background:T.butterSoft,border:"1px solid rgba(251,191,36,.2)",borderRadius:10,fontSize:10,color:T.warn}}>
                  ⚠️ Demo mode — add a Google Places API key to see real stores near you
                </div>
              )}
              {source === "instacart" && (
                <div style={{padding:"8px 12px",marginBottom:12,background:T.sageSoft,border:"1px solid rgba(74,222,128,.2)",borderRadius:10,fontSize:10,color:T.basil}}>
                  ✓ Live Instacart data — these stores deliver to your ZIP
                </div>
              )}
              <div style={{maxHeight:"42vh",overflowY:"auto",overscrollBehavior:"contain",marginBottom:14,paddingRight:4}}>
                {stores.map((s,i) => {
                  const key = s.placeId || s.chainId;
                  const isSelected = selected.has(key);
                  return (
                    <button key={i} onClick={()=>toggleStore(s)}
                      style={{width:"100%",display:"flex",alignItems:"center",gap:11,padding:"11px 13px",marginBottom:8,background:isSelected?T.sageSoft:T.parchment,border:`1.5px solid ${isSelected?T.basil:T.parchment}`,borderRadius:13,cursor:"pointer",fontFamily:"inherit",textAlign:"left",transition:"all .15s"}}>
                      <div style={{width:36,height:36,borderRadius:10,background:isSelected?`${T.forest}`:T.parchment,color:isSelected?T.paper:T.forest,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                        {isSelected ? "✓" : "🏪"}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.chainName}</div>
                        <div style={{fontSize:10,color:T.ink50,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:1}}>{s.address}</div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:3,flexWrap:"wrap"}}>
                          {s.distance != null && <span style={{fontSize:10,color:T.leaf,fontWeight:600}}>📍 {s.distance.toFixed(1)} mi</span>}
                          {s.rating != null && (
                            <span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                              <StarRating rating={s.rating} size={9}/>
                              <span style={{fontSize:10,color:T.ink50}}>({s.reviewCount})</span>
                            </span>
                          )}
                          {s.openNow != null && (
                            <span style={{fontSize:10,fontWeight:700,color:s.openNow?T.leaf:T.berry}}>
                              {s.openNow?"● OPEN":"● CLOSED"}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${T.hairline}`}}>
                <div style={{fontSize:11,color:T.ink50}}>
                  <strong style={{color:T.basil}}>{selected.size}</strong> selected
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setStep("zip")}
                    style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"9px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    ← Change ZIP
                  </button>
                  <RippleBtn sound="success" onClick={handleSave} disabled={selected.size===0}
                    style={{background:selected.size>0?`${T.forest}`:T.parchment,border:"none",color:selected.size>0?T.paper:T.ink50,borderRadius:10,padding:"9px 18px",fontSize:11,fontWeight:800,boxShadow:selected.size>0?"0 0 18px rgba(30,53,40,.18)":"none"}}>
                    Save & Continue →
                  </RippleBtn>
                </div>
              </div>
            </>
          )}

          {/* ─── Error ─── */}
          {step === "error" && (
            <div style={{textAlign:"center",padding:"30px 20px"}}>
              <div style={{fontSize:42,marginBottom:14}}>🗺️</div>
              <div style={{fontSize:13,color:T.ink50,marginBottom:18}}>{error}</div>
              <RippleBtn sound="notify" onClick={()=>{setError(null);setStep("zip");}}
                style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"10px 22px",fontSize:12,fontWeight:800}}>
                Try Again
              </RippleBtn>
            </div>
          )}
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DIETARY SETUP MODAL — onboarding + settings for food preferences
   ═══════════════════════════════════════════════════════════ */
function DietarySetupModal({ initial, onSave, onClose, isOnboarding = false }) {
  const [selected, setSelected] = useState(new Set(initial || []));

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    SFX.tabSwitch();
  }

  function handleSave() {
    onSave(Array.from(selected));
    SFX.success();
  }

  function handleSkip() {
    onSave([]);  // clear all
    SFX.tabSwitch();
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:855,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&!isOnboarding&&onClose&&onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <CartIQMark size={36} mood="happy"/>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.paper}}>
                {isOnboarding ? "What do you eat?" : "Dietary preferences"}
              </div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>
                {isOnboarding
                  ? "Skip the deals that don't fit your diet"
                  : "Update what CartIQ hides from deal lists"}
              </div>
            </div>
            {!isOnboarding && onClose && (
              <button onClick={onClose}
                aria-label="Close dietary settings"
                style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        </div>

        <div style={{padding:"20px 22px"}}>
          <div style={{padding:"12px 14px",marginBottom:14,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,fontSize:11,color:T.ink50,lineHeight:1.6}}>
            💡 Select any that apply. CartIQ will hide matching items from deal lists, the BOGOs grid, Smart Cart, and substitutions. You can change this anytime from More → Help.
          </div>

          <div style={{display:"grid",gap:8,marginBottom:16}}>
            {DIETARY_RESTRICTIONS.map(r => {
              const isActive = selected.has(r.id);
              return (
                <button key={r.id} onClick={()=>toggle(r.id)}
                  aria-pressed={isActive}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    background: isActive ? T.sageSoft : T.parchment,
                    border: `1.5px solid ${isActive ? T.sage : T.parchment}`,
                    borderRadius: 14,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    transition: "all .15s",
                  }}>
                  <div style={{width:40,height:40,borderRadius:11,background:isActive?`${T.forest}`:T.parchment,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                    <span aria-hidden="true">{isActive ? "✓" : r.icon}</span>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:isActive?T.leaf:T.forest}}>{r.label}</div>
                    <div style={{fontSize:10,color:T.ink50,marginTop:2,lineHeight:1.4}}>{r.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${T.hairline}`}}>
            <div style={{fontSize:11,color:T.ink50}}>
              {selected.size === 0 ? "No restrictions" : <><strong style={{color:T.basil}}>{selected.size}</strong> active</>}
            </div>
            <div style={{display:"flex",gap:8}}>
              {isOnboarding && (
                <button onClick={handleSkip}
                  style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"9px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  Skip
                </button>
              )}
              <RippleBtn sound="success" onClick={handleSave}
                style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"9px 20px",fontSize:11,fontWeight:800,boxShadow:T.shadowFloat}}>
                {isOnboarding ? "Get started →" : "Save"}
              </RippleBtn>
            </div>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HEALTH GOALS MODAL — onboarding + settings for Nutrition IQ
   ─────────────────────────────────────────────────────────────
   Pills source from the canonical HEALTH_GOALS constant so this
   modal stays in sync with NutritionRollup. Copy is goal-language
   only ("Watching sodium", "Protein-focused") — FDA nutrient-
   content claim terms ("Low Sodium", "High Protein") are
   regulated and never used here.
   ═══════════════════════════════════════════════════════════ */
function HealthGoalsModal({ initial, onSave, onClose, isOnboarding = false }) {
  const [selected, setSelected] = useState(new Set(initial || []));

  // Local icon + description map — HEALTH_GOALS only carries id/label/field
  // metadata. Keeping presentation data adjacent to the modal so adding a
  // new goal doesn't force unrelated lookups across the file.
  const GOAL_META = {
    sodium:  { icon: "🧂", desc: "Filter highlights low-sodium picks in your cart" },
    protein: { icon: "💪", desc: "Surface protein-rich items in your rollup" },
    sugar:   { icon: "🍬", desc: "Flag added-sugar items as you shop" },
    fiber:   { icon: "🌾", desc: "Surface fiber-rich items in your rollup" },
  };

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    SFX.tabSwitch();
  }

  function handleSave() {
    onSave(Array.from(selected));
    SFX.success();
  }

  function handleSkip() {
    onSave([]);  // clear all
    SFX.tabSwitch();
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:855,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&!isOnboarding&&onClose&&onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <CartIQMark size={36} mood="happy"/>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.paper}}>
                {isOnboarding ? "Anything you're paying attention to?" : "Update your health goals"}
              </div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>
                {isOnboarding
                  ? "We'll surface this in your Smart Cart rollup"
                  : "Change what Nutrition IQ tracks for you"}
              </div>
            </div>
            {!isOnboarding && onClose && (
              <button onClick={onClose}
                aria-label="Close health goals"
                style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        </div>

        <div style={{padding:"20px 22px"}}>
          <div style={{padding:"12px 14px",marginBottom:14,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,fontSize:11,color:T.ink50,lineHeight:1.6}}>
            💡 Pick any that fit your eating style. We use goal-language, not medical claims — change this anytime from More → Health goals.
          </div>

          <div style={{display:"grid",gap:8,marginBottom:16}}>
            {HEALTH_GOALS.map(g => {
              const isActive = selected.has(g.id);
              const meta = GOAL_META[g.id] || { icon: "🌿", desc: "" };
              return (
                <button key={g.id} onClick={()=>toggle(g.id)}
                  aria-pressed={isActive}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    background: isActive ? T.sageSoft : T.parchment,
                    border: `1.5px solid ${isActive ? T.sage : T.parchment}`,
                    borderRadius: 14,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    transition: "all .15s",
                  }}>
                  <div style={{width:40,height:40,borderRadius:11,background:isActive?`${T.forest}`:T.parchment,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                    <span aria-hidden="true">{isActive ? "✓" : meta.icon}</span>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:isActive?T.leaf:T.forest}}>{g.label}</div>
                    <div style={{fontSize:10,color:T.ink50,marginTop:2,lineHeight:1.4}}>{meta.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${T.hairline}`}}>
            <div style={{fontSize:11,color:T.ink50}}>
              {selected.size === 0 ? "No goals" : <><strong style={{color:T.basil}}>{selected.size}</strong> active</>}
            </div>
            <div style={{display:"flex",gap:8}}>
              {isOnboarding && (
                <button onClick={handleSkip}
                  style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"9px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  Skip
                </button>
              )}
              <RippleBtn sound="success" onClick={handleSave}
                style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"9px 20px",fontSize:11,fontWeight:800,boxShadow:T.shadowFloat}}>
                {isOnboarding ? "Continue →" : "Save"}
              </RippleBtn>
            </div>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROJECTED SAVINGS MODAL — first-run value reveal
   ─────────────────────────────────────────────────────────────
   Fires once at the end of onboarding (after Dietary) using the
   user's staples + active stores. Shows the dollar figure from
   rebuildCartFromStaples so the dashboard isn't the first thing
   they see — the value prop is.

   `preview` is the rebuildPreview memo from the parent. If
   estimatedSavings < $5, swap to a routing-coverage message so we
   never advertise a $0 number (feels worse than no number).
   ═══════════════════════════════════════════════════════════ */
function ProjectedSavingsModal({ preview, userName, onOpenSmartCart, onClose }) {
  const savings = preview?.estimatedSavings || 0;
  const itemCount = preview?.itemCount || 0;
  const storeCount = preview?.storeBreakdown ? Object.keys(preview.storeBreakdown).length : 0;
  const showFallback = savings < 5;
  const firstName = (userName || "").trim().split(" ")[0];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:855,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <CartIQMark size={36} mood="celebrating"/>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.paper}}>
                {firstName ? `Nice, ${firstName}` : "You're all set"}
              </div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>
                Here's what your usual cart looks like today
              </div>
            </div>
          </div>
        </div>

        <div style={{padding:"22px 22px 20px"}}>

          {/* Hero — projected savings number, or routing coverage fallback */}
          <div style={{textAlign:"center",padding:"18px 14px 22px",background:`linear-gradient(110deg,${T.sageSoft},${T.sage}4D)`,border:`1px solid ${T.sage}`,borderRadius:16,marginBottom:16}}>
            {showFallback ? (
              <>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:32,fontWeight:700,color:T.forest,letterSpacing:-1,lineHeight:1.1}}>
                  Your cart is ready
                </div>
                <div style={{fontSize:12,color:T.ink50,marginTop:8,lineHeight:1.5}}>
                  {itemCount > 0
                    ? <><strong style={{color:T.forest}}>{itemCount}</strong> item{itemCount===1?"":"s"} routed across <strong style={{color:T.forest}}>{storeCount}</strong> store{storeCount===1?"":"s"} for the best price</>
                    : <>Open Smart Cart to see your routed list</>}
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:10,letterSpacing:1.5,color:T.basil,fontWeight:800,textTransform:"uppercase",marginBottom:6}}>
                  Projected savings
                </div>
                <AnimNum v={savings} pre="$" dec={2} size={56} color={T.forest}/>
                <div style={{fontSize:12,color:T.ink50,marginTop:10,lineHeight:1.5}}>
                  vs buying the same <strong style={{color:T.forest}}>{itemCount}</strong> item{itemCount===1?"":"s"} at one store
                </div>
              </>
            )}
          </div>

          {/* Per-store breakdown — only show when we have routing */}
          {storeCount > 0 && (
            <div style={{padding:"12px 14px",marginBottom:16,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,fontSize:11,color:T.ink50,lineHeight:1.7}}>
              <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,textTransform:"uppercase",marginBottom:6}}>
                Your route
              </div>
              {Object.entries(preview.storeBreakdown).map(([sid, count]) => {
                const store = STORES.find(s => s.id === sid);
                return (
                  <div key={sid} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:T.forest}}>
                    <span style={{fontWeight:700}}>{store?.name || sid}</span>
                    <span style={{color:T.ink50}}>{count} item{count===1?"":"s"}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${T.hairline}`}}>
            <button onClick={()=>{ onClose(); SFX.tabSwitch(); }}
              style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"9px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              Maybe later
            </button>
            <RippleBtn sound="success" onClick={onOpenSmartCart}
              style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"9px 20px",fontSize:11,fontWeight:800,boxShadow:T.shadowFloat}}>
              Open Smart Cart →
            </RippleBtn>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHARE MY SAVINGS — annual/all-time + canvas export
   ═══════════════════════════════════════════════════════════
   Renders a polished savings card (1080x1350 PNG) via canvas
   for download or native share. The amount + period are passed
   in from the Dashboard hero (whichever segment is selected).
   "Include my name" defaults off so no PII goes out without
   explicit opt-in. When AFFILIATE_LIVE is false and txnHistory
   has any seeded entries, a DEMO tag is painted onto the card
   so users can't accidentally share inflated demo numbers.
   ═══════════════════════════════════════════════════════════ */
function ShareSavingsModal({ amount, period, userName, isDemo, onClose }) {
  const canvasRef = useRef(null);
  const [includeName, setIncludeName] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [canShare, setCanShare] = useState(false);

  const firstName = userName ? userName.split(/\s+/)[0] : "";
  const periodLabel = period === "month" ? "this month" : period === "year" ? "this year" : "all time";
  const amtStr = `$${amount.toFixed(2)}`;

  useEffect(() => {
    if (navigator.canShare && typeof navigator.share === "function") {
      try {
        const testFile = new File([""], "test.png", { type: "image/png" });
        setCanShare(navigator.canShare({ files: [testFile] }));
      } catch { setCanShare(false); }
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1080, H = 1350;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, T.sageSoft);
    grad.addColorStop(0.55, T.cream);
    grad.addColorStop(1, T.parchment);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = T.sage;
    ctx.lineWidth = 6;
    ctx.strokeRect(40, 40, W - 80, H - 80);

    ctx.textAlign = "center";

    ctx.fillStyle = T.forest;
    ctx.font = "700 72px Newsreader, Georgia, serif";
    ctx.fillText("CartIQ", W / 2, 200);

    ctx.fillStyle = T.basil;
    ctx.font = "800 36px Geist, sans-serif";
    ctx.fillText(`SAVED ${periodLabel.toUpperCase()}`, W / 2, H / 2 - 140);

    ctx.fillStyle = T.forest;
    ctx.font = "700 240px Newsreader, Georgia, serif";
    ctx.fillText(amtStr, W / 2, H / 2 + 60);

    if (includeName && firstName) {
      ctx.fillStyle = T.basil;
      ctx.font = "600 44px Geist, sans-serif";
      ctx.fillText(`${firstName}'s grocery savings`, W / 2, H / 2 + 160);
    }

    ctx.fillStyle = T.ink50;
    ctx.font = "700 32px Geist, sans-serif";
    ctx.fillText("cartiq.app", W / 2, H - 120);

    if (isDemo) {
      ctx.save();
      ctx.translate(W - 200, 200);
      ctx.rotate(0.25);
      ctx.fillStyle = T.warn;
      ctx.font = "800 48px Geist, sans-serif";
      ctx.fillText("DEMO", 0, 0);
      ctx.restore();
    }

    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    }, "image/png");
  }, [amount, period, includeName, firstName, isDemo, periodLabel, amtStr]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cartiq-savings-${period}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    }, "image/png");
  };

  const handleShare = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async blob => {
      if (!blob) return;
      const file = new File([blob], `cartiq-savings-${period}.png`, { type: "image/png" });
      try {
        await navigator.share({
          files: [file],
          title: `Saved ${periodLabel} on CartIQ`,
          text: `I've saved ${amtStr} ${periodLabel} on CartIQ.`,
        });
      } catch { /* user cancelled or share failed — silent */ }
    }, "image/png");
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:200,background:`${T.ink}99`,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:T.cream,borderRadius:20,padding:20,maxWidth:420,width:"100%",boxShadow:T.shadowFloat,maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.forest}}>Share your savings</div>
          <button onClick={onClose} aria-label="Close"
            style={{background:"none",border:"none",cursor:"pointer",padding:4,fontFamily:"inherit"}}>
            <Icon name="x" size={20} color={T.ink50} stroke={2.2}/>
          </button>
        </div>
        <canvas ref={canvasRef} style={{display:"none"}}/>
        {previewUrl && (
          <div style={{borderRadius:14,overflow:"hidden",border:`1px solid ${T.sage}`,marginBottom:14,background:T.parchment}}>
            <img src={previewUrl} alt="Share preview" style={{display:"block",width:"100%",height:"auto"}}/>
          </div>
        )}
        <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,marginBottom:14,cursor:"pointer"}}>
          <input type="checkbox" checked={includeName} onChange={e=>setIncludeName(e.target.checked)} style={{accentColor:T.basil}}/>
          <span style={{fontSize:12,color:T.forest,fontWeight:600}}>Include my name{firstName ? ` (${firstName})` : ""}</span>
        </label>
        <div style={{display:"flex",gap:8}}>
          <RippleBtn sound="success" onClick={handleDownload}
            style={{flex:1,background:T.forest,border:`1px solid ${T.forest}`,color:T.paper,borderRadius:12,padding:"11px",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <Icon name="download" size={14} color={T.paper} stroke={2.2}/>
            Save image
          </RippleBtn>
          {canShare && (
            <RippleBtn sound="success" onClick={handleShare}
              style={{flex:1,background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:12,padding:"11px",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              <Icon name="share" size={14} color={T.basil} stroke={2.2}/>
              Share…
            </RippleBtn>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHOP PLANNER MODAL — schedule next shop & filter by validity
   ═══════════════════════════════════════════════════════════ */
function ShopPlannerModal({ initial, onSave, onClose }) {
  // Day options: today through next 14 days
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initial?.date) return initial.date.slice(0, 10);
    return new Date().toISOString().slice(0, 10);
  });
  const [selectedTime, setSelectedTime] = useState(() => {
    if (initial?.date) {
      const d = new Date(initial.date);
      return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
    }
    return "09:00";
  });

  const days = useMemo(() => {
    const out = [];
    const today = new Date();
    today.setHours(0,0,0,0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      out.push({
        iso: d.toISOString().slice(0, 10),
        day: d.toLocaleDateString("en-US", { weekday: "short" }),
        date: d.getDate(),
        month: d.toLocaleDateString("en-US", { month: "short" }),
        isToday: i === 0,
        isTomorrow: i === 1,
      });
    }
    return out;
  }, []);

  const TIME_PRESETS = [
    { id: "07:00", label: "Early", icon: "🌅" },
    { id: "09:00", label: "Morning", icon: "☀️" },
    { id: "12:00", label: "Midday", icon: "🌤️" },
    { id: "16:00", label: "Afternoon", icon: "🌇" },
    { id: "19:00", label: "Evening", icon: "🌆" },
  ];

  function handleSave() {
    const [h, m] = selectedTime.split(":").map(Number);
    const fullDate = new Date(selectedDate);
    fullDate.setHours(h, m, 0, 0);
    const dayObj = days.find(d => d.iso === selectedDate);
    const label = `${dayObj?.day || ""} ${selectedTime}`;
    onSave({ date: fullDate.toISOString(), label, dayLabel: dayObj?.day || "", time: selectedTime });
    SFX.success();
  }

  function handleClear() {
    onSave(null);
    SFX.tabSwitch();
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:856,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&onClose&&onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:38,height:38,borderRadius:11,background:T.parchment,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}} aria-hidden="true">📅</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest}}>When are you shopping?</div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>CartIQ filters deals to ones still valid then</div>
            </div>
            {onClose && (
              <button onClick={onClose} aria-label="Close planner"
                style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        </div>

        <div style={{padding:"20px 22px"}}>
          {/* Day picker — horizontal scrollable */}
          <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,marginBottom:8,textTransform:"uppercase"}}>Pick a day</div>
          <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:16,scrollbarWidth:"none",msOverflowStyle:"none"}}>
            {days.map(d => {
              const selected = selectedDate === d.iso;
              return (
                <button key={d.iso} onClick={()=>{setSelectedDate(d.iso);SFX.tabSwitch();}}
                  aria-pressed={selected}
                  style={{
                    background: selected ? `${T.forest}` : T.parchment,
                    border: `1px solid ${selected ? T.sage : T.ink15}`,
                    color: selected ? "#fff" : T.ink50,
                    borderRadius: 12,
                    padding: "10px 12px",
                    minWidth: 60,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    boxShadow: selected ? "0 0 14px rgba(30,53,40,.15)" : "none",
                  }}>
                  <span style={{fontSize:10,letterSpacing:1,opacity:.7,fontWeight:700}}>{d.isToday?"TODAY":d.isTomorrow?"TOMORROW":d.day.toUpperCase()}</span>
                  <span style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700}}>{d.date}</span>
                  <span style={{fontSize:10,opacity:.6,letterSpacing:.5}}>{d.month}</span>
                </button>
              );
            })}
          </div>

          {/* Time picker — preset chips + manual input */}
          <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,marginBottom:8,textTransform:"uppercase"}}>Pick a time</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6,marginBottom:10}}>
            {TIME_PRESETS.map(t => {
              const selected = selectedTime === t.id;
              return (
                <button key={t.id} onClick={()=>{setSelectedTime(t.id);SFX.tabSwitch();}}
                  aria-pressed={selected}
                  style={{
                    background: selected ? T.sageSoft : T.parchment,
                    border: `1px solid ${selected ? T.sage : T.parchment}`,
                    color: selected ? T.leaf : T.ink50,
                    borderRadius: 11,
                    padding: "9px 4px",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                  }}>
                  <span style={{fontSize:14}} aria-hidden="true">{t.icon}</span>
                  <span style={{fontSize:10,fontWeight:700}}>{t.label}</span>
                </button>
              );
            })}
          </div>
          <input type="time" value={selectedTime} onChange={e=>setSelectedTime(e.target.value)}
            aria-label="Custom time"
            style={{width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:11,padding:"10px 14px",color:T.forest,fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:18}}/>

          {/* Info note */}
          <div style={{padding:"11px 14px",marginBottom:16,background:T.sageSoft,border:"1px solid rgba(46,158,79,.18)",borderRadius:12,fontSize:11,color:T.leaf,lineHeight:1.6}}>
            💡 We'll hide deals that expire before this time and surface anything ending close to it. Your scheduled shop appears as a banner on the dashboard.
          </div>

          {/* Buttons */}
          <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between"}}>
            {initial && (
              <button onClick={handleClear}
                style={{background:"rgba(220,38,38,.08)",border:"1px solid rgba(220,38,38,.25)",color:T.berry,borderRadius:10,padding:"9px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                Clear plan
              </button>
            )}
            <div style={{flex:1}}/>
            <RippleBtn sound="success" onClick={handleSave}
              style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"10px 22px",fontSize:12,fontWeight:800,boxShadow:T.shadowFloat}}>
              Save plan →
            </RippleBtn>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EMAIL ALERTS MODAL — opt-in for daily price alerts via email
   ═══════════════════════════════════════════════════════════ */
function EmailAlertsModal({ initial, onSave, onClose }) {
  const [email, setEmail] = useState(initial?.email || "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [frequency, setFrequency] = useState(initial?.frequency || "daily");
  const [error, setError] = useState(null);

  function handleSave() {
    if (enabled && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    onSave({ email: email.trim(), enabled, frequency });
    SFX.success();
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:857,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&onClose&&onClose()}>
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9)",animation:"slideUp .3s ease both"}}>
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:38,height:38,borderRadius:11,background:T.parchment,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}} aria-hidden="true">📧</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest}}>Daily Email Alerts</div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>Get watchlist drops in your inbox each morning</div>
            </div>
            {onClose && (
              <button onClick={onClose} aria-label="Close email settings"
                style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        </div>

        <div style={{padding:"20px 22px"}}>
          {/* Toggle */}
          <button onClick={()=>{setEnabled(e=>!e);SFX.tabSwitch();setError(null);}}
            aria-pressed={enabled}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              background: enabled ? T.sageSoft : T.parchment,
              border: `1.5px solid ${enabled ? T.sage : T.parchment}`,
              borderRadius: 14,
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
              marginBottom: 14,
            }}>
            <div style={{width:44,height:24,borderRadius:12,background:enabled?`${T.forest}`:T.ink15,position:"relative",flexShrink:0,transition:"background .2s"}}>
              <div style={{position:"absolute",top:2,left:enabled?22:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 2px 6px rgba(0,0,0,.4)"}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:800,color:enabled?T.leaf:T.forest}}>
                {enabled ? "Email alerts on" : "Email alerts off"}
              </div>
              <div style={{fontSize:10,color:T.ink50,marginTop:2}}>
                {enabled ? "We'll send you a digest when your watched items go on sale" : "Tap to enable"}
              </div>
            </div>
          </button>

          {/* Email input */}
          {enabled && (
            <>
              <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,marginBottom:8,textTransform:"uppercase"}}>Email address</div>
              <input type="email" value={email} onChange={e=>{setEmail(e.target.value);setError(null);}}
                aria-label="Email address"
                placeholder="you@example.com"
                autoComplete="email"
                style={{width:"100%",background:T.parchment,border:`1px solid ${error?"rgba(248,113,113,.4)":T.sage}`,borderRadius:11,padding:"12px 14px",color:T.forest,fontSize:13,fontFamily:"inherit",outline:"none",marginBottom:error?6:14}}/>
              {error && <div style={{fontSize:11,color:T.berry,marginBottom:14}}>{error}</div>}

              {/* Frequency */}
              <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,marginBottom:8,textTransform:"uppercase"}}>Frequency</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:16}}>
                {[
                  {id:"daily",label:"Daily",desc:"Every morning"},
                  {id:"weekly",label:"Weekly",desc:"Every Sunday"},
                  {id:"alerts",label:"Alerts only",desc:"When prices drop"},
                ].map(f => {
                  const sel = frequency === f.id;
                  return (
                    <button key={f.id} onClick={()=>{setFrequency(f.id);SFX.tabSwitch();}}
                      aria-pressed={sel}
                      style={{
                        background: sel ? T.sageSoft : T.parchment,
                        border: `1px solid ${sel ? T.sage : T.parchment}`,
                        color: sel ? T.leaf : T.ink50,
                        borderRadius: 11,
                        padding: "10px 8px",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "center",
                      }}>
                      <div style={{fontSize:12,fontWeight:800,marginBottom:2}}>{f.label}</div>
                      <div style={{fontSize:10,opacity:.7}}>{f.desc}</div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{padding:"11px 14px",marginBottom:16,background:"rgba(59,130,246,.06)",border:"1px solid rgba(59,130,246,.2)",borderRadius:12,fontSize:11,color:T.leaf,lineHeight:1.6}}>
            💡 Email alerts require a backend cron job. See the build guide for the Vercel Cron + Resend setup. Until configured, this saves your preference but won't send actual emails.
          </div>

          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <RippleBtn sound="success" onClick={handleSave}
              style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"10px 22px",fontSize:12,fontWeight:800,boxShadow:T.shadowFloat}}>
              Save preferences
            </RippleBtn>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STORE ALERTS MODAL — in-app v1 per-store sale/deal opt-in
   ═══════════════════════════════════════════════════════════
   Saves a preference shape designed so Web Push can layer on
   later without a migration:
     { enabled: bool, perStore: { [storeId]: { sale, deal } } }
   ═══════════════════════════════════════════════════════════ */
function StoreAlertsModal({ initial, activeStores, onSave, onClose }) {
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [perStore, setPerStore] = useState(() => {
    const seed = {};
    (activeStores || []).forEach(s => {
      const prev = initial?.perStore?.[s.id];
      seed[s.id] = { sale: prev?.sale ?? false, deal: prev?.deal ?? false };
    });
    return seed;
  });

  function toggleKind(storeId, kind) {
    setPerStore(p => ({ ...p, [storeId]: { ...p[storeId], [kind]: !p[storeId]?.[kind] } }));
    SFX.tabSwitch();
  }

  function selectAll() {
    const next = {};
    (activeStores || []).forEach(s => { next[s.id] = { sale: true, deal: true }; });
    setPerStore(next);
    SFX.tabSwitch();
  }

  function selectNone() {
    const next = {};
    (activeStores || []).forEach(s => { next[s.id] = { sale: false, deal: false }; });
    setPerStore(next);
    SFX.tabSwitch();
  }

  function handleSave() {
    onSave({ enabled, perStore });
    SFX.success();
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:857,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&onClose&&onClose()}>
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9)",animation:"slideUp .3s ease both"}}>
        <div style={{position:"relative",overflow:"hidden",padding:"20px 22px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf, T.sage]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:38,height:38,borderRadius:11,background:T.parchment,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}} aria-hidden="true">🔔</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest}}>Store Alerts</div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>Get a heads-up when your stores post sales or new deals</div>
            </div>
            {onClose && (
              <button onClick={onClose} aria-label="Close store alerts"
                style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        </div>

        <div style={{padding:"20px 22px"}}>
          {/* Master toggle */}
          <button onClick={()=>{setEnabled(e=>!e);SFX.tabSwitch();}}
            aria-pressed={enabled}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              background: enabled ? T.sageSoft : T.parchment,
              border: `1.5px solid ${enabled ? T.sage : T.parchment}`,
              borderRadius: 14,
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
              marginBottom: 14,
            }}>
            <div style={{width:44,height:24,borderRadius:12,background:enabled?`${T.forest}`:T.ink15,position:"relative",flexShrink:0,transition:"background .2s"}}>
              <div style={{position:"absolute",top:2,left:enabled?22:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 2px 6px rgba(0,0,0,.4)"}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:800,color:enabled?T.leaf:T.forest}}>
                {enabled ? "Store alerts on" : "Store alerts off"}
              </div>
              <div style={{fontSize:10,color:T.ink50,marginTop:2}}>
                {enabled ? "We'll show alerts in your Alerts panel when stores post matching activity" : "Tap to enable"}
              </div>
            </div>
          </button>

          {/* Per-store picker — only shown when master is on */}
          {enabled && (
            <>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,textTransform:"uppercase"}}>Per-store preferences</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={selectAll}
                    style={{background:T.parchment,border:`1px solid ${T.sage}`,color:T.leaf,borderRadius:8,padding:"5px 10px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                    Select all
                  </button>
                  <button onClick={selectNone}
                    style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:8,padding:"5px 10px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                    None
                  </button>
                </div>
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                {(activeStores || []).length === 0 && (
                  <div style={{padding:"12px 14px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:11,fontSize:11,color:T.ink50}}>
                    No stores selected yet. Pick stores in Location settings first.
                  </div>
                )}
                {(activeStores || []).map(s => {
                  const row = perStore[s.id] || { sale: false, deal: false };
                  return (
                    <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12}}>
                      <div aria-hidden="true" style={{width:30,height:30,borderRadius:9,background:s.glow||T.sageSoft,border:`1px solid ${s.color||T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{s.logo || "🏬"}</div>
                      <div style={{flex:1,minWidth:0,fontSize:12,fontWeight:800,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                      <button onClick={()=>toggleKind(s.id,"sale")}
                        aria-pressed={row.sale}
                        style={{background:row.sale?T.sageSoft:T.paper,border:`1px solid ${row.sale?T.sage:T.hairline}`,color:row.sale?T.leaf:T.ink50,borderRadius:8,padding:"6px 10px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                        {row.sale ? "✓ " : ""}Sales
                      </button>
                      <button onClick={()=>toggleKind(s.id,"deal")}
                        aria-pressed={row.deal}
                        style={{background:row.deal?T.sageSoft:T.paper,border:`1px solid ${row.deal?T.sage:T.hairline}`,color:row.deal?T.leaf:T.ink50,borderRadius:8,padding:"6px 10px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                        {row.deal ? "✓ " : ""}New deals
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div style={{padding:"11px 14px",marginBottom:16,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:12,fontSize:11,color:T.leaf,lineHeight:1.6}}>
            💡 In-app alerts only for now — these surface in your Alerts panel. Push notifications will arrive in a later update.
          </div>

          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <RippleBtn sound="success" onClick={handleSave}
              style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:10,padding:"10px 22px",fontSize:12,fontWeight:800,boxShadow:T.shadowFloat}}>
              Save preferences
            </RippleBtn>
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCANNER PRODUCT REVIEWS — Instacart Connect product ratings
   ═══════════════════════════════════════════════════════════
   Renders inside the barcode scanner result. Populates only when:
     1. Instacart Connect API key is configured (hasInstacartKey())
     2. The scanned product has an instacartProductId
     3. The Connect product endpoint returns rating + review data
   Renders NOTHING (no box, no skeleton) when data is unavailable.
   ═══════════════════════════════════════════════════════════ */
function ScannerProductReviews({ productId }) {
  const rating = useProductRating(productId);
  if (!rating || rating.rating == null) return null;

  return (
    <Glass style={{padding:"12px 14px",marginBottom:12,borderRadius:14,border:"1px solid rgba(251,191,36,.2)",background:"linear-gradient(135deg, rgba(251,191,36,.04), ${T.parchment})"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:rating.topReview ? 10 : 0}}>
        <div style={{width:32,height:32,borderRadius:9,background:T.butterSoft,border:"1px solid rgba(251,191,36,.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>⭐</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:10,letterSpacing:1.2,color:T.ink50,fontWeight:700,marginBottom:2}}>PRODUCT RATING</div>
          <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
            <StarRating rating={rating.rating} size={12}/>
            <span style={{fontSize:11,color:T.ink50}}>· {rating.reviewCount.toLocaleString()} reviews</span>
          </div>
        </div>
      </div>
      {rating.topReview && (
        <div style={{paddingTop:10,borderTop:`1px solid ${T.hairline}`}}>
          <div style={{fontSize:11,color:T.ink50,lineHeight:1.5,fontStyle:"italic",display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
            "{rating.topReview.text}"
          </div>
          {rating.topReview.author && (
            <div style={{fontSize:10,color:T.ink50,marginTop:5,fontWeight:600}}>— {rating.topReview.author}</div>
          )}
        </div>
      )}
    </Glass>
  );
}

/* ═══════════════════════════════════════════════════════════
   BARCODE SCANNER MODAL — uses native BarcodeDetector API
   ═══════════════════════════════════════════════════════════
   Browser support:
   - Chrome/Edge mobile + desktop: ✓
   - Safari iOS 16.4+: ✓
   - Firefox: ✗ (gracefully falls back to manual entry)
   ═══════════════════════════════════════════════════════════ */
function BarcodeScannerModal({ onClose, onResult, addToCart, addToWatchlist, isInCart, isWatched: checkWatched }) {
  const [step, setStep] = useState("scanning");  // scanning | result | error | manual
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [manualUpc, setManualUpc] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanLoopRef = useRef(null);
  const [supported] = useState(() => typeof window !== "undefined" && "BarcodeDetector" in window);

  // Start the camera and scanning loop
  useEffect(() => {
    if (step !== "scanning" || !supported) return;
    let cancelled = false;

    (async () => {
      try {
        // Cross-browser guard: navigator.mediaDevices is undefined on insecure (http://) contexts
        // and on some older browsers. Surface a clear error rather than crashing.
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Camera API not available — requires HTTPS and a modern browser");
        }
        // Request rear camera if available
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t=>t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        // Initialize detector with common grocery formats
        detectorRef.current = new window.BarcodeDetector({
          formats: ["upc_a", "upc_e", "ean_13", "ean_8", "code_128", "code_39"],
        });

        // Scan loop — runs at ~5fps, stops on first match
        const tick = async () => {
          if (cancelled || !videoRef.current || !detectorRef.current) return;
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes.length > 0) {
              const upc = codes[0].rawValue;
              SFX.scan();
              const lookup = lookupByBarcode(upc);
              if (lookup) {
                setResult(lookup);
                setStep("result");
                if (onResult) onResult(lookup);
              } else {
                setResult({ upc, item: null, deals: [] });
                setStep("result");
              }
              stopCamera();
              return;
            }
          } catch(e) {
            // Detection error — usually just "no codes in this frame", keep scanning
          }
          scanLoopRef.current = setTimeout(tick, 200);
        };
        tick();
      } catch(e) {
        if (!cancelled) {
          setError(e.name === "NotAllowedError" ? "Camera permission denied. Tap Allow to scan barcodes." : `Camera error: ${e.message}`);
          setStep("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (scanLoopRef.current) clearTimeout(scanLoopRef.current);
      stopCamera();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, supported]);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }

  function handleManualLookup() {
    const cleaned = manualUpc.replace(/\D/g, "");
    if (cleaned.length < 8) {
      setError("Enter at least 8 digits");
      return;
    }
    SFX.scan();
    const lookup = lookupByBarcode(cleaned);
    if (lookup) {
      setResult(lookup);
      setStep("result");
    } else {
      setResult({ upc: cleaned, item: null, deals: [] });
      setStep("result");
    }
  }

  function reset() {
    setResult(null);
    setError(null);
    setManualUpc("");
    setStep(supported ? "scanning" : "manual");
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:870,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .25s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:460,maxHeight:"92vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.95), 0 0 50px rgba(30,53,40,.12)",animation:"slideUp .3s ease both"}}>

        {/* Header */}
        <div style={{position:"relative",overflow:"hidden",padding:"16px 20px",borderBottom:`1px solid ${T.hairline}`,borderRadius:"22px 22px 0 0"}}>
          <MeshGradient colors={[T.forest, T.basil, T.leaf]}/>
          <div style={{position:"absolute",inset:0,background:`${T.forest}`}}/>
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:38,height:38,borderRadius:11,background:T.parchment,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>📷</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest}}>
                {step==="scanning" ? "Scan a barcode" : step==="result" ? (result?.item ? "Found!" : "Not in catalog") : step==="manual" ? "Enter UPC" : "Camera issue"}
              </div>
              <div style={{fontSize:11,color:T.sageSoft,marginTop:2}}>
                {step==="scanning" ? "Point at any product barcode" :
                 step==="result" && result?.item ? `Best price across ${result.deals.length} stores` :
                 step==="result" ? "We don't have data for this item yet" :
                 step==="manual" ? "Type the digits below the barcode" :
                 "Try entering the UPC manually"}
              </div>
            </div>
            <button onClick={onClose} style={{background:T.parchment,border:"none",color:T.forest,borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:14}}>✕</button>
          </div>
        </div>

        <div style={{padding:"18px 20px"}}>

          {/* SCANNING — live camera feed */}
          {step === "scanning" && supported && (
            <>
              <div style={{position:"relative",borderRadius:14,overflow:"hidden",aspectRatio:"4/3",background:"#000",marginBottom:14}}>
                <video ref={videoRef} playsInline muted style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                {/* Scanning overlay */}
                <div style={{position:"absolute",inset:0,pointerEvents:"none"}}>
                  <div style={{position:"absolute",top:"50%",left:"10%",right:"10%",height:2,background:`linear-gradient(90deg,transparent,${T.leaf},transparent)`,boxShadow:`0 0 12px ${T.leaf}`,animation:"scanLine 2s ease-in-out infinite"}}/>
                  <div style={{position:"absolute",top:"30%",left:"10%",width:24,height:24,borderTop:`3px solid ${T.leaf}`,borderLeft:`3px solid ${T.leaf}`}}/>
                  <div style={{position:"absolute",top:"30%",right:"10%",width:24,height:24,borderTop:`3px solid ${T.leaf}`,borderRight:`3px solid ${T.leaf}`}}/>
                  <div style={{position:"absolute",bottom:"30%",left:"10%",width:24,height:24,borderBottom:`3px solid ${T.leaf}`,borderLeft:`3px solid ${T.leaf}`}}/>
                  <div style={{position:"absolute",bottom:"30%",right:"10%",width:24,height:24,borderBottom:`3px solid ${T.leaf}`,borderRight:`3px solid ${T.leaf}`}}/>
                </div>
              </div>
              <button onClick={()=>{stopCamera();setStep("manual");}}
                style={{width:"100%",background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:11,padding:"10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                Type UPC instead →
              </button>
            </>
          )}

          {/* MANUAL UPC ENTRY (also fallback when BarcodeDetector unsupported) */}
          {(step === "manual" || (step === "scanning" && !supported)) && (
            <>
              {!supported && (
                <div style={{padding:"10px 14px",marginBottom:14,background:T.butterSoft,border:"1px solid rgba(251,191,36,.2)",borderRadius:10,fontSize:11,color:T.warn,lineHeight:1.5}}>
                  ⚠️ Your browser doesn't support live barcode scanning. Type the UPC manually below — works the same way.
                </div>
              )}
              <input
                type="text"
                inputMode="numeric"
                value={manualUpc}
                onChange={e=>{setManualUpc(e.target.value.replace(/\D/g,""));setError(null);}}
                onKeyDown={e=>e.key==="Enter"&&handleManualLookup()}
                placeholder="012345678905"
                autoFocus
                style={{width:"100%",background:T.parchment,border:`1px solid ${T.sage}`,borderRadius:12,color:T.forest,padding:"14px 18px",fontSize:18,fontFamily:FONT_MONO,outline:"none",letterSpacing:3,textAlign:"center",fontWeight:700,marginBottom:10}}
              />
              {error && <div style={{fontSize:11,color:T.berry,textAlign:"center",marginBottom:10}}>{error}</div>}
              <RippleBtn sound="success" onClick={handleManualLookup} disabled={manualUpc.length<8}
                style={{width:"100%",background:manualUpc.length>=8?`${T.forest}`:T.parchment,border:"none",color:manualUpc.length>=8?T.paper:T.ink50,borderRadius:12,padding:"12px",fontSize:13,fontWeight:800,boxShadow:manualUpc.length>=8?"0 0 18px rgba(30,53,40,.18)":"none"}}>
                Look up product →
              </RippleBtn>
              {supported && (
                <button onClick={reset}
                  style={{width:"100%",marginTop:10,background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"8px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  ← Use camera
                </button>
              )}
            </>
          )}

          {/* RESULT — found a match */}
          {step === "result" && result?.item && (
            <>
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",marginBottom:14,borderBottom:`1px solid ${T.hairline}`}}>
                <ProductImage item={result.item} size={56}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:800,color:T.forest,display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                    {result.item.name}
                    {isOrganic(result.item.name) && <OrgBadge small/>}
                  </div>
                  <div style={{fontSize:10,color:T.ink50,marginTop:2}}>UPC: {result.upc}</div>
                </div>
                <GoogleSearchButton query={result.item.name} size="medium" placement="below"/>
              </div>

              {result.deals.length > 0 ? (() => {
                // Sort by unit price (cheapest $/oz first). Items without
                // unit-price data sink to the bottom so they don't claim the
                // BEST slot. Same UPC across stores normally has matching pack
                // size, so this aligns with effectivePerUnit ordering too.
                const sortedDeals = result.deals.slice().sort((a, b) => {
                  const ua = computeUnitPrice(a);
                  const ub = computeUnitPrice(b);
                  if (!ua && !ub) return 0;
                  if (!ua) return 1;
                  if (!ub) return -1;
                  return ua.value - ub.value;
                });
                return (
                <>
                  {/* Product reviews panel — populates when Instacart Connect is live */}
                  <ScannerProductReviews productId={sortedDeals[0].instacartProductId || result.item?.instacartProductId}/>

                  <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:700,marginBottom:10}}>PRICE COMPARISON · CHEAPEST PER UNIT FIRST · {sortedDeals.length} STORE{sortedDeals.length>1?"S":""}</div>
                  {sortedDeals.map((d, i) => {
                    const up = computeUnitPrice(d);
                    const inCart = isInCart && isInCart(d.store.id, d.id);
                    const watched = checkWatched && checkWatched(d.name, d.store.id);
                    // Pricing comes from the single-source-of-truth module
                    const headline = displayPrice(d);
                    const dealMath = dealMathCaption(d);
                    return (
                      <Glass key={i} style={{padding:"12px 14px",marginBottom:8,borderRadius:14,border:i===0?"1.5px solid rgba(74,222,128,.35)":`1px solid ${T.hairline}`,boxShadow:i===0?"0 0 18px rgba(30,53,40,.10)":"none"}}>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                          {i===0 && <span style={{background:`linear-gradient(90deg,${T.basil},${T.leaf})`,color:"#000",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,letterSpacing:.5,flexShrink:0}}>BEST</span>}
                          <div style={{width:28,height:28,borderRadius:8,background:d.store.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{d.store.logo}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:700,color:T.forest}}>{d.store.name}</div>
                            <Pill label={d.dealLabel} color={d.store.color}/>
                          </div>
                          <div style={{textAlign:"right",minWidth:80}}>
                            {up ? (
                              <>
                                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:i===0?T.leaf:T.forest,lineHeight:1}}>{up.label}</div>
                                <div style={{fontSize:10,color:T.ink50,fontWeight:600,marginTop:3,lineHeight:1.3}}>${headline.toFixed(2)}{dealMath ? ` · ${dealMath}` : ""}</div>
                              </>
                            ) : (
                              <>
                                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:i===0?T.leaf:T.forest,lineHeight:1}}>${headline.toFixed(2)}</div>
                                {dealMath && <div style={{fontSize:10,color:T.ink50,fontWeight:600,marginTop:3,lineHeight:1.3}}>{dealMath}</div>}
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6}}>
                          {addToCart && (
                            <RippleBtn sound={inCart?"removeCart":"addCart"} onClick={()=>addToCart(d.store.id, d.id)}
                              style={{flex:1,background:inCart?T.sageSoft:`${T.forest}`,border:inCart?`1px solid ${T.sage}`:"none",color:inCart?T.leaf:"#fff",borderRadius:9,padding:"6px 10px",fontSize:11,fontWeight:800}}>
                              {inCart?"✓ In Cart":"+ Add to Cart"}
                            </RippleBtn>
                          )}
                          {addToWatchlist && (
                            <RippleBtn sound={watched?"removeCart":"addCart"} onClick={()=>addToWatchlist({...d, store: d.store})}
                              style={{background:watched?T.sageSoft:T.parchment,border:`1px solid ${watched?T.sage:T.ink15}`,color:watched?T.leaf:T.ink50,borderRadius:9,padding:"6px 12px",fontSize:11,fontWeight:800}}>
                              {watched?"🔖":"🤍"}
                            </RippleBtn>
                          )}
                        </div>
                      </Glass>
                    );
                  })}
                  {sortedDeals.length > 1 && (() => {
                    const cheapestUp = computeUnitPrice(sortedDeals[0]);
                    const priciestUp = computeUnitPrice(sortedDeals[sortedDeals.length-1]);
                    if (cheapestUp && priciestUp && priciestUp.value > cheapestUp.value) {
                      const savePct = Math.round(((priciestUp.value - cheapestUp.value) / priciestUp.value) * 100);
                      return (
                        <div style={{marginTop:10,padding:"10px 14px",background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:12,fontSize:11,color:T.basil,textAlign:"center",fontWeight:700}}>
                          💡 {savePct}% cheaper at {sortedDeals[0].store.name} per unit
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
                );
              })() : (
                <div style={{padding:"24px 16px",textAlign:"center",color:T.ink50,fontSize:12}}>
                  No active deals for this item right now. Add it to your watchlist to get notified when it goes on sale.
                </div>
              )}

              <button onClick={reset}
                style={{width:"100%",marginTop:12,background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"9px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                📷 Scan another
              </button>
            </>
          )}

          {/* RESULT — UPC not in catalog */}
          {step === "result" && !result?.item && (() => {
            // Surface top 3 deals from MASTER_LIST as suggestions while Instacart Connect is pending
            const topDeals = STORES.flatMap(s => s.deals.map(d => ({...d, store: s})))
              .sort((a, b) => b.savings - a.savings)
              .filter((d, i, arr) => arr.findIndex(x => x.id === d.id) === i)  // dedupe by id
              .slice(0, 3);
            return (
              <div style={{padding:"8px 0"}}>
                <div style={{textAlign:"center",marginBottom:18}}>
                  <div style={{fontSize:42,marginBottom:10,opacity:.7}} aria-hidden="true">🔍</div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,marginBottom:6}}>UPC {result.upc}</div>
                  <div style={{fontSize:12,color:T.ink50,lineHeight:1.6,maxWidth:320,margin:"0 auto"}}>
                    This exact product isn't in CartIQ's catalog yet. Once Instacart Connect approval lands, every barcode resolves to live data automatically.
                  </div>
                </div>

                {topDeals.length > 0 && (
                  <>
                    <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:700,marginBottom:10,paddingLeft:4}}>🔥 POPULAR DEALS YOU MIGHT LIKE</div>
                    {topDeals.map((d, i) => (
                      <Glass key={i} style={{padding:"10px 12px",marginBottom:8,borderRadius:12,border:`1px solid ${T.hairline}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <ProductImage item={{id:d.id,name:d.name,category:d.category}} size={38}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</div>
                            <div style={{fontSize:10,color:T.ink50,marginTop:1}}>{d.store.logo} {d.store.name} · {d.dealLabel}</div>
                          </div>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.basil,flexShrink:0}}>−${d.savings.toFixed(0)}</div>
                          {addToCart && (
                            <button onClick={()=>addToCart(d.store.id, d.id)}
                              aria-label={`Add ${d.name} to cart`}
                              style={{background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:8,width:30,height:30,fontSize:14,cursor:"pointer",flexShrink:0,fontFamily:"inherit"}}>
                              +
                            </button>
                          )}
                        </div>
                      </Glass>
                    ))}
                  </>
                )}

                <div style={{display:"flex",justifyContent:"center",marginTop:16}}>
                  <button onClick={reset}
                    style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:11,padding:"10px 22px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:T.shadowFloat}}>
                    📷 Scan another
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ERROR — camera permission denied */}
          {step === "error" && (
            <div style={{textAlign:"center",padding:"24px 16px"}}>
              <div style={{fontSize:42,marginBottom:12,opacity:.7}}>📷</div>
              <div style={{fontSize:13,color:T.berry,lineHeight:1.6,marginBottom:18}}>{error}</div>
              <button onClick={()=>setStep("manual")}
                style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:11,padding:"10px 22px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                Type UPC instead
              </button>
            </div>
          )}
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHOPPING MODE — full-screen in-store checklist
   ═══════════════════════════════════════════════════════════
   Wake-lock keeps the screen on. Big tap targets, category
   grouping, cross-off gesture, integrated scanner, progress bar.
   Optimized for one-handed use in the grocery aisle.
   ═══════════════════════════════════════════════════════════ */
function ShoppingModeScreen({ cartItems, onToggleItem, onOpenScanner, onExit }) {
  const [checked, setChecked] = useState(new Set());
  const [confirmExit, setConfirmExit] = useState(false);
  const wakeLockRef = useRef(null);

  // Acquire wake lock to keep screen on during shopping
  useEffect(() => {
    let cancelled = false;
    async function acquire() {
      if (!("wakeLock" in navigator)) return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) { lock.release(); return; }
        wakeLockRef.current = lock;
        // Re-acquire if the page becomes visible again
        lock.addEventListener("release", () => { wakeLockRef.current = null; });
      } catch(e) {
        // User may have denied or the API may fail silently — that's OK
      }
    }
    acquire();
    // Re-acquire on visibility change
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) acquire();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch(e) {}
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Group cart items by store, then by category within store
  const grouped = useMemo(() => {
    const byStore = {};
    for (const item of cartItems) {
      const sid = item.store.id;
      if (!byStore[sid]) byStore[sid] = { store: item.store, categories: {} };
      const cat = item.category || "Other";
      if (!byStore[sid].categories[cat]) byStore[sid].categories[cat] = [];
      byStore[sid].categories[cat].push(item);
    }
    return Object.values(byStore);
  }, [cartItems]);

  const totalCount = cartItems.length;
  const checkedCount = checked.size;
  const progress = totalCount > 0 ? checkedCount / totalCount : 0;

  function toggleCheck(itemKey) {
    SFX.addCart();
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey); else next.add(itemKey);
      return next;
    });
    // Light haptic if available (gated by user preference + iOS support)
    hapticTap(30);
  }

  function handleExit() {
    if (checkedCount > 0 && checkedCount < totalCount) {
      setConfirmExit(true);
    } else {
      onExit();
    }
  }

  return (
    <div style={{
      position:"fixed",
      inset:0,
      zIndex:990,
      background:T.cream,
      overflowY:"auto",overscrollBehavior:"contain",
      fontFamily:FONT_SANS,
    }}>
      {/* Top bar */}
      <div style={{
        position:"sticky",
        top:0,
        zIndex:10,
        background:T.paper,
        backdropFilter:"blur(16px)",WebkitBackdropFilter:"blur(16px)",
        borderBottom:`1px solid ${T.hairline}`,
        padding:"14px 18px",
        paddingTop:"max(14px, env(safe-area-inset-top))",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <button onClick={handleExit} aria-label="Exit shopping mode"
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.forest,borderRadius:10,width:38,height:38,fontSize:16,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
            <span aria-hidden="true">✕</span>
          </button>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:10,letterSpacing:2,color:T.basil,fontWeight:800}}>🛒 SHOPPING MODE</div>
            <div style={{fontSize:18,fontWeight:700,color:T.forest,fontFamily:FONT_DISPLAY}}>
              {checkedCount} of {totalCount} items
            </div>
          </div>
          <button onClick={onOpenScanner} aria-label="Open barcode scanner"
            style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:12,width:48,height:48,fontSize:20,cursor:"pointer",fontFamily:"inherit",flexShrink:0,boxShadow:T.shadowFloat}}>
            <span aria-hidden="true">📷</span>
          </button>
        </div>
        {/* Progress bar */}
        <div style={{height:6,background:T.parchment,borderRadius:3,overflow:"hidden"}}>
          <div style={{
            height:"100%",
            width:`${progress*100}%`,
            background:`linear-gradient(90deg,${T.basil},${T.leaf})`,
            borderRadius:3,
            transition:"width .3s ease",
            boxShadow:progress>0?"0 0 10px rgba(74,222,128,.6)":"none",
          }}/>
        </div>
      </div>

      {/* Store sections */}
      <div style={{padding:"16px 14px 100px"}}>
        {grouped.length === 0 ? (
          <div style={{textAlign:"center",padding:"60px 20px",color:T.ink50}}>
            <div style={{fontSize:48,marginBottom:14,opacity:.5}} aria-hidden="true">🛒</div>
            <div style={{fontSize:16,fontWeight:700,color:T.forest,marginBottom:6}}>Your cart is empty</div>
            <div style={{fontSize:12}}>Add items to your cart before starting a shop</div>
          </div>
        ) : grouped.map(storeGroup => (
          <div key={storeGroup.store.id} style={{marginBottom:24}}>
            <div style={{
              display:"flex",
              alignItems:"center",
              gap:10,
              padding:"10px 14px",
              marginBottom:10,
              background:storeGroup.store.bg,
              borderRadius:14,
              border:`1px solid ${T.hairline}`,
            }}>
              <span style={{fontSize:22}} aria-hidden="true">{storeGroup.store.logo}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:700,color:T.paper}}>{storeGroup.store.name}</div>
                <div style={{fontSize:10,color:T.paper,opacity:.85}}>{Object.values(storeGroup.categories).flat().length} items</div>
              </div>
            </div>
            {Object.entries(storeGroup.categories).map(([cat, items]) => (
              <div key={cat} style={{marginBottom:14}}>
                <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:800,marginBottom:8,paddingLeft:6,textTransform:"uppercase"}}>
                  {cat}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {items.map(item => {
                    const key = `${item.store.id}_${item.id}`;
                    const isChecked = checked.has(key);
                    return (
                      <button key={key} onClick={()=>toggleCheck(key)}
                        aria-pressed={isChecked}
                        aria-label={`${item.name}, ${isChecked ? "checked off" : "not yet picked up"}`}
                        style={{
                          width:"100%",
                          display:"flex",
                          alignItems:"center",
                          gap:14,
                          padding:"16px 18px",
                          background:isChecked?"rgba(46,158,79,.1)":T.parchment,
                          border:`2px solid ${isChecked?T.sage:T.ink15}`,
                          borderRadius:16,
                          cursor:"pointer",
                          fontFamily:"inherit",
                          textAlign:"left",
                          minHeight:68,
                          transition:"background .15s, border-color .15s",
                        }}>
                        {/* Big checkbox */}
                        <div style={{
                          width:36,
                          height:36,
                          borderRadius:10,
                          background:isChecked?`${T.forest}`:T.parchment,
                          border:`2px solid ${isChecked?T.leaf:T.parchment}`,
                          color:T.paper,
                          display:"flex",
                          alignItems:"center",
                          justifyContent:"center",
                          fontSize:18,
                          flexShrink:0,
                          boxShadow:isChecked?"0 0 14px rgba(74,222,128,.4)":"none",
                        }} aria-hidden="true">
                          {isChecked ? "✓" : ""}
                        </div>
                        {/* Product photo */}
                        <ProductImage item={{id:item.id,name:item.name,category:item.category}} size={42}/>
                        {/* Name + price */}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{
                            fontSize:15,
                            fontWeight:800,
                            color:isChecked?T.sage:T.forest,
                            textDecoration:isChecked?"line-through":"none",
                            overflow:"hidden",
                            textOverflow:"ellipsis",
                            whiteSpace:"nowrap",
                          }}>{item.name}</div>
                          <div style={{fontSize:11,color:T.ink50,marginTop:2}}>
                            ${(item.salePrice||item.origPrice).toFixed(2)} · save ${item.savings.toFixed(2)}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Completion celebration */}
        {totalCount > 0 && checkedCount === totalCount && (
          <div style={{
            textAlign:"center",
            padding:"30px 20px",
            background:T.sageSoft,
            border:`1.5px solid ${T.sage}`,
            borderRadius:18,
            marginTop:16,
          }}>
            <div style={{fontSize:48,marginBottom:12}} aria-hidden="true">🎉</div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.basil,marginBottom:6}}>All done!</div>
            <div style={{fontSize:12,color:T.leaf,marginBottom:16}}>You picked up everything on your list. Ready to check out?</div>
            <RippleBtn sound="success" onClick={onExit}
              style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:12,padding:"12px 24px",fontSize:13,fontWeight:800,boxShadow:T.shadowFloat}}>
              Exit Shopping Mode →
            </RippleBtn>
          </div>
        )}
      </div>

      {/* Exit confirmation dialog */}
      {confirmExit && (
        <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.55)",zIndex:20,display:"flex",alignItems:"center",justifyContent:"center",padding:20,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&setConfirmExit(false)}>
          <Glass style={{width:"100%",maxWidth:340,padding:24,borderRadius:18,border:`1px solid ${T.hairline}`,animation:"slideUp .25s ease both"}}>
            <div style={{fontSize:32,textAlign:"center",marginBottom:12}} aria-hidden="true">🛒</div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest,textAlign:"center",marginBottom:8}}>Exit with items unchecked?</div>
            <div style={{fontSize:12,color:T.ink50,textAlign:"center",marginBottom:18,lineHeight:1.5}}>
              You have {totalCount - checkedCount} item{(totalCount-checkedCount)!==1?"s":""} still to pick up.
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setConfirmExit(false)}
                style={{flex:1,background:T.parchment,border:`1px solid ${T.hairline}`,color:T.forest,borderRadius:11,padding:"11px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                Keep shopping
              </button>
              <RippleBtn sound="tabSwitch" onClick={()=>{setConfirmExit(false);onExit();}}
                style={{flex:1,background:T.berry,border:"none",color:T.paper,borderRadius:11,padding:"11px",fontSize:12,fontWeight:800}}>
                Exit anyway
              </RippleBtn>
            </div>
          </Glass>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   AI ASSISTANT
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   ONBOARDING TOUR
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   ONBOARDING TOUR
   ═══════════════════════════════════════════════════════════
   Modal walkthrough fired on first launch. Each step routes
   the app to a specific tab so the user sees the real surface
   while reading the description. Visual language matches the
   polished tab headers — light Glass card, basil accent, clean
   SVG icon chip. ═══════════════════════════════════════════ */
const TOUR_STEPS = [
  {tab:"dashboard", iconName:"home",     accent:"basil",   title:"Welcome to CartIQ",     desc:"Your savings dashboard tracks every dollar you keep, week by week. Watch totals climb as you shop smarter across every store nearby."},
  {tab:"split",     iconName:"cart",     accent:"forest",  title:"Smart Cart",            desc:"Drop in your list and CartIQ routes each item to whichever store has the best price — even after delivery fees. One cart, maximum savings."},
  {tab:"pantry",    iconName:"box",      accent:"apricot", title:"Pantry & Expiry",       desc:"Track what's at home so nothing rots in the back of the fridge. We flag items running low AND on sale at the same store, then nudge you to restock."},
  {tab:"bogos",     iconName:"tag",      accent:"leaf",    title:"Deal Browser",          desc:"Every live deal across your stores in one grid. Filter by BOGO, all-time low, ending today, organic, under $5, and more. Tap any tile to expand."},
  {tab:"watchlist", iconName:"bookmark", accent:"berry",   title:"Watchlist & Alerts",    desc:"Bookmark anything that catches your eye. CartIQ tracks the price daily and pings you the moment it drops to your target — works on every catalog item."},
];

function OnboardingTour({ step, onNext, onSkip, onFinish, onSetTab }) {
  const s = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  useEffect(() => { if(s.tab) onSetTab(s.tab); }, [step]);
  // Map symbolic accent → real palette token
  const accentColor = s.accent === "berry" ? T.berry
    : s.accent === "apricot" ? T.apricot
    : s.accent === "leaf" ? T.leaf
    : s.accent === "forest" ? T.forest
    : T.basil;
  const accentSoft = s.accent === "berry" ? T.berrySoft
    : s.accent === "apricot" ? T.peachSoft
    : s.accent === "leaf" ? T.sageSoft
    : s.accent === "forest" ? T.sageSoft
    : T.sageSoft;
  return (
    <div style={{position:"fixed",inset:0,background:`rgba(30,53,40,.55)`,backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20,animation:"fadeIn .3s ease"}}>
      <Glass style={{width:"100%",maxWidth:400,padding:0,borderRadius:22,border:`1px solid ${T.hairline}`,boxShadow:"0 24px 80px rgba(0,0,0,.45), 0 0 50px rgba(30,53,40,.12)",animation:"slideUp .35s ease both",position:"relative",overflow:"hidden"}}>
        {/* Step pill — top-left, accent-tinted */}
        <div style={{position:"absolute",top:18,left:20,display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,background:T.parchment,border:`1px solid ${T.hairline}`,fontSize:9.5,fontWeight:800,letterSpacing:1.4,color:T.ink50,textTransform:"uppercase"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:accentColor}}/>
          <span>Step {step+1} of {TOUR_STEPS.length}</span>
        </div>
        {/* Skip */}
        <button onClick={onSkip}
          style={{position:"absolute",top:14,right:14,background:"transparent",border:"none",color:T.ink50,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:"6px 8px"}}>
          Skip
        </button>

        {/* Body */}
        <div style={{padding:"56px 28px 22px"}}>
          <div style={{width:54,height:54,borderRadius:14,background:`linear-gradient(135deg, ${accentColor}28, ${accentColor}12)`,border:`1px solid ${accentColor}55`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14}}>
            <Icon name={s.iconName} size={26} color={accentColor} stroke={2.2}/>
          </div>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.forest,letterSpacing:-.3,marginBottom:9,lineHeight:1.2}}>{s.title}</div>
          <div style={{fontSize:13.5,color:T.ink70,lineHeight:1.6}}>{s.desc}</div>
        </div>

        {/* Step dots */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,padding:"0 28px 18px"}}>
          {TOUR_STEPS.map((_, i) => {
            const active = i === step;
            const past = i < step;
            return (
              <span key={i} style={{
                width: active ? 22 : 7,
                height: 7,
                borderRadius: 4,
                background: active ? accentColor : past ? T.sage : T.parchment,
                transition: "all .3s ease",
              }}/>
            );
          })}
        </div>

        {/* Footer actions */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"14px 22px 22px",borderTop:`1px solid ${T.hairline}`,background:T.parchment}}>
          {step > 0 ? (
            <button onClick={()=>onNext(step-1)}
              style={{background:"transparent",border:"none",color:T.ink70,borderRadius:9,padding:"9px 6px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:4}}>
              <Icon name="chevron-left" size={13} color={T.ink70} stroke={2.4}/>
              <span>Back</span>
            </button>
          ) : <span/>}
          <button onClick={isLast?onFinish:()=>onNext(step+1)}
            style={{background:T.forest,border:"none",color:T.paper,borderRadius:11,padding:"10px 22px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(30,53,40,.22)",display:"inline-flex",alignItems:"center",gap:5,letterSpacing:.2}}>
            <span>{isLast ? "Get started" : "Next"}</span>
            <Icon name={isLast ? "check" : "chevron-right"} size={13} color={T.paper} stroke={2.4}/>
          </button>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FIRST-VISIT TOOLTIPS — contextual teaching for secondary features
   ═══════════════════════════════════════════════════════════
   Fires once the first time users land on specific tabs. Stored
   in window.storage as an array of tab ids they've already seen.
   ═══════════════════════════════════════════════════════════ */
const FIRST_VISIT_TIPS = {
  history: {
    iconName: "trending-up",
    accent: "leaf",
    title: "Price Intelligence",
    desc: "6 weeks of price history for every item. Green bars = prices falling (buy now). Red bars = near regular price (wait). Sparklines on deal cards show the same data at a glance.",
  },
  bogos: {
    iconName: "tag",
    accent: "leaf",
    title: "Deal Browser tips",
    desc: "Filters are grouped: 6 primary chips (All, Bundle Deals, All-Time Low, Ending Today, 30%+ Off, Under $5) plus personalized 'Yours' filters and per-store rows below. Tap Bundle Deals to reveal BOGO Free, BOGO 50%, Buy 2 Get 1, and Big Deal sub-filters. Tap any tile to expand into a full deal card.",
  },
  watchlist: {
    iconName: "bookmark",
    accent: "berry",
    title: "How alerts work",
    desc: "Each watched item has three alert types: Hit target (price drops to your goal), All-time low (best price ever seen), Any sale (any drop from current). Toggle them per-item.",
  },
  subs: {
    iconName: "refresh",
    accent: "forest",
    title: "Smart Substitutions",
    desc: "Set what CartIQ should do if an item is out of stock. Options include 'closest brand', 'cheapest alternative', 'skip it', or 'contact me first'.",
  },
  coupons: {
    iconName: "gift",
    accent: "apricot",
    title: "Stack & Save",
    desc: "Combine manufacturer coupons with Ibotta cashback and store BOGOs for triple-stack savings. CartIQ shows which deals are stackable.",
  },
  receipt: {
    iconName: "wallet",
    accent: "berry",
    title: "Refunds happen in Instacart",
    desc: "Wrong item? Damaged? Missing? Open the Instacart order, tap 'Get help' → 'Report an issue' within 14 days. Most refunds hit your card in 5–10 business days. Tap 'Pro tips' in the More menu for the full guide.",
  },
  // Event-triggered tips — not keyed to a tab visit. The triggering effects
  // sit alongside the tab-visit effect and gate on their own persisted flags
  // (cartiq_tooltip_trust_seen, cartiq_tooltip_price_memory_seen).
  trust: {
    iconName: "shield-check",
    accent: "forest",
    title: "Verified price",
    desc: "VERIFIED means CartIQ has price observations for this item. The chip shows where today's price sits in the recent range.",
  },
  priceMemory: {
    iconName: "trending-up",
    accent: "leaf",
    title: "Your price history",
    desc: "This sparkline is your own purchase history. Green when prices are coming down for you, red when climbing.",
  },
};

/* ═══════════════════════════════════════════════════════════
   PRO TIPS — durable knowledge that creates return users
   ═══════════════════════════════════════════════════════════
   These tips help users get more value out of every order
   and avoid the most common money-losing mistakes. Surfaced
   via the Help → Pro tips entry in the More menu, and
   contextually as first-visit tooltips where relevant.
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   PRO TIPS — Instacart how-to and policy reference
   ═══════════════════════════════════════════════════════════
   Each tip has a `verified` date in YYYY-MM format. This is the
   last time the specific numbers in the body (refund windows,
   fees, percentages, etc.) were checked against Instacart's live
   policy pages. The modal renders this as "Verified Apr 2026"
   under each card, and shows a stale warning if older than 6 months.

   When updating the body of a tip, update its `verified` field too.
   When ALL tips have been re-verified, also update LAST_TIPS_AUDIT
   below — the modal footer references it.
   ═══════════════════════════════════════════════════════════ */
const LAST_TIPS_AUDIT = "2026-04";  // YYYY-MM, shown in modal footer

const INSTACART_TIPS = [
  {
    id: "refunds",
    iconName: "wallet",
    accent: "berry",
    title: "How refunds & returns work",
    summary: "14 days to report. 5–10 days to refund. No need to return groceries.",
    verified: "2026-04",
    body: [
      { h: "When something's wrong with your order:", p: "Open the Instacart app → Your orders → tap the order → 'Get help' → 'Report an issue'. You have 14 days from delivery to report. For perishables, sooner is better." },
      { h: "What qualifies for a refund:", p: "Missing items, damaged or spoiled food, wrong items delivered, items that don't match the description, and orders that arrived very late or never arrived." },
      { h: "How long it takes:", p: "Most refunds are issued instantly as Instacart credit (usable on your next order) or 5–10 business days back to your original payment method. Larger disputes can take up to 30 days." },
      { h: "You don't return groceries:", p: "Unlike retail, Instacart doesn't ask you to return perishable items. Just report the issue with a photo if possible — they refund and you keep (or discard) the item." },
      { h: "Tip from CartIQ:", p: "Always check your delivery against the receipt within 1 hour of arrival. Cold items left out, missing produce, or quietly substituted brands are easier to dispute fresh than days later." },
    ],
  },
  {
    id: "delivery_window",
    iconName: "clock",
    accent: "basil",
    title: "Pick the cheapest delivery window",
    summary: "Mid-week, mid-day windows can save 30–50% on delivery fees alone.",
    verified: "2026-04",
    body: [
      { h: "Why timing matters:", p: "Instacart prices delivery by demand. Friday evening and Sunday morning are surge windows — fees and 'busy pricing' fees both spike. Tuesday/Wednesday between 10am–2pm is consistently the cheapest." },
      { h: "Watch for these fees:", p: "Standard delivery fee ($3.99–$7.99), service fee (~5% of subtotal), heavy items fee, and 'busy pricing' surge during peak hours. They stack." },
      { h: "Instacart+ membership math:", p: "$99/year ($9.99/month) waives delivery fees on $35+ orders and reduces service fees. Break-even is roughly 20 orders/year. If you order 2× a month or more, it pays for itself." },
      { h: "Tip from CartIQ:", p: "Schedule your delivery 1–3 days out instead of asap. Off-peak scheduled windows are almost always cheaper than 'within 2 hours' rush slots." },
    ],
  },
  {
    id: "tipping",
    iconName: "gift",
    accent: "apricot",
    title: "Tipping shoppers — what's standard",
    summary: "20% is the new norm. Tip in-app and adjust within 24 hours if needed.",
    verified: "2026-04",
    body: [
      { h: "What shoppers actually earn:", p: "The base pay Instacart pays its shoppers is small ($4–$10 per order). Tips are most of their take-home pay. A $40 grocery order with no tip means the shopper earned roughly $7 for an hour of work." },
      { h: "Standard amounts:", p: "5% is the platform default but considered low. 15–20% is normal. For large orders ($150+), heavy items, complicated lists, or bad weather, tip more." },
      { h: "Adjust the tip after delivery:", p: "You have 24 hours after delivery to increase or decrease the tip. Increase it for a great shopper who handled substitutions well or chose ripe produce. Reduce only for genuine problems (and consider reporting the issue separately instead)." },
      { h: "Tip from CartIQ:", p: "Tipping in cash on top of the in-app tip is a great gesture but doesn't replace the in-app tip — Instacart only counts the in-app amount toward the shopper's stats and rating thresholds." },
    ],
  },
  {
    id: "substitutions",
    iconName: "refresh",
    accent: "forest",
    title: "Make substitutions work for you",
    summary: "Set per-item rules in CartIQ so out-of-stock items don't wreck your trip.",
    verified: "2026-04",
    body: [
      { h: "The default behavior is risky:", p: "If you don't set substitution preferences, Instacart's default is 'shopper's best judgment'. That can mean a $9 organic alternative for a $4 conventional item — and you'll be charged the higher price." },
      { h: "Use CartIQ's per-item rules:", p: "Open Substitutions in More → Features. For every item, choose: 'closest brand', 'cheapest alternative', 'specific replacement I named', 'no substitution', or 'contact me first'." },
      { h: "Best strategy by category:", p: "Produce → 'no substitution' (you can taste the difference). Pantry staples → 'cheapest alternative' (a can of beans is a can of beans). Meat → 'contact me first' (cuts vary wildly). Dairy → 'closest brand, same size'." },
      { h: "Tip from CartIQ:", p: "Always enable in-app chat notifications during your delivery window. A 30-second reply to your shopper's question can save you $5+ on a single substitution." },
    ],
  },
];

// Format YYYY-MM as "Mon YYYY" (e.g., "2026-04" → "Apr 2026") for display.
// Centralized so all tip surfaces format dates consistently.
function formatVerifiedDate(yyyymm) {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-").map(Number);
  if (!y || !m) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[m - 1]} ${y}`;
}

// Returns true if the verified date is more than 6 months in the past.
// Used to flag stale tips with a small warning so users (and you) know
// the policy may have changed since the last audit.
function isVerifiedDateStale(yyyymm) {
  if (!yyyymm) return true;
  const [y, m] = yyyymm.split("-").map(Number);
  if (!y || !m) return true;
  const verifiedDate = new Date(y, m - 1, 1);
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  return verifiedDate < sixMonthsAgo;
}

// Resolve symbolic accent name → palette color pair.
// Used by tip cards to tint the icon chip + expanded section borders.
function tipAccentColors(name) {
  switch (name) {
    case "berry":   return { fg: T.berry,   soft: T.berrySoft };
    case "apricot": return { fg: T.apricot, soft: T.peachSoft };
    case "leaf":    return { fg: T.leaf,    soft: T.sageSoft };
    case "forest":  return { fg: T.forest,  soft: T.sageSoft };
    case "basil":
    default:        return { fg: T.basil,   soft: T.sageSoft };
  }
}

function TipsModal({ onClose }) {
  const [expanded, setExpanded] = useState(null);  // tip id currently open, or null
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.55)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",borderRadius:22,border:`1px solid ${T.hairline}`,boxShadow:"0 24px 80px rgba(0,0,0,.45), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>
        {/* Sticky header — title + lightweight close. Uses a forest-on-cream
            chip with the lightbulb icon to match the polished tab-header style
            used across Pantry, Watchlist, etc. */}
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",gap:12,position:"sticky",top:0,background:T.paper,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",zIndex:2}}>
          <div style={{width:36,height:36,borderRadius:11,background:`linear-gradient(135deg, ${T.basil}28, ${T.basil}10)`,border:`1px solid ${T.basil}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon name="sparkle" size={18} color={T.basil} stroke={2.4}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:19,fontWeight:700,color:T.forest,letterSpacing:-.3,lineHeight:1.1}}>Pro tips</div>
            <div style={{fontSize:11,color:T.ink50,marginTop:3,lineHeight:1.3}}>Get more out of every Instacart order</div>
          </div>
          <button onClick={onClose} aria-label="Close pro tips"
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink70,borderRadius:10,width:32,height:32,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon name="x" size={14} color={T.ink70} stroke={2.4}/>
          </button>
        </div>

        <div style={{padding:"14px 16px",display:"grid",gap:10}}>
          {INSTACART_TIPS.map(tip => {
            const open = expanded === tip.id;
            const acc = tipAccentColors(tip.accent);
            return (
              <Glass key={tip.id} style={{borderRadius:14,overflow:"hidden",border:open?`1px solid ${acc.fg}55`:`1px solid ${T.hairline}`,transition:"border-color .2s",background:open?`linear-gradient(180deg, ${acc.fg}06, ${T.paper})`:undefined}}>
                <button onClick={()=>{setExpanded(open ? null : tip.id);SFX.tabSwitch();}}
                  aria-expanded={open}
                  style={{width:"100%",background:"transparent",border:"none",padding:"14px 14px",textAlign:"left",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:42,height:42,borderRadius:12,background:`linear-gradient(135deg, ${acc.fg}22, ${acc.fg}0E)`,border:`1px solid ${acc.fg}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Icon name={tip.iconName} size={20} color={acc.fg} stroke={2.2}/>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest,letterSpacing:-.2,lineHeight:1.2}}>{tip.title}</div>
                    <div style={{fontSize:11.5,color:T.ink70,marginTop:3,lineHeight:1.45}}>{tip.summary}</div>
                  </div>
                  <Icon name="chevron-right" size={14} color={T.ink50} stroke={2.4} style={{transition:"transform .25s",transform:open?"rotate(90deg)":"rotate(0)",flexShrink:0}}/>
                </button>
                {open && (
                  <div style={{padding:"2px 16px 16px 16px",borderTop:`1px solid ${T.hairline}`,animation:"slideUp .25s ease both"}}>
                    {tip.body.map((section, i) => (
                      <div key={i} style={{marginTop:14}}>
                        <div style={{fontSize:10,fontWeight:800,color:acc.fg,marginBottom:5,letterSpacing:.7,textTransform:"uppercase"}}>{section.h}</div>
                        <div style={{fontSize:12.5,color:T.ink70,lineHeight:1.6}}>{section.p}</div>
                      </div>
                    ))}
                    {/* Per-tip verification timestamp. Stale tips (>6mo) get an
                        amber tint so users know the specific numbers may have
                        drifted. The tooltip explains what "verified" means. */}
                    {tip.verified && (() => {
                      const stale = isVerifiedDateStale(tip.verified);
                      return (
                        <div style={{marginTop:14,paddingTop:10,borderTop:`1px dashed ${T.hairline}`,display:"inline-flex",alignItems:"center",gap:6,fontSize:10,color:stale?T.warn:T.ink50,padding:"4px 9px",borderRadius:8,background:stale?T.butterSoft:T.parchment,border:`1px solid ${stale?T.butter:T.hairline}`}}
                          title="The numbers and policies in this tip were last checked against Instacart's published policy on this date. Always confirm current details on Instacart's help center.">
                          <Icon name={stale?"info":"check"} size={10} color={stale?T.warn:T.basil} stroke={2.6}/>
                          <span style={{fontWeight:700,letterSpacing:.2}}>
                            {stale ? "May be outdated · " : "Verified "}
                            {formatVerifiedDate(tip.verified)}
                          </span>
                          {stale && <span style={{opacity:.8}}>· check Instacart</span>}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </Glass>
            );
          })}
        </div>
        {/* Modal footer — appears once at the bottom regardless of which tip
            is expanded. Communicates two things: (1) we last audited the policy
            details on a specific date, and (2) a direct link to Instacart's
            current help so users can verify anything they're unsure about. */}
        <div style={{padding:"14px 16px 18px 16px",borderTop:`1px solid ${T.hairline}`,display:"grid",gap:10,background:T.parchment}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,fontSize:11,color:T.ink70}}>
            <Icon name="calendar" size={11} color={T.ink70} stroke={2.4}/>
            <span>Last full policy audit: <span style={{color:T.basil,fontWeight:800}}>{formatVerifiedDate(LAST_TIPS_AUDIT)}</span></span>
          </div>
          <a href="https://www.instacart.com/help" target="_blank" rel="noopener noreferrer"
            style={{textDecoration:"none",display:"flex",alignItems:"center",justifyContent:"center",gap:7,padding:"10px 14px",background:T.paper,border:`1px solid ${T.sage}`,borderRadius:10,color:T.basil,fontSize:12,fontWeight:800,fontFamily:"inherit",letterSpacing:.2}}>
            <span>Check Instacart's current policy</span>
            <Icon name="arrow-right" size={12} color={T.basil} stroke={2.4}/>
          </a>
          <div style={{fontSize:10,color:T.ink50,textAlign:"center",lineHeight:1.5}}>
            CartIQ is independent and not affiliated with Instacart. Fees, refund windows, and policies described here may change — always confirm current details on Instacart's help center before relying on them.
          </div>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SOUND SETUP MODAL — toggle sound effects on/off
   ═══════════════════════════════════════════════════════════
   Tiny modal accessed from More → Help → Sound effects. Lets users
   silence the app's audio cues without losing any other functionality.
   The "Test sound" button plays a short success cue using the current
   live setting — so flipping the toggle and pressing Test confirms
   the change took effect immediately.
   ═══════════════════════════════════════════════════════════ */
function FeedbackSetupModal({ soundValue, hapticsValue, onSaveSound, onSaveHaptics, onClose }) {
  // Local drafts for both toggles. Committed together when the user hits Save,
  // discarded if they tap the X or backdrop. This avoids storage churn on flips.
  const [soundDraft, setSoundDraft] = useState(soundValue);
  const [hapticsDraft, setHapticsDraft] = useState(hapticsValue);
  const commit = () => { onSaveSound(soundDraft); onSaveHaptics(hapticsDraft); onClose(); };
  // Detect haptic support up front so we can show an iOS-aware note. The
  // `vibrate` API exists on Safari iOS as a no-op — present in navigator but
  // does nothing — so feature-detection alone isn't enough. Userscript-friendly
  // sniff: if the platform is iOS, surface the iOS note.
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const hasVibrate = typeof navigator !== "undefined" && "vibrate" in navigator;

  // A small reusable two-card On/Off picker. Saves us repeating styles.
  const Picker = ({ value, onChange, onLabel, offLabel, onIcon, offIcon, onColor = T.leaf, offColor = T.warn }) => (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
      <button onClick={()=>onChange(true)}
        aria-pressed={value===true}
        style={{padding:"16px 12px",borderRadius:14,border:value===true?`2px solid ${onColor}`:`1px solid ${T.hairline}`,background:value===true?`linear-gradient(135deg,${onColor}22,${onColor}10)`:T.parchment,color:T.forest,cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:6,transition:"all .18s"}}>
        <span style={{fontSize:28}}>{onIcon}</span>
        <span style={{fontSize:13,fontWeight:800}}>{onLabel}</span>
      </button>
      <button onClick={()=>onChange(false)}
        aria-pressed={value===false}
        style={{padding:"16px 12px",borderRadius:14,border:value===false?`2px solid ${offColor}`:`1px solid ${T.hairline}`,background:value===false?`linear-gradient(135deg,${offColor}22,${offColor}10)`:T.parchment,color:T.forest,cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:6,transition:"all .18s"}}>
        <span style={{fontSize:28}}>{offIcon}</span>
        <span style={{fontSize:13,fontWeight:800}}>{offLabel}</span>
      </button>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.55)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Glass style={{width:"100%",maxWidth:440,maxHeight:"90vh",overflowY:"auto",borderRadius:22,border:`1px solid ${T.sage}`,boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 50px rgba(30,53,40,.10)",animation:"slideUp .3s ease both"}}>
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:T.paper,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",zIndex:2}}>
          <div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest}}>Feedback settings</div>
            <div style={{fontSize:11,color:T.ink50,marginTop:2}}>Control how CartIQ confirms your actions</div>
          </div>
          <button onClick={onClose} aria-label="Close feedback settings" style={{background:T.parchment,border:"none",color:T.ink50,borderRadius:10,width:32,height:32,cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>✕</button>
        </div>
        <div style={{padding:"20px 22px",display:"grid",gap:22}}>

          {/* SOUND SECTION */}
          <div style={{display:"grid",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:18}}>{soundDraft ? "🔊" : "🔇"}</span>
              <span style={{fontSize:14,fontWeight:800,color:T.forest}}>Sound effects</span>
            </div>
            <Picker
              value={soundDraft}
              onChange={setSoundDraft}
              onLabel="On" offLabel="Off"
              onIcon="🔊" offIcon="🔇"
            />
            {/* Test button — temporarily flips the live audio gate to ON,
                plays the tone, then restores. Async-safe because playTone is
                fire-and-forget. We restore to the *committed* value (not draft)
                so test never accidentally enables sound after Save. */}
            <button onClick={()=>{ if (!soundDraft) return; setSoundEnabled(true); SFX.success(); setTimeout(()=>setSoundEnabled(soundValue), 600); }}
              disabled={!soundDraft}
              style={{background:soundDraft?T.sageSoft:T.parchment,border:soundDraft?`1px solid ${T.sage}`:`1px solid ${T.hairline}`,color:soundDraft?T.leaf:T.ink50,borderRadius:12,padding:"10px 14px",cursor:soundDraft?"pointer":"not-allowed",fontFamily:"inherit",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <span>🎵</span><span>{soundDraft ? "Test sound" : "Sounds off — nothing to test"}</span>
            </button>
            <div style={{fontSize:11,color:T.ink50,lineHeight:1.5}}>
              Tones for taps, adds, alerts, and confirmations. Turning these off silences all CartIQ sounds — system notifications and your phone's ringer are unaffected.
            </div>
          </div>

          {/* Divider */}
          <div style={{height:1,background:T.parchment}}/>

          {/* HAPTICS SECTION */}
          <div style={{display:"grid",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:18}}>{hapticsDraft ? "📳" : "🚫"}</span>
              <span style={{fontSize:14,fontWeight:800,color:T.forest}}>Haptic feedback</span>
            </div>
            <Picker
              value={hapticsDraft}
              onChange={setHapticsDraft}
              onLabel="On" offLabel="Off"
              onIcon="📳" offIcon="🚫"
            />
            {/* Test button — fires hapticTap directly. We bypass the live gate
                by calling navigator.vibrate ourselves so the user can feel
                what's being toggled regardless of current saved state. */}
            <button onClick={()=>{ if (!hapticsDraft) return; if (typeof navigator !== "undefined" && "vibrate" in navigator) { try { navigator.vibrate(30); } catch(e) {} } }}
              disabled={!hapticsDraft || !hasVibrate}
              style={{background:(hapticsDraft && hasVibrate)?T.sageSoft:T.parchment,border:(hapticsDraft && hasVibrate)?`1px solid ${T.sage}`:`1px solid ${T.hairline}`,color:(hapticsDraft && hasVibrate)?T.leaf:T.ink50,borderRadius:12,padding:"10px 14px",cursor:(hapticsDraft && hasVibrate)?"pointer":"not-allowed",fontFamily:"inherit",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <span>📳</span><span>{!hapticsDraft ? "Haptics off — nothing to test" : !hasVibrate ? "Not supported on this browser" : "Test vibration"}</span>
            </button>
            <div style={{fontSize:11,color:T.ink50,lineHeight:1.5}}>
              Light vibrations on item swipes, Shopping Mode check-offs, and other physical confirmations. {isIOS && (<span style={{color:T.warn}}>iPhone note: Apple disables web haptics in Safari, so this setting has no effect on iOS regardless. Add CartIQ to your home screen for the best experience.</span>)}
            </div>
          </div>

          <button onClick={commit}
            style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:12,padding:"14px",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:800,letterSpacing:.3,boxShadow:"0 6px 20px rgba(30,53,40,.18)"}}>
            Save preferences
          </button>
        </div>
      </Glass>
    </div>
  );
}

function FirstVisitTooltip({ tip, onDismiss }) {
  if (!tip) return null;
  const acc = tipAccentColors(tip.accent || "basil");
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.50)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:880,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:16,paddingBottom:100,animation:"fadeIn .2s ease"}} onClick={onDismiss}>
      <Glass style={{width:"100%",maxWidth:400,padding:"20px 22px",borderRadius:20,border:`1px solid ${acc.fg}55`,boxShadow:"0 16px 50px rgba(0,0,0,.35), 0 0 32px rgba(30,53,40,.10)",animation:"slideUp .25s ease both",background:`linear-gradient(180deg, ${acc.fg}08, ${T.paper})`}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"flex-start",gap:13}}>
          <div style={{width:44,height:44,borderRadius:13,background:`linear-gradient(135deg, ${acc.fg}22, ${acc.fg}0E)`,border:`1px solid ${acc.fg}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon name={tip.iconName || "info"} size={22} color={acc.fg} stroke={2.2}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:9.5,letterSpacing:1.4,color:acc.fg,fontWeight:800,marginBottom:5,textTransform:"uppercase"}}>
              <span style={{width:5,height:5,borderRadius:"50%",background:acc.fg}}/>
              <span>Quick tip</span>
            </div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest,letterSpacing:-.2,marginBottom:6,lineHeight:1.2}}>{tip.title}</div>
            <div style={{fontSize:12.5,color:T.ink70,lineHeight:1.6}}>{tip.desc}</div>
          </div>
        </div>
        <div style={{marginTop:14,display:"flex",justifyContent:"flex-end"}}>
          <button onClick={onDismiss}
            style={{background:T.forest,border:"none",color:T.paper,borderRadius:10,padding:"9px 20px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 14px rgba(30,53,40,.18)",letterSpacing:.2,display:"inline-flex",alignItems:"center",gap:5}}>
            <span>Got it</span>
            <Icon name="check" size={12} color={T.paper} stroke={2.6}/>
          </button>
        </div>
      </Glass>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   GOOGLE SEARCH BUTTON — with dropdown for search type
   ═══════════════════════════════════════════════════════════ */
const GOOGLE_SEARCH_TYPES = [
  { id: "general", label: "Web Search",   icon: "🔎", suffix: "" },
  { id: "recipes", label: "Recipes",      icon: "🍴", suffix: " recipes" },
  { id: "nutrition", label: "Nutrition",  icon: "🥗", suffix: " nutrition facts" },
  { id: "reviews", label: "Reviews",      icon: "⭐", suffix: " reviews" },
  { id: "compare", label: "Price Compare",icon: "💵", suffix: " price comparison" },
];

function GoogleSearchButton({ query, size = "small", placement = "above" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function runSearch(suffix) {
    const url = `https://www.google.com/search?q=${encodeURIComponent(query + suffix)}`;
    window.open(url, "_blank", "noopener");
    SFX.tabSwitch();
    setOpen(false);
  }

  const isSmall = size === "small";

  return (
    <div ref={ref} style={{position:"relative",flexShrink:0}}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label={`Google search options for ${query}`}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          background: "rgba(66,133,244,.12)",
          border: "1px solid rgba(66,133,244,.3)",
          borderRadius: isSmall ? 9 : 10,
          padding: isSmall ? "6px 10px" : "8px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontFamily: "inherit",
          fontSize: isSmall ? 10 : 11,
          fontWeight: 700,
          color: T.basil,
        }}>
        <span style={{fontSize: isSmall ? 11 : 13}} aria-hidden="true">🔎</span> Google
        <span style={{fontSize:10,marginLeft:2,opacity:.7}} aria-hidden="true">▾</span>
      </button>
      {open && (
        <div role="menu" style={{
          position:"absolute",
          [placement === "above" ? "bottom" : "top"]: "calc(100% + 6px)",
          right: 0,
          background:T.paper,
          backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",
          border:"1px solid rgba(66,133,244,.3)",
          borderRadius:12,
          padding:6,
          minWidth:160,
          boxShadow:"0 12px 36px rgba(0,0,0,.7), 0 0 24px rgba(66,133,244,.15)",
          zIndex:200,
          animation:"fadeIn .15s ease",
        }}>
          {GOOGLE_SEARCH_TYPES.map(t => (
            <button key={t.id} role="menuitem" onClick={(e)=>{e.stopPropagation();runSearch(t.suffix);}}
              style={{
                width:"100%",
                background:"transparent",
                border:"none",
                color:T.forest,
                padding:"8px 10px",
                fontSize:11,
                fontWeight:600,
                cursor:"pointer",
                fontFamily:"inherit",
                display:"flex",
                alignItems:"center",
                gap:8,
                borderRadius:8,
                textAlign:"left",
                transition:"background .12s",
              }}
              onMouseEnter={(e)=>e.currentTarget.style.background="rgba(66,133,244,.1)"}
              onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}>
              <span style={{fontSize:13}} aria-hidden="true">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DEAL CARD — with optional Instacart product rating
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   DEAL CARD ACTIONS (Phase 3)
   ═══════════════════════════════════════════════════════════
   The unified action row shown on every DealCard in the app.
   Layout: Option B — primary Smart Cart button + secondary row of
   Pantry, Watchlist, Ask Sage.

   Replaces the old checkbox-toggle + bookmark-bell + big-ADD pattern
   with a consistent four-action surface visible on every DealCard.

   Store-specific semantics preserved:
     - Smart Cart: adds this store's version of the item
     - Watchlist: watches this store + item combo
     - Pantry: adds canonical item (store-agnostic, no duplication by store)
     - Ask Sage: asks about this store's deal specifically

   Props:
     - deal, store: the deal-shaped object and its store
     - isInCart, isInPantry, isInWatchlist: boolean state for disabled UI
     - onSmartCart, onPantry, onWatchlist, onAsk: handler callbacks
   ═══════════════════════════════════════════════════════════ */
const DealCardActions = memo(function DealCardActions({
  deal, store,
  isInCart, isInPantry, isInWatchlist,
  onSmartCart, onPantry, onWatchlist, onAsk,
}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:10}}>
      {/* PRIMARY — full-width Smart Cart button. Matches the old +ADD visual
          so muscle memory still lands on a green pill here. Uses the store's
          accent color in the non-cart state for visual variety across stores. */}
      <RippleBtn
        sound={isInCart ? "removeCart" : "addCart"}
        onClick={onSmartCart}
        style={{
          width:"100%",
          background: isInCart ? T.sageSoft : T.forest,
          border: `1px solid ${isInCart ? T.sage : T.forest}`,
          color: isInCart ? T.basil : T.paper,
          borderRadius:12,
          padding:"11px 14px",
          fontSize:13,fontWeight:600,
          boxShadow: isInCart ? "none" : T.shadowSoft,
          display:"flex",alignItems:"center",justifyContent:"center",gap:8,
          letterSpacing:.3,
          cursor: "pointer",
        }}
      >
        <span style={{fontSize:15}}>🛒</span>
        <span>{isInCart ? "✕ Remove from Cart" : "Add to Smart Cart"}</span>
      </RippleBtn>

      <div style={{display:"flex",gap:6}}>
        <button
          onClick={onPantry}
          aria-label={isInPantry ? "Remove from pantry" : "Add to Pantry"}
          style={{
            flex:1,padding:"8px 6px",
            background: isInPantry ? T.parchment : T.cream,
            border: `1px solid ${isInPantry ? T.sage : T.hairline}`,
            color: isInPantry ? T.basil : T.ink70,
            borderRadius:10,cursor:"pointer",fontFamily:"inherit",
            fontSize:10,fontWeight:600,letterSpacing:.3,
            display:"flex",alignItems:"center",justifyContent:"center",gap:5,
          }}
        >
          <span style={{fontSize:13}}>🫙</span>
          <span>{isInPantry ? "✓ Pantry" : "Pantry"}</span>
        </button>
        <button
          onClick={onWatchlist}
          aria-label={isInWatchlist ? "Remove from watchlist" : "Add to Watchlist"}
          style={{
            flex:1,padding:"8px 6px",
            background: isInWatchlist ? T.butterSoft : T.cream,
            border: `1px solid ${isInWatchlist ? T.butter : T.hairline}`,
            color: isInWatchlist ? T.warn : T.ink70,
            borderRadius:10,cursor:"pointer",fontFamily:"inherit",
            fontSize:10,fontWeight:600,letterSpacing:.3,
            display:"flex",alignItems:"center",justifyContent:"center",gap:5,
          }}
        >
          <span style={{fontSize:13}}>📋</span>
          <span>{isInWatchlist ? "✓ Watching" : "+ Watch"}</span>
        </button>
        {onAsk && (
          <button
            onClick={(e) => { e.stopPropagation(); onAsk({deal, store}); SFX.tabSwitch(); }}
            aria-label="Ask Sage about this deal"
            style={{
              flex:1,padding:"8px 6px",
              background:T.sageSoft,
              border:`1px solid ${T.sage}`,
              color:T.basil,
              borderRadius:10,cursor:"pointer",fontFamily:"inherit",
              fontSize:10,fontWeight:600,letterSpacing:.3,
              display:"flex",alignItems:"center",justifyContent:"center",gap:5,
            }}
          >
            <span style={{fontSize:13}}>🌱</span>
            <span>Ask</span>
          </button>
        )}
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   REBUILD CART CARD — Phase C dashboard surface
   ═══════════════════════════════════════════════════════════
   Two render modes:
     - hasStaples=true  → primary action "Build my cart" with
       item count, store breakdown, and estimated savings preview
     - hasStaples=false → secondary CTA "Set up your staples first"
       which opens the staples picker

   Hidden when REBUILD_CART_ENABLED is false or when the cart is
   already non-trivially populated (3+ items) — at that point the
   user is already in their workflow and doesn't need this prompt.
   ═══════════════════════════════════════════════════════════ */
function RebuildCartCard({ hasStaples, preview, onRebuild, onSetupStaples }) {
  if (!REBUILD_CART_ENABLED) return null;

  if (!hasStaples) {
    return (
      <Glass style={{padding:"16px 18px",borderRadius:18,border:`1px solid ${T.plum}33`,marginBottom:14,background:`linear-gradient(135deg, ${T.plumSoft}33, ${T.parchment})`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <Icon name="cart" size={22} color={T.plum} stroke={2.2}/>
          <div style={{flex:1}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.forest}}>
              Build a smart cart in one tap
            </div>
            <div style={{fontSize:11,color:T.ink50,marginTop:2,lineHeight:1.4}}>
              Tell us what you usually buy and we'll route every item to the cheapest store automatically.
            </div>
          </div>
        </div>
        <RippleBtn sound="tabSwitch" onClick={onSetupStaples}
          style={{width:"100%",background:T.plumSoft,border:`1px solid ${T.plum}66`,color:T.plum,borderRadius:11,padding:"10px",fontSize:12,fontWeight:800}}>
          Set up your staples →
        </RippleBtn>
      </Glass>
    );
  }

  if (!preview || preview.itemCount === 0) {
    return (
      <Glass style={{padding:"16px 18px",borderRadius:18,border:`1px solid ${T.butter}`,marginBottom:14,background:`linear-gradient(135deg, ${T.butterSoft}, ${T.parchment})`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <div style={{fontSize:22}}>🍂</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.forest}}>
              No deals match your staples right now
            </div>
            <div style={{fontSize:11,color:T.ink50,marginTop:2,lineHeight:1.4}}>
              Stores rotate weekly — check back soon, or adjust your staples to match what's on sale.
            </div>
          </div>
        </div>
        <RippleBtn sound="tabSwitch" onClick={onSetupStaples}
          style={{width:"100%",background:T.parchment,border:`1px solid ${T.butter}`,color:T.warn,borderRadius:11,padding:"10px",fontSize:12,fontWeight:800}}>
          Adjust staples →
        </RippleBtn>
      </Glass>
    );
  }

  const { itemCount, estimatedSavings, storeBreakdown } = preview;
  const storeNames = Object.entries(storeBreakdown || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([sid, n]) => `${sid} (${n})`)
    .join(", ");

  return (
    <Glass style={{padding:"16px 18px",borderRadius:18,border:`1px solid ${T.sage}`,marginBottom:14,background:`linear-gradient(135deg, ${T.sageSoft}, ${T.parchment})`,boxShadow:T.shadowSoft}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
        <div style={{fontSize:22}}>🛒</div>
        <div style={{flex:1}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.forest}}>
            Rebuild your usual list
          </div>
          <div style={{fontSize:11,color:T.ink50,marginTop:2,lineHeight:1.4}}>
            {itemCount} staple{itemCount === 1 ? "" : "s"} · routed across {storeNames || "your stores"}
          </div>
        </div>
      </div>
      {estimatedSavings > 0.01 && (
        <div style={{fontSize:11,color:T.basil,marginBottom:10,fontWeight:700}}>
          ~${estimatedSavings.toFixed(2)} saved vs worst-priced store
        </div>
      )}
      <RippleBtn sound="success" onClick={onRebuild}
        style={{width:"100%",background:T.forest,border:"none",color:T.paper,borderRadius:11,padding:"11px",fontSize:13,fontWeight:800,boxShadow:T.shadowFloat}}>
        Build my cart →
      </RippleBtn>
    </Glass>
  );
}

const DealCard = memo(function DealCard({ deal, di, store, inCart: ic, onToggle, isWatched, onToggleWatch, onAsk, onAddToPantry, isInPantry }) {
  const productRating = useProductRating(deal.instacartProductId);
  const hist = PRICE_HISTORY[deal.id];
  const insight = (() => {
    const h = PRICE_HISTORY[deal.id]; if (!h || h.length < 3) return null;
    const hst = h.slice(0,-1), avg = hst.reduce((s,v)=>s+v,0)/hst.length, low = Math.min(...hst), cur = deal.salePrice || deal.origPrice;
    if (cur <= low*1.05) return {type:"low",msg:"All-time low!"};
    if (cur <= avg*.80)  return {type:"great",msg:`${Math.round((1-cur/avg)*100)}% below avg`};
    if (cur >= avg*.95)  return {type:"warn",msg:"Near regular price"};
    return null;
  })();
  const catTint = CAT_TINT[deal.category] || "transparent";

  return (
    <Glass className="deal-card anim-slide-up" style={{"--i":di,padding:0,overflow:"hidden",borderRadius:16,borderLeft:`3px solid ${deal.expiresIn<=1?T.berry:deal.expiresIn<=3?T.apricot:T.basil}`}}>
      <div style={{padding:16,background:T.paper}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
          <Pill label={deal.dealLabel}/>
          {deal.expiresIn<=1 ? <CountdownTimer hours={deal.expiresIn*8}/> : <UrgBadge days={deal.expiresIn}/>}
          <StockDot level={deal.stockLevel}/>
          <TrustBadge itemId={deal.id} currentPrice={deal.salePrice || deal.origPrice} compact/>
        </div>
        <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:6}}>
          <ProductImage item={{id:deal.id,name:deal.name,category:deal.category,instacartProductId:deal.instacartProductId}} size={48}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
              <span style={{fontFamily:FONT_SANS,fontSize:14,fontWeight:600,color:T.forest}}>{deal.name}</span>
              {isOrganic(deal.name)&&<OrgBadge small/>}
            </div>
            {productRating && productRating.rating != null && (
              <div style={{display:"flex",alignItems:"center",gap:5,marginTop:3}}>
                <StarRating rating={productRating.rating} size={10}/>
                <span style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50}}>({productRating.reviewCount} reviews)</span>
              </div>
            )}
          </div>
        </div>
        <ShrinkflationChip dealId={deal.id}/>
        {insight&&<div style={{background:insight.type==="warn"?T.butterSoft:T.sageSoft,border:`1px solid ${insight.type==="warn"?T.butter:T.sage}`,borderRadius:8,padding:"4px 10px",marginBottom:8,fontFamily:FONT_SANS,fontSize:10,color:insight.type==="warn"?T.warn:T.basil,fontWeight:600}}>{insight.type==="low"?"🏆 All-time low":insight.type==="great"?`📉 ${insight.msg}`:`⚠️ ${insight.msg}`}</div>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:10}}>
          <div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:28,fontWeight:400,color:T.basil,lineHeight:1}}>−${deal.savings.toFixed(2)}</div>
            <div style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50,marginTop:3}}>{deal.salePrice===0?`Free w/ 1 @ $${deal.origPrice}`:`$${deal.origPrice} → $${deal.salePrice.toFixed(2)}`}</div>
            <UnitPriceLine deal={deal} compact={false}/>
            {deal.ibotta>0&&<div style={{fontFamily:FONT_SANS,fontSize:10,color:T.basil,marginTop:1}}>+${deal.ibotta.toFixed(2)} Ibotta</div>}
          </div>
          {hist&&<Sparkline data={hist}/>}
        </div>
        {deal.perfectTime && (
          <div style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50,marginBottom:4}} title="Suggested shopping window based on typical restock and delivery patterns">
            💡 Try <span style={{color:T.leaf,fontWeight:600}}>{deal.perfectTime}</span>
          </div>
        )}
        <DealCardActions
          deal={deal}
          store={store}
          isInCart={ic}
          isInPantry={isInPantry}
          isInWatchlist={isWatched}
          onSmartCart={onToggle}
          onPantry={onAddToPantry}
          onWatchlist={onToggleWatch}
          onAsk={onAsk}
        />
      </div>
    </Glass>
  );
});

/* ═══════════════════════════════════════════════════════════
   STORE CARD — Dashboard grid item with live Google rating
   ═══════════════════════════════════════════════════════════ */
const StoreCard = memo(function StoreCard({ store, si, isWin, cartCount, onOpen }) {
  const { data: rating } = useStoreRating(store.placeId);
  const raw = store.deals.reduce((s,d)=>s+d.savings,0);
  const net = netBenefit(store);
  return (
    <Glass className="deal-card anim-slide-up" onClick={onOpen}
      style={{"--i":si*1.5,cursor:"pointer",overflow:"hidden",borderRadius:20,padding:0,border:isWin?`2px solid ${T.butter}`:`1px solid ${T.hairline}`,boxShadow:isWin?T.shadowFloat:T.shadowCard}}>
      {isWin&&<div style={{background:`linear-gradient(90deg,${T.butter},${T.peach},${T.butter})`,backgroundSize:"200% auto",animation:"goldShine 3s linear infinite",padding:"4px",fontFamily:FONT_SANS,fontSize:10,fontWeight:700,letterSpacing:2,color:T.forest,textAlign:"center"}}>🏆 BEST VALUE</div>}
      <div style={{position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,background:`linear-gradient(135deg,${store.color}22,${store.glow}12)`}}/>
        <div style={{position:"relative",padding:"16px 16px 8px",display:"flex",alignItems:"center",gap:11}}>
          <div style={{width:44,height:44,borderRadius:12,background:`${store.color}20`,border:`1px solid ${store.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{store.logo}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:400,color:T.forest}}>{store.name}</div>
            <div style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50,marginTop:1}}>{store.tagline}</div>
            {rating && rating.rating != null && (
              <div style={{marginTop:4,display:"flex",alignItems:"center",gap:5}}>
                <StarRating rating={rating.rating} size={10}/>
                <span style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50}}>({rating.reviewCount.toLocaleString()})</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{padding:"8px 16px 12px",textAlign:"center"}}>
        <div style={{fontFamily:FONT_DISPLAY,fontSize:38,fontWeight:400,color:T.basil,letterSpacing:-1,lineHeight:1}}>${raw.toFixed(2)}</div>
        <div style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50,marginTop:4}}>Net: <span style={{color:net>0?T.basil:T.berry,fontWeight:600}}>{net>0?"+":""}${net.toFixed(2)}</span> after delivery</div>
      </div>
      <div style={{padding:"0 12px 10px",display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center"}}>
        {store.deals.slice(0,3).map((d,i)=><Pill key={i} label={d.dealLabel}/>)}
      </div>
      <div style={{borderTop:`1px solid ${T.hairline}`,padding:"8px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontFamily:FONT_SANS,fontSize:10,color:T.ink50,fontWeight:600}}>{store.deals.length} deals</span>
        {cartCount>0&&<span style={{fontFamily:FONT_SANS,fontSize:10,color:T.basil,fontWeight:700}}>🛒 {cartCount}</span>}
        <span style={{color:T.basil,fontSize:12}}>→</span>
      </div>
    </Glass>
  );
});

/* ═══════════════════════════════════════════════════════════
   STORE DETAIL PAGE — #1 Full-screen takeover
   ═══════════════════════════════════════════════════════════ */
const StoreDetailPage = memo(function StoreDetailPage({ store, allDeals, cartMap, toggleCart, inCart, cartCount, openCheckout, onBack, catFilter, setCatFilter, isWatched, onToggleWatch, onAsk, onOpenSearch, onAddToPantry, isInPantry }) {
  const storeDeals = store.deals;
  const categories = [...new Set(storeDeals.map(d=>d.category))];
  const filtered = catFilter ? storeDeals.filter(d=>d.category===catFilter) : storeDeals;
  const gross = storeDeals.reduce((s,d)=>s+d.savings,0);
  const net = netBenefit(store);
  const cc = cartCount(store.id);
  const { data: rating, loading: ratingLoading } = useStoreRating(store.placeId);

  return (
    <div style={{animation:"pageIn .4s ease both"}}>
      {/* ── Top nav row — back + title chip + search ── */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
        <button onClick={onBack}
          aria-label="Back to dashboard"
          style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink70,borderRadius:10,width:38,height:38,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s ease"}}
          onMouseEnter={e=>e.currentTarget.style.borderColor=T.basil}
          onMouseLeave={e=>e.currentTarget.style.borderColor=T.hairline}>
          <Icon name="chevron-left" size={18} color={T.ink70} stroke={2.4}/>
        </button>
        <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:9,padding:"7px 12px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10}}>
          <div style={{width:24,height:24,borderRadius:7,background:store.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 0 8px ${store.glow}40`}}>
            <ItemIcon emoji={store.logo} size={14}/>
          </div>
          <span style={{fontSize:12,color:T.ink50,fontWeight:700,letterSpacing:.3,textTransform:"uppercase",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {store.name}
          </span>
        </div>
        {onOpenSearch && (
          <button onClick={onOpenSearch}
            aria-label={`Search ${store.name}`}
            title={`Search ${store.name}`}
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink70,borderRadius:10,width:38,height:38,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",flexShrink:0,transition:"all .15s ease"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=T.basil}
            onMouseLeave={e=>e.currentTarget.style.borderColor=T.hairline}>
            <Icon name="search" size={17} color={T.ink70} stroke={2.2}/>
          </button>
        )}
      </div>

      {/* ── Store hero — light, on-brand, NET BENEFIT as headline ── */}
      <div style={{
        position:"relative",overflow:"hidden",
        padding:"20px 22px",marginBottom:14,borderRadius:20,
        background:`
          radial-gradient(120% 80% at 100% 0%, ${store.glow}33 0%, transparent 55%),
          radial-gradient(80% 60% at 0% 100%, ${T.sageSoft}88 0%, transparent 50%),
          linear-gradient(135deg, ${T.parchment} 0%, ${T.paper} 100%)
        `,
        border:`1px solid ${store.color}33`,
        boxShadow:T.shadowFloat,
      }}>
        {/* Header row: store chip + name + rating */}
        <div style={{display:"flex",alignItems:"center",gap:13,marginBottom:16,position:"relative"}}>
          <div style={{width:48,height:48,borderRadius:13,background:store.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 4px 14px ${store.glow}55`,border:`1px solid ${store.color}66`}}>
            <ItemIcon emoji={store.logo} size={26}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:24,fontWeight:700,color:T.forest,letterSpacing:-.5,lineHeight:1}}>{store.name}</div>
            <div style={{fontSize:11.5,color:T.ink50,marginTop:4,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{store.tagline}</div>
            {rating && rating.rating != null && (
              <div style={{marginTop:7,display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                <StarRating rating={rating.rating} size={12}/>
                <span style={{fontSize:10.5,color:T.ink50,fontWeight:600}}>{rating.reviewCount.toLocaleString()} reviews</span>
                {rating.isOpen != null && (
                  <span style={{fontSize:9.5,fontWeight:800,letterSpacing:.5,color:rating.isOpen?T.basil:T.berry,background:rating.isOpen?`${T.basil}14`:`${T.berry}14`,border:`1px solid ${rating.isOpen?T.basil:T.berry}55`,borderRadius:5,padding:"2px 7px",textTransform:"uppercase"}}>
                    {rating.isOpen?"Open":"Closed"}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Headline — NET BENEFIT */}
        <div style={{position:"relative",marginBottom:14}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:6,marginBottom:6}}>
            <Icon name="bolt" size={13} color={store.color} stroke={2.4}/>
            <span style={{fontSize:10,letterSpacing:2,color:store.color,fontWeight:800,textTransform:"uppercase"}}>Net benefit</span>
          </div>
          <div style={{display:"flex",alignItems:"baseline",gap:10}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:42,fontWeight:700,color:T.forest,letterSpacing:-1.2,lineHeight:.95}}>
              {net>0?"+":""}${Math.abs(net).toFixed(2)}
            </div>
            <div style={{fontSize:11,color:T.ink50,fontWeight:700,letterSpacing:.3}}>after delivery</div>
          </div>
        </div>

        {/* Sub-stats — 3-up clean strip */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,position:"relative"}}>
          <div style={{padding:"10px 12px",background:"rgba(255,255,255,.65)",border:`1px solid ${T.hairline}`,borderRadius:11}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.basil,lineHeight:1,letterSpacing:-.3}}>${gross.toFixed(2)}</div>
            <div style={{fontSize:9.5,letterSpacing:1.2,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Deal savings</div>
          </div>
          <div style={{padding:"10px 12px",background:"rgba(255,255,255,.65)",border:`1px solid ${T.hairline}`,borderRadius:11}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.apricot,lineHeight:1,letterSpacing:-.3}}>${store.deliveryFee.toFixed(2)}</div>
            <div style={{fontSize:9.5,letterSpacing:1.2,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Delivery</div>
          </div>
          <div style={{padding:"10px 12px",background:"rgba(255,255,255,.65)",border:`1px solid ${T.hairline}`,borderRadius:11}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest,lineHeight:1,letterSpacing:-.3}}>{cc}</div>
            <div style={{fontSize:9.5,letterSpacing:1.2,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>In cart</div>
          </div>
        </div>

        {/* Delivery note pill */}
        {store.deliveryNote && (
          <div style={{marginTop:11,paddingTop:11,borderTop:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",gap:6,position:"relative"}}>
            <Icon name="truck" size={13} color={T.ink50} stroke={2.2}/>
            <span style={{fontSize:11,color:T.ink50,fontWeight:600}}>{store.deliveryNote}</span>
          </div>
        )}
      </div>

      {/* Google Reviews panel */}
      {rating && rating.reviews && rating.reviews.length > 0 && (
        <Glass style={{padding:"14px 16px",marginBottom:14,borderRadius:14,border:`1px solid ${T.hairline}`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:11}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <Icon name="star" size={13} color={T.apricot} stroke={2.2}/>
              <span style={{fontSize:10,fontWeight:800,color:T.forest,letterSpacing:.6,textTransform:"uppercase"}}>Recent reviews</span>
              <span style={{fontSize:9.5,color:T.ink50,fontWeight:600,letterSpacing:.3}}>via Google</span>
            </div>
            <StarRating rating={rating.rating} size={11}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(250px, 1fr))",gap:10}}>
            {rating.reviews.map((rv,i)=>(
              <div key={i} style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,padding:"10px 12px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <div style={{width:26,height:26,borderRadius:"50%",background:`linear-gradient(135deg,${T.basil},${T.forest})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:T.paper,flexShrink:0}}>
                    {rv.author?.charAt(0) || "?"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rv.author}</div>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <StarRating rating={rv.rating} size={9} showNumber={false}/>
                      <span style={{fontSize:10,color:T.ink50}}>{rv.relativeTime}</span>
                    </div>
                  </div>
                </div>
                <div style={{fontSize:11,color:T.ink50,lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:4,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{rv.text}</div>
              </div>
            ))}
          </div>
        </Glass>
      )}

      {/* Loading placeholder for reviews */}
      {ratingLoading && !rating && (
        <div style={{padding:"10px 14px",marginBottom:14,fontSize:11,color:T.ink50,textAlign:"center"}}>Loading store reviews…</div>
      )}

      {/* Category filter chips */}
      <div style={{display:"flex",gap:7,marginBottom:12,overflowX:"auto",paddingBottom:4}}>
        <button onClick={()=>setCatFilter(null)} style={{background:!catFilter?store.color:T.parchment,border:`1px solid ${!catFilter?store.color:T.hairline}`,color:!catFilter?T.paper:T.ink50,borderRadius:20,padding:"6px 13px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",transition:"all .12s ease"}}>All <span style={{opacity:.7,marginLeft:3}}>{storeDeals.length}</span></button>
        {categories.map(c=>(
          <button key={c} onClick={()=>setCatFilter(catFilter===c?null:c)} style={{background:catFilter===c?store.color:T.parchment,border:`1px solid ${catFilter===c?store.color:T.hairline}`,color:catFilter===c?T.paper:T.ink50,borderRadius:20,padding:"6px 13px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5,transition:"all .12s ease"}}>
            <span style={{fontSize:13}}>{CAT_ICON[c]||"🛒"}</span>{c}
          </button>
        ))}
      </div>

      {/* Section header — deal count + Add all */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:10,letterSpacing:1.6,color:T.ink50,fontWeight:800,textTransform:"uppercase"}}>{filtered.length} deal{filtered.length===1?"":"s"}{catFilter?` · ${catFilter}`:""}</div>
        <RippleBtn sound="success" onClick={()=>filtered.forEach(d=>{if(!inCart(store.id,d.id)) toggleCart(store.id,d.id);})}
          style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:store.color,borderRadius:10,padding:"6px 12px",fontSize:11,fontWeight:800,display:"inline-flex",alignItems:"center",gap:5}}>
          <Icon name="plus" size={12} color={store.color} stroke={2.6}/>
          Add all to cart
        </RippleBtn>
      </div>

      {/* Deal cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12,marginBottom:20}}>
        {filtered.map((deal,di)=>(
          <DealCard key={di} deal={deal} di={di} store={store} inCart={inCart(store.id,deal.id)} onToggle={()=>toggleCart(store.id,deal.id)}
            isWatched={isWatched ? isWatched(deal.name, store.id) : false}
            onToggleWatch={onToggleWatch ? ()=>onToggleWatch({...deal, store}) : null}
            onAsk={onAsk}
            isInPantry={isInPantry ? isInPantry(deal.id) : false}
            onAddToPantry={onAddToPantry ? ()=>onAddToPantry({...deal, store}) : null}/>
        ))}
      </div>

      {/* Sticky cart bar */}
      {cc>0 && (
        <div style={{position:"sticky",bottom:70,zIndex:100,animation:"slideUp .3s ease both"}}>
          <Glass style={{padding:"11px 14px 11px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:14,border:`1px solid ${store.color}55`,boxShadow:T.shadowFloat,gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
              <div style={{width:32,height:32,borderRadius:9,background:`${store.color}14`,border:`1px solid ${store.color}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="cart" size={16} color={store.color} stroke={2.4}/>
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:13,fontWeight:800,color:T.forest,letterSpacing:-.1,lineHeight:1.1}}>{cc} item{cc===1?"":"s"} in cart</div>
                <div style={{fontSize:11,color:T.basil,fontWeight:700,marginTop:2}}>Save ${storeDeals.filter(d=>inCart(store.id,d.id)).reduce((s,d)=>s+d.savings,0).toFixed(2)}</div>
              </div>
            </div>
            <RippleBtn sound="success" onClick={()=>openCheckout(store)}
              style={{background:`linear-gradient(135deg,${store.color},${store.glow})`,border:"none",color:T.paper,borderRadius:11,padding:"10px 16px",fontSize:12.5,fontWeight:800,boxShadow:`0 0 20px ${store.glow}40`,display:"inline-flex",alignItems:"center",gap:7,letterSpacing:.2,flexShrink:0}}>
              Send to Instacart
              <Icon name="arrow-right" size={14} color={T.paper} stroke={2.6}/>
            </RippleBtn>
          </Glass>
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   EARNINGS DASHBOARD
   ═══════════════════════════════════════════════════════════ */
const EarningsDashboard = memo(function EarningsDashboard({ activeStores = [], netBenefit = ()=>0 }) {
  const p = MOCK_PLATFORM;
  const weekMax = Math.max(...p.weeklyData);
  const currentWeek = p.weeklyData[p.weeklyData.length - 1];
  return (
    <div style={{animation:"pageIn .4s ease both"}}>
      {/* ─── Preview-mode disclosure ─────────────────────────────────────── */}
      {!AFFILIATE_LIVE && (
        <div style={{marginBottom:16,padding:"12px 14px",background:T.butterSoft,border:`1px solid ${T.butter}80`,borderRadius:14,display:"flex",alignItems:"flex-start",gap:11}}>
          <div style={{width:24,height:24,borderRadius:"50%",background:T.butter,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
            <Icon name="info" size={14} color={T.forest} stroke={2.4}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:800,color:T.forest,letterSpacing:-.1}}>Preview mode</div>
            <div style={{fontSize:11,color:T.ink70,marginTop:2,lineHeight:1.45}}>
              Numbers below are projected from demo transactions. Real earnings begin once the Impact.com / Instacart partnership is approved.
            </div>
          </div>
        </div>
      )}

      {/* ─── Hero — all-time earnings ────────────────────────────────────── */}
      <Glass style={{position:"relative",overflow:"hidden",borderRadius:24,padding:"24px 22px 22px",marginBottom:16,background:`linear-gradient(155deg, ${T.forest} 0%, #1f3a2a 55%, #0f2418 100%)`,border:"none"}}>
        {/* Subtle radial highlight, top-right */}
        <div style={{position:"absolute",top:-60,right:-60,width:260,height:260,borderRadius:"50%",background:`radial-gradient(circle, ${T.basil}38 0%, transparent 70%)`,pointerEvents:"none"}}/>
        <div style={{position:"relative",zIndex:1}}>
          {/* Header row */}
          <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:18}}>
            <div style={{width:34,height:34,background:`${T.basil}28`,border:`1px solid ${T.basil}55`,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon name="bolt" size={18} color={T.butter} stroke={2.2}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.paper,letterSpacing:-.1}}>{AFFILIATE_COPY.earningsHeader}</div>
              <div style={{fontSize:10,color:`${T.basil}d0`,fontWeight:700,letterSpacing:1.4,marginTop:2}}>{AFFILIATE_COPY.earningsSubheader} · {p.stripeAccountId}</div>
            </div>
            <div style={{background:AFFILIATE_LIVE?`${T.leaf}22`:`${T.butter}22`,border:`1px solid ${AFFILIATE_LIVE?T.leaf:T.butter}55`,borderRadius:20,padding:"4px 11px",display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:AFFILIATE_COPY.liveStatusColor,display:"inline-block",boxShadow:`0 0 8px ${AFFILIATE_COPY.liveStatusColor}`,animation:AFFILIATE_LIVE?"pulse 2s ease-in-out infinite":"none"}}/>
              <span style={{fontSize:9.5,color:AFFILIATE_COPY.liveStatusColor,fontWeight:800,letterSpacing:1.6}}>{AFFILIATE_COPY.liveStatusLabel}</span>
            </div>
          </div>
          {/* All-time earnings */}
          <div style={{fontSize:10,letterSpacing:2,color:`${T.basil}b0`,fontWeight:700,marginBottom:6}}>{AFFILIATE_COPY.earningsLabel}</div>
          <AnimNum v={p.totalEarned} size={56} color={T.butter}/>
          {/* Sub-stats — translucent, on dark */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:8,marginTop:18}}>
            {[
              {l:"THIS WEEK", v:`$${p.thisWeek.toFixed(0)}`, c:T.leaf,    d:`+${(((p.thisWeek/p.thisMonth)*4.3-1)*100).toFixed(0)}% vs avg`},
              {l:"THIS MONTH", v:`$${p.thisMonth.toFixed(0)}`, c:T.paper,  d:`+${(((p.thisMonth/p.lastMonth)-1)*100).toFixed(1)}% MoM`},
              {l:"ACTIVE USERS", v:p.activeUsers.toLocaleString(), c:T.apricot, d:p.payoutSchedule},
            ].map((s,i)=>(
              <div key={i} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.08)",borderRadius:12,padding:"10px 12px"}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:s.c,letterSpacing:-.3,lineHeight:1.1}}>{s.v}</div>
                <div style={{fontSize:9,letterSpacing:1.3,color:`${T.paper}80`,fontWeight:700,marginTop:5}}>{s.l}</div>
                <div style={{fontSize:10,color:`${T.paper}60`,fontWeight:600,marginTop:3,lineHeight:1.3}}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </Glass>

      {/* ─── 14-Week Earnings Chart ──────────────────────────────────────── */}
      <Glass style={{borderRadius:18,padding:"18px 18px 16px",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:4}}>
          <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,letterSpacing:-.2}}>14-Week Earnings</div>
          <div style={{display:"flex",alignItems:"baseline",gap:5}}>
            <Icon name="trending-up" size={13} color={T.leaf} stroke={2.4} style={{transform:"translateY(2px)"}}/>
            <span style={{fontSize:11,fontWeight:700,color:T.leaf}}>+343%</span>
            <span style={{fontSize:10,color:T.ink50,fontWeight:600}}>14w</span>
          </div>
        </div>
        <div style={{fontSize:11,color:T.ink50,marginBottom:14,lineHeight:1.4}}>
          ${p.weeklyData[0].toFixed(0)} → ${currentWeek.toFixed(0)} per week. Each bar is one week of CartIQ commission.
        </div>
        {/* Chart with grid lines + bar labels */}
        <div style={{position:"relative",height:88,marginBottom:6}}>
          {/* Y-axis grid lines */}
          {[0,0.25,0.5,0.75,1].map(t=>(
            <div key={t} style={{position:"absolute",left:0,right:0,bottom:`${t*100}%`,height:1,background:t===0?T.hairline:`${T.hairline}80`,pointerEvents:"none"}}/>
          ))}
          {/* Bars */}
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"flex-end",gap:4}}>
            {p.weeklyData.map((v,i)=>{
              const isLast = i === p.weeklyData.length - 1;
              return (
                <div key={i} style={{flex:1,position:"relative",height:`${(v/weekMax)*100}%`,minHeight:3,display:"flex",alignItems:"flex-start",justifyContent:"center"}}>
                  <div style={{
                    position:"absolute",inset:0,
                    background:isLast ? `linear-gradient(180deg, ${T.butter}, ${T.apricot})` : T.basil,
                    opacity:isLast ? 1 : 0.55 + (i/p.weeklyData.length)*0.35,
                    borderRadius:"3px 3px 0 0",
                    boxShadow:isLast ? `0 0 12px ${T.butter}60` : "none",
                  }}/>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.ink50,fontWeight:600,letterSpacing:.3}}>
          <span>14 wks ago — ${p.weeklyData[0].toFixed(0)}</span>
          <span style={{color:T.forest,fontWeight:800}}>This week — ${currentWeek.toFixed(2)}</span>
        </div>
      </Glass>

      {/* ─── Revenue Projections ────────────────────────────────────────── */}
      <Glass style={{borderRadius:18,padding:"18px 18px 14px",marginBottom:14}}>
        <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,letterSpacing:-.2,marginBottom:4}}>Revenue Projections</div>
        <div style={{fontSize:11,color:T.ink50,marginBottom:14,lineHeight:1.4}}>
          What CartIQ would earn at each user-base milestone, assuming current per-user net gain holds.
        </div>
        {/* Header row */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1.2fr",gap:8,padding:"0 4px 8px",borderBottom:`1px solid ${T.hairline}`,marginBottom:4}}>
          {["USERS","WEEKLY","ANNUAL"].map(h=>(
            <div key={h} style={{fontSize:9,letterSpacing:1.6,color:T.ink50,fontWeight:800}}>{h}</div>
          ))}
        </div>
        {/* Rows */}
        {p.projections.map((row,i)=>{
          const isLast = i === p.projections.length - 1;
          return (
            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 1fr 1.2fr",gap:8,alignItems:"center",padding:"11px 4px",borderBottom:isLast?"none":`1px solid ${T.hairline}80`}}>
              <div style={{fontSize:13,color:T.forest,fontWeight:700,letterSpacing:-.1}}>{row.users.toLocaleString()}</div>
              <div style={{fontSize:12,color:T.leaf,fontWeight:700}}>${row.weekly.toLocaleString()}</div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:isLast?17:15,fontWeight:700,color:isLast?T.butter:T.forest,letterSpacing:-.3}}>
                ${row.annual.toLocaleString()}
                {isLast && <span style={{fontSize:9,fontWeight:800,color:T.butter,marginLeft:6,letterSpacing:1.2,background:`${T.butter}1a`,padding:"2px 6px",borderRadius:6,border:`1px solid ${T.butter}40`,verticalAlign:"middle"}}>TARGET</span>}
              </div>
            </div>
          );
        })}
      </Glass>

      {/* ─── Transaction Log ────────────────────────────────────────────── */}
      <Glass style={{borderRadius:18,overflow:"hidden",marginBottom:activeStores.length>0?14:0}}>
        <div style={{padding:"15px 18px 12px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,letterSpacing:-.2}}>Transaction Log</div>
            <div style={{fontSize:10.5,color:T.ink50,marginTop:2}}>Recent affiliate redirects · {MOCK_TXN_HISTORY.length} of {MOCK_TXN_HISTORY.length}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5,background:`${AFFILIATE_COPY.liveStatusColor}1a`,border:`1px solid ${AFFILIATE_COPY.liveStatusColor}40`,borderRadius:12,padding:"3px 9px"}}>
            <span style={{width:5,height:5,borderRadius:"50%",background:AFFILIATE_COPY.liveStatusColor,display:"inline-block"}}/>
            <span style={{fontSize:9.5,color:AFFILIATE_COPY.liveStatusColor,fontWeight:800,letterSpacing:1.4}}>{AFFILIATE_COPY.liveStatusLabel}</span>
          </div>
        </div>
        {MOCK_TXN_HISTORY.map((txn,i)=>{
          // Map store name → store logo emoji from STORES if we can find one
          const matchedStore = activeStores.find(s => s.name.toLowerCase().includes(txn.store.toLowerCase().split(" ")[0])) || null;
          const logoEmoji = matchedStore?.logo || "🛒";
          const isLast = i === MOCK_TXN_HISTORY.length - 1;
          return (
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 18px",borderBottom:isLast?"none":`1px solid ${T.hairline}80`}}>
              <div style={{width:34,height:34,borderRadius:10,background:T.parchment,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <ItemIcon emoji={logoEmoji} size={20}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:T.forest,letterSpacing:-.1}}>{txn.store}</div>
                <div style={{fontSize:10.5,color:T.ink50,marginTop:1}}>{txn.date} · {txn.userCard}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.butter,letterSpacing:-.2}}>+${txn.estCommission.toFixed(2)}</div>
                <div style={{fontSize:10,color:T.leaf,fontWeight:700,marginTop:1}}>user saved ${txn.netGain.toFixed(2)}</div>
              </div>
            </div>
          );
        })}
      </Glass>

      {/* ─── Store Performance ──────────────────────────────────────────── */}
      {activeStores.length > 0 && (
        <Glass style={{padding:"18px 18px 14px",borderRadius:18}}>
          <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:4}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,letterSpacing:-.2}}>Store Performance</div>
            <div style={{fontSize:9,letterSpacing:1.6,color:T.ink50,fontWeight:800}}>NET SAVINGS</div>
          </div>
          <div style={{fontSize:11,color:T.ink50,marginBottom:14,lineHeight:1.45}}>
            Which stores win after delivery fees. Bar = raw savings; pill = net after fees.
          </div>
          {[...activeStores].sort((a,b)=>netBenefit(b)-netBenefit(a)).map((store,ri)=>{
            const raw = store.deals.reduce((s,d)=>s+d.savings,0);
            const net = netBenefit(store);
            const maxRaw = Math.max(...activeStores.map(s=>s.deals.reduce((a,d)=>a+d.savings,0)));
            const isWinner = ri === 0;
            return (
              <div key={store.id} style={{display:"flex",alignItems:"center",gap:11,padding:"10px 0",borderTop:ri===0?"none":`1px solid ${T.hairline}80`}}>
                {/* Rank — trophy for #1, number otherwise */}
                <div style={{
                  width:26,height:26,borderRadius:8,
                  background:isWinner?T.butter:`${T.parchment}`,
                  border:isWinner?`1px solid ${T.butter}`:`1px solid ${T.hairline}`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  flexShrink:0,
                }}>
                  {isWinner
                    ? <Icon name="trophy" size={15} color={T.forest} stroke={2.2}/>
                    : <span style={{fontFamily:FONT_DISPLAY,fontSize:12,fontWeight:700,color:T.ink50}}>{ri+1}</span>
                  }
                </div>
                {/* Store name + logo */}
                <div style={{width:96,display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                  <ItemIcon emoji={store.logo} size={16}/>
                  <span style={{fontSize:12.5,fontWeight:700,color:T.forest,letterSpacing:-.1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{store.name}</span>
                </div>
                {/* Bar */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{height:7,background:T.parchment,borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${(raw/maxRaw)*100}%`,background:`linear-gradient(90deg,${store.color},${store.glow})`,borderRadius:4,boxShadow:`0 0 6px ${store.glow}45`,transition:"width 1s ease"}}/>
                  </div>
                </div>
                {/* Net */}
                <div style={{minWidth:72,textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:net>0?T.leaf:T.berry,letterSpacing:-.3}}>{net>0?"+":""}${net.toFixed(2)}</div>
                </div>
              </div>
            );
          })}
        </Glass>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   SWIPEABLE SPLIT-CART ITEM
   Swipe left to reveal a delete button. Works with touch (mobile)
   and mouse drag (desktop). Threshold-based: small swipes spring
   back, swipes past 60px lock open, swipes past 140px auto-delete.
   ═══════════════════════════════════════════════════════════ */
function SwipeableSplitItem({ children, onDelete }) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startOffset = useRef(0);
  const REVEAL = 80;     // px of delete button revealed when locked open
  const AUTO_DELETE = 140; // swipe past this and the item deletes itself

  const onDown = (clientX) => {
    setDragging(true);
    startX.current = clientX;
    startOffset.current = offset;
  };
  const onMove = (clientX) => {
    if (!dragging) return;
    const dx = clientX - startX.current;
    // Only allow leftward drag (negative offset). Clamp to a max of -180.
    const next = Math.max(-180, Math.min(0, startOffset.current + dx));
    setOffset(next);
  };
  const onUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (offset <= -AUTO_DELETE) {
      // Animate fully off then delete
      setOffset(-400);
      setTimeout(() => onDelete(), 180);
    } else if (offset <= -REVEAL) {
      setOffset(-REVEAL);
    } else {
      setOffset(0);
    }
  };

  return (
    <div style={{position:"relative",overflow:"hidden",borderRadius:14}}>
      {/* Delete button revealed underneath.
          Stays CartIQ green until the user has dragged past the auto-delete
          threshold — then transitions to red as a "release to delete" cue.
          This keeps the resting/peek state on-brand instead of alarming. */}
      {(() => {
        const atDeleteThreshold = offset <= -AUTO_DELETE;
        const bg = atDeleteThreshold
          ? T.berry
          : T.forest;
        return (
          <button onClick={() => { setOffset(-400); setTimeout(() => onDelete(), 180); }}
            aria-label="Delete item"
            style={{position:"absolute",top:0,right:0,bottom:0,width:80,background:bg,border:"none",color:T.paper,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,transition:"background .18s ease"}}>
            <span style={{fontSize:18}}>{atDeleteThreshold ? "🗑" : "✕"}</span>
            <span>{atDeleteThreshold ? "RELEASE" : "REMOVE"}</span>
          </button>
        );
      })()}
      {/* The content slides over the delete button */}
      <div
        onTouchStart={(e) => onDown(e.touches[0].clientX)}
        onTouchMove={(e) => onMove(e.touches[0].clientX)}
        onTouchEnd={onUp}
        onMouseDown={(e) => onDown(e.clientX)}
        onMouseMove={(e) => dragging && onMove(e.clientX)}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? "none" : "transform .22s ease",
          touchAction: "pan-y",
          cursor: dragging ? "grabbing" : "grab",
        }}>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   usePersistedState — one-call state that loads and persists to window.storage
   ═══════════════════════════════════════════════════════════════════════════
   Replaces the useState + load-useEffect + save-useEffect triad. Semantics
   preserved from the original code:
     - Initial render uses `initial` (storage is async, can't hydrate sync)
     - On mount, attempts to read the key; on success, applies via setState
     - On every change, writes to storage (JSON-stringified)
     - All read/write failures are swallowed (matches original try/catch behavior)

   Options:
     - skipSaveIf: predicate on the value; when true, don't write to storage
       (preserves guards like `if (userLocation)` and `if (visitedTabs.length > 0)`
       that avoid persisting empty/null defaults)
     - migrate: function (loadedValue) → migratedValue, run once on hydrate.
       Used to upgrade persisted data shapes when the schema changes
       (e.g., numeric IDs → string slugs in v2). The migrated value is
       immediately re-persisted so subsequent loads are fast.

   Usage:
     const [cart, setCart] = usePersistedState("cartiq_cart", {});
     const [location, setLocation] = usePersistedState("cartiq_location", null, {
       skipSaveIf: v => v == null,
     });
   ═══════════════════════════════════════════════════════════════════════════ */
function usePersistedState(key, initial, options = {}) {
  const [value, setValue] = useState(initial);
  const hasHydrated = useRef(false);
  const userTouched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await window.storage.get(key);
        if (!cancelled && stored && !userTouched.current) {
          let parsed = JSON.parse(stored.value);
          // Run migration if provided. If the migrated value differs from
          // the loaded value (i.e. the migration actually changed something),
          // immediately persist the new shape so next load skips the work.
          if (options.migrate) {
            const migrated = options.migrate(parsed);
            if (migrated !== parsed) {
              parsed = migrated;
              try { window.storage.set(key, JSON.stringify(migrated)); } catch (e) {}
            }
          }
          setValue(parsed);
        }
      } catch (e) {
        // Read errors are non-fatal — the default `initial` stays in place
      } finally {
        if (!cancelled) hasHydrated.current = true;
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setAndMarkTouched = useCallback((next) => {
    userTouched.current = true;
    setValue(next);
  }, []);

  useEffect(() => {
    if (!hasHydrated.current) return;
    if (options.skipSaveIf && options.skipSaveIf(value)) return;
    const timer = setTimeout(() => {
      try {
        window.storage.set(key, JSON.stringify(value));
      } catch (e) {
        // Write errors are non-fatal
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value]);

  return [value, setAndMarkTouched];
}

/* ═══════════════════════════════════════════════════════════
   ERROR BOUNDARY — top-level safety net
   ═══════════════════════════════════════════════════════════
   Wraps the entire app. If any React render throws, this catches
   it and shows a friendly fallback instead of the white screen of
   death. The fallback is intentionally self-contained — it imports
   nothing, references no other components, and embeds its own SVG
   cart — so even if a shared component (CartIQMark, Glass, etc.)
   is the source of the crash, the boundary itself can still render.

   Three actions offered to the user:
     1. Refresh — reloads the page; clears in-memory state but keeps
        persisted storage (cart, watchlist, etc. survive)
     2. Report — opens an email to the support address with the error
        message and stack pre-filled, so the user only has to add
        context and hit send
     3. Reset CartIQ — nuclear option: clears all window.storage keys
        and reloads. Recovers from data corruption that survives a
        plain refresh. Confirms first.

   In dev (when the URL contains localhost or 127.0.0.1), the boundary
   also shows the raw error message + stack — useful when you're the
   one debugging. Hidden in production so end users don't see internals.
   ═══════════════════════════════════════════════════════════ */

// Standalone cart SVG — duplicated from CartIQMark's paths on purpose.
// Inlined so the ErrorBoundary has zero external dependencies.
// Variant: leaf wilted to muted brown-green and slumped lower in the
// basket, no wheels-on-the-ground impression. Reads as "something
// went wrong" without needing a sad face.
function CartIconStandalone({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: "block", filter: "drop-shadow(0 6px 18px rgba(166,113,52,.4))" }}>
      <defs>
        <linearGradient id="ebCart" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7a4f1f"/>
          <stop offset="100%" stopColor="#a67134"/>
        </linearGradient>
        <linearGradient id="ebLeafWilt" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8a9550"/>
          <stop offset="50%" stopColor="#5a6a3a"/>
          <stop offset="100%" stopColor="#3a4520"/>
        </linearGradient>
      </defs>
      {/* Cart frame — same path as primary mark */}
      <path d="M22 32 L28 32 L36 70 L78 70 L84 42 L40 42" fill="none" stroke="url(#ebCart)" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Wilted leaf — drooped lower into the basket, muted color (the "something went wrong" cue) */}
      <path d="M40 44 Q58 38 80 38 Q78 56 60 60 Q44 60 40 44 Z" fill="url(#ebLeafWilt)"/>
      {/* Wheels */}
      <circle cx="44" cy="80" r="5" fill="#7a4f1f"/>
      <circle cx="44" cy="80" r="2.2" fill="#d4a574"/>
      <circle cx="72" cy="80" r="5" fill="#7a4f1f"/>
      <circle cx="72" cy="80" r="2.2" fill="#d4a574"/>
    </svg>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    // Log to console so dev tools captures it. In production, this is
    // also the hook to wire up Sentry / LogRocket / your error tracking.
    // For now: console.error is enough for the 25-user round.
    // Tester reproductions will be paired with browser screenshots anyway.
    console.error("CartIQ ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }
  handleRefresh = () => {
    if (typeof window !== "undefined") window.location.reload();
  };
  handleReport = () => {
    const { error, errorInfo } = this.state;
    const subject = encodeURIComponent("CartIQ error report");
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
    const url = typeof window !== "undefined" ? window.location.href : "unknown";
    const body = encodeURIComponent(
      `Hi CartIQ team,\n\nI hit an error in the app. Here's what happened:\n\n` +
      `[Please describe what you were doing when this happened]\n\n` +
      `--- Technical details (auto-filled) ---\n` +
      `URL: ${url}\n` +
      `Browser: ${ua}\n` +
      `Error: ${error?.message || "unknown"}\n` +
      `Stack:\n${error?.stack?.slice(0, 1500) || "no stack available"}\n` +
      (errorInfo?.componentStack ? `\nComponent stack:\n${errorInfo.componentStack.slice(0, 800)}\n` : "")
    );
    if (typeof window !== "undefined") {
      window.location.href = `mailto:${FEEDBACK_EMAIL}?subject=${subject}&body=${body}`;
    }
  };
  handleResetCartIQ = () => {
    if (typeof window === "undefined") return;
    const ok = window.confirm(
      "This will clear ALL CartIQ data on this device — your cart, watchlist, pantry, and preferences. Your account on Instacart is unaffected. Continue?"
    );
    if (!ok) return;
    try {
      // Clear every persisted CartIQ key. Pattern matches everything written
      // by usePersistedState in this codebase.
      const keysToClear = [
        "cartiq_cart", "cartiq_pantry", "cartiq_subprefs", "cartiq_location",
        "cartiq_watchlist", "cartiq_alerts", "cartiq_visited_tabs",
        "cartiq_txn_history", "cartiq_dietary", "cartiq_planned_shop",
        "cartiq_email_alerts", "cartiq_user_name", "cartiq_sound_on",
        "cartiq_haptics_on", "cartiq_rewards_ledger",
        "cartiq_search_added_items",
        "cartiq_recent_searches",
        "cartiq_my_items", "cartiq_lists", "cartiq_list_schedules",
        "cartiq_rewards_filter",
        "cartiq_staples",
        "cartiq_budget",
        "cartiq_store_alert_prefs",
        "cartiq_price_memory",
        "cartiq_health_profile",
        "cartiq_tooltip_trust_seen",
        "cartiq_tooltip_price_memory_seen",
      ];
      // window.storage is the platform's KV store; localStorage is for
      // legacy keys like cartiq_toured. Best-effort clear on both.
      if (window.storage && typeof window.storage.delete === "function") {
        keysToClear.forEach(k => { try { window.storage.delete(k); } catch(e) {} });
      }
      try { localStorage.removeItem("cartiq_toured"); } catch(e) {}
      try { localStorage.removeItem("cartiq_dietary_seen"); } catch(e) {}
      try { localStorage.removeItem("cartiq_staples_seen"); } catch(e) {}
      try { localStorage.removeItem("cartiq_savings_revealed"); } catch(e) {}
      try { localStorage.removeItem("cartiq_health_goals_seen"); } catch(e) {}
    } catch(e) {
      console.error("Reset failed:", e);
    }
    window.location.reload();
  };
  render() {
    if (!this.state.hasError) return this.props.children;

    // Show technical details only in dev. Detect by hostname — works for
    // localhost, 127.0.0.1, and *.local domains used by tools like Vite.
    const isDev = typeof window !== "undefined" &&
      /^(localhost|127\.0\.0\.1|.*\.local)$/.test(window.location.hostname);

    return (
      <div style={{
        position: "fixed", inset: 0,
        background: "linear-gradient(180deg,#0a0a14,#13131f)",
        color: T.paper, fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, overflow: "auto",
      }}>
        <div style={{
          maxWidth: 460, width: "100%",
          background: "rgba(20,22,38,.7)",
          backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
          border: "1px solid rgba(232,97,26,.25)",
          borderRadius: 22,
          boxShadow: "0 24px 80px rgba(0,0,0,.6), 0 0 60px rgba(232,97,26,.12)",
          padding: "30px 26px",
          textAlign: "center",
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
            <CartIconStandalone size={84}/>
          </div>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 24, fontWeight: 900, lineHeight: 1.2, marginBottom: 10,
          }}>
            Something didn't grow as expected.
          </div>
          <div style={{ fontSize: 14, color: T.ink50, lineHeight: 1.55, marginBottom: 22 }}>
            CartIQ hit an unexpected error. Your saved data (cart, watchlist, pantry) is safe — try refreshing first. If it keeps happening, send a quick report so we can fix it.
          </div>

          {isDev && this.state.error && (
            <div style={{
              textAlign: "left", background: "rgba(0,0,0,.4)", border: `1px solid ${T.hairline}`,
              borderRadius: 10, padding: "10px 12px", marginBottom: 16,
              fontFamily: "Consolas, Menlo, monospace", fontSize: 11, color: T.warn,
              maxHeight: 160, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>
              <div style={{ color: T.berry, fontWeight: 700, marginBottom: 4 }}>{this.state.error.name}: {this.state.error.message}</div>
              {this.state.error.stack && <div style={{ color: T.ink50, fontSize: 10 }}>{this.state.error.stack.slice(0, 600)}</div>}
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            <button onClick={this.handleRefresh}
              style={{
                background: `${T.forest}`,
                border: "none", color: T.paper, borderRadius: 12,
                padding: "14px 18px", fontSize: 14, fontWeight: 800, letterSpacing: .3,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 6px 20px rgba(30,53,40,.18)",
              }}>
              🔄 Refresh and try again
            </button>
            <button onClick={this.handleReport}
              style={{
                background: "rgba(232,97,26,.12)",
                border: "1px solid rgba(232,97,26,.35)",
                color: "#f4892e", borderRadius: 12,
                padding: "12px 18px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
              }}>
              📣 Send error report
            </button>
            <button onClick={this.handleResetCartIQ}
              style={{
                background: "transparent", border: `1px solid ${T.hairline}`,
                color: T.ink50, borderRadius: 12,
                padding: "10px 18px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}>
              Reset CartIQ data (last resort)
            </button>
          </div>

          <div style={{ marginTop: 18, fontSize: 11, color: T.ink50 }}>
            Sorry for the trouble — this kind of feedback is exactly what helps make CartIQ better. 🥕
          </div>
        </div>
      </div>
    );
  }
}

/* ═══════════════════════════════════════════════════════════
   NUTRITION IQ — cart-level nutrition rollup + health profile
   ═══════════════════════════════════════════════════════════
   Sums nutrition across Smart Cart items that have data in
   MASTER_LIST. Quantity-aware (multiplies by line qty).
   Items without nutrition data are silently skipped — the
   rollup shows a coverage count so the user can see how
   many lines were measurable. No FDA-regulated label claims
   are made about products; we describe what the USER is
   watching and report neutral cart totals.
   ═══════════════════════════════════════════════════════════ */
function NutritionRollup({ items, profile, onToggleGoal }) {
  // items: array of { id, qty } (or { id, bestDeal:{id}, qty })
  const totals = { protein: 0, fiber: 0, calories: 0, sodium: 0, sugar: 0 };
  let measured = 0;
  let total = 0;
  (items || []).forEach(it => {
    total += 1;
    const lookupId = it.id || it.bestDeal?.id;
    const qty = typeof it.qty === "number" && it.qty > 0 ? it.qty : 1;
    const m = MASTER_LIST.find(x => x.id === lookupId);
    if (!m || !m.nutrition) return;
    measured += 1;
    const n = m.nutrition;
    totals.protein  += (n.protein  || 0) * qty;
    totals.fiber    += (n.fiber    || 0) * qty;
    totals.calories += (n.calories || 0) * qty;
    totals.sodium   += (n.sodium   || 0) * qty;
    totals.sugar    += (n.sugar    || 0) * qty;
  });

  if (total === 0) return null;
  const activeIds = new Set(profile || []);

  return (
    <Glass style={{padding:"14px 16px",marginBottom:14,borderRadius:16,border:`1px solid ${T.hairline}`}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <Icon name="leaf" size={14} color={T.basil} stroke={2.2}/>
        <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest,flex:1}}>Nutrition IQ</div>
        <div style={{fontSize:10,color:T.ink50,fontWeight:700,letterSpacing:.3}}>
          {measured}/{total} item{total===1?"":"s"} measured
        </div>
      </div>

      {/* Goal chips — informational filters for what to highlight */}
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
        {HEALTH_GOALS.map(g => {
          const on = activeIds.has(g.id);
          return (
            <button key={g.id} onClick={()=>onToggleGoal && onToggleGoal(g.id)}
              aria-pressed={on}
              style={{
                background: on ? T.sageSoft : T.parchment,
                border: `1px solid ${on ? T.sage : T.hairline}`,
                color: on ? T.basil : T.ink50,
                borderRadius:8,padding:"5px 10px",fontSize:10.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",
                letterSpacing:.2,
              }}>
              {on ? "✓ " : ""}{g.label}
            </button>
          );
        })}
      </div>

      {/* Totals grid — colored only when a matching goal is active */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:6}}>
        {HEALTH_GOALS.map(g => {
          const value = totals[g.field] || 0;
          const goalOn = activeIds.has(g.id);
          let tone = T.ink70;
          if (goalOn) {
            const meets = g.direction === "under" ? value <= g.target : value >= g.target;
            tone = meets ? T.basil : T.berry;
          }
          const displayVal = g.field === "sodium" ? Math.round(value) : Math.round(value * 10) / 10;
          return (
            <div key={g.id} style={{
              padding:"8px 6px",
              background: goalOn ? (tone === T.basil ? T.sageSoft : T.berrySoft) : T.parchment,
              border:`1px solid ${goalOn ? tone+"55" : T.hairline}`,
              borderRadius:10,textAlign:"center",
            }}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:800,color:tone,letterSpacing:-.2,lineHeight:1.1}}>
                {displayVal}<span style={{fontSize:9,fontWeight:700,fontFamily:FONT_SANS,color:T.ink50,marginLeft:1}}>{g.unit}</span>
              </div>
              <div style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,marginTop:3,textTransform:"uppercase"}}>{g.short}</div>
            </div>
          );
        })}
      </div>

      {measured < total && (
        <div style={{fontSize:10,color:T.ink50,marginTop:8,fontStyle:"italic"}}>
          {total - measured} item{total-measured===1?"":"s"} not yet in our nutrition database.
        </div>
      )}
    </Glass>
  );
}

/* ═══════════════════════════════════════════════════════════
   FOR YOUR GOALS RAIL — health-profile deal surfacing
   ═══════════════════════════════════════════════════════════
   Ranks deals against the user's HEALTH_GOALS profile. Each
   active goal contributes a normalized [0,1] score: `under`
   goals reward lower nutrient values relative to the goal
   target, `over` goals reward higher. Items without a
   MASTER_LIST nutrition record return null and are excluded
   from the rail — same coverage rule NutritionRollup follows.
   The rail is non-destructive: it sits above the store grid;
   the main savings-ranked grids elsewhere are untouched.
   Renders null on empty profile, no matched items, or fewer
   than 3 scored items — never shows a weak signal.
   ═══════════════════════════════════════════════════════════ */
function scoreDealForGoals(deal, profile) {
  if (!deal || !profile || profile.length === 0) return null;
  const m = MASTER_LIST.find(x => x.id === deal.id);
  if (!m || !m.nutrition) return null;
  const n = m.nutrition;
  let total = 0;
  let counted = 0;
  for (const goalId of profile) {
    const g = HEALTH_GOALS.find(x => x.id === goalId);
    if (!g) continue;
    const value = n[g.field];
    if (typeof value !== "number") continue;
    const ratio = g.target > 0 ? value / g.target : 0;
    const contribution = g.direction === "under"
      ? Math.max(0, 1 - ratio)
      : Math.min(1, ratio);
    total += contribution;
    counted += 1;
  }
  if (counted === 0) return null;
  return total / counted;
}

function GoalRail({ allDeals, profile, inCart, toggleCart, isWatched, onToggleWatch, setAskSageDeal, pantry, addDealToPantry }) {
  if (!profile || profile.length === 0) return null;
  const scored = (allDeals || [])
    .map(d => ({ deal: d, score: scoreDealForGoals(d, profile) }))
    .filter(x => x.score !== null && x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
  if (scored.length < 3) return null;

  const labels = profile
    .map(id => HEALTH_GOALS.find(g => g.id === id)?.short)
    .filter(Boolean)
    .join(" · ");

  return (
    <div style={{marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <Icon name="leaf" size={20} color={T.basil} stroke={2.4}/>
        <span style={{fontSize:10,letterSpacing:2,color:T.ink50,fontWeight:800}}>FOR YOUR GOALS</span>
        <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10.5,color:T.basil,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:20,padding:"2px 9px",fontWeight:700,letterSpacing:.2}}>
          {labels}
        </span>
      </div>
      <div style={{
        display:"flex",gap:12,overflowX:"auto",overflowY:"hidden",
        paddingBottom:6,scrollSnapType:"x mandatory",
        WebkitOverflowScrolling:"touch",
      }}>
        {scored.map(({deal}, di) => (
          <div key={`${deal.store.id}_${deal.id}`} style={{flex:"0 0 280px",scrollSnapAlign:"start"}}>
            <DealCard
              deal={deal}
              di={di}
              store={deal.store}
              inCart={inCart(deal.store.id, deal.id)}
              onToggle={()=>toggleCart(deal.store.id, deal.id)}
              isWatched={isWatched ? isWatched(deal.name, deal.store.id) : false}
              onToggleWatch={onToggleWatch ? ()=>onToggleWatch({...deal, store: deal.store}) : null}
              onAsk={({deal:d, store:s})=>setAskSageDeal({deal:d, store:s})}
              isInPantry={pantry ? pantry.some(p => p.id === deal.id) : false}
              onAddToPantry={addDealToPantry ? ()=>addDealToPantry({...deal, store: deal.store}) : null}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TRUST THE PRICE — observation-based badge
   ═══════════════════════════════════════════════════════════
   Reads PRICE_HISTORY[itemId] and, when present, returns a
   small chip summarizing observation count + current position
   in the observed range. v1 uses local PRICE_HISTORY only;
   when IDP catalog data lands, swap the source and the copy
   can upgrade to "verified against in-store price X ago."
   Returns null when there is no history (renders nothing).
   ═══════════════════════════════════════════════════════════ */
function computeTrustBadge(itemId, currentPrice) {
  const h = PRICE_HISTORY?.[itemId];
  if (!Array.isArray(h) || h.length < 2) return null;
  const min = Math.min(...h);
  const max = Math.max(...h);
  const range = max - min;
  const cur = typeof currentPrice === "number" && currentPrice > 0 ? currentPrice : h[h.length - 1];
  // Distinct price points (rounded to the nearest cent)
  const distinct = new Set(h.map(p => Math.round(p * 100))).size;
  // Position within observed range — terciles
  let position = "mid";
  if (range > 0) {
    const t = (cur - min) / range;
    position = t <= 0.33 ? "low" : t >= 0.67 ? "high" : "mid";
  }
  return {
    observations: h.length,
    distinctPrices: distinct,
    min, max, cur, range, position,
  };
}

function TrustBadge({ itemId, currentPrice, compact = false }) {
  const data = computeTrustBadge(itemId, currentPrice);
  if (!data) return null;
  const { observations, distinctPrices, position, min, max } = data;
  const tone = position === "low" ? T.basil : position === "high" ? T.berry : T.ink70;
  const bg   = position === "low" ? T.sageSoft : position === "high" ? T.berrySoft : T.parchment;
  const positionCopy = position === "low" ? "low end of range" : position === "high" ? "high end of range" : "mid-range";

  if (compact) {
    return (
      <div style={{
        display:"inline-flex",alignItems:"center",gap:5,
        padding:"3px 8px",borderRadius:6,
        background:bg,border:`1px solid ${tone}33`,
        fontFamily:FONT_SANS,fontSize:9.5,fontWeight:800,color:tone,letterSpacing:.3,
      }} title={`${observations} weekly observations · range $${min.toFixed(2)}–$${max.toFixed(2)}`}>
        <Icon name="shield-check" size={10} color={tone} stroke={2.4}/>
        <span>VERIFIED · {positionCopy.toUpperCase()}</span>
      </div>
    );
  }

  return (
    <div style={{
      display:"flex",alignItems:"center",gap:10,
      padding:"10px 12px",borderRadius:12,
      background:bg,border:`1px solid ${tone}33`,
      marginBottom:14,
    }}>
      <div style={{
        width:30,height:30,borderRadius:9,background:T.paper,
        border:`1px solid ${tone}40`,
        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
      }}>
        <Icon name="shield-check" size={15} color={tone} stroke={2.2}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:10,letterSpacing:1.3,color:T.ink50,fontWeight:800,textTransform:"uppercase",marginBottom:2}}>Trust the price</div>
        <div style={{fontSize:12,color:T.forest,fontWeight:600}}>
          <strong style={{fontWeight:800}}>{distinctPrices}</strong> price{distinctPrices===1?"":"s"} observed in {observations} weeks · current is{" "}
          <strong style={{color:tone,fontWeight:800}}>{positionCopy}</strong>
        </div>
        <div style={{fontSize:10.5,color:T.ink50,marginTop:2,fontFamily:FONT_SANS,fontWeight:600}}>
          Range ${min.toFixed(2)} – ${max.toFixed(2)}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PRICE MEMORY — per-item "last time you bought this" pill
   ═══════════════════════════════════════════════════════════
   Reads from priceMemory[itemId] (persisted shape:
     [{ date: ISO, price: number, storeId: string }])
   Renders nothing when no history exists for the item.
   Below 3 points: text-only delta. At 3+: text + inline sparkline.
   Props:
     itemId       : string — deal/master-list item id
     currentPrice : number — today's price for delta math
     history      : array of { date, price, storeId } | undefined
     compact      : boolean — denser layout for cart rows
   ═══════════════════════════════════════════════════════════ */
function PriceMemory({ itemId, currentPrice, history, compact = false }) {
  if (!itemId || !Array.isArray(history) || history.length === 0) return null;
  const last = history[history.length - 1];
  const lastPrice = last?.price;
  if (typeof lastPrice !== "number" || lastPrice <= 0) return null;
  const cur = typeof currentPrice === "number" && currentPrice > 0 ? currentPrice : lastPrice;
  const delta = cur - lastPrice;
  const pct = lastPrice > 0 ? (delta / lastPrice) * 100 : 0;
  const up = delta > 0.005;
  const down = delta < -0.005;
  const tone = up ? T.berry : down ? T.basil : T.ink50;
  const lastDate = (() => {
    try { return new Date(last.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch (e) { return ""; }
  })();
  const showSpark = history.length >= 3 && !compact;
  const sparkPath = (() => {
    if (!showSpark) return null;
    const pts = history.map(h => h.price);
    const min = Math.min(...pts);
    const max = Math.max(...pts);
    const span = max - min || 1;
    const w = 64, h = 18;
    const step = pts.length > 1 ? w / (pts.length - 1) : 0;
    return pts.map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - ((p - min) / span) * h).toFixed(1)}`).join(" ");
  })();

  const arrow = up ? "↑" : down ? "↓" : "•";
  const deltaCopy = `${arrow} $${Math.abs(delta).toFixed(2)} (${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%)`;

  if (compact) {
    return (
      <div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:10.5,color:T.ink50,marginTop:4,fontWeight:600}}>
        <span>Last paid <strong style={{color:T.forest,fontWeight:800}}>${lastPrice.toFixed(2)}</strong> {lastDate}</span>
        <span style={{color:tone,fontWeight:800}}>{deltaCopy}</span>
      </div>
    );
  }

  return (
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,background:T.parchment,border:`1px solid ${T.hairline}`,marginBottom:14}}>
      <div style={{width:30,height:30,borderRadius:9,background:T.paper,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <Icon name="clock" size={15} color={T.ink50} stroke={2.2}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:10,letterSpacing:1.3,color:T.ink50,fontWeight:800,textTransform:"uppercase",marginBottom:2}}>Your price memory</div>
        <div style={{fontSize:12,color:T.forest,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          Last bought <strong style={{color:T.forest,fontWeight:800,fontFamily:FONT_DISPLAY}}>${lastPrice.toFixed(2)}</strong> on {lastDate} · now <strong style={{color:tone,fontWeight:800,fontFamily:FONT_DISPLAY}}>${cur.toFixed(2)}</strong>
        </div>
        <div style={{fontSize:10.5,color:tone,fontWeight:800,marginTop:2}}>{deltaCopy}{history.length >= 3 ? ` · ${history.length} purchases tracked` : ""}</div>
      </div>
      {showSpark && (
        <svg width={64} height={18} viewBox="0 0 64 18" aria-hidden="true" style={{flexShrink:0}}>
          <path d={sparkPath} fill="none" stroke={tone} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PRICE IQ — ITEM DETAIL SHEET
   Bottom-sheet drawer. Shows hero price, big chart, stats, the
   active deal (if any), watchlist toggle + price-target editor,
   a where-to-buy list, and a footer of quick actions.
   Props:
     itemId      : MASTER_LIST item.id
     onClose     : ()=>void
     allDeals    : derived deals across active stores  (from CartIQApp)
     watchlist   : current watchlist entries           (from CartIQApp)
     toggleWatchlist, updateWatchTarget                (callbacks)
     toggleCart, inCart                                (callbacks)
     onAskSage   : (item)=>void  open Sage drawer with prefill
     activeStores: stores the user has selected
   ═══════════════════════════════════════════════════════════ */
function PriceIQDetailSheet({ itemId, onClose, allDeals, watchlist, toggleWatchlist, updateWatchTarget, toggleCart, inCart, onAskSage, activeStores, priceMemory }) {
  const item = MASTER_LIST.find(x => x.id === itemId);
  const history = item ? PRICE_HISTORY[item.id] : null;
  const [editingTarget, setEditingTarget] = useState(false);
  const [tempTarget, setTempTarget] = useState("");
  if (!item || !history) return null;

  const cur = history[history.length - 1];
  const prev = history[history.length - 2] || cur;
  const start = history[0];
  const avg = history.reduce((s,v)=>s+v, 0) / history.length;
  const atl = Math.min(...history);
  const ath = Math.max(...history);
  const wowPct = prev > 0 ? ((cur - prev) / prev) * 100 : 0;
  const sixWkPct = start > 0 ? ((cur - start) / start) * 100 : 0;
  const isAtLow = cur <= atl * 1.02;
  const isFalling = cur < start && !isAtLow;
  const isRising = cur > start * 1.05;
  const status = isAtLow ? "atLow" : isFalling ? "falling" : isRising ? "rising" : "steady";
  // Volatility — coefficient of variation (stdev/mean)
  const variance = history.reduce((s,v)=>s+Math.pow(v-avg,2), 0) / history.length;
  const stdev = Math.sqrt(variance);
  const cv = avg > 0 ? stdev / avg : 0;
  const volLabel = cv > 0.18 ? "High" : cv > 0.08 ? "Medium" : "Low";
  const volColor = cv > 0.18 ? T.berry : cv > 0.08 ? T.warn : T.basil;

  // Active deal across all monitored stores
  const deal = allDeals.find(d => d.id === item.id);
  // All stores that carry this item (any deal — including non-sale entries if available)
  // We pull from STORES (full catalog) — gives us where-to-buy regardless of active filter
  const carriers = STORES.flatMap(s => s.deals.filter(d => d.id === item.id).map(d => ({ ...d, store: s })))
    .sort((a,b) => (a.salePrice||a.regPrice||999) - (b.salePrice||b.regPrice||999));
  // Watchlist entry for this item, if any
  const wlEntry = watchlist.find(w => w.name === item.name);
  const isWatched = !!wlEntry;
  const target = wlEntry?.targetPrice ?? null;

  const trendColor = status === "rising" ? T.berry : (status === "falling" || status === "atLow") ? T.basil : T.ink50;
  const statusLabel = status === "atLow" ? "All-time low"
    : status === "falling" ? `${Math.abs(sixWkPct).toFixed(0)}% lower than 6 weeks ago`
    : status === "rising"  ? `${sixWkPct.toFixed(0)}% higher than 6 weeks ago`
    : "Steady price";
  const statusIcon = status === "atLow" ? "trophy"
    : status === "falling" ? "trending-down"
    : status === "rising"  ? "trending-up"
    : "minus";

  const startEditTarget = () => {
    setTempTarget(target != null ? String(target.toFixed(2)) : cur.toFixed(2));
    setEditingTarget(true);
  };
  const saveTarget = () => {
    const t = parseFloat(tempTarget);
    if (!isNaN(t) && t > 0 && wlEntry) {
      updateWatchTarget(wlEntry.id, t);
    }
    setEditingTarget(false);
  };

  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(30,53,40,.65)",zIndex:950,display:"flex",alignItems:"flex-end",justifyContent:"center",animation:"fadeIn .25s ease",backdropFilter:"blur(2px)"}}>
      <div onClick={e=>e.stopPropagation()}
        style={{
          width:"100%", maxWidth:520,
          maxHeight:"92vh",
          background:T.paper,
          borderRadius:"22px 22px 0 0",
          boxShadow:"0 -10px 60px rgba(30,53,40,.25)",
          animation:"slideUp .3s ease both",
          display:"flex", flexDirection:"column",
          overflow:"hidden",
        }}>

        {/* Drag handle */}
        <div style={{display:"flex",justifyContent:"center",padding:"10px 0 0",flexShrink:0}}>
          <div style={{width:36,height:4,borderRadius:2,background:T.hairline}}/>
        </div>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:11,padding:"10px 16px 14px",flexShrink:0,borderBottom:`1px solid ${T.hairline}`}}>
          <div style={{width:44,height:44,borderRadius:11,background:T.parchment,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <ItemIcon emoji={getItemEmoji(item.id, item.name, item.category)} size={28}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.3,lineHeight:1.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</div>
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3,fontSize:10,color:T.ink50,fontWeight:700,letterSpacing:.5,textTransform:"uppercase"}}>
              <span>{item.category}</span>
              {item.qty && item.unit && (<><span>·</span><span>{item.qty} {item.unit}</span></>)}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",flexShrink:0}}>
            <Icon name="x" size={16} color={T.ink50} stroke={2.2}/>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{flex:1,overflowY:"auto",padding:"14px 16px 18px"}}>

          {/* Hero price block */}
          <div style={{padding:"16px 18px",borderRadius:18,background:isAtLow ? T.sageSoft : T.parchment,border:`1px solid ${isAtLow ? T.sage : T.hairline}`,marginBottom:14}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:6}}>
              <div style={{fontSize:10,letterSpacing:1.6,color:T.ink50,fontWeight:800,textTransform:"uppercase"}}>Current price</div>
              <div style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:20,background:`${trendColor}14`,border:`1px solid ${trendColor}33`}}>
                <Icon name={status === "rising" ? "trending-up" : "trending-down"} size={12} color={trendColor} stroke={2.4}/>
                <span style={{fontSize:11,fontWeight:800,color:trendColor,letterSpacing:.2}}>
                  {wowPct >= 0 ? "+" : ""}{wowPct.toFixed(1)}% wk
                </span>
              </div>
            </div>
            <div style={{fontFamily:FONT_DISPLAY,fontSize:38,fontWeight:800,color:isAtLow ? T.basil : T.forest,letterSpacing:-1,lineHeight:1}}>${cur.toFixed(2)}</div>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:10,padding:"5px 11px",borderRadius:20,background:T.paper,border:`1px solid ${trendColor}33`}}>
              <Icon name={statusIcon} size={13} color={trendColor} stroke={2.4}/>
              <span style={{fontSize:11.5,fontWeight:700,color:trendColor,letterSpacing:.1}}>{statusLabel}</span>
            </div>
          </div>

          {/* Trust the price — observation-based badge derived from PRICE_HISTORY */}
          <TrustBadge itemId={item.id} currentPrice={cur}/>

          {/* Your price memory — renders only when there's local purchase history */}
          <PriceMemory itemId={item.id} currentPrice={cur} history={priceMemory?.[item.id]}/>

          {/* Big chart */}
          <div style={{padding:"14px 14px 12px",borderRadius:16,background:T.paper,border:`1px solid ${T.hairline}`,marginBottom:14,boxShadow:T.shadowCard}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:10,letterSpacing:1.4,color:T.ink50,fontWeight:800,textTransform:"uppercase"}}>6-week trend</div>
              <div style={{display:"flex",gap:10,fontSize:9.5,color:T.ink50,fontWeight:700,letterSpacing:.3}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:14,height:0,borderTop:`1.2px dashed ${T.ink50}`,opacity:.6,display:"inline-block"}}/>AVG</span>
                <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:14,height:0,borderTop:`1.2px dotted ${T.sage}`,display:"inline-block"}}/>LOW</span>
              </div>
            </div>
            <div style={{position:"relative"}}>
              <PriceTrendChart data={history} atl={atl} avg={avg} height={140}/>
              {/* week tick labels */}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:T.ink50,fontWeight:700,letterSpacing:.4,padding:"6px 4px 0"}}>
                {history.map((_,idx) => (
                  <span key={idx}>W{idx+1}</span>
                ))}
              </div>
            </div>
            {/* week-by-week detail strip */}
            <div style={{display:"grid",gridTemplateColumns:`repeat(${history.length},1fr)`,gap:2,marginTop:10}}>
              {history.map((p, idx) => {
                const isLast = idx === history.length - 1;
                const isMin = p === atl;
                return (
                  <div key={idx} style={{textAlign:"center",padding:"5px 2px",borderRadius:7,background:isLast ? T.sageSoft : isMin ? T.butterSoft : "transparent",border:isLast ? `1px solid ${T.sage}` : isMin ? `1px solid ${T.butter}` : "1px solid transparent"}}>
                    <div style={{fontSize:10,fontWeight:800,color:isLast ? T.basil : isMin ? T.warn : T.ink70,fontFamily:FONT_DISPLAY,letterSpacing:-.2}}>${p.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stat row — 4 connected stats */}
          <div style={{display:"flex",marginBottom:14,padding:0,borderRadius:14,background:T.parchment,border:`1px solid ${T.hairline}`,overflow:"hidden"}}>
            {[
              {l:"6wk avg", v:`$${avg.toFixed(2)}`,    c:T.ink70, icon:"chart-line"},
              {l:"All-time low", v:`$${atl.toFixed(2)}`, c:T.basil, icon:"trophy"},
              {l:"6wk range",   v:`$${(ath-atl).toFixed(2)}`, c:T.ink70, icon:"minus"},
              {l:"Volatility",  v:volLabel,             c:volColor, icon:"info"},
            ].map((s,i,arr) => (
              <Fragment key={i}>
                <div style={{flex:1,padding:"9px 8px",display:"flex",flexDirection:"column",gap:3,minWidth:0,textAlign:"center"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:4,fontSize:8.5,letterSpacing:.7,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>
                    <Icon name={s.icon} size={11} color={T.ink50} stroke={2}/>
                    <span>{s.l}</span>
                  </div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:800,color:s.c,letterSpacing:-.3,lineHeight:1.1}}>{s.v}</div>
                </div>
                {i < arr.length - 1 && <div style={{width:1,background:T.hairline}}/>}
              </Fragment>
            ))}
          </div>

          {/* Active deal */}
          {deal && (
            <div style={{padding:"12px 14px",borderRadius:14,background:T.butterSoft,border:`1px solid ${T.butter}`,marginBottom:14,display:"flex",alignItems:"center",gap:11}}>
              <div style={{width:36,height:36,borderRadius:10,background:T.paper,border:`1px solid ${T.butter}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="tag" size={18} color={T.warn} stroke={2.2}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:9.5,letterSpacing:1.2,color:T.warn,fontWeight:800,textTransform:"uppercase",marginBottom:1}}>On sale</div>
                <div style={{fontSize:13,fontWeight:700,color:T.forest,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{deal.dealLabel} at {deal.store.name}</div>
              </div>
              <RippleBtn sound="addCart" onClick={()=>toggleCart(deal.store.id, deal.id)}
                style={{background:inCart(deal.store.id, deal.id) ? T.basil : T.forest, border:"none", color:T.paper, borderRadius:10, padding:"7px 13px", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:5, flexShrink:0}}>
                <Icon name={inCart(deal.store.id, deal.id) ? "check" : "plus"} size={13} color={T.paper} stroke={2.4}/>
                {inCart(deal.store.id, deal.id) ? "In cart" : "Add"}
              </RippleBtn>
            </div>
          )}

          {/* Watchlist row */}
          <div style={{padding:"12px 14px",borderRadius:14,background:T.paper,border:`1px solid ${T.hairline}`,marginBottom:14,boxShadow:T.shadowCard}}>
            <div style={{display:"flex",alignItems:"center",gap:11}}>
              <div style={{width:36,height:36,borderRadius:10,background:isWatched ? T.sageSoft : T.parchment,border:`1px solid ${isWatched ? T.sage : T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="bookmark" size={18} color={isWatched ? T.basil : T.ink50} stroke={2.2}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:T.forest,marginBottom:1}}>{isWatched ? "Watching" : "Track this item"}</div>
                <div style={{fontSize:11,color:T.ink50}}>
                  {isWatched ? "We'll alert you when it drops below your target" : "Get notified when it hits your price"}
                </div>
              </div>
              <button onClick={()=>{
                  // toggleWatchlist expects a deal-shape; build a minimal one
                  toggleWatchlist({
                    id: item.id, name: item.name, category: item.category,
                    salePrice: cur, regPrice: avg,
                    store: deal?.store || null,
                  });
                }}
                style={{background:isWatched ? T.berry+"14" : T.sageSoft, border:`1px solid ${isWatched ? T.berry+"33" : T.sage}`, color:isWatched ? T.berry : T.basil, borderRadius:10, padding:"7px 13px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", flexShrink:0}}>
                {isWatched ? "Remove" : "Watch"}
              </button>
            </div>
            {/* Target editor — only when watched */}
            {isWatched && (
              <div style={{marginTop:10,padding:"10px 12px",borderRadius:11,background:T.parchment,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",gap:9}}>
                <Icon name="target" size={14} color={T.basil} stroke={2.2}/>
                <span style={{fontSize:11,fontWeight:700,color:T.ink70,letterSpacing:.2}}>Alert me at</span>
                {editingTarget ? (
                  <>
                    <span style={{fontSize:13,fontWeight:800,color:T.basil}}>$</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={tempTarget}
                      onChange={e=>setTempTarget(e.target.value)}
                      onBlur={saveTarget}
                      onKeyDown={e=>{ if(e.key==="Enter") saveTarget(); if(e.key==="Escape") setEditingTarget(false); }}
                      autoFocus
                      style={{flex:1,minWidth:0,fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:800,color:T.basil,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:7,padding:"3px 7px",outline:"none"}}
                    />
                  </>
                ) : (
                  <button onClick={startEditTarget}
                    style={{flex:1,textAlign:"left",background:"transparent",border:"none",fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:800,color:T.basil,cursor:"pointer",padding:0}}>
                    {target != null ? `$${target.toFixed(2)}` : "Set target"}
                  </button>
                )}
                {!editingTarget && (
                  <button onClick={startEditTarget}
                    style={{background:"transparent",border:"none",cursor:"pointer",padding:4,display:"flex",alignItems:"center"}}>
                    <Icon name="edit" size={13} color={T.ink50} stroke={2.2}/>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Where to buy */}
          {carriers.length > 0 && (
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:9}}>
                <Icon name="store" size={14} color={T.ink50} stroke={2.2}/>
                <div style={{fontSize:10,letterSpacing:1.4,color:T.ink50,fontWeight:800,textTransform:"uppercase"}}>Where to buy</div>
              </div>
              <div style={{display:"grid",gap:7}}>
                {carriers.map((c, i) => {
                  const price = c.salePrice ?? c.regPrice;
                  const isCheapest = i === 0;
                  const onSaleHere = c.salePrice != null && c.regPrice != null && c.salePrice < c.regPrice;
                  return (
                    <div key={`${c.store.id}-${i}`}
                      style={{display:"flex",alignItems:"center",gap:11,padding:"10px 12px",background:T.paper,border:`1px solid ${isCheapest ? T.sage : T.hairline}`,borderRadius:12,boxShadow:isCheapest ? T.shadowCard : "none"}}>
                      <div style={{width:32,height:32,borderRadius:9,background:c.store.color || T.parchment,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0,color:T.paper,fontWeight:800,fontFamily:FONT_DISPLAY}}>
                        {c.store.logo || c.store.name?.[0] || "?"}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12.5,fontWeight:700,color:T.forest,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.store.name}</div>
                        <div style={{display:"flex",alignItems:"center",gap:5,marginTop:2,fontSize:10,color:T.ink50,fontWeight:600}}>
                          {onSaleHere && (
                            <span style={{display:"inline-flex",alignItems:"center",gap:3,color:T.warn,fontWeight:700}}>
                              <Icon name="tag" size={10} color={T.warn} stroke={2.4}/>
                              {c.dealLabel || "On sale"}
                            </span>
                          )}
                          {!onSaleHere && <span>Regular price</span>}
                        </div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",flexShrink:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:800,color:isCheapest ? T.basil : T.forest,letterSpacing:-.3,lineHeight:1.1}}>${price?.toFixed(2)}</div>
                        {isCheapest && <div style={{fontSize:9,color:T.basil,fontWeight:800,letterSpacing:.5,marginTop:2,textTransform:"uppercase"}}>Cheapest</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{display:"flex",gap:8,padding:"12px 16px 16px",borderTop:`1px solid ${T.hairline}`,background:T.paper,flexShrink:0}}>
          {deal && (
            <RippleBtn sound="addCart" onClick={()=>{ toggleCart(deal.store.id, deal.id); }}
              style={{flex:1, background:inCart(deal.store.id, deal.id) ? T.basil : T.forest, border:"none", color:T.paper, borderRadius:11, padding:"11px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6}}>
              <Icon name={inCart(deal.store.id, deal.id) ? "check" : "plus"} size={14} color={T.paper} stroke={2.4}/>
              {inCart(deal.store.id, deal.id) ? "In cart" : "Add to cart"}
            </RippleBtn>
          )}
          <RippleBtn sound="tabSwitch" onClick={()=>{ onAskSage && onAskSage(item); }}
            style={{flex:1, background:T.parchment, border:`1px solid ${T.hairline}`, color:T.forest, borderRadius:11, padding:"11px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6}}>
            <Icon name="sparkle" size={14} color={T.basil} stroke={2.2}/>
            Ask Sage
          </RippleBtn>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════ */
function CartIQApp() {
  // Core state — persisted across sessions via usePersistedState
  const [tab, setTab] = useState("dashboard");
  const [priceIQFilter, setPriceIQFilter] = useState("all");
  const [priceIQSearch, setPriceIQSearch] = useState("");
  const [priceIQSort, setPriceIQSort] = useState("opportunity");
  const [priceIQDetail, setPriceIQDetail] = useState(null); // item id when sheet open
  // Pantry tab — filter/sort/search state, mirrors Price IQ pattern
  const [pantryFilter, setPantryFilter] = useState("all");
  const [pantrySearch, setPantrySearch] = useState("");
  const [pantrySort, setPantrySort] = useState("expiry"); // expiry | supply | alpha | recent
  // My Items tab — subview selector. Session-scoped (matches pantryFilter).
  // Tab id stays "pantry" so existing setTab("pantry") call sites keep working;
  // myItemsView is the inner switch between Pantry / Lists / All.
  const [myItemsView, setMyItemsView] = useState("pantry"); // pantry | lists | all
  const [cartMap, setCartMap] = usePersistedState("cartiq_cart", {}, { migrate: migrateCartMap });
  const [pantry, setPantry] = usePersistedState("cartiq_pantry", PANTRY_DEFAULT, { migrate: migrateItemArray });
  const [budget, setBudget] = usePersistedState("cartiq_budget", 150);
  const [toast, setToast] = useState(null);
  const [checkout, setCheckout] = useState(null);
  // Phase 4a: transient pointer for the post-checkout Trip Receipt. Auto-set
  // at the end of handleConfirmOrder and re-set when a row in the trips
  // history list is tapped. Not persisted — the durable record is txnHistory.
  const [tripReceipt, setTripReceipt] = useState(null);
  // Session-scoped disclosure tracking. We surface a one-time modal explaining
  // the preview-mode handoff the FIRST time a user reaches the redirect step
  // in any given session. Not persisted — returning users see it again next
  // session, which is intentional: the disclosure matters every time we hand
  // them off to Instacart, and silencing it forever after one dismissal would
  // make the UX feel less honest, not more polished.
  const [integrationDisclosureSeen, setIntegrationDisclosureSeen] = useState(false);
  const [integrationDisclosureOpen, setIntegrationDisclosureOpen] = useState(false);
  // Ref holds the URL to open AFTER the user dismisses the disclosure modal.
  // Using a ref (not state) avoids re-render churn on the redirect path.
  const pendingInstacartUrlRef = useRef(null);

  // Single chokepoint for opening Instacart. Called by CheckoutModal when the
  // user hits "Open Instacart Now" or "Reopen". When integration is live, opens
  // immediately. When in preview mode AND it's the first time this session,
  // shows the disclosure modal first — the modal's continue button then opens
  // the URL via the same window.open path.
  const openInstacartUrl = useCallback((url) => {
    if (!url) return;
    if (!INSTACART_INTEGRATION_LIVE && !integrationDisclosureSeen) {
      pendingInstacartUrlRef.current = url;
      setIntegrationDisclosureOpen(true);
      return;
    }
    try { window.open(url, "_blank", "noopener,noreferrer"); } catch(e) {}
  }, [integrationDisclosureSeen]);

  // Continue handler from the disclosure modal — marks it seen, closes the
  // modal, opens the pending URL.
  const handleIntegrationDisclosureContinue = useCallback(() => {
    const url = pendingInstacartUrlRef.current;
    pendingInstacartUrlRef.current = null;
    setIntegrationDisclosureSeen(true);
    setIntegrationDisclosureOpen(false);
    if (url) {
      try { window.open(url, "_blank", "noopener,noreferrer"); } catch(e) {}
    }
  }, []);

  // Cancel handler — closes the modal WITHOUT opening Instacart, but also
  // WITHOUT marking the disclosure as seen. Next attempt re-shows it.
  const handleIntegrationDisclosureClose = useCallback(() => {
    pendingInstacartUrlRef.current = null;
    setIntegrationDisclosureOpen(false);
  }, []);
  const [txnHistory, setTxnHistory] = usePersistedState("cartiq_txn_history", MOCK_TXN_HISTORY.slice(0,3));
  // Rewards ledger — append-only history of theoretical rewards per order.
  // Shape: [{id, date, timestamp, storeId, storeName, cartTotal, reward}]
  // Empty by default (even demo installs start at zero) so the first real
  // order feels like the user's first contribution. The sum of `reward`
  // across all entries is the displayed balance.
  const [rewardsLedger, setRewardsLedger] = usePersistedState("cartiq_rewards_ledger", []);
  // Filter state for the rewards history list — All | Confirmed | Pending | Expired.
  // Persisted so users keep their slice across sessions.
  const [rewardsFilter, setRewardsFilter] = usePersistedState("cartiq_rewards_filter", "all");
  // Whether the admin tools panel on the rewards screen is expanded.
  // Collapsed by default so the panel doesn't overwhelm the user-facing UI.
  const [adminToolsOpen, setAdminToolsOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Controls the "What's real vs demo" explainer modal — reached from More → Help
  const [whatsRealOpen, setWhatsRealOpen] = useState(false);
  const [loyalty, setLoyalty] = useState({
    publix:1240, sprouts:580, kroger:3870, wholefoods:0, walmart:0, target:425,
  });
  // Linked offers — each offer is keyed by program. "clipped" reflects user action.
  const [clippedOffers, setClippedOffers] = useState({});
  const toggleClipOffer = (id) => setClippedOffers(p => ({...p, [id]: !p[id]}));
  const [receiptDone, setRDone] = useState(false);

  // Store detail page — #1
  const [activeStore, setActiveStore] = useState(null);
  const [catFilter, setCatFilter] = useState(null);

  // Household
  const [hhMsg, setHhMsg] = useState("");
  const [messages, setMessages] = useState([
    {who:2,text:"Added salmon — BOGO at Publix! 🐟",time:"10:42 AM"},
    {who:3,text:"Can we get more almond butter? Almost out 🫙",time:"11:05 AM"},
  ]);

  // Share
  const [sharePeriod, setSharePeriod] = useState("month");
  const [shareCopied, setShareCopied] = useState(false);
  const [shareUserName, setShareUserName] = useState("Sarah");

  // Onboarding
  const [tourStep, setTourStep] = useState(0);
  const [tourActive, setTourActive] = useState(()=>{ try{ return !localStorage.getItem("cartiq_toured"); }catch{ return true; }});

  // User name — set during onboarding, used to personalize the dashboard greeting
  const [userName, setUserName] = usePersistedState("cartiq_user_name", "");

  const urgentTickerRef = useRef(null);

  // Ask-Sage modal (per-deal Q&A) — replaces the old global assistant
  const [askSageDeal, setAskSageDeal] = useState(null);  // the deal to ask about, or null

  // Substitutions — persisted, keyed by deal id.
  // Shape: { [dealId]: { pref, sub, votes, maxIncrease? } }
  //   pref         — SUB_OPTIONS key ("brand_only" | "similar_ok" | "any")
  //   sub          — user-picked or custom-typed alternative item name
  //   votes        — { [householdMemberId]: "up" | "down" }
  //   maxIncrease  — Phase 4c, OPTIONAL. Fraction (0.10/0.25) of the
  //                  original price the user will accept on a swap; 0.0
  //                  means same-price-only. Absent/null = no cap.
  //                  Existing entries without this field must behave
  //                  exactly as before — see SUB_CAP_PRESETS for values.
  const [subPrefs, setSubPrefs] = usePersistedState("cartiq_subprefs", {});
  const [subModalDeal, setSubModalDeal] = useState(null);

  // Location: ZIP + selected stores — skip persist when null
  const [userLocation, setUserLocation] = usePersistedState("cartiq_location", null, {
    skipSaveIf: v => v == null,
  });
  const [locationOpen, setLocationOpen] = useState(false);

  // Watchlist (save for later + price monitoring)
  const [watchlist, setWatchlist] = usePersistedState("cartiq_watchlist", [], { migrate: migrateItemArray });
  const [unreadAlerts, setUnreadAlerts] = usePersistedState("cartiq_alerts", []);  // alerts user hasn't dismissed
  // My Items — unified catalog. Pantry, Lists, and "All" are subviews of this.
  // Ticket 1 establishes the data layer; consumers (Pantry render, Lists view)
  // are swapped over in subsequent tickets. The migration effect below copies
  // legacy pantry data into my_items on first load when my_items is empty.
  const [myItems, setMyItems] = usePersistedState("cartiq_my_items", [], { migrate: migrateItemArray });
  const [lists, setLists] = usePersistedState("cartiq_lists", []);
  const [listSchedules, setListSchedules] = usePersistedState("cartiq_list_schedules", {});
  // One-shot pantry → my_items migration. Runs after both stores hydrate.
  // Guarded by a ref so it only fires once per session — on subsequent
  // mounts after the first migration, my_items has data and the helper
  // returns null, so this is doubly safe.
  const myItemsMigratedRef = useRef(false);
  useEffect(() => {
    if (myItemsMigratedRef.current) return;
    const migrated = migratePantryToMyItems(pantry, myItems);
    if (migrated) {
      myItemsMigratedRef.current = true;
      setMyItems(migrated);
    } else if (Array.isArray(myItems) && myItems.length > 0) {
      myItemsMigratedRef.current = true;
    }
  }, [pantry, myItems, setMyItems]);
  // Items added to Smart Cart via universal search (Phase 2).
  // Each entry is a "synthetic deal" — a deal-shaped object matching the shape
  // the Smart Cart engine already understands, but representing a regular-priced
  // search-added item rather than a store promotion. Union'd into `allDeals` so
  // Smart Cart sees them alongside store deals without any routing changes.
  // savings = maxPrice-across-scope minus the chosen store's price — Option B
  // from the Phase 2 design: the true value Smart Cart routing delivers.
  const [searchAddedItems, setSearchAddedItems] = usePersistedState("cartiq_search_added_items", []);

  // Phase A — Staples list. The user's chosen "items I usually buy." Different
  // from pantry (which tracks current inventory) and different from cartMap
  // (which tracks what's in Smart Cart right now). This is the durable
  // "profile" of what to seed Pantry/Cart from on Phase C "Rebuild my usual."
  const [staples, setStaples] = usePersistedState("cartiq_staples", [], {
    skipSaveIf: v => !v
  });

  // Phase A — Modal open/close. Distinct from the persisted "seen" flag below
  // so we can re-open the modal from settings later without affecting onboarding.
  const [staplesPickerOpen, setStaplesPickerOpen] = useState(false);

  // Phase 2b — recent searches. Capped array of the last N distinct queries.
  // Newest first, deduplicated. Shown as chips in the empty search overlay so
  // repeat lookups ("what was that kombucha thing?") are one tap away.
  const [recentSearches, setRecentSearches] = usePersistedState("cartiq_recent_searches", []);
  // Phase 2b — pantry "add your own item" modal state. Opens from the Pantry
  // tab's "+ Add your own item" button. Closed on save or dismiss.
  const [addOwnItemOpen, setAddOwnItemOpen] = useState(false);
  // Create-list modal — opens from My Items → Lists subview
  const [createListOpen, setCreateListOpen] = useState(false);
  // Active list detail — when set, Lists subview shows the per-list view
  const [activeListId, setActiveListId] = useState(null);
  // Multi-select picker for "+ Add items" inside list detail
  const [addItemsToListOpen, setAddItemsToListOpen] = useState(false);
  const [notifsOpen, setNotifsOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  // Phase 2 — universal search state.
  // searchOverlay: null when closed, or { initialScopeStoreId } when open.
  //   initialScopeStoreId is set when opened from a Store Detail page (scoped)
  //   or undefined when opened from the top-bar icon / Smart Cart embedded bar.
  // detailItem: null when closed, or the SearchResult object for the open sheet.
  // These are parallel so a user can close the detail sheet and still be in
  // search results (stay-in-context behavior from the Phase 2 design).
  const [searchOverlay, setSearchOverlay] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [dismissedSplit, setDismissedSplit] = useState([]);  // item ids the user swiped away from Smart Cart this session
  const [splitQty, setSplitQty] = useState({});  // { [itemId]: chosenQty } — overrides for split-cart line quantities

  // ── Deal-pricing helpers: all delegate to the top-level pricing module.
  // `getSplitQty` is kept in scope because it reads the `splitQty` state override;
  // the rest are one-line wrappers for readability at call sites.
  const isBogoDeal = (deal) => {
    const t = dealType(deal);
    return t === DEAL_TYPES.BOGO_FREE || t === DEAL_TYPES.BOGO_50;
  };
  const isB2G1Deal = (deal) => dealType(deal) === DEAL_TYPES.B2G1;
  const getSplitQty = (item) => {
    if (splitQty[item.id] != null) return splitQty[item.id];
    return defaultBundleQty(item.bestDeal);
  };
  const computeLineTotal = (deal, qty) => effectiveLinePrice(deal, qty);
  const [bogoFilter, setBogoFilter] = useState("all");  // all | bundle | bogoFree | bogo50 | b2g1 | bigDeal | allTimeLow | endingToday | highDiscount | under5 | watchlist | pantry | notInCart | organic | <storeId>
  const [showYoursFilters, setShowYoursFilters] = useState(false);  // "Yours" filter group expansion toggle
  const [activeDealModal, setActiveDealModal] = useState(null);  // {deal, store} or null
  // Original code only saved visitedTabs once the array was non-empty — preserve that guard
  const [visitedTabs, setVisitedTabs] = usePersistedState("cartiq_visited_tabs", [], {
    skipSaveIf: v => !v || v.length === 0,
  });
  // Event-triggered tooltip flags — one-shot booleans, separate from the
  // tab-visit array because their triggers are conditional renders, not tab
  // navigations.
  const [tooltipTrustSeen, setTooltipTrustSeen] = usePersistedState("cartiq_tooltip_trust_seen", false);
  const [tooltipPriceMemorySeen, setTooltipPriceMemorySeen] = usePersistedState("cartiq_tooltip_price_memory_seen", false);
  const [activeTip, setActiveTip] = useState(null);  // { tab, ...tipData } or null
  const [dietaryRestrictions, setDietaryRestrictions] = usePersistedState("cartiq_dietary", []);  // array of restriction IDs
  const [dietarySetupOpen, setDietarySetupOpen] = useState(false);  // onboarding + settings modal
  const [healthGoalsOpen, setHealthGoalsOpen] = useState(false);  // onboarding + settings modal (Nutrition IQ goals)
  const [projectedSavingsOpen, setProjectedSavingsOpen] = useState(false);  // first-run value reveal (fires once via cartiq_savings_revealed)
  const [shareSavingsOpen, setShareSavingsOpen] = useState(false);  // shareable savings card
  const [savingsPeriod, setSavingsPeriod] = useState("month");  // "month" | "year" | "all"
  // Skip persist when null (nothing planned yet)
  const [plannedShop, setPlannedShop] = usePersistedState("cartiq_planned_shop", null, {
    skipSaveIf: v => v == null,
  });
  const [shopPlannerOpen, setShopPlannerOpen] = useState(false);
  const [emailAlerts, setEmailAlerts] = usePersistedState("cartiq_email_alerts", { email: "", enabled: false, frequency: "daily" });
  const [emailSetupOpen, setEmailSetupOpen] = useState(false);
  const [storeAlertPrefs, setStoreAlertPrefs] = usePersistedState("cartiq_store_alert_prefs", { enabled: false, perStore: {} });
  const [storeAlertsOpen, setStoreAlertsOpen] = useState(false);
  // Per-item price memory — appended on checkout. Capped at PRICE_MEMORY_CAP entries
  // per item. Shape designed so IDP receipt data can layer on later without migration.
  const [priceMemory, setPriceMemory] = usePersistedState("cartiq_price_memory", {});
  // Active health goals (array of HEALTH_GOALS ids). Drives the Nutrition IQ
  // rollup at the top of Smart Cart. Empty = rollup still shows facts but no
  // goal-fit coloring.
  const [healthProfile, setHealthProfile] = usePersistedState("cartiq_health_profile", []);
  // Sound on/off — bridge React state to the module-level audio gate.
  // Default ON to match historical behavior. Returning users get their saved
  // pref applied as soon as window.storage hydrates (~50ms after mount).
  const [soundOn, setSoundOn] = usePersistedState("cartiq_sound_on", true);
  const [soundSetupOpen, setSoundSetupOpen] = useState(false);
  useEffect(() => { setSoundEnabled(soundOn); }, [soundOn]);
  // Haptics on/off — same bridge pattern. iOS users won't notice a difference
  // (Apple disabled navigator.vibrate). Android users get tactile confirmation
  // on Smart Cart swipes and Shopping Mode item checks.
  const [hapticsOn, setHapticsOn] = usePersistedState("cartiq_haptics_on", true);
  useEffect(() => { setHapticsEnabled(hapticsOn); }, [hapticsOn]);
  const [voiceListening, setVoiceListening] = useState(false);
  const voiceRecognitionRef = useRef(null);
  const [shoppingModeOpen, setShoppingModeOpen] = useState(false);
  const [tipsModalOpen, setTipsModalOpen] = useState(false);

  // Monthly savings milestones — lifetime-feel rewards
  const MILESTONES = [
    { amt: 50,  label: "Saver",      icon: "🌱", color: T.leaf },
    { amt: 100, label: "Pro Saver",  icon: "🥉", color: T.apricot },
    { amt: 250, label: "Elite",      icon: "🥈", color: T.ink50 },
    { amt: 500, label: "Master",     icon: "🥇", color: T.butter },
    { amt: 1000,label: "Legend",     icon: "👑", color: T.basil },
  ];

  // Persistence is now handled automatically by usePersistedState — see hook at top of file.
  // Each persisted state declaration above loads on mount and saves on change.

  // Trigger first-visit tooltips when user lands on a tab they've never seen
  // (but only after the main tour is dismissed to avoid double-popups)
  useEffect(() => {
    if (tourActive || activeStore) return;
    const tip = FIRST_VISIT_TIPS[tab];
    if (tip && !visitedTabs.includes(tab)) {
      // Small delay so the tab transition finishes first
      const t = setTimeout(() => {
        setActiveTip({ tab, ...tip });
        setVisitedTabs(prev => prev.includes(tab) ? prev : [...prev, tab]);
      }, 450);
      return () => clearTimeout(t);
    }
  }, [tab, tourActive, activeStore, visitedTabs]);

  // ── Watchlist actions ──
  const notify = useCallback((msg, opts = {}) => {
    // opts: { type: "ok"|"warn"|"error", action: { label, onClick }, duration }
    const toastData = typeof opts === "string"
      ? { msg, type: opts }  // backward compat for old notify(msg, "ok")
      : { msg, type: opts.type || "ok", action: opts.action };
    setToast(toastData);
    SFX.notify();
    setTimeout(() => setToast(null), opts.duration || 3200);
  }, []);

  const isWatched = useCallback((itemName, storeId) => {
    return watchlist.some(w => w.name === itemName && (storeId == null || w.storeId === storeId));
  }, [watchlist]);

  const toggleWatchlist = useCallback((deal) => {
    setWatchlist(prev => {
      const existingIdx = prev.findIndex(w => w.name === deal.name && w.storeId === deal.store?.id);
      if (existingIdx >= 0) {
        // Remove — offer undo
        const removedItem = prev[existingIdx];
        SFX.removeCart();
        notify(`Removed ${removedItem.name} from watchlist`, {
          type: "warn",
          action: {
            label: "Undo",
            onClick: () => setWatchlist(p => [...p, removedItem]),
          },
        });
        return prev.filter((_, i) => i !== existingIdx);
      } else {
        // Add — silent, visual state (🔖 badge) is sufficient feedback
        const newItem = makeWatchlistItem({
          name: deal.name,
          category: deal.category,
          storeId: deal.store?.id,
          storeName: deal.store?.name,
          currentPrice: deal.salePrice || deal.origPrice,
          instacartProductId: deal.instacartProductId,
          itemId: deal.id,
        });
        SFX.addCart();
        return [...prev, newItem];
      }
    });
  }, []);

  const updateWatchTarget = useCallback((wlId, newTarget) => {
    setWatchlist(prev => prev.map(w => w.id === wlId ? { ...w, targetPrice: parseFloat(newTarget) || w.targetPrice } : w));
  }, []);

  const updateWatchAlertPref = useCallback((wlId, prefKey, value) => {
    setWatchlist(prev => prev.map(w => w.id === wlId ? { ...w, [prefKey]: value } : w));
  }, []);

  const removeFromWatchlist = useCallback((wlId) => {
    setWatchlist(prev => {
      const removedItem = prev.find(w => w.id === wlId);
      const next = prev.filter(w => w.id !== wlId);
      if (removedItem) {
        notify(`Removed ${removedItem.name} from watchlist`, {
          type: "warn",
          action: {
            label: "Undo",
            onClick: () => setWatchlist(curr => [...curr, removedItem]),
          },
        });
      }
      return next;
    });
    SFX.removeCart();
  }, [notify]);

  // Price refresh — for now uses current STORES catalog as the data source
  // When Instacart Connect is live, this fetches real-time prices server-side
  const refreshWatchlistPrices = useCallback(() => {
    SFX.scan();
    let alertsFired = [];
    setWatchlist(prev => prev.map(w => {
      // Look for current price in active deals catalog
      const currentDeal = STORES.flatMap(s => s.deals.map(d => ({...d, storeId: s.id})))
        .find(d => d.name === w.name && (w.storeId == null || d.storeId === w.storeId));
      if (!currentDeal) return w;  // No data for this item
      const observedPrice = currentDeal.salePrice || currentDeal.origPrice;
      const result = checkWatchlistPrice(w, observedPrice);
      if (result.newAlerts.length > 0) {
        alertsFired.push(...result.newAlerts);
      }
      return result.item;
    }));
    if (alertsFired.length > 0) {
      setUnreadAlerts(prev => [...alertsFired, ...prev]);
      notify(`🔔 ${alertsFired.length} new price alert${alertsFired.length>1?"s":""}!`);
      SFX.cash();
    } else {
      notify("✓ Prices checked — no new alerts");
    }
  }, [watchlist]);

  // Auto-check prices on app load + when watchlist changes (passive)
  useEffect(() => {
    if (watchlist.length === 0) return;
    // Run a passive check after a brief delay (no notification spam on every render)
    const t = setTimeout(() => {
      let alertsFired = [];
      setWatchlist(prev => prev.map(w => {
        const currentDeal = STORES.flatMap(s => s.deals.map(d => ({...d, storeId: s.id})))
          .find(d => d.name === w.name && (w.storeId == null || d.storeId === w.storeId));
        if (!currentDeal) return w;
        const observedPrice = currentDeal.salePrice || currentDeal.origPrice;
        // Only update if price actually changed
        if (observedPrice === w.currentPrice) return w;
        const result = checkWatchlistPrice(w, observedPrice);
        if (result.newAlerts.length > 0) alertsFired.push(...result.newAlerts);
        return result.item;
      }));
      if (alertsFired.length > 0) setUnreadAlerts(prev => [...alertsFired, ...prev]);
    }, 1500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Only on mount

  const dismissAlert = useCallback((idx) => {
    setUnreadAlerts(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const dismissAllAlerts = useCallback(() => {
    setUnreadAlerts([]);
    setNotifsOpen(false);
  }, []);

  // Dismiss tour, then prompt for location if not yet set
  function dismissTour() {
    setTourActive(false);
    try{localStorage.setItem("cartiq_toured","1");}catch{}
    if (!userLocation) setTimeout(() => setLocationOpen(true), 350);
  }

  function handleLocationSave(loc) {
    setUserLocation(loc);
    setLocationOpen(false);
    // Phase A — Chain: Staples → Dietary (if neither seen).
    // Falls back to direct-to-dietary if staples picker is disabled.
    try {
      const staplesSeen = !!localStorage.getItem("cartiq_staples_seen");
      const dietarySeen = !!localStorage.getItem("cartiq_dietary_seen");
      if (STAPLES_PICKER_ENABLED && !staplesSeen) {
        setTimeout(() => setStaplesPickerOpen(true), 350);
      } else if (!dietarySeen) {
        setTimeout(() => setDietarySetupOpen(true), 350);
      }
    } catch(e) {}
  }

  // Open the projected-savings modal if it hasn't been seen yet and the user
  // has at least one staple to compute against. Pulled out so both the
  // dietary and health-goals branches of the onboarding chain can call it.
  function maybeRevealProjectedSavings() {
    try {
      if (!localStorage.getItem("cartiq_savings_revealed") && staples && staples.length > 0) {
        setTimeout(() => setProjectedSavingsOpen(true), 350);
      }
    } catch(e) {}
  }

  function handleDietarySave(restrictions) {
    setDietaryRestrictions(restrictions);
    setDietarySetupOpen(false);
    try { localStorage.setItem("cartiq_dietary_seen", "1"); } catch(e) {}
    if (restrictions.length > 0) {
      notify(`✓ Hiding ${restrictions.length} category${restrictions.length>1?" groups":""} from deals`);
    }
    // Chain: HealthGoals first (if not seen), else fall through to projected
    // savings. Health goals exists so Nutrition IQ has data on first paint.
    try {
      if (!localStorage.getItem("cartiq_health_goals_seen")) {
        setTimeout(() => setHealthGoalsOpen(true), 350);
        return;
      }
    } catch(e) {}
    maybeRevealProjectedSavings();
  }

  function handleHealthGoalsSave(goals) {
    setHealthProfile(goals);
    setHealthGoalsOpen(false);
    try { localStorage.setItem("cartiq_health_goals_seen", "1"); } catch(e) {}
    if (goals.length > 0) {
      notify(`✓ Tracking ${goals.length} health goal${goals.length>1?"s":""} in Nutrition IQ`);
    }
    maybeRevealProjectedSavings();
  }

  // Close projected-savings modal. Always sets the one-time flag so the modal
  // never reopens. If `rebuild` is true, also merges the preview into cartMap
  // and switches to Smart Cart (mirrors handleRebuildCart).
  function handleProjectedSavingsClose(rebuild) {
    setProjectedSavingsOpen(false);
    try { localStorage.setItem("cartiq_savings_revealed", "1"); } catch(e) {}
    if (rebuild) handleRebuildCart();
  }

  // Phase A — Persist staples, seed myItems (pantry data model) so the Pantry
  // tab isn't empty on first load, and seed cartMap against the first active
  // store so Smart Cart shows real routing instead of demo numbers. Then chain
  // to the dietary modal if it hasn't been seen yet.
  function handleStaplesSave(ids) {
    setStaples(ids);
    setStaplesPickerOpen(false);
    try { localStorage.setItem("cartiq_staples_seen", "1"); } catch(e) {}

    if (ids && ids.length > 0) {
      const today = new Date().toISOString().slice(0,10);
      setMyItems(prev => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const indexById = new Map(list.map((it, i) => [it.id, i]));
        ids.forEach(id => {
          const spec = STAPLES_PICKER_ITEMS.find(s => s.id === id);
          if (!spec) return;
          const existingIdx = indexById.get(id);
          if (existingIdx != null) {
            // Already in catalog — make sure it shows in Pantry
            if (!list[existingIdx].inPantry) {
              list[existingIdx] = { ...list[existingIdx], inPantry: true };
            }
          } else {
            list.push({
              ...MY_ITEM_DEFAULT_FLAGS,
              id: spec.id,
              name: spec.name,
              qty: spec.defaultQty,
              unit: spec.defaultUnit,
              inPantry: true,
              lists: [],
              listState: {},
              addedFrom: "staples",
              addedDate: today,
            });
          }
        });
        return list;
      });

      // Seed Smart Cart against the first active store so the dashboard hero
      // shows a real number on first paint. Smart Cart's routing engine will
      // re-route to the optimal store per item once the dashboard mounts.
      const firstStore = activeStores && activeStores[0];
      if (firstStore) {
        setCartMap(prev => {
          const next = { ...(prev || {}) };
          ids.forEach(id => { next[`${firstStore.id}_${id}`] = true; });
          return next;
        });
      }

      notify(`✓ Added ${ids.length} staple${ids.length>1?"s":""} to your pantry`);
    }

    try {
      if (!localStorage.getItem("cartiq_dietary_seen")) {
        setTimeout(() => setDietarySetupOpen(true), 350);
      }
    } catch(e) {}
  }

  // Phase A — Skip path. Mark the modal as seen so we don't nag, then chain
  // to dietary just like Save would. No seeding, no notify.
  function handleStaplesSkip() {
    setStaplesPickerOpen(false);
    try { localStorage.setItem("cartiq_staples_seen", "1"); } catch(e) {}
    try {
      if (!localStorage.getItem("cartiq_dietary_seen")) {
        setTimeout(() => setDietarySetupOpen(true), 350);
      }
    } catch(e) {}
  }

  function handleShopPlanSave(plan) {
    setPlannedShop(plan);
    setShopPlannerOpen(false);
    if (plan) {
      notify(`📅 Shop planned for ${plan.label} — deals filtered to still-valid then`);
    } else {
      notify("Shop plan cleared");
    }
  }

  function handleEmailAlertsSave(prefs) {
    setEmailAlerts(prefs);
    setEmailSetupOpen(false);
    if (prefs.enabled) {
      notify(`📧 Email alerts on — sending ${prefs.frequency} digest to ${prefs.email}`);
    }
  }

  function handleStoreAlertsSave(prefs) {
    setStoreAlertPrefs(prefs);
    setStoreAlertsOpen(false);
    notify("✓ Store alerts updated");
  }

  // Append a price point to per-item memory; cap at PRICE_MEMORY_CAP entries.
  // Called on checkout for every line in cartDeals. Once IDP receipts are wired,
  // a second call site there will populate from real spend with the same shape.
  function logPurchasedPrice(itemId, price, storeId) {
    if (!itemId || typeof price !== "number" || price <= 0) return;
    setPriceMemory(prev => {
      const arr = prev?.[itemId] ? [...prev[itemId]] : [];
      arr.push({ date: new Date().toISOString(), price, storeId: storeId || null });
      return { ...prev, [itemId]: arr.slice(-PRICE_MEMORY_CAP) };
    });
  }

  // Voice-add items to watchlist using Web Speech API
  function startVoiceWatchlistAdd() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      notify("Voice input not supported on this browser", { type: "warn" });
      return;
    }
    if (voiceListening && voiceRecognitionRef.current) {
      voiceRecognitionRef.current.stop();
      return;
    }
    const recognition = new SR();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = () => { setVoiceListening(true); SFX.scan(); };
    recognition.onerror = (e) => {
      setVoiceListening(false);
      if (e.error === "not-allowed") {
        notify("Microphone permission denied", { type: "warn" });
      } else if (e.error !== "no-speech" && e.error !== "aborted") {
        notify(`Voice error: ${e.error}`, { type: "warn" });
      }
    };
    recognition.onend = () => { setVoiceListening(false); };
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript.trim();
      if (!transcript) return;
      // Try to match against MASTER_LIST by name (fuzzy)
      const lower = transcript.toLowerCase();
      const match = MASTER_LIST.find(m => lower.includes(m.name.toLowerCase())) ||
                    MASTER_LIST.find(m => {
                      const words = m.name.toLowerCase().split(/\s+/);
                      return words.some(w => w.length > 3 && lower.includes(w));
                    });
      if (match) {
        // Find a deal for this item to add, or create a manual watchlist entry
        const deal = allDeals.find(d => d.id === match.id);
        if (deal) {
          toggleWatchlist(deal);
          notify(`🎤 Added "${match.name}" to watchlist`);
        } else {
          // Create a freeform watchlist entry without a store
          setWatchlist(prev => [...prev, makeWatchlistItem({
            name: match.name,
            category: match.category,
            storeId: null,
            storeName: "Any retailer",
            currentPrice: 0,
            itemId: match.id,
          })]);
          notify(`🎤 Watching "${match.name}" (no current deal)`);
        }
      } else {
        // Freeform name the user said — create entry with no match
        setWatchlist(prev => [...prev, makeWatchlistItem({
          name: transcript,
          category: null,
          storeId: null,
          storeName: "Any retailer",
          currentPrice: 0,
        })]);
        notify(`🎤 Watching "${transcript}"`);
      }
    };

    try {
      recognition.start();
      voiceRecognitionRef.current = recognition;
    } catch(e) {
      setVoiceListening(false);
      notify("Voice start failed — try again", { type: "warn" });
    }
  }

  const inCart = useCallback((sid,iid)=>!!cartMap[`${sid}_${iid}`],[cartMap]);
  const cartCount = useCallback((sid)=>Object.keys(cartMap).filter(k=>k.startsWith(sid+"_")&&cartMap[k]).length,[cartMap]);
  const toggleCart = useCallback((sid,iid)=>{ const key=`${sid}_${iid}`; setCartMap(p=>{const adding=!p[key]; if(adding) SFX.addCart(); else SFX.removeCart(); return {...p,[key]:!p[key]};}); },[]);

  // ── Derived state — memoized to prevent recalc on every render ──

  // activeStores: filter STORES by user's selected chains, or show all if no location set yet
  const activeStores = useMemo(() => {
    if (!userLocation || !userLocation.stores || userLocation.stores.length === 0) return STORES;
    const selectedChainIds = new Set(userLocation.stores.map(s => s.chainId));
    const filtered = STORES.filter(s => selectedChainIds.has(s.id));
    return filtered.length > 0 ? filtered : STORES;  // safety fallback
  }, [userLocation]);

  // allDeals derived from active stores — pre-computes pricing fields once
  const allDeals = useMemo(() => {
    let deals = activeStores.flatMap(s => s.deals.map(d => ({...d, store: s})));
    // Merge in items added via universal search (Phase 2). Each searchAddedItem
    // is already in deal-shape; we just need to attach its .store object for
    // Smart Cart routing. We look up the store by id and skip if user doesn't
    // have that store in their active set (e.g., they deselected it after adding).
    if (searchAddedItems && searchAddedItems.length > 0) {
      for (const sa of searchAddedItems) {
        const store = activeStores.find(s => s.id === sa.storeId);
        if (!store) continue;
        deals.push({ ...sa, store });
      }
    }
    // Apply dietary restrictions — filter out items the user doesn't eat
    deals = deals.filter(d => matchesDietary(d, dietaryRestrictions));
    // Apply planned-shop filter — hide deals that won't be valid at the planned time
    if (plannedShop?.date) {
      const plannedDate = new Date(plannedShop.date);
      const now = new Date();
      const daysUntilShop = Math.max(0, Math.ceil((plannedDate - now) / (1000 * 60 * 60 * 24)));
      deals = deals.filter(d => d.expiresIn >= daysUntilShop);
    }
    // Pre-compute expensive pricing/type fields once so downstream renders
    // and filters don't recalculate them on every pass.
    return deals.map(d => ({
      ...d,
      _type: dealType(d),
      _unitPrice: computeUnitPrice(d),
      _effectivePerUnit: effectivePerUnit(d),
      _displayPrice: displayPrice(d),
      _isBundle: isBundleDeal(d),
      _defaultQty: defaultBundleQty(d),
      _requiresClip: requiresClipping(d),
    }));
  }, [activeStores, dietaryRestrictions, plannedShop, searchAddedItems]);

  // Phase C — Rebuild Smart Cart from the staples list. Idempotent —
  // running it twice produces the same result. Does not clear non-staple
  // items already in the cart; merges over them.
  // Defined here (not next to handleStaplesSave) because the dep array
  // references allDeals/activeStores, which must be initialized before this
  // useCallback runs — otherwise we hit a TDZ ReferenceError on first render.
  const handleRebuildCart = useCallback(() => {
    if (!staples || staples.length === 0) {
      setStaplesPickerOpen(true);
      return;
    }
    const result = rebuildCartFromStaples(staples, allDeals, activeStores);
    if (result.itemCount === 0) {
      notify("No deals found for your staples right now — try again later", "warn");
      return;
    }
    setCartMap(prev => ({ ...prev, ...result.cartUpdates }));
    setTab("split");
    SFX.success();
    const savingsLabel = result.estimatedSavings > 0
      ? ` · ~$${result.estimatedSavings.toFixed(2)} saved vs worst price`
      : "";
    notify(`✓ Rebuilt ${result.itemCount} staple${result.itemCount === 1 ? "" : "s"}${savingsLabel}`);
  }, [staples, allDeals, activeStores, setCartMap, setTab, notify]);

  // Phase C — Rebuild preview is computed once and shared by both surfaces
  // (dashboard prompt + Smart Cart empty-state). Gating happens at call sites:
  // the dashboard uses showRebuildCard (cart < 3), the empty-state uses
  // splitCart.length === 0. Preview itself only depends on staples + offers.
  const cartItemCount = useMemo(() => Object.keys(cartMap).filter(k => cartMap[k]).length, [cartMap]);
  const showRebuildCard = REBUILD_CART_ENABLED && cartItemCount < 3;
  const rebuildPreview = useMemo(() => {
    if (!staples || staples.length === 0) return null;
    return rebuildCartFromStaples(staples, allDeals, activeStores);
  }, [staples, allDeals, activeStores]);

  // Event-triggered first-visit tooltip — Trust badge.
  // Fires once when the user lands on any deal-rendering tab while at least
  // one visible deal has trust data. Defined here (not next to the tab-visit
  // effect at the top) because allDeals must be initialized first — otherwise
  // TDZ ReferenceError on first render.
  useEffect(() => {
    if (tourActive || activeStore || activeTip || tooltipTrustSeen) return;
    if (!["home","bogos","split"].includes(tab)) return;
    const hasTrust = allDeals.some(d => computeTrustBadge(d.id, d.salePrice || d.origPrice) !== null);
    if (!hasTrust) return;
    const t = setTimeout(() => {
      setActiveTip({ tab: "__trust", ...FIRST_VISIT_TIPS.trust });
      setTooltipTrustSeen(true);
    }, 700);
    return () => clearTimeout(t);
  }, [tab, tourActive, activeStore, activeTip, tooltipTrustSeen, allDeals]);

  // Event-triggered first-visit tooltip — Price memory.
  // Fires once when PriceIQDetailSheet opens for an item the user has ≥3
  // recorded purchases for (the threshold at which the sparkline draws).
  useEffect(() => {
    if (activeTip || tooltipPriceMemorySeen) return;
    if (!priceIQDetail) return;
    const entries = (priceMemory && priceMemory[priceIQDetail]) || [];
    if (entries.length < 3) return;
    const t = setTimeout(() => {
      setActiveTip({ tab: "__price_memory", ...FIRST_VISIT_TIPS.priceMemory });
      setTooltipPriceMemorySeen(true);
    }, 600);
    return () => clearTimeout(t);
  }, [priceIQDetail, activeTip, tooltipPriceMemorySeen, priceMemory]);

  const totalSavings = useMemo(() => allDeals.reduce((s,d)=>s+d.savings,0), [allDeals]);
  const totalIbottaAll = useMemo(() => allDeals.filter(d=>d.stackable).reduce((s,d)=>s+d.ibotta,0), [allDeals]);
  const urgentDeals = useMemo(() => allDeals.filter(d=>d.expiresIn<=2), [allDeals]);
  const bestStore = useMemo(() => [...activeStores].sort((a,b)=>netBenefit(b)-netBenefit(a))[0], [activeStores]);

  // These depend on cartMap
  const cartItems = useMemo(() => allDeals.filter(d=>cartMap[`${d.store.id}_${d.id}`]), [allDeals, cartMap]);
  const cartCost = useMemo(() => cartItems.reduce((s,d)=>s+(d.salePrice||d.origPrice),0), [cartItems]);
  const totalCartSavings = useMemo(() => cartItems.reduce((s,d)=>s+d.savings,0), [cartItems]);

  // Pantry-derived
  const pantryAlerts = useMemo(() => pantry.filter(p=>p.daysLeft<=3), [pantry]);

  // Monthly savings & milestones — depend on txnHistory
  // Monthly savings = realized from txnHistory THIS calendar month
  // (lifetime savings is the running total across all time)
  const monthlySavings = useMemo(() => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    return txnHistory.reduce((s, t) => {
      // Demo entries use "Today"/"Yesterday" — count those as current-month
      // Real entries use ISO timestamps like "2026-04-08T..."
      if (!t.timestamp) return s + t.netGain;  // legacy demo entries
      const txnMonth = t.timestamp.slice(0, 7);
      return txnMonth === monthKey ? s + t.netGain : s;
    }, 0);
  }, [txnHistory]);
  const lifetimeSavings = useMemo(() => txnHistory.reduce((s,t)=>s+t.netGain,0), [txnHistory]);
  // Rolling 12-month window. Legacy demo entries (no timestamp) count as recent.
  const annualSavings = useMemo(() => {
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    return txnHistory.reduce((s, t) => {
      if (!t.timestamp) return s + t.netGain;
      return new Date(t.timestamp) >= cutoff ? s + t.netGain : s;
    }, 0);
  }, [txnHistory]);
  // Sum of every ledger entry's reward — this is the displayed "balance."
  // When AFFILIATE_LIVE flips to true, this becomes the redeemable balance.
  // Three-state rewards accounting.
  //
  // Each ledger entry has a `state` field:
  //   "pending"    — user sent to Instacart; we're waiting on Impact confirmation
  //   "confirmed"  — Impact has reported a matching commission (≥ our 2% estimate)
  //   "expired"    — no match found within REWARDS_EXPIRY_DAYS; entry closed
  //
  // The `confirmedReward` field holds the actual amount Impact paid (may be
  // lower than the initially-tracked `reward` if the commission was smaller).
  // We never owe the user more than Impact actually paid us — that's the
  // "equal to or greater than" rule: user gets min(reward, actual).
  //
  // Auto-expiry runs on every render via a derived computation. If a pending
  // entry is older than REWARDS_EXPIRY_DAYS, we present it as expired in the
  // UI even if its stored state hasn't been updated. A separate effect
  // (below) writes the expired state back to storage on the next mount.
  const nowMs = Date.now();
  const expiryMs = REWARDS_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  // Derived view — applies auto-expiry without mutating storage.
  const derivedLedger = useMemo(() => rewardsLedger.map(entry => {
    const state = entry.state || "pending";  // backfill old entries
    if (state === "pending") {
      const ts = Date.parse(entry.timestamp);
      if (Number.isFinite(ts) && (nowMs - ts) > expiryMs) {
        return { ...entry, state: "expired", _autoExpired: true };
      }
    }
    return { ...entry, state };
  }), [rewardsLedger, nowMs, expiryMs]);

  // Persist auto-expired entries back to storage so they stop being treated
  // as pending in subsequent renders. Only writes if there are entries that
  // flipped, to avoid render loops.
  useEffect(() => {
    const flipped = derivedLedger.filter(e => e._autoExpired);
    if (flipped.length === 0) return;
    setRewardsLedger(prev => prev.map(entry => {
      const match = flipped.find(f => f.id === entry.id);
      return match ? { ...entry, state: "expired", expiredAt: new Date().toISOString() } : entry;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardsLedger.length]);  // run when ledger length changes (entry added or initial mount)

  // Three balances. Confirmed is what the user actually has earned.
  // Pending is what's tracked but not yet verified. Expired is shown only
  // in the history list — not summed into any visible balance.
  const rewardsConfirmedBalance = useMemo(
    () => derivedLedger.filter(e => e.state === "confirmed").reduce((s, e) => s + (e.confirmedReward != null ? e.confirmedReward : e.reward), 0),
    [derivedLedger]
  );
  const rewardsPendingBalance = useMemo(
    () => derivedLedger.filter(e => e.state === "pending").reduce((s, e) => s + (e.reward || 0), 0),
    [derivedLedger]
  );
  const rewardsOrderCount = derivedLedger.filter(e => e.state !== "expired").length;

  // Attempt to confirm a specific ledger entry. Called by:
  //   1. The admin tool (manual confirmation during demo mode)
  //   2. (Future) A backend polling function that reads Impact commission reports
  //
  // THE RULE (proportional confirmation, v2 — replaces binary threshold):
  //
  //   threshold       = cartTotal × expectedCommissionRate
  //   floorAmount     = threshold × REWARDS_FLOOR_RATIO
  //   estimatedSpend  = actual / expectedCommissionRate
  //   proportional    = estimatedSpend × REWARDS_RATE
  //   finalReward     = min(proportional, entry.reward)   ← cap at original 2%
  //
  //   if actual ≥ threshold:
  //       state="confirmed"   confirmedReward=entry.reward     reason="threshold_met"
  //   elif actual ≥ floorAmount:
  //       state="confirmed"   confirmedReward=finalReward      reason="partial"
  //   else:
  //       state="pending"     (record actual, may confirm later)
  //
  // WHY: Meeting the full threshold proves the user purchased at least what
  // was sent. Below threshold, we now reward proportionally — paying 2% of
  // estimated actual purchase rather than nothing — which is fairer to users
  // who made a real partial purchase. The min() cap ensures users can never
  // earn more than the originally tracked 2% of send, even if Impact somehow
  // reports a higher commission than expected. The floor (default 10% of
  // expected) filters out cookie failures and trivial conversions that
  // shouldn't trigger a payout.
  //
  // The `force` flag bypasses both the threshold AND floor checks — used by
  // the admin tool for testing the confirmed UI state without real Impact
  // data. Forced entries always confirm at the original entry.reward.
  // Production automated code should NEVER pass force: true.
  //
  // Returns { ok, reason, entry, threshold, floorAmount, actual }:
  //   reason="threshold_met"  → full confirmation, ok=true
  //   reason="partial"        → partial confirmation, ok=true, partial=true
  //   reason="below_floor"    → stays pending, ok=false
  //   reason="forced"         → forced full confirmation, ok=true
  //   reason="not_found"      → entry id didn't match, ok=false
  const confirmRewardEntry = useCallback((entryId, actualImpactCommission, { force = false } = {}) => {
    let result = { ok: false, reason: "not_found", entry: null };
    setRewardsLedger(prev => prev.map(entry => {
      if (entry.id !== entryId) return entry;
      const actual = Number(actualImpactCommission);
      const rate = entry.expectedCommissionRate || EXPECTED_COMMISSION_RATE;
      const threshold = entry.cartTotal * rate;
      const floorAmount = threshold * REWARDS_FLOOR_RATIO;
      const actualValid = Number.isFinite(actual);
      const recordedActual = actualValid ? parseFloat(actual.toFixed(2)) : entry.actualImpactCommission;

      // ── Force path (admin/testing only) ───────────────────────────────────
      if (force) {
        const updated = {
          ...entry,
          state: "confirmed",
          confirmedReward: entry.reward,
          confirmedAt: new Date().toISOString(),
          actualImpactCommission: recordedActual,
          forced: true,
          partial: false,
        };
        result = { ok: true, reason: "forced", entry: updated, threshold, floorAmount, actual };
        return updated;
      }

      // ── Full confirmation: Impact paid ≥ expected threshold ──────────────
      if (actualValid && actual >= threshold) {
        const updated = {
          ...entry,
          state: "confirmed",
          confirmedReward: entry.reward,  // full 2% of send
          confirmedAt: new Date().toISOString(),
          actualImpactCommission: recordedActual,
          forced: false,
          partial: false,
        };
        result = { ok: true, reason: "threshold_met", entry: updated, threshold, floorAmount, actual };
        return updated;
      }

      // ── Partial confirmation: Impact paid above floor but below threshold ─
      if (actualValid && actual >= floorAmount && rate > 0) {
        const estimatedSpend = actual / rate;
        const proportional = estimatedSpend * REWARDS_RATE;
        // Cap at originally tracked reward — user can never earn MORE than
        // 2% of what was sent, even if Impact reports unusually high commission.
        const finalReward = parseFloat(Math.min(proportional, entry.reward).toFixed(2));
        const updated = {
          ...entry,
          state: "confirmed",
          confirmedReward: finalReward,
          confirmedAt: new Date().toISOString(),
          actualImpactCommission: recordedActual,
          estimatedActualSpend: parseFloat(estimatedSpend.toFixed(2)),
          forced: false,
          partial: true,
        };
        result = { ok: true, reason: "partial", entry: updated, threshold, floorAmount, actual };
        return updated;
      }

      // ── Below floor (or invalid amount): stay pending, record attempt ─────
      const updated = {
        ...entry,
        actualImpactCommission: recordedActual,
      };
      result = { ok: false, reason: "below_floor", entry: updated, threshold, floorAmount, actual };
      return updated;
    }));
    return result;
  }, [setRewardsLedger]);

  // Admin helper: reset an entry back to pending (for re-testing).
  const resetRewardEntry = useCallback((entryId) => {
    setRewardsLedger(prev => prev.map(entry => entry.id === entryId
      ? { ...entry, state: "pending", confirmedReward: null, confirmedAt: null, expiredAt: null, forced: false }
      : entry));
  }, [setRewardsLedger]);

  // Admin helper: manually expire an entry.
  const expireRewardEntry = useCallback((entryId) => {
    setRewardsLedger(prev => prev.map(entry => entry.id === entryId
      ? { ...entry, state: "expired", expiredAt: new Date().toISOString() }
      : entry));
  }, [setRewardsLedger]);

  // Admin mode — enables manual ledger management for demo-period testing.
  // Gated by an obscure URL param (?_x=4k2j8) so regular testers don't
  // stumble into admin tools. The param name and token live in the
  // ADMIN_PARAM and ADMIN_TOKEN constants at the top of the file — rotate
  // if you ever suspect testers have discovered the current values.
  // Value is read once on mount; refreshing with the param enables it.
  const isAdminMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    try { return new URLSearchParams(window.location.search).get(ADMIN_PARAM) === ADMIN_TOKEN; }
    catch (e) { return false; }
  }, []);
  // Legacy alias — a few existing surfaces still expect a single "balance" number.
  // Use confirmed as the headline (the real money) and fall back to pending during
  // demo mode so the Home card isn't empty on first order.
  const rewardsBalance = AFFILIATE_LIVE ? rewardsConfirmedBalance : rewardsPendingBalance;
  const currentMilestone = useMemo(() => [...MILESTONES].reverse().find(m=>monthlySavings>=m.amt), [monthlySavings]);
  const nextMilestone = useMemo(() => MILESTONES.find(m=>monthlySavings<m.amt) || MILESTONES[MILESTONES.length-1], [monthlySavings]);
  const milestoneProgress = Math.min(monthlySavings/nextMilestone.amt, 1);

  // Split cart — recomputes when active stores, dismissed items, or chosen quantities change
  const splitData = useMemo(() => {
    const dismissed = new Set(dismissedSplit);
    // Source list for Smart Cart routing: MASTER_LIST (seed items) PLUS
    // canonical entries for any search-added items. Without this, search-added
    // items would exist in allDeals but never appear in Smart Cart's split
    // view because the outer iteration only walks MASTER_LIST.
    //
    // For each search-added item, synthesize a MASTER_LIST-shaped entry from
    // the canonical catalog. The synthetic entry uses the SAME id as the
    // synthetic deal (e.g., "organic-whole-milk__via_search") so the offer
    // lookup on line below matches correctly.
    const searchSourceItems = [];
    if (searchAddedItems && searchAddedItems.length > 0) {
      const seen = new Set();
      for (const sa of searchAddedItems) {
        if (seen.has(sa.id)) continue;
        seen.add(sa.id);
        const canonical = getCanonicalItem(sa._canonicalId) || {};
        searchSourceItems.push({
          id: sa.id,
          name: sa.name,
          category: sa.category,
          qty: 1,
          unit: canonical.size || "each",
          priority: "medium",
          nutrition: { protein: 0, fiber: 0, calories: 0 },  // not shown in Smart Cart
          weeklyUse: 1,
          _searchAdded: true,
        });
      }
    }
    const sourceList = [...MASTER_LIST, ...searchSourceItems];

    const cart = sourceList.filter(item => !dismissed.has(item.id)).map(item=>{
      // For search-added items, savings may be 0 (single-store search result).
      // Don't require savings > 0 there — the user chose this item explicitly.
      const offerFilter = item._searchAdded
        ? (d) => d.id === item.id
        : (d) => d.id === item.id && d.savings > 0;
      const offers=allDeals.filter(offerFilter).sort((a,b)=>(b.savings+b.ibotta)-(a.savings+a.ibotta));
      return offers.length?{...item,bestDeal:offers[0],altDeals:offers.slice(1)}:{...item,bestDeal:null,altDeals:[]};
    }).filter(i=>i.bestDeal);
    // Savings reflect user-chosen quantities; all bundle math delegates to the pricing module.
    const savings = cart.reduce((s, i) => {
      const q = splitQty[i.id] != null ? splitQty[i.id] : defaultBundleQty(i.bestDeal);
      const mult = triggeredSavingsMultiplier(i.bestDeal, q);
      return s + (i.bestDeal.savings * mult) + i.bestDeal.ibotta;
    }, 0);
    // Projected cart total (what user would actually pay) — reward preview uses this.
    // effectiveLinePrice already accounts for BOGO/BOGO50/B2G1 triggered discounts.
    const cartTotal = cart.reduce((s, i) => {
      const q = splitQty[i.id] != null ? splitQty[i.id] : defaultBundleQty(i.bestDeal);
      return s + effectiveLinePrice(i.bestDeal, q);
    }, 0);
    const stores = [...new Set(cart.map(i=>i.bestDeal.store.id))].map(sid=>activeStores.find(s=>s.id===sid)).filter(Boolean);
    const deliv = stores.reduce((s,st)=>s+st.deliveryFee,0);
    return { cart, savings, stores, deliv, net: savings - deliv, cartTotal };
  }, [allDeals, activeStores, dismissedSplit, splitQty, searchAddedItems]);
  const splitCart = splitData.cart;
  const splitSavings = splitData.savings;
  const splitStores = splitData.stores;
  const splitDeliv = splitData.deliv;
  const splitNet = splitData.net;
  // Projected reward if user sent this entire Smart Cart to Instacart.
  // Shown in the Smart Cart hero as a small secondary line.
  const splitEstReward = splitData.cartTotal * REWARDS_RATE;

  // Phase B — Average per-unit savings across routed items.
  // For each routed item, savings = (max unit price across stores) - (chosen unit price).
  // Aggregate is the simple mean — same weight for each item the user buys.
  const avgUnitSaved = useMemo(() => {
    if (!splitData || !splitData.cart || splitData.cart.length === 0) return null;
    const items = splitData.cart;
    let totalDelta = 0;
    let count = 0;
    for (const it of items) {
      if (!it.bestDeal) continue;
      const chosen = computeUnitPrice(it.bestDeal);
      if (!chosen) continue;
      // Find max unit price across all offers for this item
      const allOffers = allDeals.filter(d => d.id === it.id);
      let maxUnit = chosen.value;
      for (const offer of allOffers) {
        const up = computeUnitPrice(offer);
        if (up && up.value > maxUnit) maxUnit = up.value;
      }
      const delta = maxUnit - chosen.value;
      if (delta > 0.0001) {
        totalDelta += delta;
        count += 1;
      }
    }
    if (count === 0) return null;
    const avg = totalDelta / count;
    return avg < 0.10 ? avg.toFixed(4) : avg.toFixed(2);
  }, [splitData, allDeals]);

  function openCheckout(store) {
    const deals=allDeals.filter(d=>d.store.id===store.id&&cartMap[`${store.id}_${d.id}`]);
    if(!deals.length){ notify("Add items to cart first!","warn"); return; }
    setCheckout({store,cartDeals:deals,trackingId:`rw_${Date.now()}`});
  }

  // From the Smart Cart tab: add every remaining item routed to this store
  // into the user's cart, then immediately open the checkout modal for that store.
  function sendSplitStoreToCart(store) {
    const itemsForStore = splitCart.filter(it => it.bestDeal.store.id === store.id);
    if (itemsForStore.length === 0) {
      notify("Nothing to send for this store", "warn");
      return;
    }
    // Mark each item as in-cart
    setCartMap(prev => {
      const next = { ...prev };
      itemsForStore.forEach(it => { next[`${store.id}_${it.id}`] = true; });
      return next;
    });
    SFX.addCart();
    // Open the checkout modal with these items pre-selected.
    // Use computeLineTotal so BOGO/BOGO-50% items show the real cash cost.
    const cartDeals = itemsForStore.map(it => {
      const q = getSplitQty(it);
      const savingsMultiplier = triggeredSavingsMultiplier(it.bestDeal, q);
      return {
        ...it.bestDeal,
        store,
        qty: q,
        savings: it.bestDeal.savings * savingsMultiplier,
        salePrice: computeLineTotal(it.bestDeal, q),
        origPrice: it.bestDeal.origPrice * q,
      };
    });
    setCheckout({ store, cartDeals, trackingId:`rw_${Date.now()}` });
  }

  // ── Phase 2: search destination handlers ──────────────────────────────────
  // These add a SearchResult (canonical item + chosen store) to the user's
  // Smart Cart, Pantry, or Watchlist. Each handler uses the existing data
  // shapes so downstream code (routing, display, persistence) doesn't change.

  // Smart Cart: create a "synthetic deal" matching the shape of STORES[*].deals
  // and persist it in cartiq_search_added_items. The allDeals memo unions these
  // with store deals so Smart Cart routing and display work unchanged.
  //
  // Savings = max price across all stores that carry this item - chosen price.
  // This is Option B from the Phase 2 design: it communicates Smart Cart's true
  // routing value even for non-promotional items.
  const addSearchItemToSmartCart = useCallback((item, chosenStore) => {
    // Compute savings across ALL stores carrying this item (not just the
    // chosen one) — the honest "value of CartIQ routing" for this purchase.
    const maxPriceAcrossStores = Math.max(...item.stores.map(s => s.price));
    const savings = Math.max(0, maxPriceAcrossStores - chosenStore.price);
    // Unique synthetic-deal ID so multiple adds (e.g., eggs from Publix AND
    // from ALDI) don't collide. Phase 3 may change this to strictly route by
    // canonical ID; for now we track both the store and the canonical item.
    const syntheticId = `${item.canonicalId}__via_search`;
    const synthetic = {
      id: syntheticId,
      name: item.name,
      category: item.category,
      salePrice: chosenStore.price,
      origPrice: maxPriceAcrossStores > chosenStore.price ? maxPriceAcrossStores : chosenStore.price,
      savings,
      dealLabel: savings > 0.01 ? `BEST PRICE` : null,
      stockLevel: "high",
      expiresIn: 99,             // search-added items don't expire
      ibotta: 0,
      stackable: false,
      storeId: chosenStore.storeId,
      confidence: 100,
      _searchAdded: true,        // marker for any UI that wants to distinguish
      _canonicalId: item.canonicalId,
    };
    setSearchAddedItems(prev => {
      // Deduplicate — if this exact item already exists for the same store, replace it
      const filtered = prev.filter(p => !(p.id === syntheticId && p.storeId === chosenStore.storeId));
      return [...filtered, synthetic];
    });
    // Also mark it in cartMap so it shows up as checked on first add
    setCartMap(prev => ({ ...prev, [`${chosenStore.storeId}_${syntheticId}`]: true }));
    SFX.addCart();
    notify(`✓ Added ${item.name} to Smart Cart · ${chosenStore.storeName}`);
  }, [setSearchAddedItems, setCartMap]);

  // Pantry: add canonical item with category + size metadata. No store
  // association (pantry tracks what you have at home, not where to buy it).
  // If already present, no-op.
  const addSearchItemToPantry = useCallback((item) => {
    setPantry(prev => {
      const idx = prev.findIndex(p => p.id === item.canonicalId);
      if (idx >= 0) {
        const removedItem = prev[idx];
        SFX.removeCart();
        notify(`Removed ${removedItem.name} from pantry`, {
          type: "warn",
          action: {
            label: "Undo",
            onClick: () => setPantry(p => [...p, removedItem]),
          },
        });
        return prev.filter((_, i) => i !== idx);
      }
      SFX.tabSwitch();
      notify(`✓ Added ${item.name} to Pantry`);
      return [...prev, {
        id: item.canonicalId,
        name: item.name,
        category: item.category,
        size: item.size,
        low: false,
        _addedVia: "search",
      }];
    });
  }, [setPantry]);

  // Phase 3 — Pantry adder for DealCard taps. Takes a deal-shaped object
  // (must include .store). Adds the deal's canonical item to pantry. Pantry
  // is store-agnostic — we track the item, not where to buy it. Keyed by
  // deal.id so dedup works against both catalog items and search-added
  // synthetic deals.
  const addDealToPantry = useCallback((deal) => {
    if (!deal || !deal.id) return;
    setPantry(prev => {
      const idx = prev.findIndex(p => p.id === deal.id);
      if (idx >= 0) {
        // Remove — offer undo
        const removedItem = prev[idx];
        SFX.removeCart();
        notify(`Removed ${removedItem.name} from pantry`, {
          type: "warn",
          action: {
            label: "Undo",
            onClick: () => setPantry(p => [...p, removedItem]),
          },
        });
        return prev.filter((_, i) => i !== idx);
      }
      SFX.tabSwitch();
      notify(`✓ Added ${deal.name} to Pantry`);
      return [...prev, {
        id: deal.id,
        name: deal.name,
        category: deal.category,
        size: deal.size || "",
        low: false,
        _addedVia: "dealcard",
      }];
    });
  }, [setPantry]);

  // Watchlist: store + product combination. makeWatchlistItem already exists
  // and is the canonical way to create watchlist entries.
  const addSearchItemToWatchlist = useCallback((item, chosenStore) => {
    setWatchlist(prev => {
      // Dedupe on (canonicalId, storeId) — same item+store shouldn't appear twice
      const existing = prev.find(w =>
        (w.itemId === item.canonicalId || w.id === item.canonicalId) &&
        w.storeId === chosenStore.storeId
      );
      if (existing) return prev;
      return [...prev, makeWatchlistItem({
        name: item.name,
        category: item.category,
        storeId: chosenStore.storeId,
        storeName: chosenStore.storeName,
        currentPrice: chosenStore.price,
        itemId: item.canonicalId,
      })];
    });
    SFX.tabSwitch();
    notify(`✓ Watching ${item.name} at ${chosenStore.storeName}`);
  }, [setWatchlist]);

  // "Already-added" checks for the detail sheet's disabled states
  const isSearchItemInCart = useCallback((item, chosenStore) => {
    const syntheticId = `${item.canonicalId}__via_search`;
    return !!cartMap[`${chosenStore.storeId}_${syntheticId}`];
  }, [cartMap]);
  const isSearchItemInPantry = useCallback((item) => {
    return pantry.some(p => p.id === item.canonicalId);
  }, [pantry]);
  const isSearchItemInWatchlist = useCallback((item, chosenStore) => {
    return watchlist.some(w =>
      (w.itemId === item.canonicalId || w.id === item.canonicalId) &&
      w.storeId === chosenStore.storeId
    );
  }, [watchlist]);

  // Phase 2b — record a search query to recent-searches history. Dedupes,
  // keeps newest first, caps at RECENT_SEARCHES_MAX. Only called when the
  // user commits to a query (opens a detail sheet from a result) — not on
  // every keystroke, so we don't fill history with partials.
  const RECENT_SEARCHES_MAX = 10;
  const recordRecentSearch = useCallback((query) => {
    const q = (query || "").trim();
    if (q.length < 2) return;
    setRecentSearches(prev => {
      const lower = q.toLowerCase();
      const filtered = prev.filter(p => p.toLowerCase() !== lower);
      const next = [q, ...filtered];
      return next.slice(0, RECENT_SEARCHES_MAX);
    });
  }, [setRecentSearches]);

  function handleConfirmOrder() {
    const store=checkout.store;
    const gross=checkout.cartDeals.reduce((s,d)=>s+d.savings,0);
    const netGain=Math.max(0,gross-store.deliveryFee);
    const now = new Date();
    // Estimate cart total. Each deal has { salePrice, qty } so sum the line
    // totals. This is what the user actually paid (or would pay) at checkout
    // and is the correct base for the 2% rewards calculation — affiliate
    // commissions are on gross order value, not savings.
    const cartTotal = checkout.cartDeals.reduce((s,d) => {
      const qty = d.qty || 1;
      const price = d.salePrice || 0;
      return s + (price * qty);
    }, 0);
    const reward = parseFloat((cartTotal * REWARDS_RATE).toFixed(2));
    // Phase 4a: snapshot line items once so both the enriched txnHistory row
    // and the Trip Receipt see the same data, independent of later cart-clears.
    const lineItems = checkout.cartDeals.map(d => ({
      name: d.name,
      qty: d.qty || 1,
      salePrice: d.salePrice || 0,
      savings: d.savings || 0,
      ibotta: d.ibotta || 0,
      stackable: !!d.stackable,
    }));
    setTxnHistory(p=>[{
      id:`txn_${Date.now()}`,
      date: now.toLocaleDateString("en-US",{month:"short",day:"numeric"}),
      timestamp: now.toISOString(),
      store: store.name,
      grossSavings: gross,
      deliveryFee: store.deliveryFee,
      netGain,
      estCommission: parseFloat((netGain*COMMISSION_REPEAT).toFixed(2)),
      userCard: "via Instacart",
      status: "redirected",
      itemCount: checkout.cartDeals.length,
      // Phase 4a enrichment: enough fields to reconstruct a Trip Receipt.
      lineItems,
      cartTotal,
      reward,
    }, ...p]);
    // Append to rewards ledger (most-recent-first like txnHistory).
    // State starts as "pending" — Impact.com confirmation (in live mode) or
    // manual admin action (in demo mode) moves it to "confirmed."
    // `confirmedReward` is populated when the state flips — it holds the
    // actual amount paid (may differ from the estimated `reward` above).
    //
    // The entry's `id` matches the `subId1` embedded in the Instacart
    // handoff URL. When Impact reports a commission, our backend looks it
    // up by this ID and transitions the entry.
    const ledgerId = checkout.trackingId || `rw_${Date.now()}`;
    setRewardsLedger(p=>[{
      id: ledgerId,
      timestamp: now.toISOString(),
      date: now.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      storeId: store.id,
      storeName: store.name,
      cartTotal: parseFloat(cartTotal.toFixed(2)),
      reward,                      // estimated at 2% of cartTotal
      // Snapshot of the expected commission rate at time of send. Stored on
      // the entry (not read from the live constant at confirmation time) so
      // historical entries aren't affected if the rate changes later.
      expectedCommissionRate: EXPECTED_COMMISSION_RATE,
      state: "pending",            // "pending" | "confirmed" | "expired"
      confirmedReward: null,       // populated on state=confirmed
      confirmedAt: null,           // ISO timestamp when state flipped
      expiredAt: null,             // ISO timestamp when state=expired
      actualImpactCommission: null, // populated when we see a matching Impact report
    }, ...p]);
    // Price memory — log each line's actual purchase price for the
    // "last time you bought this it was $X" comparison. Uses salePrice when
    // available, falls back to origPrice. Item ids are deal ids (e.g. "chicken-breast").
    checkout.cartDeals.forEach(d => {
      const paid = (typeof d.salePrice === "number" && d.salePrice > 0) ? d.salePrice : (d.origPrice || 0);
      logPurchasedPrice(d.id, paid, store.id);
    });
    setCartMap(p=>{ const n={...p}; Object.keys(n).forEach(k=>{ if(k.startsWith(store.id+"_")) n[k]=false; }); return n; });
    SFX.cash();
    notify(`🎉 Sent to Instacart — $${netGain.toFixed(2)} net savings · $${reward.toFixed(2)} ${REWARDS_COPY.checkoutCelebration}`);
    // Phase 4a: hand the user a screenshot-able artifact of the trip they
    // just sent. Shape mirrors txnHistory rows so the re-open code path can
    // pass either a fresh snapshot or a reconstructed row to TripReceiptModal.
    setTripReceipt({
      id: `trip_${Date.now()}`,
      date: now.toLocaleDateString("en-US",{month:"short",day:"numeric"}),
      timestamp: now.toISOString(),
      store: store.name,
      storeLogo: store.logo,
      grossSavings: gross,
      deliveryFee: store.deliveryFee,
      netGain,
      cartTotal,
      reward,
      lineItems,
      trackingId: checkout.trackingId,
    });
  }

  // Bottom nav tabs — #2.
  // Uses canonical SVG Icon names (not emoji) for crisp rendering across platforms.
  // "more" uses a custom inline SVG (three dots) since it's not in the Icon registry.
  const PRIMARY_TABS = [
    {id:"dashboard", iconName:"home",     label:"Home"},
    {id:"split",     iconName:"cart",     label:"Smart Cart"},
    {id:"pantry",    iconName:"box",      label:"My Items"},
    {id:"watchlist", iconName:"bookmark", label:"Watchlist"},
    {id:"more",      iconName:"more",     label:"More"},
  ];
  // More menu organized into logical groups for easier scanning
  // Function to replay the onboarding tour (resets the "seen" flag)
  const replayTour = useCallback(() => {
    try { localStorage.removeItem("cartiq_toured"); } catch(e) {}
    setTourStep(0);
    setTourActive(true);
    setShowMore(false);
    SFX.tabSwitch();
  }, []);

  const MORE_GROUPS = [
    {
      label: "Features",
      tabs: [
        {id:"bogos",icon:"🏷️",label:"Deals"},
        {id:"history",icon:"📈",label:"Price IQ"},
        {id:"subs",icon:"🔄",label:"Substitutions"},
        {id:"coupons",icon:"🎟",label:"Stack & Save"},
      ],
    },
    {
      label: "Tools",
      tabs: [
        // Beta-flagged: receipt OCR isn't end-to-end tested with real receipts yet.
        // The flag adds a small "BETA" pill below the label in the More overlay.
        // Remove the `beta:true` once the OCR pipeline is verified against real Publix/ALDI/Sprouts receipts.
        {id:"receipt",icon:"🧾",label:"Receipts",beta:true},
        {id:"loyalty",icon:"⭐",label:"Loyalty"},
        // Rewards balance — preview until AFFILIATE_LIVE flips to true.
        // Lives in the Tools group (alongside Receipts and Loyalty) rather
        // than Social/Help because it's a user-specific tracked metric.
        {id:"rewards",icon:"🎁",label:"Rewards",beta:!AFFILIATE_LIVE},
        // Earnings tab hidden until real affiliate data exists.
        // EarningsDashboard component is preserved (line ~3869) for re-enable.
        // To restore: uncomment the line below.
        // {id:"earnings",icon:"💰",label:"Earnings"},
      ],
    },
    {
      label: "Social",
      tabs: [
        {id:"household",icon:"👨‍👩‍👧",label:"Household"},
        {id:"share",icon:"🔗",label:"Share"},
      ],
    },
    {
      label: "Help",
      tabs: [
        {id:"__pro_tips",icon:"💡",label:"Pro tips",action:()=>{setTipsModalOpen(true);setShowMore(false);SFX.tabSwitch();}},
        {id:"__shop_planner",icon:"📅",label:"Plan shop",action:()=>{setShopPlannerOpen(true);setShowMore(false);SFX.tabSwitch();}},
        {id:"__email_alerts",icon:"📧",label:"Email alerts",action:()=>{setEmailSetupOpen(true);setShowMore(false);SFX.tabSwitch();}},
        {id:"__store_alerts",icon:"🔔",label:"Store alerts",action:()=>{setStoreAlertsOpen(true);setShowMore(false);SFX.tabSwitch();}},
        {id:"__dietary",icon:"🥗",label:"Diet prefs",action:()=>{setDietarySetupOpen(true);setShowMore(false);SFX.tabSwitch();}},
        {id:"__health_goals",icon:"💚",label:"Health goals",action:()=>{setHealthGoalsOpen(true);setShowMore(false);SFX.tabSwitch();}},
        // Icon reflects current sound state — matches the most-toggled half
        // of the feedback modal at a glance. Label reflects the broader scope.
        // No SFX.tabSwitch() here: opening the feedback modal shouldn't make a sound.
        {id:"__sound",icon: soundOn ? "🔊" : "🔇",label:"Sound & haptics",action:()=>{setSoundSetupOpen(true);setShowMore(false);}},
        {id:"__replay_tour",icon:"🎬",label:"Take the tour",action:replayTour},
        // What's real vs demo — quick reference for testers explaining which
        // features use live data and which are currently demo. Echoes the
        // User Quick Start content but lives in-app so testers don't need
        // to dig through an email attachment mid-session.
        {id:"__whats_real",icon:"🧪",label:"What's real vs demo",action:()=>{setWhatsRealOpen(true);setShowMore(false);SFX.tabSwitch();}},
        // In-app Feedback button — opens the user's email client with a
        // pre-filled message to cartiq.feedback@gmail.com. Testers who might
        // not remember the email address unprompted now have a one-tap path.
        // The pre-filled body includes the current app version so feedback
        // can be correlated to specific deploys.
        {id:"__feedback",icon:"📣",label:"Send feedback",action:()=>{
          const subject = encodeURIComponent(`CartIQ feedback (v${APP_VERSION})`);
          const body = encodeURIComponent(
            `Hi — sharing some feedback on CartIQ.\n\n` +
            `What I was doing: \n\n` +
            `What worked or didn't work: \n\n` +
            `Suggestions: \n\n` +
            `---\n` +
            `CartIQ v${APP_VERSION} · ${BUILD_DATE}\n` +
            `Device: ${(typeof navigator !== "undefined" ? navigator.userAgent : "unknown").slice(0,120)}`
          );
          window.location.href = `mailto:${FEEDBACK_EMAIL}?subject=${subject}&body=${body}`;
          setShowMore(false);
        }},
      ],
    },
  ];
  const MORE_TABS = MORE_GROUPS.flatMap(g => g.tabs);  // flat list for lookups
  const [showMore, setShowMore] = useState(false);
  const handleTabChange = (id) => {
    if (id === "more") { setShowMore(!showMore); return; }
    // Check for action-based entries (like Take the tour)
    const actionEntry = MORE_TABS.find(t => t.id === id && typeof t.action === "function");
    if (actionEntry) { actionEntry.action(); return; }
    SFX.tabSwitch(); setTab(id); setShowMore(false); setActiveStore(null); setCatFilter(null);
  };

  const currentTab = activeStore ? "store" : tab;

  return (
    <div style={{minHeight:"100vh",background:T.cream,fontFamily:FONT_SANS,color:T.forest,position:"relative",paddingBottom:72}}>
      <style>{`
        @import url('${FONT_URL}');
        /* ═══ Cross-browser baseline ═══ */
        :root { color-scheme: light; }
        html, body {
          min-height: 100vh;
          min-height: 100dvh;
          background: ${T.cream};
          overscroll-behavior-y: none;
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          -webkit-tap-highlight-color: transparent;
        }
        button, a, [role="button"] {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          -webkit-appearance: none;
          appearance: none;
        }
        button:focus-visible, a:focus-visible, [role="button"]:focus-visible, input:focus-visible, [tabindex]:focus-visible {
          outline: 2px solid ${T.basil};
          outline-offset: 2px;
        }
        input, textarea {
          -webkit-user-select: text;
          user-select: text;
          -webkit-appearance: none;
          appearance: none;
        }
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
        input[type="date"] { min-height: 32px; }
        /* Budget slider — track gets a subtle parchment well, thumb is a
           clearly visible basil disc with a paper ring so it reads against
           the cream/paper surface of Glass cards. */
        input[type="range"].budget-slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          height: 24px;
          width: 100%;
          outline: none;
        }
        input[type="range"].budget-slider::-webkit-slider-runnable-track {
          height: 6px;
          background: ${T.parchment};
          border: 1px solid ${T.hairline};
          border-radius: 4px;
        }
        input[type="range"].budget-slider::-moz-range-track {
          height: 6px;
          background: ${T.parchment};
          border: 1px solid ${T.hairline};
          border-radius: 4px;
        }
        input[type="range"].budget-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: ${T.basil};
          border: 3px solid ${T.paper};
          box-shadow: 0 1px 4px ${T.ink15}, 0 0 0 1px ${T.basil}66;
          cursor: pointer;
          margin-top: -8px;
        }
        input[type="range"].budget-slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: ${T.basil};
          border: 3px solid ${T.paper};
          box-shadow: 0 1px 4px ${T.ink15}, 0 0 0 1px ${T.basil}66;
          cursor: pointer;
        }
        input[type="range"].budget-slider:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 3px ${T.basil}55; }
        input[type="range"].budget-slider:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 3px ${T.basil}55; }
        ::-webkit-scrollbar { width: 3px; height: 3px; }
        ::-webkit-scrollbar-thumb { background: ${T.ink15}; border-radius: 2px; }
        * { scrollbar-width: thin; scrollbar-color: ${T.ink15} transparent; }
        @keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pageIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes ripple { 0%{transform:scale(0);opacity:.7} 100%{transform:scale(6);opacity:0} }
        @keyframes bounce { 0%,100%{transform:translateY(0);opacity:.5} 50%{transform:translateY(-6px);opacity:1} }
        @keyframes goldShine { 0%{background-position:200% center} 100%{background-position:-200% center} }
        @keyframes scanLine { 0%,100%{top:30%} 50%{top:70%} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cartiqLeafPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.07)} }
        .deal-card { transition: transform .2s, box-shadow .2s, border-color .2s; }
        .deal-card:hover { transform:translateY(-2px); box-shadow:${T.shadowFloat}; }
        .anim-slide-up { animation: slideUp .35s ease both; animation-delay: calc(var(--i, 0) * 40ms); }
        .anim-page-in { animation: pageIn .4s ease both; }
        .anim-fade-in { animation: fadeIn .15s ease both; }
        @keyframes confettiBurst { 0%{transform:scale(1)} 50%{transform:scale(1.2)} 100%{transform:scale(1)} }
        .confetti-burst { animation: confettiBurst .6s ease; }
        button { font-family: inherit; cursor: pointer; }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
          .deal-card:hover { transform: none !important; }
        }
      `}</style>

      {/* ═══ TOP HEADER BAR ═══ */}
      <div style={{background:T.paper,borderBottom:`1px solid ${T.hairline}`,padding:"10px 20px",paddingTop:"max(10px, env(safe-area-inset-top))",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:200,boxShadow:T.shadowSoft}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <CartIQMark size={28} mood="happy"/>
          <span style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,letterSpacing:-.5,color:T.forest}}>Cart<span style={{color:T.basil}}>IQ</span></span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {cartItems.length>0 && (
            <button onClick={()=>{setShoppingModeOpen(true);SFX.tabSwitch();}}
              aria-label={`Start Shopping Mode with ${cartItems.length} items`}
              title="Start Shopping Mode"
              style={{background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:20,padding:"4px 12px",display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontFamily:"inherit"}}>
              <span style={{fontSize:12}} aria-hidden="true">🛒</span>
              <span style={{fontSize:11,fontWeight:700,color:T.basil}}>{cartItems.length}</span>
              <span style={{fontSize:10,color:T.leaf}}>${totalCartSavings.toFixed(2)}</span>
            </button>
          )}
          <button onClick={()=>{setSearchOverlay({});SFX.tabSwitch();}}
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,width:32,height:32,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}
            aria-label="Search all stores"
            title="Search">
            <span aria-hidden="true">🔍</span>
          </button>
          <button onClick={()=>{setScannerOpen(true);SFX.tabSwitch();}}
            style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,width:32,height:32,cursor:"pointer",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}
            aria-label="Open barcode scanner"
            title="Scan barcode">
            <span aria-hidden="true">📷</span>
          </button>
          <button onClick={()=>{setNotifsOpen(o=>!o);SFX.tabSwitch();}}
            aria-label={unreadAlerts.length > 0 ? `Notifications: ${unreadAlerts.length} unread` : "Notifications"}
            aria-expanded={notifsOpen}
            style={{background:unreadAlerts.length>0?T.peachSoft:T.parchment,border:`1px solid ${unreadAlerts.length>0?T.apricot:T.hairline}`,borderRadius:10,width:32,height:32,cursor:"pointer",fontSize:14,position:"relative",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>
            <span aria-hidden="true">🔔</span>
            {unreadAlerts.length>0 && (
              <span aria-hidden="true" style={{position:"absolute",top:-3,right:-3,background:T.apricot,color:T.paper,borderRadius:"50%",minWidth:16,height:16,fontSize:10,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",border:`1.5px solid ${T.cream}`,padding:"0 4px"}}>
                {unreadAlerts.length>9?"9+":unreadAlerts.length}
              </span>
            )}
          </button>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:T.basil,animation:"pulse 2s infinite"}}/>
            <span style={{fontSize:10,color:T.basil,fontWeight:700,letterSpacing:2}}>LIVE</span>
          </div>
        </div>
      </div>

      {/* ═══ NOTIFICATIONS PANEL ═══ */}
      {notifsOpen && (
        <div style={{position:"fixed",inset:0,zIndex:260,background:"rgba(0,0,0,.5)",animation:"fadeIn .15s ease"}} onClick={()=>setNotifsOpen(false)}>
          <div style={{position:"fixed",top:"max(56px, calc(env(safe-area-inset-top) + 46px))",right:16,width:"min(340px, calc(100vw - 32px))",maxHeight:"70vh",animation:"slideUp .2s ease both"}} onClick={e=>e.stopPropagation()}>
            <Glass style={{borderRadius:18,overflow:"hidden",border:`1px solid ${T.hairline}`,boxShadow:"0 20px 60px rgba(0,0,0,.7)"}}>
              <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:15}}>🔔</span>
                  <span style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest}}>Notifications</span>
                </div>
                {unreadAlerts.length>0 && (
                  <button onClick={dismissAllAlerts}
                    style={{background:"transparent",border:"none",color:T.ink50,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
                    Clear all
                  </button>
                )}
              </div>
              <div style={{maxHeight:"50vh",overflowY:"auto",overscrollBehavior:"contain"}}>
                {unreadAlerts.length === 0 ? (
                  <div style={{padding:"36px 20px",textAlign:"center"}}>
                    <div style={{fontSize:36,marginBottom:10,opacity:.6}}>🌱</div>
                    <div style={{fontSize:12,color:T.ink50,lineHeight:1.6}}>No new alerts.<br/>Add items to your Watchlist to get notified when prices drop.</div>
                    <button onClick={()=>{setNotifsOpen(false);SFX.tabSwitch();setTab("watchlist");}}
                      style={{marginTop:14,background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:9,padding:"6px 14px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                      Open Watchlist →
                    </button>
                  </div>
                ) : (
                  unreadAlerts.map((a, i) => {
                    const icon = a.type === "allTimeLow" ? "🏆" : a.type === "targetHit" ? "🎯" : "💸";
                    const label = a.type === "allTimeLow" ? "All-time low!" : a.type === "targetHit" ? "Hit your target price" : "On sale";
                    const color = a.type === "allTimeLow" ? T.warn : a.type === "targetHit" ? T.leaf : T.sage;
                    return (
                      <div key={i} style={{padding:"12px 16px",borderBottom:i<unreadAlerts.length-1?`1px solid ${T.hairline}`:"none",display:"flex",alignItems:"center",gap:11}}>
                        <div style={{width:34,height:34,borderRadius:10,background:`rgba(251,191,36,.08)`,border:`1px solid ${color}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{icon}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.itemName}</div>
                          <div style={{fontSize:10,color:color,fontWeight:600,marginTop:1}}>{label} · ${a.price.toFixed(2)}</div>
                          <div style={{fontSize:10,color:T.ink50,marginTop:1}}>{a.date}</div>
                        </div>
                        <button onClick={()=>dismissAlert(i)}
                          style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:7,width:22,height:22,cursor:"pointer",fontSize:11,flexShrink:0}}>✕</button>
                      </div>
                    );
                  })
                )}
              </div>
              {unreadAlerts.length>0 && (
                <div style={{padding:"10px 16px",borderTop:`1px solid ${T.hairline}`,background:T.parchment}}>
                  <button onClick={()=>{setNotifsOpen(false);SFX.tabSwitch();setTab("watchlist");}}
                    style={{width:"100%",background:`${T.forest}`,border:"none",color:T.paper,borderRadius:9,padding:"9px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 0 16px rgba(30,53,40,.15)"}}>
                    View Watchlist →
                  </button>
                </div>
              )}
            </Glass>
          </div>
        </div>
      )}

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{padding:"16px 18px"}}>

        {/* STORE DETAIL PAGE — #1 */}
        {activeStore && (
          <StoreDetailPage
            store={activeStore} allDeals={allDeals} cartMap={cartMap} toggleCart={toggleCart}
            inCart={inCart} cartCount={cartCount} openCheckout={openCheckout}
            onBack={()=>{setActiveStore(null);setCatFilter(null);}}
            catFilter={catFilter} setCatFilter={setCatFilter}
            isWatched={isWatched} onToggleWatch={toggleWatchlist}
            onAsk={({deal,store})=>setAskSageDeal({deal,store})}
            onOpenSearch={()=>setSearchOverlay({initialScopeStoreId: activeStore.id})}
            onAddToPantry={addDealToPantry}
            isInPantry={(dealId) => pantry.some(p => p.id === dealId)}
          />
        )}

        {/* ══ DASHBOARD — the home ══ */}
        {!activeStore && tab==="dashboard" && (
          <div style={{animation:"pageIn .4s ease both"}}>

            {/* ── Personal greeting ── */}
            {(() => {
              const hour = new Date().getHours();
              const timeOfDay = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
              const firstName = userName ? userName.split(/\s+/)[0] : "";
              // Pick a dynamic status line based on actual state — picks first match in priority order
              const watchlistOnSale = watchlist.filter(w => allDeals.find(d => d.id === w.itemId && d.store.id === w.storeId));
              const pantryLowOnSale = pantryAlerts.filter(p => allDeals.find(d => d.id === p.id));
              const newBogos = allDeals.filter(d => isBundleDeal(d) && d.expiresIn >= 5);
              let statusLine = null;
              let statusTone = "neutral"; // drives the indicator dot color
              const isFirstVisit = txnHistory.length === 0 && watchlist.length === 0 && monthlySavings === 0;
              if (isFirstVisit) {
                statusLine = userLocation?.zip
                  ? `${allDeals.length} live deals at your stores — tap Today's Top Pick to start saving`
                  : `Welcome! Set your ZIP below to see deals near you`;
                statusTone = userLocation?.zip ? "positive" : "action";
              } else if (watchlistOnSale.length > 0) {
                statusLine = `${watchlistOnSale.length} watchlist ${watchlistOnSale.length===1?"item is":"items are"} on sale right now`;
                statusTone = "positive";
              } else if (pantryLowOnSale.length > 0) {
                statusLine = `${pantryLowOnSale.length} pantry ${pantryLowOnSale.length===1?"item is":"items are"} running low AND on sale`;
                statusTone = "action";
              } else if (monthlySavings > 0 && nextMilestone && nextMilestone.amt - monthlySavings < 20) {
                statusLine = `$${(nextMilestone.amt - monthlySavings).toFixed(0)} from your ${nextMilestone.label} milestone`;
                statusTone = "positive";
              } else if (newBogos.length >= 3) {
                statusLine = `${newBogos.length} fresh BOGO deals this week`;
                statusTone = "positive";
              } else if (monthlySavings > 0) {
                statusLine = `You've saved $${monthlySavings.toFixed(0)} this month · keep it up`;
                statusTone = "positive";
              } else {
                statusLine = `${allDeals.length} live deals waiting for you`;
                statusTone = "neutral";
              }
              const dotColor = statusTone === "positive" ? T.leaf : statusTone === "action" ? T.warn : T.ink50;
              return (
                <div style={{marginBottom:16,paddingTop:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.forest,lineHeight:1.2}}>
                      Good {timeOfDay}{firstName ? `, ${firstName}` : ""}
                    </div>
                    {dietaryRestrictions.length > 0 && (
                      <button onClick={()=>setDietarySetupOpen(true)}
                        aria-label={`${dietaryRestrictions.length} dietary filters active`}
                        title={`${dietaryRestrictions.length} diet filter${dietaryRestrictions.length>1?"s":""} active · tap to edit`}
                        style={{background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:20,padding:"3px 9px 3px 7px",fontSize:10,fontWeight:700,color:T.basil,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5}}>
                        <Icon name="leaf" size={19} color={T.basil} stroke={2.2}/>
                        <span>{dietaryRestrictions.length}</span>
                      </button>
                    )}
                  </div>
                  <div style={{fontSize:12,color:T.ink50,marginTop:4,lineHeight:1.4,display:"flex",alignItems:"center",gap:7}}>
                    <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:dotColor,boxShadow:statusTone!=="neutral"?`0 0 0 3px ${dotColor}1F`:"none",flexShrink:0}}/>
                    <span>{statusLine}</span>
                  </div>
                </div>
              );
            })()}

            {/* ── Savings hero ──
                Soft sage→cream gradient card. Forest-green text on top, money-bag
                emoji eyebrow, big display-font number. Milestone bar uses a flat
                forest fill on a sage track — no glow, no halo. */}
            <div style={{
              position:"relative",overflow:"hidden",
              padding:"20px 22px",marginBottom:14,borderRadius:20,
              background:`linear-gradient(110deg, ${T.sageSoft} 0%, #ECEAC9 55%, #F4EDC9 100%)`,
              border:`1px solid ${T.sage}`,
              boxShadow:T.shadowSoft,
            }}>
              {lifetimeSavings >= SHARE_MIN_SAVINGS && (
                <button onClick={()=>{ setShareSavingsOpen(true); SFX.tabSwitch(); }}
                  aria-label="Share my savings"
                  style={{position:"absolute",top:14,right:14,zIndex:2,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:10,padding:"6px 10px",display:"inline-flex",alignItems:"center",gap:5,cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700,color:T.basil}}>
                  <Icon name="share" size={14} color={T.basil} stroke={2.2}/>
                  Share
                </button>
              )}
              <div style={{position:"relative",zIndex:1}}>
                <div style={{display:"inline-flex",gap:3,padding:3,background:`${T.paper}99`,border:`1px solid ${T.sage}`,borderRadius:10,marginBottom:10}}>
                  {[{id:"month",label:"Month"},{id:"year",label:"Year"},{id:"all",label:"All Time"}].map(p => {
                    const on = savingsPeriod === p.id;
                    return (
                      <button key={p.id} onClick={()=>setSavingsPeriod(p.id)} aria-pressed={on}
                        style={{background:on?T.forest:"transparent",color:on?T.paper:T.forest,border:"none",borderRadius:7,padding:"4px 10px",fontSize:10.5,fontWeight:800,letterSpacing:.3,cursor:"pointer",fontFamily:"inherit"}}>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <ItemIcon emoji="💰" size={20}/>
                  <div style={{fontSize:11,letterSpacing:2,color:T.forest,fontWeight:800}}>
                    {savingsPeriod === "month" ? "SAVED THIS MONTH" : savingsPeriod === "year" ? "SAVED THIS YEAR" : "SAVED ALL TIME"}
                  </div>
                </div>
                <AnimNum v={savingsPeriod === "month" ? monthlySavings : savingsPeriod === "year" ? annualSavings : lifetimeSavings} size={48} color={T.forest}/>
                {txnHistory.length > 0 && (
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:6,fontSize:12,color:T.forest,fontWeight:600}}>
                    <Icon name="check" size={16} color={T.basil} stroke={2.6}/>
                    <span>{txnHistory.length} Instacart {txnHistory.length===1?"order":"orders"}{savingsPeriod === "month" ? " this month" : savingsPeriod === "year" ? " this year" : " all time"}</span>
                  </div>
                )}

                {/* Milestone progress — month view only (milestones are monthly) */}
                {savingsPeriod === "month" && (
                <div style={{marginTop:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,marginBottom:8}}>
                    <div style={{display:"inline-flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:13,color:T.forest,fontWeight:600}}>Next:</span>
                      <ItemIcon emoji={nextMilestone.icon || "🌱"} size={18}/>
                      <span style={{fontSize:14,color:T.forest,fontWeight:800,letterSpacing:.1}}>{nextMilestone.label}</span>
                    </div>
                    <span style={{fontSize:13,color:T.forest,fontWeight:700,fontFamily:FONT_DISPLAY,letterSpacing:.3}}>
                      ${monthlySavings.toFixed(0)} / ${nextMilestone.amt}
                    </span>
                  </div>
                  <div style={{position:"relative",height:8,background:T.sage,borderRadius:4,overflow:"hidden"}}>
                    <div style={{
                      position:"absolute",inset:0,width:`${milestoneProgress*100}%`,
                      background:T.basil,
                      borderRadius:4,transition:"width 1.4s ease",
                    }}/>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* ── Recent trips — Phase 4a/3 ──
                Compact tappable list of the most recent txnHistory rows.
                Each row re-opens TripReceiptModal with the row data.
                Legacy MOCK rows lack lineItems/cartTotal/reward — the
                modal degrades gracefully so old rows still open. */}
            {TRIP_RECEIPT_ENABLED && txnHistory.length > 0 && (
              <Glass style={{padding:"14px 16px",borderRadius:14,border:`1px solid ${T.hairline}`,marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:10,fontWeight:800,color:T.basil,letterSpacing:.8,textTransform:"uppercase"}}>Recent trips</div>
                  <div style={{fontSize:10,color:T.ink50,fontWeight:600}}>tap to reopen</div>
                </div>
                {txnHistory.slice(0, 5).map(row => {
                  const logo = STORES.find(s => s.name === row.store)?.logo || "🛒";
                  return (
                    <button
                      key={row.id}
                      onClick={()=>{ setTripReceipt({ ...row, storeLogo: logo }); SFX.tabSwitch(); }}
                      style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 8px",background:"none",border:"none",borderTop:`1px solid ${T.hairline}`,cursor:"pointer",fontFamily:FONT_SANS,textAlign:"left"}}>
                      <span style={{fontSize:18,lineHeight:1}}>{logo}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.ink}}>{row.store}</div>
                        <div style={{fontSize:11,color:T.ink50,marginTop:1}}>{row.date}</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:800,color:T.forest,fontVariantNumeric:"tabular-nums"}}>+${(row.netGain || 0).toFixed(2)}</div>
                        <div style={{fontSize:10,color:T.ink50,marginTop:1}}>kept</div>
                      </div>
                      <Icon name="chevron-right" size={16} color={T.ink50} stroke={2}/>
                    </button>
                  );
                })}
              </Glass>
            )}

            {showRebuildCard && (
              <RebuildCartCard
                hasStaples={staples && staples.length > 0}
                preview={rebuildPreview}
                onRebuild={handleRebuildCart}
                onSetupStaples={() => setStaplesPickerOpen(true)}
              />
            )}

            {/* ── Rewards balance — compact card, only shown when user has at least one tracked order ──
                Pulls the visual language from the Rewards screen: leaf-green confirmed
                number, gift icon chip, dotted ring micro-indicator when there's pending. */}
            {rewardsOrderCount > 0 && (
              <button onClick={()=>{ setTab("rewards"); SFX.tabSwitch(); }}
                style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",marginBottom:14}}>
                <Glass style={{padding:"14px 16px",borderRadius:14,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:38,height:38,borderRadius:10,background:`linear-gradient(135deg, ${T.leaf}26, ${T.basil}1A)`,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Icon name="gift" size={22} color={T.basil} stroke={2}/>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:9.5,letterSpacing:1.5,color:T.basil,fontWeight:800}}>
                        {AFFILIATE_LIVE && rewardsConfirmedBalance > 0 ? "REWARDS CONFIRMED" : "REWARDS TRACKED"}
                      </span>
                      {!AFFILIATE_LIVE && (
                        <span style={{fontSize:8,fontWeight:800,letterSpacing:.7,color:T.warn,background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:4,padding:"1px 5px",lineHeight:1.2}}>
                          DEMO
                        </span>
                      )}
                    </div>
                    <div style={{display:"flex",alignItems:"baseline",gap:8,marginTop:2}}>
                      <span style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.leaf,lineHeight:1,letterSpacing:-.3}}>${rewardsBalance.toFixed(2)}</span>
                      <span style={{fontSize:10.5,color:T.ink50,fontWeight:600}}>· {rewardsOrderCount} {rewardsOrderCount === 1 ? "order" : "orders"}</span>
                    </div>
                    {AFFILIATE_LIVE && rewardsPendingBalance > 0 && (
                      <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10.5,color:T.warn,marginTop:3,fontWeight:700}}>
                        <Icon name="clock" size={18} color={T.warn} stroke={2.4}/>
                        <span>+ ${rewardsPendingBalance.toFixed(2)} pending</span>
                      </div>
                    )}
                  </div>
                  <Icon name="chevron-right" size={20} color={T.ink50} stroke={2}/>
                </Glass>
              </button>
            )}

            {/* ── Today's top pick — one personalized deal ── */}
            {(() => {
              // Priority: watchlist-on-sale → pantry-low-and-on-sale → all-time-low → highest-savings fallback
              const watchIds = new Set(watchlist.map(w => `${w.storeId}_${w.itemId}`));
              const pantryIds = new Set(pantryAlerts.map(p => p.id));
              const isAllTimeLow = d => {
                const h = PRICE_HISTORY[d.id];
                if (!h || h.length < 3) return false;
                const cur = effectivePerUnit(d);
                const low = Math.min(...h.slice(0, -1));
                return cur <= low * 1.05;
              };
              let pick =
                allDeals.find(d => watchIds.has(`${d.store.id}_${d.id}`)) ||
                allDeals.find(d => pantryIds.has(d.id)) ||
                allDeals.find(isAllTimeLow) ||
                [...allDeals].sort((a,b) => b.savings - a.savings)[0];
              if (!pick) return null;
              const reason =
                watchIds.has(`${pick.store.id}_${pick.id}`) ? "From your watchlist" :
                pantryIds.has(pick.id) ? "You're running low" :
                isAllTimeLow(pick) ? "All-time low price" :
                "Biggest savings today";
              const inC = inCart(pick.store.id, pick.id);
              const displayP = displayPrice(pick);
              const mathCap = dealMathCaption(pick);
              return (
                <div style={{marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                    <Icon name="star" size={21} color={T.butter} stroke={2.4}/>
                    <span style={{fontSize:10,letterSpacing:2,color:T.ink50,fontWeight:800}}>TODAY'S TOP PICK</span>
                    <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10.5,color:T.basil,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:20,padding:"2px 9px",fontWeight:700,letterSpacing:.2}}>
                      <span style={{display:"inline-block",width:5,height:5,borderRadius:"50%",background:T.leaf}}/>
                      {reason}
                    </span>
                  </div>
                  <Glass style={{padding:16,borderRadius:18,border:`1px solid ${T.sage}`,boxShadow:T.shadowCard}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                      <div style={{width:52,height:52,borderRadius:14,background:T.parchment,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><ItemIcon emoji={getItemEmoji(pick.id, pick.name, pick.category)} size={32}/></div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,lineHeight:1.2}}>{pick.name}</div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                          <span style={{fontSize:11,fontWeight:700,color:T.forest}}>{pick.store.logo} {pick.store.name}</span>
                          <Pill label={pick.dealLabel} color={pick.store.color}/>
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.basil,lineHeight:1}}>${displayP.toFixed(2)}</div>
                        <div style={{fontSize:10,color:T.leaf,fontWeight:700,marginTop:3}}>save ${pick.savings.toFixed(2)}</div>
                      </div>
                    </div>
                    {mathCap && <div style={{fontSize:11,color:T.ink50,marginBottom:10}}>{mathCap}</div>}
                    <div style={{display:"flex",gap:8}}>
                      <RippleBtn sound={inC?"removeCart":"addCart"} onClick={()=>toggleCart(pick.store.id, pick.id)}
                        style={{flex:1,background:inC?T.sageSoft:T.forest,border:`1px solid ${inC?T.sage:T.forest}`,color:inC?T.basil:T.paper,borderRadius:12,padding:"11px",fontSize:12,fontWeight:700,boxShadow:inC?"none":T.shadowSoft,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                        <Icon name={inC?"x":"plus"} size={13} color={inC?T.basil:T.paper} stroke={2.2}/>
                        {inC ? "Remove" : "Add to Cart"}
                      </RippleBtn>
                      <button onClick={()=>setAskSageDeal({deal:pick,store:pick.store})}
                        aria-label="Ask Sage about this deal"
                        style={{background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:12,padding:"9px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:6}}>
                        <CartIQMark size={18} mood="happy"/>
                        Ask Sage
                      </button>
                    </div>
                  </Glass>
                </div>
              );
            })()}

            {/* ── For Your Goals rail — health-profile deal surfacing ── */}
            <GoalRail
              allDeals={allDeals}
              profile={healthProfile}
              inCart={inCart}
              toggleCart={toggleCart}
              isWatched={isWatched}
              onToggleWatch={toggleWatchlist}
              setAskSageDeal={setAskSageDeal}
              pantry={pantry}
              addDealToPantry={addDealToPantry}
            />

            {/* ── Planned Shop Banner ── unified layout for both states */}
            {plannedShop?.date ? (
              <button onClick={()=>{setShopPlannerOpen(true);SFX.tabSwitch();}}
                aria-label={`Shop planned for ${plannedShop.label}, tap to edit`}
                style={{width:"100%",display:"flex",alignItems:"center",gap:11,padding:"11px 14px",marginBottom:14,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                <div style={{width:30,height:30,borderRadius:9,background:T.paper,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <Icon name="calendar" size={22} color={T.basil} stroke={2.2}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:9.5,letterSpacing:1.5,color:T.basil,fontWeight:800}}>NEXT SHOP</div>
                  <div style={{fontSize:12.5,fontWeight:700,color:T.forest,marginTop:1}}>{plannedShop.label}</div>
                </div>
                <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10.5,fontWeight:700,color:T.basil,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:8,padding:"4px 9px"}}>
                  Edit
                  <Icon name="chevron-right" size={13} color={T.basil} stroke={2}/>
                </span>
              </button>
            ) : (
              <button onClick={()=>{setShopPlannerOpen(true);SFX.tabSwitch();}}
                aria-label="Plan your next shop"
                style={{width:"100%",display:"flex",alignItems:"center",gap:11,padding:"11px 14px",marginBottom:14,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                <div style={{width:30,height:30,borderRadius:9,background:T.paper,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <Icon name="calendar" size={22} color={T.ink50} stroke={2.2}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:9.5,letterSpacing:1.5,color:T.ink50,fontWeight:800}}>PLAN AHEAD</div>
                  <div style={{fontSize:12.5,fontWeight:600,color:T.forest,marginTop:1}}>Plan your next shop to filter deals by time</div>
                </div>
                <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10.5,fontWeight:700,color:T.basil,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:8,padding:"4px 9px"}}>
                  Set
                  <Icon name="chevron-right" size={13} color={T.basil} stroke={2}/>
                </span>
              </button>
            )}

            {/* ── Quick stats strip ── connected pill matching watchlist/rewards pattern */}
            <div style={{display:"flex",alignItems:"stretch",marginBottom:18,borderRadius:14,background:T.paper,border:`1px solid ${T.hairline}`,overflow:"hidden",boxShadow:T.shadowCard}}>
              {[
                {v:`$${totalSavings.toFixed(0)}`, l:"Available", c:T.basil,   icon:"wallet", action:()=>{SFX.tabSwitch();setTab("split");}},
                {v:urgentDeals.length,             l:"Expiring",  c:T.warn,    icon:"bolt",   action:()=>{SFX.tabSwitch();setTab("bogos");}},
                {v:allDeals.filter(d=>isBundleDeal(d)||d.dealLabel.includes("BIG DEAL")).length, l:"BOGO", c:T.leaf, icon:"target", action:()=>{SFX.tabSwitch();setTab("bogos");setBogoFilter("bogo");}},
                {v:pantryAlerts.length,            l:"Alerts",    c:T.apricot, icon:"package",action:()=>{SFX.tabSwitch();setTab("pantry");}},
              ].map((s,i,arr)=>(
                <Fragment key={i}>
                  <RippleBtn sound="tabSwitch" onClick={s.action}
                    style={{flex:1,background:"transparent",border:"none",borderRadius:0,padding:"11px 6px 10px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",gap:3,minWidth:0}}>
                    <Icon name={s.icon} size={19} color={s.c} stroke={2.3}/>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:s.c,lineHeight:1,marginTop:2}}>{s.v}</div>
                    <div style={{fontSize:9.5,letterSpacing:.6,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>{s.l}</div>
                  </RippleBtn>
                  {i < arr.length-1 && <div style={{width:1,background:T.hairline}}/>}
                </Fragment>
              ))}
            </div>

            {/* ── Stores grid header ── matches section-header pattern */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <Icon name="store" size={20} color={T.ink50} stroke={2.2}/>
                <span style={{fontSize:10,letterSpacing:1.8,color:T.ink50,fontWeight:800}}>YOUR STORES{userLocation?.zip ? ` · ${userLocation.zip}` : ""}</span>
              </div>
              <button onClick={()=>setLocationOpen(true)}
                style={{display:"inline-flex",alignItems:"center",gap:5,background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:9,padding:"4px 10px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                <Icon name="pin" size={19} color={T.basil} stroke={2.2}/>
                {userLocation?.zip ? "Change" : "Set ZIP"}
              </button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14,marginBottom:18}}>
              {activeStores.length > 0 ? [...activeStores].sort((a,b)=>netBenefit(b)-netBenefit(a)).map((store,si)=>(
                <StoreCard key={store.id} store={store} si={si} isWin={store.id===bestStore?.id} cartCount={cartCount(store.id)} onOpen={()=>{SFX.tabSwitch();setActiveStore(store);}}/>
              )) : (
                <Glass style={{gridColumn:"1 / -1",padding:"36px 24px",textAlign:"center",borderRadius:18,border:`1px dashed ${T.sage}`}}>
                  <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:54,height:54,borderRadius:"50%",background:`linear-gradient(135deg, ${T.leaf}26, ${T.basil}1A)`,border:`1px solid ${T.sage}`,marginBottom:12}}>
                    <Icon name="store" size={32} color={T.basil} stroke={2}/>
                  </div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest,marginBottom:6}}>No stores to show</div>
                  <div style={{fontSize:12,color:T.ink50,lineHeight:1.6,maxWidth:320,margin:"0 auto 16px"}}>
                    Pick some stores near you and we'll start tracking deals automatically.
                  </div>
                  <RippleBtn sound="tabSwitch" onClick={()=>setLocationOpen(true)}
                    style={{background:`${T.forest}`,border:"none",color:T.paper,borderRadius:11,padding:"10px 22px",fontSize:12,fontWeight:700,boxShadow:T.shadowFloat,display:"inline-flex",alignItems:"center",gap:7}}>
                    <Icon name="pin" size={19} color={T.paper} stroke={2.2}/>
                    Pick Stores
                  </RippleBtn>
                </Glass>
              )}
            </div>
          </div>
        )}

        {/* ══ SPLIT CART ══ */}
        {!activeStore && tab==="split" && (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* ── HEADER ───────────────────────────────────────────── */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <div style={{width:32,height:32,borderRadius:9,background:`linear-gradient(135deg, ${T.basil}33, ${T.leaf}1A)`,border:`1px solid ${T.basil}66`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="cart" size={18} color={T.basil} stroke={2.2}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.4,lineHeight:1}}>Smart Cart</div>
              </div>
            </div>
            <div style={{fontSize:12,color:T.ink50,marginBottom:14,marginLeft:42,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              Routed across {splitStores.length} store{splitStores.length===1?"":"s"} · {splitCart.length} item{splitCart.length===1?"":"s"}
            </div>

            {/* ── HERO — sage→cream with NET as headline, GROSS / DELIVERY as derivations ── */}
            <div style={{
              position:"relative",overflow:"hidden",
              padding:"22px 22px",marginBottom:14,borderRadius:20,
              background:`
                radial-gradient(120% 80% at 100% 0%, ${T.butterSoft}EE 0%, transparent 55%),
                radial-gradient(80% 60% at 0% 100%, ${T.sageSoft}88 0%, transparent 50%),
                linear-gradient(135deg, ${T.sageSoft} 0%, ${T.parchment} 100%)
              `,
              border:`1px solid ${T.sage}66`,
              boxShadow:T.shadowFloat,
            }}>
              {/* Decorative wheat sprig */}
              <div style={{position:"absolute",top:14,right:16,opacity:.18,pointerEvents:"none"}}>
                <Icon name="wheat" size={64} color={T.basil} stroke={1.6}/>
              </div>

              {/* Eyebrow */}
              <div style={{display:"inline-flex",alignItems:"center",gap:6,marginBottom:10,position:"relative"}}>
                <Icon name="cart" size={14} color={T.basil} stroke={2.4}/>
                <span style={{fontSize:10,letterSpacing:2,color:T.basil,fontWeight:800,textTransform:"uppercase"}}>Net savings</span>
              </div>

              {/* Headline — NET, big */}
              <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:14,position:"relative"}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:48,fontWeight:700,color:T.forest,letterSpacing:-1.4,lineHeight:.95}}>${splitNet.toFixed(2)}</div>
                <div style={{fontSize:11,color:T.basil,fontWeight:700,letterSpacing:.4}}>after delivery</div>
              </div>

              {/* Two-cell breakdown — gross saved & delivery cost */}
              <div style={{display:"grid",gridTemplateColumns:avgUnitSaved?"1fr 1fr 1fr":"1fr 1fr",gap:10,position:"relative"}}>
                <div style={{padding:"10px 12px",background:`${T.paper}8C`,border:`1px solid ${T.sage}55`,borderRadius:12}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.basil,lineHeight:1,letterSpacing:-.3}}>${splitSavings.toFixed(2)}</div>
                  <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Saved gross</div>
                </div>
                <div style={{padding:"10px 12px",background:`${T.paper}8C`,border:`1px solid ${T.sage}55`,borderRadius:12}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.apricot,lineHeight:1,letterSpacing:-.3}}>−${splitDeliv.toFixed(2)}</div>
                  <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Delivery</div>
                </div>
                {avgUnitSaved && (
                  <div style={{padding:"10px 12px",background:`${T.paper}8C`,border:`1px solid ${T.sage}55`,borderRadius:12}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.skyBright,lineHeight:1,letterSpacing:-.3}}>${avgUnitSaved}</div>
                    <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Avg/unit</div>
                  </div>
                )}
              </div>

              {/* Rewards preview row — quiet, lives below the breakdown */}
              {splitEstReward > 0 && (
                <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.sage}55`,display:"flex",alignItems:"center",gap:7,position:"relative",flexWrap:"wrap"}}>
                  <Icon name="gift" size={14} color={T.apricot} stroke={2.2}/>
                  <span style={{fontSize:12,color:T.basil,fontWeight:600}}>
                    Earn{" "}
                    <span style={{color:T.forest,fontWeight:800,fontFamily:FONT_DISPLAY,fontSize:14,letterSpacing:-.2}}>+${splitEstReward.toFixed(2)}</span>
                    {" "}{REWARDS_COPY.shortLabel.toLowerCase()} if sent via Instacart
                  </span>
                  {!AFFILIATE_LIVE && (
                    <span style={{fontSize:8.5,fontWeight:800,letterSpacing:.8,color:T.apricot,background:`${T.butterSoft}cc`,border:`1px solid ${T.apricot}66`,borderRadius:4,padding:"2px 5px",lineHeight:1}}>
                      PREVIEW
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Nutrition IQ — cart-level nutrition rollup, neutral facts only */}
            {splitCart.length > 0 && (
              <NutritionRollup
                items={splitCart.map(it => ({ id: it.id, qty: getSplitQty(it) }))}
                profile={healthProfile}
                onToggleGoal={(gid)=>{
                  setHealthProfile(prev => prev.includes(gid) ? prev.filter(x => x !== gid) : [...prev, gid]);
                  SFX.tabSwitch();
                }}
              />
            )}

            {/* ── BUDGET BAR — running cart total vs weekly budget, always visible.
                Slider edits the shared `budget` state; the dashboard Weekly Budget
                card reads/writes the same value. Two-segment bar: budget zone fills
                with threshold color; when over, a separate berry segment extends
                past the budget zone showing exact overage. ─────────────────────── */}
            {(() => {
              const cost = splitData.cartTotal;
              const overage = Math.max(0, cost - budget);
              const isOver = overage > 0;
              const totalScale = isOver ? cost : budget;
              const budgetBarWidth = (budget / totalScale) * 100;
              const overageBarWidth = (overage / totalScale) * 100;
              const fillPct = budget > 0 ? Math.min((cost/budget)*100, 100) : 0;
              const fillColor = cost/budget>.9 ? T.berry : cost/budget>.7 ? T.apricot : T.basil;
              return (
                <Glass style={{padding:"14px 16px",marginBottom:14,borderRadius:16,border:`1px solid ${T.hairline}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <Icon name="wallet" size={14} color={T.basil} stroke={2.2}/>
                      <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest}}>Weekly Budget</div>
                    </div>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.3}}>${budget}</div>
                  </div>
                  <input
                    className="budget-slider"
                    type="range" min={50} max={500} step={10} value={budget}
                    onChange={e=>setBudget(+e.target.value)}
                    aria-label="Weekly budget amount"
                    style={{margin:"0 0 10px 0"}}/>
                  <div style={{display:"flex",height:7,background:T.parchment,borderRadius:4,overflow:"hidden",marginBottom:6}}>
                    <div style={{position:"relative",width:`${budgetBarWidth}%`,background:T.parchment}}>
                      <div style={{position:"absolute",inset:0,width:`${fillPct}%`,background:fillColor,transition:"width .4s ease"}}/>
                    </div>
                    {isOver && (
                      <div style={{width:`${overageBarWidth}%`,background:T.berry,borderLeft:`2px solid ${T.paper}`}}/>
                    )}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.ink50}}>
                    <span>Cart: <strong style={{color:T.forest,fontWeight:700}}>${cost.toFixed(2)}</strong></span>
                    {isOver ? (
                      <span>Over by <strong style={{color:T.berry,fontWeight:700}}>${overage.toFixed(2)}</strong></span>
                    ) : (
                      <span>Left: <strong style={{color:cost/budget>.9?T.berry:T.leaf,fontWeight:700}}>${(budget-cost).toFixed(2)}</strong></span>
                    )}
                  </div>
                </Glass>
              );
            })()}

            {/* Phase 2 — Embedded search bar. Always visible at the top of
                Smart Cart so the primary "add an item to my cart" flow is
                one tap away. Opens the same GlobalSearchOverlay used by the
                top-bar icon — unscoped, respecting dietary restrictions. */}
            <button
              onClick={()=>{setSearchOverlay({});SFX.tabSwitch();}}
              style={{
                width:"100%",display:"flex",alignItems:"center",gap:10,
                padding:"12px 14px",marginBottom:14,
                background:T.parchment,
                border:`1px solid ${T.hairline}`,
                borderRadius:12,cursor:"pointer",fontFamily:"inherit",
                textAlign:"left",transition:"all .15s ease",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.sage}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.hairline}
              aria-label="Search for items to add to Smart Cart"
            >
              <Icon name="search" size={18} color={T.ink50} stroke={2.2}/>
              <span style={{flex:1,fontSize:13,color:T.ink50,fontWeight:500}}>Search for an item to add…</span>
              <span style={{fontSize:10,color:T.ink50,letterSpacing:.5,fontWeight:700,background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:5,padding:"2px 6px"}}>{EXPANDED_CATALOG.length}+</span>
            </button>

            {/* "Restore items" pill — only shown when user has dismissed at least one item */}
            {dismissedSplit.length > 0 && (
              <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}>
                <button onClick={()=>{setDismissedSplit([]);SFX.tabSwitch();}}
                  style={{display:"inline-flex",alignItems:"center",gap:6,background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:20,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  <Icon name="refresh" size={22} color={T.basil} stroke={2.2}/>
                  Restore {dismissedSplit.length} removed item{dismissedSplit.length>1?"s":""}
                </button>
              </div>
            )}

            {splitCart.length === 0 ? (
              <>
              {/* Phase C — When cart is empty, lead with the rebuild card */}
              {REBUILD_CART_ENABLED && (
                <RebuildCartCard
                  hasStaples={staples && staples.length > 0}
                  preview={rebuildPreview}
                  onRebuild={handleRebuildCart}
                  onSetupStaples={() => setStaplesPickerOpen(true)}
                />
              )}
              {/* Existing empty state — kept as fallback messaging */}
              <Glass style={{padding:"36px 24px",textAlign:"center",borderRadius:18,border:`1px dashed ${T.sage}`,marginBottom:16}}>
                <div style={{width:64,height:64,borderRadius:"50%",background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
                  <Icon name="cart" size={40} color={T.basil} stroke={2.2}/>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,marginBottom:6}}>Nothing to split</div>
                <div style={{fontSize:13,color:T.ink50,lineHeight:1.6,maxWidth:320,margin:"0 auto"}}>
                  {dismissedSplit.length > 0
                    ? "You've removed every item. Tap Restore above to bring them back."
                    : "Add stores in your area to start optimizing your shopping list."}
                </div>
              </Glass>
              </>
            ) : (
              <div style={{display:"grid",gap:18,marginBottom:16}}>
                {splitStores.map((store, gi) => {
                  const itemsForStore = splitCart.filter(it => it.bestDeal.store.id === store.id);
                  if (itemsForStore.length === 0) return null;
                  // Totals now reflect user-chosen quantities (qty defaults to 2 for BOGO)
                  const storeTotal = itemsForStore.reduce((s, it) => s + computeLineTotal(it.bestDeal, getSplitQty(it)), 0);
                  const storeSavings = itemsForStore.reduce((s, it) => {
                    const q = getSplitQty(it);
                    const mult = triggeredSavingsMultiplier(it.bestDeal, q);
                    return s + (it.bestDeal.savings * mult) + it.bestDeal.ibotta;
                  }, 0);
                  const totalUnits = itemsForStore.reduce((s, it) => s + getSplitQty(it), 0);
                  return (
                    <div key={store.id} className="anim-slide-up" style={{"--i":gi*1.25}}>
                      {/* Store section header */}
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"0 4px"}}>
                        <div style={{width:36,height:36,borderRadius:10,background:store.bg,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 14px ${store.glow}40`,border:`1px solid ${store.glow}55`}}>
                          <ItemIcon emoji={store.logo} size={20}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                            <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:store.color,lineHeight:1.1,letterSpacing:-.2}}>{store.name}</div>
                            <div style={{fontSize:9.5,letterSpacing:.6,color:store.color,background:`${store.glow}22`,border:`1px solid ${store.glow}44`,borderRadius:5,padding:"2px 6px",fontWeight:800,textTransform:"uppercase"}}>{itemsForStore.length} item{itemsForStore.length>1?"s":""}</div>
                          </div>
                          <div style={{fontSize:11,color:T.ink50,marginTop:3,fontWeight:600}}>
                            {totalUnits} unit{totalUnits>1?"s":""} · saves <span style={{color:T.basil,fontWeight:800}}>${storeSavings.toFixed(2)}</span>
                          </div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.forest,lineHeight:1,letterSpacing:-.4}}>${storeTotal.toFixed(2)}</div>
                          <div style={{fontSize:9.5,letterSpacing:1.4,color:T.ink50,marginTop:4,fontWeight:700}}>SUBTOTAL</div>
                        </div>
                      </div>

                      {/* Items in this store */}
                      <div style={{display:"grid",gap:8,marginBottom:10}}>
                        {itemsForStore.map((item, i) => {
                          const bd = item.bestDeal;
                          const isBogo = isBogoDeal(bd);
                          const isB2G1 = isB2G1Deal(bd);
                          const qty = getSplitQty(item);
                          const lineTotal = computeLineTotal(bd, qty);
                          const lineOrig = bd.origPrice * qty;
                          const savingsMult = triggeredSavingsMultiplier(bd, qty);
                          const lineSaved = bd.savings * savingsMult;
                          const minQty = bundleMinQty(bd);
                          const effectivePerUnit = qty > 0 ? lineTotal / qty : 0;
                          const setQty = (next) => {
                            const clamped = Math.max(minQty, Math.min(99, next));
                            setSplitQty(prev => ({ ...prev, [item.id]: clamped }));
                            SFX.tabSwitch();
                          };
                          return (
                            <SwipeableSplitItem key={item.id}
                              onDelete={()=>{
                                setDismissedSplit(prev => prev.includes(item.id) ? prev : [...prev, item.id]);
                                SFX.removeCart();
                                notify(`Removed ${item.name}`, {
                                  type: "warn",
                                  action: { label: "UNDO", onClick: () => setDismissedSplit(prev => prev.filter(id => id !== item.id)) },
                                });
                              }}>
                              <Glass className="anim-slide-up" style={{"--i":i,padding:0,overflow:"hidden",display:"flex",alignItems:"stretch",borderRadius:14}}>
                                <div style={{width:4,background:`linear-gradient(180deg,${store.color},${store.glow})`,flexShrink:0}}/>
                                <div style={{padding:"12px 14px",flex:1,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                                  <ItemIcon emoji={getItemEmoji(item.id, item.name, item.category)} size={24}/>
                                  <div style={{flex:1,minWidth:120}}>
                                    <div style={{fontSize:14,fontWeight:700,color:T.forest,lineHeight:1.2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                      <span>{item.name}</span>
                                      {isOrganic(item.name) && <OrgBadge small/>}
                                      <Pill label={bd.dealLabel} color={store.color}/>
                                    </div>
                                    <div style={{fontSize:11,color:T.ink50,marginTop:3}}>{item.category} · {item.unit}</div>
                                    {/* Saved amount */}
                                    <div style={{fontSize:16,fontWeight:800,color:T.basil,marginTop:5,fontFamily:FONT_DISPLAY,letterSpacing:-.3,lineHeight:1.1}}>
                                      −${lineSaved.toFixed(2)} <span style={{fontSize:10,color:T.ink50,fontWeight:600,letterSpacing:.4,fontFamily:"inherit"}}>saved</span>
                                    </div>
                                    {/* Your price memory — only renders if user has prior history for this item */}
                                    <PriceMemory itemId={bd.id} currentPrice={effectivePerUnit || bd.salePrice || bd.origPrice} history={priceMemory?.[bd.id]} compact/>
                                    {/* Bundle-deal hints */}
                                    {isBogo && !isB2G1 && qty < 2 && (
                                      <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,color:T.warn,marginTop:4,fontWeight:700}}><Icon name="info" size={21} color={T.warn} stroke={2.4}/>BOGO needs 2 to qualify</div>
                                    )}
                                    {isBogo && !isB2G1 && qty % 2 !== 0 && qty >= 2 && (
                                      <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,color:T.warn,marginTop:4,fontWeight:700}}><Icon name="info" size={21} color={T.warn} stroke={2.4}/>Odd qty — last unit pays full price</div>
                                    )}
                                    {isB2G1 && qty < 3 && (
                                      <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,color:T.warn,marginTop:4,fontWeight:700}}><Icon name="info" size={21} color={T.warn} stroke={2.4}/>Buy 2 Get 1 needs 3 to qualify</div>
                                    )}
                                    {isB2G1 && qty >= 3 && qty % 3 !== 0 && (
                                      <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,color:T.warn,marginTop:4,fontWeight:700}}><Icon name="info" size={21} color={T.warn} stroke={2.4}/>Not a multiple of 3 — extra units pay full price</div>
                                    )}
                                    {/* Quantity stepper */}
                                    <div style={{display:"flex",alignItems:"center",gap:0,marginTop:8,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,width:"fit-content",overflow:"hidden"}}>
                                      <button onClick={()=>setQty(qty - 1)} disabled={qty <= minQty}
                                        aria-label="Decrease quantity"
                                        style={{background:"transparent",border:"none",color:qty<=minQty?T.ink15:T.forest,width:36,height:34,fontSize:18,fontWeight:800,cursor:qty<=minQty?"not-allowed":"pointer",fontFamily:"inherit"}}>−</button>
                                      <span style={{minWidth:30,textAlign:"center",fontSize:14,fontWeight:800,color:T.forest,fontFamily:FONT_DISPLAY}}>{qty}</span>
                                      <button onClick={()=>setQty(qty + 1)}
                                        aria-label="Increase quantity"
                                        style={{background:"transparent",border:"none",color:T.forest,width:36,height:34,fontSize:18,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>+</button>
                                    </div>
                                  </div>
                                  {/* Actual sale price, far right */}
                                  <div style={{textAlign:"right",minWidth:74}}>
                                    <div style={{fontFamily:FONT_DISPLAY,fontSize:24,fontWeight:700,color:T.forest,lineHeight:1}}>${lineTotal.toFixed(2)}</div>
                                    {lineTotal < lineOrig && (
                                      <div style={{fontSize:11,color:T.ink50,textDecoration:"line-through",marginTop:4}}>${lineOrig.toFixed(2)}</div>
                                    )}
                                    {qty > 1 && (
                                      <div style={{fontSize:10,color:T.ink50,marginTop:2}}>${effectivePerUnit.toFixed(2)} ea</div>
                                    )}
                                  </div>
                                </div>
                              </Glass>
                            </SwipeableSplitItem>
                          );
                        })}
                      </div>

                      {/* Per-store Send button */}
                      <RippleBtn sound="success" onClick={()=>sendSplitStoreToCart(store)}
                        style={{background:`linear-gradient(135deg,${store.color},${store.glow})`,border:"none",color:T.paper,borderRadius:12,padding:"13px 16px",fontSize:13,fontWeight:800,boxShadow:`0 0 18px ${store.glow}40`,width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,letterSpacing:.2}}>
                        <ItemIcon emoji={store.logo} size={18}/>
                        <span>Send {totalUnits} unit{totalUnits>1?"s":""} to {store.name}</span>
                        <span style={{fontSize:13,fontWeight:700,opacity:.95}}>· ${storeTotal.toFixed(2)}</span>
                      </RippleBtn>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ PANTRY ══
            Layout mirrors Price IQ: header → budget → summary strip → controls → list.
            Budget card sits at the top of the pantry subview as the primary
            planning anchor; shares `cartiq_budget` state with Smart Cart.
            ─────────────────────────────────────────────────────────── */}
        {!activeStore && tab==="pantry" && (() => {
          const today = new Date("2026-03-31");
          // Per-item analytics — expiry days, supply class, on-sale lookup
          const rows = pantry.map(item => {
            const ci = item._userAdded
              ? item.category
              : MASTER_LIST.find(m=>m.id===item.id)?.category;
            const deal = item._userAdded ? null : allDeals.find(d=>d.id===item.id);
            const expiry = item.expiryDate ? new Date(item.expiryDate) : null;
            const daysToExpiry = expiry ? Math.ceil((expiry-today)/86400000) : null;
            const supply = typeof item.daysLeft === "number" ? item.daysLeft : null;
            const expired = daysToExpiry !== null && daysToExpiry <= 0;
            const expiringSoon = daysToExpiry !== null && daysToExpiry > 0 && daysToExpiry <= 3;
            const lowSupply = supply !== null && supply <= 3;
            return { item, ci, deal, daysToExpiry, supply, expired, expiringSoon, lowSupply };
          });

          const summary = {
            total: rows.length,
            expiring: rows.filter(r=>r.expired || r.expiringSoon).length,
            lowSupply: rows.filter(r=>r.lowSupply).length,
            onSale: rows.filter(r=>r.deal).length,
          };

          // Sort: expiry asc (nulls last), supply asc, alpha, recent (last bought)
          const sortFn = {
            expiry: (a,b) => {
              const A = a.daysToExpiry, B = b.daysToExpiry;
              if (A === null && B === null) return 0;
              if (A === null) return 1;
              if (B === null) return -1;
              return A - B;
            },
            supply: (a,b) => {
              const A = a.supply, B = b.supply;
              if (A === null && B === null) return 0;
              if (A === null) return 1;
              if (B === null) return -1;
              return A - B;
            },
            alpha: (a,b) => a.item.name.localeCompare(b.item.name),
            recent: (a,b) => {
              const A = a.item.lastBought ? new Date(a.item.lastBought + " 2026").getTime() : 0;
              const B = b.item.lastBought ? new Date(b.item.lastBought + " 2026").getTime() : 0;
              return B - A;
            },
          }[pantrySort] || ((a,b)=>0);
          const sorted = [...rows].sort(sortFn);

          // Filter chips — match Price IQ pattern with counts
          const q = pantrySearch.trim().toLowerCase();
          const filtered = sorted.filter(r => {
            if (pantryFilter === "expiring" && !(r.expired || r.expiringSoon)) return false;
            if (pantryFilter === "low" && !r.lowSupply) return false;
            if (pantryFilter === "onSale" && !r.deal) return false;
            if (pantryFilter === "custom" && !r.item._userAdded) return false;
            if (q) {
              const hay = `${r.item.name} ${r.ci || ""}`.toLowerCase();
              if (!hay.includes(q)) return false;
            }
            return true;
          });
          const filterChips = [
            {id:"all",      label:"All",         count:summary.total},
            {id:"expiring", label:"Expiring",    count:summary.expiring},
            {id:"low",      label:"Low supply",  count:summary.lowSupply},
            {id:"onSale",   label:"On sale",     count:summary.onSale},
            {id:"custom",   label:"Custom",      count:rows.filter(r=>r.item._userAdded).length},
          ];

          // My Items subview meta — drives subtitle copy and counts.
          const subviewMeta = {
            pantry: { subtitle: <>Tracking <span style={{color:T.forest,fontWeight:700}}>{summary.total}</span> items · expiry & supply alerts</> },
            lists:  { subtitle: <>Recurring missions and one-off occasions · <span style={{color:T.forest,fontWeight:700}}>{lists.length}</span> active</> },
            all:    { subtitle: <>Every item you've saved across pantry and lists</> },
          };
          const subviewChips = [
            { id: "pantry", label: "Pantry", count: summary.total },
            { id: "lists",  label: "Lists",  count: lists.length },
            { id: "all",    label: "All",    count: myItems.length },
          ];

          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* ── HEADER ───────────────────────────────────────────── */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <div style={{width:32,height:32,borderRadius:9,background:`linear-gradient(135deg, ${T.peach}33, ${T.apricot}1A)`,border:`1px solid ${T.peach}66`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="box" size={18} color={T.basil} stroke={2.2}/>
              </div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.forest,letterSpacing:-.3,flex:1,minWidth:0}}>My Items</div>
              <button onClick={()=>{setAddOwnItemOpen(true);SFX.tabSwitch();}}
                aria-label="Add your own item"
                style={{background:T.berrySoft,border:`1px solid rgba(160,69,98,.30)`,color:T.basil,borderRadius:10,padding:"6px 12px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:5,flexShrink:0,whiteSpace:"nowrap"}}>
                <Icon name="plus" size={11} color={T.basil} stroke={2.6}/>
                <span>Add item</span>
              </button>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:12,fontSize:12,color:T.ink50,lineHeight:1.5}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:T.leaf,boxShadow:`0 0 0 3px ${T.leaf}1A`}}/>
              <span>{subviewMeta[myItemsView].subtitle}</span>
            </div>

            {/* ── SUBVIEW CHIPS — Pantry / Lists / All ─────────────── */}
            <div style={{display:"flex",gap:6,marginBottom:14}}>
              {subviewChips.map(chip => {
                const isActive = myItemsView === chip.id;
                return (
                  <button key={chip.id}
                    onClick={() => { setMyItemsView(chip.id); SFX.tabSwitch(); }}
                    style={{
                      flex:1,
                      display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6,
                      padding:"9px 12px",
                      borderRadius:12,
                      background: isActive ? T.forest : T.paper,
                      border: `1px solid ${isActive ? T.forest : T.hairline}`,
                      color: isActive ? T.paper : T.ink70,
                      fontSize:12, fontWeight:700,
                      fontFamily:"inherit", cursor:"pointer",
                      transition:"all .15s",
                      boxShadow: isActive ? "none" : T.shadowCard,
                    }}>
                    <span>{chip.label}</span>
                    <span style={{
                      display:"inline-flex", alignItems:"center", justifyContent:"center",
                      minWidth:18, height:18, padding:"0 5px",
                      borderRadius:9,
                      background: isActive ? "rgba(255,255,255,.18)" : T.parchment,
                      color: isActive ? T.paper : T.ink50,
                      fontSize:10, fontWeight:800,
                    }}>{chip.count}</span>
                  </button>
                );
              })}
            </div>

            {myItemsView === "pantry" && (<>
            {/* ── BUDGET CARD — primary planning anchor at the top.
                Two-segment bar: budget zone fills with threshold color; when
                over, a separate berry segment extends past the budget zone
                showing exact overage. Shares `cartiq_budget` state with the
                Smart Cart Budget Bar. ────────────────────────────────────── */}
            {rows.length > 0 && (() => {
              const overage = Math.max(0, cartCost - budget);
              const isOver = overage > 0;
              const totalScale = isOver ? cartCost : budget;
              const budgetBarWidth = (budget / totalScale) * 100;
              const overageBarWidth = (overage / totalScale) * 100;
              const fillPct = budget > 0 ? Math.min((cartCost/budget)*100, 100) : 0;
              const fillColor = cartCost/budget>.9 ? T.berry : cartCost/budget>.7 ? T.apricot : T.basil;
              return (
                <Glass style={{padding:"14px 16px",marginBottom:14,borderRadius:16,border:`1px solid ${T.hairline}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <Icon name="wallet" size={14} color={T.basil} stroke={2.2}/>
                      <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest}}>Weekly Budget</div>
                    </div>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.3}}>${budget}</div>
                  </div>
                  <input
                    className="budget-slider"
                    type="range" min={50} max={500} step={10} value={budget}
                    onChange={e=>setBudget(+e.target.value)}
                    aria-label="Weekly budget amount"
                    style={{margin:"0 0 10px 0"}}/>
                  <div style={{display:"flex",height:7,background:T.parchment,borderRadius:4,overflow:"hidden",marginBottom:6}}>
                    <div style={{position:"relative",width:`${budgetBarWidth}%`,background:T.parchment}}>
                      <div style={{position:"absolute",inset:0,width:`${fillPct}%`,background:fillColor,transition:"width .4s ease"}}/>
                    </div>
                    {isOver && (
                      <div style={{width:`${overageBarWidth}%`,background:T.berry,borderLeft:`2px solid ${T.paper}`}}/>
                    )}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.ink50}}>
                    <span>Cart: <strong style={{color:T.forest,fontWeight:700}}>${cartCost.toFixed(2)}</strong></span>
                    {isOver ? (
                      <span>Over by <strong style={{color:T.berry,fontWeight:700}}>${overage.toFixed(2)}</strong></span>
                    ) : (
                      <span>Left: <strong style={{color:cartCost/budget>.9?T.berry:T.leaf,fontWeight:700}}>${(budget-cartCost).toFixed(2)}</strong></span>
                    )}
                  </div>
                </Glass>
              );
            })()}
            {/* ── SUMMARY STRIP — connected pill, mirrors Price IQ ─── */}
            <div style={{display:"flex",marginBottom:14,padding:0,borderRadius:14,background:T.parchment,border:`1px solid ${T.hairline}`,overflow:"hidden"}}>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="clock" size={12} color={T.berry} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Expiring</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:summary.expiring>0?T.berry:T.forest,letterSpacing:-.3,lineHeight:1.1}}>{summary.expiring}</div>
              </div>
              <div style={{width:1,background:T.hairline}}/>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="trending-down" size={12} color={T.warn} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Low</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:summary.lowSupply>0?T.warn:T.forest,letterSpacing:-.3,lineHeight:1.1}}>{summary.lowSupply}</div>
              </div>
              <div style={{width:1,background:T.hairline}}/>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="tag" size={12} color={T.leaf} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>On sale</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:summary.onSale>0?T.leaf:T.forest,letterSpacing:-.3,lineHeight:1.1}}>{summary.onSale}</div>
              </div>
              <div style={{width:1,background:T.hairline}}/>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="box" size={12} color={T.ink50} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Total</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.3,lineHeight:1.1}}>{summary.total}</div>
              </div>
            </div>

            {/* ── SEARCH + SORT (only show when there's content) ─── */}
            {rows.length > 0 && (
              <>
                <div style={{display:"flex",gap:8,marginBottom:10}}>
                  <div style={{flex:1,display:"flex",alignItems:"center",gap:9,padding:"9px 12px",background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:12,boxShadow:T.shadowCard}}>
                    <Icon name="search" size={16} color={T.ink50} stroke={2.2}/>
                    <input
                      type="text"
                      value={pantrySearch}
                      onChange={e=>setPantrySearch(e.target.value)}
                      placeholder="Search pantry…"
                      aria-label="Search pantry"
                      style={{flex:1,minWidth:0,background:"transparent",border:"none",outline:"none",fontFamily:"inherit",fontSize:13,color:T.forest}}
                    />
                    {pantrySearch && (
                      <button onClick={()=>setPantrySearch("")}
                        aria-label="Clear search"
                        style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:"50%",width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",padding:0,flexShrink:0}}>
                        <Icon name="x" size={11} color={T.ink50} stroke={2.4}/>
                      </button>
                    )}
                  </div>
                  <div style={{position:"relative",flexShrink:0}}>
                    <select
                      value={pantrySort}
                      onChange={e=>{setPantrySort(e.target.value);SFX.tabSwitch();}}
                      aria-label="Sort pantry"
                      style={{appearance:"none",WebkitAppearance:"none",MozAppearance:"none",height:"100%",padding:"9px 32px 9px 32px",background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:12,boxShadow:T.shadowCard,fontFamily:"inherit",fontSize:12,fontWeight:700,color:T.forest,cursor:"pointer"}}>
                      <option value="expiry">Expiring soonest</option>
                      <option value="supply">Lowest supply</option>
                      <option value="alpha">A → Z</option>
                      <option value="recent">Recently bought</option>
                    </select>
                    <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",display:"flex"}}>
                      <Icon name="filter" size={14} color={T.ink50} stroke={2.2}/>
                    </span>
                    <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",display:"flex"}}>
                      <Icon name="chevron-down" size={14} color={T.ink50} stroke={2.4}/>
                    </span>
                  </div>
                </div>

                {/* ── FILTER CHIPS ─────────────────────────────────── */}
                <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:14,paddingBottom:2,scrollbarWidth:"none"}}>
                  {filterChips.map(chip => {
                    const isActive = pantryFilter === chip.id;
                    return (
                      <button key={chip.id}
                        onClick={() => { setPantryFilter(chip.id); SFX.tabSwitch(); }}
                        style={{
                          flexShrink:0,
                          display:"inline-flex", alignItems:"center", gap:6,
                          padding:"7px 13px",
                          borderRadius:20,
                          background: isActive ? T.forest : T.paper,
                          border: `1px solid ${isActive ? T.forest : T.hairline}`,
                          color: isActive ? T.paper : T.ink70,
                          fontSize:11.5, fontWeight:700,
                          fontFamily:"inherit", cursor:"pointer",
                          whiteSpace:"nowrap",
                          transition:"all .15s",
                        }}>
                        <span>{chip.label}</span>
                        <span style={{
                          display:"inline-flex", alignItems:"center", justifyContent:"center",
                          minWidth:18, height:18, padding:"0 5px",
                          borderRadius:9,
                          background: isActive ? "rgba(255,255,255,.18)" : T.parchment,
                          color: isActive ? T.paper : T.ink50,
                          fontSize:10, fontWeight:800, letterSpacing:.2,
                        }}>{chip.count}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── PANTRY LIST ────────────────────────────────────── */}
            {rows.length === 0 ? (
              <EmptyState icon="🏠" title="Pantry is empty" sub="Add items from your fridge to track expiry dates and get deal alerts." action="+ Add Items" onAction={()=>setPantry(PANTRY_DEFAULT)}/>
            ) : filtered.length === 0 ? (
              <Glass style={{padding:"36px 24px",textAlign:"center",borderRadius:18,border:`1px dashed ${T.sage}`}}>
                <div style={{width:64,height:64,borderRadius:"50%",background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
                  <Icon name="search" size={28} color={T.basil} stroke={2.2}/>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,marginBottom:6}}>No items match</div>
                <div style={{fontSize:13,color:T.ink50,lineHeight:1.6,maxWidth:320,margin:"0 auto"}}>
                  Try a different filter or clear your search.
                </div>
              </Glass>
            ) : (
              <div style={{display:"grid",gap:10,marginBottom:14}}>
                {filtered.map((row, i) => {
                  const { item, ci, deal, daysToExpiry, supply, expired, expiringSoon, lowSupply } = row;
                  // Border accent: red for expired/expiring, amber for low supply, neutral otherwise
                  const accent = expired ? T.berry : expiringSoon ? T.berry : lowSupply ? T.warn : null;
                  // Status pill (single, most-urgent signal)
                  const statusPill = expired
                    ? { bg:"rgba(160,69,98,.10)", border:`rgba(160,69,98,.28)`, color:T.berry, icon:"bolt", text:"EXPIRED" }
                    : expiringSoon
                      ? { bg:"rgba(160,69,98,.08)", border:`rgba(160,69,98,.22)`, color:T.berry, icon:"clock", text:`${daysToExpiry}d to expiry` }
                      : daysToExpiry !== null && daysToExpiry <= 7
                        ? { bg:T.butterSoft, border:T.butter, color:T.warn, icon:"clock", text:`${daysToExpiry}d to expiry` }
                        : daysToExpiry !== null
                          ? { bg:T.parchment, border:T.hairline, color:T.ink70, icon:"clock", text:`${daysToExpiry}d fresh` }
                          : null;

                  return (
                    <Glass key={item.id} className="anim-slide-up"
                      style={{
                        "--i":i,
                        padding:0,
                        borderRadius:16,
                        overflow:"hidden",
                        borderLeft: accent ? `3px solid ${accent}` : undefined,
                      }}>
                      {/* Top row: icon, name, supply badge */}
                      <div style={{padding:"13px 14px 10px",display:"flex",alignItems:"flex-start",gap:11}}>
                        <ItemIcon emoji={getItemEmoji(item.id, item.name, ci)} size={26}/>
                        <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:3}}>
                          <div style={{fontSize:13.5,fontWeight:700,color:T.forest,lineHeight:1.3,wordBreak:"break-word"}}>
                            {item.name}
                            {isOrganic(item.name) && <> <OrgBadge small/></>}
                            {item._userAdded && (
                              <> <span style={{fontSize:9,background:T.berrySoft,color:T.basil,padding:"2px 6px",borderRadius:10,fontWeight:800,letterSpacing:.3,verticalAlign:"middle",whiteSpace:"nowrap"}}>CUSTOM</span></>
                            )}
                          </div>
                          <div style={{fontSize:11,color:T.ink50,lineHeight:1.4}}>
                            <span>{item.qty} {item.unit}</span>
                            {item.lastBought && <><span style={{opacity:.5,margin:"0 6px"}}>·</span><span>Last: {item.lastBought}</span></>}
                            {ci && <><span style={{opacity:.5,margin:"0 6px"}}>·</span><span>{ci}</span></>}
                          </div>
                        </div>
                        {/* Supply pill — visible only when daysLeft is tracked */}
                        {supply !== null && (
                          <div style={{
                            textAlign:"center",
                            background: lowSupply ? "rgba(199,122,44,.10)" : T.parchment,
                            border: `1px solid ${lowSupply ? "rgba(199,122,44,.25)" : T.hairline}`,
                            borderRadius:10,
                            padding:"5px 10px",
                            minWidth:50,
                            flexShrink:0,
                          }}>
                            <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:lowSupply?T.warn:T.leaf,lineHeight:1}}>{supply}d</div>
                            <div style={{fontSize:9,color:T.ink50,letterSpacing:.4,fontWeight:700,marginTop:1}}>SUPPLY</div>
                          </div>
                        )}
                      </div>

                      {/* Status row — expiry pill + deal pill (when present) */}
                      {(statusPill || deal) && (
                        <div style={{display:"flex",alignItems:"center",gap:7,padding:"0 14px 11px",flexWrap:"wrap"}}>
                          {statusPill && (
                            <div style={{display:"inline-flex",alignItems:"center",gap:5,background:statusPill.bg,border:`1px solid ${statusPill.border}`,color:statusPill.color,borderRadius:8,padding:"4px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                              <Icon name={statusPill.icon} size={11} color={statusPill.color} stroke={2.2}/>
                              <span>{statusPill.text}</span>
                            </div>
                          )}
                          {deal && (
                            <div style={{display:"inline-flex",alignItems:"center",gap:5,background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:8,padding:"4px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                              <Icon name="tag" size={11} color={T.basil} stroke={2.2}/>
                              <span>On sale at {deal.store.name} · save ${deal.savings.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action row — expiry editor + restock + remove (custom) */}
                      <div style={{display:"flex",alignItems:"center",gap:7,padding:"9px 12px",borderTop:`1px solid ${T.hairline}`,background:T.paper}}>
                        <label style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0}}>
                          <Icon name="calendar" size={12} color={T.ink50} stroke={2.2}/>
                          <input
                            type="date"
                            value={item.expiryDate||""}
                            onChange={e=>setPantry(p=>p.map(x=>x.id===item.id?{...x,expiryDate:e.target.value}:x))}
                            aria-label={`Expiry date for ${item.name}`}
                            style={{background:"transparent",border:"none",color:T.forest,padding:"2px 0",fontSize:11,fontFamily:"inherit",fontWeight:600,outline:"none",flex:1,minWidth:0}}/>
                        </label>
                        <RippleBtn sound="restock"
                          onClick={()=>setPantry(p=>p.map(x=>x.id===item.id?{...x,daysLeft:(typeof x.daysLeft==="number"?x.daysLeft:0)+3,qty:(x.qty||0)+1}:x))}
                          style={{background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:9,padding:"5px 12px",fontSize:11,fontWeight:700,display:"inline-flex",alignItems:"center",gap:4}}>
                          <Icon name="plus" size={10} color={T.basil} stroke={2.6}/>
                          <span>Restock</span>
                        </RippleBtn>
                        {item._userAdded && (
                          <button onClick={()=>{
                            if (!window.confirm(`Remove "${item.name}" from your pantry?`)) return;
                            setPantry(p => p.filter(x => x.id !== item.id));
                            SFX.tabSwitch();
                          }}
                            style={{background:"rgba(160,69,98,.06)",border:"1px solid rgba(160,69,98,.18)",color:T.berry,borderRadius:9,padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center"}}
                            aria-label={`Remove ${item.name}`}>
                            <Icon name="x" size={11} color={T.berry} stroke={2.4}/>
                          </button>
                        )}
                      </div>
                    </Glass>
                  );
                })}
              </div>
            )}

            </>)}

            {myItemsView === "lists" && (() => {
              const OCCASION_ICON = { groceries:"🧺", snacks:"🍿", meal:"🍝", event:"🎉" };
              // Stable item-count lookup for each list — items where lists[] includes the list name.
              const countForList = (listName) => myItems.reduce((n, it) => (it.lists || []).includes(listName) ? n + 1 : n, 0);

              // Detail view — when activeListId resolves to a real list record.
              const activeList = activeListId ? lists.find(l => l.id === activeListId) : null;
              if (activeList) {
                const itemsInList = myItems.filter(it => (it.lists || []).includes(activeList.name));
                const candidates = myItems.filter(it => !(it.lists || []).includes(activeList.name));
                const hasItems = itemsInList.length > 0;
                const handleRemoveItem = (itemId) => {
                  setMyItems(prev => removeItemFromList(prev, itemId, activeList.name));
                  SFX.removeCart();
                };
                const handleDelete = () => {
                  // Remove list metadata + strip membership from any items that referenced it.
                  setLists(prev => prev.filter(l => l.id !== activeList.id));
                  setMyItems(prev => prev.map(it => {
                    if (!(it.lists || []).includes(activeList.name)) return it;
                    const lists2 = (it.lists || []).filter(l => l !== activeList.name);
                    const { [activeList.name]: _drop, ...listState2 } = it.listState || {};
                    return { ...it, lists: lists2, listState: listState2 };
                  }));
                  setActiveListId(null);
                  notify(`✓ Deleted list "${activeList.name}"`);
                };
                const handleSendToCart = () => {
                  const targetStore = (activeStores && activeStores[0]) || STORES[0];
                  if (!targetStore) { notify("No store available", "warn"); return; }
                  const activeItems = itemsInList.filter(it => {
                    const ls = (it.listState || {})[activeList.name];
                    return !ls || ls.active !== false;
                  });
                  if (activeItems.length === 0) { notify("No active items in this list", "warn"); return; }
                  setCartMap(prev => {
                    const next = { ...prev };
                    activeItems.forEach(it => { next[`${targetStore.id}_${it.id}`] = true; });
                    return next;
                  });
                  setLists(prev => prev.map(l => l.id === activeList.id
                    ? { ...l, fillCount: (l.fillCount || 0) + 1, lastFilledDate: new Date().toISOString().slice(0,10) }
                    : l));
                  SFX.addCart();
                  notify(`✓ Sent ${activeItems.length} ${activeItems.length === 1 ? "item" : "items"} to Smart Cart`);
                  setTab("split");
                };
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    {/* Back + delete row */}
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button
                        onClick={() => { setActiveListId(null); SFX.tabSwitch(); }}
                        aria-label="Back to lists"
                        style={{display:"inline-flex",alignItems:"center",gap:4,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:10,padding:"7px 11px 7px 8px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700,color:T.ink70,boxShadow:T.shadowCard}}>
                        <Icon name="chevron-left" size={14} color={T.ink70} stroke={2.4}/>
                        <span>Lists</span>
                      </button>
                      <div style={{flex:1}}/>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete list "${activeList.name}"? Items stay in your catalog.`)) handleDelete();
                        }}
                        style={{background:"transparent",border:"none",color:T.berry,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",padding:"6px 4px"}}>
                        Delete list
                      </button>
                    </div>

                    {/* Detail header */}
                    <Glass style={{padding:"16px 18px",display:"flex",alignItems:"center",gap:12}}>
                      <div style={{width:42,height:42,borderRadius:12,background:`linear-gradient(135deg, ${T.peach}33, ${T.apricot}1A)`,border:`1px solid ${T.peach}66`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                        <span aria-hidden="true">{OCCASION_ICON[activeList.occasion] || "📋"}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.2,lineHeight:1.2,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeList.name}</div>
                        <div style={{fontSize:11,color:T.ink50,fontWeight:600,textTransform:"capitalize"}}>
                          {activeList.occasion || "general"} · {itemsInList.length} {itemsInList.length === 1 ? "item" : "items"}
                          {activeList.fillCount > 0 && <> · filled {activeList.fillCount}×</>}
                        </div>
                      </div>
                    </Glass>

                    {/* Items list */}
                    {hasItems ? (
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {itemsInList.map(it => (
                          <Glass key={it.id} style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                            <span style={{fontSize:18,flexShrink:0}} aria-hidden="true">{CAT_ICON[it.category] || "🧺"}</span>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:700,color:T.forest,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.name}</div>
                              {it.category && <div style={{fontSize:10.5,color:T.ink50,fontWeight:600,marginTop:2}}>{it.category}</div>}
                            </div>
                            <button
                              onClick={() => handleRemoveItem(it.id)}
                              aria-label={`Remove ${it.name} from list`}
                              style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:8,width:28,height:28,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                              <Icon name="x" size={12} color={T.ink50} stroke={2.4}/>
                            </button>
                          </Glass>
                        ))}
                      </div>
                    ) : (
                      <Glass style={{padding:"24px 18px",textAlign:"center"}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.forest,marginBottom:4}}>This list is empty</div>
                        <div style={{fontSize:11.5,color:T.ink50,lineHeight:1.5,maxWidth:280,marginLeft:"auto",marginRight:"auto"}}>
                          Add items from your catalog to fill it out.
                        </div>
                      </Glass>
                    )}

                    {/* Actions */}
                    <div style={{display:"flex",gap:8}}>
                      <button
                        onClick={() => { setAddItemsToListOpen(true); SFX.tabSwitch(); }}
                        style={{flex:1,padding:"12px",background:T.parchment,border:`1px solid ${T.hairline}`,color:T.forest,borderRadius:12,fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,boxShadow:T.shadowCard}}>
                        <Icon name="plus" size={13} color={T.forest} stroke={2.6}/>
                        <span>Add items</span>
                      </button>
                      <button
                        onClick={handleSendToCart}
                        disabled={!hasItems}
                        style={{
                          flex:2,padding:"12px",
                          background: hasItems ? T.forest : T.parchment,
                          border:`1px solid ${hasItems ? T.forest : T.hairline}`,
                          color: hasItems ? T.paper : T.ink50,
                          borderRadius:12,fontSize:12.5,fontWeight:800,cursor:hasItems?"pointer":"default",fontFamily:"inherit",
                          display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,
                          boxShadow:hasItems ? T.shadowCard : "none",
                        }}>
                        <Icon name="cart-plus" size={13} color={hasItems ? T.paper : T.ink50} stroke={2.4}/>
                        <span>Send to Cart</span>
                      </button>
                    </div>
                  </div>
                );
              }

              // Cards view — default when no list is open.
              return (
                <>
                  {lists.length === 0 ? (
                    <Glass style={{padding:"32px 20px",textAlign:"center",marginBottom:12}}>
                      <div style={{width:48,height:48,margin:"0 auto 14px",borderRadius:14,background:`linear-gradient(135deg, ${T.peach}33, ${T.apricot}1A)`,border:`1px solid ${T.peach}66`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <Icon name="bookmark" size={22} color={T.basil} stroke={2}/>
                      </div>
                      <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.2,marginBottom:6}}>No lists yet</div>
                      <div style={{fontSize:12.5,color:T.ink50,lineHeight:1.5,maxWidth:280,marginLeft:"auto",marginRight:"auto"}}>
                        Lists turn one-off shops into recurring missions — Taco Tuesday, Sunday meal prep, Halloween baskets.
                      </div>
                    </Glass>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:12}}>
                      {lists.map(list => {
                        const count = countForList(list.name);
                        return (
                          <Glass key={list.id}
                            onClick={() => { setActiveListId(list.id); SFX.tabSwitch(); }}
                            style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                            <div style={{width:38,height:38,borderRadius:11,background:`linear-gradient(135deg, ${T.peach}33, ${T.apricot}1A)`,border:`1px solid ${T.peach}66`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                              <span aria-hidden="true">{OCCASION_ICON[list.occasion] || "📋"}</span>
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest,letterSpacing:-.2,lineHeight:1.2,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{list.name}</div>
                              <div style={{fontSize:11,color:T.ink50,fontWeight:600}}>
                                {count} {count === 1 ? "item" : "items"}
                                {list.fillCount > 0 && <> · filled {list.fillCount}×</>}
                              </div>
                            </div>
                            <Icon name="chevron-right" size={16} color={T.ink50} stroke={2.2}/>
                          </Glass>
                        );
                      })}
                    </div>
                  )}

                  <button
                    onClick={() => { setCreateListOpen(true); SFX.tabSwitch(); }}
                    aria-label="Create a new list"
                    style={{
                      width:"100%",
                      padding:"13px 16px",
                      background:T.forest,
                      border:`1px solid ${T.forest}`,
                      color:T.paper,
                      borderRadius:14,
                      fontSize:13, fontWeight:800,
                      fontFamily:"inherit",
                      cursor:"pointer",
                      display:"inline-flex", alignItems:"center", justifyContent:"center", gap:7,
                      boxShadow:T.shadowCard,
                    }}>
                    <Icon name="plus" size={14} color={T.paper} stroke={2.6}/>
                    <span>Create list</span>
                  </button>
                </>
              );
            })()}

            {myItemsView === "all" && (
              <Glass style={{padding:"32px 20px",textAlign:"center"}}>
                <div style={{width:48,height:48,margin:"0 auto 14px",borderRadius:14,background:`linear-gradient(135deg, ${T.sageSoft}, ${T.sage}33)`,border:`1px solid ${T.sage}66`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Icon name="box" size={22} color={T.basil} stroke={2}/>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.2,marginBottom:6}}>Unified catalog</div>
                <div style={{fontSize:12.5,color:T.ink50,lineHeight:1.5,marginBottom:16,maxWidth:280,marginLeft:"auto",marginRight:"auto"}}>
                  Every item across your pantry and lists, in one place. Coming next.
                </div>
                <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:99,fontSize:11,fontWeight:700,color:T.ink50,letterSpacing:.4,textTransform:"uppercase"}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:T.apricot}}/>
                  <span>In progress</span>
                </div>
              </Glass>
            )}
          </div>
          );
        })()}

        {/* ══ PRICE IQ ══ */}
        {!activeStore && tab==="history" && (() => {
          /* ── Build per-item analytics once ────────────────────────────────
             For each item with PRICE_HISTORY we compute: current price, 6-week
             average, all-time low, week-over-week % change, status (atLow /
             falling / rising / steady), and any active deal. We sort by
             "savings opportunity" — items at all-time low first, then biggest
             week-over-week drops. ─────────────────────────────────────── */
          const items = MASTER_LIST.filter(it => PRICE_HISTORY[it.id]).map(it => {
            const h = PRICE_HISTORY[it.id];
            const cur = h[h.length-1];
            const prev = h[h.length-2] || cur;
            const start = h[0];
            const avg = h.reduce((s,v)=>s+v, 0) / h.length;
            const atl = Math.min(...h);
            const wowDelta = cur - prev;
            const wowPct = prev > 0 ? (wowDelta / prev) * 100 : 0;
            const sixWkDelta = cur - start;
            const sixWkPct = start > 0 ? (sixWkDelta / start) * 100 : 0;
            const isAtLow = cur <= atl * 1.02;
            const isFalling = cur < start && !isAtLow;
            const isRising = cur > start * 1.05;
            const status = isAtLow ? "atLow" : isFalling ? "falling" : isRising ? "rising" : "steady";
            const deal = allDeals.find(d => d.id === it.id);
            return {item: it, h, cur, prev, avg, atl, wowPct, sixWkPct, status, deal};
          });
          const summary = {
            atLow: items.filter(x=>x.status==="atLow").length,
            falling: items.filter(x=>x.status==="falling").length,
            rising: items.filter(x=>x.status==="rising").length,
            onSale: items.filter(x=>x.deal).length,
            avgSave: (() => {
              const vs = items.filter(x=>x.cur < x.avg).map(x => ((x.avg - x.cur) / x.avg) * 100);
              return vs.length ? vs.reduce((s,v)=>s+v,0) / vs.length : 0;
            })(),
          };
          // Sort: at-low first, then biggest weekly drops, then alphabetical
          const sortRank = x => x.status === "atLow" ? -1000 : x.wowPct;
          const sortedItems = [...items].sort((a,b) => {
            if (priceIQSort === "opportunity") return sortRank(a) - sortRank(b);
            if (priceIQSort === "biggestDrop") return a.wowPct - b.wowPct;
            if (priceIQSort === "biggestRise") return b.wowPct - a.wowPct;
            if (priceIQSort === "lowestPrice") return a.cur - b.cur;
            if (priceIQSort === "highestPrice") return b.cur - a.cur;
            if (priceIQSort === "alpha") return a.item.name.localeCompare(b.item.name);
            return 0;
          });
          // Filter chip — local state via priceIQFilter
          const activeFilter = priceIQFilter || "all";
          const q = priceIQSearch.trim().toLowerCase();
          const filtered = sortedItems.filter(x => {
            // Filter chip
            if (activeFilter === "atLow"   && x.status !== "atLow")  return false;
            if (activeFilter === "falling" && x.status !== "falling") return false;
            if (activeFilter === "rising"  && x.status !== "rising")  return false;
            if (activeFilter === "onSale"  && !x.deal)                return false;
            // Search
            if (q && !x.item.name.toLowerCase().includes(q) && !x.item.category.toLowerCase().includes(q)) return false;
            return true;
          });
          const filterChips = [
            {id:"all",      label:"All",          count:items.length},
            {id:"atLow",    label:"All-time low", count:summary.atLow},
            {id:"falling",  label:"Falling",      count:summary.falling},
            {id:"rising",   label:"Rising",       count:summary.rising},
            {id:"onSale",   label:"On sale",      count:summary.onSale},
          ];

          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* ── HEADER ───────────────────────────────────────────── */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
              <div style={{width:32,height:32,borderRadius:9,background:`linear-gradient(135deg, ${T.leaf}26, ${T.basil}1A)`,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon name="chart-line" size={19} color={T.basil} stroke={2.2}/>
              </div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.forest,letterSpacing:-.3}}>Price Intelligence</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:14,fontSize:12,color:T.ink50,lineHeight:1.5}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:T.leaf,boxShadow:`0 0 0 3px ${T.leaf}1A`}}/>
              <span>Tracking <span style={{color:T.forest,fontWeight:700}}>{items.length}</span> items · 6-week price history</span>
            </div>

            {/* ── SUMMARY STRIP — connected pill ───────────────────── */}
            <div style={{display:"flex",marginBottom:14,padding:0,borderRadius:14,background:T.parchment,border:`1px solid ${T.hairline}`,overflow:"hidden"}}>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="trophy" size={12} color={T.basil} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>At low</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.basil,letterSpacing:-.3,lineHeight:1.1}}>{summary.atLow}</div>
              </div>
              <div style={{width:1,background:T.hairline}}/>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="trending-down" size={12} color={T.leaf} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Falling</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.leaf,letterSpacing:-.3,lineHeight:1.1}}>{summary.falling}</div>
              </div>
              <div style={{width:1,background:T.hairline}}/>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="trending-up" size={12} color={T.berry} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Rising</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.berry,letterSpacing:-.3,lineHeight:1.1}}>{summary.rising}</div>
              </div>
              <div style={{width:1,background:T.hairline}}/>
              <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="wallet" size={12} color={T.ink50} stroke={2}/>
                  <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Avg save</span>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.3,lineHeight:1.1}}>{summary.avgSave.toFixed(0)}%</div>
              </div>
            </div>

            {/* ── SEARCH + SORT ────────────────────────────────────── */}
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <div style={{flex:1,display:"flex",alignItems:"center",gap:9,padding:"9px 12px",background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:12,boxShadow:T.shadowCard}}>
                <Icon name="search" size={16} color={T.ink50} stroke={2.2}/>
                <input
                  type="text"
                  value={priceIQSearch}
                  onChange={e=>setPriceIQSearch(e.target.value)}
                  placeholder="Search items or categories…"
                  aria-label="Search Price IQ"
                  style={{flex:1,minWidth:0,background:"transparent",border:"none",outline:"none",fontFamily:"inherit",fontSize:13,color:T.forest}}
                />
                {priceIQSearch && (
                  <button onClick={()=>setPriceIQSearch("")}
                    aria-label="Clear search"
                    style={{background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:"50%",width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",padding:0,flexShrink:0}}>
                    <Icon name="x" size={11} color={T.ink50} stroke={2.4}/>
                  </button>
                )}
              </div>
              <div style={{position:"relative",flexShrink:0}}>
                <select
                  value={priceIQSort}
                  onChange={e=>{setPriceIQSort(e.target.value);SFX.tabSwitch();}}
                  aria-label="Sort items"
                  style={{
                    appearance:"none",
                    WebkitAppearance:"none",
                    MozAppearance:"none",
                    height:"100%",
                    padding:"9px 32px 9px 32px",
                    background:T.paper,
                    border:`1px solid ${T.hairline}`,
                    borderRadius:12,
                    boxShadow:T.shadowCard,
                    fontFamily:"inherit",
                    fontSize:12,
                    fontWeight:700,
                    color:T.forest,
                    cursor:"pointer",
                  }}>
                  <option value="opportunity">Best opportunity</option>
                  <option value="biggestDrop">Biggest drop</option>
                  <option value="biggestRise">Biggest rise</option>
                  <option value="lowestPrice">Lowest price</option>
                  <option value="highestPrice">Highest price</option>
                  <option value="alpha">A → Z</option>
                </select>
                <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",display:"flex"}}>
                  <Icon name="filter" size={14} color={T.ink50} stroke={2.2}/>
                </span>
                <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",display:"flex"}}>
                  <Icon name="chevron-down" size={14} color={T.ink50} stroke={2.4}/>
                </span>
              </div>
            </div>

            {/* ── FILTER CHIPS ─────────────────────────────────────── */}
            <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:14,paddingBottom:2,scrollbarWidth:"none"}}>
              {filterChips.map(chip => {
                const isActive = activeFilter === chip.id;
                return (
                  <button key={chip.id}
                    onClick={() => { setPriceIQFilter(chip.id); SFX.tabSwitch(); }}
                    style={{
                      flexShrink:0,
                      display:"inline-flex", alignItems:"center", gap:6,
                      padding:"7px 13px",
                      borderRadius:20,
                      background: isActive ? T.forest : T.paper,
                      border: `1px solid ${isActive ? T.forest : T.hairline}`,
                      color: isActive ? T.paper : T.ink70,
                      fontSize:11.5, fontWeight:700,
                      fontFamily:"inherit", cursor:"pointer",
                      whiteSpace:"nowrap",
                      transition:"all .15s",
                    }}>
                    <span>{chip.label}</span>
                    <span style={{
                      display:"inline-flex", alignItems:"center", justifyContent:"center",
                      minWidth:18, height:18, padding:"0 5px",
                      borderRadius:9,
                      background: isActive ? "rgba(255,255,255,.18)" : T.parchment,
                      color: isActive ? T.paper : T.ink50,
                      fontSize:10, fontWeight:800, letterSpacing:.2,
                    }}>{chip.count}</span>
                  </button>
                );
              })}
            </div>

            {/* ── CARD GRID ────────────────────────────────────────── */}
            {filtered.length === 0 ? (
              <Glass style={{padding:"36px 24px",textAlign:"center",borderRadius:18,border:`1px dashed ${T.sage}`}}>
                <div style={{width:64,height:64,borderRadius:"50%",background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
                  <Icon name="chart-line" size={32} color={T.basil} stroke={2.2}/>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,marginBottom:6}}>No items in this view</div>
                <div style={{fontSize:13,color:T.ink50,lineHeight:1.6,maxWidth:320,margin:"0 auto"}}>
                  Try a different filter, or wait — fresh price data flows in weekly.
                </div>
              </Glass>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
                {filtered.map((row,i) => {
                  const {item, h, cur, avg, atl, wowPct, sixWkPct, status, deal} = row;
                  const trendColor = status === "rising" ? T.berry : status === "falling" || status === "atLow" ? T.basil : T.ink50;
                  const trendIcon = status === "rising" ? "trending-up" : status === "falling" ? "trending-down" : status === "atLow" ? "trophy" : "minus";
                  const trendLabel = status === "atLow"
                    ? "All-time low"
                    : status === "falling"
                      ? `${Math.abs(sixWkPct).toFixed(0)}% lower than 6 wks ago`
                      : status === "rising"
                        ? `${sixWkPct.toFixed(0)}% higher than 6 wks ago`
                        : "Steady price";
                  return (
                    <Glass key={i} className="anim-slide-up"
                      onClick={()=>{ setPriceIQDetail(item.id); SFX.tabSwitch(); }}
                      style={{"--i":i, padding:0, borderRadius:18, overflow:"hidden", display:"flex", flexDirection:"column", cursor:"pointer", transition:"transform .15s ease, box-shadow .15s ease"}}
                      onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.transform=""; }}>

                      {/* Item header */}
                      <div style={{padding:"13px 14px 10px",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:9,flex:1,minWidth:0}}>
                          <div style={{
                            width:36, height:36, borderRadius:10,
                            background: T.parchment, border:`1px solid ${T.hairline}`,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            flexShrink:0,
                          }}>
                            <ItemIcon emoji={getItemEmoji(item.id, item.name, item.category)} size={22}/>
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.forest,lineHeight:1.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</div>
                            <div style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:3,fontSize:9.5,color:T.ink50,fontWeight:700,letterSpacing:.4,textTransform:"uppercase"}}>
                              {item.category}
                            </div>
                          </div>
                        </div>
                        {/* Trend arrow + WoW % */}
                        <div style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:20,background:`${trendColor}14`,border:`1px solid ${trendColor}33`,flexShrink:0}}>
                          <Icon name={status === "rising" ? "trending-up" : "trending-down"} size={12} color={trendColor} stroke={2.4}/>
                          <span style={{fontSize:10.5,fontWeight:800,color:trendColor,letterSpacing:.2}}>
                            {wowPct >= 0 ? "+" : ""}{wowPct.toFixed(1)}%
                          </span>
                        </div>
                      </div>

                      {/* Chart */}
                      <div style={{padding:"4px 10px 8px",position:"relative"}}>
                        <PriceTrendChart data={h} atl={atl} avg={avg}/>
                        {/* axis labels */}
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:8.5,color:T.ink50,fontWeight:600,letterSpacing:.4,padding:"0 4px",marginTop:2}}>
                          <span>6 WK AGO</span>
                          <span>NOW</span>
                        </div>
                      </div>

                      {/* Stat ladder */}
                      <div style={{display:"flex",margin:"4px 14px 12px",borderRadius:11,background:T.parchment,border:`1px solid ${T.hairline}`,overflow:"hidden"}}>
                        <div style={{flex:1,padding:"8px 10px",textAlign:"left"}}>
                          <div style={{fontSize:8.5,letterSpacing:.7,color:T.ink50,fontWeight:700,textTransform:"uppercase",marginBottom:1}}>Now</div>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:800,color:status==="atLow"?T.basil:T.forest,letterSpacing:-.3,lineHeight:1.1}}>${cur.toFixed(2)}</div>
                        </div>
                        <div style={{width:1,background:T.hairline}}/>
                        <div style={{flex:1,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:8.5,letterSpacing:.7,color:T.ink50,fontWeight:700,textTransform:"uppercase",marginBottom:1}}>6wk avg</div>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:800,color:T.ink70,letterSpacing:-.3,lineHeight:1.1}}>${avg.toFixed(2)}</div>
                        </div>
                        <div style={{width:1,background:T.hairline}}/>
                        <div style={{flex:1,padding:"8px 10px",textAlign:"right"}}>
                          <div style={{fontSize:8.5,letterSpacing:.7,color:T.ink50,fontWeight:700,textTransform:"uppercase",marginBottom:1}}>All-time low</div>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:800,color:T.basil,letterSpacing:-.3,lineHeight:1.1}}>${atl.toFixed(2)}</div>
                        </div>
                      </div>

                      {/* Status row — single line: trend label + optional sale chip */}
                      <div style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",borderTop:`1px solid ${T.hairline}`,background:status==="atLow"?T.sageSoft:T.paper,flexWrap:"wrap"}}>
                        <Icon name={trendIcon} size={13} color={trendColor} stroke={2.2}/>
                        <span style={{fontSize:11,fontWeight:700,color:trendColor,letterSpacing:.1,flex:1,minWidth:0}}>{trendLabel}</span>
                        {deal && (
                          <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:20,background:T.butterSoft,border:`1px solid ${T.butter}`,fontSize:10,fontWeight:700,color:T.warn,letterSpacing:.1}}>
                            <Icon name="tag" size={11} color={T.warn} stroke={2.4}/>
                            On sale at {deal.store.name}
                          </span>
                        )}
                      </div>
                    </Glass>
                  );
                })}
              </div>
            )}
          </div>
          );
        })()}

        {/* ══ SUBSTITUTIONS ══ */}
        {!activeStore && tab==="subs" && (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* Page header — display type, no decorative emoji */}
            <div style={{marginBottom:18}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.5,lineHeight:1.05}}>
                Smart Substitutions
              </div>
              <div style={{fontSize:11.5,color:T.ink50,marginTop:4,letterSpacing:-.1,lineHeight:1.55,maxWidth:480}}>
                Set preferences for each item. If something is out of stock, CartIQ applies your rules automatically.
              </div>
            </div>
            {activeStores.map(store=>(
              <div key={store.id} style={{marginBottom:22}}>
                {/* Per-store header with hairline rule */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <div style={{width:24,height:24,borderRadius:7,background:store.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <ItemIcon emoji={store.logo} size={14}/>
                  </div>
                  <span style={{fontSize:13,fontWeight:700,color:store.color,letterSpacing:-.1}}>{store.name}</span>
                  <span style={{flex:1,height:1,background:T.hairline,marginLeft:4}}></span>
                  <span style={{fontSize:10,color:T.ink50,fontVariantNumeric:"tabular-nums"}}>{store.deals.length}</span>
                </div>
                <div style={{display:"grid",gap:8}}>
                  {store.deals.map((deal,i)=>{
                    const pref=subPrefs[deal.id],prefData=pref?SUB_OPTIONS[pref.pref]:null;
                    const pv = pref?.votes || {};
                    const pUp = Object.values(pv).filter(v=>v==="up").length;
                    const pDown = Object.values(pv).filter(v=>v==="down").length;
                    const disputed = pUp > 0 && pDown > 0;
                    return (
                      <Glass key={i} onClick={()=>setSubModalDeal({...deal,store})}
                        className="anim-slide-up" style={{"--i":i,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",borderRadius:14,border:disputed?`1px solid ${T.butter}`:undefined}}>
                        <div style={{flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",width:34,height:34,background:T.parchment,borderRadius:9}}>
                          <ItemIcon emoji={getItemEmoji(deal.id, deal.name, deal.category)} size={20}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:T.forest,display:"flex",alignItems:"center",gap:6,letterSpacing:-.1}}>
                            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{deal.name}</span>
                            {disputed && (
                              <span title="Household disagrees on this swap" style={{display:"inline-flex",alignItems:"center",flexShrink:0}} aria-hidden="true">
                                <Icon name="info" size={12} color={T.warn} stroke={2.4}/>
                              </span>
                            )}
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                            <Pill label={deal.dealLabel} color={store.color}/>
                            {(pUp > 0 || pDown > 0) && (
                              <span style={{display:"flex",alignItems:"center",gap:6,fontSize:10,fontVariantNumeric:"tabular-nums"}}>
                                {pUp > 0 && (
                                  <span style={{display:"inline-flex",alignItems:"center",gap:2.5,color:T.basil,fontWeight:700}}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 11V21H3V11h4Zm4 0L13 4l3 1.5L14.5 11H20a2 2 0 0 1 2 2.3l-1 7A2 2 0 0 1 19 22h-8V11Z"/></svg>
                                    {pUp}
                                  </span>
                                )}
                                {pDown > 0 && (
                                  <span style={{display:"inline-flex",alignItems:"center",gap:2.5,color:T.berry,fontWeight:700}}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 13V3h4v10h-4Zm-4 0L11 20l-3-1.5L9.5 13H4a2 2 0 0 1-2-2.3l1-7A2 2 0 0 1 5 2h8v11Z"/></svg>
                                    {pDown}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Phase 4c: cost-cap pill — only when the user has set a ceiling. */}
                        {SUB_COST_CAP_ENABLED && pref?.maxIncrease != null && (
                          <span style={{flexShrink:0}}>
                            <Pill label={pref.maxIncrease === 0 ? "price-match" : `+${Math.round(pref.maxIncrease * 100)}% cap`} color={T.warn}/>
                          </span>
                        )}
                        {pref ? (
                          <div style={{background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:9,padding:"5px 10px",display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                            <ItemIcon emoji={prefData.icon} size={12}/>
                            <span style={{fontSize:10.5,fontWeight:700,color:T.basil,letterSpacing:-.05}}>{prefData.label}</span>
                          </div>
                        ) : (
                          <div style={{border:`1px dashed ${T.ink15}`,borderRadius:9,padding:"4px 10px",fontSize:12,fontWeight:600,color:T.ink50,letterSpacing:-.1,flexShrink:0,lineHeight:1.3}}>Set rule</div>
                        )}
                        <span style={{color:T.ink50,display:"flex",alignItems:"center",flexShrink:0}} aria-hidden="true">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                        </span>
                      </Glass>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ STACK & SAVE ══
            Find deals where store discount AND Ibotta cashback both apply,
            so savings get stacked. Hero equation, then grouped by store. */}
        {!activeStore && tab==="coupons" && (() => {
          const stackable = allDeals.filter(d => d.ibotta > 0);
          const totalDeal = stackable.reduce((s,d) => s + d.savings, 0);
          const totalIb   = stackable.reduce((s,d) => s + d.ibotta,  0);
          const grand     = totalDeal + totalIb;
          // Group stackable deals by store for visual rhythm
          const byStore = {};
          stackable.forEach(d => {
            const k = d.store.id;
            if (!byStore[k]) byStore[k] = { store: d.store, deals: [] };
            byStore[k].deals.push(d);
          });
          const storeGroups = Object.values(byStore);
          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* Page header — display type, no decorative emoji */}
            <div style={{marginBottom:14,padding:"0 2px"}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.4,lineHeight:1.1}}>Stack & Save</div>
              <div style={{fontSize:12.5,color:T.ink50,marginTop:5,lineHeight:1.45,maxWidth:340}}>
                Store discounts <span style={{color:T.basil,fontWeight:700}}>+</span> Ibotta cashback combined on the same item — applied automatically at checkout.
              </div>
            </div>

            {/* Hero equation card — sage→cream gradient, big display total */}
            <div style={{
              position:"relative",overflow:"hidden",
              padding:"20px 22px",marginBottom:14,borderRadius:20,
              background:`linear-gradient(110deg, ${T.sageSoft} 0%, #ECEAC9 55%, #F4EDC9 100%)`,
              border:`1px solid ${T.sage}`,
              boxShadow:T.shadowSoft,
            }}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{width:22,height:22,borderRadius:6,background:"rgba(30,53,40,.08)",display:"flex",alignItems:"center",justifyContent:"center",border:`1px solid ${T.sage}`}}>
                  <Icon name="bolt" size={16} color={T.basil} stroke={2.4}/>
                </div>
                <div style={{fontSize:10.5,letterSpacing:2,color:T.forest,fontWeight:800}}>STACKED THIS WEEK</div>
                <span style={{marginLeft:"auto",fontSize:10,fontWeight:800,letterSpacing:.6,color:T.basil,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:5,padding:"2px 7px"}}>
                  {stackable.length} {stackable.length===1?"DEAL":"DEALS"}
                </span>
              </div>
              {/* Equation row */}
              <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap",marginBottom:6}}>
                <div style={{display:"flex",alignItems:"baseline",gap:3}}>
                  <span style={{fontFamily:FONT_DISPLAY,fontSize:30,fontWeight:700,color:T.basil,letterSpacing:-.5}}>${totalDeal.toFixed(0)}</span>
                  <span style={{fontSize:11,color:T.basil,fontWeight:700,letterSpacing:1}}>DEAL</span>
                </div>
                <span style={{fontFamily:FONT_DISPLAY,fontSize:24,fontWeight:400,color:T.ink50,lineHeight:1}}>+</span>
                <div style={{display:"flex",alignItems:"baseline",gap:3}}>
                  <span style={{fontFamily:FONT_DISPLAY,fontSize:30,fontWeight:700,color:T.apricot,letterSpacing:-.5}}>${totalIb.toFixed(0)}</span>
                  <span style={{fontSize:11,color:T.apricot,fontWeight:700,letterSpacing:1}}>IBOTTA</span>
                </div>
                <span style={{fontFamily:FONT_DISPLAY,fontSize:24,fontWeight:400,color:T.ink50,lineHeight:1}}>=</span>
                <div style={{display:"flex",alignItems:"baseline",gap:4}}>
                  <span style={{fontFamily:FONT_DISPLAY,fontSize:38,fontWeight:700,color:T.forest,letterSpacing:-.8,lineHeight:1}}>${grand.toFixed(2)}</span>
                </div>
              </div>
              <div style={{fontSize:11.5,color:T.basil,fontWeight:600,marginTop:2}}>
                That's <span style={{fontFamily:FONT_DISPLAY,fontWeight:700,fontSize:13}}>${(totalIb).toFixed(2)}</span> in cashback you'd miss without stacking.
              </div>
            </div>

            {/* How stacking works — quiet legend strip */}
            <Glass style={{padding:"10px 13px",marginBottom:14,borderRadius:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{width:10,height:10,borderRadius:3,background:T.basil,display:"inline-block"}}/>
                <span style={{fontSize:11,color:T.forest,fontWeight:700}}>Store discount</span>
              </div>
              <span style={{color:T.ink50,fontWeight:600}}>+</span>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{width:10,height:10,borderRadius:3,background:T.apricot,display:"inline-block"}}/>
                <span style={{fontSize:11,color:T.forest,fontWeight:700}}>Ibotta cashback</span>
              </div>
              <span style={{marginLeft:"auto",fontSize:10.5,color:T.ink50,fontWeight:600}}>
                Applied at checkout · No clipping needed
              </span>
            </Glass>

            {/* Per-store groups */}
            {storeGroups.map(({ store, deals }, gi) => {
              const groupDeal = deals.reduce((s,d) => s + d.savings, 0);
              const groupIb   = deals.reduce((s,d) => s + d.ibotta,  0);
              return (
                <div key={gi} style={{marginBottom:16}}>
                  {/* Store header */}
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"0 4px"}}>
                    <div style={{width:30,height:30,borderRadius:9,background:store.bg,display:"flex",alignItems:"center",justifyContent:"center",border:`1px solid ${T.hairline}`}}>
                      <ItemIcon emoji={store.logo} size={18}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:700,color:store.color,letterSpacing:-.1,lineHeight:1.1}}>{store.name}</div>
                      <div style={{fontSize:10.5,color:T.ink50,fontWeight:600,marginTop:2}}>{deals.length} stackable {deals.length===1?"deal":"deals"}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest,letterSpacing:-.2}}>${(groupDeal+groupIb).toFixed(2)}</div>
                      <div style={{fontSize:9.5,color:T.ink50,fontWeight:700,letterSpacing:.8}}>STACKED</div>
                    </div>
                  </div>
                  {/* Deals */}
                  <div style={{display:"grid",gap:7}}>
                    {deals.map((deal,i) => (
                      <Glass key={i} style={{padding:"11px 13px",borderRadius:14}}>
                        <div style={{display:"flex",alignItems:"center",gap:11}}>
                          <ItemIcon emoji={getItemEmoji(deal.id, deal.name, deal.category)} size={22}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12.5,fontWeight:700,color:T.forest,letterSpacing:-.1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{deal.name}</div>
                            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                              <Pill label={deal.dealLabel} color={deal.store.color}/>
                              <span style={{fontSize:10,color:T.ink50,fontWeight:600}}>·</span>
                              <span style={{fontSize:10.5,color:T.ink50,fontWeight:600}}>${deal.origPrice.toFixed(2)} list</span>
                            </div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontFamily:FONT_DISPLAY,fontSize:15,fontWeight:700,color:T.forest,letterSpacing:-.3,lineHeight:1}}>−${(deal.savings+deal.ibotta).toFixed(2)}</div>
                            <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end",marginTop:3}}>
                              <span style={{fontSize:10,fontWeight:700,color:T.basil}}>${deal.savings.toFixed(2)}</span>
                              <span style={{fontSize:10,color:T.ink50,fontWeight:700}}>+</span>
                              <span style={{fontSize:10,fontWeight:700,color:T.apricot}}>${deal.ibotta.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </Glass>
                    ))}
                  </div>
                </div>
              );
            })}

            {stackable.length === 0 && (
              <Glass style={{padding:"24px 18px",borderRadius:16,textAlign:"center"}}>
                <div style={{fontSize:13,fontWeight:700,color:T.forest,marginBottom:4}}>No stackable deals this week</div>
                <div style={{fontSize:11.5,color:T.ink50}}>Check back next week — Ibotta refreshes Mondays.</div>
              </Glass>
            )}
          </div>
          );
        })()}

        {/* ══ RECEIPTS ══ */}
        {!activeStore && tab==="receipt" && (
          <div style={{animation:"pageIn .4s ease both"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.forest}}>🧾 Receipt Scanner</div>
              {/* Inline experimental marker — same amber styling as the More-menu pill
                  for visual consistency. Lets users know upfront before they invest
                  effort uploading a receipt. */}
              <span style={{fontSize:9,fontWeight:700,letterSpacing:.8,color:T.warn,background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:5,padding:"2px 7px",lineHeight:1.3}}>
                BETA
              </span>
            </div>
            <div style={{fontSize:12,color:T.ink50,marginBottom:12}}>Upload receipts to verify deals and flag missed savings.</div>
            {/* Experimental disclosure banner. Sits above the upload CTA so users
                can decide before they upload whether they want to participate. */}
            <Glass style={{padding:"11px 14px",marginBottom:14,borderRadius:12,border:`1px solid ${T.butter}`,background:"linear-gradient(135deg,rgba(251,191,36,.08),rgba(180,83,9,.04))"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <span style={{fontSize:18,flexShrink:0,marginTop:1}}>🧪</span>
                <div style={{fontSize:11.5,color:T.forest,lineHeight:1.5}}>
                  <span style={{fontWeight:800,color:T.warn}}>Experimental feature.</span> Receipt OCR is still being tuned. Some receipts may not parse correctly or may flag deals incorrectly. If you hit a snag, please share the receipt image and what went wrong via <span style={{color:T.warn,fontWeight:700}}>📣 Send Feedback</span>.
                </div>
              </div>
            </Glass>
            {!receiptDone ? (
              <EmptyState icon="🧾" title="Scan your receipt" sub="Upload a photo, PDF, or forward an email receipt. We'll verify every deal was applied." action="📷 Upload Receipt" onAction={()=>{SFX.scan();setTimeout(()=>{setRDone(true);SFX.success();notify("✓ Receipt verified!");},1500);}}/>
            ) : (
              <div style={{animation:"fadeIn .4s ease both"}}>
                <Glass style={{padding:"13px 17px",marginBottom:12,borderRadius:14,border:"1px solid rgba(22,163,106,.2)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:11}}>
                    <span style={{fontSize:24}}>✅</span>
                    <div><div style={{fontSize:13,fontWeight:700,color:T.basil}}>Receipt verified · 3 of 4 deals applied</div><div style={{fontSize:11,color:T.ink50}}>Savings logged</div></div>
                  </div>
                </Glass>
                {[{item:"Atlantic Salmon",cat:"Seafood",store:"Publix",exp:9.99,ok:true},{item:"Greek Yogurt",cat:"Dairy",store:"ALDI",exp:2.49,ok:true},{item:"Almond Butter",cat:"Pantry",store:"Sprouts",exp:4.99,ok:true},{item:"Cherry Tomatoes",cat:"Produce",store:"Sprouts",exp:2.98,ok:false,note:"Deal not applied — contact store"}].map((r,i)=>(
                  <Glass key={i} style={{padding:"11px 15px",marginBottom:8,display:"flex",alignItems:"center",gap:11,borderRadius:14,borderColor:r.ok?T.parchment:"rgba(220,38,38,.15)"}}>
                    <ItemIcon emoji={getItemEmoji(null, r.item, r.cat)} size={18}/>
                    <span style={{fontSize:16}}>{r.ok?"✅":"⚠️"}</span>
                    <div style={{flex:1}}><div style={{fontSize:12,fontWeight:700,color:T.forest}}>{r.item}</div>{!r.ok&&<div style={{fontSize:10,color:T.berry}}>{r.note}</div>}</div>
                    <div style={{fontSize:12,fontWeight:700,color:r.ok?T.leaf:T.berry}}>{r.ok?`−$${r.exp.toFixed(2)} ✓`:"Missed"}</div>
                  </Glass>
                ))}
                <RippleBtn sound="notify" onClick={()=>setRDone(false)} style={{background:T.parchment,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:10,padding:"8px 16px",fontSize:11,fontWeight:600,marginTop:6}}>Scan another</RippleBtn>
              </div>
            )}
          </div>
        )}

        {/* ══ HOUSEHOLD ══
            Multi-user shared list / chat. Members, dispute resolution, and
            a live shopping-list chat thread. */}
        {!activeStore && tab==="household" && (() => {
          const onlineCount = HOUSEHOLD.filter(m => m.lastActive === "now").length;
          // Disputed substitutions count, computed once
          const disputed = Object.entries(subPrefs)
            .map(([dealId, pref]) => {
              const pv = pref?.votes || {};
              const up = Object.values(pv).filter(v=>v==="up").length;
              const down = Object.values(pv).filter(v=>v==="down").length;
              if (up === 0 || down === 0) return null;
              for (const s of STORES) {
                const d = s.deals.find(dd => String(dd.id) === String(dealId));
                if (d) return { deal: {...d, store: s}, up, down };
              }
              return null;
            })
            .filter(Boolean);

          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* Page header */}
            <div style={{marginBottom:14,padding:"0 2px"}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.4,lineHeight:1.1}}>Household</div>
              <div style={{fontSize:12.5,color:T.ink50,marginTop:5,lineHeight:1.45,maxWidth:340}}>
                Shared shopping list, chat thread, and substitution decisions for everyone in your home.
              </div>
            </div>

            {/* Members card */}
            <Glass style={{padding:"14px 16px",marginBottom:12,borderRadius:18}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:11}}>
                <div style={{fontSize:10.5,letterSpacing:1.5,color:T.ink50,fontWeight:800}}>MEMBERS · {HOUSEHOLD.length}</div>
                <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,color:T.basil,fontWeight:700}}>
                  <span style={{width:7,height:7,borderRadius:4,background:T.basil,boxShadow:`0 0 0 3px ${T.sage}`,display:"inline-block"}}/>
                  <span>{onlineCount} online</span>
                </div>
              </div>
              <div style={{display:"grid",gap:8}}>
                {HOUSEHOLD.map(m => {
                  const isOnline = m.lastActive === "now";
                  return (
                    <div key={m.id} style={{display:"flex",alignItems:"center",gap:11,padding:"9px 11px",background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12}}>
                      <div style={{position:"relative",flexShrink:0}}>
                        <div style={{width:36,height:36,borderRadius:18,background:T.paper,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                          <ItemIcon emoji={m.avatar} size={22}/>
                        </div>
                        {isOnline && (
                          <div style={{position:"absolute",bottom:-1,right:-1,width:11,height:11,borderRadius:6,background:T.basil,border:`2px solid ${T.parchment}`}}/>
                        )}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:13,fontWeight:700,color:T.forest,letterSpacing:-.1}}>{m.name}</span>
                          {m.role === "admin" && (
                            <span style={{fontSize:8.5,fontWeight:800,letterSpacing:.7,color:T.basil,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:4,padding:"1px 5px",lineHeight:1.3}}>ADMIN</span>
                          )}
                        </div>
                        <div style={{fontSize:10.5,color:T.ink50,marginTop:2,fontWeight:600}}>
                          {isOnline ? "Active now" : `Last seen ${m.lastActive}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Invite row */}
              <button onClick={() => notify("Invite link copied")}
                style={{width:"100%",marginTop:9,padding:"9px 12px",background:"transparent",border:`1px dashed ${T.sage}`,borderRadius:11,color:T.basil,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <Icon name="plus" size={14} color={T.basil} stroke={2.4}/>
                <span>Invite a household member</span>
              </button>
            </Glass>

            {/* Aggregate signal strip */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
              {[
                { v: messages.length, l: "MESSAGES", c: T.forest },
                { v: disputed.length, l: "CONFLICTS", c: disputed.length > 0 ? T.warn : T.basil },
                { v: HOUSEHOLD.length, l: "ON LIST", c: T.basil },
              ].map((s,i) => (
                <Glass key={i} style={{padding:"10px 12px",borderRadius:13,textAlign:"center"}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:s.c,letterSpacing:-.3,lineHeight:1}}>{s.v}</div>
                  <div style={{fontSize:9,letterSpacing:1.2,color:T.ink50,marginTop:5,fontWeight:700}}>{s.l}</div>
                </Glass>
              ))}
            </div>

            {/* ── Disputed substitutions panel ── */}
            {disputed.length > 0 && (
              <Glass style={{padding:"14px 16px",marginBottom:12,borderRadius:18,border:`1px solid ${T.butter}`,background:`linear-gradient(135deg,${T.butterSoft},rgba(251,191,36,.04))`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <Icon name="info" size={18} color={T.warn} stroke={2.4}/>
                  <div style={{fontSize:11,letterSpacing:1.5,color:T.warn,fontWeight:800}}>SPLIT VOTES · {disputed.length}</div>
                </div>
                <div style={{fontSize:11.5,color:T.forest,marginBottom:11,lineHeight:1.5}}>
                  Members disagree on these substitution choices. Tap to resolve.
                </div>
                <div style={{display:"grid",gap:7}}>
                  {disputed.map(({deal,up,down},i) => (
                    <button key={i} onClick={()=>{setTab("subs");setSubModalDeal(deal);}}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                      <ItemIcon emoji={getItemEmoji(deal.id, deal.name, deal.category)} size={20}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12.5,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{deal.name}</div>
                        <div style={{fontSize:10.5,color:T.ink50,fontWeight:600,marginTop:2}}>{deal.store.name}</div>
                      </div>
                      <div style={{display:"flex",gap:4,alignItems:"center"}}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:11,fontWeight:800,color:T.basil,background:T.sageSoft,border:`1px solid ${T.sage}`,borderRadius:7,padding:"3px 7px"}}>
                          <span>👍</span><span>{up}</span>
                        </span>
                        <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:11,fontWeight:800,color:T.berry,background:T.berrySoft,border:`1px solid ${T.berry}30`,borderRadius:7,padding:"3px 7px"}}>
                          <span>👎</span><span>{down}</span>
                        </span>
                      </div>
                      <Icon name="chevron-right" size={16} color={T.ink50} stroke={2.4}/>
                    </button>
                  ))}
                </div>
              </Glass>
            )}

            {/* Shopping chat */}
            <Glass style={{overflow:"hidden",borderRadius:18,marginBottom:12}}>
              <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.hairline}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <Icon name="list" size={16} color={T.basil} stroke={2.4}/>
                  <span style={{fontFamily:FONT_DISPLAY,fontWeight:700,fontSize:14,color:T.forest,letterSpacing:-.1}}>Shopping list chat</span>
                </div>
                <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,color:T.basil,fontWeight:700,letterSpacing:.5}}>
                  <span style={{width:6,height:6,borderRadius:4,background:T.basil,display:"inline-block",animation:"pulse 1.6s ease-in-out infinite"}}/>
                  <span>LIVE</span>
                </span>
              </div>
              <div style={{padding:"13px 15px",display:"flex",flexDirection:"column",gap:10,minHeight:120,maxHeight:260,overflowY:"auto"}}>
                {messages.map((msg,i) => {
                  const who = HOUSEHOLD.find(h => h.id === msg.who);
                  const isMe = msg.who === 1; // current user is Sarah
                  return (
                    <div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",flexDirection:isMe?"row-reverse":"row"}}>
                      <div style={{width:28,height:28,borderRadius:14,background:T.parchment,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <ItemIcon emoji={who?.avatar} size={16}/>
                      </div>
                      <div style={{
                        background:isMe?T.sageSoft:T.parchment,
                        border:`1px solid ${isMe?T.sage:T.hairline}`,
                        borderRadius:isMe?"12px 4px 12px 12px":"4px 12px 12px 12px",
                        padding:"7px 11px",
                        maxWidth:"75%",
                      }}>
                        <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:1}}>
                          <span style={{fontSize:10.5,color:T.forest,fontWeight:700,letterSpacing:-.05}}>{who?.name}</span>
                          <span style={{fontSize:9.5,color:T.ink50,fontWeight:600}}>{msg.time}</span>
                        </div>
                        <div style={{fontSize:12.5,color:T.forest,lineHeight:1.4}}>{msg.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{padding:"10px 13px",borderTop:`1px solid ${T.hairline}`,display:"flex",gap:7,alignItems:"center",background:T.parchment}}>
                <input style={{flex:1,background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:9,color:T.forest,padding:"9px 12px",fontSize:12.5,fontFamily:"inherit",outline:"none"}}
                  placeholder="Add to list…"
                  value={hhMsg}
                  onChange={e=>setHhMsg(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter"&&hhMsg.trim()){ setMessages(p=>[...p,{who:1,text:hhMsg,time:"Now"}]); setHhMsg(""); SFX.notify(); } }}/>
                <RippleBtn sound="notify"
                  onClick={()=>{ if(hhMsg.trim()){ setMessages(p=>[...p,{who:1,text:hhMsg,time:"Now"}]); setHhMsg(""); } }}
                  style={{background:T.forest,border:`1px solid ${T.forest}`,color:T.paper,borderRadius:9,padding:"9px 14px",fontSize:12,fontWeight:700,fontFamily:"inherit",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="arrow-right" size={14} color={T.paper} stroke={2.6}/>
                  <span>Send</span>
                </RippleBtn>
              </div>
            </Glass>
          </div>
          );
        })()}

        {/* ══ LOYALTY ══ */}
        {/* ══ LOYALTY ══
            Warm celebratory page — butter/apricot hero, per-program cards with
            nearest-reward progress, tier progress, linked offers, and birthday perks.
            ─────────────────────────────────────────────────────────── */}
        {!activeStore && tab==="loyalty" && (() => {
          // Program definitions — colocated, since loyalty is independent of STORES detail.
          const PROGRAMS = [
            {key:"publix",     name:"Publix",         program:"Club Card",      logo:"🌿", color:T.basil,  bg:`linear-gradient(135deg, ${T.basil}, ${T.forest})`,
              ptPerDollar:1, redeem:500, redeemVal:5,
              perks:["BOGO weekly digital deals","Birthday treat coupon"],
              birthday:"April 12 — $5 free",
              tiers:null,
              offers:[{id:"pub1",label:"$2 off Publix Greenwise milk",exp:"3d"},{id:"pub2",label:"BOGO Boar's Head deli meat",exp:"5d"}]},
            {key:"sprouts",    name:"Sprouts",        program:"Rewards",        logo:"🌱", color:T.leaf,   bg:`linear-gradient(135deg, ${T.leaf}, ${T.basil})`,
              ptPerDollar:1, redeem:500, redeemVal:5,
              perks:["Double Ad Wednesdays","5x points on produce"],
              birthday:null,
              tiers:null,
              offers:[{id:"spr1",label:"$1 off organic eggs",exp:"4d"},{id:"spr2",label:"15% off bulk bins",exp:"6d"}]},
            {key:"kroger",     name:"Kroger",         program:"Plus",           logo:"🛒", color:T.berry,  bg:`linear-gradient(135deg, ${T.berry}, ${T.apricot})`,
              ptPerDollar:1, redeem:100, redeemVal:1, // 100 fuel pts = $0.10/gal, simplified to $1
              perks:["Fuel points · 1¢/gal per 100 pts","Personalized coupons"],
              birthday:null,
              tiers:[{name:"Silver",min:0},{name:"Gold",min:1500},{name:"Diamond",min:5000}],
              offers:[{id:"kro1",label:"4x fuel points on gift cards",exp:"2d"},{id:"kro2",label:"$3 off $15 grocery",exp:"7d"}]},
            {key:"wholefoods", name:"Whole Foods",    program:"Prime",          logo:"🌾", color:T.forest, bg:`linear-gradient(135deg, ${T.forest}, ${T.basil})`,
              ptPerDollar:0, redeem:0, redeemVal:0,
              perks:["10% off sale items (Prime)","Weekly Prime member deals","Free 2-hr delivery $35+"],
              birthday:null,
              tiers:null,
              offers:[{id:"wf1",label:"$10 off $50 first delivery",exp:"14d"}]},
            {key:"walmart",    name:"Walmart+",       program:"Membership",     logo:"⭐", color:T.apricot, bg:`linear-gradient(135deg, ${T.apricot}, ${T.peach})`,
              ptPerDollar:0, redeem:0, redeemVal:0,
              perks:["Free delivery $35+","10¢/gal fuel discount","Member prices"],
              birthday:null,
              tiers:null,
              offers:[]},
            {key:"target",     name:"Target",         program:"Circle",         logo:"🎯", color:T.berry,  bg:`linear-gradient(135deg, ${T.berry}, ${T.apricot})`,
              ptPerDollar:1, redeem:0, redeemVal:0, // Circle is offers-based, not point-redeem
              perks:["1% back to spend later","Personalized deals","Birthday 5% off"],
              birthday:"April 12 — 5% off",
              tiers:null,
              offers:[{id:"tar1",label:"20% off home essentials",exp:"3d"},{id:"tar2",label:"$5 RedCard reward",exp:"8d"}]},
          ];

          // Compute total $ value across all programs (only point-based ones)
          const totalDollarValue = PROGRAMS.reduce((s,p) => {
            if (!p.redeem) return s;
            const pts = loyalty[p.key] || 0;
            return s + (pts/p.redeem) * p.redeemVal;
          }, 0);
          const totalPoints = PROGRAMS.reduce((s,p) => s + (loyalty[p.key]||0), 0);
          const activePrograms = PROGRAMS.filter(p => (loyalty[p.key]||0) > 0).length;
          const upcomingBirthdays = PROGRAMS.filter(p => p.birthday).length;
          const totalClipped = Object.values(clippedOffers).filter(Boolean).length;

          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* ── HEADER ───────────────────────────────────────────── */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <div style={{width:32,height:32,borderRadius:9,background:`linear-gradient(135deg, ${T.apricot}33, ${T.butter}1A)`,border:`1px solid ${T.apricot}66`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="trophy" size={18} color={T.apricot} stroke={2.2}/>
              </div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.4,lineHeight:1}}>Loyalty</div>
            </div>
            <div style={{fontSize:12,color:T.ink50,marginBottom:14,marginLeft:42,fontWeight:500}}>
              {activePrograms} active program{activePrograms===1?"":"s"} · {totalPoints.toLocaleString()} total points
            </div>

            {/* ── HERO — warm butter→apricot, total $ value as headline ── */}
            <div style={{
              position:"relative",overflow:"hidden",
              padding:"22px 22px",marginBottom:14,borderRadius:20,
              background:`
                radial-gradient(120% 80% at 100% 0%, ${T.peach}AA 0%, transparent 55%),
                radial-gradient(80% 60% at 0% 100%, ${T.butterSoft}EE 0%, transparent 50%),
                linear-gradient(135deg, ${T.butter} 0%, ${T.parchment} 100%)
              `,
              border:`1px solid ${T.apricot}44`,
              boxShadow:T.shadowFloat,
            }}>
              {/* Confetti-like decorative dots */}
              <div style={{position:"absolute",top:14,right:18,opacity:.25,pointerEvents:"none"}}>
                <Icon name="trophy" size={56} color={T.apricot} stroke={1.6}/>
              </div>

              {/* Eyebrow */}
              <div style={{display:"inline-flex",alignItems:"center",gap:6,marginBottom:10,position:"relative"}}>
                <Icon name="trophy" size={14} color={T.apricot} stroke={2.4}/>
                <span style={{fontSize:10,letterSpacing:2,color:T.apricot,fontWeight:800,textTransform:"uppercase"}}>Total reward value</span>
              </div>

              {/* Headline */}
              <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:14,position:"relative"}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:48,fontWeight:700,color:T.forest,letterSpacing:-1.4,lineHeight:.95}}>${totalDollarValue.toFixed(2)}</div>
                <div style={{fontSize:11,color:T.apricot,fontWeight:700,letterSpacing:.4}}>ready to redeem</div>
              </div>

              {/* Three-cell strip — points · clipped · birthdays */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,position:"relative"}}>
                <div style={{padding:"10px 12px",background:"rgba(255,255,255,.6)",border:`1px solid ${T.apricot}33`,borderRadius:12}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,lineHeight:1,letterSpacing:-.3}}>{totalPoints.toLocaleString()}</div>
                  <div style={{fontSize:9.5,letterSpacing:1.2,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Points</div>
                </div>
                <div style={{padding:"10px 12px",background:"rgba(255,255,255,.6)",border:`1px solid ${T.apricot}33`,borderRadius:12}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.basil,lineHeight:1,letterSpacing:-.3}}>{totalClipped}</div>
                  <div style={{fontSize:9.5,letterSpacing:1.2,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Clipped</div>
                </div>
                <div style={{padding:"10px 12px",background:"rgba(255,255,255,.6)",border:`1px solid ${T.apricot}33`,borderRadius:12}}>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.berry,lineHeight:1,letterSpacing:-.3}}>{upcomingBirthdays}</div>
                  <div style={{fontSize:9.5,letterSpacing:1.2,color:T.ink50,marginTop:4,fontWeight:700,textTransform:"uppercase"}}>Birthdays</div>
                </div>
              </div>
            </div>

            {/* ── Sync banner — replaces the demo +/- buttons ── */}
            <button onClick={()=>{
                // simulated sync: bump points realistically
                setLoyalty(p=>({...p,
                  publix: p.publix + 80,
                  sprouts: p.sprouts + 45,
                  kroger: p.kroger + 220,
                  target: p.target + 60,
                }));
                SFX.success?.();
                notify("Synced 4 programs from latest receipts", {type:"success"});
              }}
              style={{
                width:"100%",display:"flex",alignItems:"center",gap:10,
                padding:"12px 14px",marginBottom:14,
                background:T.parchment,border:`1px solid ${T.hairline}`,
                borderRadius:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left",
                transition:"all .15s ease",
              }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.apricot}
              onMouseLeave={e=>e.currentTarget.style.borderColor=T.hairline}
              aria-label="Sync points from latest receipts"
            >
              <div style={{width:30,height:30,borderRadius:8,background:T.butterSoft,border:`1px solid ${T.butter}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="refresh" size={15} color={T.apricot} stroke={2.4}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,color:T.forest,fontWeight:700,lineHeight:1.2}}>Sync points from latest receipts</div>
                <div style={{fontSize:10.5,color:T.ink50,marginTop:2,fontWeight:600}}>Last synced 2 days ago · 4 programs linked</div>
              </div>
              <Icon name="chevron-right" size={16} color={T.ink50} stroke={2.2}/>
            </button>

            {/* ── PROGRAM CARDS ── */}
            <div style={{display:"grid",gap:12,marginBottom:14}}>
              {PROGRAMS.map((p, gi) => {
                const pts = loyalty[p.key] || 0;
                const hasPoints = p.redeem > 0;
                const dollarValue = hasPoints ? (pts/p.redeem)*p.redeemVal : 0;
                // Nearest reward — how many points until the next $5/$1 milestone
                const ptsToNext = hasPoints ? Math.max(0, p.redeem - (pts % p.redeem)) : 0;
                const nextRewardPct = hasPoints ? ((pts % p.redeem) / p.redeem) : 0;
                // Tier progress
                let currentTier = null, nextTier = null, tierPct = 0;
                if (p.tiers) {
                  for (let i=0; i<p.tiers.length; i++) {
                    if (pts >= p.tiers[i].min) currentTier = p.tiers[i];
                    if (pts < p.tiers[i].min && !nextTier) nextTier = p.tiers[i];
                  }
                  if (currentTier && nextTier) {
                    tierPct = (pts - currentTier.min) / (nextTier.min - currentTier.min);
                  } else if (currentTier && !nextTier) {
                    tierPct = 1;
                  }
                }
                const canRedeem = hasPoints && pts >= p.redeem;
                return (
                  <Glass key={p.key} className="anim-slide-up" style={{"--i":gi*1.0,padding:0,overflow:"hidden",borderRadius:16}}>
                    {/* Top strip — gradient with logo + name + points */}
                    <div style={{padding:"13px 16px",background:p.bg,display:"flex",alignItems:"center",gap:12,position:"relative",overflow:"hidden"}}>
                      <div style={{width:40,height:40,borderRadius:11,background:`${T.paper}2E`,border:`1px solid ${T.paper}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <ItemIcon emoji={p.logo} size={22}/>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.paper,lineHeight:1.1,letterSpacing:-.2}}>{p.name}</div>
                        <div style={{fontSize:10.5,color:T.paper,opacity:.78,marginTop:2,fontWeight:600}}>{p.program}</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        {hasPoints ? (
                          <>
                            <div style={{fontFamily:FONT_DISPLAY,fontSize:22,fontWeight:700,color:T.butter,lineHeight:1,letterSpacing:-.4}}>{pts.toLocaleString()}</div>
                            <div style={{fontSize:9,letterSpacing:1.4,color:T.paper,opacity:.7,marginTop:3,fontWeight:700}}>POINTS</div>
                          </>
                        ) : (
                          <div style={{fontSize:9.5,letterSpacing:.8,color:`${T.paper}D9`,fontWeight:800,background:`${T.paper}2E`,borderRadius:5,padding:"3px 7px",textTransform:"uppercase"}}>Membership</div>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div style={{padding:"14px 16px"}}>
                      {/* Nearest reward progress (if applicable) */}
                      {hasPoints && (
                        <div style={{marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                            <span style={{fontSize:11,color:T.ink50,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>Next reward</span>
                            <span style={{fontSize:11.5,color:T.forest,fontWeight:700}}>
                              {canRedeem ? (
                                <span style={{color:T.basil,fontWeight:800}}>${p.redeemVal} ready</span>
                              ) : (
                                <>{ptsToNext.toLocaleString()} pts away</>
                              )}
                            </span>
                          </div>
                          <div style={{height:7,background:T.parchment,borderRadius:99,overflow:"hidden",border:`1px solid ${T.hairline}`}}>
                            <div style={{height:"100%",width:`${(canRedeem?1:nextRewardPct)*100}%`,background:`linear-gradient(90deg, ${p.color}, ${T.apricot})`,borderRadius:99,transition:"width .4s ease"}}/>
                          </div>
                          <div style={{fontSize:10.5,color:T.ink50,marginTop:5,fontWeight:600}}>
                            ${dollarValue.toFixed(2)} earned · {p.redeem} pts = ${p.redeemVal} off
                          </div>
                        </div>
                      )}

                      {/* Tier progress (if program has tiers) */}
                      {p.tiers && currentTier && (
                        <div style={{marginBottom:12,padding:"10px 12px",background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:10}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                            <span style={{fontSize:10,color:T.apricot,fontWeight:800,letterSpacing:.5,textTransform:"uppercase"}}>
                              <Icon name="trophy" size={11} color={T.apricot} stroke={2.4} style={{verticalAlign:"-1px",marginRight:3}}/>
                              {currentTier.name} tier
                            </span>
                            {nextTier && (
                              <span style={{fontSize:10.5,color:T.forest,fontWeight:700}}>{(nextTier.min-pts).toLocaleString()} pts to {nextTier.name}</span>
                            )}
                          </div>
                          <div style={{height:5,background:"rgba(255,255,255,.7)",borderRadius:99,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${tierPct*100}%`,background:`linear-gradient(90deg, ${T.apricot}, ${T.berry})`,borderRadius:99}}/>
                          </div>
                        </div>
                      )}

                      {/* Birthday/anniversary chip */}
                      {p.birthday && (
                        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:12,padding:"8px 11px",background:`${T.berry}11`,border:`1px solid ${T.berry}33`,borderRadius:9}}>
                          <Icon name="gift" size={14} color={T.berry} stroke={2.2}/>
                          <span style={{fontSize:11.5,color:T.forest,fontWeight:600}}>
                            <span style={{color:T.berry,fontWeight:800}}>Birthday perk · </span>
                            {p.birthday}
                          </span>
                        </div>
                      )}

                      {/* Perks list */}
                      <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:p.offers.length?12:0}}>
                        {p.perks.map((perk,j) => (
                          <div key={j} style={{display:"flex",alignItems:"center",gap:7,fontSize:11.5,color:T.ink50,fontWeight:500}}>
                            <Icon name="check" size={11} color={p.color} stroke={2.6} style={{flexShrink:0}}/>
                            <span>{perk}</span>
                          </div>
                        ))}
                      </div>

                      {/* Linked offers — clip-able */}
                      {p.offers.length > 0 && (
                        <div style={{marginTop:0}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
                            <span style={{fontSize:10,color:T.ink50,fontWeight:800,letterSpacing:.6,textTransform:"uppercase"}}>Linked offers</span>
                            <span style={{fontSize:10,color:T.ink50,fontWeight:600}}>{p.offers.filter(o=>clippedOffers[o.id]).length}/{p.offers.length} clipped</span>
                          </div>
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {p.offers.map(o => {
                              const clipped = !!clippedOffers[o.id];
                              return (
                                <button key={o.id} onClick={()=>{toggleClipOffer(o.id);SFX.tabSwitch?.();}}
                                  style={{
                                    display:"flex",alignItems:"center",gap:9,padding:"8px 11px",
                                    background:clipped?`${p.color}14`:T.parchment,
                                    border:`1px solid ${clipped?p.color:T.hairline}`,
                                    borderRadius:9,cursor:"pointer",fontFamily:"inherit",textAlign:"left",
                                    transition:"all .15s ease",
                                  }}>
                                  <div style={{width:18,height:18,borderRadius:5,background:clipped?p.color:T.paper,border:`1.5px solid ${clipped?p.color:T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                    {clipped && <Icon name="check" size={11} color={T.paper} stroke={3}/>}
                                  </div>
                                  <span style={{flex:1,fontSize:11.5,color:T.forest,fontWeight:clipped?700:600,lineHeight:1.3}}>{o.label}</span>
                                  <span style={{fontSize:9.5,color:T.ink50,fontWeight:700,letterSpacing:.3}}>exp {o.exp}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Redeem button */}
                      {canRedeem && (
                        <RippleBtn sound="redeem" onClick={()=>{setLoyalty(prev=>({...prev,[p.key]:Math.max(0,prev[p.key]-p.redeem)}));notify(`Redeemed ${p.redeem} pts for $${p.redeemVal} off`,{type:"success"});}}
                          style={{marginTop:12,background:`linear-gradient(135deg,${p.color},${T.apricot})`,border:"none",color:T.paper,borderRadius:11,padding:"11px 16px",fontSize:12.5,fontWeight:800,width:"100%",letterSpacing:.2,boxShadow:`0 0 18px ${p.color}30`}}>
                          Redeem {p.redeem} pts for ${p.redeemVal} off
                        </RippleBtn>
                      )}
                    </div>
                  </Glass>
                );
              })}
            </div>
          </div>
          );
        })()}

        {/* ══ REWARDS ══ */}
        {!activeStore && tab==="rewards" && (() => {
          // Per-state counts and stats. Computed once for header chips, summary
          // strip, and filter chips.
          const confirmedEntries = derivedLedger.filter(e=>e.state==="confirmed");
          const pendingEntries   = derivedLedger.filter(e=>e.state==="pending");
          const expiredEntries   = derivedLedger.filter(e=>e.state==="expired");
          const lifetimeEarned = confirmedEntries.reduce((s,e)=>s+(e.confirmedReward != null ? e.confirmedReward : e.reward),0);
          // "This month" = confirmed entries whose timestamp is in the current calendar month
          const now = new Date();
          const ym = `${now.getFullYear()}-${now.getMonth()}`;
          const thisMonthEarned = confirmedEntries.reduce((s,e)=>{
            const d = new Date(e.timestamp);
            return (`${d.getFullYear()}-${d.getMonth()}` === ym) ? s + (e.confirmedReward != null ? e.confirmedReward : e.reward) : s;
          }, 0);
          // % of total reward pool currently pending — drives the dotted ring around the hero balance
          const totalRewardPool = rewardsConfirmedBalance + rewardsPendingBalance;
          const pendingPct = totalRewardPool > 0 ? (rewardsPendingBalance / totalRewardPool) : 0;
          // Filter the visible ledger
          const filteredLedger = rewardsFilter === "all" ? derivedLedger
            : rewardsFilter === "confirmed" ? confirmedEntries
            : rewardsFilter === "pending"   ? pendingEntries
            : expiredEntries;

          // Filter chip — used for state filter row
          const FilterChip = ({value, label, count, color}) => {
            const active = rewardsFilter === value;
            return (
              <button onClick={()=>setRewardsFilter(value)}
                style={{
                  flex:1, minWidth:0,
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,
                  padding:"7px 4px",
                  borderRadius:9,
                  border: active ? `1.5px solid ${color}` : `1px solid ${T.hairline}`,
                  background: active ? `${color}14` : T.paper,
                  color: active ? color : T.ink50,
                  fontWeight: active ? 800 : 600,
                  cursor:"pointer", fontFamily:"inherit",
                  transition:"all .12s ease",
                }}>
                <span style={{fontSize:10,letterSpacing:.4,textTransform:"uppercase"}}>{label}</span>
                <span style={{fontSize:11,fontWeight:800}}>{count}</span>
              </button>
            );
          };

          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* ── Header ── icon chip + title + mode badges */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
              <div style={{width:32,height:32,borderRadius:9,background:`linear-gradient(135deg, ${T.leaf}26, ${T.basil}1A)`,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon name="gift" size={25} color={T.basil} stroke={2.2}/>
              </div>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.forest}}>Rewards</div>
              <div style={{flex:1}}/>
              {!AFFILIATE_LIVE && (
                <span style={{fontSize:9,fontWeight:800,letterSpacing:.9,color:T.warn,background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:5,padding:"2px 7px",lineHeight:1.3}}>
                  DEMO
                </span>
              )}
              {isAdminMode && (
                <span style={{fontSize:9,fontWeight:800,letterSpacing:.9,color:T.basil,background:"rgba(167,139,250,.14)",border:"1px solid rgba(160,69,98,.30)",borderRadius:5,padding:"2px 7px",lineHeight:1.3}}>
                  ADMIN
                </span>
              )}
            </div>
            <div style={{fontSize:12,color:T.ink50,marginBottom:14}}>{AFFILIATE_LIVE ? "Earn cash back on every confirmed Instacart order placed through CartIQ." : "Demo mode — tracked estimates until CartIQ's Instacart partnership is live."}</div>

            {/* ── Hero balance card ──
                Forest backdrop with a subtle radial wash. Headline number in leaf-green
                (= money you've earned, on-brand). When there's pending money, a dotted
                ring on the right shows what % of the total is still unconfirmed. */}
            <div style={{
              position:"relative",overflow:"hidden",
              padding:"22px 22px",marginBottom:12,borderRadius:20,
              background:`radial-gradient(circle at 100% 0%, ${T.basil}22 0%, transparent 60%), ${T.forest}`,
              border:`1px solid ${T.forest}`,
            }}>
              <div style={{position:"relative",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:14}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:10,letterSpacing:1.8,color:T.sage,fontWeight:800,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="wallet" size={13} color={T.sage} stroke={2}/>
                    {REWARDS_COPY.heroConfirmedLabel}
                  </div>
                  <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:6}}>
                    <span style={{fontFamily:FONT_DISPLAY,fontSize:46,fontWeight:700,color:T.leaf,lineHeight:1,letterSpacing:-1}}>${rewardsConfirmedBalance.toFixed(2)}</span>
                  </div>
                  <div style={{fontSize:11,color:T.sageSoft,lineHeight:1.4}}>
                    {confirmedEntries.length === 0
                      ? (AFFILIATE_LIVE ? "Place your first order to start earning" : "Tracked rewards will confirm when partnership is live")
                      : `from ${confirmedEntries.length} confirmed ${confirmedEntries.length===1?"order":"orders"}`}
                  </div>
                </div>

                {/* Pending ring — only when there's something pending */}
                {rewardsPendingBalance > 0 && (() => {
                  const r = 30, c = 2*Math.PI*r;
                  return (
                    <div style={{position:"relative",width:78,height:78,flexShrink:0}}>
                      <svg width={78} height={78} viewBox="0 0 78 78" style={{transform:"rotate(-90deg)"}}>
                        <circle cx={39} cy={39} r={r} fill="none" stroke={T.sage} strokeOpacity={0.2} strokeWidth={4}/>
                        <circle cx={39} cy={39} r={r} fill="none" stroke={T.butter} strokeWidth={4} strokeLinecap="round"
                          strokeDasharray={`${c*pendingPct} ${c}`} strokeDashoffset={0} style={{transition:"stroke-dasharray .4s ease"}}/>
                      </svg>
                      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:14,fontWeight:700,color:T.butter,lineHeight:1}}>${rewardsPendingBalance.toFixed(0)}</div>
                        <div style={{fontSize:10,color:T.ink50,letterSpacing:.8,fontWeight:700,marginTop:2}}>PENDING</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ── Quick stats strip ── 3-up connected pill (matches watchlist pattern) */}
            {derivedLedger.length > 0 && (
              <div style={{
                display:"flex",alignItems:"stretch",
                marginBottom:12,borderRadius:12,
                background:T.paper,border:`1px solid ${T.hairline}`,
                overflow:"hidden",
              }}>
                <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <Icon name="calendar" size={12} color={T.ink50} stroke={2}/>
                    <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>This month</span>
                  </div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,lineHeight:1.1}}>${thisMonthEarned.toFixed(2)}</div>
                </div>
                <div style={{width:1,background:T.hairline}}/>
                <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <Icon name="clock" size={12} color={T.ink50} stroke={2}/>
                    <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Pending</span>
                  </div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,lineHeight:1.1}}>{pendingEntries.length}<span style={{fontSize:11,color:T.ink50,fontWeight:600,marginLeft:4}}>{pendingEntries.length===1?"order":"orders"}</span></div>
                </div>
                <div style={{width:1,background:T.hairline}}/>
                <div style={{flex:1,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <Icon name="trophy" size={12} color={T.ink50} stroke={2}/>
                    <span style={{fontSize:9,letterSpacing:.8,color:T.ink50,fontWeight:700,textTransform:"uppercase"}}>Lifetime</span>
                  </div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:700,color:T.forest,lineHeight:1.1}}>${lifetimeEarned.toFixed(2)}</div>
                </div>
              </div>
            )}

            {/* ── How it works ── 3-step mini flow replacing the prose disclosure */}
            <div style={{padding:"14px 16px",marginBottom:14,borderRadius:14,background:AFFILIATE_LIVE ? T.sageSoft : T.butterSoft,border:`1px solid ${AFFILIATE_LIVE ? T.sage : T.butter}`}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <Icon name="info" size={14} color={AFFILIATE_LIVE ? T.basil : T.warn} stroke={2}/>
                <div style={{fontSize:10,letterSpacing:1.2,fontWeight:800,color:T.forest,textTransform:"uppercase"}}>How it works</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                {[
                  {icon:"cart", label:"Order placed", color:T.basil},
                  {icon:"clock", label:"Impact confirms", color:T.warn},
                  {icon:"wallet", label:"Cash arrives", color:T.leaf},
                ].map((step,i)=>(
                  <Fragment key={step.label}>
                    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:5,minWidth:0}}>
                      <div style={{width:30,height:30,borderRadius:"50%",background:T.paper,border:`1.5px solid ${step.color}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <Icon name={step.icon} size={13} color={step.color} stroke={2}/>
                      </div>
                      <div style={{fontSize:10,fontWeight:700,color:T.forest,textAlign:"center",lineHeight:1.2}}>{step.label}</div>
                    </div>
                    {i<2 && (
                      <svg width={14} height={2} style={{flexShrink:0,marginTop:-14}}>
                        <line x1={0} y1={1} x2={14} y2={1} stroke={T.ink50} strokeOpacity={.4} strokeWidth={1.5} strokeDasharray="2 2"/>
                      </svg>
                    )}
                  </Fragment>
                ))}
              </div>
              <div style={{fontSize:10.5,color:T.ink50,lineHeight:1.5,marginTop:10,paddingTop:10,borderTop:`1px solid ${AFFILIATE_LIVE ? T.sage : T.butter}66`}}>
                {REWARDS_COPY.detailDisclosure}
              </div>
            </div>

            {/* Admin panel — gated by ?admin=true URL param. Only visible to
                the founder during the demo period. Collapsed by default so it
                doesn't dominate the user-facing UI. */}
            {isAdminMode && (
              <div style={{marginBottom:14}}>
                <button onClick={()=>setAdminToolsOpen(o=>!o)}
                  style={{
                    width:"100%",display:"flex",alignItems:"center",gap:8,
                    padding:"8px 12px",borderRadius:10,
                    background:adminToolsOpen ? T.berrySoft : T.parchment,
                    border:`1px solid ${adminToolsOpen ? "rgba(160,69,98,.30)" : T.hairline}`,
                    color:T.basil, fontSize:11, fontWeight:700, fontFamily:"inherit",
                    cursor:"pointer", letterSpacing:.5,
                  }}>
                  <Icon name="settings" size={14} color={T.basil} stroke={2}/>
                  <span>ADMIN TOOLS</span>
                  <span style={{flex:1,textAlign:"right",fontSize:10,color:T.ink50,fontWeight:600,letterSpacing:.3}}>
                    {adminToolsOpen ? "Hide" : "Show"}
                  </span>
                  <Icon name={adminToolsOpen ? "chevron-up" : "chevron-down"} size={11} color={T.basil} stroke={2}/>
                </button>

                {adminToolsOpen && (
                  <div style={{marginTop:8,padding:"14px 16px",borderRadius:12,border:"1px solid rgba(160,69,98,.25)",background:T.berrySoft,fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>
                    <AdminCatalogSearchPanel />
                    {derivedLedger.length > 0 && (
                      <>
                        <div style={{fontSize:10.5,color:T.ink50,lineHeight:1.5,marginBottom:10,marginTop:14}}>
                          Per-entry controls appear in the history list below. Each Confirm prompt asks for the real Impact commission amount — the entry confirms only if that amount is at least {(EXPECTED_COMMISSION_RATE*100).toFixed(0)}% of the send amount. Use Force to bypass the threshold for UI testing.
                        </div>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <button onClick={()=>{
                            const pendingIds = derivedLedger.filter(e=>e.state==="pending").map(e=>e.id);
                            if (pendingIds.length === 0) { notify("No pending entries to confirm","warn"); return; }
                            if (!window.confirm(`Force-confirm all ${pendingIds.length} pending entries at their tracked 2%? (Bypasses threshold check — for UI testing only.)`)) return;
                            pendingIds.forEach(id => confirmRewardEntry(id, 0, { force: true }));
                            notify(`✓ Force-confirmed ${pendingIds.length} entries`);
                          }}
                            style={{background:T.paper,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:6,padding:"5px 10px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                            Force-confirm pending
                          </button>
                          <button onClick={()=>{
                            const input = window.prompt(`Expire all pending entries older than how many days?\n\n(Default is ${REWARDS_EXPIRY_DAYS} — production threshold. Lower = useful for testing; 0 = expire ALL pending immediately.)`, String(REWARDS_EXPIRY_DAYS));
                            if (input == null) return;
                            const days = parseInt(input, 10);
                            if (!Number.isFinite(days) || days < 0) { notify("Invalid number of days","warn"); return; }
                            const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
                            const toExpire = derivedLedger.filter(e => {
                              if (e.state !== "pending") return false;
                              const ts = Date.parse(e.timestamp);
                              return Number.isFinite(ts) && ts <= cutoff;
                            });
                            if (toExpire.length === 0) { notify(`No pending entries older than ${days} day${days === 1 ? "" : "s"}`, "warn"); return; }
                            if (!window.confirm(`Expire ${toExpire.length} pending entries older than ${days} day${days === 1 ? "" : "s"}?`)) return;
                            toExpire.forEach(e => expireRewardEntry(e.id));
                            notify(`✓ Expired ${toExpire.length} entries`);
                          }}
                            style={{background:T.paper,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:6,padding:"5px 10px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                            Expire older than…
                          </button>
                          <button onClick={()=>{
                            if (!window.confirm("Reset ALL entries back to pending? Clears confirmed/expired state.")) return;
                            derivedLedger.forEach(e => resetRewardEntry(e.id));
                            notify("✓ All entries reset to pending");
                          }}
                            style={{background:T.paper,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:6,padding:"5px 10px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                            Reset all
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Empty state OR History list ── */}
            {derivedLedger.length === 0 ? (
              <Glass style={{padding:"32px 22px",borderRadius:18,textAlign:"center"}}>
                <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:54,height:54,borderRadius:"50%",background:`linear-gradient(135deg, ${T.leaf}26, ${T.basil}1A)`,border:`1px solid ${T.sage}`,marginBottom:12}}>
                  <Icon name="leaf" size={32} color={T.basil} stroke={2}/>
                </div>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:17,fontWeight:700,color:T.forest,marginBottom:6}}>No rewards yet</div>
                <div style={{fontSize:12,color:T.ink50,lineHeight:1.5,maxWidth:280,margin:"0 auto 16px"}}>
                  Place your first order through CartIQ and rewards will appear here as Impact confirms them.
                </div>
                {/* Mini 1·2·3 illustration */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,maxWidth:280,margin:"0 auto"}}>
                  {[
                    {n:1, label:"Build cart"},
                    {n:2, label:"Send to store"},
                    {n:3, label:"Earn 2%"},
                  ].map((s,i)=>(
                    <Fragment key={s.n}>
                      <div style={{flex:1,padding:"8px 4px",borderRadius:8,background:T.parchment,border:`1px solid ${T.hairline}`}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:13,fontWeight:700,color:T.basil,lineHeight:1}}>{s.n}</div>
                        <div style={{fontSize:9,color:T.ink50,marginTop:3,fontWeight:600}}>{s.label}</div>
                      </div>
                      {i<2 && <Icon name="chevron-right" size={13} color={T.ink50} stroke={2}/>}
                    </Fragment>
                  ))}
                </div>
              </Glass>
            ) : (
              <>
                {/* Section header + filter chips */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,paddingLeft:2}}>
                  <Icon name="filter" size={13} color={T.ink50} stroke={2}/>
                  <div style={{fontSize:10,letterSpacing:1.6,color:T.ink50,fontWeight:800}}>REWARDS HISTORY</div>
                </div>
                <div style={{display:"flex",gap:6,marginBottom:10}}>
                  <FilterChip value="all"       label="All"       count={derivedLedger.length}     color={T.basil}/>
                  <FilterChip value="confirmed" label="Confirmed" count={confirmedEntries.length} color={T.leaf}/>
                  <FilterChip value="pending"   label="Pending"   count={pendingEntries.length}   color={T.warn}/>
                  <FilterChip value="expired"   label="Expired"   count={expiredEntries.length}   color={T.ink50}/>
                </div>

                {filteredLedger.length === 0 ? (
                  <div style={{padding:"22px 14px",borderRadius:12,background:T.parchment,border:`1px dashed ${T.hairline}`,textAlign:"center"}}>
                    <div style={{fontSize:12,color:T.ink50}}>No <span style={{fontWeight:700,color:T.forest}}>{rewardsFilter}</span> rewards yet</div>
                  </div>
                ) : filteredLedger.slice(0, 20).map(entry => {
                  const isConfirmed = entry.state === "confirmed";
                  const isExpired = entry.state === "expired";
                  const isPending = entry.state === "pending";
                  const stateColor = isConfirmed ? T.leaf : isExpired ? T.ink50 : T.warn;
                  const displayAmount = isConfirmed && entry.confirmedReward != null
                    ? entry.confirmedReward
                    : entry.reward;
                  const threshold = entry.cartTotal * (entry.expectedCommissionRate || EXPECTED_COMMISSION_RATE);
                  // Subtle row tint keyed to state — keeps states scannable without
                  // shouting. Expired rows also drop opacity slightly.
                  const rowBg = isConfirmed ? `${T.leaf}0A`
                    : isPending ? `${T.butter}14`
                    : T.parchment;
                  return (
                    <div key={entry.id} style={{
                      padding:"12px 14px",marginBottom:8,borderRadius:12,
                      background:rowBg,border:`1px solid ${T.hairline}`,
                      opacity: isExpired ? 0.65 : 1,
                    }}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        {/* State indicator — replaces the 3-letter pill. Filled green dot for
                            confirmed, hollow amber ring for pending, X-marked grey for expired. */}
                        <div style={{width:30,height:30,flexShrink:0,position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {isConfirmed && (
                            <div style={{width:30,height:30,borderRadius:"50%",background:T.leaf,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 0 3px ${T.leaf}26`}}>
                              <Icon name="check" size={19} color={T.paper} stroke={2.5}/>
                            </div>
                          )}
                          {isPending && (
                            <>
                              <svg width={30} height={30} style={{position:"absolute",inset:0,transform:"rotate(-90deg)"}}>
                                <circle cx={15} cy={15} r={13} fill="none" stroke={T.butter} strokeWidth={2} strokeDasharray="3 3"/>
                              </svg>
                              <Icon name="clock" size={17} color={T.warn} stroke={2}/>
                            </>
                          )}
                          {isExpired && (
                            <div style={{width:30,height:30,borderRadius:"50%",background:T.parchment,border:`1px solid ${T.hairline}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              <Icon name="x" size={17} color={T.ink50} stroke={2}/>
                            </div>
                          )}
                        </div>

                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:700,color:T.forest,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.2,marginBottom:3,textDecoration:isExpired?"line-through":"none"}}>
                            {entry.storeName}
                          </div>
                          <div style={{fontSize:10.5,color:T.ink50,display:"flex",alignItems:"center",gap:6}}>
                            <span>{entry.date}</span>
                            <span style={{color:T.hairline}}>·</span>
                            <span>${entry.cartTotal.toFixed(2)} order</span>
                          </div>
                        </div>

                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:16,fontWeight:isConfirmed?800:700,color:stateColor,lineHeight:1.1,textDecoration:isExpired?"line-through":"none"}}>
                            {isConfirmed ? "+" : ""}${displayAmount.toFixed(2)}
                          </div>
                          <div style={{fontSize:9,color:T.ink50,letterSpacing:.4,fontWeight:600,marginTop:2}}>
                            {isPending ? "tracked" : isExpired ? "not paid" : `${(REWARDS_RATE*100).toFixed(0)}% reward`}
                          </div>
                        </div>
                      </div>

                      {/* Admin-only per-entry controls */}
                      {isAdminMode && (
                        <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${T.sage}`,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                          <span style={{fontSize:9,color:T.basil,fontWeight:700,letterSpacing:.5,marginRight:4,fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>ADMIN:</span>
                          {entry.state === "pending" && (
                            <>
                              <button onClick={()=>{
                                const rate = entry.expectedCommissionRate || EXPECTED_COMMISSION_RATE;
                                const floorAmount = threshold * REWARDS_FLOOR_RATIO;
                                const input = window.prompt(`Enter Impact commission paid for this order:\n\nSend amount: $${entry.cartTotal.toFixed(2)}\nFull threshold: $${threshold.toFixed(2)} (${(rate*100).toFixed(0)}% of send) → confirms at $${entry.reward.toFixed(2)}\nPartial floor: $${floorAmount.toFixed(2)} (${(REWARDS_FLOOR_RATIO*100).toFixed(0)}% of threshold) → confirms proportionally\nBelow floor: stays pending`, entry.actualImpactCommission != null ? String(entry.actualImpactCommission) : "");
                                if (input == null) return;
                                const val = parseFloat(input);
                                if (!Number.isFinite(val)) { notify("Invalid amount","warn"); return; }
                                const result = confirmRewardEntry(entry.id, val);
                                if (result.reason === "threshold_met") {
                                  notify(`✓ Confirmed in full at $${entry.reward.toFixed(2)}`);
                                } else if (result.reason === "partial") {
                                  const partialAmt = result.entry.confirmedReward.toFixed(2);
                                  notify(`✓ Partial confirm at $${partialAmt} (proportional to ~$${result.entry.estimatedActualSpend.toFixed(2)} purchase)`);
                                } else if (result.reason === "below_floor") {
                                  notify(`⚠️ $${val.toFixed(2)} below floor of $${floorAmount.toFixed(2)} — stays pending`, "warn");
                                }
                              }}
                                style={{background:T.paper,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>
                                Confirm with $
                              </button>
                              <button onClick={()=>{
                                if (!window.confirm("Force-confirm this entry (bypass threshold check)? For UI testing only.")) return;
                                confirmRewardEntry(entry.id, 0, { force: true });
                                notify(`✓ Force-confirmed at $${entry.reward.toFixed(2)}`);
                              }}
                                style={{background:T.paper,border:"1px solid rgba(160,69,98,.25)",color:T.basil,borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>
                                Force
                              </button>
                              <button onClick={()=>{ expireRewardEntry(entry.id); notify("Entry marked expired"); }}
                                style={{background:T.paper,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>
                                Expire
                              </button>
                            </>
                          )}
                          {(entry.state === "confirmed" || entry.state === "expired") && (
                            <button onClick={()=>{ resetRewardEntry(entry.id); notify("Reset to pending"); }}
                              style={{background:T.paper,border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>
                              Reset to pending
                            </button>
                          )}
                          {entry.actualImpactCommission != null && (
                            <span style={{fontSize:9,color:T.ink50,marginLeft:"auto",fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>
                              Last Impact: ${entry.actualImpactCommission.toFixed(2)}
                            </span>
                          )}
                          {entry.forced && (
                            <span style={{fontSize:9,color:T.basil,fontWeight:700,fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}}>(forced)</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {filteredLedger.length > 20 && (
                  <div style={{fontSize:11,color:T.ink50,textAlign:"center",padding:"8px 0"}}>
                    Showing 20 most recent · {filteredLedger.length} total in this filter
                  </div>
                )}
              </>
            )}
          </div>
          );
        })()}

        {/* ══ SHARE ══
            Lets users post their savings as a CartIQ-branded social card.
            The card itself uses the same sage→cream language as the rest of
            the app — feels like a real artifact, not a dark UI screenshot. */}
        {!activeStore && tab==="share" && (() => {
          // Build period-specific stats from actual transaction history
          const PERIOD_LABEL = { week:"This Week", month:"This Month", quarter:"This Quarter", year:"This Year" };
          const allTxn = txnHistory;
          const netSaved = allTxn.reduce((s,t) => s + t.netGain, 0);
          const grossSaved = allTxn.reduce((s,t) => s + t.grossSavings, 0);
          const orderCt = allTxn.length;
          // Top store from transactions
          const storeCounts = {};
          allTxn.forEach(t => { storeCounts[t.store] = (storeCounts[t.store] || 0) + 1; });
          const topStore = Object.entries(storeCounts).sort((a,b)=>b[1]-a[1])[0];
          // Top store's logo (look up in STORES)
          const topStoreObj = topStore ? STORES.find(s => s.name === topStore[0]) : null;

          return (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* Page header */}
            <div style={{marginBottom:14,padding:"0 2px"}}>
              <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.4,lineHeight:1.1}}>Savings Report</div>
              <div style={{fontSize:12.5,color:T.ink50,marginTop:5,lineHeight:1.45,maxWidth:340}}>
                A shareable card showing what CartIQ saved you. Made for stories, captions, and screenshots.
              </div>
            </div>

            {/* Period selector — segmented control above the card */}
            <div style={{display:"flex",gap:5,marginBottom:14,padding:3,background:T.parchment,border:`1px solid ${T.hairline}`,borderRadius:12}}>
              {["week","month","quarter","year"].map(p => (
                <button key={p} onClick={() => { setSharePeriod(p); SFX.tabSwitch(); }}
                  style={{flex:1,background:sharePeriod===p?T.paper:"transparent",border:sharePeriod===p?`1px solid ${T.sage}`:"1px solid transparent",color:sharePeriod===p?T.forest:T.ink50,borderRadius:9,padding:"7px 0",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize",letterSpacing:.2,boxShadow:sharePeriod===p?T.shadowSoft:"none",transition:"all .15s"}}>
                  {p}
                </button>
              ))}
            </div>

            {/* ── Shareable Card ──
                Sage→cream gradient with a subtle radial wash for depth.
                Forest text on top. The card is intentionally portrait-ish at
                ~9:11 so it screenshots well for IG stories without cropping. */}
            <div style={{
              position:"relative",overflow:"hidden",borderRadius:24,marginBottom:18,
              border:`1px solid ${T.sage}`,
              boxShadow:"0 24px 64px rgba(30,53,40,.18), 0 0 0 1px rgba(30,53,40,.04)",
              background:`
                radial-gradient(circle at 85% 15%, ${T.butterSoft} 0%, transparent 50%),
                radial-gradient(circle at 10% 85%, ${T.sageSoft} 0%, transparent 55%),
                linear-gradient(160deg, ${T.sageSoft} 0%, #ECEAC9 50%, #F4EDC9 100%)
              `,
            }}>
              {/* Top row: brand mark + verified seal */}
              <div style={{position:"relative",zIndex:1,padding:"18px 22px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <CartIQMark size={36} mood="celebrating"/>
                  <div>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:19,fontWeight:700,color:T.forest,letterSpacing:-.5,lineHeight:1}}>
                      Cart<span style={{color:T.basil}}>IQ</span>
                    </div>
                    <div style={{fontSize:9.5,color:T.basil,letterSpacing:1.8,marginTop:3,fontWeight:700}}>SMART GROCERY SAVINGS</div>
                  </div>
                </div>
                <div style={{display:"inline-flex",alignItems:"center",gap:5,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:20,padding:"4px 10px 4px 7px"}}>
                  <Icon name="check-circle" size={14} color={T.basil} stroke={2.6}/>
                  <span style={{fontSize:9.5,fontWeight:800,color:T.basil,letterSpacing:1.2}}>VERIFIED</span>
                </div>
              </div>

              {/* Hero number block */}
              <div style={{position:"relative",zIndex:1,padding:"28px 22px 22px",textAlign:"center"}}>
                <div style={{fontSize:10,letterSpacing:3,color:T.basil,marginBottom:10,fontWeight:800}}>
                  NET SAVINGS · {PERIOD_LABEL[sharePeriod].toUpperCase()}
                </div>
                <AnimNum v={netSaved} size={64} color={T.forest}/>
                <div style={{fontSize:11.5,color:T.ink50,marginTop:8,fontWeight:600}}>
                  ${grossSaved.toFixed(2)} gross savings · ${(grossSaved-netSaved).toFixed(2)} in fees
                </div>

                {currentMilestone && (
                  <div style={{display:"inline-flex",alignItems:"center",gap:7,background:T.paper,border:`1px solid ${T.sage}`,borderRadius:20,padding:"6px 14px 6px 11px",marginTop:14,boxShadow:T.shadowSoft}}>
                    <ItemIcon emoji={currentMilestone.icon} size={16}/>
                    <span style={{fontSize:11,fontWeight:800,color:T.forest,letterSpacing:1}}>{currentMilestone.label.toUpperCase()} TIER</span>
                  </div>
                )}
              </div>

              {/* Stat row — 3 facts that make the card share-worthy */}
              <div style={{position:"relative",zIndex:1,padding:"0 18px 18px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                  <div style={{background:T.paper,border:`1px solid ${T.sage}`,borderRadius:13,padding:"10px 12px",textAlign:"center"}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.forest,letterSpacing:-.3,lineHeight:1}}>{orderCt}</div>
                    <div style={{fontSize:9,letterSpacing:1.2,color:T.ink50,marginTop:5,fontWeight:700}}>ORDERS</div>
                  </div>
                  <div style={{background:T.paper,border:`1px solid ${T.sage}`,borderRadius:13,padding:"10px 12px",textAlign:"center"}}>
                    <div style={{fontFamily:FONT_DISPLAY,fontSize:18,fontWeight:700,color:T.basil,letterSpacing:-.3,lineHeight:1}}>${orderCt > 0 ? (netSaved/orderCt).toFixed(0) : 0}</div>
                    <div style={{fontSize:9,letterSpacing:1.2,color:T.ink50,marginTop:5,fontWeight:700}}>AVG / ORDER</div>
                  </div>
                  <div style={{background:T.paper,border:`1px solid ${T.sage}`,borderRadius:13,padding:"10px 8px",textAlign:"center",overflow:"hidden"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                      {topStoreObj && <ItemIcon emoji={topStoreObj.logo} size={16}/>}
                      <span style={{fontSize:13,fontWeight:700,color:T.forest,letterSpacing:-.1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:64}}>{topStore ? topStore[0] : "—"}</span>
                    </div>
                    <div style={{fontSize:9,letterSpacing:1.2,color:T.ink50,marginTop:5,fontWeight:700}}>TOP STORE</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{position:"relative",zIndex:1,borderTop:`1px solid ${T.sage}`,padding:"11px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,253,247,.4)"}}>
                <span style={{fontSize:10,color:T.basil,fontWeight:700,letterSpacing:.4}}>cartiq.app</span>
                <span style={{fontSize:10,color:T.basil,fontWeight:600}}>Powered by Instacart</span>
              </div>
            </div>

            {/* Primary actions — copy link + save image */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
              <RippleBtn sound="success" onClick={() => { setShareCopied(true); notify("Link copied"); setTimeout(()=>setShareCopied(false), 1800); }}
                style={{background:T.forest,border:`1px solid ${T.forest}`,borderRadius:12,padding:"13px 14px",display:"flex",alignItems:"center",justifyContent:"center",gap:8,color:T.paper,fontSize:12.5,fontWeight:700,fontFamily:"inherit",cursor:"pointer"}}>
                <Icon name={shareCopied ? "check" : "clip"} size={16} color={T.paper} stroke={2.4}/>
                <span>{shareCopied ? "Copied!" : "Copy link"}</span>
              </RippleBtn>
              <RippleBtn sound="success" onClick={() => notify("Image saved to Photos")}
                style={{background:T.paper,border:`1px solid ${T.sage}`,borderRadius:12,padding:"13px 14px",display:"flex",alignItems:"center",justifyContent:"center",gap:8,color:T.forest,fontSize:12.5,fontWeight:700,fontFamily:"inherit",cursor:"pointer"}}>
                <Icon name="camera" size={16} color={T.forest} stroke={2.4}/>
                <span>Save image</span>
              </RippleBtn>
            </div>

            {/* Share targets — minimal labels, brand-tinted chips */}
            <div style={{fontSize:10,letterSpacing:1.5,color:T.ink50,fontWeight:700,marginBottom:8,padding:"0 2px"}}>SHARE TO</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
              {[
                { id:"ig",  label:"Instagram", color:"#E4405F", emoji:"📸" },
                { id:"x",   label:"X",         color:"#000000", emoji:"✖️" },
                { id:"sms", label:"Messages",  color:"#34C759", emoji:"💬" },
                { id:"fb",  label:"Facebook",  color:"#1877F2", emoji:"📘" },
              ].map(s => (
                <RippleBtn key={s.id} sound="tabSwitch" onClick={() => notify(`Opening ${s.label}…`)}
                  style={{background:T.paper,border:`1px solid ${T.hairline}`,borderRadius:12,padding:"11px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:5,color:T.forest,cursor:"pointer",fontFamily:"inherit"}}>
                  <ItemIcon emoji={s.emoji} size={20}/>
                  <span style={{fontSize:10.5,fontWeight:700,color:T.forest,letterSpacing:.1}}>{s.label}</span>
                </RippleBtn>
              ))}
            </div>
          </div>
          );
        })()}

        {/* ══ WATCHLIST ══ */}
        {!activeStore && tab==="watchlist" && (
          <div style={{animation:"pageIn .4s ease both"}}>
            {/* ── HEADER ─────────────────────────────────────────────── */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
              <div style={{display:"flex",alignItems:"center",gap:11}}>
                <div style={{width:34,height:34,borderRadius:11,background:T.sageSoft,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <Icon name="bookmark" size={21} color={T.leaf} strokeWidth={1.8}/>
                </div>
                <div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.forest,lineHeight:1.1,letterSpacing:-.3}}>Watchlist</div>
                  <div style={{fontSize:11,color:T.ink50,marginTop:3,letterSpacing:.1}}>Track prices across any retailer</div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <button onClick={startVoiceWatchlistAdd}
                  aria-label={voiceListening ? "Stop voice input" : "Add item by voice"}
                  aria-pressed={voiceListening}
                  style={{
                    background: voiceListening ? "rgba(220,38,38,.15)" : "rgba(66,133,244,.10)",
                    border: `1px solid ${voiceListening ? "rgba(248,113,113,.4)" : "rgba(66,133,244,.28)"}`,
                    color: voiceListening ? T.berry : T.basil,
                    borderRadius: 11,
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    animation: voiceListening ? "pulse 1.2s ease-in-out infinite" : "none",
                  }}>
                  <span style={{display:"inline-flex",width:7,height:7,borderRadius:"50%",background:voiceListening?T.berry:T.basil,boxShadow:voiceListening?`0 0 0 3px ${T.berry}33`:"none"}}/>
                  {voiceListening ? "Listening…" : "Voice"}
                </button>
                {watchlist.length > 0 && (
                  <RippleBtn sound="scan" onClick={refreshWatchlistPrices}
                    style={{background:T.sageSoft,border:`1px solid ${T.sage}`,color:T.basil,borderRadius:11,padding:"8px 12px",fontSize:11,fontWeight:800,display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="refresh" size={17} color={T.basil} strokeWidth={2}/>
                    Refresh
                  </RippleBtn>
                )}
              </div>
            </div>

            {watchlist.length === 0 ? (
              /* ── EMPTY STATE ─────────────────────────────────────── */
              <Glass style={{padding:"44px 26px",borderRadius:20,textAlign:"center",position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at top, ${T.sageSoft} 0%, transparent 60%)`,opacity:.6,pointerEvents:"none"}}/>
                <div style={{position:"relative"}}>
                  <div style={{width:64,height:64,borderRadius:18,background:T.parchment,border:`1px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
                    <Icon name="bookmark" size={34} color={T.leaf} strokeWidth={1.6}/>
                  </div>
                  <div style={{fontFamily:FONT_DISPLAY,fontSize:19,fontWeight:700,color:T.forest,marginBottom:8,letterSpacing:-.2}}>Nothing on watch yet</div>
                  <div style={{fontSize:13,color:T.ink50,lineHeight:1.6,maxWidth:300,margin:"0 auto 20px"}}>
                    Tap the bookmark on any deal to track it. We'll watch the price daily and ping you when it drops to your target.
                  </div>
                  <RippleBtn sound="tabSwitch" onClick={()=>{SFX.tabSwitch();setTab("dashboard");}}
                    style={{background:T.forest,border:"none",color:T.paper,borderRadius:11,padding:"11px 22px",fontSize:12,fontWeight:800,boxShadow:T.shadowFloat,display:"inline-flex",alignItems:"center",gap:7}}>
                    Browse deals
                    <Icon name="arrow-right" size={17} color={T.paper} strokeWidth={2}/>
                  </RippleBtn>
                </div>
              </Glass>
            ) : (
              <>
                {/* ── SUMMARY STRIP — single connected pill ───────────── */}
                <Glass style={{display:"flex",alignItems:"stretch",borderRadius:14,padding:0,marginBottom:14,overflow:"hidden",border:`1px solid ${T.hairline}`}}>
                  {[
                    {l:"Watching",v:watchlist.length,c:T.forest,icon:"bookmark"},
                    {l:"On sale",v:watchlist.filter(w=>w.currentPrice<=w.targetPrice).length,c:T.leaf,icon:"tag"},
                    {l:"Alerts",v:unreadAlerts.length,c:T.warn,icon:"bell"},
                  ].map((s,i)=>(
                    <div key={i} style={{flex:1,padding:"12px 10px",display:"flex",alignItems:"center",gap:10,borderLeft:i?`1px solid ${T.hairline}`:"none",minWidth:0}}>
                      <div style={{width:30,height:30,borderRadius:9,background:`${s.c}14`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <Icon name={s.icon} size={14} color={s.c} strokeWidth={1.8}/>
                      </div>
                      <div style={{minWidth:0}}>
                        <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:s.c,lineHeight:1,letterSpacing:-.4}}>{s.v}</div>
                        <div style={{fontSize:10,color:T.ink50,marginTop:3,fontWeight:600,letterSpacing:.3}}>{s.l}</div>
                      </div>
                    </div>
                  ))}
                </Glass>

                {/* ── WATCHLIST CARDS ─────────────────────────────────── */}
                <div style={{display:"grid",gap:11}}>
                  {watchlist.map((w,i) => {
                    const onSale  = w.currentPrice <= w.targetPrice;
                    const atLow   = w.currentPrice <= w.allTimeLow;
                    const above   = !onSale;
                    const dropPct = w.priceHistory.length > 1
                      ? Math.round(((w.priceHistory[0].price - w.currentPrice) / w.priceHistory[0].price) * 100)
                      : 0;
                    const aboveTargetPct = above
                      ? Math.round(((w.currentPrice - w.targetPrice) / w.targetPrice) * 100)
                      : 0;

                    // Single status badge (priority: ATL > on-sale > watching)
                    const badge = atLow
                      ? { label:"All-time low", color:T.warn, bg:T.butterSoft, border:"rgba(251,191,36,.4)", icon:"trending-down" }
                      : onSale
                        ? { label:"On sale", color:T.leaf, bg:T.sageSoft, border:T.sage, icon:"tag" }
                        : null;

                    const priceColor = atLow ? T.warn : onSale ? T.leaf : T.forest;
                    const cardAccent = atLow ? "rgba(251,191,36,.5)" : onSale ? T.sage : T.parchment;

                    return (
                      <Glass key={w.id} className="anim-slide-up"
                        style={{
                          "--i":i,
                          padding:0,
                          borderRadius:16,
                          border:`1px solid ${cardAccent}`,
                          boxShadow: (atLow||onSale) ? "0 0 20px rgba(30,53,40,.08)" : "none",
                          overflow:"hidden",
                        }}>
                        {/* Top: product + status + remove */}
                        <div style={{display:"flex",alignItems:"flex-start",gap:12,padding:"13px 14px 11px"}}>
                          <ProductImage item={{id:w.itemId,name:w.name,category:w.category,instacartProductId:w.instacartProductId}} size={52}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}}>
                              <span style={{fontSize:14,fontWeight:700,color:T.forest,letterSpacing:-.1}}>{w.name}</span>
                              {isOrganic(w.name) && <OrgBadge small/>}
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                              {badge && (
                                <span style={{display:"inline-flex",alignItems:"center",gap:4,background:badge.bg,border:`1px solid ${badge.border}`,color:badge.color,fontSize:10,fontWeight:800,padding:"2px 8px 2px 6px",borderRadius:20,letterSpacing:.4,textTransform:"uppercase"}}>
                                  <Icon name={badge.icon} size={10} color={badge.color} strokeWidth={2.2}/>
                                  {badge.label}
                                </span>
                              )}
                              <span style={{fontSize:10,color:T.ink50,fontWeight:600}}>
                                {w.storeName || "Any retailer"} · added {w.addedDate}
                              </span>
                            </div>
                          </div>
                          <button onClick={()=>removeFromWatchlist(w.id)}
                            aria-label="Remove from watchlist"
                            style={{background:"transparent",border:`1px solid ${T.hairline}`,color:T.ink50,borderRadius:9,width:28,height:28,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            <Icon name="x" size={14} color={T.ink50} strokeWidth={2}/>
                          </button>
                        </div>

                        {/* Price ladder — target | current | ATL */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",alignItems:"center",gap:10,padding:"4px 14px 12px"}}>
                          {/* Target */}
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            <div style={{fontSize:9,letterSpacing:1.4,color:T.ink50,fontWeight:700,display:"flex",alignItems:"center",gap:4}}>
                              <span style={{display:"inline-block",width:8,height:1,background:T.warn,opacity:.7}}/>
                              TARGET
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:2}}>
                              <span style={{fontSize:13,fontWeight:700,color:T.warn,fontFamily:FONT_DISPLAY}}>$</span>
                              <input type="number" step="0.01" inputMode="decimal" value={w.targetPrice}
                                onChange={e=>updateWatchTarget(w.id, e.target.value)}
                                style={{background:"transparent",border:"none",borderBottom:`1px dashed ${T.warn}66`,color:T.warn,padding:"1px 0",fontSize:16,fontFamily:FONT_DISPLAY,fontWeight:700,outline:"none",width:60,letterSpacing:-.4}}/>
                            </div>
                          </div>

                          {/* Current — the hero */}
                          <div style={{textAlign:"center",padding:"0 6px",borderLeft:`1px solid ${T.hairline}`,borderRight:`1px solid ${T.hairline}`}}>
                            <div style={{fontSize:9,letterSpacing:1.4,color:T.ink50,fontWeight:700,marginBottom:2}}>NOW</div>
                            <div style={{fontFamily:FONT_DISPLAY,fontSize:28,fontWeight:700,color:priceColor,lineHeight:1,letterSpacing:-.6}}>${w.currentPrice.toFixed(2)}</div>
                            {above && aboveTargetPct > 0 && (
                              <div style={{fontSize:10,color:T.ink50,fontWeight:600,marginTop:3}}>
                                ${(w.currentPrice - w.targetPrice).toFixed(2)} above target
                              </div>
                            )}
                            {onSale && !atLow && (
                              <div style={{fontSize:10,color:T.leaf,fontWeight:700,marginTop:3,letterSpacing:.2}}>
                                Below your target ✓
                              </div>
                            )}
                            {atLow && (
                              <div style={{fontSize:10,color:T.warn,fontWeight:700,marginTop:3,letterSpacing:.2}}>
                                Lowest ever recorded
                              </div>
                            )}
                          </div>

                          {/* All-time low */}
                          <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"flex-end"}}>
                            <div style={{fontSize:9,letterSpacing:1.4,color:T.ink50,fontWeight:700,display:"flex",alignItems:"center",gap:4}}>
                              ALL-TIME LOW
                              <span style={{display:"inline-block",width:8,height:1,background:T.leaf,opacity:.6}}/>
                            </div>
                            <div style={{fontSize:16,fontWeight:700,color:T.leaf,fontFamily:FONT_DISPLAY,letterSpacing:-.4}}>${w.allTimeLow.toFixed(2)}</div>
                          </div>
                        </div>

                        {/* Price track — full-width history with target & ATL reference lines */}
                        {w.priceHistory.length > 1 && (
                          <div style={{padding:"0 14px 8px",position:"relative"}}>
                            <PriceTrack
                              data={w.priceHistory.map(p=>p.price)}
                              target={w.targetPrice}
                              atl={w.allTimeLow}
                              onSale={onSale}
                              height={52}
                            />
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4,fontSize:10,color:T.ink50,fontWeight:600}}>
                              <span>{w.priceHistory[0].date}</span>
                              {dropPct !== 0 ? (
                                <span style={{display:"inline-flex",alignItems:"center",gap:3,color:dropPct>0?T.leaf:T.berry,fontWeight:700}}>
                                  <Icon name={dropPct>0?"trending-down":"trending-up"} size={11} color={dropPct>0?T.leaf:T.berry} strokeWidth={2.2}/>
                                  {Math.abs(dropPct)}% since added
                                </span>
                              ) : (
                                <span>Tracking…</span>
                              )}
                              <span>Today</span>
                            </div>
                          </div>
                        )}

                        {/* Alert preferences */}
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",padding:"10px 14px 12px",borderTop:`1px solid ${T.hairline}`,background:"rgba(0,0,0,.015)"}}>
                          <span style={{fontSize:9,letterSpacing:1.3,color:T.ink50,fontWeight:700,display:"flex",alignItems:"center",gap:5,marginRight:2}}>
                            <Icon name="bell" size={13} color={T.ink50} strokeWidth={2}/>
                            ALERT ME
                          </span>
                          {[
                            {key:"alertOnTarget",label:"Hits target"},
                            {key:"alertOnAllTimeLow",label:"New low"},
                            {key:"alertOnAnySale",label:"Any sale"},
                          ].map(p => {
                            const on = w[p.key];
                            return (
                              <button key={p.key} onClick={()=>updateWatchAlertPref(w.id, p.key, !on)}
                                style={{background:on?T.sageSoft:"transparent",border:`1px solid ${on?T.sage:T.hairline}`,color:on?T.leaf:T.ink50,borderRadius:20,padding:"4px 10px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:4,transition:"all .15s ease"}}>
                                <span style={{display:"inline-flex",width:11,height:11,borderRadius:"50%",background:on?T.leaf:"transparent",border:`1.5px solid ${on?T.leaf:T.ink50}`,alignItems:"center",justifyContent:"center"}}>
                                  {on && <Icon name="check" size={7} color={T.paper} strokeWidth={3}/>}
                                </span>
                                {p.label}
                              </button>
                            );
                          })}
                        </div>
                      </Glass>
                    );
                  })}
                </div>

                {/* Future Instacart note */}
                <div style={{marginTop:16,padding:"12px 14px",background:"rgba(59,130,246,.06)",border:"1px solid rgba(59,130,246,.18)",borderRadius:12,fontSize:11,color:T.basil,lineHeight:1.55,display:"flex",alignItems:"flex-start",gap:10}}>
                  <div style={{width:22,height:22,borderRadius:7,background:"rgba(59,130,246,.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                    <Icon name="info" size={14} color={T.basil} strokeWidth={2}/>
                  </div>
                  <span>
                    Prices currently refresh from CartIQ's deal catalog. Once Instacart Connect is approved, watchlist items will auto-refresh hourly across every Instacart retailer — including CVS, Best Buy, Lowe's, and Total Wine.
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══ BOGOs ══ */}
        {!activeStore && tab==="bogos" && (() => {
          // ── DEAL BROWSER — every deal type, with rich filter chips ──
          // (tab id stays "bogos" for backward compat with existing state/links)
          const allDealsForBrowser = allDeals;  // already dietary-filtered upstream
          const pantryIds = new Set(pantry.map(p => p.id));
          const watchedKeys = new Set(watchlist.map(w => `${w.storeId}_${w.name}`));

          // Helper predicates — each chip uses one
          // "bogo" is the umbrella filter: any bundle deal (BOGO/BOGO50/B2G1) OR "BIG DEAL"
          const isBogo = d => isBundleDeal(d) || d.dealLabel.includes("BIG DEAL");
          const isAllTimeLow = d => {
            const h = PRICE_HISTORY[d.id];
            if (!h || h.length < 3) return false;
            // Use effective per-unit so bundle deals compare apples-to-apples with sales
            const cur = effectivePerUnit(d);
            const low = Math.min(...h.slice(0, -1));
            return cur <= low * 1.05;
          };
          const isHighDiscount = d => {
            if (!d.origPrice || d.origPrice === 0) return false;
            const cur = effectivePerUnit(d);
            const pct = ((d.origPrice - cur) / d.origPrice) * 100;
            return pct >= 30;
          };
          const isEndingToday = d => d.expiresIn <= 1;
          const isUnder5 = d => {
            const cur = effectivePerUnit(d);
            return cur > 0 && cur < 5;
          };

          // Apply selected filter
          let filtered = allDealsForBrowser;
          if (bogoFilter === "bogo" || bogoFilter === "bundle")  filtered = allDealsForBrowser.filter(isBogo);
          else if (bogoFilter === "bogo50")   filtered = allDealsForBrowser.filter(d => dealType(d) === DEAL_TYPES.BOGO_50);
          else if (bogoFilter === "bogoFree") filtered = allDealsForBrowser.filter(d => dealType(d) === DEAL_TYPES.BOGO_FREE);
          else if (bogoFilter === "b2g1")     filtered = allDealsForBrowser.filter(d => dealType(d) === DEAL_TYPES.B2G1);
          else if (bogoFilter === "bigDeal")  filtered = allDealsForBrowser.filter(d => d.dealLabel.includes("BIG DEAL"));
          else if (bogoFilter === "allTimeLow") filtered = allDealsForBrowser.filter(isAllTimeLow);
          else if (bogoFilter === "highDiscount") filtered = allDealsForBrowser.filter(isHighDiscount);
          else if (bogoFilter === "endingToday") filtered = allDealsForBrowser.filter(isEndingToday);
          else if (bogoFilter === "organic")  filtered = allDealsForBrowser.filter(d => isOrganic(d.name));
          else if (bogoFilter === "under5")   filtered = allDealsForBrowser.filter(isUnder5);
          else if (bogoFilter === "pantry")   filtered = allDealsForBrowser.filter(d => pantryIds.has(d.id));
          else if (bogoFilter === "watchlist") filtered = allDealsForBrowser.filter(d => watchedKeys.has(`${d.store.id}_${d.name}`));
          else if (bogoFilter === "notInCart") filtered = allDealsForBrowser.filter(d => !inCart(d.store.id, d.id));
          else if (bogoFilter !== "all")      filtered = allDealsForBrowser.filter(d => d.store.id === bogoFilter);

          const storeIds = [...new Set(allDealsForBrowser.map(d => d.store.id))];

          // Count helpers — deduped to avoid recomputation in JSX
          const counts = {
            all:          allDealsForBrowser.length,
            bundle:       allDealsForBrowser.filter(isBogo).length,
            bogoFree:     allDealsForBrowser.filter(d => dealType(d) === DEAL_TYPES.BOGO_FREE).length,
            bogo50:       allDealsForBrowser.filter(d => dealType(d) === DEAL_TYPES.BOGO_50).length,
            b2g1:         allDealsForBrowser.filter(d => dealType(d) === DEAL_TYPES.B2G1).length,
            bigDeal:      allDealsForBrowser.filter(d => d.dealLabel.includes("BIG DEAL")).length,
            allTimeLow:   allDealsForBrowser.filter(isAllTimeLow).length,
            endingToday:  allDealsForBrowser.filter(isEndingToday).length,
            highDiscount: allDealsForBrowser.filter(isHighDiscount).length,
            under5:       allDealsForBrowser.filter(isUnder5).length,
            watchlist:    allDealsForBrowser.filter(d => watchedKeys.has(`${d.store.id}_${d.name}`)).length,
            pantry:       allDealsForBrowser.filter(d => pantryIds.has(d.id)).length,
            notInCart:    allDealsForBrowser.filter(d => !inCart(d.store.id, d.id)).length,
            organic:      allDealsForBrowser.filter(d => isOrganic(d.name)).length,
          };

          // Is the user currently inside a bundle sub-filter? Controls sub-row visibility.
          const bundleSubFilters = new Set(["bundle", "bogo", "bogoFree", "bogo50", "b2g1", "bigDeal"]);
          const inBundleMode = bundleSubFilters.has(bogoFilter);

          // Is the user currently inside a "yours" filter? Forces the group open.
          const yoursFilters = new Set(["watchlist", "pantry", "notInCart", "organic"]);
          const inYoursMode = yoursFilters.has(bogoFilter);
          const yoursExpanded = showYoursFilters || inYoursMode;

          // Primary chip row — focused on what most users actually want.
          // No icons: filter labels are self-evident; an emoji per chip just adds visual noise.
          const primaryChips = [
            {id:"all",          label:"All",            count:counts.all},
            {id:"bundle",       label:"Bundle Deals",   count:counts.bundle},
            {id:"allTimeLow",   label:"All-Time Low",   count:counts.allTimeLow},
            {id:"endingToday",  label:"Ending Today",   count:counts.endingToday},
            {id:"highDiscount", label:"30%+ Off",       count:counts.highDiscount},
            {id:"under5",       label:"Under $5",       count:counts.under5},
          ];

          // Bundle sub-filters — shown only when user is inside a bundle filter
          const bundleChips = [
            {id:"bundle",   label:"All Bundles",    count:counts.bundle},
            {id:"bogoFree", label:"BOGO Free",      count:counts.bogoFree},
            {id:"bogo50",   label:"BOGO 50%",       count:counts.bogo50},
            {id:"b2g1",     label:"Buy 2 Get 1",    count:counts.b2g1},
            {id:"bigDeal",  label:"Big Deal",       count:counts.bigDeal},
          ];

          // "Yours" filters — personalized, collapsed by default
          const yoursChips = [
            {id:"watchlist",  label:"Watchlist",    count:counts.watchlist},
            {id:"pantry",     label:"Pantry",       count:counts.pantry},
            {id:"notInCart",  label:"Not in Cart",  count:counts.notInCart},
            {id:"organic",    label:"Organic",      count:counts.organic},
          ];

          // Per-store chips — store name only; the dedicated "BY STORE" row makes the icon redundant
          const storeChips = storeIds.map(sid => {
            const s = STORES.find(st => st.id === sid);
            return s ? {id:sid, label:s.name, count:allDealsForBrowser.filter(d=>d.store.id===sid).length, storeColor:s.color} : null;
          }).filter(Boolean);

          // Shared chip button renderer keeps the three rows visually consistent.
          // Count is shown as a faded inline numeral, not a parenthetical — reads as data, not text.
          const renderChip = (chip, isSubFilter) => {
            const active = bogoFilter===chip.id;
            return (
              <button key={chip.id} onClick={()=>{setBogoFilter(chip.id);SFX.tabSwitch();}}
                aria-pressed={active}
                style={{
                  background: active
                    ? (isSubFilter ? T.berry : T.forest)
                    : T.parchment,
                  border: `1px solid ${active
                    ? (isSubFilter ? T.berry : T.forest)
                    : T.ink15}`,
                  color: active ? "#fff" : T.forest,
                  borderRadius: 999,
                  padding: "7px 12px 7px 12px",
                  fontSize: 11.5,
                  fontWeight: 600,
                  letterSpacing: -.1,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 6,
                  boxShadow: active
                    ? (isSubFilter ? "0 1px 8px rgba(160,69,98,.25)" : "0 1px 8px rgba(30,53,40,.18)")
                    : "none",
                  transition: "all .14s ease",
                }}>
                <span>{chip.label}</span>
                {chip.count != null && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                    color: active ? "rgba(255,255,255,.65)" : T.ink50,
                  }}>{chip.count}</span>
                )}
              </button>
            );
          };

          // All chips, combined for any future lookup needs
          const allChips = [...primaryChips, ...bundleChips, ...yoursChips, ...storeChips];

          return (
            <div style={{animation:"pageIn .4s ease both"}}>
              {/* Page header — display weight title, no decorative emoji */}
              <div style={{marginBottom:18}}>
                <div style={{fontFamily:FONT_DISPLAY,fontSize:26,fontWeight:700,color:T.forest,letterSpacing:-.5,lineHeight:1.05}}>
                  Deal Browser
                </div>
                <div style={{fontSize:11.5,color:T.ink50,marginTop:4,letterSpacing:-.1}}>
                  <span style={{fontWeight:700,color:T.forest,fontVariantNumeric:"tabular-nums"}}>{allDealsForBrowser.length}</span> live deals{userLocation?.zip ? ` near ${userLocation.zip}` : ""} <span style={{opacity:.5}}>·</span> tap any to expand
                </div>
              </div>

              {/* Primary filter row */}
              <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:8,scrollbarWidth:"none",msOverflowStyle:"none"}}>
                {primaryChips.map(chip => renderChip(chip, false))}
              </div>

              {/* Bundle sub-filter row — appears only when Bundle Deals is active */}
              {inBundleMode && (
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:8,scrollbarWidth:"none",msOverflowStyle:"none",paddingLeft:14,borderLeft:"2px solid rgba(160,69,98,.25)"}}>
                  {bundleChips.map(chip => renderChip(chip, true))}
                </div>
              )}

              {/* "Yours" filter group — collapsible, personalized */}
              <div style={{marginBottom:10}}>
                <button onClick={()=>setShowYoursFilters(s => !s)}
                  aria-expanded={yoursExpanded}
                  style={{
                    background:"transparent",border:"none",
                    color:T.ink50,fontSize:10,letterSpacing:1.6,fontWeight:700,
                    cursor:"pointer",fontFamily:"inherit",
                    padding:"4px 0",
                    display:"flex",alignItems:"center",gap:7,
                    width:"100%",
                    marginBottom: yoursExpanded ? 8 : 0,
                  }}>
                  <span style={{
                    fontSize:8,
                    transform: yoursExpanded ? "rotate(90deg)" : "rotate(0deg)",
                    transition:"transform .18s ease",
                    display:"inline-block",
                    color: T.ink50,
                  }}>▸</span>
                  <span>YOURS</span>
                  <span style={{flex:1,height:1,background:T.hairline,marginLeft:2}}></span>
                  <span style={{
                    fontSize:9.5,letterSpacing:.4,fontWeight:600,
                    color:T.ink50,opacity:.7,textTransform:"none",
                  }}>{yoursExpanded ? "hide" : "show"}</span>
                </button>
                {yoursExpanded && (
                  <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,scrollbarWidth:"none",msOverflowStyle:"none"}}>
                    {yoursChips.map(chip => renderChip(chip, false))}
                  </div>
                )}
              </div>

              {/* Per-store row */}
              {storeChips.length > 0 && (
                <div style={{marginBottom:18}}>
                  <div style={{
                    display:"flex",alignItems:"center",gap:7,
                    fontSize:10,letterSpacing:1.6,color:T.ink50,fontWeight:700,
                    marginBottom:8,
                  }}>
                    <span>BY STORE</span>
                    <span style={{flex:1,height:1,background:T.hairline}}></span>
                  </div>
                  <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,scrollbarWidth:"none",msOverflowStyle:"none"}}>
                    {storeChips.map(chip => renderChip(chip, false))}
                  </div>
                </div>
              )}

              {/* Compact grid — 4 per row on wide screens, 3 on narrow */}
              {filtered.length === 0 ? (
                <Glass style={{padding:"40px 24px",textAlign:"center",borderRadius:18}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:14,opacity:.55}}>
                    <ItemIcon emoji="🔎" size={44}/>
                  </div>
                  <div style={{fontSize:14,fontWeight:600,color:T.forest,marginBottom:4}}>No deals match this filter</div>
                  <div style={{fontSize:12,color:T.ink50,marginBottom:18}}>Try widening the search to see what's live near you.</div>
                  <button onClick={()=>{setBogoFilter("all");SFX.tabSwitch();}}
                    style={{background:T.forest,border:"none",color:"#fff",borderRadius:999,padding:"9px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 1px 6px rgba(30,53,40,.18)"}}>
                    Show all deals
                  </button>
                </Glass>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
                  {filtered.map((deal, i) => {
                    const ic = inCart(deal.store.id, deal.id);
                    const watched = isWatched(deal.name, deal.store.id);
                    const inPantry = pantryIds.has(deal.id);
                    const allTimeLow = isAllTimeLow(deal);
                    // Pricing comes from the single-source-of-truth module.
                    // For tile cards we use a SHORT caption ("buy 2"/"each"/"+ ½ off 2nd")
                    // rather than the full dealMathCaption to keep the tile compact.
                    const tileDisplayPrice = displayPrice(deal);
                    const t = dealType(deal);
                    const priceCaption =
                      t === DEAL_TYPES.B2G1      ? "buy 2" :
                      t === DEAL_TYPES.BOGO_FREE ? "each"  :
                      t === DEAL_TYPES.BOGO_50   ? "+ ½ off 2nd" :
                      null;
                    return (
                      <button key={`${deal.store.id}_${deal.id}_${i}`}
                        onClick={()=>{setActiveDealModal({deal, store: deal.store});SFX.tabSwitch();}}
                        aria-label={`${deal.name} at ${deal.store.name}, $${tileDisplayPrice.toFixed(2)}, save $${deal.savings.toFixed(2)}`}
                        className="anim-slide-up"
                        style={{
                          "--i": i,
                          background:T.paper,
                          backdropFilter: "blur(14px)",WebkitBackdropFilter:"blur(14px)",
                          border: `1px solid ${ic ? T.sage : allTimeLow ? T.butter : T.ink15}`,
                          borderRadius: 14,
                          padding: 10,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                          position: "relative",
                          overflow: "hidden",
                        }}>
                        {/* Status micro-pills — text labels, not emoji. Sit top-right, stack vertically. */}
                        <div style={{position:"absolute",top:6,right:6,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3,zIndex:2}}>
                          {ic && (
                            <span style={{background:T.basil,color:"#fff",fontSize:8.5,fontWeight:700,padding:"2px 6px",borderRadius:4,letterSpacing:.5,textTransform:"uppercase",lineHeight:1.2}}>
                              In cart
                            </span>
                          )}
                          {allTimeLow && (
                            <span style={{background:"#fbbf24",color:"#000",fontSize:8.5,fontWeight:700,padding:"2px 6px",borderRadius:4,letterSpacing:.5,textTransform:"uppercase",lineHeight:1.2}}>
                              Low
                            </span>
                          )}
                          {watched && !ic && !allTimeLow && (
                            <span style={{background:"rgba(74,222,128,.95)",color:"#0a3d12",fontSize:8.5,fontWeight:700,padding:"2px 6px",borderRadius:4,letterSpacing:.5,textTransform:"uppercase",lineHeight:1.2}}>
                              Watch
                            </span>
                          )}
                          {inPantry && !ic && !allTimeLow && !watched && (
                            <span style={{background:"rgba(251,146,60,.95)",color:"#fff",fontSize:8.5,fontWeight:700,padding:"2px 6px",borderRadius:4,letterSpacing:.5,textTransform:"uppercase",lineHeight:1.2}}>
                              Pantry
                            </span>
                          )}
                        </div>
                        {/* Photo */}
                        <div style={{display:"flex",justifyContent:"center",padding:"4px 0"}}>
                          <ProductImage item={{id:deal.id,name:deal.name,category:deal.category,instacartProductId:deal.instacartProductId}} size={56}/>
                        </div>
                        {/* Deal label */}
                        <div style={{display:"flex",justifyContent:"center"}}>
                          <span style={{background:`linear-gradient(135deg,${deal.store.color},${deal.store.glow})`,color:T.paper,fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:10,letterSpacing:.5,textTransform:"uppercase"}}>
                            {deal.dealLabel}
                          </span>
                        </div>
                        {/* Name */}
                        <div style={{fontSize:11.5,fontWeight:600,color:T.forest,textAlign:"center",lineHeight:1.25,minHeight:28,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",letterSpacing:-.1}}>
                          {deal.name}
                        </div>
                        {/* Actual price — large display centered */}
                        <div style={{textAlign:"center",lineHeight:1}}>
                          <div style={{fontFamily:FONT_DISPLAY,fontSize:20,fontWeight:700,color:T.forest,letterSpacing:-.5,fontVariantNumeric:"tabular-nums"}}>
                            ${tileDisplayPrice.toFixed(2)}
                          </div>
                          {(tileDisplayPrice < deal.origPrice || priceCaption) && (
                            <div style={{fontSize:10,color:T.ink50,marginTop:3,display:"flex",alignItems:"center",justifyContent:"center",gap:5,fontVariantNumeric:"tabular-nums"}}>
                              {tileDisplayPrice < deal.origPrice && (
                                <span style={{textDecoration:"line-through"}}>${deal.origPrice.toFixed(2)}</span>
                              )}
                              {priceCaption && <span style={{fontWeight:600,color:T.ink50}}>{priceCaption}</span>}
                            </div>
                          )}
                        </div>
                        {/* Store + savings */}
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${T.hairline}`,paddingTop:6,marginTop:"auto",gap:6}}>
                          <span style={{display:"inline-flex",alignItems:"center"}} aria-hidden="true">
                            <ItemIcon emoji={deal.store.logo} size={14}/>
                          </span>
                          <span style={{fontFamily:FONT_DISPLAY,fontSize:13,fontWeight:700,color:T.basil,fontVariantNumeric:"tabular-nums",letterSpacing:-.2}}>
                            −${deal.savings.toFixed(2)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══ EARNINGS ══ */}
        {!activeStore && tab==="earnings" && <EarningsDashboard activeStores={activeStores} netBenefit={netBenefit}/>}
      </div>

      {/* ═══ MORE MENU OVERLAY ═══ */}
      {showMore && (
        <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,.4)",zIndex:280,animation:"fadeIn .15s ease"}} onClick={()=>setShowMore(false)}>
          <div style={{position:"fixed",bottom:72,left:0,right:0,padding:"12px 16px",animation:"slideUp .2s ease both"}} onClick={e=>e.stopPropagation()}>
            <Glass style={{borderRadius:20,padding:"16px 14px",maxHeight:"65vh",overflowY:"auto",overscrollBehavior:"contain"}}>
              {MORE_GROUPS.map((group, gi) => (
                <div key={group.label} style={{marginBottom: gi < MORE_GROUPS.length-1 ? 14 : 0}}>
                  <div style={{fontSize:10,letterSpacing:1.8,color:T.ink50,fontWeight:700,marginBottom:8,paddingLeft:4,textTransform:"uppercase"}}>
                    {group.label}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
                    {group.tabs.map(t=>(
                      <button key={t.id} onClick={()=>handleTabChange(t.id)}
                        style={{position:"relative",background:tab===t.id?T.sageSoft:T.parchment,border:`1px solid ${tab===t.id?T.sage:T.hairline}`,borderRadius:14,padding:"12px 6px",textAlign:"center",cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        {t.beta && (
                          <span style={{position:"absolute",top:4,right:4,fontSize:7.5,fontWeight:700,letterSpacing:.6,color:T.warn,background:T.butterSoft,border:`1px solid ${T.butter}`,borderRadius:4,padding:"1px 4px",lineHeight:1.2}}>
                            BETA
                          </span>
                        )}
                        <span style={{fontSize:20}}>{t.icon}</span>
                        <span style={{fontSize:10,fontWeight:600,color:tab===t.id?T.basil:T.ink50}}>{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </Glass>
          </div>
        </div>
      )}

      {/* ═══ BOTTOM NAV — #2 ═══
          Polished tab bar: SVG icons (no emoji), active state shows a soft
          basil-tinted pill behind the icon + bold label, inactive icons sit
          in muted ink50. The top accent bar is gone — the pill now carries
          the active signal more cleanly. */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:250,background:T.paper,borderTop:`1px solid ${T.hairline}`,padding:"6px 8px",paddingBottom:"max(6px, env(safe-area-inset-bottom))",boxShadow:"0 -1px 0 rgba(0,0,0,.02), 0 -8px 24px -12px rgba(30,53,40,.10)"}}>
        <div style={{display:"flex",justifyContent:"space-around",maxWidth:500,margin:"0 auto"}}>
          {PRIMARY_TABS.map(t=>{
            const active = t.id==="more" ? showMore : (!activeStore && tab===t.id);
            // "more" gets a custom three-dot SVG; everything else uses the registry.
            const renderIcon = () => {
              if (t.iconName === "more") {
                return (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? T.basil : T.ink50} aria-hidden="true">
                    <circle cx="5" cy="12" r="2.2"/>
                    <circle cx="12" cy="12" r="2.2"/>
                    <circle cx="19" cy="12" r="2.2"/>
                  </svg>
                );
              }
              return <Icon name={t.iconName} size={22} color={active ? T.basil : T.ink50} stroke={2.1}/>;
            };
            return (
              <button key={t.id} onClick={()=>handleTabChange(t.id)}
                aria-current={active ? "page" : undefined}
                aria-label={t.label}
                style={{
                  background:"none",border:"none",
                  display:"flex",flexDirection:"column",alignItems:"center",gap:3,
                  padding:"6px 10px 4px",cursor:"pointer",fontFamily:"inherit",
                  position:"relative",flex:1,maxWidth:96,
                }}>
                {/* Icon-pill — soft basil halo behind icon when active */}
                <span style={{
                  display:"flex",alignItems:"center",justifyContent:"center",
                  width:44,height:28,borderRadius:14,
                  background: active ? T.sageSoft : "transparent",
                  transition:"background .18s ease",
                }}>
                  {renderIcon()}
                </span>
                <span style={{
                  fontSize:10,
                  fontWeight: active ? 700 : 500,
                  letterSpacing: -.05,
                  color: active ? T.basil : T.ink50,
                  transition:"color .15s, font-weight .15s",
                }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ MODALS & OVERLAYS ═══ */}
      {checkout && <CheckoutModal store={checkout.store} cartDeals={checkout.cartDeals} trackingId={checkout.trackingId} subPrefs={subPrefs} onOpenSubModal={(d)=>setSubModalDeal({...d, store: checkout.store})} onClose={()=>setCheckout(null)} onConfirm={handleConfirmOrder} onRequestRedirect={openInstacartUrl}/>}
      {TRIP_RECEIPT_ENABLED && tripReceipt && (
        <TripReceiptModal
          receipt={tripReceipt}
          lifetimeSavings={lifetimeSavings}
          orderCount={txnHistory.length}
          isDemo={!AFFILIATE_LIVE && txnHistory.length > 0}
          onClose={()=>setTripReceipt(null)}
          onShare={()=>{ setTripReceipt(null); setShareSavingsOpen(true); }}
        />
      )}
      {integrationDisclosureOpen && <IntegrationDisclosureModal onContinue={handleIntegrationDisclosureContinue} onClose={handleIntegrationDisclosureClose}/>}
      {subModalDeal && <SubstitutionModal deal={subModalDeal} prefs={subPrefs} onSave={(id,pref)=>setSubPrefs(p=>({...p,[id]:pref}))} onClose={()=>setSubModalDeal(null)}/>}
      {priceIQDetail && <PriceIQDetailSheet
        itemId={priceIQDetail}
        onClose={()=>setPriceIQDetail(null)}
        allDeals={allDeals}
        watchlist={watchlist}
        toggleWatchlist={toggleWatchlist}
        updateWatchTarget={updateWatchTarget}
        toggleCart={toggleCart}
        inCart={inCart}
        onAskSage={()=>{ /* TODO: open Sage with prefilled question about this item */ }}
        activeStores={activeStores}
        priceMemory={priceMemory}
      />}
      {tourActive && <OnboardingTour step={tourStep} onNext={s=>setTourStep(s)} onSkip={dismissTour} onFinish={dismissTour} onSetTab={t=>setTab(t)}/>}
      {activeTip && <FirstVisitTooltip tip={activeTip} onDismiss={()=>setActiveTip(null)}/>}
      {locationOpen && <LocationSetupModal initialZip={userLocation?.zip} initialName={userName} onSave={handleLocationSave} onSaveName={setUserName} onClose={()=>setLocationOpen(false)}/>}
      {dietarySetupOpen && <DietarySetupModal initial={dietaryRestrictions} onSave={handleDietarySave} onClose={()=>setDietarySetupOpen(false)} isOnboarding={(()=>{ try { return !localStorage.getItem("cartiq_dietary_seen"); } catch(e) { return false; } })()}/>}
      {healthGoalsOpen && <HealthGoalsModal initial={healthProfile} onSave={handleHealthGoalsSave} onClose={()=>setHealthGoalsOpen(false)} isOnboarding={(()=>{ try { return !localStorage.getItem("cartiq_health_goals_seen"); } catch(e) { return false; } })()}/>}
      {projectedSavingsOpen && <ProjectedSavingsModal preview={rebuildPreview} userName={userName} onOpenSmartCart={()=>handleProjectedSavingsClose(true)} onClose={()=>handleProjectedSavingsClose(false)}/>}
      {shareSavingsOpen && <ShareSavingsModal amount={savingsPeriod==="month"?monthlySavings:savingsPeriod==="year"?annualSavings:lifetimeSavings} period={savingsPeriod} userName={userName} isDemo={!AFFILIATE_LIVE && txnHistory.length>0} onClose={()=>setShareSavingsOpen(false)}/>}
      {staplesPickerOpen && (
        <StaplesPickerModal
          userName={userName}
          onSave={handleStaplesSave}
          onSkip={handleStaplesSkip}
          onClose={handleStaplesSkip}
        />
      )}
      {shopPlannerOpen && <ShopPlannerModal initial={plannedShop} onSave={handleShopPlanSave} onClose={()=>setShopPlannerOpen(false)}/>}
      {shoppingModeOpen && <ShoppingModeScreen cartItems={cartItems} onToggleItem={(sid,iid)=>toggleCart(sid,iid)} onOpenScanner={()=>setScannerOpen(true)} onExit={()=>setShoppingModeOpen(false)}/>}
      {emailSetupOpen && <EmailAlertsModal initial={emailAlerts} onSave={handleEmailAlertsSave} onClose={()=>setEmailSetupOpen(false)}/>}
      {storeAlertsOpen && <StoreAlertsModal initial={storeAlertPrefs} activeStores={activeStores} onSave={handleStoreAlertsSave} onClose={()=>setStoreAlertsOpen(false)}/>}
      {tipsModalOpen && <TipsModal onClose={()=>setTipsModalOpen(false)}/>}
      {soundSetupOpen && <FeedbackSetupModal soundValue={soundOn} hapticsValue={hapticsOn} onSaveSound={setSoundOn} onSaveHaptics={setHapticsOn} onClose={()=>setSoundSetupOpen(false)}/>}
      {scannerOpen && <BarcodeScannerModal
        onClose={()=>setScannerOpen(false)}
        addToCart={(sid,iid)=>toggleCart(sid,iid)}
        addToWatchlist={(deal)=>toggleWatchlist(deal)}
        isInCart={(sid,iid)=>inCart(sid,iid)}
        isWatched={isWatched}
      />}

      {/* Expanded deal modal — opens when tapping a grid item on BOGOs tab */}
      {activeDealModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(30,53,40,0.60)",zIndex:860,display:"flex",alignItems:"center",justifyContent:"center",padding:16,animation:"fadeIn .2s ease"}} onClick={e=>e.target===e.currentTarget&&setActiveDealModal(null)}>
          <div style={{width:"100%",maxWidth:400,animation:"slideUp .25s ease both",position:"relative"}}>
            <button onClick={()=>setActiveDealModal(null)}
              style={{position:"absolute",top:-40,right:0,background:T.parchment,border:"none",color:T.paper,borderRadius:"50%",width:34,height:34,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>
              ✕
            </button>
            <DealCard
              deal={activeDealModal.deal}
              di={0}
              store={activeDealModal.store}
              inCart={inCart(activeDealModal.store.id, activeDealModal.deal.id)}
              onToggle={()=>{toggleCart(activeDealModal.store.id, activeDealModal.deal.id);}}
              isWatched={isWatched(activeDealModal.deal.name, activeDealModal.store.id)}
              onToggleWatch={()=>toggleWatchlist({...activeDealModal.deal, store: activeDealModal.store})}
              onAsk={({deal,store})=>setAskSageDeal({deal,store})}
              isInPantry={pantry.some(p => p.id === activeDealModal.deal.id)}
              onAddToPantry={()=>addDealToPantry({...activeDealModal.deal, store: activeDealModal.store})}
            />
          </div>
        </div>
      )}
      {askSageDeal && <AskSageModal deal={askSageDeal.deal} store={askSageDeal.store} userName={userName} onClose={()=>setAskSageDeal(null)}/>}
      {termsOpen && <TermsModal onClose={()=>setTermsOpen(false)}/>}
      {whatsRealOpen && <WhatsRealModal onClose={()=>setWhatsRealOpen(false)}/>}
      {addOwnItemOpen && (
        <PantryAddOwnItemModal
          onSave={(newItem) => {
            // Dedup by normalized name+category so users don't stack duplicates
            const normalizedName = newItem.name.toLowerCase().trim();
            setPantry(prev => {
              const dup = prev.find(p =>
                p.name.toLowerCase().trim() === normalizedName &&
                p.category === newItem.category
              );
              if (dup) {
                notify(`"${newItem.name}" is already in your pantry`, "warn");
                return prev;
              }
              notify(`✓ Added ${newItem.name} to Pantry`);
              return [...prev, newItem];
            });
          }}
          onClose={() => setAddOwnItemOpen(false)}
        />
      )}
      {createListOpen && (
        <CreateListModal
          existingNames={lists.map(l => l.name)}
          onSave={({ name, occasion }) => {
            const next = makeList({ name, occasion });
            setLists(prev => [...prev, next]);
            notify(`✓ Created list "${name}"`);
          }}
          onClose={() => setCreateListOpen(false)}
        />
      )}
      {addItemsToListOpen && activeListId && (() => {
        const list = lists.find(l => l.id === activeListId);
        if (!list) return null;
        const candidates = myItems.filter(it => !(it.lists || []).includes(list.name));
        return (
          <AddItemsToListModal
            list={list}
            candidates={candidates}
            onConfirm={(ids) => {
              setMyItems(prev => ids.reduce((acc, id) => setItemListMembership(acc, id, list.name, { active: true }), prev));
              notify(`✓ Added ${ids.length} ${ids.length === 1 ? "item" : "items"} to "${list.name}"`);
            }}
            onClose={() => setAddItemsToListOpen(false)}
          />
        );
      })()}

      {/* Phase 2 — Universal search overlay and item detail sheet */}
      {searchOverlay && (
        <GlobalSearchOverlay
          initialScopeStoreId={searchOverlay.initialScopeStoreId}
          dietaryRestrictions={dietaryRestrictions}
          activeStores={activeStores}
          recentSearches={recentSearches}
          onRecordSearch={recordRecentSearch}
          onClearRecentSearches={() => setRecentSearches([])}
          onClose={() => setSearchOverlay(null)}
          onOpenItem={(item) => setDetailItem(item)}
        />
      )}
      {detailItem && (
        <ItemDetailSheet
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onAddToSmartCart={addSearchItemToSmartCart}
          onAddToPantry={addSearchItemToPantry}
          onAddToWatchlist={addSearchItemToWatchlist}
          alreadyInCart={isSearchItemInCart}
          alreadyInPantry={isSearchItemInPantry}
          alreadyInWatchlist={isSearchItemInWatchlist}
        />
      )}

      {/* Footer — copyright, terms, privacy, and the version stamp.
          The version stamp lets testers reference a specific build in their
          feedback, and lets you correlate feedback to specific deploys.
          Keeps the 10px muted style consistent with the rest of the footer. */}
      <div style={{padding:"12px 18px 4px",display:"flex",alignItems:"center",justifyContent:"center",gap:8,flexWrap:"wrap"}}>
        <span style={{fontSize:10,color:T.ink50}}>{COPYRIGHT_NOTICE}</span>
        <button onClick={()=>setTermsOpen(true)} style={{background:"none",border:"none",color:T.ink50,fontSize:10,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",padding:0}}>Terms</button>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{color:T.ink50,fontSize:10,textDecoration:"underline"}}>Privacy</a>
      </div>
      <div style={{padding:"0 18px 12px",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:60}}>
        <span style={{fontSize:10,color:T.ink50,letterSpacing:.3}}>CartIQ v{APP_VERSION} · {BUILD_DATE}</span>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{position:"fixed",bottom:84,left:"50%",transform:"translateX(-50%)",zIndex:999,background:toast.type==="ok"?T.forest:toast.type==="warn"?T.warn:T.berry,border:`1px solid ${toast.type==="ok"?T.leaf:toast.type==="warn"?T.apricot:T.berrySoft}`,color:T.paper,borderRadius:14,padding:"11px 16px 11px 18px",fontSize:12,fontWeight:700,boxShadow:"0 8px 32px rgba(0,0,0,.7)",animation:"fadeIn .3s ease both",maxWidth:360,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:15}}>{toast.type==="ok"?"✨":toast.type==="warn"?"↩️":"⚠️"}</span>
          <span style={{flex:1}}>{toast.msg}</span>
          {toast.action && (
            <button onClick={()=>{toast.action.onClick();setToast(null);}}
              style={{background:"rgba(255,255,255,.12)",border:"1px solid rgba(255,255,255,.18)",color:T.paper,borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",textTransform:"uppercase",letterSpacing:.5,flexShrink:0}}>
              {toast.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
/* ═══════════════════════════════════════════════════════════
   ROOT EXPORT — wraps the app in the ErrorBoundary so any render
   crash anywhere in the tree is caught and shown to the user as
   the friendly fallback above instead of a white screen.
   ═══════════════════════════════════════════════════════════ */
export default function CartIQ() {
  return (
    <ErrorBoundary>
      <CartIQApp/>
    </ErrorBoundary>
  );
}
